import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Check,
  CheckCircle2,
  CheckCheck,
  Clock,
  Database,
  Cloud,
  Table2,
  FileSpreadsheet,
  FileText,
  Loader2,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Download,
  RefreshCw,
  Link,
  Zap,
  Upload,
  Plus,
  X,
  History,
  Search,
  Scale,
  GitCompareArrows,
  Play,
  FileCode,
  Trash2,
  Sparkles,
  GitMerge,
  ListChecks,
  Microscope,
  ArrowUpRight,
  Maximize2,
  Copy,
  Server,
  Folder,
  File as FileIcon,
  Pencil,
  Eye,
  Info,
} from "lucide-react";
import {
  apiv2,
  getPipelineProgress,
  subscribeToPipelineProgress,
} from "../apiv2";
import { stagingTableSuffix } from "@/lib/stagingTableSuffix";
import { downloadTableAsExcel, downloadTableAsPdf } from "@/lib/tableExport";
import { openAgentInNewTab } from "@/lib/utils";
import {
  buildBulkMappingPairs,
  buildCrmToDbFieldMappings,
  capMappingSelectionForMode,
  computeFkAwareSequentialOrder,
  effectiveSchemaNameFromConnection,
  filenameTimestamp,
  formatSqlForDisplay,
  mergeCarryForwardIntoLoadStatus,
  parsePastedColumnNames,
  RECONCILE_COLUMN_EXPORT_HEADERS,
  reconcileColumnExportRows,
  RECONCILE_ROW_EXPORT_HEADERS,
  reconcileRowExportRows,
  resolveFailedTableRetryPlan,
  resolveLoaderResumeStep,
  scopeTablesForInitialAutoMap,
  seedCrmStreamFieldMappings,
  sttmTabsToShow,
  validateCrmStreamLoadConfig,
} from "./dataConverterView.utils";
import {
  CrmStreamLoadModal,
  type CrmStreamLoadConfig,
} from "./CrmStreamLoadModal";
import {
  ConnectDatabaseDialog,
  type ConnectResult,
  type DbConnectFormValues,
} from "./data-source/ConnectDatabaseDialog";
import type {
  DbConnectionConfig,
  ConverterConnectionRef,
  ProjectKnowledgeDocInfo,
  ProjectKnowledgeDocType,
  ConverterSkillInfo,
  SchemaExtractResult,
  SchemaDiff,
  SsisPackageDetails,
  AdditionalItem,
  TableMeta,
  MdmOracleOutput,
  SalesforceConnectionConfig,
  SalesforceObjectMeta,
  SalesforceField,
  TableObjectMapping,
  SttmFieldMapping,
  JoinCondition,
  ComplexMappingGroup,
  SttmGenerateResponse,
  DataDictionaryGenerateResponse,
  ProjectMappingFileOut,
  DdlStatement,
  DdlExecuteResult,
  DdlReconcileResult,
  DdlDropResponse,
  DdlDuplicate,
  DdlDuplicateResolution,
  LoaderSqlPreviewOut,
  DataLoadStatusResponse,
  DataLoadTableResult,
  DataLoadTableProgress,
  DbToSfLoadStatusResponse,
  TableCountResult,
  ReconcileColumnResponse,
  ColumnReconcileResult,
  ReconcileRowResponse,
  RowMismatchDetail,
  RowSummaryDetail,
  TransformRulesImportResponse,
  LegacyRuleBatch,
  LegacyRuleItem,
  LegacyArtifactType,
  ConverterSourceField,
  ConverterSourceFormat,
  ConverterFileUploadResponse,
  ConverterPdfExtractedTable,
  ConverterPdfPreviewResponse,
  ConverterSftpConnectionConfig,
  ConverterSftpFileMeta,
  ConverterSharePointConnectionConfig,
  ConverterSharePointListMeta,
  ConverterTask,
  ConverterPreviewResponse,
  ConverterLoadResponse,
  ConverterBadRecord,
  ConverterTestConnectionResponse,
  CrmConnectionOut,
  SavedConnectionOut,
  ConverterDdlStateOut,
  ConverterLoadWatermarkOut,
  ConverterReconciliationStateOut,
  ConverterLatestLoadJobOut,
  ConverterMappingHistoryEntryOut,
  ConverterReconcileDetailedOut,
  ConverterFinalTableDqOut,
  ConverterLoadHistoryEntryOut,
  ConverterReconciliationHistoryEntryOut,
  ConverterFinalLoadHistoryEntryOut,
  FileReconcileSourceFile,
  PipelineProgressResponse,
  ConverterBodsDataflowImportResponse,
  ConverterInformaticaMappingImportResponse,
  CrmToDbFieldMapping,
  CrmToDbLoadStatusResponse,
} from "../apiv2";
import { loadConn, saveConn, clearConn } from "../lib/devConnections";
import ConverterActivityPanel from "./ConverterActivityPanel";
import SavedConnectionsPanel from "./SavedConnectionsPanel";
import { ConverterHistoryPanel } from "./ConverterHistoryPanel";
import { ConverterQualityReport } from "./ConverterQualityReport";
import { StandaloneReconcilePanel } from "./StandaloneReconcilePanel";

interface Props {
  onBack: () => void;
  projectId?: string;
}

type Step =
  | "source"
  | "schema"
  | "salesforce"
  | "mapping"
  | "sttm"
  | "ddl"
  | "load"
  | "reconcile"
  | "sf_load";

const STEPS: { id: Step; label: string; icon: React.ReactNode }[] = [
  { id: "source", label: "Source", icon: <Database size={16} /> },
  { id: "schema", label: "Schema DDL", icon: <Table2 size={16} /> },
  { id: "salesforce", label: "Target Platform", icon: <Cloud size={16} /> },
  { id: "mapping", label: "STTM-Mapping", icon: <Link size={16} /> },
  {
    id: "sttm",
    label: "Review & Export Mapping",
    icon: <FileSpreadsheet size={16} />,
  },
  { id: "ddl", label: "Create Staging Tables", icon: <Database size={16} /> },
  { id: "load", label: "Load Data to Staging", icon: <Upload size={16} /> },
  {
    id: "reconcile",
    label: "Reconcile Source vs Staging",
    icon: <Scale size={16} />,
  },
  { id: "sf_load", label: "Load to Target", icon: <Cloud size={16} /> },
];

// The four standalone actions a user can pick from the landing page, each
// scoped to its own step subset (a subset of the step-render blocks below —
// almost none of that JSX had to change, only which subset is reachable and
// how goNext/goPrev/ProgressBar walk it). Replaces the old single global
// STEP_ORDER linear wizard entirely.
//
// "load_target" (Load to Target) used to be its own 4th action; it's now
// folded into "loader" as that action's 3rd step (see ACTION_STEPS below)
// — Data Loader now owns every kind of load (into the staging DB, and from
// there into the connected target platform), matching the Mapping/Loader/
// Reconciliation 3-agent split. The step id itself, its label/icon, and
// every step === "sf_load" render block are UNCHANGED — only which action
// reaches it changed, so an in-progress project's saved DDL/load-job/
// Load-to-Target state all still resume correctly (see checkResumable's
// merged "loader" branch below).
//
// 2026-09-01, requested by user: "createTables" is a new 4th standalone
// action — Create Staging Tables no longer has to be entered through the
// Data Loader flow. It reuses the same "ddl" step (still also reachable as
// Data Loader's first step, unchanged, so an in-progress loader session
// resumes exactly as before) and only requires a finalized STTM mapping to
// already exist for the project (enforced the same way every non-"mapping"
// action already is — see the scopedMappingStatus effect/render below).
type ConverterAction = "mapping" | "loader" | "createTables" | "reconciliation";

const ACTION_STEPS: Record<ConverterAction, Step[]> = {
  mapping: ["source", "schema", "salesforce", "mapping", "sttm"],
  loader: ["ddl", "load", "sf_load"],
  createTables: ["ddl"],
  reconciliation: ["reconcile"],
};

// 2026-09-01, requested by user: an object-oriented target (Salesforce/
// Dynamics 365/Power Platform) can be loaded either through an intermediate
// staging RDBMS (source -> staging -> target, the loader's normal 3 steps)
// or directly (source -> target, skipping "ddl"/"load" entirely). The
// user's choice (crmLoadStrategy state) only changes the *effective* step
// list for the "loader" action — ACTION_STEPS itself stays the single
// source of truth for "what steps exist", this just narrows which of them
// are actually reachable for this session.
//
// 2026-09-07, requested by user: generalized beyond CRM targets — an RDBMS
// target (Oracle/Postgres/Synapse/etc.) can now ALSO be loaded directly,
// source -> target, skipping staging entirely. Unlike the CRM case (which
// pushes into an object-oriented API via the "sf_load" step), an RDBMS
// direct load reuses the plain "load" step's own source/target-DB SELECT->
// INSERT job — it's the same job a staged load runs, just pointed at the
// real target table instead of a staging one (see stagedTargetTable).
function effectiveActionSteps(
  action: ConverterAction,
  crmLoadStrategy: "staged" | "direct" | null,
  targetPlatformMode: "crm" | "rdbms" | "mirror",
): Step[] {
  if (action === "loader" && crmLoadStrategy === "direct") {
    return targetPlatformMode === "rdbms" ? ["load"] : ["sf_load"];
  }
  return ACTION_STEPS[action];
}

const ACTION_META: {
  id: ConverterAction;
  label: string;
  description: string;
  icon: React.ReactNode;
}[] = [
  {
    id: "mapping",
    label: "Source → Target Mapping",
    description:
      "Connect a source database, scan its schema, connect a target platform, and generate/review the STTM field mapping.",
    icon: <Link size={20} />,
  },
  {
    id: "loader",
    label: "Data Loader",
    description:
      "Create staging tables from a persisted mapping and push staged data into the connected target platform, or load a fixed-width/delimited/structured file directly into a database table — no STTM mapping needed for files.",
    icon: <Upload size={20} />,
  },
  {
    id: "createTables",
    label: "Create Staging Tables",
    description:
      "Standalone: generate and run the staging-table DDL for a project whose STTM mapping is already finalized — no need to go through the full Data Loader flow first.",
    icon: <Database size={20} />,
  },
  {
    id: "reconciliation",
    label: "Reconciliation",
    description:
      "Compare row counts and column-level values between the source and staged data.",
    icon: <Scale size={20} />,
  },
];

// Labels generated DDL comments dynamically with whatever the user
// actually picked on the Target Platform step, instead of always assuming
// Salesforce (see computeTargetPlatformLabel and the "-- <label> Object: "
// comment agents/data_converter's generate_ddl now renders).
const _RDBMS_DIALECT_LABELS: Record<string, string> = {
  snowflake: "Snowflake",
  databricks: "Databricks",
  postgresql: "PostgreSQL",
  postgres: "PostgreSQL",
  oracle: "Oracle",
  mssql: "SQL Server",
  mysql: "MySQL",
  synapse: "Azure Synapse",
};
const _CRM_TARGET_LABELS: Record<string, string> = {
  salesforce: "Salesforce",
  dynamics365: "Dynamics 365",
  power_platform: "Power Platform",
  workday: "Workday",
};

// "Generate from Legacy Code" — accepted extensions, mirrored from
// legacy_schema_parser.py's own detection list. Used both for the file
// picker's `accept` attribute and to filter a folder selection down to
// only the files this pipeline can actually parse (a folder picker has no
// `accept` filtering of its own).
const _LEGACY_CODE_EXTENSIONS = [
  ".cpy",
  ".cob",
  ".cbl",
  ".copybook",
  ".copy",
  ".sql",
  ".ddl",
  ".json",
  ".jsonl",
  ".xml",
  ".xsd",
  ".dtsx",
  ".parquet",
  ".pq",
  ".csv",
  ".tsv",
  // Data-dictionary exports from erwin, ER/Studio, PowerDesigner, Toad
  // Data Modeler, etc. — their native project files are proprietary/binary,
  // but every one of them can export a tabular entity/attribute report.
  ".xlsx",
  ".xls",
];

// The Source Connection page's top-level "kind" — genericizes it beyond
// live SQL databases. jdbc_bridge is a live, vendor-agnostic JDBC connection
// (DB2 for z/OS, an ADABAS SQL Gateway, or any other JDBC-reachable
// mainframe bridge); mainframe_copybook has no live connection at all — it
// wraps the existing COBOL copybook parser so a mainframe flat-file extract
// can be "connected"/"scanned" through the same flow. Derived from
// dbConn.db_type rather than tracked as separate state, so there's a single
// source of truth.
type ConnectionKind = "database" | "jdbc_bridge" | "mainframe_copybook";
const connectionKindOf = (dbType: string): ConnectionKind =>
  dbType === "jdbc_bridge" || dbType === "mainframe_copybook"
    ? dbType
    : "database";

// Salesforce / GovConnect field data types offered in the STTM Target Type
// dropdown. Covers the common scalar, text, numeric, reference, and
// picklist types a mapping can target.
const SF_FIELD_TYPES = [
  "string",
  "textarea",
  "int",
  "double",
  "currency",
  "percent",
  "boolean",
  "date",
  "datetime",
  "time",
  "email",
  "phone",
  "url",
  "picklist",
  "multipicklist",
  "reference",
  "id",
  "base64",
  "address",
  "location",
];

// Salesforce standard Text fields cap at 255 chars (Long Text Area/"textarea"
// fields go far higher) — mirrors agents/data_converter/processors/table_mapper.py.
const SF_TEXT_MAX_LENGTH = 255;

function clampTargetLength(
  targetType: string,
  length: number | null | undefined,
): number | null {
  if (length == null) return length ?? null;
  return targetType === "string" && length > SF_TEXT_MAX_LENGTH
    ? SF_TEXT_MAX_LENGTH
    : length;
}

// The file-source /build-task endpoint's own request schema (BuildFileTaskRequest
// in ztd_app/api/modules/data_converter/schemas.py) only accepts
// fail/replace/append — "truncate" is a load-time-only value on the agent's
// own LoadTarget model, reached instead by mutating the already-built task
// object's target.if_exists after the build call returns (see both call
// sites). Passing "truncate" straight into the build request 422s.
function buildSafeIfExists(
  v: "fail" | "replace" | "append" | "truncate",
): "fail" | "replace" | "append" {
  return v === "truncate" ? "append" : v;
}

// Rendered next to the action that raised it (not a page-top global banner),
// so it stays visually anchored to the button the user just clicked; long
// messages (e.g. a full SQL error) scroll within a capped height instead of
// stretching the layout.
function InlineErrorBanner({
  message,
  onDismiss,
}: {
  message: string;
  onDismiss: () => void;
}) {
  return (
    <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
      <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
      <span className="max-h-40 overflow-y-auto whitespace-pre-wrap break-words">
        {message}
      </span>
      <button
        onClick={onDismiss}
        aria-label="Dismiss error"
        className="ml-auto flex-shrink-0 text-red-300/70 hover:text-red-200 transition"
      >
        <X size={14} />
      </button>
    </div>
  );
}

// Bulk folder ingest's per-file target table name when "one table per file"
// is selected (the Bronze-style case: N distinct source extracts, each its
// own target table, not N parts of the same table) — mirrors the sanitizer
// the single-file flow already applies to its auto-suggested table name.
function sanitizeTableNameFromFilename(filename: string): string {
  return filename.replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9_]+/g, "_");
}

// The auto-mapping heuristic guesses a target type per field (date/boolean/
// double/etc.), but those guesses are frequently wrong for a migration
// staging pass and cause avoidable DDL/type-cast failures downstream. Default
// every field's target type to "string" when a mapping is freshly generated
// for editing — the operator can still override any individual field's type
// in the STTM Review & Export table.
function withDefaultStringTypes(
  list: TableObjectMapping[],
): TableObjectMapping[] {
  return list.map((m) => ({
    ...m,
    field_mappings: m.field_mappings.map((fm) => ({
      ...fm,
      target_type: "string",
    })),
  }));
}

// Adapts a scanned RDBMS schema into the same SalesforceObjectMeta/
// SalesforceField shape the mapping pipeline already understands, so a
// plain-database Target Platform connection can reuse Continue-to-Mapping,
// Generate Mappings, DDL, and Load unchanged (see /data-converter/target/scan
// + converterTargetScan).
// Mirrors table_mapper.py's _DATE_TIME_TARGET_LENGTH/_is_date_time_source_type
// — a raw DATE/TIMESTAMP column's own reported length is an internal storage
// byte count, not a meaningful staging VARCHAR2 size.
const DATE_TIME_SOURCE_TYPE_PATTERN = /date|timestamp|^time\b/i;

/** A file's own header/table name (e.g. "$ Amount", "Grade Level") is fine
 * as a display label but not as an actual target column/table identifier —
 * the load scripts generated downstream (CREATE TABLE / INSERT) need every
 * one to be a safe, unquoted SQL name. Collapses any run of characters
 * outside [A-Za-z0-9_] into a single underscore, trims stray leading/
 * trailing underscores, and guards against a leading digit or an entirely-
 * invalid/empty result. Already-safe names pass through unchanged. */
function sanitizeTargetIdentifier(name: string): string {
  const collapsed = name
    .trim()
    .replace(/[^A-Za-z0-9_]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (!collapsed) return "COLUMN";
  return /^[0-9]/.test(collapsed) ? `_${collapsed}` : collapsed;
}

/** Two distinct source names (e.g. two degenerate columns like "$$$" and
 * "###", or simply two names that collapse to the same sanitized form)
 * can both sanitize to the identical identifier via sanitizeTargetIdentifier
 * — most visibly its "COLUMN" empty-result fallback. Left alone, that's a
 * silent duplicate-column bug in the generated DDL/load script. Call this
 * on a full list of already-sanitized names (in original order) to
 * suffix every collision after the first occurrence (_2, _3, ...) so
 * every identifier in the returned list is unique. */
function dedupeSanitizedIdentifiers(names: string[]): string[] {
  const seenCounts = new Map<string, number>();
  return names.map((name) => {
    const priorCount = seenCounts.get(name) ?? 0;
    seenCounts.set(name, priorCount + 1);
    return priorCount === 0 ? name : `${name}_${priorCount + 1}`;
  });
}

/** "Use source table as-is" mapping pairs skip target-object matching
 * entirely — there's no suitable target object (or none is wanted), so
 * every source column carries straight through under its own name/shape
 * instead. Mirrors table_mapper.py's _string_target_length (min(length,
 * 255)) and date/time default so DDL sizing matches the same convention a
 * normal generated mapping would use, keeping the resulting staging
 * table a faithful structural clone of the source table.
 *
 * `targetNameOverride` lets the user rename the auto-derived target table
 * (see the STTM Review "Rename" action and the pairing screen's editable
 * "Same as source" field) — the sanitized form becomes target_object (the
 * real DDL/load identifier) while the raw override stays target_label (the
 * display name), same split every other target_object/target_label pair
 * in this file already uses. */
function buildIdentityMapping(
  table: TableMeta,
  targetNameOverride?: string,
): TableObjectMapping {
  const targetDisplayName = targetNameOverride?.trim() || table.table_name;
  const dedupedTargetFields = dedupeSanitizedIdentifiers(
    table.columns.map((col) => sanitizeTargetIdentifier(col.name)),
  );
  return {
    source_table: table.table_name,
    source_schema: table.schema_name,
    target_object: sanitizeTargetIdentifier(targetDisplayName),
    target_label: targetDisplayName,
    confidence: 1,
    source_file: table.source_file ?? "",
    source_connection_id: table.source_connection_id,
    field_mappings: table.columns.map((col, i) => {
      const isDateTime = DATE_TIME_SOURCE_TYPE_PATTERN.test(
        col.data_type || "",
      );
      const targetLength = isDateTime
        ? 26
        : Math.min(col.length ?? col.precision ?? 255, 255);
      return {
        source_column: col.name,
        source_type: col.data_type,
        target_field: dedupedTargetFields[i],
        target_type: "string",
        target_length: targetLength,
        target_field_type: "",
        picklist_values: [],
        reference_to: [],
        transformation: "Direct copy",
        validation_rule: "",
        notes:
          "Loaded as-is — no target object mapping; source column carried through unchanged.",
        confidence: 1,
        status: "Mapped" as const,
      };
    }),
  };
}

function tablesToTargetObjects(tables: TableMeta[]): SalesforceObjectMeta[] {
  return tables.map((t) => ({
    api_name: t.table_name,
    label: t.table_name,
    object_type: "custom",
    fields: t.columns.map((c) => ({
      api_name: c.name,
      label: c.name,
      data_type: c.data_type,
      length: c.length ?? null,
      required: !c.nullable,
      nillable: c.nullable,
      unique: c.is_primary_key,
      custom: false,
      external_id: c.is_primary_key,
      id_lookup: c.is_primary_key,
      // Carries the scanned FK constraint ("OTHER_TABLE.COLUMN") over as a
      // lookup-style reference_to, so a real RDBMS target's referential
      // integrity feeds the same FK-aware sequential load ordering a CRM
      // target's lookup/master-detail fields already do.
      reference_to:
        c.is_foreign_key && c.fk_references
          ? [c.fk_references.split(".")[0]]
          : [],
    })),
  }));
}

/** "Import Target Model" — unlike buildIdentityMapping (mirror mode, which
 * collapses every column to a "string" staging type on purpose), this
 * keeps each column's own declared type/length from the imported DDL/
 * schema file: the whole point of importing a real target model is to
 * create the target database with THOSE types, not a generic staging
 * shape. `source_table`/`source_column`/`source_type` are set to the
 * imported table's own identity purely as required placeholders for
 * /ddl/generate's mapping shape — this runs before any real source has
 * been paired against it. */
function buildImportedTargetMapping(table: TableMeta): TableObjectMapping {
  return {
    source_table: table.table_name,
    source_schema: table.schema_name,
    target_object: table.table_name,
    target_label: table.table_name,
    confidence: 1,
    source_file: table.source_file ?? "",
    field_mappings: table.columns.map((col) => ({
      source_column: col.name,
      source_type: col.data_type,
      target_field: col.name,
      target_type: col.data_type,
      target_length: col.length ?? col.precision ?? null,
      target_field_type: "",
      picklist_values: [],
      reference_to: [],
      transformation: "Direct copy",
      validation_rule: "",
      notes:
        "Imported target model — column created with its own declared type.",
      confidence: 1,
      status: "Mapped" as const,
    })),
  };
}

// Shared by handleCreateImportedTargetModel and handleExecuteDdl: a table
// reconcile reports "up_to_date" for is left alone (no DROP, no CREATE) --
// everything else (not created yet, or drifted) actually executes. A table
// reconcile didn't report on at all (e.g. outside this mapping set) is
// treated as "create", the same safe default both call sites used before
// this helper existed.
function filterDdlStatementsToExecute(
  statements: DdlStatement[],
  reconcileResults: DdlReconcileResult[],
): DdlStatement[] {
  const actionByTable = new Map(
    reconcileResults.map((r) => [r.table_name, r.action]),
  );
  return statements.filter(
    (s) => actionByTable.get(s.table_name) !== "up_to_date",
  );
}

function ProgressBar({
  steps,
  current,
  onStepClick,
  labelOverrides,
}: {
  steps: Step[];
  current: Step;
  onStepClick?: (step: Step) => void;
  // Per-step label override, keyed by step id — used for the RDBMS direct-
  // load flow, where the shared "load" step's usual "Load Data to Staging"
  // label would be misleading (there's no staging table in that flow at
  // all, see effectiveActionSteps/stagedTargetTable).
  labelOverrides?: Partial<Record<Step, string>>;
}) {
  // Scoped to the current action's step subset (ACTION_STEPS[action]) rather
  // than one global walk — preserves STEPS' relative order since each
  // action's list is already an ordered subset of it.
  const scoped = STEPS.filter((s) => steps.includes(s.id));
  const idx = steps.indexOf(current);
  return (
    <div className="flex items-center gap-0 mb-4">
      {scoped.map((step, i) => {
        const done = i < idx;
        const active = i === idx;
        // The last step in the current action's subset is always reachable
        // (mirrors the old flow letting you jump straight to a final step
        // regardless of how far you'd progressed) — everything else only
        // once visited.
        const clickable = done || i === scoped.length - 1;
        return (
          <div
            key={step.id}
            className="flex items-center flex-1 last:flex-none"
          >
            <div
              onClick={() => clickable && onStepClick?.(step.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                active
                  ? "bg-brand-500/20 text-brand-400 border border-brand-500/40"
                  : done
                    ? "text-brand-400 cursor-pointer hover:bg-brand-500/10"
                    : clickable
                      ? "text-white/50 cursor-pointer hover:bg-white/5"
                      : "text-white/30"
              }`}
            >
              {done ? (
                <CheckCircle2 size={14} className="text-brand-400" />
              ) : (
                step.icon
              )}
              <span className="hidden sm:block">
                {labelOverrides?.[step.id] ?? step.label}
              </span>
            </div>
            {i < scoped.length - 1 && (
              <div
                className={`h-px flex-1 mx-1 transition-all ${done ? "bg-brand-500/50" : "bg-white/10"}`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

interface SearchableFieldOption {
  value: string;
  label: string;
  badge?: string;
  sublabel?: string;
}

/** Compact, searchable field picker for table cells — type to filter by label
 * or api name; click (or Enter on a single match) to select. Falls back to
 * showing the raw current value even if it isn't among the options, so a
 * custom/unknown field is never silently dropped.
 *
 * The option list renders through a portal into document.body, positioned via
 * getBoundingClientRect(). These pickers live inside scrollable/overflow-clipped
 * table wrappers (mapping tables can run to dozens of rows), and a plain
 * `position: absolute` dropdown gets visually clipped by those ancestors —
 * fields further down an object's field list (e.g. Birthdate on Contact) would
 * still match a search but render outside the visible/clipped area, making
 * them appear "missing". The portal escapes that clipping entirely. */
function FieldSearchSelect({
  value,
  options,
  onChange,
  onCreateNew,
  createNameSuggestion,
  createLabel,
  emptyLabel = "No matching fields",
  placeholder,
  className,
}: {
  value: string;
  options: SearchableFieldOption[];
  onChange: (value: string) => void;
  // When set, a typed name that matches no existing option offers a
  // "+ Create field" action instead of just "No matching fields" — for a
  // source column (e.g. from a joined table) that has nothing to map onto
  // yet in the target.
  onCreateNew?: (value: string) => void;
  // Pre-fills the search box with this name (typically the source column)
  // when opened with no value picked yet, so "Create field" is offered
  // immediately without the user having to type the name themselves.
  createNameSuggestion?: string;
  // Overrides the default "Create field "X" in target table" wording — for
  // reuse where onCreateNew means "use this typed text as-is" rather than
  // actually creating a field (e.g. a hardcoded/manually-typed source column).
  createLabel?: (typed: string) => string;
  // Shown when the query matches nothing (and nothing can be created) --
  // for pickers whose options aren't fields, e.g. source tables.
  emptyLabel?: string;
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [rect, setRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const updateRect = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.bottom, left: r.left, width: r.width });
  }, []);

  useEffect(() => {
    if (!open) return;
    updateRect();
    const onDocMouseDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setQuery("");
      }
    };
    const onReposition = () => updateRect();
    document.addEventListener("mousedown", onDocMouseDown);
    // capture:true catches scroll on any nested scrollable ancestor, not just window
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [open, updateRect]);

  const optionText = (o: SearchableFieldOption) =>
    o.value === "" || o.label === o.value ? o.label : `${o.label} — ${o.value}`;

  const selected = options.find((o) => o.value === value);
  const displayValue = open ? query : selected ? optionText(selected) : value;

  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          o.value.toLowerCase().includes(q),
      )
    : options;
  const canCreate =
    !!onCreateNew &&
    !!query.trim() &&
    !options.some((o) => o.value.toLowerCase() === q);

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search
          size={11}
          className="absolute left-2 top-1/2 -translate-y-1/2 text-white/25 pointer-events-none"
        />
        <input
          ref={inputRef}
          value={displayValue}
          title={displayValue}
          onFocus={() => {
            setOpen(true);
            setQuery(
              !value && createNameSuggestion ? createNameSuggestion : "",
            );
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              close();
              (e.target as HTMLInputElement).blur();
            } else if (e.key === "Enter" && filtered.length === 1) {
              onChange(filtered[0].value);
              close();
              (e.target as HTMLInputElement).blur();
            } else if (
              e.key === "Enter" &&
              filtered.length === 0 &&
              canCreate
            ) {
              onCreateNew!(query.trim());
              close();
              (e.target as HTMLInputElement).blur();
            }
          }}
          placeholder={placeholder}
          className={`${className ?? ""} pl-5 truncate`}
        />
      </div>
      {open &&
        rect &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: rect.top + 4,
              left: rect.left,
              width: rect.width,
            }}
            className="z-50 max-h-56 overflow-y-auto rounded-lg border border-white/10 bg-surface-500 shadow-xl"
          >
            {filtered.length === 0 && !canCreate && (
              <div className="px-2 py-1.5 text-xs text-white/30">
                {emptyLabel}
              </div>
            )}
            {filtered.map((o) => (
              <button
                type="button"
                key={o.value}
                onMouseDown={(e) => {
                  // Stop this from reaching the document-level listener above
                  // (which would close the dropdown before onClick fires) and
                  // from moving focus off the input.
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={() => {
                  onChange(o.value);
                  close();
                }}
                title={optionText(o)}
                className={`w-full flex items-center gap-1 px-2 py-1 text-left text-xs hover:bg-white/10 transition ${
                  o.value === value
                    ? "bg-brand-500/10 text-brand-300"
                    : "text-white/80"
                }`}
              >
                {o.badge && <span className="flex-shrink-0">{o.badge}</span>}
                <span className="truncate font-mono">{optionText(o)}</span>
                {o.sublabel && (
                  <span className="ml-auto text-[11px] text-white/30 flex-shrink-0">
                    {o.sublabel}
                  </span>
                )}
              </button>
            ))}
            {canCreate && (
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={() => {
                  onCreateNew!(query.trim());
                  close();
                }}
                className="w-full flex items-center gap-1.5 px-2 py-1 text-left text-xs text-emerald-300 hover:bg-emerald-500/10 transition border-t border-white/10"
              >
                <Plus size={11} className="flex-shrink-0" />
                <span className="truncate">
                  {createLabel
                    ? createLabel(query.trim())
                    : `Create field "${query.trim()}" in target table`}
                </span>
              </button>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}

/** A single-line cell that opens a centered modal with a larger textarea to
 * edit its value — used for "Transform" / "Value Map" cells where the full
 * logic doesn't fit (or isn't easy to review) in a cramped table input.
 * Edits are local to the modal until "Save"; closing/Escape discards them. */
function ExpandableTextField({
  value,
  onChange,
  title,
  placeholder,
  hint,
  valueClassName,
  compact,
}: {
  value: string;
  onChange: (value: string) => void;
  title: string;
  placeholder?: string;
  hint?: string;
  valueClassName?: string;
  compact?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);

  const openModal = () => {
    setDraft(value);
    setOpen(true);
  };
  const close = () => setOpen(false);
  const save = () => {
    onChange(draft);
    setOpen(false);
  };

  return (
    <>
      {compact ? (
        <button
          type="button"
          onClick={openModal}
          title={value || placeholder || "Click to expand"}
          className={`w-full h-[22px] flex items-center justify-center bg-surface-400/60 border border-white/10 rounded hover:border-brand-500/40 transition ${value ? "text-brand-400" : "text-white/25"}`}
        >
          <Maximize2 size={11} />
        </button>
      ) : (
        <button
          type="button"
          onClick={openModal}
          title="Click to expand"
          className={`w-full h-[22px] flex items-center gap-1 px-2 bg-surface-400/60 border border-white/10 rounded text-xs text-left leading-none hover:border-brand-500/40 transition ${valueClassName ?? "text-white/70"}`}
        >
          <span
            className={`flex-1 min-w-0 truncate ${value ? "" : "text-white/20"}`}
          >
            {value || placeholder || "—"}
          </span>
          <Maximize2 size={10} className="flex-shrink-0 text-white/25" />
        </button>
      )}
      {open &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60" onClick={close} />
            <div className="relative w-full max-w-lg bg-surface-500 border border-white/10 rounded-xl shadow-2xl p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white">{title}</h3>
                <button
                  onClick={close}
                  className="p-1 rounded-lg hover:bg-white/5 text-white/50 hover:text-white transition"
                >
                  <X size={16} />
                </button>
              </div>
              {hint && <p className="text-[11px] text-white/40">{hint}</p>}
              <textarea
                autoFocus
                rows={5}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") close();
                  else if (e.key === "Enter" && (e.metaKey || e.ctrlKey))
                    save();
                }}
                placeholder={placeholder}
                className="w-full px-3 py-2 bg-surface-400/60 border border-white/10 rounded-lg text-sm text-white/90 font-mono focus:outline-none focus:border-brand-500/60 resize-y"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={close}
                  className="ztd-btn ztd-btn-secondary text-xs"
                >
                  Cancel
                </button>
                <button
                  onClick={save}
                  className="ztd-btn ztd-btn-primary text-xs"
                >
                  Save
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

/** Multi-select counterpart to FieldSearchSelect — search-to-filter, click a
 * row to toggle it in/out of the selection (stays open across clicks so
 * several items can be picked in one go), same portal-positioning approach
 * so it isn't clipped by a scrollable ancestor. Used to bulk-select several
 * source tables and several target objects before adding them as mapping
 * pairs in one step, instead of adding + configuring one pair at a time. */
function FieldMultiSearchSelect({
  values,
  options,
  onChange,
  placeholder,
  className,
}: {
  values: string[];
  options: SearchableFieldOption[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [rect, setRect] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const updateRect = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.bottom, left: r.left, width: r.width });
  }, []);

  useEffect(() => {
    if (!open) return;
    updateRect();
    const onDocMouseDown = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setQuery("");
      }
    };
    const onReposition = () => updateRect();
    document.addEventListener("mousedown", onDocMouseDown);
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [open, updateRect]);

  const optionText = (o: SearchableFieldOption) =>
    o.value === "" || o.label === o.value ? o.label : `${o.label} — ${o.value}`;

  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          o.value.toLowerCase().includes(q),
      )
    : options;

  const toggle = (v: string) => {
    onChange(
      values.includes(v) ? values.filter((x) => x !== v) : [...values, v],
    );
  };

  const displayValue =
    values.length === 0
      ? ""
      : values.length === 1
        ? (options.find((o) => o.value === values[0])?.label ?? values[0])
        : `${values.length} selected`;

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search
          size={11}
          className="absolute left-2 top-1/2 -translate-y-1/2 text-white/25 pointer-events-none"
        />
        <input
          ref={inputRef}
          value={open ? query : displayValue}
          readOnly={!open}
          onFocus={() => {
            setOpen(true);
            setQuery("");
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setOpen(false);
              setQuery("");
              (e.target as HTMLInputElement).blur();
            }
          }}
          placeholder={placeholder}
          className={`${className ?? ""} pl-5 truncate`}
        />
      </div>
      {open &&
        rect &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: rect.top + 4,
              left: rect.left,
              width: rect.width,
            }}
            className="z-50 max-h-56 overflow-y-auto rounded-lg border border-white/10 bg-surface-500 shadow-xl"
          >
            {filtered.length === 0 && (
              <div className="px-2 py-1.5 text-xs text-white/30">
                No matching options
              </div>
            )}
            {filtered.map((o) => (
              <button
                type="button"
                key={o.value}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={() => toggle(o.value)}
                className={`w-full flex items-center gap-2 px-2 py-1 text-left text-xs hover:bg-white/10 transition ${
                  values.includes(o.value)
                    ? "bg-brand-500/10 text-brand-300"
                    : "text-white/80"
                }`}
              >
                <span
                  className={`flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center ${
                    values.includes(o.value)
                      ? "bg-brand-500 border-brand-500"
                      : "border-white/30"
                  }`}
                >
                  {values.includes(o.value) && (
                    <Check size={10} className="text-black" />
                  )}
                </span>
                {o.badge && <span className="flex-shrink-0">{o.badge}</span>}
                <span className="truncate font-mono">{optionText(o)}</span>
                {o.sublabel && (
                  <span className="ml-auto text-[11px] text-white/30 flex-shrink-0">
                    {o.sublabel}
                  </span>
                )}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

/** Handover card for the mapping step: any real profiling need (PII
 * detection, data quality scoring, column stats, ...) is the Data Profiler
 * agent's job, not the Converter's — this just deep-links there instead of
 * duplicating that agent's connect/run/results flow inline. */
function ProfileSourceDataCard({
  schemaResult,
  projectId,
}: {
  schemaResult: SchemaExtractResult | null;
  projectId?: string;
}) {
  if (!schemaResult || schemaResult.tables.length === 0 || !projectId)
    return null;

  return (
    <div className="p-3 bg-white/3 border border-white/8 rounded-xl flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2 text-white/50">
        <Microscope size={14} />
        <span className="text-xs text-white/60">
          Need PII detection, data quality scoring, or column profiling on these
          source tables? Hand off to the Data Profiler agent.
        </span>
      </div>
      <button
        onClick={() =>
          openAgentInNewTab(`/projects/${projectId}/agents/data-profiler`)
        }
        className="ztd-btn ztd-btn-secondary ztd-btn-sm flex-shrink-0"
      >
        <Microscope size={12} /> Open Data Profiler
        <ArrowUpRight size={12} className="text-white/40" />
      </button>
    </div>
  );
}

/** Project-level override for which functional-domain knowledge pack
 * (Child Support, Child Welfare, Integrated Eligibility, Medicaid, Higher
 * Education, ...) biases mapping suggestions toward that domain's own
 * vocabulary (see ztd_app.tools.converter.functional_skills). Left on
 * "Auto-detect" (the empty selection, and this project's default), the
 * agent picks the best-matching domain itself from the scanned schema
 * (auto_select_functional_skills — requires >=3 confident vocabulary
 * matches, and picks nothing if it can't clear that bar). A functional
 * skill is single-select here, mirroring that same function's "at most
 * one domain" contract; any separately-selected Integration Skills
 * (Metadata Curator, Data Profiling, ...) live in the same project-skills
 * list server-side but are left untouched by this control. */
function FunctionalSkillsPicker({ projectId }: { projectId?: string }) {
  const [available, setAvailable] = useState<ConverterSkillInfo[]>([]);
  const [allSelected, setAllSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiv2
      .converterListSkills()
      .then((skills) => {
        if (!cancelled) setAvailable(skills);
      })
      .catch(() => {
        if (!cancelled) setAvailable([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!projectId) {
      setAllSelected([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiv2
      .converterGetProjectSkills(projectId)
      .then((res) => {
        if (!cancelled) setAllSelected(res.skills);
      })
      .catch(() => {
        if (!cancelled) setAllSelected([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (!projectId || available.length === 0) return null;

  const availableSlugs = new Set(available.map((s) => s.slug));
  const selectedDomainSlug =
    allSelected.find((s) => availableSlugs.has(s)) ?? "";

  const handleSelect = async (slug: string) => {
    if (!projectId) return;
    const nonDomain = allSelected.filter((s) => !availableSlugs.has(s));
    const next = slug ? [...nonDomain, slug] : nonDomain;
    setSaving(true);
    try {
      const res = await apiv2.converterSetProjectSkills(projectId, next);
      setAllSelected(res.skills);
    } catch {
      // Best-effort — keep showing the prior selection on save failure
      // rather than silently reverting the dropdown out from under the user.
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-3 bg-white/3 border border-white/8 rounded-xl space-y-2">
      <div className="flex items-center gap-2">
        <Sparkles size={14} className="text-white/50" />
        <span className="text-xs font-semibold text-white/70 uppercase tracking-wider">
          Functional Skill
        </span>
        {(loading || saving) && (
          <Loader2 size={12} className="animate-spin text-white/40" />
        )}
      </div>
      <p className="text-xs text-white/50">
        Bias mapping suggestions toward a specific domain's vocabulary. Leave on
        "Auto-detect" to let the agent pick the best match from your scanned
        schema, or choose one to override it.
      </p>
      <select
        value={selectedDomainSlug}
        onChange={(e) => void handleSelect(e.target.value)}
        disabled={saving}
        title="Functional-domain skill applied to mapping suggestions"
        className="px-2.5 py-1.5 rounded-lg bg-surface-400/50 border border-white/10 text-xs text-white focus:outline-none focus:border-brand-500/50 max-w-sm w-full"
      >
        <option value="">Auto-detect (default)</option>
        {available.map((s) => (
          <option key={s.slug} value={s.slug} title={s.description}>
            {s.label}
          </option>
        ))}
      </select>
    </div>
  );
}

const KNOWLEDGE_DOC_TYPE_LABELS: Record<ProjectKnowledgeDocType, string> = {
  source_data_dictionary: "Source Data Dictionary",
  target_data_dictionary: "Target Data Dictionary",
  source_data_model: "Source Data Model",
  target_data_model: "Target Data Model",
  source_functional_spec: "Source Functional Spec",
  target_functional_spec: "Target Functional Spec",
  other: "Other",
};

/** Upload reference documents (source/target data dictionaries, data
 * models, functional specs, or any other supporting material) for a
 * project. These are stored at the project level (not scoped to Data
 * Converter) so any agent working on this project can use them. Content
 * is matched deterministically via plain-text search (agents.
 * data_converter.processors.table_mapper.apply_knowledge_base_hints for
 * the mapping generator) — it is never sent to an LLM. Purely additive:
 * mapping generation works exactly as before when nothing is uploaded. */
function MappingKnowledgeBaseCard({ projectId }: { projectId?: string }) {
  const [docs, setDocs] = useState<ProjectKnowledgeDocInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState<ProjectKnowledgeDocType>(
    "source_data_dictionary",
  );
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(() => {
    if (!projectId) {
      setDocs([]);
      return;
    }
    setLoading(true);
    apiv2
      .projectListKnowledgeDocs(projectId)
      .then((res) => setDocs(res))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [projectId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!projectId) return null;

  const handleFileChosen = async (file: File | undefined) => {
    if (!file || !projectId) return;
    setError(null);
    setUploading(true);
    try {
      await apiv2.projectUploadKnowledgeDoc(projectId, file, docType);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (docId: string) => {
    if (!projectId) return;
    try {
      await apiv2.projectDeleteKnowledgeDoc(projectId, docId);
      setDocs((prev) => prev.filter((d) => d.id !== docId));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
    }
  };

  return (
    <div className="p-4 bg-white/3 border border-white/8 rounded-xl space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <FileIcon size={14} className="text-white/50" />
            <span className="text-xs font-semibold text-white/70 uppercase tracking-wider">
              Project Knowledge Base
            </span>
            {(loading || uploading) && (
              <Loader2 size={12} className="animate-spin text-white/40" />
            )}
          </div>
          <p className="text-[11px] text-white/40 mt-1 max-w-2xl">
            Upload a source or target data dictionary, data model, functional
            documentation, or any other reference material — the mapping
            generator matches this deterministically (plain- text search, no
            AI/LLM) to fill in or confirm table mappings. Documents are stored
            at the project level, so other agents can use them too.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <select
            value={docType}
            onChange={(e) =>
              setDocType(e.target.value as ProjectKnowledgeDocType)
            }
            disabled={uploading}
            title="Document type"
            className="px-2.5 py-1.5 rounded-lg bg-surface-400/50 border border-white/10 text-xs text-white focus:outline-none focus:border-brand-500/50"
          >
            {(
              Object.keys(
                KNOWLEDGE_DOC_TYPE_LABELS,
              ) as ProjectKnowledgeDocType[]
            ).map((t) => (
              <option key={t} value={t}>
                {KNOWLEDGE_DOC_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,.md"
            className="hidden"
            onChange={(e) => void handleFileChosen(e.target.files?.[0])}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="ztd-btn ztd-btn-secondary ztd-btn-sm"
          >
            {uploading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Upload size={12} />
            )}
            Upload Document
          </button>
        </div>
      </div>

      {error && (
        <p className="text-[11px] text-red-400 flex items-center gap-1">
          <AlertTriangle size={11} /> {error}
        </p>
      )}

      {docs.length > 0 && (
        <div className="space-y-1.5">
          {docs.map((d) => (
            <div
              key={d.id}
              className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-surface-400/30 border border-white/8 rounded-lg"
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileIcon size={12} className="text-white/40 flex-shrink-0" />
                <span className="text-xs text-white/80 truncate">
                  {d.filename}
                </span>
                <span className="text-[10px] text-white/40 flex-shrink-0">
                  {KNOWLEDGE_DOC_TYPE_LABELS[d.doc_type]}
                </span>
              </div>
              <button
                onClick={() => void handleDelete(d.id)}
                title="Remove document"
                className="text-white/30 hover:text-red-400 flex-shrink-0"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SectionCard({
  title,
  actions,
  children,
  defaultCollapsed = false,
}: {
  title: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  defaultCollapsed?: boolean;
}) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  // 2026-09-13, issue #623: p-5/space-y-3 (was p-6/space-y-4) — shared by
  // every Data Converter screen, so this one change tightens whitespace
  // consistently everywhere instead of per-screen.
  return (
    <div className="bg-surface-300/40 border border-white/8 rounded-xl p-5 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={() => setCollapsed((v) => !v)}
          className="flex items-center gap-2 text-left shrink-0 group"
        >
          {collapsed ? (
            <ChevronRight
              size={14}
              className="text-white/40 group-hover:text-white/70 transition flex-shrink-0"
            />
          ) : (
            <ChevronDown
              size={14}
              className="text-white/40 group-hover:text-white/70 transition flex-shrink-0"
            />
          )}
          <h3 className="text-sm font-semibold text-white/80 uppercase tracking-wider truncate">
            {title}
          </h3>
        </button>
        {actions}
      </div>
      {!collapsed && children}
    </div>
  );
}

// Shared by the SAP BODS dataflow and Informatica PowerCenter mapping import
// cards below -- both flows are structurally identical (choose file(s)/folder,
// parse, download the same four document types), differing only in copy,
// state, and the parse handler.
interface LegacyXmlImportDoc {
  download_url: string;
  filename: string;
}

interface LegacyXmlImportResult {
  mapping_count: number;
  total_mapping_rows: number;
  table_count: number;
  source_table_count: number;
  target_table_count: number;
  warnings: string[];
  sttm: LegacyXmlImportDoc;
  source_ddl: LegacyXmlImportDoc;
  target_ddl: LegacyXmlImportDoc;
  audit: LegacyXmlImportDoc;
}

function LegacyXmlImportCard({
  title,
  description,
  files,
  setFiles,
  parsing,
  onParse,
  parseButtonLabel,
  result,
}: {
  title: string;
  description: React.ReactNode;
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  parsing: boolean;
  onParse: () => void;
  parseButtonLabel: string;
  result: LegacyXmlImportResult | null;
}) {
  return (
    <SectionCard title={title}>
      <p className="text-xs text-white/40 mb-3">{description}</p>
      {files.length > 0 && (
        <ul className="mb-3 space-y-1">
          {files.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              className="flex items-center justify-between gap-2 text-xs text-white/60 bg-white/5 rounded-lg px-2.5 py-1"
            >
              <span className="truncate">{f.name}</span>
              <button
                onClick={() =>
                  setFiles((prev) => prev.filter((_, idx) => idx !== i))
                }
                className="text-white/40 hover:text-red-400 flex-shrink-0"
                aria-label={`Remove ${f.name}`}
              >
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        <label className="ztd-btn ztd-btn-secondary cursor-pointer">
          <Upload size={12} />
          Choose File(s)
          <input
            type="file"
            multiple
            accept=".xml"
            className="hidden"
            onChange={(e) => {
              const picked = Array.from(e.target.files ?? []);
              setFiles((prev) => [...prev, ...picked]);
              e.target.value = "";
            }}
          />
        </label>
        <label className="ztd-btn ztd-btn-secondary cursor-pointer">
          <Upload size={12} />
          Choose Folder
          <input
            type="file"
            multiple
            {...({
              webkitdirectory: "",
              directory: "",
            } as Record<string, string>)}
            className="hidden"
            onChange={(e) => {
              const picked = Array.from(e.target.files ?? []).filter((f) =>
                f.name.toLowerCase().endsWith(".xml"),
              );
              setFiles((prev) => [...prev, ...picked]);
              e.target.value = "";
            }}
          />
        </label>
        <button
          onClick={onParse}
          disabled={parsing || files.length === 0}
          className="ztd-btn ztd-btn-primary flex-shrink-0"
        >
          {parsing ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <ArrowRight size={14} />
          )}
          {parseButtonLabel}
          {files.length > 1 ? ` (${files.length} files)` : ""}
        </button>
      </div>
      {result && (
        <div className="mt-4 border-t border-white/10 pt-3">
          <p className="text-xs text-white/60 mb-2">
            {result.mapping_count} table mapping(s), {result.total_mapping_rows}{" "}
            field row(s), from {result.table_count} table(s) found (
            {result.source_table_count} source, {result.target_table_count}{" "}
            target).
          </p>
          {result.warnings.length > 0 && (
            <ul className="mb-2 space-y-0.5">
              {result.warnings.map((w, i) => (
                <li key={i} className="text-xs text-amber-400/80">
                  ⚠ {w}
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center gap-3 flex-wrap">
            <a
              href={result.sttm.download_url}
              download={result.sttm.filename}
              className="ztd-btn ztd-btn-secondary"
            >
              <FileSpreadsheet size={14} /> STTM Mapping
            </a>
            <a
              href={result.source_ddl.download_url}
              download={result.source_ddl.filename}
              className="ztd-btn ztd-btn-secondary"
            >
              <FileText size={14} /> Source DDL
            </a>
            <a
              href={result.target_ddl.download_url}
              download={result.target_ddl.filename}
              className="ztd-btn ztd-btn-secondary"
            >
              <FileText size={14} /> Target DDL
            </a>
            <a
              href={result.audit.download_url}
              download={result.audit.filename}
              className="ztd-btn ztd-btn-secondary"
            >
              <Download size={14} /> Audit Workbook
            </a>
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function InputField({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  required,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  hint?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-white/50 mb-1.5">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 bg-surface-400/50 border border-white/10 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 transition"
      />
      {hint && <p className="mt-1 text-[10px] text-white/30">{hint}</p>}
    </div>
  );
}

function StatusBadge({
  status,
  confidence,
}: {
  status: string;
  confidence?: number;
}) {
  const colors: Record<string, string> = {
    Mapped: "bg-green-500/20 text-green-300 border-green-500/30",
    Review: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
    Unmapped: "bg-red-500/20 text-red-300 border-red-500/30",
  };
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${colors[status] ?? colors.Review}`}
    >
      {status}
      {confidence !== undefined && ` · ${Math.round(confidence * 100)}%`}
    </span>
  );
}

/** Every `schemaResult.tables` entry with no corresponding row anywhere in
 * `mappingList` — a table is "covered" if it's a plain row's `source_table`
 * or appears in a joined row's `source_tables[]`; a row with zero
 * `field_mappings` left (every field individually removed) counts as NOT
 * covering its table(s) either, so deleting fields one-by-one down to zero
 * has the same effect as deleting the whole row. */
function computeUnmappedTables(
  tables: TableMeta[],
  mappingList: TableObjectMapping[],
): TableMeta[] {
  const covered = new Set<string>();
  for (const m of mappingList) {
    if (m.field_mappings.length === 0) continue;
    const names = m.source_tables?.length ? m.source_tables : [m.source_table];
    names.forEach((n) => covered.add(n));
  }
  return tables.filter((t) => !covered.has(t.table_name));
}

/** Target fields referenced by more than one row within the same table
 * mapping — usually a mistake (two source columns overwriting the same
 * target field on load) and worth flagging for manual review. */
// 2026-09-01, requested by user: any table's field-mapping row count above
// this threshold gets an "Expand to window" button on its inline (400px
// capped) list, reopening the same rows in a full-screen overlay instead.
const LARGE_TABLE_POPOUT_THRESHOLD = 20;

function getDuplicateTargetFields(
  fieldMappings: SttmFieldMapping[],
): Set<string> {
  const counts = new Map<string, number>();
  for (const fm of fieldMappings) {
    if (!fm.target_field) continue;
    counts.set(fm.target_field, (counts.get(fm.target_field) ?? 0) + 1);
  }
  const duplicates = new Set<string>();
  for (const [field, count] of counts) {
    if (count > 1) duplicates.add(field);
  }
  return duplicates;
}

/** Target objects picked by more than one row in the flat mapping-pairs
 * list — sometimes intentional (several source tables feeding one target),
 * but worth flagging so the operator double-checks before generating. */
function getDuplicateTargetObjects(
  pairs: { sourceTable: string; targetObject: string }[],
): Set<string> {
  const counts = new Map<string, number>();
  for (const p of pairs) {
    if (!p.targetObject) continue;
    counts.set(p.targetObject, (counts.get(p.targetObject) ?? 0) + 1);
  }
  const duplicates = new Set<string>();
  for (const [obj, count] of counts) {
    if (count > 1) duplicates.add(obj);
  }
  return duplicates;
}

export default function DataConverterView({ onBack, projectId }: Props) {
  // null = landing page (no action chosen yet). Picking an action sets both
  // this and `step` to that action's first scoped step.
  const [action, setAction] = useState<ConverterAction | null>(null);
  const [step, setStep] = useState<Step>("source");
  // 2026-09-01, requested by user: when Data Loader targets an
  // object-oriented platform, this holds the user's answer to "load
  // through an intermediate staging database, or directly?" — null until
  // chosen (gates a dedicated choice screen, see loaderCrmStrategyPending
  // below), reset to null whenever "loader" is freshly (re-)selected from
  // the landing page, and set eagerly to "staged" by every resume/shortcut
  // path that implies the classic staged flow (all pre-existing sessions'
  // saved state predates "direct" and was always staged).
  const [crmLoadStrategy, setCrmLoadStrategy] = useState<
    "staged" | "direct" | null
  >(null);
  // Gates the "mapping" action behind a choice screen: import an existing
  // mapping document (skip straight to the mapping step's upload UI),
  // build a fresh one (connect-source/schema/connect-target, then
  // Auto-Map or manual pairs — many-to-one joins are now auto-detected
  // server-side, no separate "Complex Mapping" mode to pick). Legacy
  // code/schema artifacts are just one of the source types under
  // "create" (see Source Type: Files/Servers/Legacy Code), not a
  // separate entry choice. null = not yet chosen; reset whenever a new
  // action is picked.
  const [mappingEntryChoice, setMappingEntryChoice] = useState<
    "import" | "create" | null
  >(null);
  // Gates the "loader" action behind a choice screen too: load data into a
  // target already finalized via Source → Target Mapping (the existing
  // ddl/load/sf_load flow, requires scopedMappingStatus === "ready"), or
  // load a file directly into a database table — no STTM mapping involved
  // at all, since the file already dictates its own target table. null =
  // not yet chosen; reset whenever "loader" is freshly (re-)selected.
  // SharePoint moved to Source -> Target Mapping's Legacy Code source type
  // (see legacySourceMethod); PDF is available both there (multi-file,
  // full STTM mapping) AND as this standalone entry point (single file,
  // pick one detected table, no mapping at all) — restored per user
  // request. OneDrive was removed outright (no longer needed).
  const [loaderEntryChoice, setLoaderEntryChoice] = useState<
    "mapped" | "file" | "pdf" | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // DDL step actions each get their own flag — they used to all share
  // `loading`, so clicking any one of Refresh Connection / Generate DDL /
  // Approve All / Create Tables lit up every button's spinner at once.
  const [refreshingConnection, setRefreshingConnection] = useState(false);
  const [generatingDdl, setGeneratingDdl] = useState(false);
  const [resolvingDdlDuplicates, setResolvingDdlDuplicates] = useState(false);
  const [executingDdl, setExecutingDdl] = useState(false);
  const ddlBusy = generatingDdl || resolvingDdlDuplicates;
  // Same fix, applied to every other step's actions that used to share the
  // one generic `loading` flag with unrelated buttons on the same screen.
  const [scanningSchema, setScanningSchema] = useState(false);
  const [refreshingSourceConnection, setRefreshingSourceConnection] =
    useState(false);
  const [connectingMainframe, setConnectingMainframe] = useState(false);
  const [connectingTargetPlatform, setConnectingTargetPlatform] =
    useState(false);
  const [continuingToMapping, setContinuingToMapping] = useState(false);
  const [autoMappingAll, setAutoMappingAll] = useState(false);
  const [generatingMappings, setGeneratingMappings] = useState(false);
  const [generatingSttm, setGeneratingSttm] = useState(false);
  // Loader/Reconciliation/Load to Target all depend on a mapping generated
  // by the Source → Target Mapping action — "missing" drives an empty
  // state directing the user back there instead of rendering a step with
  // nothing to operate on. The Mapping action itself generates its own, so
  // it's never gated on this.
  const [scopedMappingStatus, setScopedMappingStatus] = useState<
    "idle" | "loading" | "ready" | "missing"
  >("idle");

  // Session-resume: each action's own saved artifact (mapping / DDL state /
  // reconciliation results / latest load job), checked once when the user
  // picks that action from the landing page, so re-entering the Converter
  // agent never silently starts over — the user explicitly decides whether
  // to pick up where they left off. Distinct from scopedMappingStatus above,
  // which pulls the *mapping prerequisite* those 3 other actions need to
  // function at all; this is about each action's own prior work product.
  interface ResumeInfo {
    action: ConverterAction;
    summary: string;
    // Only for "mapping" — a list of up to the 5 most recent saves (newest
    // first) to pick from, instead of a single yes/no choice. Each entry's
    // full mappings are fetched on demand once one is actually selected
    // (see handleResumeMappingEntry) rather than all up front.
    mappingHistory?: ConverterMappingHistoryEntryOut[];
    // (loader/reconciliation only, resolved via
    // handleResumeChoice below — mapping never populates these.)
    ddlState?: ConverterDdlStateOut;
    loadJob?: ConverterLatestLoadJobOut;
    loadStatus?: DataLoadStatusResponse;
    reconciliation?: ConverterReconciliationStateOut;
    sfLoadJob?: ConverterLatestLoadJobOut;
    sfLoadStatus?: DbToSfLoadStatusResponse;
    // 2026-09-04, reported by user: choosing "Resume" from
    // jumpToLoadTarget/jumpToLoadDataDirect's resumable-check always fell
    // back to the generic "ddl first" default (resolveLoaderResumeStep)
    // instead of honoring the step those jumps actually intended to land
    // on — e.g. Reconciliation's "Continue to Load to Target" button
    // landed back on the intermediate staging step instead of the final
    // target-load step after a page refresh (crmLoadStrategy resets to
    // null on reload, so the resumable check runs again). Set by whichever
    // jump function initiated the resumable check; handleResumeChoice
    // honors it over the generic default when present.
    intendedStep?: Step;
  }
  const [pendingResumeChoice, setPendingResumeChoice] =
    useState<ResumeInfo | null>(null);
  const [checkingResume, setCheckingResume] = useState(false);
  // 2026-09-13, reported by user: after starting a genuinely new mapping
  // this session (mirror's "Map & Load to a Target", or any other
  // "start fresh" mapping/loader entry point — see resetLoadResultsState),
  // the "loader" action's own resumable-check is scoped by PROJECT only,
  // not by "is this the same mapping as before" — so it kept finding the
  // OLD ddlState/loadJob from an earlier, now-superseded mapping (e.g. the
  // mirror's own initial source→bronze staging load) and either popped a
  // stale "Resume?" dialog or, once "Resume" was picked, hydrated
  // loadProgress from that unrelated old job — showing "Re-load" on the
  // Load Data button for a mapping that has never actually been loaded.
  // Set alongside resetLoadResultsState() at every such entry point so the
  // "loader" branch below treats there as being nothing to resume until
  // this fresh mapping's own load actually runs; a real page reload (the
  // only way to legitimately resume old state) naturally resets this back
  // to false along with every other piece of in-memory state.
  const [skipLoaderResumeCheck, setSkipLoaderResumeCheck] = useState(false);

  const checkResumable = async (
    a: ConverterAction,
    intendedStep?: Step,
  ): Promise<ResumeInfo | null> => {
    if (!projectId) return null;
    try {
      if (a === "mapping") {
        const entries = await apiv2.converterListMappingHistory(projectId);
        if (entries.length === 0) return null;
        return {
          action: a,
          summary: `${entries.length} saved mapping${entries.length !== 1 ? "s" : ""} from previous sessions — pick one to continue from.`,
          mappingHistory: entries,
        };
      }
      if (a === "loader") {
        if (skipLoaderResumeCheck) return null;
        // Folded together (see ConverterAction's own comment): the
        // staging-DB pair (ddlState/loadJob) AND the Load-to-Target job
        // are all checked in one pass now that they share the "loader"
        // action, so a project resuming under the new 3-action UI still
        // surfaces whichever of the two it last worked on.
        const [ddlState, loadJob, sfLoadJob] = await Promise.all([
          apiv2.converterGetDdlState(projectId).catch(() => null),
          apiv2
            .converterGetLatestLoadJob(projectId, "db_to_db")
            .catch(() => null),
          apiv2
            .converterGetLatestLoadJob(projectId, "db_to_sf")
            .catch(() => null),
        ]);
        if (!ddlState && !loadJob && !sfLoadJob) return null;
        const [loadStatus, sfLoadStatus] = await Promise.all([
          loadJob
            ? apiv2
                .converterLoadDataStatus(loadJob.job_id)
                .catch(() => undefined)
            : Promise.resolve(undefined),
          sfLoadJob
            ? apiv2
                .converterLoadDbToSfStatus(sfLoadJob.job_id)
                .catch(() => undefined)
            : Promise.resolve(undefined),
        ]);
        const parts: string[] = [];
        if (ddlState)
          parts.push(`DDL for ${ddlState.ddl_statements.length} table(s)`);
        if (loadJob) parts.push(`a load job (${loadJob.status})`);
        if (sfLoadJob) parts.push(`a Load to Target job (${sfLoadJob.status})`);
        return {
          action: a,
          summary: `${parts.join(", ")} saved from a previous session.`,
          ddlState: ddlState ?? undefined,
          loadJob: loadJob ?? undefined,
          loadStatus,
          sfLoadJob: sfLoadJob ?? undefined,
          sfLoadStatus,
          intendedStep,
        };
      }
      if (a === "reconciliation") {
        const rec = await apiv2
          .converterGetReconciliation(projectId)
          .catch(() => null);
        if (!rec || (!rec.counts_result && !rec.columns_result)) return null;
        return {
          action: a,
          summary: "Reconciliation results saved from a previous session.",
          reconciliation: rec,
        };
      }
      // 2026-09-01, requested by user: standalone "Create Staging Tables"
      // action — only resumes its own DDL state (never loadJob/sfLoadJob,
      // those belong to "loader"'s own resume branch above).
      if (a === "createTables") {
        const ddlState = await apiv2
          .converterGetDdlState(projectId)
          .catch(() => null);
        if (!ddlState) return null;
        return {
          action: a,
          summary: `DDL for ${ddlState.ddl_statements.length} table(s) saved from a previous session.`,
          ddlState,
        };
      }
      return null;
    } catch {
      return null;
    }
  };

  // 2026-09-08, reported by user: Finalize's "Load Data" button
  // (jumpToLoadDataDirect below) only pops the "Resume?" dialog when SOME
  // prior loader state exists -- a brand-new project that never ran
  // Create Staging Tables has none, so the button silently assumed
  // staging tables already existed and jumped straight to the Load Data
  // step; running the load then failed at the database with a "table
  // does not exist" error. Reuses checkResumable("loader")'s own "is
  // there anything to resume" check (rather than duplicating its 3
  // fetches) to gate the button so it's disabled until Create Staging
  // Tables has run at least once for this project.
  const [loaderStateExists, setLoaderStateExists] = useState<boolean | null>(
    null,
  );
  useEffect(() => {
    if (!projectId || step !== "sttm") return;
    let cancelled = false;
    checkResumable("loader").then((info) => {
      if (!cancelled) setLoaderStateExists(!!info);
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, step]);

  const selectAction = async (a: ConverterAction) => {
    if (projectId) {
      setCheckingResume(true);
      const resumable = await checkResumable(a);
      setCheckingResume(false);
      if (resumable) {
        setPendingResumeChoice(resumable);
        return;
      }
    }
    // 2026-09-01, requested by user: a fresh (non-resumed) "loader"
    // selection always re-asks the staged-vs-direct question below.
    //
    // 2026-09-16, reported by user: except when Direct isn't actually a
    // choice for this mapping (a CRM target with joined source tables
    // only ever offers the staging card, disabled) — re-asking a
    // one-option "question" is just an extra click, so skip straight to
    // staged instead of showing the gate.
    if (a === "loader") {
      setCrmLoadStrategy(hasJoinedMappingsForDirectLoad ? "staged" : null);
      setLoaderEntryChoice(null);
    }
    if (a === "reconciliation") {
      setReconciliationMappingConfirmed(false);
    }
    setAction(a);
    setStep(ACTION_STEPS[a][0]);
    setMappingEntryChoice(null);
  };

  // Direct jump straight to the Load-to-Target step of the Data Loader
  // action — used by the "Load to Target" button at the end of the
  // Reconciliation results page. Unlike selectAction(a)'s default of
  // landing on ACTION_STEPS[a][0] ("ddl", the earliest loader step), this
  // one is an explicit forward-navigation from a step the user is already
  // mid-session on, so it still checks for resumable Load-to-Target state
  // (same as before this action was folded into "loader") but otherwise
  // jumps straight to the destination step below rather than back to "ddl".
  //
  // 2026-09-03, reported by user: "continue to load target needs to be
  // enabled at the reconciliation page" -- clicking this right after
  // running a staging load THIS session always found that same load's own
  // ddlState/loadJob as "resumable" and popped a "resume a previous
  // session?" dialog instead of just continuing forward, since
  // checkResumable has no way to tell "an old session's leftovers" apart
  // from "the live state of the session I'm already in". crmLoadStrategy
  // being set is exactly that signal: it's only set once this session has
  // already gone through the loader's staged-vs-direct choice, so the
  // resumable check is skipped entirely in that case -- it only still runs
  // for a genuine cold start (Reconciliation picked directly from the
  // action list, never having touched Create Staging Tables/Load Data this
  // session).
  //
  // 2026-09-04, reported by user: this always jumped to "sf_load" (the
  // Salesforce/Dynamics/Power Platform push step) even for a project whose
  // target is a plain RDBMS/mirror -- that step's whole UI (connect a CRM
  // platform, "Load Fields & Auto-Map" against an SF/Dataverse object, ...)
  // is meaningless there; for an RDBMS/mirror target, the staging load
  // itself ("load" step's own Load Results card) already IS the final
  // load, so that's the correct destination instead. Also, after a page
  // refresh crmLoadStrategy/targetPlatformMode reset to their defaults
  // before this runs, so the intended destination is now threaded through
  // checkResumable's resumable-choice dialog too (see intendedStep on
  // ResumeInfo) -- otherwise "Resume" would still land on the generic
  // cold-start default ("ddl") instead of here, exactly the inconsistency
  // reported: clicking straight through (no resume prompt, crmLoadStrategy
  // already set) correctly reached sf_load, but refresh -> Resume did not.
  const jumpToLoadTarget = async () => {
    const destinationStep: Step =
      targetPlatformMode === "crm" ? "sf_load" : "load";
    if (projectId && !crmLoadStrategy) {
      setCheckingResume(true);
      const resumable = await checkResumable("loader", destinationStep);
      setCheckingResume(false);
      if (resumable) {
        setPendingResumeChoice(resumable);
        return;
      }
    }
    setAction("loader");
    // This shortcut always implies the staged flow (it's jumping into the
    // final load step on the assumption staging already ran) — never
    // re-asks.
    setCrmLoadStrategy("staged");
    setLoaderEntryChoice("mapped");
    setStep(destinationStep);
    setMappingEntryChoice(null);
  };

  // 2026-09-01, requested by user: Finalize Mapping's last step now offers
  // "Load Data" as an alternative to "Create Tables" — jumps straight to
  // the Data Loader's "load" step instead of always landing on "ddl"
  // first. Mirrors jumpToLoadTarget's same pattern (still checks for
  // resumable loader state first, same as selectAction("loader") would).
  const jumpToLoadDataDirect = async () => {
    if (projectId) {
      setCheckingResume(true);
      const resumable = await checkResumable("loader");
      setCheckingResume(false);
      if (resumable) {
        setPendingResumeChoice(resumable);
        return;
      }
    }
    setAction("loader");
    // Same reasoning as jumpToLoadTarget above — jumping into "load"
    // assumes the staged flow, so this never re-asks the CRM question.
    setCrmLoadStrategy("staged");
    setLoaderEntryChoice("mapped");
    setStep("load");
    setMappingEntryChoice(null);
  };

  // 2026-09-03, requested by user: once a staging load finishes, offer
  // Reconciliation as an explicit choice instead of only ever being
  // carried straight to Load to Target — "reconciliation" is its own
  // top-level action (not one of "loader"'s ACTION_STEPS), so this jump
  // switches actions rather than just advancing a step, same
  // resumable-check pattern as jumpToLoadTarget/jumpToLoadDataDirect above.
  const jumpToReconciliation = async () => {
    if (projectId) {
      setCheckingResume(true);
      const resumable = await checkResumable("reconciliation");
      setCheckingResume(false);
      if (resumable) {
        setPendingResumeChoice(resumable);
        return;
      }
    }
    setAction("reconciliation");
    setStep("reconcile");
    setMappingEntryChoice(null);
  };

  // 2026-09-11, requested by user: "No Target Model — Mirror Source" mode
  // has no real target platform at all — its "staging" tables (bronze_*)
  // ARE the finished output. Reconciliation's old "Continue to Load to
  // Target" button still fired for mirror projects too, but jumpToLoadTarget
  // just re-lands on the "load" step (source -> staging), which already ran
  // — from the user's perspective that looked like being sent backward to
  // "stage creation" for no reason. This is the mirror-only alternative:
  // an explicit choice to now map the already-staged/mirrored data to a
  // genuine target platform, reusing the exact same Mapping ("salesforce"
  // target-connect -> "mapping" -> "sttm") and Loader ("ddl"/"load"/
  // "sf_load") steps every non-mirror project already uses — just pointed
  // at the staging DB as the new "source" instead of the original one.
  const [startingMirrorTargetMapping, setStartingMirrorTargetMapping] =
    useState(false);
  const startMirrorTargetMapping = async () => {
    if (!targetConnectionId) {
      setErr("No staging database connection found to map from");
      return;
    }
    const stagingConnectionId = targetConnectionId;
    const stagingSchema = ddlTargetSchema || targetSchemaName || "";
    // Only scan the tables actually mirrored in this project instead of the
    // whole schema — scanning every table (PK/FK/columns/row-count, 4 round
    // trips each) was what made this button slow on schemas with many
    // tables, same as the Salesforce Load "Auto-Map" fix above.
    const stagedTableNames = mappings.map((m) =>
      stagedTargetTable(m.target_object),
    );
    setStartingMirrorTargetMapping(true);
    setError(null);
    try {
      const res = await apiv2.converterSourceScan(
        { connectionId: stagingConnectionId, projectId },
        stagingSchema || undefined,
        stagedTableNames.length > 0 ? stagedTableNames : undefined,
      );
      setSchemaResult(res);
      setSelectedSourceTablesForMapping(
        new Set(res.tables.map((t) => t.table_name)),
      );
      setSourceConnectionId(stagingConnectionId);
      setSourceConnLabel(targetConnLabel || "Staging database (mirrored data)");
      setSourceSchemaName(stagingSchema);
      // The previous (mirror source -> staging) load's results must not
      // linger and look like results for this genuinely new target load.
      resetLoadResultsState();
      // Clear the old mirror-mode target/mapping state so the Target
      // Platform step prompts for a genuine connection instead of showing
      // the staging one as already connected.
      setTargetPlatformMode("crm");
      setTargetConnectionId(null);
      setTargetConnLabel("");
      setTargetDbType(null);
      setTargetSchemaName("");
      setSfOrgInfo(null);
      setAvailableTargetObjects([]);
      setSfObjects([]);
      setDdlTargetConnected(null);
      setMappings([]);
      setEditedMappings([]);
      setMappingPairs([]);
      setRemovedTableMappings(new Set());
      setRemovedFieldMappings({});
      setKeepFieldsSelected({});
      setSttmResult(null);
      setSttmActiveTab(0);
      setUnmappedReviewTablesOpen(false);
      // Same reasoning as resetForFreshMappingFlow below — a prior mapping's
      // STTM review banners (rule-import results, STTM import info/history)
      // must not linger once this genuinely different target mapping starts.
      setSttmHistory([]);
      setSttmHistoryOpen(false);
      setSttmImportInfo(null);
      setImportRulesResult(null);
      setDqRuleDecisions({});
      setDqRuleRemoved(new Set());
      setDqDecisionsApplied(null);
      setDqDecisionsNotFound([]);
      setLegacyRulesError(null);
      setLegacyRulesBatch(null);
      setLegacyRulesPanelOpen(false);
      setLegacyRulesHistory([]);
      setLegacyRulesHistoryOpen(false);
      setMappingImportWarnings([]);
      // Same reasoning as resetForFreshMappingFlow below — a prior
      // finalized mapping's "Saved HH:MM" badge/Data Dictionary must not
      // linger once this genuinely different target mapping starts.
      setFinalizedAt(null);
      setDataDictionaryResult(null);
      setProjectMappingFile(null);
      setDdlStatements([]);
      setDdlDuplicates([]);
      setDdlDuplicateDecisions({});
      setDdlTargetSchema("");
      // Same reasoning as resetForFreshMappingFlow below — a custom naming
      // override from a prior mapping session must not carry into this
      // genuinely different target mapping.
      setTableNamingMode("auto");
      setTableNamingValue("");
      setTableNamingScope("all");
      setIncludeAuditColumns(true);
      // The staging connection's schema list (e.g. from Generate & Preview
      // DDL or the RDBMS Target Platform picker earlier in this session)
      // must not leak into the genuinely new target connection's picker —
      // otherwise "List schemas"/manual-entry state stays stuck showing
      // the staging DB's schemas until the user notices and clicks
      // "Refresh" themselves.
      setTargetSchemas([]);
      setTargetSchemaManualEntry(false);
      setCrmLoadStrategy(null);
      setTargetModelChoice("crm");
      setNoTargetSubOption("mirror");
      setImportedTargetModelFile(null);
      setImportedTargetSchema(null);
      setImportedTargetModelCreateResult(null);
      setAction("mapping");
      setStep("salesforce");
    } catch (e) {
      setErr(
        e instanceof Error
          ? e.message
          : "Could not read the staged/mirrored data's schema",
      );
    } finally {
      setStartingMirrorTargetMapping(false);
    }
  };

  // Landing-page "Saved Connections" browser — lets a user check/activate a
  // source or target DB connection before picking an action. Activating here
  // updates sourceConnectionId/targetConnectionId immediately (see
  // refreshSourceConnectionFromSaved/refreshTargetConnectionFromSaved below),
  // so the Source DB / Create Tables steps already show "Connected" once the
  // user picks an action, instead of needing to reconnect from scratch.
  const [showSavedConnections, setShowSavedConnections] = useState(false);
  const [savedConnectionsKind, setSavedConnectionsKind] = useState<
    "source" | "target"
  >("source");

  // Source DB state — a connection_id (one of the project's saved
  // connections, shared with every other agent) once connected. No raw
  // credentials are held in component state or persisted client-side —
  // see ConnectDatabaseDialog / resolve_connect_config on the backend.
  const [sourceConnectionId, setSourceConnectionId] = useState<string | null>(
    null,
  );
  const [sourceConnLabel, setSourceConnLabel] = useState("");
  // Schema name only (never a credential) — kept for the Load Data step's
  // default srcSchema/tgtSchema fallback, since that's UI convenience, not
  // secret material.
  const [sourceSchemaName, setSourceSchemaName] = useState("");
  const [targetSchemaName, setTargetSchemaName] = useState("");
  // Schema picker defaults to collapsed (just the current schema shown) —
  // a connection can expose dozens of schemas and dumping them all as
  // pills at once is noisy; "Change Schema" reveals a searchable picker.
  const [showSchemaPicker, setShowSchemaPicker] = useState(false);
  const [showSourceConnectDialog, setShowSourceConnectDialog] = useState(false);
  const [connStatus, setConnStatus] = useState<{ schemas: string[] } | null>(
    null,
  );
  // Additional source connections beyond the primary sourceConnectionId —
  // lets one project mix source tables from several different source
  // databases/schemas across mapping rows (TableObjectMapping.
  // source_connection_id) instead of a single active connection. Each
  // entry's connectionId is one of the project's saved connections (same
  // library sourceConnectionId itself resolves against) — the SAME saved
  // connection can appear more than once here with a different schemaName,
  // since one DB connection commonly exposes many schemas (this is why
  // `id` — not connectionId — is the unique/removal key, and why adding
  // is never blocked just because connectionId matches the primary or an
  // existing entry).
  const [extraSourceConnections, setExtraSourceConnections] = useState<
    {
      id: string;
      connectionId: string;
      label: string;
      schemaName: string;
      schemas: string[];
    }[]
  >([]);
  // Which extraSourceConnections row (by id) has its schema picker open —
  // mirrors showSchemaPicker's collapsed-by-default pattern for the primary.
  const [extraSchemaPickerId, setExtraSchemaPickerId] = useState<string | null>(
    null,
  );
  const [showAddSourceConnectionDialog, setShowAddSourceConnectionDialog] =
    useState(false);
  // Manual connect for the two mainframe-oriented kinds ConnectDatabaseDialog
  // doesn't cover (it only knows postgresql/mysql/mssql/oracle/snowflake/
  // databricks) — a live vendor-agnostic JDBC bridge, or a copybook-described
  // flat-file extract with no live connection at all. See
  // handleMainframeSourceConnect below.
  const [mainframeConn, setMainframeConn] = useState<DbConnectionConfig>({
    db_type: "jdbc_bridge",
    host: "",
    port: 0,
    database: "",
    username: "",
    password: "",
    schema_name: "",
    service_name: null,
    jdbc_driver_class: null,
    jdbc_url: null,
    jdbc_driver_jar_path: null,
    bridge_label: null,
    copybook_text: null,
  });

  // File Source state — independent lightweight flow (upload -> parse
  // layout/copybook -> auto-build identity-mapped task -> preview -> load).
  // Does not use the `step` state machine; rendered inline within the
  // "source" step when sourceMode === "file".
  //
  // 2026-09-08, issue #621: "mainframe" promoted to its own top-level
  // Source Type — it used to be a nested section under "database" (see
  // handleMainframeSourceConnect/mainframeConn above).
  const [sourceMode, setSourceMode] = useState<
    "database" | "file" | "legacy" | "mainframe" | "crm"
  >("database");
  // "Generate from Legacy Code" entry point — parses an uploaded COBOL
  // copybook/SQL DDL/JSON/XML/Parquet artifact straight into a
  // SchemaExtractResult (see converterParseLegacySource), independent of
  // any live connection. Distinct from sourceMode === "file" above, which
  // is a different pipeline (fixed-width flat-file ingestion for the Data
  // Loader, not schema/mapping generation).
  // Holds 1..N files — a metadata-only source (e.g. one copybook per record
  // layout, or one DDL script per table) is often spread across several
  // files, so this always crawls the whole selection in one pass rather
  // than forcing a file-at-a-time upload.
  const [legacyFiles, setLegacyFiles] = useState<File[]>([]);
  const [parsingLegacyCode, setParsingLegacyCode] = useState(false);
  // "consolidate" merges CSV/TSV/JSON/Parquet files sharing the same
  // columns into one source table (the default, e.g. a batch of monthly
  // exports of the same table); "separate" always creates one source
  // table per file/table, regardless of structure overlap -- the
  // independent-mapping option. Mirrors pdfConsolidateMode below for the
  // "Upload File(s)" acquisition method. See handleParseLegacyCode.
  const [legacyConsolidateMode, setLegacyConsolidateMode] = useState<
    "consolidate" | "separate"
  >("consolidate");
  // SAP BODS ETL dataflow import -- a separate, dedicated flow from the
  // schema-only "Generate Schema from Files/Servers/Legacy Code" card
  // above: these files are real <DataIntegratorExport> job/workflow/
  // dataflow XML, parsed directly into an STTM mapping (plus source/target
  // DDL and an audit workbook) in one pass, with no intermediate
  // interactive/LLM-assisted mapping step. See handleParseBodsDataflow.
  const [bodsDataflowFiles, setBodsDataflowFiles] = useState<File[]>([]);
  const [parsingBodsDataflow, setParsingBodsDataflow] = useState(false);
  const [bodsDataflowResult, setBodsDataflowResult] =
    useState<ConverterBodsDataflowImportResponse | null>(null);
  // Informatica PowerCenter mapping import -- same dedicated-flow shape as
  // the BODS dataflow import above, for Informatica's own "Export to XML"
  // mapping export format. See handleParseInformaticaMapping.
  const [informaticaMappingFiles, setInformaticaMappingFiles] = useState<
    File[]
  >([]);
  const [parsingInformaticaMapping, setParsingInformaticaMapping] =
    useState(false);
  const [informaticaMappingResult, setInformaticaMappingResult] =
    useState<ConverterInformaticaMappingImportResponse | null>(null);
  // Which method the Legacy Code source type uses to acquire its
  // metadata/data files -- local upload (default, unchanged behavior),
  // SharePoint Lists, PDF table extraction, or a remote SFTP server.
  // SharePoint and PDF used to be their own separate Data Loader entry
  // points/flows; they're now acquisition methods within Legacy Code so
  // every source type ends up in the same Schema DDL / Target Platform /
  // Mapping / Review pipeline.
  const [legacySourceMethod, setLegacySourceMethod] = useState<
    "upload" | "sharepoint" | "pdf" | "sftp"
  >("upload");
  // PDF files staged for the Legacy Code source type's PDF acquisition
  // method (see legacySourceMethod === "pdf") -- uploaded to object
  // storage on selection, then "Parse & Continue" runs handleParseLegacyPdf
  // against every uploaded file's path.
  const [legacyPdfUploads, setLegacyPdfUploads] = useState<
    ConverterFileUploadResponse[]
  >([]);
  // "consolidate" merges similar-layout PDFs into one source table (the
  // default); "separate" always creates one source table per detected
  // table, regardless of layout -- the regular per-file STTM flow. See
  // handleParseLegacyPdf.
  const [pdfConsolidateMode, setPdfConsolidateMode] = useState<
    "consolidate" | "separate"
  >("consolidate");
  // Polling handle for handleParseLegacyPdf's async job -- cleared on
  // completion/failure/unmount so a stale interval never fires after the
  // user navigates away or starts another parse.
  const pdfParsePollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [legacySourceInfo, setLegacySourceInfo] = useState<{
    filename: string;
    format: string;
    tableCount: number;
    warnings: string[];
    // Non-null only for a PDF parse where similar-layout files were
    // merged into one source table -- see pdfConsolidateMode.
    consolidationNote: string | null;
    // Only non-empty for SSIS (.dtsx) parses — keyed by originating
    // filename, everything else the package contained besides the
    // tables already reflected in tableCount, for manual review.
    packageDetails: Record<string, SsisPackageDetails>;
    // Only non-empty for SQL DDL, COBOL copybook, and XML parses — keyed
    // by originating filename, everything found in the file that isn't
    // reflected as a table/column, for manual review.
    additionalItems: Record<string, AdditionalItem[]>;
  } | null>(null);
  const [legacyPackageDetailsOpen, setLegacyPackageDetailsOpen] =
    useState(false);
  const [legacyAdditionalItemsOpen, setLegacyAdditionalItemsOpen] =
    useState(false);
  // Every file from the last "Generate from Legacy Code" crawl that was
  // also persisted as a re-loadable data file (CSV/TSV/JSON/JSONL/Parquet),
  // keyed by original filename — see SchemaExtractResult.file_refs. Lets
  // the Load step's "Files" mode auto-find each mapped table's source file
  // (via TableObjectMapping.source_file) instead of asking the user to
  // re-upload it one table at a time, even across hundreds of tables.
  const [legacyFileRefs, setLegacyFileRefs] = useState<
    Record<string, ConverterFileUploadResponse>
  >({});
  const [autoFillingSourceFiles, setAutoFillingSourceFiles] = useState(false);
  const [fileUpload, setFileUpload] = useState<{
    file_path: string;
    filename: string;
    size_bytes: number;
  } | null>(null);
  // Additional files beyond the first, from a bulk (multi-file) selection —
  // assumed to share the first file's format/layout (same source system,
  // just split across files), so only the first goes through format/column
  // auto-detection. Each becomes its own load, forced to if_exists="append"
  // onto whatever the first file's own if_exists choice created/prepared
  // (see handleLoadFileTask).
  const [additionalFileUploads, setAdditionalFileUploads] = useState<
    ConverterFileUploadResponse[]
  >([]);
  const [fileBatchLoadResults, setFileBatchLoadResults] = useState<
    (ConverterLoadResponse & { error?: string })[] | null
  >(null);
  // "Import from SFTP" -- alternate File Source entry point alongside local
  // upload (see handleSftpConnect/handleSftpListFiles/handleSftpImportFile).
  // Connection is request-scoped only; never persisted.
  const [showSftpPanel, setShowSftpPanel] = useState(false);
  const [sftpConn, setSftpConn] = useState<ConverterSftpConnectionConfig>({
    host: "",
    port: 22,
    username: "",
    password: "",
    remote_path: ".",
    host_key_fingerprint: "",
  });
  const [sftpConnecting, setSftpConnecting] = useState(false);
  const [sftpConnected, setSftpConnected] = useState(false);
  const [sftpListing, setSftpListing] = useState(false);
  const [sftpImportingPath, setSftpImportingPath] = useState<string | null>(
    null,
  );
  const [sftpError, setSftpError] = useState<string | null>(null);
  const [sftpFiles, setSftpFiles] = useState<ConverterSftpFileMeta[]>([]);
  // "Import from SharePoint" -- alternate File Source entry point (SharePoint
  // Lists, not document-library files). Connection is request-scoped only;
  // never persisted (mirrors the SFTP panel above). Reached via its own
  // Data Loader entry choice, so unlike the SFTP panel it has no
  // show/hide toggle -- it's the only thing on that screen.
  const [sharePointConn, setSharePointConn] =
    useState<ConverterSharePointConnectionConfig>({
      tenant_id: "",
      client_id: "",
      client_secret: "",
      site_url: "",
    });
  const [spConnecting, setSpConnecting] = useState(false);
  const [spConnected, setSpConnected] = useState(false);
  const [spListing, setSpListing] = useState(false);
  const [spError, setSpError] = useState<string | null>(null);
  const [spLists, setSpLists] = useState<ConverterSharePointListMeta[]>([]);
  // Multi-select: lets the user check several lists and parse them all in
  // one "Parse & Continue" action (see handleParseLegacySharePoint) --
  // SharePoint is now a Legacy Code source-type acquisition method
  // alongside local file upload and PDF, not its own separate Data Loader
  // flow/wizard.
  const [spSelectedIds, setSpSelectedIds] = useState<Set<string>>(new Set());
  // "CRM/SaaS Systems" -- Legacy Code source-type acquisition method:
  // connect to Salesforce/Dynamics 365/Power Platform/Workday AS A SOURCE
  // (extract records from selected objects), then "Parse & Continue"
  // extracts them and feeds the result through the same parse-legacy
  // pipeline a local file upload uses (see handleParseLegacyCrm). Distinct,
  // request-scoped-only state from the Target Platform step's sfConn/etc.
  // above -- a project may extract from one CRM/SaaS org and load into a
  // completely different target, so source and target connections must
  // never share state.
  const [crmSourceConn, setCrmSourceConn] =
    useState<SalesforceConnectionConfig>({
      target_type: "salesforce",
      instance_url: "",
      username: "",
      password: "",
      security_token: "",
    });
  const [crmSourceConnecting, setCrmSourceConnecting] = useState(false);
  const [crmSourceConnected, setCrmSourceConnected] = useState(false);
  const [crmSourceLoadingObjects, setCrmSourceLoadingObjects] = useState(false);
  const [crmSourceError, setCrmSourceError] = useState<string | null>(null);
  const [crmSourceObjects, setCrmSourceObjects] = useState<
    SalesforceObjectMeta[]
  >([]);
  const [crmSourceSelectedObjects, setCrmSourceSelectedObjects] = useState<
    Set<string>
  >(new Set());
  // Opt-in bounded preview sample for Parse & Continue (issue #735 step 2)
  // -- schema/mapping preview only, defaults false to preserve the
  // existing full-object extraction behavior.
  const [crmParseSampleOnly, setCrmParseSampleOnly] = useState(false);
  // "Stream to DB" (issue #735) -- an alternative to Parse & Continue for
  // one CRM/SaaS object at a time: streams records directly into a target
  // DB table without ever materializing the full object in memory/CSV.
  // Modal-driven rather than tabbed (unlike sfLoadTabs below) since only
  // one object is ever streamed at a time here.
  const [crmStreamModalObject, setCrmStreamModalObject] =
    useState<SalesforceObjectMeta | null>(null);
  const [crmStreamConfig, setCrmStreamConfig] = useState<CrmStreamLoadConfig>({
    targetSchema: "",
    targetTable: "",
    batchSize: 2000,
    truncate: false,
    fieldMappings: [],
  });
  const [crmStreamJobId, setCrmStreamJobId] = useState<string | null>(null);
  const [crmStreamStatus, setCrmStreamStatus] =
    useState<CrmToDbLoadStatusResponse | null>(null);
  const [crmStreamStarting, setCrmStreamStarting] = useState(false);
  const [crmStreamError, setCrmStreamError] = useState<string | null>(null);
  const crmStreamPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Object api_name -> job_id for a still-running "Stream to DB" load whose
  // modal was closed. Without this, closing the modal while a load is
  // running permanently orphans that job: the Celery job keeps running
  // server-side but there is no UI path back to it, and reopening the
  // modal for the same object starts an independent new load rather than
  // reconnecting (PR #740 review finding #2).
  const crmStreamActiveJobsRef = useRef<Map<string, string>>(new Map());
  // "Import from SFTP" -- SFTP as a Legacy Code source-type acquisition
  // method: connect, browse a remote directory, check one or more files,
  // then "Parse & Continue" downloads and parses them all through the same
  // pipeline a local upload uses (see handleParseLegacySftp). Distinct,
  // request-scoped-only state from the File Source flow's sftpConn/etc.
  // above -- a different screen with a different (multi-select) flow.
  const [legacySftpConn, setLegacySftpConn] =
    useState<ConverterSftpConnectionConfig>({
      host: "",
      port: 22,
      username: "",
      password: "",
      remote_path: ".",
      host_key_fingerprint: "",
    });
  const [legacySftpConnecting, setLegacySftpConnecting] = useState(false);
  const [legacySftpConnected, setLegacySftpConnected] = useState(false);
  const [legacySftpListing, setLegacySftpListing] = useState(false);
  const [legacySftpError, setLegacySftpError] = useState<string | null>(null);
  const [legacySftpFiles, setLegacySftpFiles] = useState<
    ConverterSftpFileMeta[]
  >([]);
  const [legacySftpSelectedPaths, setLegacySftpSelectedPaths] = useState<
    Set<string>
  >(new Set());
  // "Extract Tables from PDF" -- alternate Legacy Code source-type
  // acquisition method: upload PDF(s), then "Parse & Continue" runs
  // deterministic (non-LLM) table detection and feeds every detected table
  // through the same parse-legacy pipeline a local file upload uses (see
  // handleParseLegacyPdf).
  const [pdfExtracting, setPdfExtracting] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  // "Preview Parsed Data" -- read-only sanity check of what pdfplumber
  // extracted from the currently-staged legacyPdfUploads, before "Parse &
  // Continue" snapshots the schema (see handlePreviewLegacyPdf).
  const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
  const [pdfPreviewError, setPdfPreviewError] = useState<string | null>(null);
  const [pdfPreviewResult, setPdfPreviewResult] =
    useState<ConverterPdfPreviewResponse | null>(null);
  // Standalone Data Loader "Extract Tables from PDF" entry point (distinct
  // from the Legacy Code acquisition method above): a single PDF's
  // detected tables, and which one is currently being applied as the File
  // Source (see handlePdfFileSelected/handlePdfUseTable).
  const [pdfTables, setPdfTables] = useState<ConverterPdfExtractedTable[]>([]);
  const [pdfUsingIndex, setPdfUsingIndex] = useState<number | null>(null);
  // "Import Folder" -- alternative to picking PDFs one at a time: a
  // native OS folder picker (input[webkitdirectory]) hands us every file
  // in the chosen folder, which we filter to *.pdf and upload/extract the
  // same way as a multi-file selection (see handlePdfFolderFilesSelected),
  // reusing the same pdfTables/pdfError state above.
  const [pdfFolderName, setPdfFolderName] = useState<string | null>(null);
  const [pdfFolderLoading, setPdfFolderLoading] = useState(false);
  const [layoutFormat, setLayoutFormat] = useState<
    "simple" | "cobol" | "pyspark"
  >("simple");
  const [layoutText, setLayoutText] = useState("");
  const [layoutFields, setLayoutFields] = useState<ConverterSourceField[]>([]);
  // The File Source flow explicitly caters to 4 source shapes: a genuine
  // positional mainframe extract (fixed_width, custom start/length layout),
  // a COBOL-copybook-driven fixed-width extract (same format_type, but the
  // layout comes from PIC clauses instead), a comma/tab-delimited file
  // (delimited), and a self-describing structured file (structured: JSON/
  // JSONL/Parquet). Auto-detected from the uploaded filename's extension,
  // but always user-overridable via the File Format selector below —
  // extension alone can be wrong (e.g. a delimited extract saved as
  // `.txt`).
  const [fileCategory, setFileCategory] = useState<
    "fixed_width" | "copybook" | "delimited" | "structured"
  >("fixed_width");
  const [delimitedSubFormat, setDelimitedSubFormat] = useState<"csv" | "tsv">(
    "csv",
  );
  const [structuredSubFormat, setStructuredSubFormat] = useState<
    "json" | "jsonl" | "parquet"
  >("json");
  // Detected from the uploaded file's extension (see
  // _detectFileSourceFormatType) -- csv/tsv/json/jsonl/parquet are
  // self-describing (delimiter + header row, or an embedded schema) and
  // skip the manual Record Layout step entirely via handleFileSourceUpload's
  // automatic call to converterInspectFile; only "fixed_width" still needs
  // one.
  const [fileFormatType, setFileFormatType] =
    useState<ConverterSourceFormat["format_type"]>("fixed_width");
  const [detectingColumns, setDetectingColumns] = useState(false);
  const [fileHasHeader, setFileHasHeader] = useState(false);
  const [fileEncoding, setFileEncoding] = useState("utf-8");
  const [fileTargetTable, setFileTargetTable] = useState("");
  const [fileTargetSchema, setFileTargetSchema] = useState("public");
  // Drives which single step of the File-source flow is visible at a time
  // (Upload → Layout → Build & Preview → Connect Target → If Table Exists →
  // Load Data) instead of showing every step's card stacked on one screen.
  const [fileWizardStep, setFileWizardStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  // Single-file wizard and multi-file bulk ingest are mutually exclusive —
  // showing both at once suggested "bulk load" was a step of the single-file
  // flow, when it's actually a separate ingestion path.
  const [fileIngestMode, setFileIngestMode] = useState<
    "single" | "bulk" | "multi_format"
  >("single");
  // "Multiple Files (Different Formats)" -- unlike "single" (extra files
  // share the first file's layout/table) or "bulk" (one Format applies to
  // every file), each file selected here gets its own tab with a fully
  // independent Detected Columns / Build & Preview / Connect Target / Load
  // Data pass and its own target table -- files can differ in format
  // (csv/json/parquet/...) AND in structure (columns) from one another.
  // Implemented by saving/restoring the shared file-wizard state above as
  // a per-tab snapshot (see FileWizardSnapshot near
  // handleMultiFormatFilesSelected) rather than duplicating that ~500-line
  // wizard's JSX once per tab.
  const [multiFormatTabs, setMultiFormatTabs] = useState<
    { filename: string; file_path: string; snapshot: FileWizardSnapshot }[]
  >([]);
  const [multiFormatActiveIndex, setMultiFormatActiveIndex] = useState<
    number | null
  >(null);
  const [multiFormatBusy, setMultiFormatBusy] = useState(false);
  const [multiFormatError, setMultiFormatError] = useState<string | null>(null);
  const [fileIfExists, setFileIfExists] = useState<
    "fail" | "replace" | "append" | "truncate"
  >("append");
  // Target connection for the File Source flow — a ConverterConnectionRef
  // (either a fresh DbConnectionConfig or a saved connectionId) via the
  // SAME shared, registry-driven ConnectDatabaseDialog the Source DB /
  // Target Platform steps already use, instead of this flow's own
  // previously-hand-rolled host/port/user/password form that was hardcoded
  // to postgres/oracle/mssql only and could never reach Snowflake/
  // Databricks/etc. fileTargetDbType is populated from the backend's own
  // dialect detection (converterTestFileConnection's response) rather than
  // trusted from the client, since a saved-connection ref carries no type
  // info of its own — needed for the Synapse-blob-staging /
  // Snowflake-bulk-load conditional UI below, which the backend rejects
  // outright for any other dialect.
  const [fileTargetConn, setFileTargetConn] =
    useState<ConverterConnectionRef | null>(null);
  const [fileTargetConnLabel, setFileTargetConnLabel] = useState("");
  const [fileTargetDbType, setFileTargetDbType] = useState("");
  const [showFileTargetConnectDialog, setShowFileTargetConnectDialog] =
    useState(false);
  const [fileConnTestResult, setFileConnTestResult] =
    useState<ConverterTestConnectionResponse | null>(null);
  const [fileConnTesting, setFileConnTesting] = useState(false);
  // Existing tables on the connected target, for picking one to insert into
  // instead of always typing a brand-new table name.
  const [fileTargetTables, setFileTargetTables] = useState<TableMeta[] | null>(
    null,
  );
  const [fileTargetTablesScanning, setFileTargetTablesScanning] =
    useState(false);
  const [fileTargetTablesError, setFileTargetTablesError] = useState<
    string | null
  >(null);
  const [fileBuiltTask, setFileBuiltTask] = useState<ConverterTask | null>(
    null,
  );
  const [filePreviewResult, setFilePreviewResult] =
    useState<ConverterPreviewResponse | null>(null);
  const [fileLoadResult, setFileLoadResult] =
    useState<ConverterLoadResponse | null>(null);
  const [fileBusy, setFileBusy] = useState(false);
  // Shown inline next to their respective action buttons instead of the
  // global error banner at the top of the screen, which was easy to miss/
  // confusing this far down a long File-source flow.
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  const [layoutError, setLayoutError] = useState<string | null>(null);
  const [fileBuildError, setFileBuildError] = useState<string | null>(null);
  const [fileLoadError, setFileLoadError] = useState<string | null>(null);
  // Bulk load (Snowflake PUT+COPY INTO / Synapse COPY INTO from Parquet)
  // instead of row-by-row insert — same toggle/config as the folder ingest
  // flow's folderBulkLoad below, kept as its own state since this flow's
  // "one primary file + additionalFileUploads" selection is independent of
  // that separate flow's folderFiles. Applies to the primary file AND every
  // additional file (see handleLoadFileTask).
  const [fileBulkLoad, setFileBulkLoad] = useState(false);
  // Build & Preview's "exclude flagged records" checkbox -- when set,
  // handleLoadFileTask tells the backend (LoadTarget.exclude_incomplete_rows)
  // to drop the same mostly-empty rows flagged by filePreviewResult.bad_records
  // before the actual DB write.
  const [fileExcludeBadRecords, setFileExcludeBadRecords] = useState(false);
  // The preview table itself must reflect exclusion too — otherwise
  // checking the box has no visible effect until after clicking Load,
  // which read as "my checkbox selection isn't doing anything."
  const fileBadRowNumbers = useMemo(
    () => new Set(filePreviewResult?.bad_records.map((r) => r.row_number)),
    [filePreviewResult],
  );
  const fileVisiblePreviewRows = useMemo(() => {
    const rows = filePreviewResult?.preview ?? [];
    if (!fileExcludeBadRecords) return rows;
    return rows.filter((_, i) => !fileBadRowNumbers.has(i + 1));
  }, [filePreviewResult, fileExcludeBadRecords, fileBadRowNumbers]);
  const [fileBlobAccountName, setFileBlobAccountName] = useState("");
  const [fileBlobContainer, setFileBlobContainer] = useState("");
  const [fileBlobAuthMode, setFileBlobAuthMode] = useState<
    "sas_token" | "account_key" | "aad_service_principal"
  >("account_key");
  const [fileBlobCredential, setFileBlobCredential] = useState("");

  // Bulk folder ingest: multiple files, same layout/target as the single-
  // file flow above, real parallel processing via the pipelines engine
  // (see apiv2.converterLoadBatchPipeline) instead of one sequential job.
  const [folderFiles, setFolderFiles] = useState<File[]>([]);
  // "fixed_width" (default) needs a parsed copybook/layout, same as the
  // single-file flow above. "csv"/"tsv" skip that requirement entirely —
  // added for the common case of a folder of plain delimited extracts
  // (e.g. a Bronze-layer load) where there's no fixed-width layout to
  // define at all.
  const [folderFormatType, setFolderFormatType] = useState<
    "fixed_width" | "csv" | "tsv"
  >("fixed_width");
  // When true, each file gets its OWN target table (auto-named from its
  // filename) instead of every file loading into the single shared
  // fileTargetTable — the Bronze case: N distinct source extracts, not N
  // parts of one table. Defaults to on for delimited formats (the common
  // case there) and off for fixed-width (preserves this feature's
  // original same-table-multi-file behavior).
  const [folderOneTablePerFile, setFolderOneTablePerFile] = useState(false);
  const [folderBulkLoad, setFolderBulkLoad] = useState(false);
  const [folderBlobAccountName, setFolderBlobAccountName] = useState("");
  const [folderBlobContainer, setFolderBlobContainer] = useState("");
  const [folderBlobAuthMode, setFolderBlobAuthMode] = useState<
    "sas_token" | "account_key" | "aad_service_principal"
  >("account_key");
  const [folderBlobCredential, setFolderBlobCredential] = useState("");
  const [folderBusy, setFolderBusy] = useState(false);
  const [folderPipelineRunId, setFolderPipelineRunId] = useState<string | null>(
    null,
  );
  const [folderProgress, setFolderProgress] =
    useState<PipelineProgressResponse | null>(null);

  // Schema DDL state
  const [schemaResult, setSchemaResult] = useState<SchemaExtractResult | null>(
    null,
  );
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());
  const [schemaTableFilter, setSchemaTableFilter] = useState("");
  // Per-extra-connection equivalents of schemaTableFilter / db_type — the
  // primary connection keeps using schemaTableFilter/schemaResult.db_type
  // completely unchanged (so a single-connection scan is byte-for-byte the
  // same as before), while each additional connection gets its own fully
  // separate "Schema: ..." section (own filter box, own db type in the
  // title) instead of sharing the primary's filter/header.
  const [extraSchemaTableFilters, setExtraSchemaTableFilters] = useState<
    Record<string, string>
  >({});
  const [extraSchemaDbType, setExtraSchemaDbType] = useState<
    Record<string, string>
  >({});
  // Raw table count as scanned for each extra connection, BEFORE any
  // name-collision dedup — lets groupTablesByConnection tell "scanned but
  // every table collided with another connection's names" (still show the
  // group, 0 contributed) apart from "never scanned" (hide the group).
  const [extraSchemaRawTableCount, setExtraSchemaRawTableCount] = useState<
    Record<string, number>
  >({});
  // Per-connection raw scan cache — keyed "__primary__" for the primary,
  // by extraSourceConnections' `id` for each extra — so re-scanning or
  // switching ONE connection's schema only ever calls /source/scan for
  // THAT connection, reusing every other connection's last scan result
  // to rebuild the merged view instead of re-hitting every connection's
  // database on every single-section action.
  const [rawScanCache, setRawScanCache] = useState<
    Record<string, SchemaExtractResult>
  >({});
  // Which extraSourceConnections row (by id) currently has a scan in
  // flight — kept separate from `scanningSchema` (primary-only) so
  // switching/re-scanning one connection's schema never disables or
  // spins every other connection's card too.
  const [scanningExtraId, setScanningExtraId] = useState<string | null>(null);
  // Two different connections/schemas legitimately CAN have a table with
  // the identical bare name (they're different physical tables, not
  // duplicates) — but every mapping-generation/STTM/Load/reconciliation
  // code path downstream keys a mapping row by that bare table_name alone,
  // so at most one physical table per name can flow into schemaResult.tables
  // at a time. Rather than silently dropping the loser (the original,
  // data-losing behavior), buildMergedSchemaResult records a conflict
  // here for every name collision, defaults to keeping the primary's
  // version, and the Schema DDL step renders an explicit picker so the
  // user can swap in the other connection's version instead — nothing is
  // ever lost without the user seeing both sides and choosing.
  const [tableNameConflicts, setTableNameConflicts] = useState<
    {
      tableName: string;
      primaryTable: TableMeta;
      primaryLabel: string;
      primarySchemaName: string;
      extraTable: TableMeta;
      extraLabel: string;
      extraSchemaName: string;
    }[]
  >([]);
  // Buckets a table list by originating connection (primary first, then
  // each extraSourceConnections entry in order) for grouped display/
  // selection. The primary group is ALWAYS shown once a scan has run
  // (`schemaResult` is set) — even 0 tables — so it can never disappear
  // just because extra connections exist. An extra connection's group is
  // shown as long as it was actually scanned, even with 0 tables (empty
  // schema, or every table name collided with another connection's).
  const groupTablesByConnection = useCallback(
    (tables: TableMeta[]) => {
      const groups: {
        key: string;
        label: string;
        schemaName: string;
        tables: TableMeta[];
      }[] = [];
      const primaryTables = tables.filter((t) => !t.source_connection_id);
      groups.push({
        key: "__primary__",
        label: sourceConnLabel || "Primary connection",
        schemaName: schemaResult?.schema_name ?? "",
        tables: primaryTables,
      });
      for (const c of extraSourceConnections) {
        const connTables = tables.filter(
          (t) => t.source_connection_id === c.connectionId,
        );
        // Still show the group if it was actually scanned (even if every
        // one of its tables collided with another connection's name and
        // contributed 0 tables to `tables`) — only hide a connection that
        // was never scanned at all. Otherwise a connection whose schema
        // fully duplicates another one's table names silently vanishes
        // with no way to change its schema or re-scan it.
        if (connTables.length === 0 && !(c.connectionId in extraSchemaDbType))
          continue;
        groups.push({
          key: c.connectionId,
          label: c.label,
          schemaName: c.schemaName || "",
          tables: connTables,
        });
      }
      return groups;
    },
    [extraSourceConnections, sourceConnLabel, schemaResult, extraSchemaDbType],
  );

  // Lookup caches for the mapping and STTM editors. A 700-table / 28,000-
  // field project (MI SACWIS) re-renders this whole component on every
  // keystroke, and every mapping pair / field row used to redo a linear scan
  // of the scanned tables (and rebuild its option list) on each render.
  // Keyed on schemaResult, so a re-scan invalidates them.
  const schemaTablesByName = useMemo(() => {
    const byName = new Map<string, TableMeta>();
    // First occurrence wins, as the .find() this replaces did.
    for (const t of schemaResult?.tables ?? [])
      if (!byName.has(t.table_name)) byName.set(t.table_name, t);
    return byName;
  }, [schemaResult]);
  // Filled lazily, one entry per table a field row actually asks about.
  const sourceColumnOptionsCache = useMemo(
    () => new Map<string, SearchableFieldOption[]>(),
    // schemaResult is unused in the body on purpose: a new scan resets the cache.
    [schemaResult],
  );

  // Looks up which connection/schema a given source table name came from,
  // for the small badge shown next to each mapping pair — undefined
  // source_connection_id always means the primary connection.
  const getSourceTableConnectionInfo = useCallback(
    (tableName: string): { label: string; schemaName: string } | null => {
      const table = schemaTablesByName.get(tableName);
      if (!table) return null;
      if (!table.source_connection_id) {
        return {
          label: sourceConnLabel || "Primary connection",
          schemaName: schemaResult?.schema_name ?? "",
        };
      }
      const extraConn = extraSourceConnections.find(
        (c) => c.connectionId === table.source_connection_id,
      );
      return extraConn
        ? { label: extraConn.label, schemaName: extraConn.schemaName || "" }
        : null;
    },
    [schemaResult, schemaTablesByName, extraSourceConnections, sourceConnLabel],
  );

  // Options for the mapping-pairs editor's Source Table picker -- built once
  // per scan and shared by every pair. Each pair used to render a native
  // <select> with one <option> per scanned table, ~490,000 DOM nodes for a
  // 700-pair / 700-table project; the searchable picker only renders its
  // list while open. With extra source connections the connection label
  // rides along as the sublabel (it was the <optgroup> label).
  const sourceTablePickerOptions = useMemo((): SearchableFieldOption[] => {
    if (extraSourceConnections.length === 0) {
      return (schemaResult?.tables ?? []).map((t) => ({
        value: t.table_name,
        label: t.table_name,
      }));
    }
    return groupTablesByConnection(schemaResult?.tables ?? []).flatMap(
      (group) =>
        group.tables.map((t) => ({
          value: t.table_name,
          label: t.table_name,
          sublabel: group.label,
        })),
    );
  }, [schemaResult, extraSourceConnections, groupTablesByConnection]);

  // Saved source connection id -> human-readable label, for the STTM
  // workbook's "Source Connection" column (see TableObjectMapping.
  // join_table_connection_ids / apiv2.converterGenerateSttm's
  // connectionLabels param) — a heterogeneous (cross-connection) joined
  // mapping's rows can each name a different connection id, which without
  // this would show as a raw id instead of something meaningful.
  const connectionLabelsForSttm = useMemo(() => {
    const labels: Record<string, string> = {};
    if (sourceConnectionId)
      labels[sourceConnectionId] = sourceConnLabel || "Primary connection";
    for (const c of extraSourceConnections) labels[c.connectionId] = c.label;
    return labels;
  }, [sourceConnectionId, sourceConnLabel, extraSourceConnections]);

  // Load step's per-mapping analog of getSourceTableConnectionInfo above —
  // keyed off TableObjectMapping.source_connection_id directly (set at
  // mapping-generation time, see withSourceConnectionIds/buildIdentityMapping)
  // instead of re-derived from schemaResult, so it still resolves correctly
  // once the mapping step is behind us. Returns null for primary-connection
  // tables (the common case) as well as for a stale/removed extra
  // connection id.
  const getMappingSourceConnectionInfo = useCallback(
    (
      m: TableObjectMapping,
    ): { label: string; schemaName: string; connectionId: string } | null => {
      if (!m.source_connection_id) return null;
      const extraConn = extraSourceConnections.find(
        (c) => c.connectionId === m.source_connection_id,
      );
      return extraConn
        ? {
            label: extraConn.label,
            schemaName: extraConn.schemaName || "",
            connectionId: extraConn.connectionId,
          }
        : null;
    },
    [extraSourceConnections],
  );

  // Default Source Schema for one Load-step mapping row. Prefers the
  // mapping's OWN originating connection's current schema so a table
  // pulled from an extra source connection keeps ITS schema — falling
  // back to the primary connection's schema (re-connecting a different
  // source system for Load should use ITS schema, not a stale one baked
  // into the mapping — see buildDefaultLoadMappingConfig) only for tables
  // that actually belong to the primary connection. Previously this always
  // used the primary's schema for every row, which made every extra-
  // connection table's Load-step "Source Schema" silently collapse onto
  // the primary connection's schema/database.
  const getMappingSourceSchemaDefault = useCallback(
    (m: TableObjectMapping): string => {
      const extraInfo = getMappingSourceConnectionInfo(m);
      if (extraInfo) return extraInfo.schemaName || m.source_schema || "";
      return sourceSchemaName || m.source_schema || "";
    },
    [getMappingSourceConnectionInfo, sourceSchemaName],
  );

  // Connection to actually scan/preview against for one Load-step mapping
  // row — the mapping's own extra connection when it has one, otherwise
  // the primary source connection (unchanged default behavior).
  const getMappingSourceConnectionRef = useCallback(
    (m: TableObjectMapping): ConverterConnectionRef | null => {
      const extraInfo = getMappingSourceConnectionInfo(m);
      if (extraInfo) return { connectionId: extraInfo.connectionId, projectId };
      return sourceConnectionId
        ? { connectionId: sourceConnectionId, projectId }
        : null;
    },
    [getMappingSourceConnectionInfo, sourceConnectionId, projectId],
  );

  // Salesforce state
  const [sfConn, setSfConn] = useState<SalesforceConnectionConfig>(() =>
    loadConn<SalesforceConnectionConfig>("sfConn", {
      target_type: "salesforce",
      instance_url: "",
      username: "",
      password: "",
      security_token: "",
    }),
  );
  const [sfOrgInfo, setSfOrgInfo] = useState<{
    org_name: string;
    object_count: number;
  } | null>(null);
  const [sfObjects, setSfObjects] = useState<SalesforceObjectMeta[]>([]);
  // Shared by every target-object picker (one per mapping pair, plus the
  // Change Target / add-mapping pickers) instead of one array per picker per
  // render.
  const sfObjectPickerOptions = useMemo(
    (): SearchableFieldOption[] =>
      sfObjects.map((o) => ({ value: o.api_name, label: o.label })),
    [sfObjects],
  );
  // Per target object: its fields, external-ID/lookup fields first, and the
  // Target Field picker options built from them. Filled lazily and reset
  // when sfObjects changes -- every field row of the active STTM table used
  // to copy + sort its object's whole field list on every render.
  const targetFieldCache = useMemo(
    () =>
      new Map<
        string,
        { fields: SalesforceField[]; options: SearchableFieldOption[] }
      >(),
    // sfObjects is unused in the body on purpose: a new object list resets the cache.
    [sfObjects],
  );
  // Target Platform step also supports a plain RDBMS target (not just
  // Salesforce/Dynamics/Power Platform) — reuses the same connection
  // machinery as the Data Loader's "Create Tables" step
  // (targetConnectionId/ConnectDatabaseDialog(kind="target")) since it's
  // the same real-world target database, just connected earlier in the
  // flow to scan its schema for mapping purposes.
  //
  // "mirror" is a third mode for when there's no real target model at
  // all yet: rather than connecting/scanning anything, it auto-generates
  // one that's a 1:1 mirror of the source schema (see
  // handleGenerateMirrorTargetModel) and skips straight to Review & Export
  // — there's nothing to match/pick a target object for since the target
  // IS the source, structurally.
  const [targetPlatformMode, setTargetPlatformMode] = useState<
    "crm" | "rdbms" | "mirror"
  >("crm");
  const [generatingMirrorTargetModel, setGeneratingMirrorTargetModel] =
    useState(false);
  const [scanningTargetRdbms, setScanningTargetRdbms] = useState(false);
  // Flags what changed since the project's last target-schema scan (added/
  // removed/modified tables & columns) — null until a scan with a diff to
  // show has run; undefined-vs-null distinguishes "haven't scanned yet"
  // from "scanned, nothing to compare against (first scan) or no changes".
  const [targetSchemaChanges, setTargetSchemaChanges] =
    useState<SchemaDiff | null>(null);
  // Which of the 3 top-level buttons is active — kept separate from
  // targetPlatformMode because "No Target Model > Import Target Model"
  // renders its own panel but still needs targetPlatformMode="rdbms"
  // under the hood (same real-target-DB, needs-field-mapping machinery
  // "RDBMS / Database" uses) rather than "mirror"'s skip-mapping one.
  const [targetModelChoice, setTargetModelChoice] = useState<
    "crm" | "rdbms" | "no_target"
  >("crm");
  // Sub-choice within "No Target Model": "mirror" (unchanged auto-mirror
  // behavior) or "import" (upload a DDL/schema file, create those tables
  // in a connected target DB, then map/load like a real RDBMS target).
  const [noTargetSubOption, setNoTargetSubOption] = useState<
    "mirror" | "import"
  >("mirror");
  const [importedTargetModelFile, setImportedTargetModelFile] =
    useState<File | null>(null);
  const importedTargetModelFileInputRef = useRef<HTMLInputElement>(null);
  const [parsingImportedTargetModel, setParsingImportedTargetModel] =
    useState(false);
  const [importedTargetSchema, setImportedTargetSchema] =
    useState<SchemaExtractResult | null>(null);
  const [creatingImportedTargetModel, setCreatingImportedTargetModel] =
    useState(false);
  const [importedTargetModelCreateResult, setImportedTargetModelCreateResult] =
    useState<{ created: number; upToDate: number } | null>(null);
  // Server-side persisted target platform connection — mirrors the
  // dbConn/ddlTargetConn savedXDb pattern, hitting the new project-scoped
  // crm-connection endpoints.
  const [savedCrmConn, setSavedCrmConn] = useState<CrmConnectionOut | null>(
    null,
  );

  // Functional-domain skill selection (Child Support, Child Welfare,
  // Integrated Eligibility, Medicaid, ...) is no longer a manual checkbox —
  // the backend auto-selects the best-matching domain from the scanned
  // schema's own table/column names on every mapping-generate call (see
  // ztd_app.tools.converter.functional_skills.
  // auto_select_functional_skills + the router's _auto_apply_functional_
  // skill). Cross-agent "integration skills" (Metadata Curator / Data
  // Profiling / Data Modeler enrichment) were removed the same way — use
  // the "Related Agents" jump buttons on the landing page instead of
  // toggling inline enrichment. The Data Dictionary output that used to be
  // gated behind an "integration skill" toggle is now always generated at
  // STTM Finalize (see the `true /* always generate */` sites below).

  // Mapping state
  const [mappings, setMappings] = useState<TableObjectMapping[]>([]);
  // A joined mapping (several source tables materialized into one staging
  // table) has no single real source table left to read directly from via
  // the CRM push flow's simple single-table SELECT — direct load is only
  // offered for simple, non-joined mappings there. The RDBMS direct flow
  // reuses the full Load Data job (same one the staged flow runs), which
  // already builds and executes the real join query, so joins are fine
  // there and this restriction doesn't apply.
  //
  // 2026-09-16, bugfix: this MUST be declared here (immediately after the
  // `mappings` state it depends on), not further down near where it's
  // rendered. `selectAction`/`handleStartFresh` below reference it, and
  // this component has several early `return` statements (action===null,
  // the mapping/loader entry-choice screens) between here and where this
  // used to be declared. A plain `const` after an early return is only
  // initialized on renders that don't take that early return — any render
  // that does take it leaves this binding permanently in its temporal
  // dead zone, so calling `selectAction`/`handleStartFresh` from one of
  // those screens (e.g. clicking "Data Loader" from the home screen, then
  // "Start Fresh") threw `ReferenceError: Cannot access
  // 'hasJoinedMappingsForDirectLoad' before initialization` and silently
  // broke the rest of that render (matches user report: clicking through
  // the Data Loader flow appeared to do nothing / bounced back to the
  // home screen).
  const hasJoinedMappingsForDirectLoad =
    targetPlatformMode === "crm" &&
    mappings.some((m) => (m.source_tables?.length ?? 0) > 1);
  const [expandedMappings, setExpandedMappings] = useState<Set<string>>(
    new Set(),
  );
  // 2026-09-01, requested by user: a table with many columns is hard to
  // scan inside the row's inline 400px-tall scroller — tracks which single
  // table's field-mapping rows (if any) are currently popped out into a
  // full-screen window instead (see FieldMappingPopoutModal usage below).
  const [popoutTableKey, setPopoutTableKey] = useState<string | null>(null);
  // "Unmapped tables" callout is collapsed by default — with schemas that
  // have dozens/hundreds of tables, expanded-by-default pushed the actual
  // mapping controls far down the page.
  const [unmappedTablesOpen, setUnmappedTablesOpen] = useState(false);
  const [unmappedReviewTablesOpen, setUnmappedReviewTablesOpen] =
    useState(false);
  // Source tables to include in auto-mapping — re-seeded to "all tables"
  // whenever a fresh schema scan completes (see runSourceScan).
  const [selectedSourceTablesForMapping, setSelectedSourceTablesForMapping] =
    useState<Set<string>>(new Set());
  const [sourceTableMappingFilter, setSourceTableMappingFilter] = useState("");
  // Per-extra-connection equivalent of sourceTableMappingFilter — the
  // primary connection's group keeps sharing sourceTableMappingFilter
  // unchanged (mirror/auto-map table-selection stays byte-for-byte the
  // same for a single-connection scan); each additional connection gets
  // its own separate search box instead.
  const [extraMappingTableFilters, setExtraMappingTableFilters] = useState<
    Record<string, string>
  >({});
  // The full object catalogue for whichever target platform is connected
  // (Salesforce standard/custom objects, or Dataverse entities) — fetched
  // live right after a successful Test Connection, replacing what used to
  // be a hardcoded Contact/Account-only picker list.
  const [availableTargetObjects, setAvailableTargetObjects] = useState<
    SalesforceObjectMeta[]
  >([]);
  const [loadingTargetObjects, setLoadingTargetObjects] = useState(false);

  // Multi-pair mapping: each entry is one source table → one target SF object.
  const [mappingPairs, setMappingPairs] = useState<
    {
      sourceTable: string;
      targetObject: string;
      // No suitable target object exists (or none is wanted) — skip target
      // matching entirely and carry every source column through unchanged
      // (see buildIdentityMapping). targetObject mirrors sourceTable while
      // this is set, purely for display/duplicate-detection purposes.
      useSourceAsIsTarget?: boolean;
    }[]
  >([]);
  // Bulk-add: pick several source tables and several target objects, then
  // "Add" appends one pair per selection, matched positionally (selection
  // order in each multi-select) — lets a user build up many pairs in one
  // step instead of adding + configuring one row at a time. The per-row
  // list below still supports fixing up individual pairs afterward.
  // Keyed by connection group ("__primary__" or an extra's connectionId,
  // matching groupTablesByConnection's `key`) so each connection gets its
  // own independent Add-pair row/selection — picking source tables for one
  // connection never touches another connection's picker, and each row's
  // source-table options are scoped to that connection's tables only.
  const [bulkSourceTablesByGroup, setBulkSourceTablesByGroup] = useState<
    Record<string, string[]>
  >({});
  const [bulkTargetObjectsByGroup, setBulkTargetObjectsByGroup] = useState<
    Record<string, string[]>
  >({});
  // 2026-09-01, requested by user: default to a single src → target pair
  // per "Add" click; multi-select (building several pairs at once) is now
  // an explicit opt-in via this toggle instead of always being available.
  const [bulkMappingMode, setBulkMappingMode] = useState(false);

  const addBulkMappingPairs = (groupKey: string) => {
    const sourceTables = bulkSourceTablesByGroup[groupKey] ?? [];
    const targetObjects = bulkTargetObjectsByGroup[groupKey] ?? [];
    if (sourceTables.length === 0 && targetObjects.length === 0) return;
    // The "Duplicate target" badge above already warns when several pairs
    // land on the same target object.
    const newPairs = buildBulkMappingPairs(sourceTables, targetObjects);
    setMappingPairs((p) => [...p, ...newPairs]);
    setBulkSourceTablesByGroup((prev) => ({ ...prev, [groupKey]: [] }));
    setBulkTargetObjectsByGroup((prev) => ({ ...prev, [groupKey]: [] }));
  };
  const removeMappingPair = (idx: number) =>
    setMappingPairs((p) => p.filter((_, i) => i !== idx));
  // Deleting a mapping row here doesn't just remove it from the review
  // list — its source table(s) become "unmapped" again (see
  // unmappedSourceTables below), surfaced in a dedicated section so the
  // user always knows what's left to map.
  const removeMapping = (idx: number) =>
    setMappings((prev) => prev.filter((_, i) => i !== idx));
  const updateMappingPair = (
    idx: number,
    field: "sourceTable" | "targetObject",
    value: string,
  ) =>
    setMappingPairs((p) =>
      p.map((pair, i) => {
        if (i !== idx) return pair;
        const updated = { ...pair, [field]: value };
        // Keep an "as-is" pair's target in sync with its source table, so
        // switching the source table doesn't leave a stale target name.
        if (field === "sourceTable" && pair.useSourceAsIsTarget) {
          updated.targetObject = value;
        }
        return updated;
      }),
    );
  const toggleUseSourceAsIsTarget = (idx: number) =>
    setMappingPairs((p) =>
      p.map((pair, i) => {
        if (i !== idx) return pair;
        const next = !pair.useSourceAsIsTarget;
        return {
          ...pair,
          useSourceAsIsTarget: next,
          targetObject: next ? pair.sourceTable : "",
        };
      }),
    );

  // STTM state
  const [projectName, setProjectName] = useState("Data Migration");
  const [sttmResult, setSttmResult] = useState<SttmGenerateResponse | null>(
    null,
  );
  const [editedMappings, setEditedMappings] = useState<TableObjectMapping[]>(
    [],
  );
  const [sttmActiveTab, setSttmActiveTab] = useState<number>(0);
  const [sttmTabFilter, setSttmTabFilter] = useState("");
  // The tab bar scrolls once it is height-capped (see sttmTabsToShow), so a
  // tab activated from elsewhere (adding a field, a join, restoring a split)
  // can sit below the visible rows. Scroll only the bar itself:
  // scrollIntoView would also move the page.
  const sttmTabBarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const bar = sttmTabBarRef.current;
    const tab = bar?.querySelector<HTMLElement>(
      '[data-sttm-active-tab="true"]',
    );
    if (!bar || !tab || bar.scrollHeight <= bar.clientHeight) return;
    if (tab.offsetTop < bar.scrollTop) {
      bar.scrollTop = tab.offsetTop;
    } else if (
      tab.offsetTop + tab.offsetHeight >
      bar.scrollTop + bar.clientHeight
    ) {
      bar.scrollTop = tab.offsetTop + tab.offsetHeight - bar.clientHeight;
    }
  }, [sttmActiveTab, sttmTabFilter]);
  const [downloadingTableIdx, setDownloadingTableIdx] = useState<number | null>(
    null,
  );
  // A whole table's mapping is "soft-deleted" here (index kept, just
  // hidden) rather than spliced out of `editedMappings` — sttmActiveTab,
  // removedFieldMappings, and the keep-fields selection are all keyed by
  // index into that array, so removing an entry outright would silently
  // misalign every one of them for every row after it.
  const [removedTableMappings, setRemovedTableMappings] = useState<Set<number>>(
    new Set(),
  );
  const removeTableMapping = (mappingIdx: number) => {
    setRemovedTableMappings((prev) => new Set(prev).add(mappingIdx));
    if (sttmActiveTab === mappingIdx) {
      // Prefer a tab the current filter still shows, so removing the active
      // table doesn't jump to an unrelated one the filter is hiding.
      const stillShown = sttmTabsToShow(
        editedMappings,
        new Set(removedTableMappings).add(mappingIdx),
        -1,
        sttmTabFilter,
      ).visible;
      const nextIdx =
        stillShown[0] ??
        editedMappings.findIndex(
          (_, i) => i !== mappingIdx && !removedTableMappings.has(i),
        );
      if (nextIdx >= 0) setSttmActiveTab(nextIdx);
    }
  };
  // Inline "add another table to this join" picker — mappingIdx of the tab
  // currently showing the picker (null = closed), plus its in-progress form
  // fields. Calls the backend's generate-complex endpoint on submit so the
  // newly joined table's fields get the same real column-matching logic as
  // the initial auto-detected join, instead of landing unmapped.
  const [joinPickerMappingIdx, setJoinPickerMappingIdx] = useState<
    number | null
  >(null);
  const [joinPickerTable, setJoinPickerTable] = useState("");
  // One entry per AND'd column pair — a composite (multi-column) join key
  // is expressed as more than one pair here, joined with "," per side on
  // submit (see split_join_columns on the backend). Always at least one.
  const [joinPickerColumnPairs, setJoinPickerColumnPairs] = useState<
    { left: string; right: string }[]
  >([{ left: "", right: "" }]);
  const [joinPickerJoinType, setJoinPickerJoinType] = useState<
    "inner" | "left" | "right" | "full" | "cross"
  >("inner");
  // Which of joinPickerTable's columns to actually pull into the mapping
  // (vs. every column landing in, mapped or not) — defaults to "all
  // selected" whenever joinPickerTable changes, so leaving every checkbox
  // checked reproduces today's "include everything" behavior.
  const [joinPickerSelectedColumns, setJoinPickerSelectedColumns] = useState<
    string[]
  >([]);
  const [joiningTable, setJoiningTable] = useState(false);
  // One undo slot per mapping row for ungroupTableFromMapping — a full
  // pre-split snapshot of that row plus the index/indices of whichever new
  // standalone tab(s) the split created, so "Add Back" can restore the
  // exact prior join (not just re-run the picker and risk different field
  // matching).
  const [removedJoinTables, setRemovedJoinTables] = useState<
    Record<
      number,
      Array<{
        table: string;
        priorMapping: TableObjectMapping;
        newMappingIndices: number[];
      }>
    >
  >({});
  // Inline "remap this tab to a different target object" picker — mirrors
  // the join picker above. Re-runs the same generate-complex endpoint
  // against the new object so the tab's fields land pre-matched instead of
  // blank, which matters most right after ungroupTableFromMapping spins a
  // removed table off into its own standalone tab still pointed at the
  // original (possibly wrong) joined target.
  const [retargetMappingIdx, setRetargetMappingIdx] = useState<number | null>(
    null,
  );
  const [retargetObjectApiName, setRetargetObjectApiName] = useState("");
  const [retargeting, setRetargeting] = useState(false);
  // Inline "rename this as-is/mirror target table" picker — a lightweight
  // alternative to "Change Target" for identity mappings (no real target
  // object to match against, just a plain rename of the auto-derived
  // target table name). See handleRenameIdentityTarget.
  const [renamingMappingIdx, setRenamingMappingIdx] = useState<number | null>(
    null,
  );
  const [renameTargetInput, setRenameTargetInput] = useState("");
  // Which field-mapping row's Source Table cell is showing its reassign
  // picker instead of plain text — keyed "mappingIdx-fieldIdx", null when
  // none. Only relevant for joined mappings (>1 source table).
  const [editingSourceTableCell, setEditingSourceTableCell] = useState<
    string | null
  >(null);
  const [sttmHistory, setSttmHistory] = useState<
    Array<{
      version: number;
      label: string;
      timestamp: string;
      mappings: TableObjectMapping[];
      sttmResult: SttmGenerateResponse | null;
    }>
  >([]);
  const [sttmHistoryOpen, setSttmHistoryOpen] = useState(false);

  // Import an existing STTM workbook (previously generated or externally
  // authored/edited) and reconstruct its mappings, bypassing regeneration.
  const [importingSttm, setImportingSttm] = useState(false);
  const [sttmImportInfo, setSttmImportInfo] = useState<{
    filename: string;
    table_count: number;
    total_mapping_rows: number;
  } | null>(null);

  // Import transformation rules (Table/Column/Dimension/Description workbook)
  const [importingRules, setImportingRules] = useState(false);
  const [importRulesResult, setImportRulesResult] =
    useState<TransformRulesImportResponse | null>(null);
  // Reviewer's per-rule exclude/include decision, keyed by "table::column"
  // (one entry per importRulesResult.applied row) — defaults to "include"
  // with the imported Description as a starting point for real Transform
  // logic, since prose alone never executes (see compile_transformation).
  const [dqRuleDecisions, setDqRuleDecisions] = useState<
    Record<string, { action: "exclude" | "include"; logic: string }>
  >({});
  // Rules the reviewer deleted from this batch before the final Apply —
  // keyed the same way as dqRuleDecisions ("table::column"). Deleted rules
  // are skipped entirely by handleApplyDqDecisions and can be restored via
  // "Add Back" until Apply DQ Decisions actually runs.
  const [dqRuleRemoved, setDqRuleRemoved] = useState<Set<string>>(new Set());
  const [applyingDqDecisions, setApplyingDqDecisions] = useState(false);
  const [dqDecisionsApplied, setDqDecisionsApplied] = useState<number | null>(
    null,
  );
  // table.column pairs a rule decision referenced but couldn't be applied
  // to (e.g. the mapping was edited/removed after the rule was imported) —
  // surfaced next to the "Applied to N fields" banner so a decision that
  // silently didn't take effect isn't invisible.
  const [dqDecisionsNotFound, setDqDecisionsNotFound] = useState<
    { table: string; column: string }[]
  >([]);

  // Import transformation rules from legacy artifacts (SQL, PL/SQL, SSIS,
  // Informatica, BODS, Excel) — parsed + auto-mapped server-side, persisted
  // as an audit-trail batch, and gated behind per-item human review before
  // anything is applied to the STTM.
  const [importingLegacyRules, setImportingLegacyRules] = useState(false);
  const [legacyRulesError, setLegacyRulesError] = useState<string | null>(null);
  const [legacyRulesBatch, setLegacyRulesBatch] =
    useState<LegacyRuleBatch | null>(null);
  const [legacyRulesPanelOpen, setLegacyRulesPanelOpen] = useState(false);
  const [legacyRulesHistory, setLegacyRulesHistory] = useState<
    LegacyRuleBatch[]
  >([]);
  const [legacyRulesHistoryOpen, setLegacyRulesHistoryOpen] = useState(false);
  const [editingLegacyItemId, setEditingLegacyItemId] = useState<string | null>(
    null,
  );
  const [editingLegacyItemValue, setEditingLegacyItemValue] = useState("");
  const [applyingLegacyBatch, setApplyingLegacyBatch] = useState(false);
  // Reviewer's manual target-field pick for items the auto-mapper couldn't
  // resolve — keyed by item id, value is "TargetObject::target_field".
  const [legacyItemTargetPick, setLegacyItemTargetPick] = useState<
    Record<string, string>
  >({});
  const [expandedLegacyItemId, setExpandedLegacyItemId] = useState<
    string | null
  >(null);

  // Upload a data-mapping document (at the user's discretion) on the
  // "mapping" step — "replace" adopts it as the mapping outright, "seed"
  // uses it as a starting point for the normal heuristic/LLM generator.
  const [importingMappingDoc, setImportingMappingDoc] = useState(false);
  const [mappingImportMode, setMappingImportMode] = useState<
    "replace" | "seed"
  >("replace");
  const [mappingImportWarnings, setMappingImportWarnings] = useState<string[]>(
    [],
  );

  // "Keep only these fields" bulk filter — per active tab, a checkbox pick
  // list of source columns (keyed by uppercased source_column). Opening the
  // panel for a tab defaults every currently-present field to checked, so
  // unchecking is how you exclude a column.
  const [keepFieldsOpen, setKeepFieldsOpen] = useState(false);
  const [keepFieldsSelected, setKeepFieldsSelected] = useState<
    Record<number, Set<string>>
  >({});

  // Paste-a-list alternative to clicking each checkbox above — e.g. a
  // column list copied straight out of a source-system field doc.
  const [keepFieldsPasteOpen, setKeepFieldsPasteOpen] = useState(false);
  const [keepFieldsPasteText, setKeepFieldsPasteText] = useState("");

  // Rows removed (individually or via the bulk filter) per tab, so they can
  // be added back without re-generating the whole mapping.
  const [removedFieldMappings, setRemovedFieldMappings] = useState<
    Record<number, SttmFieldMapping[]>
  >({});
  const [removedFieldsOpen, setRemovedFieldsOpen] = useState(false);

  // DDL / Create Tables state
  const [ddlTargetSchema, setDdlTargetSchema] = useState("");
  // Table-naming override for the Generate DDL step, applied instead of
  // the automatic per-platform staging suffix (stagingSuffix below) when
  // not "auto". Read by stagedTargetTable — the SAME helper Load Data/
  // Reconciliation/SF Load use to build target table names — so every
  // step keeps agreeing on the same table name (see the PR #556
  // ORA-00942 regression note on stagedTargetTable for why that matters).
  const [tableNamingMode, setTableNamingMode] = useState<
    "auto" | "prefix" | "suffix" | "none"
  >("auto");
  const [tableNamingValue, setTableNamingValue] = useState("");
  // "all" applies tableNamingMode to every table; otherwise a set of
  // mapping.target_object names it's scoped to — every other table keeps
  // the automatic suffix regardless of tableNamingMode.
  const [tableNamingScope, setTableNamingScope] = useState<"all" | Set<string>>(
    "all",
  );
  // Whether Generate DDL appends the standard audit columns (created/
  // modified datetime + by) to every table. Defaults to on to preserve
  // this app's existing behavior (these columns were unconditional
  // before this toggle existed) — unchecking opts a run out entirely.
  const [includeAuditColumns, setIncludeAuditColumns] = useState(true);
  // 2026-09-02, requested by user: "enable the ability for me to choose the
  // schema on the target loader based on the connection" — the schemas
  // that already exist on the currently connected target (same live-test
  // result the source side's schema dropdown already uses), so Target
  // Schema Name can be picked from a real list instead of typed blind.
  // Empty until a live target connect/list-schemas call actually runs —
  // switching to a manual schema name (one that doesn't exist yet, e.g.
  // ahead of Create Staging Tables creating it) always stays possible.
  const [targetSchemas, setTargetSchemas] = useState<string[]>([]);
  const [targetSchemaManualEntry, setTargetSchemaManualEntry] = useState(false);
  const [loadingTargetSchemas, setLoadingTargetSchemas] = useState(false);
  const [ddlStatements, setDdlStatements] = useState<DdlStatement[]>([]);
  const [ddlExecuteResults, setDdlExecuteResults] = useState<
    DdlExecuteResult[] | null
  >(null);
  // Per-table drift status against the live target DB, checked
  // automatically as part of "Create Tables" — a table reported
  // "up_to_date" is left completely untouched (no DROP, no CREATE) so the
  // existing "Truncate before load" option at Load Data time is all that's
  // needed for it; only "create"/"recreate" tables are actually sent to
  // /ddl/execute.
  const [ddlReconcileResults, setDdlReconcileResults] = useState<
    DdlReconcileResult[] | null
  >(null);
  const [reconcilingDdl, setReconcilingDdl] = useState(false);
  // Drop Tables is a standalone action (separate from Create Tables), so it
  // gets its own loading/result/confirm-armed state.
  const [dropTablesLoading, setDropTablesLoading] = useState(false);
  const [dropTablesResult, setDropTablesResult] =
    useState<DdlDropResponse | null>(null);
  const [dropTablesArmed, setDropTablesArmed] = useState(false);
  // Target (staging) DB state — same connection_id pattern as the source.
  // Reused as-is for the Load Data, Reconcile, and DB→GovConnect steps
  // below (the staging DB acts as the load target and, later, as the
  // "source" for the push to GovConnect) — no separate copies needed.
  const [targetConnectionId, setTargetConnectionId] = useState<string | null>(
    null,
  );
  const [targetConnLabel, setTargetConnLabel] = useState("");
  // The connected target DB's actual platform (e.g. "oracle", "postgres",
  // "snowflake") — set whenever a target connection succeeds (new, saved,
  // or refreshed). Used (alongside sfConn.target_type in CRM mode) to
  // derive the staging-table suffix below instead of hardcoding "_sf" —
  // see stagingTableSuffix in lib/stagingTableSuffix.ts — and to label
  // generated DDL comments dynamically instead of always assuming
  // Salesforce (see _RDBMS_DIALECT_LABELS/targetPlatformLabel below).
  const [targetDbType, setTargetDbType] = useState<string | null>(null);
  const [showTargetConnectDialog, setShowTargetConnectDialog] = useState(false);
  const [ddlCopied, setDdlCopied] = useState<string | null>(null);
  // Brief "Refreshed!" confirmation on the Load to GovConnect
  // "Refresh Connection" button — mirrors ddlCopied's copy-confirmation flash.
  const [sfLoadConnRefreshed, setSfLoadConnRefreshed] = useState(false);
  const [ddlDuplicates, setDdlDuplicates] = useState<DdlDuplicate[]>([]);
  // Per-duplicate reviewer decision, keyed by `${table}::${column ?? ""}`.
  // Defaults to "remove" (today's silent-auto-dedup behavior) until the
  // user picks Keep/Edit for a given duplicate, then "Apply & Regenerate
  // DDL" resends these as duplicate_resolutions instead of silently
  // dropping every duplicate.
  const [ddlDuplicateDecisions, setDdlDuplicateDecisions] = useState<
    Record<string, { action: "remove" | "keep" | "rename"; newName: string }>
  >({});
  const [ddlDuplicatesExpanded, setDdlDuplicatesExpanded] = useState(true);
  // Which target table's tab is showing in the duplicate-review list below —
  // grouped by target table instead of one long flat list, since a large
  // schema's duplicates are otherwise unreadable (see ddlDuplicatesByTable).
  // `null` means "no explicit choice yet, fall back to the first tab."
  const [ddlDuplicateActiveTable, setDdlDuplicateActiveTable] = useState<
    string | null
  >(null);
  // Running log of every duplicate/mismatch a "default" action was applied
  // to (per-table or whole-population), so the on-screen list can be
  // cleared immediately after a decision without losing the record of what
  // was decided and why — downloadable via downloadSttmMappingLog below.
  const [sttmMappingLog, setSttmMappingLog] = useState<string[]>([]);
  const [ddlTargetConnected, setDdlTargetConnected] = useState<{
    db_version?: string;
    schemas?: string[];
  } | null>(null);

  // The platform this migration's staging tables are ultimately named for:
  // the CRM target_type when Target Platform is in CRM mode (e.g.
  // "salesforce"), or the connected target DB's own db_type in RDBMS mode.
  // Every staging/target table name built anywhere in this component MUST
  // derive its suffix from this same value (via stagingTableSuffix) so
  // Create Tables and Load Data/Reconciliation/SF Load always agree on the
  // table name — see the PR #556 ORA-00942 regression note in
  // generate_ddl (service.py) for what happens when they don't.
  const activeTargetPlatform =
    targetPlatformMode === "crm" ? sfConn.target_type : targetDbType;
  const stagingSuffix = stagingTableSuffix(activeTargetPlatform);
  // Single helper for every call site that builds a staged target table
  // name from a mapping's target_object — keeps them all in lockstep with
  // stagingSuffix instead of re-deriving `${targetObject}${stagingSuffix}`
  // independently at each of the (previously 9) call sites.
  //
  // 2026-09-07, requested by user: an RDBMS target loaded "Directly" (no
  // staging tables at all — see the loaderCrmStrategyPending choice card)
  // must resolve to the REAL final table name, not a staging-suffixed one
  // that's never created in that flow. CRM's "direct" mode is unaffected
  // here — it never reads this helper's staging-table branch anyway (its
  // own source/target table names are configured on the sf_load tab, see
  // computeSfLoadSourceTableDefault below).
  //
  // Optional `strategyOverride`: the "Directly" button's onClick calls
  // setCrmLoadStrategy("direct") and, in the same synchronous handler,
  // needs THIS call's result computed as if that state update had already
  // landed — but React state setters don't apply until the next render,
  // so reading crmLoadStrategy here would still see the old (null) value
  // and wrongly fall through to the suffixed branch. Passing "direct"
  // explicitly bypasses that stale-closure read; every other call site
  // omits it and keeps reading the live crmLoadStrategy state as before.
  const stagedTargetTable = (
    targetObject: string,
    strategyOverride?: "staged" | "direct" | null,
  ) => {
    if (
      (strategyOverride ?? crmLoadStrategy) === "direct" &&
      targetPlatformMode === "rdbms"
    ) {
      return targetObject;
    }
    // Table-naming override (see its state declarations above) takes
    // priority over the automatic suffix, but only for tables in scope —
    // everything else falls through to the historical `${target}${suffix}`
    // behavior so this stays backward compatible when left at "auto".
    if (
      tableNamingMode !== "auto" &&
      (tableNamingScope === "all" || tableNamingScope.has(targetObject))
    ) {
      if (tableNamingMode === "prefix")
        return `${tableNamingValue}${targetObject}`;
      if (tableNamingMode === "suffix")
        return `${targetObject}${tableNamingValue}`;
      return targetObject; // "none"
    }
    return `${targetObject}${stagingSuffix}`;
  };
  // Distinct target_object names across the current STTM mappings — feeds
  // the "apply to selected tables" checkbox list in the Generate DDL
  // section's table-naming controls.
  const ddlTargetTableNames = useMemo(
    () => Array.from(new Set(mappings.map((m) => m.target_object))),
    [mappings],
  );
  // 2026-09-02, requested by user: single source of truth for which table
  // the Load to Target step reads from by default, so "staged" always
  // means the staging table (never the real source table) even when the
  // source and staging connections happen to be the same physical
  // database/schema (source and staging table NAMES still differ via
  // stagingSuffix, so there's no ambiguity as long as this — not a
  // re-derivation at each call site — is what every call site uses).
  // Always user-overridable per tab (see SfLoadTabState.sourceTable).
  const computeSfLoadSourceTableDefault = (mapping: TableObjectMapping) =>
    crmLoadStrategy === "direct"
      ? mapping.source_table
      : stagedTargetTable(mapping.target_object);

  // Remember Salesforce connection details locally (localStorage) so the
  // form prefills next time. Values never leave the browser and are never
  // committed. (The DB source/target connections above use the shared
  // project-level connection_id instead — no raw credentials persisted.)
  useEffect(() => saveConn("sfConn", sfConn), [sfConn]);

  // Re-sync sourceConnectionId/targetConnectionId (the ids
  // ConnectDatabaseDialog also writes to — see admin/project_db_config's
  // single bound connection per project+kind) after a Data-Converter-scoped
  // ConverterSavedConnection row is activated via SavedConnectionsPanel
  // (service.activate_saved_connection pushes it into that same bound slot
  // under the hood), so the two saved-connection mechanisms agree on which
  // connection is actually live.
  const refreshSourceConnectionFromSaved = useCallback(
    async (label?: string) => {
      if (!projectId) return;
      try {
        const cfg = await apiv2.getSourceDB(projectId);
        if (!cfg) return;
        setSourceConnectionId(cfg.id);
        setSourceConnLabel(label ?? `${cfg.db_type} · ${cfg.host}`);
      } catch {
        // No bound connection yet (or not permitted to read it) — leave
        // sourceConnectionId as-is.
      }
    },
    [projectId],
  );

  // 2026-09-02, requested by user: same reasoning as
  // handleTargetConnectionActivatedFromLibrary below — the always-visible
  // Saved Connections library lets a user switch to a DIFFERENT saved
  // source connection even after one is already connected/scanned, so the
  // stale schema-test status (connStatus) must be cleared rather than keep
  // showing schemas from the connection that's no longer active.
  const handleSourceConnectionActivatedFromLibrary = useCallback(() => {
    setConnStatus(null);
    void refreshSourceConnectionFromSaved();
  }, [refreshSourceConnectionFromSaved]);

  const refreshTargetConnectionFromSaved = useCallback(
    async (label?: string, options?: { forceSchema?: boolean }) => {
      if (!projectId) return;
      try {
        const cfg = await apiv2.getTargetDB(projectId);
        if (!cfg) return;
        setTargetConnectionId(cfg.id);
        setTargetConnLabel(label ?? `${cfg.db_type} · ${cfg.host}`);
        setTargetDbType(cfg.db_type);
        // /target-db is RDBMS-only — restoring a saved connection here
        // must also restore RDBMS mode, otherwise targetPlatformMode stays
        // at its "crm" default and every later step (e.g. Data Loader's
        // "How should Data Loader reach Salesforce?") wrongly assumes a
        // Salesforce target even though a real RDBMS connection is bound.
        // A mirror-mode (or CRM-mode) project has a real saved target-DB
        // connection too (its staging tables have to land somewhere, and
        // switching modes in the UI doesn't delete the old row), so only
        // default to "rdbms" when the project's own persisted mode isn't
        // explicitly a non-default one — otherwise this silently clobbers
        // a deliberate Mirror/CRM choice back to "rdbms" on every resume.
        const savedMode = await apiv2
          .converterGetDdlState(projectId)
          .then((s) => s?.target_platform_mode)
          .catch(() => null);
        setTargetPlatformMode(
          savedMode === "mirror" || savedMode === "crm" ? savedMode : "rdbms",
        );
        const effectiveSchema = effectiveSchemaNameFromConnection(
          cfg.schema_name,
          cfg.db_type,
        );
        if (effectiveSchema) {
          // 2026-09-04, PR review: this function is also called by the
          // silent per-action hydration effect (projectId/action changed,
          // no explicit user action), which can race against that same
          // effect's own converterGetDdlState hydration — whichever
          // resolves second must never clobber a schema the other path
          // already recalled. options.forceSchema (only passed by an
          // explicit "Use This"/activate-from-library click) opts into
          // overriding regardless, since the user just deliberately
          // switched connections.
          if (options?.forceSchema) {
            setDdlTargetSchema(effectiveSchema);
          } else {
            setDdlTargetSchema((prev) => prev || effectiveSchema);
          }
        }
      } catch {
        // No bound connection yet (or not permitted to read it) — leave
        // targetConnectionId as-is.
      }
    },
    [projectId],
  );

  // 2026-09-02, requested by user: the always-visible Saved Connections
  // library (see the ddl/mapping/load steps below) lets a user switch to a
  // DIFFERENT saved target connection even after one is already
  // connected/tested — without this, ddlTargetConnected's db_version (and
  // the buttons it gates, e.g. "Create Staging Tables") would keep
  // reflecting the PREVIOUS connection until "Refresh Connection" is
  // clicked separately.
  const handleTargetConnectionActivatedFromLibrary = useCallback(() => {
    setDdlTargetConnected(null);
    // The just-switched-away-from connection's schema list no longer
    // applies here — refreshTargetConnectionFromSaved doesn't re-run a
    // live connect (see its own comment), so nothing repopulates this
    // until "List schemas" is clicked again for the new connection.
    setTargetSchemas([]);
    void refreshTargetConnectionFromSaved(undefined, { forceSchema: true });
  }, [refreshTargetConnectionFromSaved]);

  // Records the schema list from a live target connect/list-schemas result
  // (see build_loader_sql_preview's sibling test_connection, which already
  // returns this for every db_type) and decides whether the picker should
  // default to "choose from list" or "type manually" — manual when the
  // list is empty, or when the current Target Schema Name isn't one of
  // the schemas that actually exist yet (e.g. "MIGRATION" ahead of Create
  // Staging Tables creating it for the first time).
  const applyTargetSchemas = useCallback(
    (schemas: string[] | undefined) => {
      const list = schemas ?? [];
      setTargetSchemas(list);
      setTargetSchemaManualEntry(
        list.length === 0 || !list.includes(ddlTargetSchema),
      );
    },
    [ddlTargetSchema],
  );

  // On-demand (re)fetch of the currently connected target's schema list —
  // used both by the "List/Refresh schemas" button next to Target Schema
  // Name and after a fresh connect below. A separate action rather than
  // folded into every connection-refresh path so switching to a different
  // saved connection (which doesn't itself run a live connect) never
  // triggers a surprise live DB connection attempt on its own.
  const handleLoadTargetSchemas = useCallback(async () => {
    if (!targetConnectionId) return;
    setLoadingTargetSchemas(true);
    setError(null);
    try {
      const res = await apiv2.converterTargetConnect({
        connectionId: targetConnectionId,
        projectId,
      });
      applyTargetSchemas(res.schemas);
    } catch (e) {
      setErr(
        e instanceof Error
          ? e.message
          : "Failed to list schemas for this connection",
      );
    } finally {
      setLoadingTargetSchemas(false);
    }
  }, [targetConnectionId, projectId, applyTargetSchemas]);

  // Load-on-entry for the 3 actions that consume a mapping generated by
  // Source → Target Mapping rather than producing their own. Fetches the
  // project's persisted mapping (GET /project/{id}/mapping) whenever the
  // user switches into one of these actions; "missing" renders an empty
  // state (see the render section) instead of a step with nothing to act on.
  useEffect(() => {
    if (!action || action === "mapping") return;
    // No project context (standalone use) — nothing to fetch from, fall
    // back to whatever's already in memory from this session.
    if (!projectId) {
      setScopedMappingStatus(mappings.length > 0 ? "ready" : "missing");
      return;
    }
    let cancelled = false;
    setScopedMappingStatus("loading");
    apiv2
      .converterGetProjectMapping(projectId)
      .then((res) => {
        if (cancelled) return;
        if (res && res.mappings.length > 0) {
          setMappings(res.mappings);
          setEditedMappings(
            withDefaultStringTypes(JSON.parse(JSON.stringify(res.mappings))),
          );
          setScopedMappingStatus("ready");
        } else {
          setScopedMappingStatus("missing");
        }
      })
      .catch(() => {
        if (!cancelled) setScopedMappingStatus("missing");
      });
    // Resuming into Data Loader in a fresh session had no way to re-fill
    // Files mode's source files: legacyFileRefs only ever lived in this
    // session's React state, set once by handleParseLegacyCode and never
    // persisted anywhere. The legacy-parse routes now snapshot their result
    // (file_refs included) the same way a live DB scan does — restore it
    // here so "Auto-Fill Source Files from Mapping" has something to match
    // against instead of reporting 0 matches for every table.
    apiv2
      .converterGetSchemaSnapshot(projectId, "source")
      .then((snapshot) => {
        if (cancelled || !snapshot?.schema_result.file_refs) return;
        setLegacyFileRefs(snapshot.schema_result.file_refs);
      })
      .catch(() => {
        // Best-effort only — the manual "Choose File" fallback still works.
      });
    // Same gap as the two restores above, for the *target* (staging DB)
    // connection: refreshTargetConnectionFromSaved/
    // refreshSourceConnectionFromSaved previously only ever ran when the
    // user manually activated a saved connection in THIS session (see
    // their call sites) — resuming straight into Loader/Reconciliation/
    // Load to Target in a fresh session left targetConnectionId (and
    // sourceConnectionId for DB-mode loads) null, which is exactly what
    // silently disabled "Load Data from Files"/"Load Data" even though
    // the mapping itself had resumed correctly. Only fill in whichever
    // connection isn't already set in memory — never clobber a connection
    // the user just picked in this same session.
    if (!targetConnectionId) refreshTargetConnectionFromSaved();
    if (!sourceConnectionId) refreshSourceConnectionFromSaved();
    // 2026-09-04, reported by user: entering Reconciliation directly (never
    // having visited Loader/Create Staging Tables this session) left
    // ddlTargetSchema at whatever it happened to be (previously a
    // hardcoded "MIGRATION" placeholder) instead of the schema Create
    // Staging Tables actually created the target tables in for THIS
    // project — recall it from the project's own saved DDL state, same
    // authoritative source handleResumeChoice already hydrates from for
    // the "loader" resume path. Only fills in if nothing's set yet, so it
    // never clobbers a value the user already customized this session.
    if (!ddlTargetSchema) {
      apiv2
        .converterGetDdlState(projectId)
        .then((ddlState) => {
          if (cancelled) return;
          if (ddlState?.target_schema) {
            setDdlTargetSchema(ddlState.target_schema);
            return;
          }
          // A project can have successfully run staged Load-to-Target jobs
          // (db_to_sf) reading from real staging tables even though its
          // own converter_ddl_states row was never saved (DDL created
          // outside this fold, or the persist step was skipped) — the
          // last such job's own request_meta.source_schema is exactly the
          // staging schema it read from, so it's the next-best
          // authoritative fallback before leaving the field blank.
          apiv2
            .converterGetLatestLoadJob(projectId, "db_to_sf")
            .then((job) => {
              if (!cancelled && job?.source_schema) {
                setDdlTargetSchema(job.source_schema);
              }
            })
            .catch(() => {
              // Best-effort only — the Target Schema field stays editable.
            });
        })
        .catch(() => {
          // Best-effort only — the Target Schema field stays editable.
        });
    }
    return () => {
      cancelled = true;
    };
  }, [action, projectId, mappings.length]);

  const [loadJobId, setLoadJobId] = useState<string | null>(null);
  const [loadProgress, setLoadProgress] =
    useState<DataLoadStatusResponse | null>(null);
  const [dqReportDismissed, setDqReportDismissed] = useState(false);
  const [loadErrorBannerDismissed, setLoadErrorBannerDismissed] =
    useState(false);
  const [loadResultsCleared, setLoadResultsCleared] = useState(false);
  const loadPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Wall-clock timing for the DB-to-DB Load Data job, purely a frontend
  // stopwatch (the job status response carries no timestamps) — reset each
  // time a load/retry starts, frozen once the job leaves "running".
  const dbLoadStartedAtRef = useRef<number | null>(null);
  const [dbLoadElapsedMs, setDbLoadElapsedMs] = useState(0);
  interface MdmLookupConfig {
    lookupSchema: string;
    lookupTable: string;
    sourceMatchField: string;
    lookupMatchField: string;
    lookupFields: string; // comma-separated column names, no source equivalent
    overrideFields: string; // comma-separated column names, override a mapped source field
    // The lookup/golden-record table lives on a DIFFERENT database
    // connection than the source (own host/credentials, even a different
    // db_type) — toggled via a checkbox in the MDM Lookup panel. `connection`
    // holds those details once entered; false/undefined (the common case)
    // means the lookup table is on the SAME connection as the source,
    // joined in one SQL statement.
    useCustomConnection?: boolean;
    connection?: DbConnectionConfig;
    // "mdmRun" when the lookup table/connection above were auto-filled from
    // a real MDM agent run's completed Oracle golden-record push (picked
    // via the run selector below) instead of typed in by hand — that table
    // is always shaped `(cluster_id, golden_json CLOB)`, so
    // lookupMatchField/lookupFields/overrideFields name JSON keys inside
    // golden_json rather than real columns. undefined/"manual" (the
    // original, default behavior) is unaffected.
    sourceMode?: "manual" | "mdmRun";
    mdmRunId?: string;
  }
  const [loadMappingConfig, setLoadMappingConfig] = useState<
    {
      srcSchema: string;
      srcTable: string;
      tgtSchema: string;
      tgtTable: string;
      mdmLookup?: MdmLookupConfig;
      // Per-table row-limit override — "default" (or unset) defers to the
      // global Load Options default below; "all"/"limited" override it just
      // for this table.
      rowLimit?: "default" | "all" | "limited";
      rowCount?: number;
      // Which Load Order stage this table belongs to (only used when
      // loadOrderMode === "staged"). Tables sharing a stage number load
      // concurrently; stages run in ascending numeric order.
      loadStage?: number;
      // Per-table truncate-before-load override — "default" (or unset)
      // defers to the global Load Options default below; "yes"/"no"
      // override it just for this table.
      truncate?: "default" | "yes" | "no";
      // Whether this table is included in the next Load Data run. Defaults
      // to true (all tables load) — unchecking a table in Mapping Summary
      // excludes it without removing its mapping/config.
      included?: boolean;
      // Only used when loadSourceMode === "files" — this table's data comes
      // from an uploaded file instead of a query against sourceConnectionId
      // (see handleChooseSourceFileForRow/handleStartFileBasedLoad).
      sourceFile?: { file_path: string; filename: string };
      sourceFileFormat?: ConverterSourceFormat["format_type"];
      sourceFileFields?: ConverterSourceField[];
    }[]
  >([]);
  // "database" (default) reads every mapped table via a live query against
  // sourceConnectionId, same as always. "files" is for when there's no
  // source database at all — just a folder of files — so each mapped table
  // instead gets its own uploaded file (handleChooseSourceFileForRow) and
  // handleStartFileBasedLoad reads directly from those instead of running
  // any SELECT against a source connection.
  const [loadSourceMode, setLoadSourceMode] = useState<"database" | "files">(
    "database",
  );
  const [startingFileLoad, setStartingFileLoad] = useState(false);
  // Transient confirmation shown after (re-)choosing a source file for one
  // row — without this, re-selecting a file with the SAME name as before
  // (e.g. after re-exporting it to fix bad data) looks identical before and
  // after, since the persistent "<filename> — N column(s) detected" line
  // doesn't visibly change. Auto-clears after a few seconds.
  const [fileUploadNotice, setFileUploadNotice] = useState<{
    idx: number;
    filename: string;
    fieldCount: number;
  } | null>(null);
  const [fileLoadResults, setFileLoadResults] = useState<
    | (ConverterLoadResponse & {
        error?: string;
        tableName: string;
        durationMs?: number;
      })[]
    | null
  >(null);
  // Wall-clock elapsed time for the in-progress (or just-finished) file
  // load — the previous "one giant blocking request, no feedback until it
  // either finishes or the tab times out" UX gave no way to tell a load
  // that's genuinely still running from one that's silently stuck.
  const [fileLoadStartedAt, setFileLoadStartedAt] = useState<number | null>(
    null,
  );
  const [fileLoadElapsedMs, setFileLoadElapsedMs] = useState(0);
  // Background job id + poll interval for the file-mode load's async job
  // (see pollFileLoadJob) — separate from loadJobId/loadPollRef, which are
  // the DB-to-DB Load Data job's own equivalents.
  const [fileLoadJobId, setFileLoadJobId] = useState<string | null>(null);
  const fileLoadPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Set optimistically on Cancel click, cleared once the job actually
  // reaches a terminal status (see pollFileLoadJob) -- the backend already
  // honors cancellation for this job type (_run_file_bulk_load_job checks
  // is_cancel_requested between load-order stages), this just wires up the
  // same generic /data/load-cancel/{job_id} endpoint handleCancelDataLoad
  // already uses for DB-mode loads.
  const [fileLoadCancelRequested, setFileLoadCancelRequested] = useState(false);
  // Comma-joined table name(s) actively running right now, per the job's
  // current_table field (same shape _run_data_load_job's DB-mode job uses)
  // -- lets the file-mode progress list show Queued/Loading…/Done per table
  // instead of only the done-count the old synchronous flow could show.
  const [fileLoadCurrentTable, setFileLoadCurrentTable] = useState<
    string | null
  >(null);
  // Live in-flight row count per table currently loading (keyed by target
  // table name, same key current_table/table_results use) -- only the
  // regular chunked-insert path reports this; a bulk_load=True (PUT+COPY
  // INTO) table never appears here, same as DB-mode's own table_progress.
  const [fileLoadTableProgress, setFileLoadTableProgress] = useState<
    Record<string, DataLoadTableProgress>
  >({});
  // 2026-09-13, reported by user: starting a genuinely new load/mapping flow
  // (mirror's "Map & Load to a Target", or any "start fresh" mapping/loader
  // entry point) left the PREVIOUS flow's Load Results panel showing on the
  // "load" step — nothing cleared loadProgress/fileLoadResults, so the old
  // run's results looked like they belonged to the new flow before it had
  // even run once. Called from every "start fresh"/"genuinely new flow"
  // entry point; deliberately NOT called from the Resume-dialog's "Resume"
  // path (handleResumeChoice), which intentionally re-hydrates loadProgress
  // from the server for that path.
  const resetLoadResultsState = () => {
    if (loadPollRef.current) clearInterval(loadPollRef.current);
    loadPollRef.current = null;
    setLoadProgress(null);
    setLoadJobId(null);
    setDqReportDismissed(false);
    setLoadErrorBannerDismissed(false);
    setLoadResultsCleared(false);
    setLastLoadRowLimitSummary(null);
    if (fileLoadPollRef.current) clearInterval(fileLoadPollRef.current);
    fileLoadPollRef.current = null;
    setFileLoadJobId(null);
    setFileLoadResults(null);
    setFileLoadStartedAt(null);
    setFileLoadElapsedMs(0);
    // See skipLoaderResumeCheck's own comment above checkResumable — this
    // fresh mapping/load has nothing of its own to resume yet, so the
    // "loader" resumable-check should treat it that way until a real load
    // runs for it, instead of surfacing an old, now-superseded job.
    setSkipLoaderResumeCheck(true);
  };
  // Reconnecting to a (possibly different) source DB should re-target every
  // table's Load query at THAT connection's schema, not whatever schema was
  // baked into the mapping doc or a previously connected DB — otherwise
  // switching source connections on the Load step silently keeps querying
  // the old/stale schema. No-op if the Load step's mapping config hasn't
  // been populated yet (nothing to refresh).
  const refreshLoadSourceSchema = useCallback((schemaName: string) => {
    if (!schemaName) return;
    setLoadMappingConfig((prev) =>
      prev.length === 0
        ? prev
        : prev.map((cfg) => ({ ...cfg, srcSchema: schemaName })),
    );
  }, []);
  // Which mapping rows have their MDM Lookup panel expanded (by index).
  const [mdmLookupOpen, setMdmLookupOpen] = useState<Set<number>>(new Set());
  // Completed Oracle golden-record pushes from real MDM agent runs,
  // available to auto-fill the MDM Lookup panel's connection/schema/table
  // (see apiv2.listMdmOracleOutputs) — fetched lazily, once, the first time
  // any row switches its MDM Lookup source to "MDM Agent Run".
  const [mdmOracleOutputs, setMdmOracleOutputs] = useState<
    MdmOracleOutput[] | "loading" | "error" | null
  >(null);
  const ensureMdmOracleOutputs = useCallback(() => {
    if (mdmOracleOutputs !== null || !projectId) return;
    setMdmOracleOutputs("loading");
    apiv2
      .listMdmOracleOutputs(projectId)
      .then((outputs) => setMdmOracleOutputs(outputs))
      .catch(() => setMdmOracleOutputs("error"));
  }, [mdmOracleOutputs, projectId]);
  // 2026-09-03, reported by user: "Aggregate rollup (GROUP BY)" showed as
  // already checked for every mapping by default, with nothing actually
  // configured. Root cause: the backend's TableObjectMapping.group_by_columns
  // field always serializes as [] (Field(default_factory=list) in
  // patterns.py, never omitted), so a freshly-generated/imported mapping is
  // indistinguishable from "the user turned this on but hasn't picked any
  // columns yet" by presence-of-key alone. Tracks mapping indices where the
  // checkbox was actually turned on THIS session — a mapping with 1+ real
  // group_by_columns already unambiguously counts as "on" without needing
  // this set at all (see isAggregateModeEnabled below).
  const [aggregateModeEnabledIdx, setAggregateModeEnabledIdx] = useState<
    Set<number>
  >(new Set());
  // Which mapping rows have their Loader Script panel expanded (by index),
  // and each row's last-fetched SELECT/INSERT SQL preview (or a
  // loading/error sentinel) — see apiv2.converterPreviewLoaderSql.
  const [loaderScriptOpen, setLoaderScriptOpen] = useState<Set<number>>(
    new Set(),
  );
  const [loaderScriptPreview, setLoaderScriptPreview] = useState<
    Record<number, LoaderSqlPreviewOut | "loading" | { error: string }>
  >({});
  // 2026-09-02, requested by user: per-row "Save Script"/"Reset to
  // Auto-Generated" persist mapping.custom_load_sql via the same
  // full-mapping-array save Finalize uses — savingLoaderScript tracks the
  // in-flight save per row index, loaderScriptSavedAt a transient
  // "Saved" confirmation timestamp.
  const [savingLoaderScript, setSavingLoaderScript] = useState<Set<number>>(
    new Set(),
  );
  const [loaderScriptSavedAt, setLoaderScriptSavedAt] = useState<
    Record<number, number>
  >({});
  const handleSaveLoaderScript = async (idx: number) => {
    if (!projectId) {
      setErr("A project is required to save a custom loader script");
      return;
    }
    setSavingLoaderScript((prev) => new Set(prev).add(idx));
    try {
      await apiv2.converterSetProjectMapping(projectId, mappings);
      setLoaderScriptSavedAt((prev) => ({ ...prev, [idx]: Date.now() }));
    } catch (e) {
      setErr(
        e instanceof Error ? e.message : "Failed to save the loader script",
      );
    } finally {
      setSavingLoaderScript((prev) => {
        const next = new Set(prev);
        next.delete(idx);
        return next;
      });
    }
  };
  // Issue #599 — "Force Full Reload": deletes a table's saved incremental
  // watermark so its next run re-reads every row from scratch. Same
  // in-flight-set/transient-confirmation pattern as the loader script save
  // above.
  const [resettingWatermark, setResettingWatermark] = useState<Set<number>>(
    new Set(),
  );
  const handleResetLoadWatermark = async (idx: number, targetTable: string) => {
    if (!projectId) {
      setErr("A project is required to reset an incremental load watermark");
      return;
    }
    if (!targetTable) {
      setErr("This table has no target table name yet");
      return;
    }
    setResettingWatermark((prev) => new Set(prev).add(idx));
    try {
      await apiv2.converterResetLoadWatermark(projectId, targetTable);
      // Deleted server-side — reflect that immediately rather than waiting
      // for the next fetch effect run (targetTable/incremental_column
      // haven't changed, so it wouldn't re-fire on its own).
      setLoadWatermarks((prev) => ({ ...prev, [idx]: null }));
    } catch (e) {
      setErr(
        e instanceof Error
          ? e.message
          : "Failed to reset the incremental load watermark",
      );
    } finally {
      setResettingWatermark((prev) => {
        const next = new Set(prev);
        next.delete(idx);
        return next;
      });
    }
  };
  // Issue #599 — current saved watermark per incremental table, so the
  // panel can show "currently loaded up to X" instead of leaving the user
  // to guess where the next run will start from. Keyed by mapping index
  // (not target table) to match resettingWatermark's convention.
  const [loadWatermarks, setLoadWatermarks] = useState<
    Record<number, ConverterLoadWatermarkOut | null>
  >({});
  const incrementalWatermarkTargets = useMemo(
    () =>
      mappings
        .map((m, idx) => ({
          idx,
          targetTable:
            loadMappingConfig[idx]?.tgtTable ??
            stagedTargetTable(m.target_object),
          loadMode: m.load_mode,
        }))
        .filter((e) => e.loadMode === "incremental" && e.targetTable),
    [mappings, loadMappingConfig],
  );
  useEffect(() => {
    if (!projectId || incrementalWatermarkTargets.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        incrementalWatermarkTargets.map(async ({ idx, targetTable }) => {
          try {
            const wm = await apiv2.converterGetLoadWatermark(
              projectId,
              targetTable,
            );
            return [idx, wm] as const;
          } catch {
            return [idx, null] as const;
          }
        }),
      );
      if (cancelled) return;
      setLoadWatermarks((prev) => {
        const next = { ...prev };
        for (const [idx, wm] of entries) next[idx] = wm;
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, incrementalWatermarkTargets]);
  // "loaded up to <value>" for the incremental panel — the persisted
  // envelope's value is already the right display shape for every type
  // this can be (ISO datetime/date string, or a plain number/decimal string).
  const formatWatermarkValue = (wm: ConverterLoadWatermarkOut): string =>
    String(wm.last_value.value);
  // Lazily-scanned table/column metadata for MDM Lookup schemas, keyed by
  // schema name — populates the Lookup Table / Lookup Match Field / Lookup
  // Fields dropdowns without a full re-scan every time a panel is opened.
  // Shared across mapping rows since it's scoped to the source connection,
  // not any one row.
  const [mdmSchemaCache, setMdmSchemaCache] = useState<
    Record<string, SchemaExtractResult | "loading" | "error">
  >({});
  // A cross-database MDM lookup (see MdmLookupConfig.connection) scans a
  // totally different connection than the source -- keyed separately from
  // a same-connection schema of the same name so the two never collide.
  const mdmSchemaCacheKey = (
    schemaName: string,
    customConnection?: DbConnectionConfig,
  ) =>
    customConnection
      ? `custom:${customConnection.db_type}:${customConnection.host}:${customConnection.port}:${customConnection.database}:${schemaName}`
      : schemaName;
  const ensureMdmSchemaScanned = useCallback(
    (schemaName: string, customConnection?: DbConnectionConfig) => {
      if (!schemaName) return;
      if (!customConnection && !sourceConnectionId) return;
      const cacheKey = mdmSchemaCacheKey(schemaName, customConnection);
      if (mdmSchemaCache[cacheKey]) return;
      setMdmSchemaCache((prev) => ({ ...prev, [cacheKey]: "loading" }));
      apiv2
        .converterSourceScan(
          customConnection ?? { connectionId: sourceConnectionId!, projectId },
          schemaName,
        )
        .then((res) =>
          setMdmSchemaCache((prev) => ({ ...prev, [cacheKey]: res })),
        )
        .catch(() =>
          setMdmSchemaCache((prev) => ({ ...prev, [cacheKey]: "error" })),
        );
    },
    [sourceConnectionId, projectId, mdmSchemaCache],
  );

  const [loadRowLimit, setLoadRowLimit] = useState<"all" | "limited">("all");
  const [loadRowCount, setLoadRowCount] = useState<number>(100);
  // Global default: truncate every target table before loading into it, so
  // pre-existing rows from an earlier run don't get appended to/duplicated.
  const [loadTruncateBeforeLoad, setLoadTruncateBeforeLoad] =
    useState<boolean>(false);
  // CASCADE the truncate so FK-dependent child rows are cleared along with
  // it instead of the truncate failing outright. Only takes effect when
  // truncation itself is happening, and is silently ignored by the backend
  // for target dialects whose TRUNCATE syntax doesn't support CASCADE
  // (only Oracle/PostgreSQL do).
  const [loadTruncateCascade, setLoadTruncateCascade] =
    useState<boolean>(false);
  // Optional pre-flight check: runs the real insert against each target
  // table (so genuine type/length/null/FK constraint errors surface) but
  // rolls back every table's connection instead of committing — no data is
  // ever written. Off by default; a real load still has to run afterward.
  const [loadDryRun, setLoadDryRun] = useState<boolean>(false);
  // Load order: "sequential" loads tables one at a time in the order shown
  // in Mapping Summary (the original/default behavior); "parallel" loads
  // every table concurrently; "staged" groups tables by the per-table Load
  // Stage number (Mapping Summary) — stages run in order, tables sharing a
  // stage run concurrently.
  const [loadOrderMode, setLoadOrderMode] = useState<
    "sequential" | "parallel" | "staged"
  >("sequential");
  // Snapshot of the row limit actually sent with the most recently
  // submitted DB-to-DB load job — shown in Load Results so a change made
  // in Load Options is visibly confirmed to have taken effect on that run
  // (a table already smaller than the limit will show 0 "Rows Not Read"
  // regardless of the limit's value, which otherwise looks identical to
  // the limit silently not being applied).
  const [lastLoadRowLimitSummary, setLastLoadRowLimitSummary] = useState<
    string | null
  >(null);

  // ── SF Load step state ────────────────────────────────────────────────────
  // One tab per STTM-mapped source table → GovConnect object pair (from
  // `mappings`/`editedMappings`), each with its own independent field
  // mapping, load options, and in-flight/last-run job — switching tabs never
  // loses another tab's progress or results, and jobs keep running/polling
  // in the background regardless of which tab is currently active.
  interface SfLoadTabState {
    sourceSchema: string;
    // 2026-09-02, requested by user: explicit, user-confirmable override
    // for which table this tab actually reads from — auto-computed default
    // is the staged table name in "staged" mode (mapping.target_object +
    // suffix) or the real source table in "direct" mode, but when source
    // and staging happen to be the SAME physical connection/schema (a real
    // customer setup, not just a test convenience), silently trusting that
    // computed name isn't enough; the user needs to see and, if needed,
    // correct it — especially when entering this step standalone (resumed
    // session, never having freshly run "Load Data to Staging" in this
    // browser tab) where nothing else on screen states it. Empty string =
    // "use the computed default" (see computeSfLoadSourceTableDefault).
    sourceTable: string;
    objectMeta: SalesforceObjectMeta | null;
    fieldMappings: {
      source_column: string;
      sf_field: string;
      // dict: literal FROM=TO exact-match lookup (unchanged). string: a
      // Transform-engine expression or plain-English instruction (e.g.
      // "UPPER(FirstName)", "M as Male, F as Female") evaluated per row.
      value_map?: Record<string, string> | string;
    }[];
    rowLimit: "all" | "limited";
    rowCount: number;
    operation: "insert" | "upsert" | "update";
    upsertKey: string;
    result: {
      rows_read: number;
      rows_loaded: number;
      rows_failed: number;
      errors: string[];
    } | null;
    jobId: string | null;
    progress: DbToSfLoadStatusResponse | null;
    loading: boolean;
    error: string | null;
  }
  const DEFAULT_SF_LOAD_TAB: SfLoadTabState = {
    sourceSchema: "",
    sourceTable: "",
    objectMeta: null,
    fieldMappings: [],
    rowLimit: "all",
    rowCount: 100,
    operation: "insert",
    upsertKey: "",
    result: null,
    jobId: null,
    progress: null,
    loading: false,
    error: null,
  };
  const sfLoadTabKey = (m: { source_table: string; target_object: string }) =>
    `${m.source_table}::${m.target_object}`;

  const [sfLoadActiveTab, setSfLoadActiveTab] = useState(0);
  const [sfLoadTabState, setSfLoadTabState] = useState<
    Record<string, SfLoadTabState>
  >({});
  // Background poll intervals, one per tab key, so a job started in a tab
  // the user has since navigated away from keeps polling correctly.
  const sfLoadPollRefs = useRef<Record<string, ReturnType<typeof setInterval>>>(
    {},
  );

  const getSfLoadTab = useCallback(
    (key: string): SfLoadTabState => sfLoadTabState[key] ?? DEFAULT_SF_LOAD_TAB,
    [sfLoadTabState],
  );
  const updateSfLoadTab = useCallback(
    (key: string, patch: Partial<SfLoadTabState>) =>
      setSfLoadTabState((prev) => ({
        ...prev,
        [key]: { ...(prev[key] ?? DEFAULT_SF_LOAD_TAB), ...patch },
      })),
    [],
  );

  // Stop all in-flight polls on unmount so none keep firing after the view closes.
  useEffect(
    () => () => {
      Object.values(sfLoadPollRefs.current).forEach(clearInterval);
      sfLoadPollRefs.current = {};
      if (loadPollRef.current) clearInterval(loadPollRef.current);
      if (fileLoadPollRef.current) clearInterval(fileLoadPollRef.current);
      if (crmStreamPollRef.current) clearInterval(crmStreamPollRef.current);
      if (pdfParsePollRef.current) clearInterval(pdfParsePollRef.current);
    },
    [],
  );

  // Tabs come from the STTM's latest mappings — prefer the user's edited
  // version (STTM Review and Export) over the freshly-generated one. Tables
  // soft-removed via removedTableMappings must stay excluded here too, or a
  // "deleted" table reappears in the Load to GovConnect tab list.
  const sfLoadMappings = editedMappings.length
    ? editedMappings.filter((_, i) => !removedTableMappings.has(i))
    : mappings;
  const activeSfLoadMapping: TableObjectMapping | undefined =
    sfLoadMappings[sfLoadActiveTab];
  const activeSfLoadKey = activeSfLoadMapping
    ? sfLoadTabKey(activeSfLoadMapping)
    : null;
  const activeSfLoadTab = activeSfLoadKey
    ? getSfLoadTab(activeSfLoadKey)
    : DEFAULT_SF_LOAD_TAB;

  // ── Reconciliation step state ─────────────────────────────────────────────
  // One tab per mapped table, mirroring the Load to GovConnect tabs above.
  // Each tab independently tracks its own schema overrides and last-run
  // count/column reconciliation results.
  interface ReconcileTabState {
    sourceSchema: string;
    targetSchema: string;
    countResult: TableCountResult | null;
    countLoading: boolean;
    columnResult: ReconcileColumnResponse | null;
    columnLoading: boolean;
    // Row-by-row reconciliation: the key column(s) used to match a source
    // row to its target row (auto-populated from the scanned schema's
    // primary key when known — see the keyColumns derivation below — but
    // user-editable, since not every table's PK is recorded).
    keyColumns: string[];
    rowResult: ReconcileRowResponse | null;
    rowLoading: boolean;
    error: string | null;
    // Which result section to show — only the most recently run action's
    // results render, instead of stacking every result the user has ever
    // run for this table (matches the fix already applied to
    // StandaloneReconcilePanel's tabs).
    activeResultTab: "counts" | "columns" | "rows" | null;
  }
  const DEFAULT_RECONCILE_TAB: ReconcileTabState = {
    sourceSchema: "",
    targetSchema: "",
    countResult: null,
    countLoading: false,
    columnResult: null,
    columnLoading: false,
    keyColumns: [],
    rowResult: null,
    rowLoading: false,
    error: null,
    activeResultTab: null,
  };
  const reconcileTabKey = sfLoadTabKey;

  const [reconcileActiveTab, setReconcileActiveTab] = useState(0);
  const [reconcileTabState, setReconcileTabState] = useState<
    Record<string, ReconcileTabState>
  >({});

  // 2026-09-27, requested by user: entering Reconciliation directly (its
  // own top-level action, picked straight from the landing page) silently
  // auto-hydrated whichever mapping happened to already be persisted for
  // the project, with no way to pick a different one — defeating the
  // point of Reconciliation being usable as a standalone action. false
  // means "not yet confirmed this session"; the reconcile step then shows
  // a chooser (continue with the current mapping / pick from saved
  // mapping history / import an STTM file) before rendering the dashboard.
  // Not reset on Resume — a resumed reconciliation session already has an
  // explicit, previously-confirmed mapping behind its saved results.
  const [reconciliationMappingConfirmed, setReconciliationMappingConfirmed] =
    useState(false);

  // 2026-10-15, requested by user: a 3rd option on that same chooser —
  // skip mapping selection entirely and hand-define the source/target
  // tables and the key/compare fields, for projects with no STTM mapping
  // at all and no intent to create one.
  const [standaloneReconcileMode, setStandaloneReconcileMode] = useState(false);

  const getReconcileTab = useCallback(
    (key: string): ReconcileTabState =>
      reconcileTabState[key] ?? DEFAULT_RECONCILE_TAB,
    [reconcileTabState],
  );
  const updateReconcileTab = useCallback(
    (key: string, patch: Partial<ReconcileTabState>) =>
      setReconcileTabState((prev) => ({
        ...prev,
        [key]: { ...(prev[key] ?? DEFAULT_RECONCILE_TAB), ...patch },
      })),
    [],
  );

  // Tabs come from the same latest-STTM mappings as the Load to GovConnect
  // step, with the same soft-removed-table exclusion (see sfLoadMappings above).
  const reconcileMappings = editedMappings.length
    ? editedMappings.filter((_, i) => !removedTableMappings.has(i))
    : mappings;
  // Whether a given table's data came from an uploaded file rather than a
  // live source DB — deliberately independent of the Loader step's own
  // loadSourceMode toggle, which is plain React state the user only ever
  // sets by visiting that step's Database/Files switch in the CURRENT
  // session. Resuming straight into Reconciliation (a standalone action,
  // never passing through Loader first) left loadSourceMode stuck at its
  // "database" default, silently gating file-sourced projects behind
  // "Connect source and target databases first" and showing the DB-only
  // "Detailed Reconciliation + DQ" button. mappings[].source_file survives
  // any resume path (it's part of the persisted STTM mapping itself);
  // legacyFileRefs is a secondary signal for the rare case a table's own
  // source_file wasn't set but the project's legacy-parse snapshot was.
  //
  // PR #602 review finding: an earlier, project-wide version of this check
  // (`reconcileMappings.some(m => m.source_file) || legacyFileRefs is
  // non-empty`) misrouted a MIXED project (some file-sourced tables, some
  // live-DB-sourced ones with a blank source_file) by forcing EVERY table
  // into file mode whenever ANY legacyFileRefs existed, even for a table
  // that had a perfectly good sourceConnectionId. Trust each mapping's own
  // source_file first; only fall back to the project-wide legacy-snapshot
  // signal when NO mapping at all carries a source_file — the one case
  // that's still a genuinely all-files project (generated before
  // source_file existed on TableObjectMapping).
  const projectPredatesSourceFileTagging =
    reconcileMappings.length > 0 &&
    reconcileMappings.every((m) => !m.source_file) &&
    Object.keys(legacyFileRefs).length > 0;
  const isMappingFileSourced = (mapping: TableObjectMapping): boolean =>
    Boolean(mapping.source_file) || projectPredatesSourceFileTagging;
  // Row-by-row reconciliation's key-column auto-detect: mapping's source
  // table's primary key column(s), read from the earlier schema scan —
  // returned as target_field (not source_column) names, since key columns
  // are matched against reconcile_rows'/reconcile_file_rows' target-field-
  // keyed field_mappings. Empty when no PK was recorded for this table
  // (legacy/file-crawled schema, or a genuinely key-less table) — the UI
  // falls back to a manual picker in that case.
  const getSuggestedReconcileKeyColumns = (
    mapping: TableObjectMapping,
  ): string[] => {
    const table = schemaResult?.tables.find(
      (t) => t.table_name === mapping.source_table,
    );
    if (!table) return [];
    const pkSourceCols = new Set(
      table.columns.filter((c) => c.is_primary_key).map((c) => c.name),
    );
    return mapping.field_mappings
      .filter((fm) => pkSourceCols.has(fm.source_column))
      .map((fm) => fm.target_field)
      .filter(Boolean);
  };
  const activeReconcileMapping: TableObjectMapping | undefined =
    reconcileMappings[reconcileActiveTab];
  const activeReconcileKey = activeReconcileMapping
    ? reconcileTabKey(activeReconcileMapping)
    : null;
  const activeReconcileTab = (() => {
    const tab = activeReconcileKey
      ? getReconcileTab(activeReconcileKey)
      : DEFAULT_RECONCILE_TAB;
    // Derive the effective tab at render instead of relying on every write
    // path (handlers, resume-from-persisted-state, bulk "Run all") to set
    // activeResultTab explicitly — a resumed session (or a future write
    // path that forgets to) otherwise has a populated countResult/
    // columnResult/rowResult but activeResultTab stuck at its null
    // default, so the result section renders nothing at all until the
    // user re-triggers a run. Falls back to whichever result exists,
    // preferring counts > columns > rows (the order they're normally run in).
    if (tab.activeResultTab) return tab;
    const fallback = tab.countResult
      ? "counts"
      : tab.columnResult
        ? "columns"
        : tab.rowResult
          ? "rows"
          : null;
    return fallback ? { ...tab, activeResultTab: fallback } : tab;
  })();
  const suggestedReconcileKeyColumns: string[] = activeReconcileMapping
    ? getSuggestedReconcileKeyColumns(activeReconcileMapping)
    : [];

  // ── Detailed reconciliation + final-table Data Quality report state ─────
  const [detailedReconcileResult, setDetailedReconcileResult] =
    useState<ConverterReconcileDetailedOut | null>(null);
  const [detailedReconcileLoading, setDetailedReconcileLoading] =
    useState(false);
  const [finalTableDqReport, setFinalTableDqReport] = useState<
    ConverterFinalTableDqOut["report"] | null
  >(null);
  const [finalTableDqLoading, setFinalTableDqLoading] = useState(false);
  // Bulk "Run Column Reconciliation — All Tables" — the Files-mode
  // dashboard's replacement for "Detailed Reconciliation + DQ" (DB-only;
  // never runs against a file source), which only ever offered a per-table
  // "Run Column Reconciliation" button.
  const [reconcileAllColumnsLoading, setReconcileAllColumnsLoading] =
    useState(false);
  // Bulk "Run Row-by-Row Reconciliation — All Tables", same shape as
  // reconcileAllColumnsLoading above.
  const [reconcileAllRowsLoading, setReconcileAllRowsLoading] = useState(false);

  // ── Resume-picker side panels: Data Loader / Reconciliation / Load to
  // Target — each mirrors the mapping resume picker's "last 10 saves" list,
  // but as its own dedicated side pane rather than the initial-entry modal.
  const [loadHistoryPanelOpen, setLoadHistoryPanelOpen] = useState(false);
  const [loadHistoryEntries, setLoadHistoryEntries] = useState<
    ConverterLoadHistoryEntryOut[]
  >([]);
  const [loadHistoryLoading, setLoadHistoryLoading] = useState(false);

  const [reconciliationHistoryPanelOpen, setReconciliationHistoryPanelOpen] =
    useState(false);
  const [reconciliationHistoryEntries, setReconciliationHistoryEntries] =
    useState<ConverterReconciliationHistoryEntryOut[]>([]);
  const [reconciliationHistoryLoading, setReconciliationHistoryLoading] =
    useState(false);

  const [finalLoadHistoryPanelOpen, setFinalLoadHistoryPanelOpen] =
    useState(false);
  const [finalLoadHistoryEntries, setFinalLoadHistoryEntries] = useState<
    ConverterFinalLoadHistoryEntryOut[]
  >([]);
  const [finalLoadHistoryLoading, setFinalLoadHistoryLoading] = useState(false);

  const openLoadHistoryPanel = useCallback(async () => {
    if (!projectId) return;
    setLoadHistoryPanelOpen(true);
    setLoadHistoryLoading(true);
    try {
      setLoadHistoryEntries(await apiv2.converterListLoadHistory(projectId));
    } catch {
      setLoadHistoryEntries([]);
    } finally {
      setLoadHistoryLoading(false);
    }
  }, [projectId]);

  const openReconciliationHistoryPanel = useCallback(async () => {
    if (!projectId) return;
    setReconciliationHistoryPanelOpen(true);
    setReconciliationHistoryLoading(true);
    try {
      setReconciliationHistoryEntries(
        await apiv2.converterListReconciliationHistory(projectId),
      );
    } catch {
      setReconciliationHistoryEntries([]);
    } finally {
      setReconciliationHistoryLoading(false);
    }
  }, [projectId]);

  const openFinalLoadHistoryPanel = useCallback(async () => {
    if (!projectId) return;
    setFinalLoadHistoryPanelOpen(true);
    setFinalLoadHistoryLoading(true);
    try {
      setFinalLoadHistoryEntries(
        await apiv2.converterListFinalLoadHistory(projectId),
      );
    } catch {
      setFinalLoadHistoryEntries([]);
    } finally {
      setFinalLoadHistoryLoading(false);
    }
  }, [projectId]);

  const handleSelectReconciliationHistoryEntry = useCallback(
    async (entry: ConverterReconciliationHistoryEntryOut) => {
      if (!projectId) return;
      setReconciliationHistoryPanelOpen(false);
      try {
        const detail = await apiv2.converterGetReconciliationHistoryEntry(
          projectId,
          entry.id,
        );
        if (detail.quality_result) {
          const q =
            detail.quality_result as unknown as ConverterReconcileDetailedOut;
          setDetailedReconcileResult(q);
        }
      } catch (e) {
        setErr(
          e instanceof Error
            ? e.message
            : "Failed to load reconciliation history entry",
        );
      }
    },
    [projectId],
  );

  const handleReconcileDetailed = async () => {
    if (reconcileMappings.length === 0) return;
    if (!sourceConnectionId || !targetConnectionId) {
      setErr("Connect source and target databases first");
      return;
    }
    setDetailedReconcileLoading(true);
    try {
      const pairs = reconcileMappings.map((m) =>
        buildReconcilePair(m, getReconcileTab(reconcileTabKey(m))),
      );
      const res = await apiv2.converterReconcileDetailed({
        sourceConnectionRef: { connectionId: sourceConnectionId, projectId },
        targetConnectionRef: { connectionId: targetConnectionId, projectId },
        mappings: reconcileMappings,
        targetSchema: ddlTargetSchema || targetSchemaName || "",
        pairs,
        projectId,
        targetPlatform: activeTargetPlatform,
      });
      setDetailedReconcileResult(res);
      if (projectId) {
        apiv2
          .converterSetReconciliation(projectId, {
            counts_result: res.counts_result,
            columns_result: res.columns_result,
            quality_result: res as unknown as Record<string, unknown>,
          })
          .catch(() => {});
        apiv2
          .converterSaveReconciliationHistory(projectId, {
            counts_result: res.counts_result,
            columns_result: res.columns_result,
            quality_result: res as unknown as Record<string, unknown>,
            overall_score: res.overall_score,
          })
          .catch(() => {});
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Detailed reconciliation failed");
    } finally {
      setDetailedReconcileLoading(false);
    }
  };

  const handleGenerateFinalTableDq = async () => {
    if (!projectId || !detailedReconcileResult) return;
    setFinalTableDqLoading(true);
    try {
      const res = await apiv2.converterGenerateFinalTableDq(projectId, {
        tables: detailedReconcileResult.tables,
        overall_score: detailedReconcileResult.overall_score,
      });
      setFinalTableDqReport(res.report);
    } catch (e) {
      setErr(
        e instanceof Error
          ? e.message
          : "Failed to generate final-table DQ report",
      );
    } finally {
      setFinalTableDqLoading(false);
    }
  };

  const setErr = useCallback((msg: string) => setError(msg), []);

  // Shared by the connect handlers (which already know the resolved
  // connection + schema and must scan with those exact values, not
  // whatever fileTargetSchema happened to hold before this connection was
  // made) and handleScanFileTargetTables (manual "Refresh table list" +
  // the auto-scan effect below, which only have component state to go on).
  const scanFileTargetTablesFor = async (
    connRef: ConverterConnectionRef,
    schema: string,
  ) => {
    setFileTargetTablesScanning(true);
    setFileTargetTablesError(null);
    try {
      const res = await apiv2.converterTargetScan(
        connRef,
        schema.trim() || undefined,
      );
      setFileTargetTables(res.tables);
    } catch (e) {
      setFileTargetTablesError(
        e instanceof Error ? e.message : "Could not list target tables",
      );
    } finally {
      setFileTargetTablesScanning(false);
    }
  };

  const handleFileTargetConnectNew = useCallback(
    async (values: DbConnectFormValues): Promise<ConnectResult> => {
      const connection = buildConverterConnection(values);
      try {
        const testResult = await apiv2.converterTestFileConnection(connection);
        setFileTargetConn(connection);
        setFileTargetConnLabel(
          values.connection_name ||
            `${connection.db_type} · ${connection.host}`,
        );
        setFileTargetDbType(testResult.dialect || connection.db_type);
        setFileConnTestResult(testResult);
        // Take the schema straight from the connection just entered,
        // instead of leaving whatever was previously typed/picked (e.g.
        // from a prior connection's table selection) in the field.
        const schema = connection.schema_name?.trim() || "public";
        setFileTargetSchema(schema);
        setFileTargetTables(null);
        setFileTargetTablesError(null);
        await scanFileTargetTablesFor(connection, schema);
        return { ok: true };
      } catch (err: unknown) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : "Connection failed",
        };
      }
    },
    [],
  );

  const handleFileTargetConnectExisting = useCallback(
    async (connectionId: string, label?: string): Promise<ConnectResult> => {
      const ref = { connectionId, projectId };
      try {
        const testResult = await apiv2.converterTestFileConnection(ref);
        setFileTargetConn(ref);
        setFileTargetConnLabel(label ?? "saved connection");
        setFileTargetDbType(testResult.dialect || "");
        setFileConnTestResult(testResult);
        setFileTargetTables(null);
        setFileTargetTablesError(null);
        // A saved connection's schema isn't in the test-connection
        // response (only dialect is) -- look it up from the project's
        // saved target connections instead of leaving a stale/default
        // schema in the field.
        let schema = "public";
        if (projectId) {
          try {
            const saved = await apiv2.listTargetConnections(projectId);
            const match = saved.find((c) => c.id === connectionId);
            if (match?.schema_name) schema = match.schema_name;
          } catch {
            // Non-fatal — falls back to "public" below, same as before
            // this lookup existed.
          }
        }
        setFileTargetSchema(schema);
        await scanFileTargetTablesFor(ref, schema);
        return { ok: true };
      } catch (err: unknown) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : "Connection failed",
        };
      }
    },
    [projectId],
  );

  // ── Step handlers ─────────────────────────────────────────────────────────

  const buildConverterConnection = (
    values: DbConnectFormValues,
  ): DbConnectionConfig => ({
    db_type: values.db_type as DbConnectionConfig["db_type"],
    host: values.host,
    port: parseInt(values.port, 10) || 0,
    database: values.database,
    username: values.username,
    password: values.password,
    schema_name: values.schema_name,
    service_name: values.service_name || null,
    http_path: values.http_path || null,
    client_id: values.client_id || null,
    client_secret: values.client_secret || null,
    // Snowflake — previously dropped here despite ConnectDatabaseDialog's
    // own form collecting them, silently breaking a fresh Snowflake
    // "New Connection" (only a saved connection_id ever worked, since that
    // path never runs through this mapping).
    account: values.account || null,
    warehouse: values.warehouse || null,
    role: values.role || null,
    // jdbc_bridge (mainframe DB2 / ADABAS SQL Gateway / any JDBC source)
    jdbc_driver_class: values.jdbc_driver_class || null,
    jdbc_url: values.jdbc_url || null,
    jdbc_driver_jar_path: values.jdbc_driver_jar_path || null,
    bridge_label: values.bridge_label || null,
  });

  const handleSourceConnectNew = useCallback(
    async (values: DbConnectFormValues): Promise<ConnectResult> => {
      try {
        const connection = buildConverterConnection(values);
        const res = await apiv2.converterSourceConnect({
          connection,
          projectId,
          connectionName: values.connection_name || undefined,
        });
        setConnStatus(res);
        setSourceConnectionId(res.connection_id ?? null);
        setSourceConnLabel(
          values.connection_name ||
            `${connection.db_type} · ${connection.host}`,
        );
        setSourceSchemaName(connection.schema_name);
        refreshLoadSourceSchema(connection.schema_name);
        return { ok: true };
      } catch (err: unknown) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : "Connection failed",
        };
      }
    },
    [projectId],
  );

  // Scans every extraSourceConnections entry too, tagging each of ITS
  // tables with source_connection_id so mapping generation can carry that
  // onto TableObjectMapping.source_connection_id (see buildIdentityMapping
  // / withSourceConnectionIds below) — this is what "Add Another Source
  // Connection" is for. Returns `primary` completely unchanged (same
  // object) when there are no extra connections, so a plain single-
  // connection scan is byte-for-byte identical to before this existed.
  //
  // A table name collision across connections does NOT mean a duplicate —
  // two different schemas/databases can genuinely each have their own
  // "CUSTOMER" table. But every mapping-generation/STTM/Load/reconciliation
  // code path downstream keys a mapping row by the bare table_name alone,
  // so at most one physical table per name can flow into schemaResult.tables
  // at once. Rather than silently dropping the loser, we keep the primary's
  // version by default and record the collision in tableNameConflicts (both
  // full TableMeta objects, not just a message) so the Schema DDL step can
  // show the user both sides and let them swap in the other connection's
  // version instead — nothing is ever lost without the user seeing it.
  // Pure/synchronous — never calls the scan API itself. Rebuilds the merged
  // schemaResult from whatever's already in `cache` (each connection's last
  // scan response, keyed "__primary__" for the primary and by `id` for each
  // extraSourceConnections entry). This is what lets re-scanning or
  // switching ONE connection's schema stay a single-connection action: the
  // caller scans only the connection that actually changed, updates just
  // that one cache entry, and calls this to recompute the merged view —
  // every other connection's tables are reused exactly as last scanned,
  // never re-fetched. A connection missing from `cache` (not scanned yet)
  // contributes no tables and is simply skipped.
  const buildMergedSchemaResult = useCallback(
    (
      primary: SchemaExtractResult,
      connections: typeof extraSourceConnections,
      cache: Record<string, SchemaExtractResult>,
    ): SchemaExtractResult => {
      if (connections.length === 0) {
        setTableNameConflicts([]);
        setExtraSchemaDbType({});
        setExtraSchemaRawTableCount({});
        return primary;
      }
      const primaryByName = new Map(
        primary.tables.map((t) => [t.table_name, t]),
      );
      const seenNames = new Set(primaryByName.keys());
      const extraTables: TableMeta[] = [];
      const extraWarnings: string[] = [];
      const conflicts: {
        tableName: string;
        primaryTable: TableMeta;
        primaryLabel: string;
        primarySchemaName: string;
        extraTable: TableMeta;
        extraLabel: string;
        extraSchemaName: string;
      }[] = [];
      const dbTypeByConnection: Record<string, string> = {};
      const rawTableCountByConnection: Record<string, number> = {};
      for (const c of connections) {
        const res = cache[c.id];
        if (!res) continue; // never scanned yet
        dbTypeByConnection[c.connectionId] = res.db_type;
        rawTableCountByConnection[c.connectionId] = res.tables.length;
        for (const t of res.tables) {
          const taggedTable: TableMeta = {
            ...t,
            source_connection_id: c.connectionId,
          };
          const existingPrimary = primaryByName.get(t.table_name);
          if (seenNames.has(t.table_name)) {
            if (existingPrimary) {
              conflicts.push({
                tableName: t.table_name,
                primaryTable: existingPrimary,
                primaryLabel: sourceConnLabel || "primary connection",
                primarySchemaName: primary.schema_name,
                extraTable: taggedTable,
                extraLabel: c.label,
                extraSchemaName: c.schemaName || res.schema_name,
              });
            }
            extraWarnings.push(
              `"${t.table_name}" also exists in ${c.label} (${
                c.schemaName || res.schema_name
              }) — keeping the version from ${
                existingPrimary
                  ? sourceConnLabel || "the primary connection"
                  : "the first connection that scanned it"
              } by default; use the conflict picker below to swap.`,
            );
            continue;
          }
          seenNames.add(t.table_name);
          extraTables.push(taggedTable);
        }
      }
      setTableNameConflicts(conflicts);
      setExtraSchemaDbType(dbTypeByConnection);
      setExtraSchemaRawTableCount(rawTableCountByConnection);
      if (extraTables.length === 0 && extraWarnings.length === 0) {
        return primary;
      }
      return {
        ...primary,
        tables: [...primary.tables, ...extraTables],
        warnings: [...(primary.warnings ?? []), ...extraWarnings],
      };
    },
    [sourceConnLabel],
  );

  // Swaps which connection's version of a name-conflicted table is active
  // in schemaResult.tables — the only place a conflict's resolution lives,
  // so selection/expansion state (keyed by table_name, unaffected by which
  // version is active) and mapping generation (reads schemaResult.tables at
  // generation time) automatically pick up the swap with no further wiring.
  // ALSO re-tags any mapping already generated against this table name — a
  // swap made AFTER mappings were generated used to leave the existing
  // mapping's source_connection_id pointing at whichever connection was
  // active at generation time, so Load would keep reading from the wrong
  // connection (right srcSchema override, wrong physical database) even
  // though the Schema DDL step now shows the swapped-in version as active.
  const swapConflictTable = useCallback(
    (tableName: string, useExtra: boolean) => {
      const conflict = tableNameConflicts.find(
        (c) => c.tableName === tableName,
      );
      if (!conflict) return;
      const replacement = useExtra
        ? conflict.extraTable
        : conflict.primaryTable;
      setSchemaResult((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          tables: prev.tables.map((t) =>
            t.table_name === tableName ? replacement : t,
          ),
        };
      });
      setMappings((prev) =>
        prev.map((m) =>
          (m.source_tables?.[0] ?? m.source_table) === tableName
            ? { ...m, source_connection_id: replacement.source_connection_id }
            : m,
        ),
      );
    },
    [tableNameConflicts],
  );

  // Backfills TableObjectMapping.source_connection_id from schemaResult's
  // per-table tag for a freshly-generated batch of mappings — the backend's
  // /mapping/generate endpoint matches source tables to target objects, it
  // has no notion of which connection a table came from. A no-op (every
  // table's tag is undefined) unless buildMergedSchemaResult actually
  // merged in extra-connection tables, so this never changes behavior for
  // the common single-connection case. Only fills a mapping's tag if unset
  // (??) — never overrides one already carried through (e.g. from
  // buildIdentityMapping).
  const withSourceConnectionIds = useCallback(
    (ms: TableObjectMapping[]): TableObjectMapping[] => {
      if (!schemaResult) return ms;
      const byTable = new Map(
        schemaResult.tables.map((t) => [t.table_name, t.source_connection_id]),
      );
      return ms.map((m) => ({
        ...m,
        source_connection_id:
          m.source_connection_id ??
          byTable.get(m.source_tables?.[0] ?? m.source_table),
      }));
    },
    [schemaResult],
  );

  // Re-scans (or does the initial scan for) ONLY the primary connection —
  // every extraSourceConnections entry that already has a cached scan is
  // reused as-is (never re-fetched); only a brand-new extra that's never
  // been scanned before gets an initial scan here, since there's no cache
  // to reuse yet for it.
  const runSourceScan = useCallback(
    async (connectionId: string, schemaName: string) => {
      setScanningSchema(true);
      setError(null);
      try {
        const res = await apiv2.converterSourceScan(
          { connectionId, projectId },
          schemaName || undefined,
        );
        let cache: Record<string, SchemaExtractResult> = {
          ...rawScanCache,
          __primary__: res,
        };
        for (const c of extraSourceConnections) {
          if (cache[c.id]) continue;
          try {
            const extraRes = await apiv2.converterSourceScan(
              { connectionId: c.connectionId, projectId },
              c.schemaName || undefined,
            );
            cache = { ...cache, [c.id]: extraRes };
          } catch (e) {
            setErr(
              `Failed to scan ${c.label}: ${
                e instanceof Error ? e.message : "scan failed"
              }`,
            );
          }
        }
        setRawScanCache(cache);
        const merged = buildMergedSchemaResult(
          res,
          extraSourceConnections,
          cache,
        );
        setSchemaResult(merged);
        setSelectedSourceTablesForMapping(
          new Set(merged.tables.map((t) => t.table_name)),
        );
        setStep("schema");
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Schema scan failed");
      } finally {
        setScanningSchema(false);
      }
    },
    [projectId, extraSourceConnections, rawScanCache, buildMergedSchemaResult],
  );

  const handleSourceConnectExisting = useCallback(
    async (connectionId: string, label?: string): Promise<ConnectResult> => {
      try {
        const res = await apiv2.converterSourceConnect({
          connectionId,
          projectId,
        });
        setConnStatus(res);
        const resolvedId = res.connection_id ?? connectionId;
        setSourceConnectionId(resolvedId);
        setSourceConnLabel(label ?? "saved connection");
        setSourceSchemaName(res.schema_name ?? "");
        // Connect only — closes the dialog immediately instead of blocking
        // it on a full schema scan. The user triggers "Scan Schema DDL"
        // themselves once back on the Source DB step, so it's visible as
        // its own in-progress action rather than hidden behind the dialog.
        return { ok: true };
      } catch (err: unknown) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : "Connection failed",
        };
      }
    },
    [projectId],
  );

  // Adds a connection to extraSourceConnections instead of replacing the
  // primary sourceConnectionId — lets STTM-Mapping rows pull source tables
  // from more than one saved connection in the same session (see
  // TableObjectMapping.source_connection_id). Never blocked on connectionId
  // already being in use (as the primary or an existing extra entry) —
  // the whole point is to let the SAME saved connection be added again
  // pointed at a different schema, since one DB connection commonly
  // exposes many schemas.
  const handleAddSourceConnectionExisting = useCallback(
    async (connectionId: string, label?: string): Promise<ConnectResult> => {
      try {
        const res = await apiv2.converterSourceConnect({
          connectionId,
          projectId,
        });
        const resolvedId = res.connection_id ?? connectionId;
        setExtraSourceConnections((prev) => [
          ...prev,
          {
            id: `${resolvedId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            connectionId: resolvedId,
            label: label ?? "saved connection",
            schemaName: res.schema_name ?? "",
            schemas: res.schemas ?? [],
          },
        ]);
        return { ok: true };
      } catch (err: unknown) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : "Connection failed",
        };
      }
    },
    [projectId],
  );

  const handleAddSourceConnectionNew = useCallback(
    async (values: DbConnectFormValues): Promise<ConnectResult> => {
      try {
        const connection = buildConverterConnection(values);
        const res = await apiv2.converterSourceConnect({
          connection,
          projectId,
          connectionName: values.connection_name || undefined,
        });
        const resolvedId = res.connection_id;
        if (resolvedId) {
          setExtraSourceConnections((prev) => [
            ...prev,
            {
              id: `${resolvedId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
              connectionId: resolvedId,
              label:
                values.connection_name ||
                `${connection.db_type} · ${connection.host}`,
              schemaName: res.schema_name ?? connection.schema_name,
              schemas: res.schemas ?? [],
            },
          ]);
        }
        return { ok: true };
      } catch (err: unknown) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : "Connection failed",
        };
      }
    },
    [projectId],
  );

  const handleRemoveExtraSourceConnection = useCallback((id: string) => {
    setExtraSourceConnections((prev) => prev.filter((c) => c.id !== id));
    setExtraSchemaPickerId((prev) => (prev === id ? null : prev));
  }, []);

  const handleChangeExtraSourceSchema = useCallback(
    (id: string, schemaName: string) => {
      setExtraSourceConnections((prev) =>
        prev.map((c) => (c.id === id ? { ...c, schemaName } : c)),
      );
      setExtraSchemaPickerId(null);
    },
    [],
  );

  // Switches an extra connection's schema AND immediately re-scans, for the
  // live dropdown in the Schema DDL step's per-connection section (mirrors
  // the primary's schema-switch <select>). Scans ONLY this one connection —
  // the primary and every other extra are reused from rawScanCache exactly
  // as last scanned — and tracks its own `scanningExtraId` rather than the
  // shared `scanningSchema` flag, so switching/re-scanning one connection's
  // schema never shows any other connection's card as busy.
  const rescanExtraConnectionSchema = useCallback(
    async (id: string, connectionId: string, newSchemaName: string) => {
      const primaryEntry = rawScanCache.__primary__;
      if (!primaryEntry) return;
      const overriddenExtras = extraSourceConnections.map((c) =>
        c.id === id ? { ...c, schemaName: newSchemaName } : c,
      );
      setExtraSourceConnections(overriddenExtras);
      setScanningExtraId(id);
      setError(null);
      try {
        const res = await apiv2.converterSourceScan(
          { connectionId, projectId },
          newSchemaName || undefined,
        );
        const cache: Record<string, SchemaExtractResult> = {
          ...rawScanCache,
          [id]: res,
        };
        setRawScanCache(cache);
        const merged = buildMergedSchemaResult(
          primaryEntry,
          overriddenExtras,
          cache,
        );
        setSchemaResult(merged);
        setSelectedSourceTablesForMapping(
          new Set(merged.tables.map((t) => t.table_name)),
        );
      } catch (e) {
        setErr(e instanceof Error ? e.message : "Schema scan failed");
      } finally {
        setScanningExtraId(null);
      }
    },
    [projectId, extraSourceConnections, rawScanCache, buildMergedSchemaResult],
  );

  // Same as handleSourceConnectExisting, minus the schema scan + step jump —
  // used from the Load Data step (the "loader" action's ACTION_STEPS has no
  // "schema" step, e.g. when the mapping came from an imported STTM/mapping
  // document and "source" was never visited), where we only need the
  // connection_id wired up, not a schema scan.
  const handleSourceConnectExistingForLoad = useCallback(
    async (connectionId: string, label?: string): Promise<ConnectResult> => {
      try {
        const res = await apiv2.converterSourceConnect({
          connectionId,
          projectId,
        });
        setConnStatus(res);
        setSourceConnectionId(res.connection_id ?? connectionId);
        setSourceConnLabel(label ?? "saved connection");
        setSourceSchemaName(res.schema_name ?? "");
        refreshLoadSourceSchema(res.schema_name ?? "");
        return { ok: true };
      } catch (err: unknown) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : "Connection failed",
        };
      }
    },
    [projectId],
  );

  // Re-test the currently-connected source (activating a saved connection
  // only checks it's a real, resolvable connection_id — it never actually
  // opens a connection, so "Connected" can be stale/wrong e.g. a host only
  // reachable from a different network). Re-runs the same connect call
  // against the existing sourceConnectionId and surfaces a real error if it
  // still can't reach the database.
  const handleRefreshSourceConnection = async () => {
    if (!sourceConnectionId) return;
    setRefreshingSourceConnection(true);
    setError(null);
    try {
      const res = await apiv2.converterSourceConnect({
        connectionId: sourceConnectionId,
        projectId,
      });
      setConnStatus(res);
    } catch (e) {
      setConnStatus(null);
      setErr(
        e instanceof Error ? e.message : "Could not connect to the database",
      );
    } finally {
      setRefreshingSourceConnection(false);
    }
  };

  // Manual connect for the mainframe/JDBC-bridge/copybook kinds
  // ConnectDatabaseDialog doesn't cover — see mainframeConn's declaration.
  const handleMainframeSourceConnect = async () => {
    setConnectingMainframe(true);
    setError(null);
    try {
      const res = await apiv2.converterSourceConnect({
        connection: mainframeConn,
        projectId,
      });
      setConnStatus(res);
      setSourceConnectionId(res.connection_id ?? null);
      setSourceConnLabel(mainframeConn.bridge_label || mainframeConn.db_type);
      setSourceSchemaName(mainframeConn.schema_name);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Connection failed");
    } finally {
      setConnectingMainframe(false);
    }
  };

  // ── File Source handlers ──────────────────────────────────────────────

  // Extension -> (category, format_type), mirroring legacy_schema_parser.py's
  // own detection. Only a genuine starting guess — always user-overridable
  // via the File Format selector, since extension alone can be wrong (e.g.
  // a delimited extract saved with a plain ".txt" extension).
  const _detectFileSourceCategory = (
    filename: string,
  ): {
    category: "fixed_width" | "copybook" | "delimited" | "structured";
    formatType: ConverterSourceFormat["format_type"];
  } => {
    const lower = filename.toLowerCase();
    if (lower.endsWith(".csv"))
      return { category: "delimited", formatType: "csv" };
    if (lower.endsWith(".tsv"))
      return { category: "delimited", formatType: "tsv" };
    if (lower.endsWith(".jsonl"))
      return { category: "structured", formatType: "jsonl" };
    if (lower.endsWith(".json"))
      return { category: "structured", formatType: "json" };
    if (lower.endsWith(".parquet"))
      return { category: "structured", formatType: "parquet" };
    if (
      lower.endsWith(".cpy") ||
      lower.endsWith(".cbl") ||
      lower.endsWith(".cob")
    ) {
      return { category: "copybook", formatType: "fixed_width" };
    }
    return { category: "fixed_width", formatType: "fixed_width" };
  };

  // Detects columns straight from an already-uploaded delimited/structured
  // file (see converterInspectFile) instead of asking the user to define a
  // start/length layout that doesn't apply to a self-describing format.
  const _runColumnInspection = async (
    filePath: string,
    formatType: Exclude<ConverterSourceFormat["format_type"], "fixed_width">,
    hasHeader: boolean,
  ) => {
    setDetectingColumns(true);
    try {
      const inspected = await apiv2.converterInspectFile({
        file_path: filePath,
        format_type: formatType,
        encoding: fileEncoding,
        has_header: hasHeader,
      });
      setLayoutFields(inspected.fields);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Could not detect columns");
    } finally {
      setDetectingColumns(false);
    }
  };

  const handleFileSourceUpload = async (file: File) => {
    setFileBusy(true);
    setFileUploadError(null);
    setLayoutFields([]);
    setFileBuiltTask(null);
    setFilePreviewResult(null);
    setFileExcludeBadRecords(false);
    setFileLoadResult(null);
    setFileBatchLoadResults(null);
    try {
      const res = await apiv2.converterUploadFile(file, projectId);
      await _afterFileSourceUploaded(res, file.name);
    } catch (e) {
      setFileUploadError(e instanceof Error ? e.message : "File upload failed");
    } finally {
      setFileBusy(false);
    }
  };

  const _computeDefaultTargetTableName = (filename: string) =>
    filename.replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9_]+/g, "_");

  // Shared by handleFileSourceUpload (local "Choose File(s)") and
  // handleSftpImportFile (remote "Import from SFTP") -- both end up with the
  // exact same {file_id, file_path, filename, size_bytes} shape, so
  // everything downstream (target-table default, category/format detection,
  // column auto-inspection) runs identically regardless of where the file
  // came from.
  const _afterFileSourceUploaded = async (
    res: ConverterFileUploadResponse,
    filename: string,
  ) => {
    setFileUpload(res);
    if (!fileTargetTable.trim()) {
      setFileTargetTable(_computeDefaultTargetTableName(filename));
    }
    // res.filename (NOT the passed `filename`, the originally-selected
    // name) -- an Excel workbook is converted to CSV server-side (see
    // DataConverterService.upload_source_file), so res.filename is
    // "....csv" even when the user picked a ".xlsx" file. Detecting off
    // the stale original name would misclassify it as "fixed_width".
    const { category, formatType } = _detectFileSourceCategory(res.filename);
    setFileCategory(category);
    setFileFormatType(formatType);
    setLayoutFormat(category === "copybook" ? "cobol" : "simple");
    if (category === "delimited") {
      const delimFormat = formatType as "csv" | "tsv";
      setDelimitedSubFormat(delimFormat);
      setFileHasHeader(true);
      await _runColumnInspection(res.file_path, delimFormat, true);
    } else if (category === "structured") {
      const structFormat = formatType as "json" | "jsonl" | "parquet";
      setStructuredSubFormat(structFormat);
      await _runColumnInspection(res.file_path, structFormat, false);
    }
  };

  // Snapshot of every piece of state the single-file wizard (steps 2-5)
  // reads or writes. "Multiple Files (Different Formats)" mode gives each
  // selected file its own independent pass through that same wizard by
  // saving the shared state above into one of these per file, and
  // restoring it back whenever that file's tab becomes active again --
  // this reuses all of steps 2-5's existing JSX/handlers completely
  // unmodified instead of duplicating that ~500-line wizard once per tab.
  // Transient in-flight flags (fileBusy, detectingColumns, fileConnTesting,
  // fileTargetTablesScanning) are deliberately excluded -- they're reset on
  // restore, not preserved, since a tab can never be "mid-request" while
  // it's in the background.
  type FileWizardSnapshot = {
    fileUpload: typeof fileUpload;
    fileUploadError: typeof fileUploadError;
    layoutFormat: typeof layoutFormat;
    layoutText: typeof layoutText;
    layoutFields: typeof layoutFields;
    layoutError: typeof layoutError;
    fileCategory: typeof fileCategory;
    delimitedSubFormat: typeof delimitedSubFormat;
    structuredSubFormat: typeof structuredSubFormat;
    fileFormatType: typeof fileFormatType;
    fileHasHeader: typeof fileHasHeader;
    fileEncoding: typeof fileEncoding;
    fileTargetTable: typeof fileTargetTable;
    fileTargetSchema: typeof fileTargetSchema;
    fileIfExists: typeof fileIfExists;
    fileWizardStep: typeof fileWizardStep;
    fileTargetConn: typeof fileTargetConn;
    fileTargetConnLabel: typeof fileTargetConnLabel;
    fileTargetDbType: typeof fileTargetDbType;
    fileConnTestResult: typeof fileConnTestResult;
    fileTargetTables: typeof fileTargetTables;
    fileTargetTablesError: typeof fileTargetTablesError;
    fileBuiltTask: typeof fileBuiltTask;
    fileBuildError: typeof fileBuildError;
    filePreviewResult: typeof filePreviewResult;
    fileExcludeBadRecords: typeof fileExcludeBadRecords;
    fileLoadResult: typeof fileLoadResult;
    fileLoadError: typeof fileLoadError;
    fileBulkLoad: typeof fileBulkLoad;
    fileBlobAccountName: typeof fileBlobAccountName;
    fileBlobContainer: typeof fileBlobContainer;
    fileBlobAuthMode: typeof fileBlobAuthMode;
    fileBlobCredential: typeof fileBlobCredential;
  };

  const captureFileWizardSnapshot = (): FileWizardSnapshot => ({
    fileUpload,
    fileUploadError,
    layoutFormat,
    layoutText,
    layoutFields,
    layoutError,
    fileCategory,
    delimitedSubFormat,
    structuredSubFormat,
    fileFormatType,
    fileHasHeader,
    fileEncoding,
    fileTargetTable,
    fileTargetSchema,
    fileIfExists,
    fileWizardStep,
    fileTargetConn,
    fileTargetConnLabel,
    fileTargetDbType,
    fileConnTestResult,
    fileTargetTables,
    fileTargetTablesError,
    fileBuiltTask,
    fileBuildError,
    filePreviewResult,
    fileExcludeBadRecords,
    fileLoadResult,
    fileLoadError,
    fileBulkLoad,
    fileBlobAccountName,
    fileBlobContainer,
    fileBlobAuthMode,
    fileBlobCredential,
  });

  const applyFileWizardSnapshot = (snap: FileWizardSnapshot) => {
    setFileUpload(snap.fileUpload);
    setFileUploadError(snap.fileUploadError);
    setLayoutFormat(snap.layoutFormat);
    setLayoutText(snap.layoutText);
    setLayoutFields(snap.layoutFields);
    setLayoutError(snap.layoutError);
    setFileCategory(snap.fileCategory);
    setDelimitedSubFormat(snap.delimitedSubFormat);
    setStructuredSubFormat(snap.structuredSubFormat);
    setFileFormatType(snap.fileFormatType);
    setFileHasHeader(snap.fileHasHeader);
    setFileEncoding(snap.fileEncoding);
    setFileTargetTable(snap.fileTargetTable);
    setFileTargetSchema(snap.fileTargetSchema);
    setFileIfExists(snap.fileIfExists);
    setFileWizardStep(snap.fileWizardStep);
    setFileTargetConn(snap.fileTargetConn);
    setFileTargetConnLabel(snap.fileTargetConnLabel);
    setFileTargetDbType(snap.fileTargetDbType);
    setFileConnTestResult(snap.fileConnTestResult);
    setFileTargetTables(snap.fileTargetTables);
    setFileTargetTablesError(snap.fileTargetTablesError);
    setFileBuiltTask(snap.fileBuiltTask);
    setFileBuildError(snap.fileBuildError);
    setFilePreviewResult(snap.filePreviewResult);
    setFileExcludeBadRecords(snap.fileExcludeBadRecords);
    setFileLoadResult(snap.fileLoadResult);
    setFileLoadError(snap.fileLoadError);
    setFileBulkLoad(snap.fileBulkLoad);
    setFileBlobAccountName(snap.fileBlobAccountName);
    setFileBlobContainer(snap.fileBlobContainer);
    setFileBlobAuthMode(snap.fileBlobAuthMode);
    setFileBlobCredential(snap.fileBlobCredential);
    // Transient in-flight flags are never part of a saved tab -- always
    // reset rather than restored (see the type's comment above).
    setFileTargetTablesScanning(false);
    setFileConnTesting(false);
    setDetectingColumns(false);
    setFileBusy(false);
    setAdditionalFileUploads([]);
    setFileBatchLoadResults(null);
  };

  const blankFileWizardSnapshot = (): FileWizardSnapshot => ({
    fileUpload: null,
    fileUploadError: null,
    layoutFormat: "simple",
    layoutText: "",
    layoutFields: [],
    layoutError: null,
    fileCategory: "fixed_width",
    delimitedSubFormat: "csv",
    structuredSubFormat: "json",
    fileFormatType: "fixed_width",
    fileHasHeader: false,
    fileEncoding: "utf-8",
    fileTargetTable: "",
    fileTargetSchema: "public",
    fileIfExists: "append",
    fileWizardStep: 2,
    fileTargetConn: null,
    fileTargetConnLabel: "",
    fileTargetDbType: "",
    fileConnTestResult: null,
    fileTargetTables: null,
    fileTargetTablesError: null,
    fileBuiltTask: null,
    fileBuildError: null,
    filePreviewResult: null,
    fileExcludeBadRecords: false,
    fileLoadResult: null,
    fileLoadError: null,
    fileBulkLoad: false,
    fileBlobAccountName: "",
    fileBlobContainer: "",
    fileBlobAuthMode: "account_key",
    fileBlobCredential: "",
  });

  // Pure (no live-state reads/writes) so it's safe to call in a loop over
  // several newly-selected files without racing React's state updates --
  // mirrors what _afterFileSourceUploaded does, but returns a standalone
  // snapshot instead of writing straight into the shared wizard state.
  const _buildSnapshotForUpload = async (
    res: ConverterFileUploadResponse,
    filename: string,
  ): Promise<FileWizardSnapshot> => {
    // res.filename, not the passed `filename` -- see _afterFileSourceUploaded's
    // identical comment (an Excel upload is converted to CSV server-side).
    const { category, formatType } = _detectFileSourceCategory(res.filename);
    const base: FileWizardSnapshot = {
      ...blankFileWizardSnapshot(),
      fileUpload: res,
      fileTargetTable: _computeDefaultTargetTableName(filename),
      fileCategory: category,
      fileFormatType: formatType,
      layoutFormat: category === "copybook" ? "cobol" : "simple",
    };
    if (category === "delimited") {
      const delimFormat = formatType as "csv" | "tsv";
      const inspected = await apiv2.converterInspectFile({
        file_path: res.file_path,
        format_type: delimFormat,
        encoding: base.fileEncoding,
        has_header: true,
      });
      return {
        ...base,
        delimitedSubFormat: delimFormat,
        fileHasHeader: true,
        layoutFields: inspected.fields,
      };
    }
    if (category === "structured") {
      const structFormat = formatType as "json" | "jsonl" | "parquet";
      const inspected = await apiv2.converterInspectFile({
        file_path: res.file_path,
        format_type: structFormat,
        encoding: base.fileEncoding,
        has_header: false,
      });
      return {
        ...base,
        structuredSubFormat: structFormat,
        layoutFields: inspected.fields,
      };
    }
    return base;
  };

  // Entry point for "Multiple Files (Different Formats)": each file becomes
  // its own tab with its own independent snapshot (see FileWizardSnapshot
  // above). A file that fails to upload/inspect doesn't block the rest --
  // its error is collected and surfaced via multiFormatError, and every
  // other file still gets its tab.
  const handleMultiFormatFilesSelected = async (files: File[]) => {
    if (files.length === 0) return;
    setMultiFormatBusy(true);
    setMultiFormatError(null);
    let tabs = multiFormatTabs;
    let activeIndex = multiFormatActiveIndex;
    const failures: string[] = [];
    for (const file of files) {
      try {
        const uploaded = await apiv2.converterUploadFile(file, projectId);
        const snapshot = await _buildSnapshotForUpload(uploaded, file.name);
        tabs = [
          ...tabs,
          { filename: file.name, file_path: uploaded.file_path, snapshot },
        ];
        activeIndex = tabs.length - 1;
      } catch (e) {
        failures.push(
          `${file.name}: ${e instanceof Error ? e.message : "upload/inspection failed"}`,
        );
      }
    }
    // Preserve whatever progress was on-screen for the previously active
    // tab before switching the wizard over to display the newly added file.
    if (multiFormatActiveIndex !== null && tabs !== multiFormatTabs) {
      const outgoing = multiFormatActiveIndex;
      tabs = tabs.map((t, i) =>
        i === outgoing ? { ...t, snapshot: captureFileWizardSnapshot() } : t,
      );
    }
    setMultiFormatTabs(tabs);
    if (activeIndex !== multiFormatActiveIndex && activeIndex !== null) {
      applyFileWizardSnapshot(tabs[activeIndex].snapshot);
      setMultiFormatActiveIndex(activeIndex);
    }
    if (failures.length > 0) {
      setMultiFormatError(failures.join("; "));
    }
    setMultiFormatBusy(false);
  };

  // Switching tabs saves the outgoing tab's live progress and restores the
  // target tab's -- so revisiting a tab resumes exactly where it was left,
  // never losing work in progress (a build/preview/connection/load already
  // done on one file is untouched by configuring another).
  const handleMultiFormatSwitchTab = (targetIndex: number) => {
    // Block navigation while an "Add File(s)" upload is in flight -- that
    // handler captures multiFormatActiveIndex/tabs at call time and only
    // re-applies them after its awaits resolve, so switching tabs mid-flight
    // could write the new file's progress into the wrong slot.
    if (multiFormatBusy) return;
    if (targetIndex === multiFormatActiveIndex) return;
    let tabs = multiFormatTabs;
    if (multiFormatActiveIndex !== null) {
      const outgoing = multiFormatActiveIndex;
      tabs = tabs.map((t, i) =>
        i === outgoing ? { ...t, snapshot: captureFileWizardSnapshot() } : t,
      );
      setMultiFormatTabs(tabs);
    }
    applyFileWizardSnapshot(tabs[targetIndex].snapshot);
    setMultiFormatActiveIndex(targetIndex);
  };

  // Removes one tab from the multi-format flow. Blocked while an upload is
  // in flight for the same reason handleMultiFormatSwitchTab is (in-flight
  // closures capture stale tabs/index state). If the removed tab was the
  // active one, falls back to the previous tab (or the new first tab, or
  // none if that was the last one) and applies/clears the wizard state to
  // match -- otherwise the shared file-wizard state would keep showing the
  // just-removed tab's now-orphaned in-progress work.
  const handleMultiFormatRemoveTab = (idx: number) => {
    if (multiFormatBusy) return;
    const tabs = multiFormatTabs.filter((_t, i) => i !== idx);
    if (multiFormatActiveIndex === null) {
      setMultiFormatTabs(tabs);
      return;
    }
    if (idx !== multiFormatActiveIndex) {
      setMultiFormatTabs(tabs);
      setMultiFormatActiveIndex(
        idx < multiFormatActiveIndex
          ? multiFormatActiveIndex - 1
          : multiFormatActiveIndex,
      );
      return;
    }
    setMultiFormatTabs(tabs);
    if (tabs.length === 0) {
      // Wizard body is gated on multiFormatActiveIndex !== null (see the
      // "No files added yet" empty state just below) -- no need to reset
      // every individual fileXxx state, it simply stops being rendered.
      setMultiFormatActiveIndex(null);
      return;
    }
    const nextIndex = idx > 0 ? idx - 1 : 0;
    applyFileWizardSnapshot(tabs[nextIndex].snapshot);
    setMultiFormatActiveIndex(nextIndex);
  };

  // Entry point for a bulk (multi-file) selection: the first file drives
  // format/column auto-detection exactly like a single-file upload always
  // has; every additional file is assumed to share that same layout and is
  // just uploaded and parked in additionalFileUploads for handleLoadFileTask
  // to build+load once the target table is configured.
  const handleFileSourceFilesSelected = async (files: File[]) => {
    if (files.length === 0) return;
    const [first, ...rest] = files;
    await handleFileSourceUpload(first);
    setAdditionalFileUploads([]);
    if (rest.length === 0) return;
    setFileBusy(true);
    setFileUploadError(null);
    try {
      const uploaded = await Promise.all(
        rest.map((f) => apiv2.converterUploadFile(f, projectId)),
      );
      setAdditionalFileUploads(uploaded);
    } catch (e) {
      setFileUploadError(
        e instanceof Error
          ? e.message
          : "One or more additional files failed to upload",
      );
    } finally {
      setFileBusy(false);
    }
  };

  // "Import from SFTP" -- an alternate File Source entry point alongside the
  // local "Choose File(s) to Import" button, for source flat files that land
  // on a vendor/mainframe SFTP drop rather than a local disk. Connect/list
  // are request-scoped only (credentials never saved); a successful import
  // hands off to the exact same _afterFileSourceUploaded post-processing a
  // local upload uses.
  const handleSftpConnect = async () => {
    setSftpConnecting(true);
    setSftpError(null);
    try {
      await apiv2.converterSftpConnect(sftpConn);
      setSftpConnected(true);
      await handleSftpListFiles(sftpConn.remote_path || ".");
    } catch (e) {
      setSftpConnected(false);
      setSftpFiles([]);
      setSftpError(e instanceof Error ? e.message : "SFTP connection failed");
    } finally {
      setSftpConnecting(false);
    }
  };

  const handleSftpListFiles = async (path: string) => {
    setSftpListing(true);
    setSftpError(null);
    try {
      const res = await apiv2.converterSftpListFiles(sftpConn, path);
      setSftpConn((p) => ({ ...p, remote_path: res.path }));
      setSftpFiles(res.files);
    } catch (e) {
      setSftpError(
        e instanceof Error ? e.message : "Could not list SFTP files",
      );
    } finally {
      setSftpListing(false);
    }
  };

  const handleSftpImportFile = async (f: ConverterSftpFileMeta) => {
    if (f.is_dir) {
      void handleSftpListFiles(f.path);
      return;
    }
    setSftpImportingPath(f.path);
    setSftpError(null);
    setFileBusy(true);
    try {
      const res = await apiv2.converterSftpImportFile(
        sftpConn,
        f.path,
        projectId,
      );
      await _afterFileSourceUploaded(res, f.name);
      setShowSftpPanel(false);
    } catch (e) {
      setSftpError(e instanceof Error ? e.message : "File import failed");
    } finally {
      setSftpImportingPath(null);
      setFileBusy(false);
    }
  };

  // "Import from SharePoint" -- an alternate File Source entry point:
  // connect, list the site's Lists, then import one as a CSV File Source
  // (same hand-off to _afterFileSourceUploaded a local upload/SFTP use).
  const handleSharePointConnect = async () => {
    setSpConnecting(true);
    setSpError(null);
    try {
      await apiv2.converterSharePointConnect(sharePointConn);
      setSpConnected(true);
      await handleSharePointRefreshLists();
    } catch (e) {
      setSpConnected(false);
      setSpLists([]);
      setSpError(
        e instanceof Error ? e.message : "SharePoint connection failed",
      );
    } finally {
      setSpConnecting(false);
    }
  };

  const handleSharePointRefreshLists = async () => {
    setSpListing(true);
    setSpError(null);
    try {
      const res = await apiv2.converterSharePointListLists(sharePointConn);
      setSpLists(res.lists);
    } catch (e) {
      setSpError(
        e instanceof Error ? e.message : "Could not list SharePoint lists",
      );
    } finally {
      setSpListing(false);
    }
  };

  const handleSharePointToggleSelected = (id: string) => {
    setSpSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // SFTP as a Legacy Code source-type acquisition method -- connect, then
  // browse into the configured remote_path.
  const handleLegacySftpConnect = async () => {
    setLegacySftpConnecting(true);
    setLegacySftpError(null);
    try {
      await apiv2.converterSftpConnect(legacySftpConn);
      setLegacySftpConnected(true);
      await handleLegacySftpListFiles(legacySftpConn.remote_path || ".");
    } catch (e) {
      setLegacySftpConnected(false);
      setLegacySftpFiles([]);
      setLegacySftpError(
        e instanceof Error ? e.message : "SFTP connection failed",
      );
    } finally {
      setLegacySftpConnecting(false);
    }
  };

  const handleLegacySftpListFiles = async (path: string) => {
    setLegacySftpListing(true);
    setLegacySftpError(null);
    try {
      const res = await apiv2.converterSftpListFiles(legacySftpConn, path);
      setLegacySftpConn((p) => ({ ...p, remote_path: res.path }));
      setLegacySftpFiles(res.files);
    } catch (e) {
      setLegacySftpError(
        e instanceof Error ? e.message : "Could not list SFTP files",
      );
    } finally {
      setLegacySftpListing(false);
    }
  };

  const handleLegacySftpToggleSelected = (path: string) => {
    setLegacySftpSelectedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  // "Extract Tables from PDF" -- the standalone Data Loader entry point
  // (distinct from the Legacy Code acquisition method below): upload a
  // single PDF, run deterministic (pdfplumber, non-LLM) table detection,
  // then let the user pick which detected table becomes the active File
  // Source (each is already registered as its own CSV via
  // upload_source_file, so _afterFileSourceUploaded needs no PDF-specific
  // handling).
  const handlePdfFileSelected = async (file: File) => {
    setPdfExtracting(true);
    setPdfError(null);
    setPdfTables([]);
    try {
      const uploaded = await apiv2.converterUploadFile(file, projectId);
      const res = await apiv2.converterPdfExtractTables(uploaded.file_path);
      setPdfTables(res.tables);
      if (res.tables.length === 0) {
        setPdfError("No tables were detected in this PDF.");
      }
    } catch (e) {
      setPdfError(
        e instanceof Error ? e.message : "PDF table extraction failed",
      );
    } finally {
      setPdfExtracting(false);
    }
  };

  // "Import Folder" -- reads every PDF found (recursively) under a
  // folder picked via the browser's native folder picker, instead of
  // uploading one at a time. When every PDF yields exactly one
  // matching-columns table, the backend merges them into a single
  // consolidated table (tagged with a source_file column); otherwise it
  // falls back to one entry per detected table -- either way the result
  // lands in the same pdfTables list handlePdfUseTable already knows how
  // to apply.
  const handlePdfFolderFilesSelected = async (files: File[]) => {
    const pdfFiles = files.filter((f) => f.name.toLowerCase().endsWith(".pdf"));
    const folderName =
      (
        files[0] as File & { webkitRelativePath?: string }
      )?.webkitRelativePath?.split("/")[0] ?? null;
    setPdfFolderName(folderName);
    if (pdfFiles.length === 0) {
      setPdfError("No PDF files were found in this folder.");
      return;
    }
    setPdfFolderLoading(true);
    setPdfError(null);
    setPdfTables([]);
    // Upload all files concurrently (not one-at-a-time) so N files cost one
    // round trip's worth of wall-clock time rather than N -- a bad file
    // (e.g. corrupt/0-byte) still doesn't discard any sibling upload that
    // succeeded, since each settles independently.
    const pdfResults = await Promise.allSettled(
      pdfFiles.map((file) => apiv2.converterUploadFile(file, projectId)),
    );
    const uploads: ConverterFileUploadResponse[] = [];
    const uploadFailures: string[] = [];
    pdfResults.forEach((result, i) => {
      if (result.status === "fulfilled") {
        uploads.push(result.value);
      } else {
        const e = result.reason;
        uploadFailures.push(
          `${pdfFiles[i].name} (${e instanceof Error ? e.message : "upload failed"})`,
        );
      }
    });
    if (uploads.length === 0) {
      setPdfError(
        uploadFailures.length > 0
          ? `${uploadFailures.length} file(s) could not be uploaded: ${uploadFailures.join("; ")}`
          : "No PDF files could be uploaded.",
      );
      setPdfFolderLoading(false);
      return;
    }
    try {
      const res = await apiv2.converterPdfExtractTablesMulti(
        uploads.map((u) => u.file_path),
      );
      setPdfTables(res.tables);
      const allErrors = [
        ...uploadFailures,
        ...res.errors.map((e) => `${e.filename} (${e.error})`),
      ];
      if (res.tables.length === 0) {
        setPdfError(allErrors[0] ?? "No tables were detected in this folder.");
      } else if (allErrors.length > 0) {
        setPdfError(
          `${allErrors.length} file(s) could not be processed: ${allErrors.join("; ")}`,
        );
      }
    } catch (e) {
      setPdfError(e instanceof Error ? e.message : "PDF folder import failed");
    } finally {
      setPdfFolderLoading(false);
    }
  };

  const handlePdfUseTable = async (table: ConverterPdfExtractedTable) => {
    setPdfUsingIndex(table.table_index);
    try {
      await _afterFileSourceUploaded(table, table.filename);
      // Skip past step 1 (the PDF/file picker) straight to the normal
      // File Source wizard (Record Layout -> Build & Preview -> Connect
      // Target -> Load Data) -- the detected table is already registered
      // as a File Source and its columns already auto-detected.
      setFileWizardStep(2);
    } finally {
      setPdfUsingIndex(null);
    }
  };

  // "Extract Tables from PDF" -- Legacy Code source-type acquisition
  // method: upload one or more PDFs to object storage so their paths are
  // available for handleParseLegacyPdf's "Parse & Continue" call, which
  // runs deterministic (pdfplumber, non-LLM) table detection and feeds
  // every detected table through the same parse-legacy pipeline a local
  // file upload uses.
  const handlePdfFilesSelected = async (files: File[]) => {
    setPdfExtracting(true);
    setPdfError(null);
    // Upload all files concurrently (not one-at-a-time) so N files cost one
    // round trip's worth of wall-clock time rather than N -- a bad file
    // still doesn't discard any sibling upload that succeeded, since each
    // settles independently (mirrors handlePdfFolderFilesSelected).
    const results = await Promise.allSettled(
      files.map((file) => apiv2.converterUploadFile(file, projectId)),
    );
    const uploads: ConverterFileUploadResponse[] = [];
    const failures: string[] = [];
    results.forEach((result, i) => {
      if (result.status === "fulfilled") {
        uploads.push(result.value);
      } else {
        const e = result.reason;
        failures.push(
          `${files[i].name} (${e instanceof Error ? e.message : "upload failed"})`,
        );
      }
    });
    if (uploads.length > 0) {
      setLegacyPdfUploads((prev) => [...prev, ...uploads]);
    }
    if (failures.length > 0) {
      setPdfError(
        `${failures.length} file(s) could not be uploaded: ${failures.join("; ")}`,
      );
    }
    setPdfExtracting(false);
  };

  // User-driven override from the File Format selector — re-detects
  // columns against the CURRENTLY uploaded file (if any) under the newly
  // chosen category/sub-format, rather than only ever trusting the
  // extension-based guess made at upload time.
  const handleFileCategoryChange = async (
    category: "fixed_width" | "copybook" | "delimited" | "structured",
  ) => {
    setFileCategory(category);
    setLayoutFormat(category === "copybook" ? "cobol" : "simple");
    setLayoutFields([]);
    setFileBuiltTask(null);
    setFilePreviewResult(null);
    setFileExcludeBadRecords(false);
    setFileLoadResult(null);
    if (category === "fixed_width" || category === "copybook") {
      setFileFormatType("fixed_width");
      return;
    }
    const formatType =
      category === "delimited" ? delimitedSubFormat : structuredSubFormat;
    setFileFormatType(formatType);
    if (fileUpload) {
      await _runColumnInspection(
        fileUpload.file_path,
        formatType,
        category === "delimited" ? fileHasHeader : false,
      );
    }
  };

  const handleParseLayout = async () => {
    if (!layoutText.trim()) {
      setLayoutError("Paste a copybook or layout definition first");
      return;
    }
    setFileBusy(true);
    setLayoutError(null);
    try {
      const res = await apiv2.converterParseLayout(layoutText, layoutFormat);
      setLayoutFields(res.fields);
      setFileBuiltTask(null);
      setFilePreviewResult(null);
      setFileExcludeBadRecords(false);
      setFileLoadResult(null);
    } catch (e) {
      setLayoutError(e instanceof Error ? e.message : "Layout parse failed");
    } finally {
      setFileBusy(false);
    }
  };

  const handleImportLayoutFile = async (file: File) => {
    setLayoutError(null);
    try {
      const text = await file.text();
      setLayoutText(text);
    } catch {
      setLayoutError("Could not read the selected layout file");
    }
  };

  const handleBuildAndPreviewFileTask = async () => {
    if (!fileUpload) {
      setFileBuildError("Upload a source file first");
      return;
    }
    if (!layoutFields.length) {
      setFileBuildError("Parse a copybook/layout first");
      return;
    }
    if (!fileTargetTable.trim()) {
      setFileBuildError("Target table name is required");
      return;
    }
    setFileBusy(true);
    setFileBuildError(null);
    try {
      const built = await apiv2.converterBuildFileTask({
        file_path: fileUpload.file_path,
        format_type: fileFormatType,
        encoding: fileEncoding,
        has_header: fileHasHeader,
        fields: layoutFields,
        target_table: fileTargetTable.trim(),
        target_schema: fileTargetSchema.trim() || "public",
        // The build-task endpoint's own request schema only accepts
        // fail/replace/append (truncate is a load-time-only concept there);
        // build with a safe placeholder, then patch the real choice onto
        // the returned task object before it's ever previewed/loaded.
        if_exists: buildSafeIfExists(fileIfExists),
        chunk_size: 5000,
      });
      built.task.target.if_exists = fileIfExists;
      const preview = await apiv2.converterPreview(built.task, 25);
      setFileBuiltTask(built.task);
      setFileExcludeBadRecords(false);
      setFilePreviewResult(preview);
      setFileLoadResult(null);
    } catch (e) {
      setFileBuildError(
        e instanceof Error ? e.message : "Build/preview failed",
      );
    } finally {
      setFileBusy(false);
    }
  };

  // Lets the Target Table Name field offer already-existing tables (e.g. to
  // append/truncate into one) instead of only ever typing a brand-new name.
  const handleScanFileTargetTables = async () => {
    if (!fileTargetConn) {
      setFileTargetTablesError("Connect a target database first");
      return;
    }
    await scanFileTargetTablesFor(fileTargetConn, fileTargetSchema);
  };

  // Auto-populate the searchable target-table list as soon as a connection
  // exists, so the operator can search existing tables without an extra click.
  useEffect(() => {
    if (fileTargetConn && !fileTargetTables && !fileTargetTablesScanning) {
      void handleScanFileTargetTables();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileTargetConn]);

  const handleTestFileConnection = async () => {
    if (!fileTargetConn) {
      setErr("Connect a target database first");
      return;
    }
    setFileConnTesting(true);
    setFileConnTestResult(null);
    setError(null);
    try {
      const res = await apiv2.converterTestFileConnection(fileTargetConn);
      setFileConnTestResult(res);
      setFileTargetDbType(res.dialect || fileTargetDbType);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Connection test failed");
    } finally {
      setFileConnTesting(false);
    }
  };

  const handleLoadFileTask = async () => {
    if (!fileBuiltTask) return;
    if (!fileTargetConn) {
      setFileLoadError("Connect a target database first");
      return;
    }
    if (
      fileBulkLoad &&
      fileTargetDbType === "synapse" &&
      (!fileBlobAccountName.trim() ||
        !fileBlobContainer.trim() ||
        !fileBlobCredential.trim())
    ) {
      setFileLoadError(
        "Azure Blob staging details are required for a Synapse bulk load",
      );
      return;
    }
    // fileBuiltTask.target.if_exists/table_name/schema_name were all set
    // from fileIfExists/fileTargetTable/fileTargetSchema at Build & Preview
    // time; re-sync all of them here so editing any of these fields after
    // building (without rebuilding) is actually honored instead of silently
    // loading against the stale value that was baked in earlier — the exact
    // bug class that caused a corrected schema name to still load into the
    // old, mistyped schema. bulk_load/synapse_blob_staging are set here for
    // the same reason — the toggle/fields can change after Build & Preview
    // without forcing a rebuild.
    fileBuiltTask.target.table_name = fileTargetTable.trim();
    fileBuiltTask.target.schema_name = fileTargetSchema.trim() || "public";
    fileBuiltTask.target.if_exists = fileIfExists;
    fileBuiltTask.target.bulk_load = fileBulkLoad;
    fileBuiltTask.target.exclude_incomplete_rows = fileExcludeBadRecords;
    fileBuiltTask.target.synapse_blob_staging =
      fileBulkLoad && fileTargetDbType === "synapse"
        ? {
            account_name: fileBlobAccountName,
            container: fileBlobContainer,
            auth_mode: fileBlobAuthMode,
            credential_value: fileBlobCredential,
          }
        : undefined;
    setFileBusy(true);
    setFileLoadError(null);
    try {
      // The first file's own if_exists choice (fail/replace/append) creates
      // or prepares the table; every additional file in the bulk selection
      // is forced to "append" onto whatever the first file just produced —
      // running them concurrently instead (converterLoadBatch) with mixed
      // if_exists values would race a table replace/create against inserts
      // into a table that may not exist yet.
      //
      // Bulk load over these synchronous endpoints is fine at moderate file
      // sizes, but a bulk_load=True item over ~600MB is confirmed to exceed
      // CloudFront's 60s origin timeout (the load still finishes server-side,
      // the caller just never sees it) — see convert_load_batch's docstring.
      // Large bulk-load files should go through the folder ingest flow below
      // instead, which dispatches through the async pipeline engine.
      const firstResult = await apiv2.converterLoad(
        fileBuiltTask,
        fileTargetConn,
      );
      setFileLoadResult(firstResult);
      if (additionalFileUploads.length === 0) {
        setFileBatchLoadResults(null);
        return;
      }
      // Each additional file becomes its own bulk_load_synapse() call,
      // running concurrently via load_conversion_batch (up to 4 at once).
      // Fine for Snowflake (independent PUT+COPY INTO per file). For
      // Synapse specifically, bulk_load_synapse's own docstring flags that
      // concurrent COPY INTOs compete for the same dedicated-pool DWU
      // slots instead of actually going faster -- a large multi-file
      // Synapse bulk load should go through the folder ingest flow's
      // bulk_load_synapse_folder (one wildcard COPY INTO) instead. This
      // path is still a large win over row-by-row insert for a moderate
      // number of files; it's the "many large Synapse files at once" case
      // that wants the other flow.
      const restTasks = await Promise.all(
        additionalFileUploads.map(async (upload) => {
          const built = await apiv2.converterBuildFileTask({
            file_path: upload.file_path,
            format_type: fileFormatType,
            encoding: fileEncoding,
            has_header: fileHasHeader,
            fields: layoutFields,
            target_table: fileTargetTable.trim(),
            target_schema: fileTargetSchema.trim() || "public",
            if_exists: "append",
            chunk_size: 5000,
          });
          built.task.target.bulk_load = fileBulkLoad;
          built.task.target.synapse_blob_staging =
            fileBulkLoad && fileTargetDbType === "synapse"
              ? {
                  account_name: fileBlobAccountName,
                  container: fileBlobContainer,
                  auth_mode: fileBlobAuthMode,
                  credential_value: fileBlobCredential,
                }
              : undefined;
          return built.task;
        }),
      );
      const batch = await apiv2.converterLoadBatch(restTasks, fileTargetConn);
      setFileBatchLoadResults(batch.results);
    } catch (e) {
      setFileLoadError(e instanceof Error ? e.message : "Load failed");
    } finally {
      setFileBusy(false);
    }
  };

  // Lets the operator start a fresh file after a successful (or failed)
  // load, without leaving stale preview/result state visible on Step 1.
  // Target connection is intentionally left in place — loading several
  // files into the same database is the common case.
  const handleStartNewFileUpload = () => {
    setFileUpload(null);
    setFileUploadError(null);
    setLayoutFields([]);
    setFileBuiltTask(null);
    setFilePreviewResult(null);
    setFileExcludeBadRecords(false);
    setFileLoadResult(null);
    setFileBatchLoadResults(null);
    setAdditionalFileUploads([]);
    setFileLoadError(null);
    setFileTargetTable("");
    setFileWizardStep(1);
  };

  const handleFolderFilesSelected = (files: FileList | null) => {
    setFolderFiles(files ? Array.from(files) : []);
  };

  const handleStartFolderIngest = async () => {
    if (!folderFiles.length) {
      setErr("Choose one or more files first");
      return;
    }
    const isDelimited = folderFormatType !== "fixed_width";
    if (!isDelimited && !layoutFields.length) {
      setErr("Parse a copybook/layout first");
      return;
    }
    if (!folderOneTablePerFile && !fileTargetTable.trim()) {
      setErr("Target table name is required");
      return;
    }
    if (!fileTargetConn) {
      setErr("Connect a target database first");
      return;
    }
    if (
      folderBulkLoad &&
      fileTargetDbType === "synapse" &&
      (!folderBlobAccountName.trim() ||
        !folderBlobContainer.trim() ||
        !folderBlobCredential.trim())
    ) {
      setErr("Azure Blob staging details are required for a Synapse bulk load");
      return;
    }

    setFolderBusy(true);
    setError(null);
    setFolderPipelineRunId(null);
    setFolderProgress(null);
    try {
      const tasks: ConverterTask[] = [];
      for (const file of folderFiles) {
        const uploaded = await apiv2.converterUploadFile(file, projectId);
        const targetTable = folderOneTablePerFile
          ? sanitizeTableNameFromFilename(file.name)
          : fileTargetTable.trim();
        const built = await apiv2.converterBuildFileTask({
          file_path: uploaded.file_path,
          format_type: folderFormatType,
          encoding: fileEncoding,
          has_header: fileHasHeader,
          // Fixed-width formats need the parsed layout; delimited formats
          // read their own header row and need nothing here.
          fields: isDelimited ? [] : layoutFields,
          target_table: targetTable,
          target_schema: fileTargetSchema.trim() || "public",
          if_exists: buildSafeIfExists(fileIfExists),
          chunk_size: 5000,
        });
        const task = built.task;
        task.target.if_exists = fileIfExists;
        task.target.bulk_load = folderBulkLoad;
        if (folderBulkLoad && fileTargetDbType === "synapse") {
          task.target.synapse_blob_staging = {
            account_name: folderBlobAccountName,
            container: folderBlobContainer,
            auth_mode: folderBlobAuthMode,
            credential_value: folderBlobCredential,
          };
        }
        tasks.push(task);
      }

      const res = await apiv2.converterLoadBatchPipeline(tasks, fileTargetConn);
      setFolderPipelineRunId(res.pipeline_run_id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Bulk ingest failed to start");
    } finally {
      setFolderBusy(false);
    }
  };

  // Poll on mount, then layer live SSE push updates on top — same
  // belt-and-suspenders pattern as MdmRunStatusView, since SSE alone can
  // silently stop delivering across a proxy/idle timeout.
  useEffect(() => {
    if (!folderPipelineRunId) return;
    let cancelled = false;
    const TERMINAL = new Set(["completed", "failed", "cancelled", "revoked"]);
    let lastStatus = "";

    const poll = async () => {
      try {
        const progress = await getPipelineProgress(folderPipelineRunId);
        if (!cancelled) setFolderProgress(progress);
      } catch {
        // transient — next poll or SSE frame will recover
      }
    };
    poll();
    const intervalId = setInterval(() => {
      if (!TERMINAL.has(lastStatus)) poll();
    }, 5000);

    const es = subscribeToPipelineProgress(
      folderPipelineRunId,
      (progress) => {
        if (!cancelled) setFolderProgress(progress);
      },
      (status) => {
        lastStatus = status;
      },
      (status) => {
        // The server ended the stream: this user's access to the run is gone
        // (403) or the run no longer exists (404). Stop polling it too.
        lastStatus = "revoked";
        clearInterval(intervalId);
        if (!cancelled) {
          setErr(
            status === 404
              ? "This bulk-load run no longer exists."
              : "Your access to this bulk-load run was revoked.",
          );
        }
      },
    );

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      es.close();
    };
  }, [folderPipelineRunId]);

  const handleScanSchema = async () => {
    if (!sourceConnectionId) {
      setErr("Connect to a source database first");
      return;
    }
    await runSourceScan(sourceConnectionId, sourceSchemaName);
  };

  // Shared tail for every Legacy Code acquisition method (local upload,
  // SharePoint, PDF) -- all of them resolve to the same SchemaExtractResult
  // shape, so they all finish the same way: snapshot it as this project's
  // source schema and move on to Schema DDL.
  const _finishLegacyParse = (
    res: SchemaExtractResult,
    sourceLabel: string,
  ) => {
    setSchemaResult(res);
    setLegacyFileRefs(res.file_refs ?? {});
    setLegacySourceInfo({
      filename: sourceLabel,
      format: res.schema_name || res.db_type,
      tableCount: res.tables.length,
      warnings: res.warnings ?? [],
      consolidationNote: res.consolidation_note ?? null,
      packageDetails: res.package_details ?? {},
      additionalItems: res.additional_items ?? {},
    });
    setLegacyPackageDetailsOpen(false);
    setLegacyAdditionalItemsOpen(false);
    setStep("schema");
  };

  const handleParseLegacyCode = async () => {
    if (legacyFiles.length === 0) {
      setErr("Choose one or more legacy code/schema files first");
      return;
    }
    setParsingLegacyCode(true);
    setError(null);
    try {
      const res =
        legacyFiles.length === 1
          ? await apiv2.converterParseLegacySource(legacyFiles[0], projectId)
          : await apiv2.converterParseLegacySourceBatch(
              legacyFiles,
              projectId,
              legacyConsolidateMode === "consolidate",
            );
      _finishLegacyParse(
        res,
        legacyFiles.length === 1
          ? legacyFiles[0].name
          : `${legacyFiles.length} files`,
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Legacy source parse failed");
    } finally {
      setParsingLegacyCode(false);
    }
  };

  // SAP BODS ETL dataflow import -- parses real <DataIntegratorExport>
  // job/workflow/dataflow XML directly into an STTM mapping plus source/
  // target DDL and an audit workbook, all in one call (no intermediate
  // schema/mapping-review step, since the ETL logic itself already
  // determines the mapping). Kept entirely separate from
  // handleParseLegacyCode/schemaResult -- this doesn't feed the normal
  // Source -> Target Mapping pipeline at all, it produces four standalone
  // downloadable documents for manual review/handoff.
  const handleParseBodsDataflow = async () => {
    if (bodsDataflowFiles.length === 0) {
      setErr("Choose one or more BODS dataflow export XML files first");
      return;
    }
    setParsingBodsDataflow(true);
    setError(null);
    setBodsDataflowResult(null);
    try {
      const res = await apiv2.converterParseBodsDataflowBatch(
        bodsDataflowFiles,
        projectName,
        projectId,
      );
      setBodsDataflowResult(res);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "BODS dataflow import failed");
    } finally {
      setParsingBodsDataflow(false);
    }
  };

  // Informatica PowerCenter mapping import -- parses real Source
  // Qualifier/Expression/Joiner/Lookup/Filter transformation logic
  // directly into an STTM mapping plus source/target DDL and an audit
  // workbook, all in one call. Same standalone-document shape as
  // handleParseBodsDataflow -- doesn't feed schemaResult or the normal
  // Source -> Target Mapping pipeline.
  const handleParseInformaticaMapping = async () => {
    if (informaticaMappingFiles.length === 0) {
      setErr("Choose one or more Informatica mapping export XML files first");
      return;
    }
    setParsingInformaticaMapping(true);
    setError(null);
    setInformaticaMappingResult(null);
    try {
      const res = await apiv2.converterParseInformaticaMappingBatch(
        informaticaMappingFiles,
        projectName,
        projectId,
      );
      setInformaticaMappingResult(res);
    } catch (e) {
      setErr(
        e instanceof Error ? e.message : "Informatica mapping import failed",
      );
    } finally {
      setParsingInformaticaMapping(false);
    }
  };

  // SharePoint as a Legacy Code source-type acquisition method -- parses
  // every checked SharePoint list into the same SchemaExtractResult shape
  // a local file upload would produce.
  const handleParseLegacySharePoint = async () => {
    const targets = spLists.filter((l) => spSelectedIds.has(l.id));
    if (targets.length === 0) {
      setErr("Select one or more SharePoint lists first");
      return;
    }
    setParsingLegacyCode(true);
    setError(null);
    try {
      const res = await apiv2.converterParseLegacySharePoint(
        sharePointConn,
        targets.map((l) => l.id),
        targets.map((l) => l.name),
        projectId,
      );
      _finishLegacyParse(
        res,
        targets.length === 1
          ? targets[0].name
          : `${targets.length} SharePoint lists`,
      );
      setSpSelectedIds(new Set());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "SharePoint source parse failed");
    } finally {
      setParsingLegacyCode(false);
    }
  };

  // CRM/SaaS Systems as a Legacy Code source-type acquisition method --
  // verifies the connection and loads the object/dataset catalogue for the
  // picker below (crmSourceObjects), same call converterSalesforceConnect/
  // Objects makes for the Target Platform step, just against crmSourceConn
  // instead of sfConn so a project's extract-from and load-into
  // connections never collide.
  const handleCrmSourceConnect = async () => {
    setCrmSourceConnecting(true);
    setCrmSourceError(null);
    try {
      await apiv2.converterSalesforceConnect(crmSourceConn, projectId);
      setCrmSourceConnected(true);
      setCrmSourceConnecting(false);
      setCrmSourceLoadingObjects(true);
      try {
        const objRes = await apiv2.converterSalesforceObjects(
          crmSourceConn,
          undefined,
          true,
          projectId,
        );
        setCrmSourceObjects(objRes.objects);
      } finally {
        setCrmSourceLoadingObjects(false);
      }
    } catch (e) {
      setCrmSourceError(
        e instanceof Error ? e.message : "CRM/SaaS connection failed",
      );
      setCrmSourceConnecting(false);
    }
  };

  const handleCrmSourceToggleSelected = (apiName: string) => {
    setCrmSourceSelectedObjects((prev) => {
      const next = new Set(prev);
      if (next.has(apiName)) next.delete(apiName);
      else next.add(apiName);
      return next;
    });
  };

  // "Stream to DB" (issue #735) -- opens the modal for one object, seeded
  // with a 1:1 field-name mapping guess; the user adjusts target
  // table/columns before starting. If this object already has a load
  // running in the background (modal previously closed mid-load),
  // reconnect to that job instead of discarding it and starting a new one.
  const openCrmStreamLoad = (obj: SalesforceObjectMeta) => {
    setCrmStreamModalObject(obj);
    setCrmStreamConfig({
      targetSchema: ddlTargetSchema || targetSchemaName || "",
      targetTable: obj.api_name.toUpperCase(),
      batchSize: 2000,
      truncate: false,
      fieldMappings: seedCrmStreamFieldMappings(obj),
    });
    setCrmStreamError(null);
    const activeJobId = crmStreamActiveJobsRef.current.get(obj.api_name);
    if (activeJobId) {
      setCrmStreamJobId(activeJobId);
      setCrmStreamStatus(null);
      pollCrmStreamLoad(activeJobId, obj.api_name);
    } else {
      setCrmStreamJobId(null);
      setCrmStreamStatus(null);
    }
  };

  // Closing the modal no longer discards an in-flight load: if one is
  // running, its job_id stays tracked (crmStreamActiveJobsRef) and polling
  // continues in the background so reopening the modal for the same
  // object reconnects to live status instead of orphaning the job.
  const closeCrmStreamLoad = () => {
    const stillRunning =
      !!crmStreamJobId &&
      (!crmStreamStatus || crmStreamStatus.status === "running");
    if (!stillRunning && crmStreamPollRef.current) {
      clearInterval(crmStreamPollRef.current);
      crmStreamPollRef.current = null;
    }
    setCrmStreamModalObject(null);
  };

  const pollCrmStreamLoad = useCallback(
    (jobId: string, objectApiName: string) => {
      crmStreamActiveJobsRef.current.set(objectApiName, jobId);
      if (crmStreamPollRef.current) clearInterval(crmStreamPollRef.current);
      const checkStatus = async () => {
        try {
          const status = await apiv2.converterLoadCrmToDbStatus(jobId);
          setCrmStreamStatus(status);
          if (status.status !== "running") {
            crmStreamActiveJobsRef.current.delete(objectApiName);
            if (crmStreamPollRef.current) {
              clearInterval(crmStreamPollRef.current);
              crmStreamPollRef.current = null;
            }
          }
        } catch (e) {
          crmStreamActiveJobsRef.current.delete(objectApiName);
          if (crmStreamPollRef.current) {
            clearInterval(crmStreamPollRef.current);
            crmStreamPollRef.current = null;
          }
          setCrmStreamError(
            e instanceof Error ? e.message : "Lost track of the load job",
          );
        }
      };
      void checkStatus();
      crmStreamPollRef.current = setInterval(() => void checkStatus(), 1500);
    },
    [],
  );

  const handleStartCrmStreamLoad = async () => {
    if (!crmStreamModalObject) return;
    const validationError = validateCrmStreamLoadConfig(
      crmStreamConfig,
      !!targetConnectionId,
    );
    if (validationError) {
      setCrmStreamError(validationError);
      return;
    }
    if (!targetConnectionId) return; // narrows for TS below; validated above
    setCrmStreamStarting(true);
    setCrmStreamError(null);
    try {
      const fieldMappings: CrmToDbFieldMapping[] = buildCrmToDbFieldMappings(
        crmStreamConfig.fieldMappings,
      );
      const { job_id } = await apiv2.converterLoadCrmToDb({
        connection: crmSourceConn,
        objectName: crmStreamModalObject.api_name,
        targetConnectionRef: { connectionId: targetConnectionId, projectId },
        targetSchema: crmStreamConfig.targetSchema,
        targetTable: crmStreamConfig.targetTable,
        fieldMappings,
        batchSize: crmStreamConfig.batchSize,
        truncate: crmStreamConfig.truncate,
        projectId,
      });
      setCrmStreamJobId(job_id);
      pollCrmStreamLoad(job_id, crmStreamModalObject.api_name);
    } catch (e) {
      setCrmStreamError(
        e instanceof Error ? e.message : "CRM streaming load failed to start",
      );
    } finally {
      setCrmStreamStarting(false);
    }
  };

  const handleCancelCrmStreamLoad = async () => {
    if (!crmStreamJobId) return;
    try {
      const status = await apiv2.converterLoadCrmToDbCancel(crmStreamJobId);
      setCrmStreamStatus(status);
    } catch (e) {
      setCrmStreamError(
        e instanceof Error ? e.message : "Failed to cancel the load",
      );
    }
  };

  // CRM/SaaS Systems' Parse & Continue -- extracts records from every
  // checked object and parses them into the same SchemaExtractResult
  // shape a local file upload would produce.
  const handleParseLegacyCrm = async () => {
    const targets = Array.from(crmSourceSelectedObjects);
    if (targets.length === 0) {
      setErr("Select one or more objects first");
      return;
    }
    setParsingLegacyCode(true);
    setError(null);
    try {
      const res = await apiv2.converterParseLegacyCrm(
        crmSourceConn,
        targets,
        projectId,
        crmParseSampleOnly,
      );
      _finishLegacyParse(
        res,
        targets.length === 1
          ? targets[0]
          : `${targets.length} ${_CRM_TARGET_LABELS[crmSourceConn.target_type]} objects`,
      );
      setCrmSourceSelectedObjects(new Set());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "CRM/SaaS source parse failed");
    } finally {
      setParsingLegacyCode(false);
    }
  };

  // SFTP as a Legacy Code source-type acquisition method -- downloads
  // every checked remote file and parses them into the same
  // SchemaExtractResult shape a local file upload would produce.
  const handleParseLegacySftp = async () => {
    const targets = Array.from(legacySftpSelectedPaths);
    if (targets.length === 0) {
      setErr("Select one or more files from the SFTP server first");
      return;
    }
    setParsingLegacyCode(true);
    setError(null);
    try {
      const res = await apiv2.converterParseLegacySftp(
        legacySftpConn,
        targets,
        projectId,
        legacyConsolidateMode === "consolidate",
      );
      _finishLegacyParse(
        res,
        targets.length === 1 ? targets[0] : `${targets.length} SFTP files`,
      );
      setLegacySftpSelectedPaths(new Set());
    } catch (e) {
      setErr(e instanceof Error ? e.message : "SFTP source parse failed");
    } finally {
      setParsingLegacyCode(false);
    }
  };

  // PDF table extraction as a Legacy Code source-type acquisition method --
  // parses every table detected across the uploaded PDF(s) into the same
  // SchemaExtractResult shape a local file upload would produce. Runs as a
  // Celery background job (see converterParseLegacyPdfAsync) rather than a
  // single blocking request -- a large or scanned-heavy batch can take tens
  // of seconds to a few minutes of OCR, which previously ran in-process on
  // the API and could make the whole request (and, on a memory-constrained
  // host, the API process itself) time out.
  const handleParseLegacyPdf = async () => {
    if (legacyPdfUploads.length === 0) {
      setErr("Choose one or more PDF files first");
      return;
    }
    const uploads = legacyPdfUploads;
    const sourceLabel =
      uploads.length === 1
        ? uploads[0].filename
        : `${uploads.length} PDF files`;
    setParsingLegacyCode(true);
    setError(null);
    if (pdfParsePollRef.current) clearInterval(pdfParsePollRef.current);
    try {
      const { job_id } = await apiv2.converterParseLegacyPdfAsync(
        uploads.map((u) => u.file_path),
        projectId,
        pdfConsolidateMode === "consolidate",
      );
      // Guard against a stuck/forever-"running" job (worker crash, lost
      // Celery task, etc.) wedging the UI in a permanent spinner, and
      // tolerate a handful of consecutive transient poll failures (a single
      // dropped request shouldn't force the user to re-upload and restart)
      // while still giving up on a genuinely broken connection rather than
      // polling silently forever.
      const pollStartedAt = Date.now();
      // The backend task runs under HEAVY_BATCH_LIMITS (3600s hard / 3300s
      // soft, see ztd_app/worker/task_policy.py) -- a large OCR batch can
      // legitimately take up to ~55 minutes, so this must not time out
      // before the backend does. 65 minutes gives the stale-job reaper room
      // to mark a hard-killed job failed before the UI gives up on it too.
      const MAX_POLL_DURATION_MS = 65 * 60 * 1000;
      const MAX_CONSECUTIVE_POLL_FAILURES = 5;
      let consecutivePollFailures = 0;
      let pollInFlight = false;
      const stopPolling = () => {
        if (pdfParsePollRef.current) {
          clearInterval(pdfParsePollRef.current);
          pdfParsePollRef.current = null;
        }
        setParsingLegacyCode(false);
      };
      pdfParsePollRef.current = setInterval(async () => {
        // A slow status call can still be in flight when the next tick
        // fires; without this guard, two overlapping calls can both see
        // status "completed" and both run _finishLegacyParse.
        if (pollInFlight) return;
        if (Date.now() - pollStartedAt > MAX_POLL_DURATION_MS) {
          stopPolling();
          setErr(
            `PDF source parse timed out waiting for job ${job_id} — it may still finish in the background; check again shortly before re-uploading`,
          );
          return;
        }
        pollInFlight = true;
        try {
          const status = await apiv2.converterParseLegacyPdfStatus(job_id);
          consecutivePollFailures = 0;
          if (status.status === "running") return;
          stopPolling();
          if (status.status === "completed" && status.result) {
            _finishLegacyParse(status.result, sourceLabel);
            setLegacyPdfUploads([]);
          } else {
            setErr(status.errors[0] ?? "PDF source parse failed");
          }
        } catch (e) {
          consecutivePollFailures += 1;
          if (consecutivePollFailures < MAX_CONSECUTIVE_POLL_FAILURES) {
            // Transient failure (e.g. a single dropped request) — keep
            // polling rather than aborting the whole job on the first miss.
            return;
          }
          stopPolling();
          setErr(e instanceof Error ? e.message : "PDF source parse failed");
        } finally {
          pollInFlight = false;
        }
      }, 1500);
    } catch (e) {
      setParsingLegacyCode(false);
      setErr(e instanceof Error ? e.message : "PDF source parse failed");
    }
  };

  // Read-only sanity-check preview of what pdfplumber extracted from the
  // currently-staged legacyPdfUploads (columns + a capped row sample per
  // detected table) -- lets the user verify extraction looks right before
  // committing to "Parse & Continue", which snapshots the schema. Never
  // persists or registers anything.
  const handlePreviewLegacyPdf = async () => {
    if (legacyPdfUploads.length === 0) {
      setPdfPreviewError("Choose one or more PDF files first");
      return;
    }
    setPdfPreviewLoading(true);
    setPdfPreviewError(null);
    try {
      const res = await apiv2.converterPreviewPdfTables(
        legacyPdfUploads.map((u) => u.file_path),
      );
      setPdfPreviewResult(res);
    } catch (e) {
      setPdfPreviewError(
        e instanceof Error ? e.message : "Could not preview parsed PDF data",
      );
    } finally {
      setPdfPreviewLoading(false);
    }
  };

  const handleSfConnect = async () => {
    setConnectingTargetPlatform(true);
    setError(null);
    try {
      const res = await apiv2.converterSalesforceConnect(sfConn, projectId);
      setSfOrgInfo({ org_name: res.org_name, object_count: res.object_count });
      // Connect itself has succeeded — stop the "Test Connection" button's
      // own spinner here rather than after the (much slower) object catalogue
      // fetch below, so it doesn't keep spinning right alongside the success
      // message. The catalogue fetch gets its own separate loadingTargetObjects
      // indicator.
      setConnectingTargetPlatform(false);
      // Fetch the full live object catalogue for the picker below — same
      // call the old flow made later with a name filter, just without one.
      setLoadingTargetObjects(true);
      try {
        const objRes = await apiv2.converterSalesforceObjects(
          sfConn,
          undefined,
          true,
          projectId,
        );
        setAvailableTargetObjects(objRes.objects);
      } finally {
        setLoadingTargetObjects(false);
      }
    } catch (e) {
      setErr(
        e instanceof Error ? e.message : "Target Platform connection failed",
      );
      setConnectingTargetPlatform(false);
    }
  };

  // Field-level target_type defaults to "string" on generation regardless of
  // the Salesforce field's native type — safest default, still editable per
  // row. Since this can flatten a genuine "textarea" field (large length
  // legitimately uncapped) down to "string" (255-char Salesforce Text cap),
  // the length must be re-clamped here too, not just at generation time.
  const withDefaultStringTargetTypes = (
    ms: TableObjectMapping[],
  ): TableObjectMapping[] =>
    ms.map((m) => ({
      ...m,
      field_mappings: m.field_mappings.map((fm) => ({
        ...fm,
        target_type: "string",
        target_length: clampTargetLength("string", fm.target_length),
      })),
    }));

  // Target Platform's RDBMS mode — scans the already-connected
  // targetConnectionId's schema and adapts it into availableTargetObjects +
  // a synthetic sfOrgInfo, so the rest of the pipeline (Continue to Mapping,
  // sfObjects, mappingPairs, Generate Mappings) runs unchanged.
  const handleScanTargetRdbms = async () => {
    if (!targetConnectionId) {
      setErr("Connect a target database first");
      return;
    }
    setScanningTargetRdbms(true);
    setError(null);
    try {
      const res = await apiv2.converterTargetScan(
        {
          connectionId: targetConnectionId,
          projectId,
        },
        ddlTargetSchema || undefined,
      );
      const objects = tablesToTargetObjects(res.tables);
      setAvailableTargetObjects(objects);
      setSfOrgInfo({
        org_name: targetConnLabel || "RDBMS target database",
        object_count: objects.length,
      });
      setTargetSchemaChanges(res.schema_changes ?? null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Target schema scan failed");
    } finally {
      setScanningTargetRdbms(false);
    }
  };

  // "Import Target Model" — RDBMS mode's alternative to Scan Target
  // Schema, for a target database that doesn't have the desired tables
  // yet. Parses an uploaded DDL script/schema export into a
  // SchemaExtractResult (same parser "Generate from Legacy Code" uses for
  // sources) without touching availableTargetObjects/sfOrgInfo yet — those
  // only get populated once the model is actually created below, so the
  // UI can't let mapping proceed against tables that don't exist yet.
  const handleParseImportedTargetModel = async () => {
    if (!importedTargetModelFile) {
      setErr("Choose a target model file first");
      return;
    }
    setParsingImportedTargetModel(true);
    setError(null);
    try {
      const res = await apiv2.converterParseLegacyTarget(
        importedTargetModelFile,
        projectId,
      );
      setImportedTargetSchema(res);
      setImportedTargetModelCreateResult(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Target model parse failed");
    } finally {
      setParsingImportedTargetModel(false);
    }
  };

  // Creates the imported model's tables in the connected target database,
  // using each column's own declared type (buildImportedTargetMapping) —
  // reuses the same generate -> reconcile -> execute pipeline the "Create
  // Tables" step runs later, so re-running this (or that step, afterward)
  // is safe/idempotent instead of unconditionally dropping and recreating.
  // Once created, availableTargetObjects/sfOrgInfo are populated exactly
  // like a live Scan Target Schema would, so "Source Tables to Auto-Map"
  // and Continue to Mapping below work unchanged.
  const handleCreateImportedTargetModel = async () => {
    if (!targetConnectionId) {
      setErr("Connect a target database first");
      return;
    }
    if (!importedTargetSchema || importedTargetSchema.tables.length === 0) {
      setErr("Import a target model file first");
      return;
    }
    setCreatingImportedTargetModel(true);
    setError(null);
    try {
      const importedMappings = importedTargetSchema.tables.map(
        buildImportedTargetMapping,
      );
      const schema = ddlTargetSchema || targetSchemaName || "";
      // Independent calls (reconcile only needs the live target DB +
      // importedMappings, not generate's response) -- run concurrently
      // rather than one round trip after the other.
      const [gen, reconcile] = await Promise.all([
        apiv2.converterGenerateDdl(
          importedMappings,
          schema,
          undefined,
          undefined,
          projectId,
          targetConnLabel || "Imported target model",
          targetDbType,
        ),
        apiv2.converterReconcileDdl(
          importedMappings,
          { connectionId: targetConnectionId, projectId },
          schema,
          targetDbType,
          projectId,
        ),
      ]);
      const toExecute = filterDdlStatementsToExecute(
        gen.ddl_statements,
        reconcile.results,
      );
      if (toExecute.length > 0) {
        await apiv2.converterExecuteDdl(
          { connectionId: targetConnectionId, projectId },
          toExecute.map((s) => s.ddl),
          true,
          projectId,
        );
      }
      setImportedTargetModelCreateResult({
        created: toExecute.length,
        upToDate: gen.ddl_statements.length - toExecute.length,
      });
      const objects = tablesToTargetObjects(importedTargetSchema.tables);
      setAvailableTargetObjects(objects);
      setSfOrgInfo({
        org_name: targetConnLabel || "Imported Target Model",
        object_count: objects.length,
      });
    } catch (e) {
      setErr(
        e instanceof Error ? e.message : "Creating the target model failed",
      );
    } finally {
      setCreatingImportedTargetModel(false);
    }
  };

  // Target Platform's "mirror" mode — for when there's no real target model
  // to connect to at all. Auto-generates one that's a structural 1:1 mirror
  // of the source schema (every table/column carried through unchanged, via
  // the same buildIdentityMapping/tablesToTargetObjects helpers the "Use
  // source as-is" per-table toggle already uses) and jumps straight to
  // Review & Export — there's no target object to match/pick per table
  // since the target IS the source, so the "STTM-Mapping" pairing step
  // would have nothing left to do.
  const handleGenerateMirrorTargetModel = () => {
    if (!schemaResult || schemaResult.tables.length === 0) {
      setErr("Scan or upload a source schema first");
      return;
    }
    const tablesToMirror = schemaResult.tables.filter((t) =>
      selectedSourceTablesForMapping.has(t.table_name),
    );
    if (tablesToMirror.length === 0) {
      setErr("Select at least one source table to mirror");
      return;
    }
    setGeneratingMirrorTargetModel(true);
    setError(null);
    try {
      const identityMappings = tablesToMirror.map((t) =>
        buildIdentityMapping(t),
      );
      const mirrorObjects = tablesToTargetObjects(tablesToMirror);
      setAvailableTargetObjects(mirrorObjects);
      setSfObjects(mirrorObjects);
      setSfOrgInfo({
        org_name: "Auto-Generated Target Model (mirrors source 1:1)",
        object_count: mirrorObjects.length,
      });
      setMappingPairs(
        identityMappings.map((m) => ({
          sourceTable: m.source_table,
          targetObject: m.target_object,
          useSourceAsIsTarget: true,
        })),
      );
      setMappings(identityMappings);
      setEditedMappings(JSON.parse(JSON.stringify(identityMappings)));
      setRemovedTableMappings(new Set());
      setRemovedFieldMappings({});
      setKeepFieldsSelected({});
      setSttmActiveTab(0);
      setStep("sttm");
      // Every other path into "sttm" (Generate STTM, Import Existing STTM)
      // records an initial snapshot so Version History has something to
      // show/restore — this mirror-model shortcut skipped straight to the
      // step without one, leaving the "N version(s)" button permanently
      // hidden until the next Apply & Regenerate.
      saveSttmSnapshot("Auto-generated mirror model", identityMappings, null);
    } finally {
      setGeneratingMirrorTargetModel(false);
    }
  };

  const handleLoadSfObjects = async () => {
    if (!schemaResult) return;
    setContinuingToMapping(true);
    setError(null);
    try {
      // The full metadata (incl. fields) for every object was already
      // fetched into availableTargetObjects by handleSfConnect — no need
      // to re-fetch it here.
      const objects = availableTargetObjects;
      setSfObjects(objects);

      // Auto-generate mappings for every table selected in step 1 here —
      // selecting more than one table signals intent to map them all, so
      // only a lone single-table selection is scoped down to build trust
      // in one mapping before mapping more via "Map All Remaining Tables"
      // (handleAutoMapAll). See scopeTablesForInitialAutoMap.
      const scopedTables = scopeTablesForInitialAutoMap(
        schemaResult.tables,
        selectedSourceTablesForMapping,
      );
      if (scopedTables.length > 0 && objects.length > 0) {
        // Auto-suggest a best-match target object for each selected source
        // table (heuristic + LLM refinement, no pins). projectId is passed
        // so this first, trust-building suggestion is biased by whatever
        // domain skill / knowledge-base doc was already set on the
        // Target Platform step above, the same as every later regenerate.
        const suggestRes = await apiv2.converterGenerateMapping(
          { ...schemaResult, tables: scopedTables },
          objects,
          true,
          undefined,
          projectId,
        );
        setMappingPairs(
          suggestRes.mappings.map((m) => ({
            sourceTable: m.source_table,
            targetObject: m.target_object,
          })),
        );
        const suggested = withSourceConnectionIds(
          withDefaultStringTargetTypes(suggestRes.mappings),
        );
        setMappings(suggested);
        setEditedMappings(JSON.parse(JSON.stringify(suggested)));
        setSttmActiveTab(0);
      } else {
        setMappingPairs([]);
      }
      setStep("mapping");
    } catch (e) {
      setErr(
        e instanceof Error
          ? e.message
          : "Failed to load Target Platform objects",
      );
    } finally {
      setContinuingToMapping(false);
    }
  };

  // Load the project's saved target platform connection (crm-connection —
  // GET/PUT /api/v2/data-converter/project/{id}/crm-connection), same
  // pattern as the Source DB / staged DB connections above. Secrets never
  // round-trip in plaintext — has_password/has_client_secret drive an
  // "already saved" hint instead. A named callback (not just a mount
  // effect) so it can also re-run after activating a saved-connections
  // library entry (SavedConnectionsPanel's onActivated).
  const refreshSavedCrmConn = useCallback(async () => {
    if (!projectId) return;
    try {
      const cfg = await apiv2.converterGetCrmConnection(projectId);
      if (!cfg) return;
      setSavedCrmConn(cfg);
      setSfConn((prev) => ({
        ...prev,
        target_type: cfg.target_type,
        instance_url: cfg.instance_url,
        username: cfg.username,
        password: "",
        // 2026-09-04, PR review: the API no longer returns the stored
        // security_token (only cfg.has_security_token), so blank it the
        // same way password is blanked one line up — blank means
        // "unchanged" on the next save.
        security_token: "",
        consumer_key: cfg.consumer_key || null,
        consumer_secret: null,
        tenant_id: cfg.tenant_id || null,
        client_id: cfg.client_id || null,
        client_secret: null,
        resource_url: cfg.resource_url || null,
        // Workday's OAuth2 refresh_token grant credential — same
        // blank-means-unchanged convention as the other secrets above.
        refresh_token: null,
      }));
    } catch {
      // No saved connection yet (or not permitted to read it) — the form
      // just stays at its default/local-fallback values.
    }
  }, [projectId]);

  useEffect(() => {
    refreshSavedCrmConn();
  }, [refreshSavedCrmConn]);

  // 2026-09-02, requested by user: same reasoning as
  // handleTargetConnectionActivatedFromLibrary above — the always-visible
  // Saved Connections library lets a user switch to a DIFFERENT saved CRM
  // connection even after one is already tested this session, so sfOrgInfo
  // (and the object catalogue it gated) must be cleared rather than keep
  // showing "connection successful" for the connection that's no longer
  // active.
  const handleCrmConnectionActivatedFromLibrary = useCallback(() => {
    setSfOrgInfo(null);
    setAvailableTargetObjects([]);
    void refreshSavedCrmConn();
  }, [refreshSavedCrmConn]);

  // Load a saved-connections-library "crm"-kind entry into the Target
  // Platform form (preview/edit — does NOT make it the project's active CRM
  // connection; that's the SavedConnectionsPanel's "Use This" button, which
  // calls the activate endpoint and then refreshSavedCrmConn above to
  // re-sync from the single active slot). The source/target DB kinds no
  // longer have an equivalent form-fill handler — see
  // refreshSourceConnectionFromSaved/refreshTargetConnectionFromSaved.
  const handleLoadSavedCrmConn = (c: SavedConnectionOut) => {
    setSfConn((prev) => ({
      ...prev,
      target_type: (c.target_type ||
        prev.target_type) as SalesforceConnectionConfig["target_type"],
      instance_url: c.instance_url,
      username: c.username,
      password: "",
      // 2026-09-04, PR review: not returned by the API any more (only
      // c.has_security_token) — blank, same convention as password above.
      security_token: "",
      consumer_key: c.consumer_key || null,
      consumer_secret: null,
      tenant_id: c.tenant_id || null,
      client_id: c.client_id || null,
      client_secret: null,
      resource_url: c.resource_url || null,
      // Same blank-means-unchanged convention as the other secrets above.
      refresh_token: null,
    }));
    setSavedCrmConn({
      project_id: projectId ?? "",
      target_type: c.target_type as CrmConnectionOut["target_type"],
      instance_url: c.instance_url,
      username: c.username,
      consumer_key: c.consumer_key,
      tenant_id: c.tenant_id,
      client_id: c.client_id,
      resource_url: c.resource_url,
      has_password: c.has_password,
      has_client_secret: c.has_client_secret,
      has_security_token: c.has_security_token,
      has_refresh_token: c.has_refresh_token,
      updated_at: c.updated_at,
    });
    setSfOrgInfo(null);
  };

  // Pre-populates EVERY source table with its best-guess target object in
  // one shot — no pairs sent, so the server's heuristic/LLM matcher (biased
  // by the project's selected Functional Skills' table hints, and enriched
  // by whichever Integration Skills are enabled) picks a target for each
  // table exactly the way it would for a single explicitly-picked pair.
  // The result seeds mappingPairs so the existing per-row picker becomes
  // the "review and change my selection" UI, and Generate Mappings
  // (below) re-runs against the (possibly edited) full set as explicit pins.
  const handleAutoMapAll = async () => {
    if (!schemaResult || schemaResult.tables.length === 0) {
      setErr("Scan a source schema first");
      return;
    }
    if (sfObjects.length === 0) {
      setErr("Connect a target platform and load its objects first");
      return;
    }
    // Map only the tables NOT already covered by an existing mapping —
    // handleLoadSfObjects now seeds just the first table on entry, and
    // this button completes the rest of the schema rather than
    // regenerating (and discarding edits to) everything from scratch.
    const alreadyMappedTables = new Set(
      mappings.flatMap((m) =>
        m.source_tables && m.source_tables.length > 0
          ? m.source_tables
          : [m.source_table],
      ),
    );
    const remainingTables = schemaResult.tables.filter(
      (t) => !alreadyMappedTables.has(t.table_name),
    );
    if (remainingTables.length === 0) {
      setErr("Every source table already has a mapping.");
      return;
    }
    setAutoMappingAll(true);
    setError(null);
    try {
      const res = await apiv2.converterGenerateMapping(
        { ...schemaResult, tables: remainingTables },
        sfObjects,
        true,
        undefined,
        projectId,
      );
      const merged = [...mappings, ...withSourceConnectionIds(res.mappings)];
      setMappings(merged);
      setEditedMappings(
        withDefaultStringTypes(JSON.parse(JSON.stringify(merged))),
      );
      setMappingPairs(
        merged.map((m) => ({
          sourceTable: m.source_table,
          targetObject: m.target_object,
        })),
      );
      // Appending, not replacing — existing entries keep their array
      // index, so removedTableMappings/removedFieldMappings/
      // keepFieldsSelected (all keyed by index) stay valid and don't need
      // resetting the way a full overwrite would require.
      setSttmActiveTab(0);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Auto-mapping failed");
    } finally {
      setAutoMappingAll(false);
    }
  };

  const handleGenerateMappings = async () => {
    if (!schemaResult) return;
    if (mappingPairs.length === 0) {
      setErr("Add at least one source → target mapping pair.");
      return;
    }
    // "Use source as-is" pairs skip target-object matching entirely (see
    // buildIdentityMapping) — split them out before scoping the API call to
    // the remaining, genuinely target-matched pairs.
    const asIsPairs = mappingPairs.filter((p) => p.useSourceAsIsTarget);
    const normalPairs = mappingPairs.filter((p) => !p.useSourceAsIsTarget);
    const asIsMappings: TableObjectMapping[] = asIsPairs
      .map((p) => {
        const table = schemaResult.tables.find(
          (t) => t.table_name === p.sourceTable,
        );
        // p.targetObject carries a user rename (see updateMappingPair's
        // editable "Same as source" field) when it differs from the
        // source table's own name — falls through to that name otherwise.
        return table ? buildIdentityMapping(table, p.targetObject) : null;
      })
      .filter((m): m is TableObjectMapping => !!m);

    // Table-level joins added via "+ Join Table" in STTM Review only ever
    // land in editedMappings (handleAddTableToJoin never touches
    // mappingPairs) — regenerating from pins alone would otherwise flatten
    // every one of those back to a plain single-table row. Preserve any
    // join whose anchor (primary) table is still paired to the same target.
    const priorJoins = editedMappings.filter(
      (m, i) =>
        !removedTableMappings.has(i) && (m.source_tables?.length ?? 0) > 1,
    );

    if (normalPairs.length === 0) {
      if (asIsMappings.length === 0) {
        setErr("Select a source table for each pair.");
        return;
      }
      setMappings(asIsMappings);
      setEditedMappings(JSON.parse(JSON.stringify(asIsMappings)));
      setRemovedTableMappings(new Set());
      setRemovedFieldMappings({});
      setKeepFieldsSelected({});
      setSttmActiveTab(0);
      return;
    }
    // Build scoped schema containing only the source tables referenced in the pairs.
    const selectedTableNames = [
      ...new Set(normalPairs.map((p) => p.sourceTable)),
    ];
    const selectedObjectNames = [
      ...new Set(normalPairs.map((p) => p.targetObject)),
    ];
    const scopedTables = schemaResult.tables.filter((t) =>
      selectedTableNames.includes(t.table_name),
    );
    const scopedSfObjects = sfObjects.filter((o) =>
      selectedObjectNames.includes(o.api_name),
    );
    if (scopedTables.length === 0 || scopedSfObjects.length === 0) {
      setErr("Select a source table and a target object for each pair.");
      return;
    }
    setGeneratingMappings(true);
    setError(null);
    try {
      const scopedSchema: SchemaExtractResult = {
        ...schemaResult,
        tables: scopedTables,
      };
      const res = await apiv2.converterGenerateMapping(
        scopedSchema,
        scopedSfObjects,
        true,
        normalPairs.map((p) => ({
          source_table: p.sourceTable,
          target_object: p.targetObject,
        })),
        projectId,
      );
      const preservedJoins = priorJoins.filter((pj) =>
        normalPairs.some(
          (p) =>
            p.sourceTable === pj.source_table &&
            p.targetObject === pj.target_object,
        ),
      );
      const preservedAnchors = new Set(
        preservedJoins.map((m) => m.source_table),
      );
      const generated = [
        ...withSourceConnectionIds(
          withDefaultStringTargetTypes(res.mappings),
        ).filter((m) => !preservedAnchors.has(m.source_table)),
        ...preservedJoins,
        ...asIsMappings,
      ];
      setMappings(generated);
      setEditedMappings(JSON.parse(JSON.stringify(generated))); // deep copy for editing
      // Brand-new mappings array — any table/field removals keyed by index
      // into the *previous* array no longer refer to the same tables.
      setRemovedTableMappings(new Set());
      setRemovedFieldMappings({});
      setKeepFieldsSelected({});
      setSttmActiveTab(0);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Mapping generation failed");
    } finally {
      setGeneratingMappings(false);
    }
  };

  // Upload a user-provided data-mapping document — at the user's discretion,
  // either "replace" (adopt it as the mapping outright) or "seed" (treat it
  // as an authoritative starting point and let the normal heuristic/LLM
  // pass fill any gaps it didn't cover).
  const handleImportMappingDocument = async (file: File) => {
    setImportingMappingDoc(true);
    setError(null);
    setMappingImportWarnings([]);
    try {
      const res = await apiv2.converterImportMappingDocument(
        file,
        mappingImportMode,
        {
          projectId,
          sourceSchema: schemaResult ?? undefined,
          sfObjects,
          llmEnhance: true,
        },
      );
      setMappings(res.mappings);
      setEditedMappings(
        withDefaultStringTypes(JSON.parse(JSON.stringify(res.mappings))),
      );
      setRemovedTableMappings(new Set());
      setRemovedFieldMappings({});
      setKeepFieldsSelected({});
      setSttmActiveTab(0);
      setMappingImportWarnings(res.warnings);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Mapping document import failed");
    } finally {
      setImportingMappingDoc(false);
    }
  };

  const saveSttmSnapshot = (
    label: string,
    currentMappings: TableObjectMapping[],
    currentResult: SttmGenerateResponse | null,
  ) => {
    if (currentMappings.length === 0) return;
    setSttmHistory((prev) => {
      const version = prev.length + 1;
      const now = new Date();
      const timestamp = `${now.toLocaleDateString()} ${now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
      return [
        {
          version,
          label,
          timestamp,
          mappings: JSON.parse(JSON.stringify(currentMappings)),
          sttmResult: currentResult,
        },
        ...prev,
      ];
    });
  };

  const handleGenerateSttm = async (useMappings?: TableObjectMapping[]) => {
    setGeneratingSttm(true);
    setError(null);
    // Captured before any await so it reflects the state at call time
    // (handleApplyAndRegenerate clears sttmResult only via a later re-render).
    const isRegenerate = sttmResult != null;
    try {
      const activeMappings = useMappings ?? mappings;
      const res = await apiv2.converterGenerateSttm(
        activeMappings,
        schemaResult,
        sfObjects,
        projectName,
        projectId,
        connectionLabelsForSttm,
      );
      setSttmResult(res);
      setSttmActiveTab(0);
      setStep("sttm");
      // Snapshot the freshly generated version so every entry — including the
      // initial one — has a downloadable workbook attached.
      saveSttmSnapshot(
        isRegenerate ? "Regenerated" : "Initial generation",
        activeMappings,
        res,
      );
      // Persist this mapping (server-side, per project) so the Data
      // Loader / Reconciliation / Load to Target actions can pick it up in
      // a fresh session without re-running Source → Target Mapping.
      // Best-effort — a persistence failure shouldn't block STTM review.
      if (projectId) {
        apiv2
          .converterSetProjectMapping(projectId, activeMappings)
          .catch(() => {
            // Non-fatal — the mapping still works in-memory for this
            // session; the user can retry by regenerating/applying again.
          });
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : "STTM generation failed");
    } finally {
      setGeneratingSttm(false);
    }
  };

  const handleApplyAndRegenerate = async () => {
    // Tables removed via the STTM Review tab's "x" are only soft-hidden
    // (removedTableMappings), never spliced out of editedMappings itself —
    // exclude them here or they'd silently reappear in mappings (and
    // therefore Load Data, which reads mappings, not editedMappings).
    const activeMappings = editedMappings.filter(
      (_, i) => !removedTableMappings.has(i),
    );
    setMappings(activeMappings);
    setSttmResult(null);
    await handleGenerateSttm(activeMappings);
  };

  // Generate and download a standalone STTM workbook containing only the
  // one table currently being viewed, using its live-edited field mappings
  // — does not touch sttmResult/mappings/version history, which stay scoped
  // to the whole-workbook "Apply & Regenerate STTM" flow.
  const handleDownloadTableSttm = async (idx: number) => {
    const tableMapping = editedMappings[idx];
    if (!tableMapping) return;
    setDownloadingTableIdx(idx);
    setError(null);
    try {
      const res = await apiv2.converterGenerateSttm(
        [tableMapping],
        schemaResult,
        sfObjects,
        projectName,
        undefined,
        connectionLabelsForSttm,
      );
      const link = document.createElement("a");
      link.href = res.download_url;
      link.download = res.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Downloading table STTM failed");
    } finally {
      setDownloadingTableIdx(null);
    }
  };

  // STTM review's final action: explicitly (re-)saves the finalized mapping
  // (mapping generation already best-effort auto-saves on every
  // generate/regenerate — this makes the save an explicit, confirmed step)
  // and, when the project's "Data Dictionary" integration skill is enabled,
  // also generates and saves a Data Dictionary workbook alongside it.
  const [finalizing, setFinalizing] = useState(false);
  const [finalizedAt, setFinalizedAt] = useState<Date | null>(null);
  const [dataDictionaryResult, setDataDictionaryResult] =
    useState<DataDictionaryGenerateResponse | null>(null);
  // 2026-09-02, requested by user: the .xlsx mapping workbook Finalize now
  // also generates and persists — see converterGenerateProjectMappingFile.
  // Loaded on entry to every project-scoped step below (Load Data, Create
  // Tables, Load to Target, Reconciliation) so a resumed session can
  // download/replace it without having to revisit STTM Review first.
  const [projectMappingFile, setProjectMappingFile] =
    useState<ProjectMappingFileOut | null>(null);
  const [uploadingMappingFile, setUploadingMappingFile] = useState(false);

  const handleFinalizeMapping = async () => {
    if (!projectId) {
      setErr("A project is required to finalize and save");
      return;
    }
    setFinalizing(true);
    setError(null);
    try {
      await apiv2.converterSetProjectMapping(projectId, mappings);
      setFinalizedAt(new Date());
      // Data Dictionary generation is now always bundled with Finalize
      // (previously gated behind the removed "Data Dictionary" integration
      // skill checkbox).
      const dict = await apiv2.converterGenerateDataDictionary(
        mappings,
        schemaResult,
        projectName,
        projectId,
      );
      setDataDictionaryResult(dict);
      // 2026-09-02, requested by user: also generate + persist the .xlsx
      // mapping workbook itself (distinct from the Data Dictionary above)
      // so later steps in this project can download/replace it.
      const mappingFile = await apiv2.converterGenerateProjectMappingFile(
        projectId,
        mappings,
        schemaResult,
        sfObjects,
        projectName,
        connectionLabelsForSttm,
      );
      setProjectMappingFile(mappingFile);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Finalize failed");
    } finally {
      setFinalizing(false);
    }
  };

  // 2026-09-11, requested by user: "Create New Mapping" / "Generate from
  // Legacy Code" / "Import Your Own Mapping" all claim to start a fresh
  // Source → Target Mapping, but none of them actually cleared the PREVIOUS
  // flow's source/target connections, generated field mappings, or the
  // Finalize step's "Saved HH:MM" badge + Data Dictionary — so starting a
  // second mapping for a genuinely different source/target pair kept
  // showing the first one's leftovers (stale STTM table rows, unmapped-
  // table count, finalize timestamp) until the user manually reconnected
  // and regenerated everything. This is the single reset point all three
  // entry points now call before jumping into the fresh flow.
  const resetForFreshMappingFlow = () => {
    setSourceConnectionId(null);
    setSourceConnLabel("");
    setSourceSchemaName("");
    setConnStatus(null);
    setSchemaResult(null);
    setSelectedSourceTablesForMapping(new Set());
    setSourceTableMappingFilter("");
    setLegacyFileRefs({});
    setLegacySourceInfo(null);

    setTargetPlatformMode("crm");
    setTargetConnectionId(null);
    setTargetConnLabel("");
    setTargetDbType(null);
    setTargetSchemaName("");
    setTargetSchemas([]);
    setTargetSchemaManualEntry(false);
    setSfOrgInfo(null);
    setAvailableTargetObjects([]);
    setSfObjects([]);
    setDdlTargetSchema("");
    setDdlTargetConnected(null);
    setCrmLoadStrategy(null);
    setTargetModelChoice("crm");
    setNoTargetSubOption("mirror");
    setImportedTargetModelFile(null);
    setImportedTargetSchema(null);
    setImportedTargetModelCreateResult(null);

    setMappings([]);
    setEditedMappings([]);
    setMappingPairs([]);
    setRemovedTableMappings(new Set());
    setRemovedFieldMappings({});
    setKeepFieldsSelected({});
    setSttmResult(null);
    setSttmActiveTab(0);
    setUnmappedReviewTablesOpen(false);
    // STTM review banners (rule-import results, STTM import info/history)
    // belong to the mapping session they were produced in — carrying them
    // into a new mapping flow makes a stale import look like it just ran.
    setSttmHistory([]);
    setSttmHistoryOpen(false);
    setSttmImportInfo(null);
    setImportRulesResult(null);
    setDqRuleDecisions({});
    setDqRuleRemoved(new Set());
    setDqDecisionsApplied(null);
    setDqDecisionsNotFound([]);
    setLegacyRulesError(null);
    setLegacyRulesBatch(null);
    setLegacyRulesPanelOpen(false);
    setLegacyRulesHistory([]);
    setLegacyRulesHistoryOpen(false);
    setMappingImportWarnings([]);

    setFinalizedAt(null);
    setDataDictionaryResult(null);
    setProjectMappingFile(null);

    setDdlStatements([]);
    setDdlDuplicates([]);
    setDdlDuplicateDecisions({});

    // The previous flow's Load Results must not linger and look like
    // results for this genuinely new mapping.
    resetLoadResultsState();

    // A custom naming override set in one mapping session must not carry
    // into the next brand-new one (would silently rename tables the user
    // never asked to rename this time).
    setTableNamingMode("auto");
    setTableNamingValue("");
    setTableNamingScope("all");
    setIncludeAuditColumns(true);
  };

  // Loads the project's persisted mapping-file reference whenever a
  // non-mapping action becomes active — same trigger the scopedMappingStatus
  // effect above uses, since both are "does this project already have a
  // finalized mapping artifact" checks.
  useEffect(() => {
    if (!projectId || !action || action === "mapping") return;
    let cancelled = false;
    apiv2
      .converterGetProjectMappingFile(projectId)
      .then((ref) => {
        if (!cancelled) setProjectMappingFile(ref);
      })
      .catch(() => {
        // No file yet (or not permitted to read it) — leave null, the
        // download/replace card below just doesn't render.
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, action]);

  // Re-upload/replace the project's mapping from a previously downloaded
  // (or externally edited) .xlsx — reuses the same import parser "Import
  // Existing STTM" uses, then re-persists both the JSON mapping and a
  // fresh workbook reference so the two never drift apart.
  const handleReplaceProjectMappingFile = async (file: File) => {
    if (!projectId) return;
    setUploadingMappingFile(true);
    setError(null);
    try {
      const imported = await apiv2.converterImportSttm(file, schemaResult);
      setMappings(imported.mappings);
      setEditedMappings(
        withDefaultStringTypes(JSON.parse(JSON.stringify(imported.mappings))),
      );
      await apiv2.converterSetProjectMapping(projectId, imported.mappings);
      const mappingFile = await apiv2.converterGenerateProjectMappingFile(
        projectId,
        imported.mappings,
        schemaResult,
        sfObjects,
        projectName,
        connectionLabelsForSttm,
      );
      setProjectMappingFile(mappingFile);
      // 2026-09-02, requested by user: uploading a mapping directly from
      // the "No mapping found yet" empty state (action !== "mapping") must
      // unblock that gate immediately, not just update the mapping data —
      // otherwise the loader/reconciliation/load-to-target steps below
      // stay hidden until the next full page load re-runs the fetch effect.
      if (action !== "mapping") setScopedMappingStatus("ready");
      // Satisfies Reconciliation's own "which mapping?" confirm screen too
      // (no-op for every other action).
      setReconciliationMappingConfirmed(true);
      // A project that reached "No mapping found yet" while already
      // resumed onto the loader's "ddl" step (e.g. a stale DDL-state
      // resume from before the mapping was cleared/reassigned) had
      // crmLoadStrategy locked to "staged" from that resume, skipping
      // straight to Create Staging Tables instead of re-asking staged-vs-
      // direct now that a mapping actually exists. Force that question
      // to show again for a freshly-attached mapping.
      if (action === "loader") {
        setCrmLoadStrategy(null);
        setStep(ACTION_STEPS.loader[0]);
      }
    } catch (e) {
      setErr(
        e instanceof Error ? e.message : "Failed to replace project mapping",
      );
    } finally {
      setUploadingMappingFile(false);
    }
  };

  // 2026-09-02, requested by user: "No mapping found yet" (any non-mapping
  // action with scopedMappingStatus === "missing") needs its own way to
  // pick a previously saved mapping — not just "go generate one from
  // scratch". Distinct from handleResumeMappingEntry (the landing-page
  // resume modal's mapping picker), which switches `action` to "mapping"
  // and lands on that action's own "mapping" step — this one stays on
  // whichever non-mapping action/step the user is already trying to use.
  const [showMappingHistoryPicker, setShowMappingHistoryPicker] =
    useState(false);
  const [missingMappingHistory, setMissingMappingHistory] = useState<
    ConverterMappingHistoryEntryOut[] | null
  >(null);
  const [loadingMappingHistory, setLoadingMappingHistory] = useState(false);
  const [selectingMappingHistoryId, setSelectingMappingHistoryId] = useState<
    string | null
  >(null);

  const handleOpenMappingHistoryPicker = async () => {
    if (!projectId) return;
    setShowMappingHistoryPicker(true);
    setLoadingMappingHistory(true);
    setError(null);
    try {
      const entries = await apiv2.converterListMappingHistory(projectId);
      setMissingMappingHistory(entries);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load mapping history");
      setShowMappingHistoryPicker(false);
    } finally {
      setLoadingMappingHistory(false);
    }
  };

  const handleSelectMissingMappingHistoryEntry = async (historyId: string) => {
    if (!projectId) return;
    setSelectingMappingHistoryId(historyId);
    setError(null);
    try {
      const entry = await apiv2.converterGetMappingHistoryEntry(
        projectId,
        historyId,
      );
      setMappings(entry.mappings);
      setEditedMappings(
        withDefaultStringTypes(JSON.parse(JSON.stringify(entry.mappings))),
      );
      // Re-persist as the project's CURRENT mapping (not just this
      // session's in-memory state) — a history entry is, by definition,
      // an earlier save, so picking one here re-promotes it to "current".
      await apiv2.converterSetProjectMapping(projectId, entry.mappings);
      setScopedMappingStatus("ready");
      // Satisfies Reconciliation's own "which mapping?" confirm screen too
      // (no-op for every other action).
      setReconciliationMappingConfirmed(true);
      // Same reasoning as handleReplaceProjectMappingFile above — don't
      // silently keep a stale "staged" lock from before this mapping
      // existed; re-ask staged-vs-direct now that one is actually attached.
      if (action === "loader") {
        setCrmLoadStrategy(null);
        setStep(ACTION_STEPS.loader[0]);
      }
      setShowMappingHistoryPicker(false);
    } catch (e) {
      setErr(
        e instanceof Error
          ? e.message
          : "Failed to load that saved mapping — try again.",
      );
    } finally {
      setSelectingMappingHistoryId(null);
    }
  };

  // Import a previously generated (or externally authored/edited) STTM
  // workbook and use its mappings directly instead of regenerating from the
  // Source DB → GovConnect field mapping. Clears sttmResult since the
  // download token no longer matches the (now imported) mappings — the user
  // can click "Generate STTM" to produce a fresh, downloadable copy.
  const handleImportSttm = async (file: File) => {
    setImportingSttm(true);
    setError(null);
    try {
      const res = await apiv2.converterImportSttm(file, schemaResult);
      // Snapshot whatever was in the editor before this import overwrites it,
      // so the user can revert back to it later from Version History.
      if (editedMappings.length > 0) {
        saveSttmSnapshot(
          sttmImportInfo
            ? `Before import (was: ${sttmImportInfo.filename})`
            : "Before import (original generated STTM)",
          editedMappings,
          sttmResult,
        );
      }
      // Merge rather than replace: only tables present in the imported
      // workbook get overwritten (by source schema + source table); any
      // other tables already in the editor are left untouched, and tables
      // that only exist in the imported file are appended.
      const tableKey = (m: TableObjectMapping) =>
        `${m.source_schema}::${m.source_table}`;
      const importedByKey = new Map(res.mappings.map((m) => [tableKey(m), m]));
      const matchedKeys = new Set<string>();
      const merged: TableObjectMapping[] = editedMappings.map((m) => {
        const replacement = importedByKey.get(tableKey(m));
        if (replacement) {
          matchedKeys.add(tableKey(m));
          return replacement;
        }
        return m;
      });
      for (const m of res.mappings) {
        if (!matchedKeys.has(tableKey(m))) merged.push(m);
      }
      setMappings(merged);
      setEditedMappings(JSON.parse(JSON.stringify(merged)));
      setSttmActiveTab(0);
      setSttmResult(null);
      setSttmImportInfo({
        filename: res.filename,
        table_count: res.table_count,
        total_mapping_rows: res.total_mapping_rows,
      });
      saveSttmSnapshot(`Imported ${res.filename}`, merged, null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Importing STTM failed");
    } finally {
      setImportingSttm(false);
    }
  };

  const restoreSttmVersion = (idx: number) => {
    const snap = sttmHistory[idx];
    if (!snap) return;
    setEditedMappings(JSON.parse(JSON.stringify(snap.mappings)));
    setSttmResult(snap.sttmResult);
    setSttmActiveTab(0);
    setSttmHistoryOpen(false);
    // Keep the "Imported" banner in sync with the restored version — clear it
    // when reverting to a non-imported (e.g. originally generated) snapshot.
    const importedFilename = snap.label.startsWith("Imported ")
      ? snap.label.slice("Imported ".length)
      : null;
    setSttmImportInfo(
      importedFilename
        ? {
            filename: importedFilename,
            table_count: snap.mappings.length,
            total_mapping_rows: snap.mappings.reduce(
              (sum, m) => sum + m.field_mappings.length,
              0,
            ),
          }
        : null,
    );
  };

  // Import a Table/Column/Dimension/Description workbook and apply
  // Accuracy/Validity-dimension descriptions as transformation rules onto
  // the matching mapped fields (by source table + source column). The
  // resulting mappings replace editedMappings so they're visible here for
  // review before "Apply & Regenerate STTM".
  const handleImportTransformRules = async (file: File) => {
    if (editedMappings.length === 0) {
      setErr("Generate the STTM mapping first.");
      return;
    }
    setImportingRules(true);
    setError(null);
    try {
      const res = await apiv2.converterImportTransformRules(
        editedMappings,
        file,
      );
      setEditedMappings(res.mappings);
      setImportRulesResult(res);
      setDqDecisionsApplied(null);
      setDqDecisionsNotFound([]);
      setDqRuleRemoved(new Set());
      const decisions: Record<
        string,
        { action: "exclude" | "include"; logic: string }
      > = {};
      for (const a of res.applied) {
        decisions[`${a.table}::${a.column}`] = {
          action: "include",
          logic: a.transformation,
        };
      }
      setDqRuleDecisions(decisions);
    } catch (e) {
      setErr(
        e instanceof Error
          ? e.message
          : "Importing transformation rules failed",
      );
    } finally {
      setImportingRules(false);
    }
  };

  // Apply the reviewer's per-rule exclude/include decisions (see
  // dqRuleDecisions) onto editedMappings: "exclude" stores a boolean
  // condition checked per row at Load Data (violating rows are skipped and
  // reported), "include" replaces the field's Transform text with the
  // reviewer's real logic AND is scoped to only the rows actually violating
  // the rule (the rule's own original description, sent as `description`,
  // is what the backend uses to identify those rows) — both take effect
  // during the next Load Data run.
  const handleApplyDqDecisions = async () => {
    if (!importRulesResult || importRulesResult.applied.length === 0) return;
    const rulesToApply = importRulesResult.applied.filter(
      (a) => !dqRuleRemoved.has(`${a.table}::${a.column}`),
    );
    if (rulesToApply.length === 0) return;
    setApplyingDqDecisions(true);
    setError(null);
    try {
      const decisions = rulesToApply.map((a) => {
        const d = dqRuleDecisions[`${a.table}::${a.column}`] ?? {
          action: "include" as const,
          logic: a.transformation,
        };
        return {
          table: a.table,
          column: a.column,
          action: d.action,
          logic: d.logic,
          description: a.transformation,
        };
      });
      const res = await apiv2.converterApplyRuleDecisions(
        editedMappings,
        decisions,
      );
      setEditedMappings(res.mappings);
      setDqDecisionsApplied(res.decisions_applied);
      setDqDecisionsNotFound(res.not_found);
    } catch (e) {
      setErr(
        e instanceof Error ? e.message : "Applying DQ rule decisions failed",
      );
    } finally {
      setApplyingDqDecisions(false);
    }
  };

  // Import a legacy code/config artifact (SQL, PL/SQL, SSIS, Informatica,
  // BODS) or an Excel rules workbook. The server extracts candidate
  // transformation rules, auto-maps them against the current STTM, and
  // persists the result as a reviewable audit-trail batch — nothing is
  // applied to editedMappings until each item is reviewed and the batch is
  // explicitly applied.
  const handleImportLegacyRules = async (
    file: File,
    artifactType?: LegacyArtifactType,
  ) => {
    if (editedMappings.length === 0) {
      setErr("Generate the STTM mapping first.");
      return;
    }
    setImportingLegacyRules(true);
    setLegacyRulesError(null);
    try {
      const batch = await apiv2.converterImportLegacyRules(
        editedMappings,
        file,
        { projectId, artifactType },
      );
      setLegacyRulesBatch(batch);
      setLegacyRulesPanelOpen(true);
    } catch (e) {
      setLegacyRulesError(
        e instanceof Error ? e.message : "Importing legacy rules failed",
      );
    } finally {
      setImportingLegacyRules(false);
    }
  };

  const loadLegacyRulesHistory = async () => {
    setLegacyRulesHistoryOpen((v) => !v);
    try {
      const res = await apiv2.converterLegacyRuleBatches(projectId);
      setLegacyRulesHistory(res.batches);
    } catch (e) {
      setLegacyRulesError(
        e instanceof Error ? e.message : "Could not load import history",
      );
    }
  };

  const handleReviewLegacyItem = async (
    item: LegacyRuleItem,
    decision: "approved" | "rejected" | "edited" | "pending",
    finalTransformation?: string,
  ) => {
    try {
      const pick = legacyItemTargetPick[item.id];
      const [pickedTable, pickedField] = pick ? pick.split("::") : [];
      const updated = await apiv2.converterReviewLegacyRuleItem(
        item.id,
        decision,
        {
          finalTransformation,
          matchedTargetTable: pickedTable || undefined,
          matchedTargetField: pickedField || undefined,
        },
      );
      setLegacyRulesBatch((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items?.map((i) =>
                i.id === updated.id ? updated : i,
              ),
            }
          : prev,
      );
      setEditingLegacyItemId(null);
    } catch (e) {
      setLegacyRulesError(
        e instanceof Error ? e.message : "Recording the review decision failed",
      );
    }
  };

  // Counts used to explain/gate the "Apply Approved Rules" button — applying
  // is a no-op (and previously failed silently from the user's perspective)
  // when nothing has been approved or edited yet.
  const legacyRulesApprovedCount = useMemo(
    () =>
      legacyRulesBatch?.items?.filter(
        (i) => i.review_status === "approved" || i.review_status === "edited",
      ).length ?? 0,
    [legacyRulesBatch],
  );

  const legacyRulesPendingMatchedCount = useMemo(
    () =>
      legacyRulesBatch?.items?.filter(
        (i) => i.review_status === "pending" && i.match_status === "matched",
      ).length ?? 0,
    [legacyRulesBatch],
  );

  // Bulk-approves every still-pending, auto-matched item in one go so a
  // reviewer doesn't have to click "Approve" one-by-one for a large batch.
  // Unmatched items are left untouched since they need a target picked first.
  const handleApproveAllMatchedLegacyItems = async () => {
    const targets =
      legacyRulesBatch?.items?.filter(
        (i) => i.review_status === "pending" && i.match_status === "matched",
      ) ?? [];
    for (const item of targets) {
      await handleReviewLegacyItem(item, "approved");
    }
  };

  const handleApplyLegacyBatch = async () => {
    if (!legacyRulesBatch) return;
    setApplyingLegacyBatch(true);
    setLegacyRulesError(null);
    try {
      const res = await apiv2.converterApplyLegacyRuleBatch(
        legacyRulesBatch.id,
        editedMappings,
      );
      setEditedMappings(res.mappings);
    } catch (e) {
      setLegacyRulesError(
        e instanceof Error ? e.message : "Applying approved rules failed",
      );
    } finally {
      setApplyingLegacyBatch(false);
    }
  };

  // Target tables/fields from the current STTM, used to let a reviewer pick
  // (or override) which table + field a legacy rule applies to. Split into
  // two lookups (table first, then that table's fields) rather than one
  // flattened "table.field" list, since with many mapped tables a single
  // combined dropdown becomes hard to scan for the right target.
  const legacyTargetTableOptions = useMemo(
    () =>
      Array.from(new Set(editedMappings.map((m) => m.target_object))).map(
        (t) => ({ value: t, label: t }),
      ),
    [editedMappings],
  );

  const legacyTargetFieldOptionsForTable = useCallback(
    (table: string) =>
      (
        editedMappings.find((m) => m.target_object === table)?.field_mappings ??
        []
      ).map((fm) => ({ value: fm.target_field, label: fm.target_field })),
    [editedMappings],
  );

  const updateEditedTargetType = (
    mappingIdx: number,
    fieldIdx: number,
    value: string,
  ) => {
    setEditedMappings((prev) =>
      prev.map((m, mi) =>
        mi !== mappingIdx
          ? m
          : {
              ...m,
              field_mappings: m.field_mappings.map((fm, fi) =>
                fi !== fieldIdx ? fm : { ...fm, target_type: value },
              ),
            },
      ),
    );
  };

  const updateEditedTargetLength = (
    mappingIdx: number,
    fieldIdx: number,
    value: number | null,
  ) => {
    setEditedMappings((prev) =>
      prev.map((m, mi) =>
        mi !== mappingIdx
          ? m
          : {
              ...m,
              field_mappings: m.field_mappings.map((fm, fi) =>
                fi !== fieldIdx ? fm : { ...fm, target_length: value },
              ),
            },
      ),
    );
  };

  const updateEditedTransformation = (
    mappingIdx: number,
    fieldIdx: number,
    value: string,
  ) => {
    setEditedMappings((prev) =>
      prev.map((m, mi) =>
        mi !== mappingIdx
          ? m
          : {
              ...m,
              field_mappings: m.field_mappings.map((fm, fi) =>
                fi !== fieldIdx ? fm : { ...fm, transformation: value },
              ),
            },
      ),
    );
  };

  // Lets the user override an auto-detected complex mapping's join type
  // (e.g. switch INNER to LEFT so unmatched rows on the joined table still
  // come through as nulls) or which columns a join is keyed on.
  const updateEditedJoinType = (
    mappingIdx: number,
    joinIdx: number,
    value: "inner" | "left" | "right" | "full" | "cross",
  ) => {
    setEditedMappings((prev) =>
      prev.map((m, mi) =>
        mi !== mappingIdx
          ? m
          : {
              ...m,
              joins: (m.joins ?? []).map((j, ji) =>
                ji !== joinIdx ? j : { ...j, join_type: value },
              ),
            },
      ),
    );
  };

  const updateEditedJoinColumn = (
    mappingIdx: number,
    joinIdx: number,
    side: "left" | "right",
    value: string,
  ) => {
    setEditedMappings((prev) =>
      prev.map((m, mi) =>
        mi !== mappingIdx
          ? m
          : {
              ...m,
              joins: (m.joins ?? []).map((j, ji) =>
                ji !== joinIdx
                  ? j
                  : side === "left"
                    ? { ...j, left_column: value }
                    : { ...j, right_column: value },
              ),
            },
      ),
    );
  };

  // Lets the user pick WHICH two of this mapping's source_tables a given
  // join row connects — e.g. re-key the second join from PERSON ⋈
  // PERSON_IDENTIFIER to PERSON_ADDRESS ⋈ PERSON_IDENTIFIER — so multi-table
  // chains aren't stuck joining every table onto a single fixed "hub" table.
  const updateEditedJoinTable = (
    mappingIdx: number,
    joinIdx: number,
    side: "left" | "right",
    value: string,
  ) => {
    setEditedMappings((prev) =>
      prev.map((m, mi) =>
        mi !== mappingIdx
          ? m
          : {
              ...m,
              joins: (m.joins ?? []).map((j, ji) =>
                ji !== joinIdx
                  ? j
                  : side === "left"
                    ? { ...j, left_table: value }
                    : { ...j, right_table: value },
              ),
            },
      ),
    );
  };

  // WHERE-clause fragment (no leading "WHERE") applied to this table's
  // extract at Load to Target — re-validated server-side before use.
  const updateEditedWhereClause = (mappingIdx: number, value: string) => {
    setEditedMappings((prev) =>
      prev.map((m, mi) =>
        mi !== mappingIdx ? m : { ...m, where_clause: value },
      ),
    );
  };

  // Turns a mapping's load into a Gold-fact-table-style GROUP BY rollup
  // (agents/data_converter/patterns.py's TableObjectMapping.group_by_columns
  // + SttmFieldMapping.aggregate_function) instead of a flat per-row copy.
  // Toggling off clears every field's aggregate_function too, so a mapping
  // never ends up with a stray aggregate function but no group_by_columns.
  const toggleMappingAggregateMode = (mappingIdx: number, enabled: boolean) => {
    setAggregateModeEnabledIdx((prev) => {
      const next = new Set(prev);
      if (enabled) next.add(mappingIdx);
      else next.delete(mappingIdx);
      return next;
    });
    setEditedMappings((prev) =>
      prev.map((m, mi) => {
        if (mi !== mappingIdx) return m;
        if (!enabled) {
          // group_by_columns removed entirely (not just emptied) so
          // "aggregate mode on" can be told apart from "on with nothing
          // picked yet" purely by whether the key is present.
          const { group_by_columns: _drop, ...rest } = m;
          return {
            ...rest,
            field_mappings: m.field_mappings.map((fm) => ({
              ...fm,
              aggregate_function: null,
            })),
          };
        }
        return { ...m, group_by_columns: m.group_by_columns ?? [] };
      }),
    );
  };

  // Whether mapping `mi`'s Aggregate Rollup checkbox should show as
  // checked — true once it actually has 1+ real group_by_columns (an
  // unambiguous "on" regardless of session state, e.g. reloaded from a
  // previously-saved config), OR the user explicitly flipped the checkbox
  // on this session (see aggregateModeEnabledIdx's own comment for why
  // group_by_columns' mere presence/[] can't be trusted on its own).
  const isAggregateModeEnabled = (m: TableObjectMapping, mi: number): boolean =>
    (m.group_by_columns?.length ?? 0) > 0 || aggregateModeEnabledIdx.has(mi);

  const toggleGroupByColumn = (mappingIdx: number, targetField: string) => {
    setEditedMappings((prev) =>
      prev.map((m, mi) => {
        if (mi !== mappingIdx) return m;
        const current = m.group_by_columns ?? [];
        const isGrouped = current.includes(targetField);
        return {
          ...m,
          group_by_columns: isGrouped
            ? current.filter((c) => c !== targetField)
            : [...current, targetField],
          // A field can't be both grouped and aggregated — joining the
          // group-by side clears any aggregate function it was carrying.
          field_mappings: isGrouped
            ? m.field_mappings
            : m.field_mappings.map((fm) =>
                fm.target_field === targetField
                  ? { ...fm, aggregate_function: null }
                  : fm,
              ),
        };
      }),
    );
  };

  const updateFieldAggregateFunction = (
    mappingIdx: number,
    fieldIdx: number,
    fn: "sum" | "count" | "avg" | "min" | "max" | null,
  ) => {
    setEditedMappings((prev) =>
      prev.map((m, mi) =>
        mi !== mappingIdx
          ? m
          : {
              ...m,
              field_mappings: m.field_mappings.map((fm, fi) =>
                fi !== fieldIdx ? fm : { ...fm, aggregate_function: fn },
              ),
            },
      ),
    );
  };

  // BFS reachability check used by ungroupTableFromMapping — confirms every
  // table in `tables` is still reachable from tables[0] via `joins` after a
  // table's been removed from a group, so removing a "cut vertex" (the only
  // bridge connecting two other tables) can't leave a join chain that
  // resolve_join_chain (table_mapper.py) would reject as disconnected at
  // Load Data time.
  const joinTablesAreConnected = (
    tables: string[],
    joins: JoinCondition[],
  ): boolean => {
    if (tables.length <= 1) return true;
    const adjacency = new Map<string, string[]>(tables.map((t) => [t, []]));
    for (const j of joins) {
      adjacency.get(j.left_table)?.push(j.right_table);
      adjacency.get(j.right_table)?.push(j.left_table);
    }
    const seen = new Set<string>([tables[0]]);
    const queue = [tables[0]];
    while (queue.length) {
      const current = queue.shift()!;
      for (const next of adjacency.get(current) ?? []) {
        if (!seen.has(next)) {
          seen.add(next);
          queue.push(next);
        }
      }
    }
    return tables.every((t) => seen.has(t));
  };

  // Peels one table out of a joined mapping back into its own standalone
  // (no-join, 1:1) mapping — appended as a new tab — so a table the
  // auto-detected join grouped in unnecessarily can be mapped independently
  // instead. If removing it disconnects the remaining tables from each
  // other (it was the only bridge between them), every remaining table
  // falls back to standalone mappings too, rather than leaving a broken
  // join no Load Data job could actually execute.
  const ungroupTableFromMapping = (
    mappingIdx: number,
    tableToRemove: string,
  ) => {
    const m = editedMappings[mappingIdx];
    if (!m) return;
    const tables = m.source_tables?.length ? m.source_tables : [m.source_table];
    if (tables.length <= 1 || !tables.includes(tableToRemove)) return;

    const fieldsOf = (t: string) =>
      m.field_mappings.filter(
        (fm) => (fm.source_table_ref || m.source_table) === t,
      );
    const remainingTables = tables.filter((t) => t !== tableToRemove);
    const remainingJoins = (m.joins ?? []).filter(
      (j) => j.left_table !== tableToRemove && j.right_table !== tableToRemove,
    );

    const standalone = (t: string): TableObjectMapping => ({
      ...m,
      source_table: t,
      source_tables: [],
      joins: [],
      join_sql_preview: undefined,
      where_clause: undefined,
      field_mappings: fieldsOf(t),
    });

    const newEntries: TableObjectMapping[] = [];
    let keptMapping: TableObjectMapping;
    if (remainingTables.length === 1) {
      keptMapping = standalone(remainingTables[0]);
    } else if (joinTablesAreConnected(remainingTables, remainingJoins)) {
      keptMapping = {
        ...m,
        source_table: remainingTables[0],
        source_tables: remainingTables,
        joins: remainingJoins,
        field_mappings: remainingTables.flatMap(fieldsOf),
      };
    } else {
      // The removed table was the only bridge connecting the rest — no
      // single valid join chain remains, so every remaining table becomes
      // its own standalone mapping instead.
      keptMapping = standalone(remainingTables[0]);
      newEntries.push(...remainingTables.slice(1).map(standalone));
    }

    // Computed from the render-time editedMappings (not the setEditedMappings
    // updater's own `prev`) so this side effect only ever runs once per call —
    // React 18 StrictMode double-invokes updater functions, which would
    // otherwise double-record this entry.
    const appended = [...newEntries, standalone(tableToRemove)];
    const newMappingIndices = appended.map((_, i) => editedMappings.length + i);
    setEditedMappings((prev) => [
      ...prev.map((mm, mi) => (mi === mappingIdx ? keptMapping : mm)),
      ...appended,
    ]);
    setRemovedJoinTables((r) => ({
      ...r,
      [mappingIdx]: [
        ...(r[mappingIdx] ?? []),
        { table: tableToRemove, priorMapping: m, newMappingIndices },
      ],
    }));
  };

  // Reverses a specific ungroupTableFromMapping call — restores the row's
  // exact pre-split snapshot (same join, same field mappings, no
  // re-matching) and hides the standalone tab(s) that split created.
  const restoreUngroupedTable = (mappingIdx: number, removedIdx: number) => {
    const record = removedJoinTables[mappingIdx]?.[removedIdx];
    if (!record) return;
    setEditedMappings((prev) =>
      prev.map((mm, mi) => (mi === mappingIdx ? record.priorMapping : mm)),
    );
    setRemovedTableMappings((prev) => {
      const next = new Set(prev);
      record.newMappingIndices.forEach((i) => next.add(i));
      return next;
    });
    setRemovedJoinTables((r) => ({
      ...r,
      [mappingIdx]: (r[mappingIdx] ?? []).filter((_, i) => i !== removedIdx),
    }));
  };

  // Opposite of ungroup: joins another (currently standalone or
  // differently-grouped) table INTO this mapping via the backend's
  // generate-complex endpoint — reuses the real column-matching logic so
  // the newly joined table's fields land pre-mapped, not blank/unmapped.
  //
  // Only the NEWLY joined table's field_mappings come from the backend's
  // response — the existing tables' field_mappings (which may carry manual
  // target-field overrides, transformation edits, or review-status changes
  // made before this join) are kept exactly as-is and spliced back onto the
  // result. Passing the full existing+new table set to generate-complex
  // (rather than just the new table alone) is still necessary so the
  // backend's target-field matching knows which target fields the existing
  // tables already claimed and doesn't hand the new table a duplicate.
  const handleAddTableToJoin = async (mappingIdx: number) => {
    const m = editedMappings[mappingIdx];
    const validJoinPickerPairs = joinPickerColumnPairs.filter(
      (p) => p.left && p.right,
    );
    if (
      !m ||
      !joinPickerTable ||
      (joinPickerJoinType !== "cross" &&
        (validJoinPickerPairs.length === 0 ||
          validJoinPickerPairs.length !== joinPickerColumnPairs.length))
    )
      return;
    if (joinPickerJoinType !== "cross") {
      // A composite key with the same left/right column pair listed twice
      // produces a redundant (but harmless-looking) "AND x = y AND x = y"
      // predicate that silently hides a typo in another row — reject it
      // up front rather than sending it to the backend.
      const seenPairs = new Set<string>();
      for (const p of validJoinPickerPairs) {
        const key = `${p.left}\u0000${p.right}`;
        if (seenPairs.has(key)) {
          setErr(
            `Duplicate join column pair: ${m.source_table}.${p.left} = ${joinPickerTable}.${p.right}`,
          );
          return;
        }
        seenPairs.add(key);
      }
    }
    if (!schemaResult) {
      setErr(
        "Source schema not available — re-scan the source before editing joins.",
      );
      return;
    }
    // A join can span tables from more than one saved source connection
    // (a "heterogeneous" join, executed in-process via DuckDB at Load
    // Data/reconciliation time — see is_heterogeneous_join in
    // agents/data_converter/patterns.py) — no longer blocked here. Build
    // an explicit per-table connection/schema map covering the hub table,
    // every already-joined table's existing override, and the newly
    // picked table, so re-running generate-complex for this join doesn't
    // silently reset an earlier table's cross-connection override back to
    // the hub's own connection (_build_joined_mapping only trusts what's
    // in this dict for the current call, not any prior mapping state).
    const pickedTable = schemaResult.tables.find(
      (t) => t.table_name === joinPickerTable,
    );
    const pickedTableConnectionId = pickedTable?.source_connection_id;
    const tableConnectionIds: Record<string, string | null> = {
      ...(m.source_connection_id
        ? { [m.source_table]: m.source_connection_id }
        : {}),
      ...(m.join_table_connection_ids ?? {}),
      [joinPickerTable]: pickedTableConnectionId ?? null,
    };
    const tableSchemas: Record<string, string> = {
      [m.source_table]: m.source_schema,
      ...(m.join_table_schemas ?? {}),
      ...(pickedTable?.schema_name
        ? { [joinPickerTable]: pickedTable.schema_name }
        : {}),
    };
    setJoiningTable(true);
    setError(null);
    try {
      const existingTables = m.source_tables?.length
        ? m.source_tables
        : [m.source_table];
      const newJoin: JoinCondition = {
        left_table: m.source_table,
        left_column: validJoinPickerPairs.map((p) => p.left).join(","),
        right_table: joinPickerTable,
        right_column: validJoinPickerPairs.map((p) => p.right).join(","),
        join_type: joinPickerJoinType,
      };
      const groups: ComplexMappingGroup[] = [
        {
          sourceTables: [...existingTables, joinPickerTable],
          joins: [...(m.joins ?? []), newJoin],
          targetObject: m.target_object,
          tableConnectionIds,
          tableSchemas,
          // Always send the picker's explicit selection (even an empty
          // array) rather than only when non-empty — omitting the key
          // entirely means "include every column" server-side, which is
          // the opposite of what an intentionally-emptied selection means.
          includedColumns: { [joinPickerTable]: joinPickerSelectedColumns },
        },
      ];
      const res = await apiv2.converterGenerateComplexMapping(
        schemaResult,
        sfObjects,
        groups,
        projectId,
      );
      const merged = res.mappings[0];
      if (!merged) throw new Error("No mapping returned");
      // Only the rows the backend attributed to the newly joined table —
      // everything else in `merged.field_mappings` duplicates a table we
      // already had, freshly (re-)matched, which would discard the user's
      // prior edits to those rows if used wholesale.
      const newTableRows = merged.field_mappings.filter(
        (fm) =>
          (fm.source_table_ref ?? merged.source_table) === joinPickerTable,
      );
      const combined: TableObjectMapping = {
        ...m,
        source_tables: merged.source_tables,
        joins: merged.joins,
        join_sql_preview: merged.join_sql_preview,
        source_connection_id: merged.source_connection_id,
        source_schema: merged.source_schema,
        join_table_connection_ids: merged.join_table_connection_ids,
        join_table_schemas: merged.join_table_schemas,
        field_mappings: [...m.field_mappings, ...newTableRows],
        confidence:
          [...m.field_mappings, ...newTableRows].reduce(
            (sum, fm) => sum + fm.confidence,
            0,
          ) / Math.max(1, m.field_mappings.length + newTableRows.length),
      };
      // If the newly joined table was already its own standalone tab, hide
      // that now-redundant tab (soft-delete, same as removeTableMapping).
      // Computed from render-time editedMappings, not setEditedMappings'
      // updater `prev`, so this side effect can't get double-invoked by
      // React 18 StrictMode.
      const dupIdx = editedMappings.findIndex((mm, mi) => {
        if (mi === mappingIdx) return false;
        const mmTables = mm.source_tables?.length
          ? mm.source_tables
          : [mm.source_table];
        return mmTables.length === 1 && mmTables[0] === joinPickerTable;
      });
      setEditedMappings((prev) =>
        prev.map((mm, mi) => (mi === mappingIdx ? combined : mm)),
      );
      if (dupIdx >= 0) {
        setRemovedTableMappings((prevRemoved) =>
          new Set(prevRemoved).add(dupIdx),
        );
      }
      setJoinPickerMappingIdx(null);
      setJoinPickerTable("");
      setJoinPickerColumnPairs([{ left: "", right: "" }]);
      setJoinPickerJoinType("inner");
      setJoinPickerSelectedColumns([]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Adding table to join failed");
    } finally {
      setJoiningTable(false);
    }
  };

  // Remaps an existing tab (standalone or joined) to a different Salesforce
  // target object, re-running the same generate-complex endpoint so fields
  // land pre-matched against the new object instead of blank. Most useful
  // right after ungroupTableFromMapping spins a removed table off into its
  // own tab that's still carrying the original join's (possibly wrong)
  // target object.
  const handleRetargetMapping = async (mappingIdx: number) => {
    const m = editedMappings[mappingIdx];
    if (!m || !retargetObjectApiName) return;
    if (!schemaResult) {
      setErr(
        "Source schema not available — re-scan the source before remapping.",
      );
      return;
    }
    setRetargeting(true);
    setError(null);
    try {
      const tables = m.source_tables?.length
        ? m.source_tables
        : [m.source_table];
      const groups: ComplexMappingGroup[] = [
        {
          sourceTables: tables,
          joins: m.joins ?? [],
          targetObject: retargetObjectApiName,
        },
      ];
      const res = await apiv2.converterGenerateComplexMapping(
        schemaResult,
        sfObjects,
        groups,
        projectId,
      );
      const remapped = res.mappings[0];
      if (!remapped) throw new Error("No mapping returned");
      setEditedMappings((prev) =>
        prev.map((mm, mi) => (mi === mappingIdx ? remapped : mm)),
      );
      setRetargetMappingIdx(null);
      setRetargetObjectApiName("");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Remapping target object failed");
    } finally {
      setRetargeting(false);
    }
  };

  // Renames an as-is/identity-mapping tab's target table — the field
  // mappings themselves don't change, just target_object (the sanitized,
  // DDL-safe identifier) and target_label (the display name). Also syncs
  // the corresponding mappingPairs entry (matched by source table, not
  // index — mappingPairs and editedMappings can diverge after joins/
  // removals) so a later "Generate Mappings" re-run from the pairing
  // screen doesn't silently discard the rename back to the source name.
  const handleRenameIdentityTarget = (mappingIdx: number) => {
    const m = editedMappings[mappingIdx];
    const newName = renameTargetInput.trim();
    if (!m || !newName) return;
    const sanitized = sanitizeTargetIdentifier(newName);
    setEditedMappings((prev) =>
      prev.map((mm, mi) =>
        mi === mappingIdx
          ? { ...mm, target_object: sanitized, target_label: newName }
          : mm,
      ),
    );
    setMappingPairs((prev) =>
      prev.map((p) =>
        p.sourceTable === m.source_table ? { ...p, targetObject: newName } : p,
      ),
    );
    setRenamingMappingIdx(null);
    setRenameTargetInput("");
  };

  // A joined mapping's field rows need to say WHICH source table a column
  // comes from — a manually added row (addManualFieldMapping) never sets
  // source_table_ref, silently defaulting to the hub table, which
  // qualifies the field against the wrong table in the generated SELECT
  // (build_loader_sql_preview / _load_one_table both read
  // fm.source_table_ref || mapping.source_table).
  const updateEditedSourceTableRef = (
    mappingIdx: number,
    fieldIdx: number,
    value: string,
  ) => {
    setEditedMappings((prev) =>
      prev.map((m, mi) =>
        mi !== mappingIdx
          ? m
          : {
              ...m,
              field_mappings: m.field_mappings.map((fm, fi) =>
                fi !== fieldIdx
                  ? fm
                  : { ...fm, source_table_ref: value || null },
              ),
            },
      ),
    );
  };

  // Source Column/Type are normally read from the scanned schema, but a
  // manually added row (see addManualFieldMapping) has no backing source
  // column — these let the user type one in, or leave it blank when the
  // target is populated from a hardcoded value or cross-field logic instead.
  const updateEditedSourceColumn = (
    mappingIdx: number,
    fieldIdx: number,
    value: string,
  ) => {
    setEditedMappings((prev) =>
      prev.map((m, mi) =>
        mi !== mappingIdx
          ? m
          : {
              ...m,
              field_mappings: m.field_mappings.map((fm, fi) =>
                fi !== fieldIdx ? fm : { ...fm, source_column: value },
              ),
            },
      ),
    );
  };

  const updateEditedSourceType = (
    mappingIdx: number,
    fieldIdx: number,
    value: string,
  ) => {
    setEditedMappings((prev) =>
      prev.map((m, mi) =>
        mi !== mappingIdx
          ? m
          : {
              ...m,
              field_mappings: m.field_mappings.map((fm, fi) =>
                fi !== fieldIdx ? fm : { ...fm, source_type: value },
              ),
            },
      ),
    );
  };

  // Appends a blank, fully-editable row to the active tab's mapping — for
  // target fields with no direct source column (e.g. a hardcoded constant,
  // or a value derived from other source fields via the Transform column).
  const addManualFieldMapping = (mappingIdx: number) => {
    setEditedMappings((prev) =>
      prev.map((m, mi) =>
        mi !== mappingIdx
          ? m
          : {
              ...m,
              field_mappings: [
                ...m.field_mappings,
                {
                  source_column: "",
                  source_type: "",
                  target_field: "",
                  target_type: "string",
                  target_length: null,
                  transformation: "",
                  validation_rule: "",
                  notes: "Manually added",
                  confidence: 1.0,
                  status: "Review",
                },
              ],
            },
      ),
    );
    setSttmActiveTab(mappingIdx);
  };

  // Fields available on the target object for a given mapping — powers the
  // Target Field dropdown. Empty when the object metadata wasn't loaded.
  // External-ID (upsert-key) fields are surfaced first so they're easy to find.
  const targetFieldsFor = (objectApiName: string) => {
    const cached = targetFieldCache.get(objectApiName);
    if (cached) return cached;
    const raw = sfObjects.find((o) => o.api_name === objectApiName)?.fields;
    const rank = (f: SalesforceField) =>
      f.external_id ? 0 : f.id_lookup ? 1 : 2;
    const fields = raw ? [...raw].sort((a, b) => rank(a) - rank(b)) : [];
    const entry = {
      fields,
      options: fields.map(
        (f): SearchableFieldOption => ({
          value: f.api_name,
          label: f.label,
          badge: f.external_id ? "★" : f.id_lookup ? "◆" : undefined,
          sublabel: f.external_id
            ? "External ID"
            : f.id_lookup
              ? "ID lookup"
              : undefined,
        }),
      ),
    };
    // Only cache a hit: an object whose metadata hasn't loaded yet must be
    // looked up again once it has.
    if (raw) targetFieldCache.set(objectApiName, entry);
    return entry;
  };
  const fieldsForObject = (objectApiName: string): SalesforceField[] =>
    targetFieldsFor(objectApiName).fields;

  // Columns actually scanned on a given source table — powers the Source
  // Column dropdown (search/select a real column, vs. typing one that isn't
  // in the scanned schema, e.g. for a hardcoded/derived row).
  const sourceColumnOptions = (tableName: string): SearchableFieldOption[] => {
    const cached = sourceColumnOptionsCache.get(tableName);
    if (cached) return cached;
    const table = schemaTablesByName.get(tableName);
    if (!table) return [];
    const options = table.columns.map((c) => ({
      value: c.name,
      label: c.name,
      badge: c.is_primary_key ? "PK" : undefined,
      sublabel: c.data_type,
    }));
    sourceColumnOptionsCache.set(tableName, options);
    return options;
  };

  // Selecting a real source column also syncs Source Type to that column's
  // scanned type, mirroring selectEditedTargetField below.
  const selectEditedSourceColumn = (
    mappingIdx: number,
    fieldIdx: number,
    value: string,
    tableName: string,
  ) => {
    const column = schemaResult?.tables
      .find((t) => t.table_name === tableName)
      ?.columns.find((c) => c.name === value);
    setEditedMappings((prev) =>
      prev.map((m, mi) =>
        mi !== mappingIdx
          ? m
          : {
              ...m,
              field_mappings: m.field_mappings.map((fm, fi) =>
                fi !== fieldIdx
                  ? fm
                  : {
                      ...fm,
                      source_column: value,
                      source_type: column ? column.data_type : fm.source_type,
                    },
              ),
            },
      ),
    );
  };

  // Selecting a target field also syncs the target type to that field's
  // native type (when known), so the type column follows the chosen column.
  // `fieldOverride` lets a caller that just built the SalesforceField object
  // itself (e.g. createAndSelectTargetField) pass it straight in, instead of
  // re-deriving it via fieldsForObject(sfObjects) -- sfObjects may not have
  // committed that field yet if a setSfObjects update is still pending.
  const selectEditedTargetField = (
    mappingIdx: number,
    fieldIdx: number,
    value: string,
    fieldOverride?: SalesforceField,
  ) => {
    setEditedMappings((prev) =>
      prev.map((m, mi) => {
        if (mi !== mappingIdx) return m;
        const match =
          fieldOverride ??
          fieldsForObject(m.target_object).find((f) => f.api_name === value);
        return {
          ...m,
          field_mappings: m.field_mappings.map((fm, fi) =>
            fi !== fieldIdx
              ? fm
              : {
                  ...fm,
                  target_field: value,
                  target_type: match?.data_type || fm.target_type,
                  target_length: clampTargetLength(
                    match?.data_type || fm.target_type,
                    match?.length ?? fm.target_length,
                  ),
                  // A manual target-field pick is how a user resolves an
                  // "Unmapped" row -- build_loader_sql_preview/_load_one_table
                  // both filter out fm.status === "Unmapped" regardless of
                  // target_field, so leaving status untouched meant a row
                  // manually mapped here stayed permanently excluded from
                  // the load no matter what target field was chosen.
                  // Clearing the pick re-parks it as Unmapped, same as it
                  // started.
                  status: value
                    ? fm.status === "Unmapped"
                      ? "Mapped"
                      : fm.status
                    : "Unmapped",
                },
          ),
        };
      }),
    );
  };

  // Search-select had no matching target field at all (e.g. a joined
  // table's column, like expense.AMOUNT, that the target object simply
  // doesn't have yet) -- add a new field to that target object's catalog
  // (so it shows up as a normal option from now on) and map this row onto
  // it, same as picking an existing one. Generate DDL/Create Tables reads
  // straight off field_mappings, so this new column is created for real the
  // next time DDL is (re)generated for this target object.
  const createAndSelectTargetField = (
    mappingIdx: number,
    fieldIdx: number,
    apiName: string,
  ) => {
    const trimmed = apiName.trim();
    if (!trimmed) return;
    const targetObject = editedMappings[mappingIdx]?.target_object;
    const alreadyExists = targetObject
      ? fieldsForObject(targetObject).some(
          (f) => f.api_name.toLowerCase() === trimmed.toLowerCase(),
        )
      : false;
    const newField: SalesforceField = {
      api_name: trimmed,
      label: trimmed,
      data_type: "string",
      length: 255,
      required: false,
      nillable: true,
      unique: false,
      custom: true,
    };
    if (targetObject && !alreadyExists) {
      setSfObjects((prev) =>
        prev.map((o) =>
          o.api_name !== targetObject
            ? o
            : {
                ...o,
                fields: [...o.fields, newField],
              },
        ),
      );
    }
    // Pass the field we just built directly -- setSfObjects above hasn't
    // committed yet, so looking it up via fieldsForObject(sfObjects) here
    // would still miss it (see selectEditedTargetField's fieldOverride).
    selectEditedTargetField(mappingIdx, fieldIdx, trimmed, newField);
  };

  const removeEditedFieldMapping = (mappingIdx: number, fieldIdx: number) => {
    const removed = editedMappings[mappingIdx]?.field_mappings[fieldIdx];
    if (removed) {
      setRemovedFieldMappings((r) => ({
        ...r,
        [mappingIdx]: [...(r[mappingIdx] ?? []), removed],
      }));
    }
    setEditedMappings((prev) =>
      prev.map((m, mi) =>
        mi !== mappingIdx
          ? m
          : {
              ...m,
              field_mappings: m.field_mappings.filter(
                (_, fi) => fi !== fieldIdx,
              ),
            },
      ),
    );
  };

  // Every source column on a tab, defaulting to fully checked the first time
  // its Limit Fields panel is opened for that tab.
  const getKeepFieldsSelection = useCallback(
    (mappingIdx: number): Set<string> => {
      const existing = keepFieldsSelected[mappingIdx];
      if (existing) return existing;
      const mapping = editedMappings[mappingIdx];
      return new Set(
        (mapping?.field_mappings ?? []).map((fm) =>
          fm.source_column.toUpperCase(),
        ),
      );
    },
    [keepFieldsSelected, editedMappings],
  );

  const toggleKeepField = (mappingIdx: number, sourceColumn: string) => {
    const key = sourceColumn.toUpperCase();
    setKeepFieldsSelected((prev) => {
      const current = new Set(
        prev[mappingIdx] ?? getKeepFieldsSelection(mappingIdx),
      );
      if (current.has(key)) current.delete(key);
      else current.add(key);
      return { ...prev, [mappingIdx]: current };
    });
  };

  const setAllKeepFields = (mappingIdx: number, checked: boolean) => {
    const mapping = editedMappings[mappingIdx];
    const all = checked
      ? new Set(
          (mapping?.field_mappings ?? []).map((fm) =>
            fm.source_column.toUpperCase(),
          ),
        )
      : new Set<string>();
    setKeepFieldsSelected((prev) => ({ ...prev, [mappingIdx]: all }));
  };

  // Same result as clicking each checkbox individually, but driven from a
  // pasted list of column names instead — split on newline/comma/tab/
  // semicolon so it accepts a one-per-line paste, a comma-separated row, or
  // a copied spreadsheet column. Matches case-insensitively against this
  // tab's actual source columns; a pasted name that doesn't match anything
  // (typo, wrong table, ...) is silently ignored rather than staged.
  const applyPastedKeepFields = (mappingIdx: number, text: string) => {
    const mapping = editedMappings[mappingIdx];
    const matched = parsePastedColumnNames(
      text,
      (mapping?.field_mappings ?? []).map((fm) => fm.source_column),
    );
    if (matched.size === 0) {
      setErr(
        text.trim()
          ? "None of the pasted names matched a column on this table — check spelling."
          : "Paste one or more column names first.",
      );
      return;
    }
    setError(null);
    setKeepFieldsSelected((prev) => ({ ...prev, [mappingIdx]: matched }));
  };

  // Bulk-restrict the active tab's mapping to only the checked source
  // columns, dropping every other row.
  const handleKeepOnlyFields = (mappingIdx: number, wanted: Set<string>) => {
    if (wanted.size === 0) {
      setErr("Select at least one column to keep.");
      return;
    }
    setError(null);
    const wantedUpper = new Set(Array.from(wanted, (s) => s.toUpperCase()));
    const mapping = editedMappings[mappingIdx];
    if (mapping) {
      const removed = mapping.field_mappings.filter(
        (fm) => !wantedUpper.has(fm.source_column.toUpperCase()),
      );
      if (removed.length > 0) {
        setRemovedFieldMappings((r) => ({
          ...r,
          [mappingIdx]: [...(r[mappingIdx] ?? []), ...removed],
        }));
      }
    }
    setEditedMappings((prev) =>
      prev.map((m, mi) =>
        mi !== mappingIdx
          ? m
          : {
              ...m,
              field_mappings: m.field_mappings.filter((fm) =>
                wantedUpper.has(fm.source_column.toUpperCase()),
              ),
            },
      ),
    );
  };

  // Re-add a previously removed row (individually, or via the bulk filter)
  // to the end of the tab's mapping, exactly as it was before removal.
  const handleRestoreFieldMapping = (
    mappingIdx: number,
    removedIdx: number,
  ) => {
    const row = removedFieldMappings[mappingIdx]?.[removedIdx];
    if (!row) return;
    setEditedMappings((prev) =>
      prev.map((m, mi) =>
        mi !== mappingIdx
          ? m
          : { ...m, field_mappings: [...m.field_mappings, row] },
      ),
    );
    setRemovedFieldMappings((r) => ({
      ...r,
      [mappingIdx]: (r[mappingIdx] ?? []).filter((_, i) => i !== removedIdx),
    }));
  };

  const toggleTable = (name: string) => {
    setExpandedTables((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const toggleMapping = (name: string) => {
    setExpandedMappings((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  // Shared by goNext's "load" pre-populate and the "Directly" choice
  // card's RDBMS branch (which jumps straight to "load", bypassing goNext
  // entirely) — both need the exact same defaults so handleLoadData's
  // load_pairs override always agrees with what the Load Data screen shows.
  //
  // `strategyOverride` is forwarded to stagedTargetTable — see its comment
  // for why the "Directly" branch must pass "direct" explicitly rather
  // than rely on crmLoadStrategy state, which hasn't updated yet in the
  // same click handler that also calls setCrmLoadStrategy("direct").
  const buildDefaultLoadMappingConfig = (
    strategyOverride?: "staged" | "direct" | null,
  ) =>
    mappings.map((m) => ({
      // Prefer the schema of whichever source DB is actually connected
      // right now over the schema the mapping doc was originally generated
      // against — connecting a different source system for Load should use
      // ITS schema, not a stale one baked into the mapping. That only
      // applies to primary-connection tables though — a table pulled from
      // an extra source connection must keep its OWN connection's schema
      // (see getMappingSourceSchemaDefault), never the primary's.
      srcSchema: getMappingSourceSchemaDefault(m),
      srcTable: m.source_table,
      tgtSchema: ddlTargetSchema || targetSchemaName || "",
      tgtTable: stagedTargetTable(m.target_object, strategyOverride),
    }));

  const goNext = () => {
    const order = action
      ? effectiveActionSteps(action, crmLoadStrategy, targetPlatformMode)
      : [];
    const idx = order.indexOf(step);
    if (idx < order.length - 1) {
      const next = order[idx + 1];
      // Pre-populate load step connections from previous steps
      if (next === "sttm" && editedMappings.length === 0) {
        setEditedMappings(
          withDefaultStringTypes(JSON.parse(JSON.stringify(mappings))),
        );
      }
      if (next === "load") {
        setLoadMappingConfig(buildDefaultLoadMappingConfig());
      }
      setStep(next);
    }
  };

  const goPrev = () => {
    const order = action
      ? effectiveActionSteps(action, crmLoadStrategy, targetPlatformMode)
      : [];
    const idx = order.indexOf(step);
    if (idx > 0) setStep(order[idx - 1]);
  };

  const handleTargetConnectNew = useCallback(
    async (values: DbConnectFormValues): Promise<ConnectResult> => {
      try {
        const connection = buildConverterConnection(values);
        const res = await apiv2.converterTargetConnect({
          connection,
          projectId,
          connectionName: values.connection_name || undefined,
        });
        setDdlTargetConnected(res);
        setTargetConnectionId(res.connection_id ?? null);
        setTargetConnLabel(
          values.connection_name ||
            `${connection.db_type} · ${connection.host}`,
        );
        setTargetDbType(res.db_type ?? connection.db_type);
        setTargetSchemaName(connection.schema_name);
        const effectiveSchema = effectiveSchemaNameFromConnection(
          connection.schema_name,
          connection.db_type,
        );
        if (!ddlTargetSchema && effectiveSchema) {
          setDdlTargetSchema(effectiveSchema);
        }
        applyTargetSchemas(res.schemas);
        return { ok: true };
      } catch (err: unknown) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : "Connection failed",
        };
      }
    },
    [projectId, ddlTargetSchema, applyTargetSchemas],
  );

  const handleTargetConnectExisting = useCallback(
    async (connectionId: string, label?: string): Promise<ConnectResult> => {
      try {
        const res = await apiv2.converterTargetConnect({
          connectionId,
          projectId,
        });
        setDdlTargetConnected(res);
        setTargetConnectionId(res.connection_id ?? connectionId);
        setTargetConnLabel(label ?? "saved connection");
        if (res.db_type) setTargetDbType(res.db_type);
        applyTargetSchemas(res.schemas);
        return { ok: true };
      } catch (err: unknown) {
        return {
          ok: false,
          error: err instanceof Error ? err.message : "Connection failed",
        };
      }
    },
    [projectId, applyTargetSchemas],
  );

  // Re-test the currently-connected target — same rationale as
  // handleRefreshSourceConnection above.
  const handleRefreshTargetConnection = async () => {
    if (!targetConnectionId) return;
    setRefreshingConnection(true);
    setError(null);
    try {
      const res = await apiv2.converterTargetConnect({
        connectionId: targetConnectionId,
        projectId,
      });
      setDdlTargetConnected(res);
      if (res.db_type) setTargetDbType(res.db_type);
    } catch (e) {
      setDdlTargetConnected(null);
      setErr(
        e instanceof Error ? e.message : "Could not connect to the database",
      );
    } finally {
      setRefreshingConnection(false);
    }
  };

  // Best-effort auto-save of DDL generation output, so re-entering the
  // Data Loader action in a later session can offer to resume instead of
  // requiring DDL to be regenerated from the mapping every time — same
  // `.catch(() => {})` pattern as the mapping/CRM-connection auto-saves.
  const persistDdlState = (
    ddlStatementsToSave: DdlStatement[],
    duplicateReportToSave: DdlDuplicate[],
    executed: boolean,
  ) => {
    if (!projectId) return;
    apiv2
      .converterSetDdlState(projectId, {
        target_schema: ddlTargetSchema || "",
        ddl_statements: ddlStatementsToSave,
        duplicate_report: duplicateReportToSave,
        executed,
        target_platform_mode: targetPlatformMode,
      })
      .catch(() => {});
  };

  // Whatever the user actually picked on the Target Platform step --
  // mirrors the mode/dialect/CRM type this component already tracks, so
  // generated DDL comments read e.g. "Snowflake Object" instead of always
  // assuming Salesforce.
  const computeTargetPlatformLabel = (): string => {
    if (targetPlatformMode === "mirror") return "Auto-Generated Target Model";
    if (targetPlatformMode === "rdbms") {
      return (
        _RDBMS_DIALECT_LABELS[targetDbType ?? ""] ??
        (targetDbType || "Target Database")
      );
    }
    return _CRM_TARGET_LABELS[sfConn.target_type] ?? "Target";
  };

  const handleGenerateDdl = async () => {
    if (mappings.length === 0) {
      setErr("Generate STTM mappings first before creating tables.");
      return;
    }
    setGeneratingDdl(true);
    setError(null);
    try {
      const res = await apiv2.converterGenerateDdl(
        mappings,
        ddlTargetSchema || "",
        undefined,
        undefined,
        projectId,
        computeTargetPlatformLabel(),
        activeTargetPlatform,
        {
          mode: tableNamingMode,
          value: tableNamingValue,
          scope:
            tableNamingScope === "all"
              ? undefined
              : Array.from(tableNamingScope),
        },
        includeAuditColumns,
      );
      setDdlStatements(res.ddl_statements);
      setDdlDuplicates(res.duplicate_report ?? []);
      setDdlDuplicateDecisions({});
      setDdlDuplicatesExpanded(true);
      setDdlExecuteResults(null);
      setDdlReconcileResults(null);
      setDropTablesResult(null);
      setDropTablesArmed(false);
      persistDdlState(res.ddl_statements, res.duplicate_report ?? [], false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "DDL generation failed");
    } finally {
      setGeneratingDdl(false);
    }
  };

  const ddlDuplicateKey = (d: DdlDuplicate) => `${d.table}::${d.column ?? ""}`;

  const setDdlDuplicateDecision = (
    d: DdlDuplicate,
    action: "remove" | "keep" | "rename",
  ) => {
    const key = ddlDuplicateKey(d);
    setDdlDuplicateDecisions((prev) => ({
      ...prev,
      [key]: {
        action,
        newName:
          prev[key]?.newName ?? (d.column ? `${d.column}_2` : `${d.table}_2`),
      },
    }));
  };

  const setDdlDuplicateNewName = (d: DdlDuplicate, newName: string) => {
    const key = ddlDuplicateKey(d);
    setDdlDuplicateDecisions((prev) => ({
      ...prev,
      [key]: { action: prev[key]?.action ?? "rename", newName },
    }));
  };

  // Re-run DDL generation from a given decision map (unset rows fall back to
  // "remove"), used by both "apply my custom choices" and "approve all
  // defaults" so the two entry points share one code path.
  const applyDdlDuplicateResolutions = async (
    decisions: Record<
      string,
      { action: "remove" | "keep" | "rename"; newName: string }
    >,
  ) => {
    if (mappings.length === 0) return;
    setResolvingDdlDuplicates(true);
    setError(null);
    try {
      const resolutions: DdlDuplicateResolution[] = ddlDuplicates.map((d) => {
        const decision = decisions[ddlDuplicateKey(d)];
        const action = decision?.action ?? "remove";
        return {
          table: d.table,
          column: d.column,
          action,
          ...(action === "rename" && decision?.newName
            ? { new_name: decision.newName }
            : {}),
        };
      });
      const res = await apiv2.converterGenerateDdl(
        mappings,
        ddlTargetSchema || "",
        undefined,
        resolutions,
        projectId,
        computeTargetPlatformLabel(),
        activeTargetPlatform,
        {
          mode: tableNamingMode,
          value: tableNamingValue,
          scope:
            tableNamingScope === "all"
              ? undefined
              : Array.from(tableNamingScope),
        },
        includeAuditColumns,
      );
      setDdlStatements(res.ddl_statements);
      setDdlDuplicates(res.duplicate_report ?? []);
      setDdlDuplicateDecisions(decisions);
      setDdlExecuteResults(null);
      setDdlReconcileResults(null);
      setDropTablesResult(null);
      setDropTablesArmed(false);
      persistDdlState(res.ddl_statements, res.duplicate_report ?? [], false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "DDL generation failed");
    } finally {
      setResolvingDdlDuplicates(false);
    }
  };

  // "Apply My Choices" — respects whatever Keep/Remove/Edit each row was
  // set to (unset rows default to "remove").
  const handleApplyDdlDuplicateDecisions = () =>
    applyDdlDuplicateResolutions(ddlDuplicateDecisions);

  // Groups the flat duplicate/mismatch report by target table so the review
  // UI can render one tab per table instead of a single long list — a large
  // schema can otherwise produce dozens of rows with no way to tell which
  // target table each one belongs to at a glance.
  const ddlDuplicatesByTable = useMemo(() => {
    const map = new Map<string, DdlDuplicate[]>();
    for (const d of ddlDuplicates) {
      const existing = map.get(d.table);
      if (existing) existing.push(d);
      else map.set(d.table, [d]);
    }
    return map;
  }, [ddlDuplicates]);
  const ddlDuplicateTableNames = useMemo(
    () => Array.from(ddlDuplicatesByTable.keys()),
    [ddlDuplicatesByTable],
  );
  // Falls back to the first tab whenever the explicitly-selected one no
  // longer has any entries (e.g. it was just cleared by a default action).
  const ddlDuplicateActiveTableResolved =
    ddlDuplicateActiveTable && ddlDuplicatesByTable.has(ddlDuplicateActiveTable)
      ? ddlDuplicateActiveTable
      : (ddlDuplicateTableNames[0] ?? null);

  // Records what was decided (and why) before the on-screen list is cleared,
  // so nothing reviewed this session is lost once a "default" action removes
  // it from view — downloaded on demand via downloadSttmMappingLog below.
  const logSttmMappingDecisions = (
    entries: DdlDuplicate[],
    actionLabel: string,
  ) => {
    const timestamp = new Date().toISOString();
    const lines = entries.map(
      (d) =>
        `[${timestamp}] ${d.table}${d.column ? `.${d.column}` : ""} — ${d.detail} => ${actionLabel}`,
    );
    setSttmMappingLog((prev) => [...prev, ...lines]);
  };

  // "Approve All (Default)" — forces every duplicate to "remove", overriding
  // any per-row customization, so a large duplicate list can be cleared in
  // one click without reviewing each row.
  const handleApproveAllDdlDuplicatesDefault = () => {
    const allDefault: Record<
      string,
      { action: "remove" | "keep" | "rename"; newName: string }
    > = {};
    for (const d of ddlDuplicates) {
      allDefault[ddlDuplicateKey(d)] = {
        action: "remove",
        newName: d.column ? `${d.column}_2` : `${d.table}_2`,
      };
    }
    logSttmMappingDecisions(
      ddlDuplicates,
      "Default (Remove) — approved for whole population",
    );
    applyDdlDuplicateResolutions(allDefault);
  };

  // "Approve Default" scoped to a single tab's target table — forces just
  // that table's duplicates to "remove" while leaving every other table's
  // rows exactly as the reviewer already set them.
  const handleApproveTableDdlDuplicatesDefault = (table: string) => {
    const tableDuplicates = ddlDuplicates.filter((d) => d.table === table);
    if (tableDuplicates.length === 0) return;
    const merged = { ...ddlDuplicateDecisions };
    for (const d of tableDuplicates) {
      merged[ddlDuplicateKey(d)] = {
        action: "remove",
        newName: d.column ? `${d.column}_2` : `${d.table}_2`,
      };
    }
    logSttmMappingDecisions(
      tableDuplicates,
      `Default (Remove) — approved for table '${table}'`,
    );
    applyDdlDuplicateResolutions(merged);
  };

  // Client-side only — same pattern as downloadFailuresTxt (defined below),
  // duplicated here rather than shared since this log accumulates across
  // multiple regenerate calls instead of reflecting one operation's result.
  const downloadSttmMappingLog = () => {
    const text = `STTM Mapping Log (${sttmMappingLog.length})\n${"=".repeat(40)}\n${sttmMappingLog.join("\n")}\n`;
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sttm_mapping_log_${filenameTimestamp()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExecuteDdl = async () => {
    if (ddlStatements.length === 0) return;
    if (!targetConnectionId) {
      setErr("Connect to a target database first");
      return;
    }
    setExecutingDdl(true);
    setReconcilingDdl(true);
    setError(null);
    try {
      // Check each target table against the live database first — a table
      // that already matches the mapping is left completely alone (no
      // DROP, no CREATE); only tables that don't exist yet or have drifted
      // actually go to /ddl/execute. This is what makes "Create Tables" a
      // safe, repeatable action instead of unconditionally recreating
      // every table on every click.
      const reconcile = await apiv2.converterReconcileDdl(
        mappings,
        { connectionId: targetConnectionId, projectId },
        ddlTargetSchema || "",
        activeTargetPlatform,
        projectId,
        {
          mode: tableNamingMode,
          value: tableNamingValue,
          scope:
            tableNamingScope === "all"
              ? undefined
              : Array.from(tableNamingScope),
        },
      );
      setDdlReconcileResults(reconcile.results);
      setReconcilingDdl(false);

      const toExecute = filterDdlStatementsToExecute(
        ddlStatements,
        reconcile.results,
      );

      if (toExecute.length === 0) {
        setDdlExecuteResults([]);
        persistDdlState(ddlStatements, ddlDuplicates, true);
        return;
      }

      const res = await apiv2.converterExecuteDdl(
        { connectionId: targetConnectionId, projectId },
        toExecute.map((s) => s.ddl),
        true,
        projectId,
      );
      setDdlExecuteResults(res.results);
      persistDdlState(ddlStatements, ddlDuplicates, true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "DDL execution failed");
    } finally {
      setExecutingDdl(false);
      setReconcilingDdl(false);
    }
  };

  // Standalone Drop Tables action — independent of Create Tables. Requires
  // a second click to confirm since it's destructive against a real DB.
  const handleDropTablesClick = async () => {
    if (ddlStatements.length === 0 || !targetConnectionId) return;
    if (!dropTablesArmed) {
      setDropTablesArmed(true);
      return;
    }
    setDropTablesArmed(false);
    setDropTablesLoading(true);
    setError(null);
    try {
      const res = await apiv2.converterDropTables(
        { connectionId: targetConnectionId, projectId },
        ddlTargetSchema || "",
        ddlStatements.map((s) => s.table_name),
      );
      setDropTablesResult(res);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Drop tables failed");
    } finally {
      setDropTablesLoading(false);
    }
  };

  // Trigger a browser download from base64 content embedded directly in an
  // API response, rather than a follow-up GET to a server-tracked file_id —
  // the latter can 404 in a horizontally-scaled deployment if the request
  // lands on a different backend instance than the one that generated the
  // file (see DataLoadDqReportInfo on the backend for the full rationale).
  const downloadBase64File = (
    filename: string,
    base64: string,
    mimeType: string,
  ) => {
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export the Load Data results table as CSV — generated entirely
  // client-side from data already in loadProgress, no server round-trip.
  const downloadLoadResultsCsv = (results: DataLoadTableResult[]) => {
    const header = [
      "Source Table",
      "Target Table",
      "Source Count",
      "Rows Read",
      "Rows Not Read",
      "Loaded",
      "Failed",
      "DQ Excluded",
      "Status",
    ];
    const csvEscape = (v: string | number) => {
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = results.map((r) => [
      r.source_table,
      r.target_table,
      r.source_total_count,
      r.rows_read,
      Math.max(0, r.source_total_count - r.rows_read),
      r.rows_loaded,
      r.rows_failed,
      r.rows_dq_excluded ?? 0,
      r.error ?? "Success",
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `load_results_${filenameTimestamp()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export the file-mode ("Load Data from Files") results table as CSV —
  // same pattern as downloadLoadResultsCsv above, but against fileLoadResults'
  // own shape (ConverterLoadResponse + tableName/durationMs/error), which
  // doesn't match DataLoadTableResult closely enough to share one function.
  const downloadFileLoadResultsCsv = (
    results: NonNullable<typeof fileLoadResults>,
  ) => {
    const header = [
      "Source Table",
      "Target Table",
      "Source Count",
      "Loaded",
      "Rejected",
      "Duration (ms)",
      "Status",
    ];
    const csvEscape = (v: string | number) => {
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = results.map((r) => [
      r.tableName,
      `${r.schema_name}.${r.table_name}`,
      r.rows_read ?? "",
      r.rows_loaded,
      r.rows_rejected,
      r.durationMs ?? "",
      r.error ?? (r.rows_rejected > 0 ? "Rejects" : "Success"),
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `file_load_results_${filenameTimestamp()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export the Reconciliation Dashboard's count-check results as CSV —
  // generated entirely client-side from data already in reconcileMappings/
  // getReconcileTab, same pattern as downloadLoadResultsCsv above.
  const downloadReconciliationCsv = () => {
    const header = [
      "Source Table",
      "Target Table",
      "Source Count",
      "Target Count",
      "Difference",
      "Status",
    ];
    const csvEscape = (v: string | number) => {
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = reconcileMappings.map((m) => {
      const tab = getReconcileTab(reconcileTabKey(m));
      const result = tab.countResult;
      const status =
        tab.error || result?.error
          ? "Error"
          : !result
            ? "Not checked"
            : result.match
              ? "Match"
              : result.mdm_matched
                ? "Matched with MDM"
                : "Mismatch";
      return [
        m.source_table,
        stagedTargetTable(m.target_object),
        result?.source_count ?? "—",
        result?.target_count ?? "—",
        result?.difference ?? "—",
        status,
      ];
    });
    const csv = [header, ...rows]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reconciliation_results_${filenameTimestamp()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export Build & Preview's flagged "bad records" (mostly-empty rows that
  // passed type/required-field checks but are clearly not real records next
  // to their peers — see service.py's _flag_incomplete_rows) as a CSV for
  // later review, generated entirely client-side from data already in
  // filePreviewResult.bad_records — no extra server round-trip needed.
  const downloadBadRecordsCsv = (
    columns: string[],
    badRecords: ConverterBadRecord[],
  ) => {
    const csvEscape = (v: unknown) => {
      const s = v === null || v === undefined ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ["row_number", "reason", ...columns];
    const rows = badRecords.map((r) => [
      r.row_number,
      r.reason,
      ...columns.map((c) => r.row[c]),
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bad_records_${filenameTimestamp()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // DDL-generation notes (merged/duplicate tables & columns) are written to
  // a plain-text file instead of an inline on-screen list — generated
  // entirely client-side from data already in local state, no server
  // round-trip, same pattern as downloadLoadResultsCsv above. Shared by
  // every "download the failure/notes list as one consolidated .txt"
  // link in this file — whenever an operation reports 1+ failures, this
  // is the one place that turns them into a file instead of an on-screen
  // list, so the whole set (not just what fits on screen) is always
  // available.
  const downloadFailuresTxt = (
    title: string,
    filename: string,
    lines: string[],
  ) => {
    const text = `${title} (${lines.length})\n${"=".repeat(40)}\n${lines.join("\n")}\n`;
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const SCHEMA_EXPORT_HEADERS = [
    "Table",
    "Column",
    "Data Type",
    "Length",
    "Precision",
    "Scale",
    "Nullable",
    "Primary Key",
    "Foreign Key",
    "FK Reference",
  ];

  // Flattens every table's column list into one row-per-column export —
  // shared by the Schema DDL step's PDF/Excel download icons below.
  const schemaExportRows = (tables: TableMeta[]) =>
    tables.flatMap((t) =>
      t.columns.map((c) => [
        t.table_name,
        c.name,
        c.data_type,
        c.length ?? "",
        c.precision ?? "",
        c.scale ?? "",
        c.nullable ? "Yes" : "No",
        c.is_primary_key ? "Yes" : "No",
        c.is_foreign_key ? "Yes" : "No",
        c.fk_references ?? "",
      ]),
    );

  // Pulls together everything the wizard already knows about this run —
  // schema stats, mapping coverage, DDL status, load results, DQ findings,
  // reconciliation — into one shareable PDF. Purely a client-side snapshot
  // of existing state; no new data is fetched.
  const handleDownloadMigrationSummary = () => {
    if (!schemaResult) return;
    const doc = new jsPDF({ orientation: "portrait", unit: "pt" });
    const margin = 40;
    const pageHeight = doc.internal.pageSize.getHeight();
    let cursorY = 40;

    const ensureRoom = (needed: number) => {
      if (cursorY + needed > pageHeight - 40) {
        doc.addPage();
        cursorY = 40;
      }
    };
    const sectionHeading = (text: string) => {
      ensureRoom(30);
      doc.setFontSize(13);
      doc.setTextColor(30);
      doc.text(text, margin, cursorY);
      cursorY += 8;
    };

    doc.setFontSize(18);
    doc.setTextColor(0);
    doc.text(
      `Migration Summary — ${schemaResult.schema_name}`,
      margin,
      cursorY,
    );
    cursorY += 18;
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(
      `${schemaResult.db_type.toUpperCase()} · generated ${new Date().toLocaleString()}`,
      margin,
      cursorY,
    );
    doc.setTextColor(0);
    cursorY += 30;

    // Schema overview
    sectionHeading("Schema Overview");
    const totalColumns = schemaResult.tables.reduce(
      (sum, t) => sum + t.columns.length,
      0,
    );
    autoTable(doc, {
      startY: cursorY,
      margin: { left: margin, right: margin },
      body: [
        ["Source tables", String(schemaResult.tables.length)],
        ["Total columns", String(totalColumns)],
        ["Extracted at", new Date(schemaResult.extracted_at).toLocaleString()],
      ],
      styles: { fontSize: 9, cellPadding: 4 },
      theme: "plain",
    });
    cursorY = (doc as any).lastAutoTable.finalY + 20;

    // Mapping coverage
    if (mappings.length > 0) {
      sectionHeading(
        `Mapping Coverage (${mappings.length} of ${schemaResult.tables.length} tables mapped)`,
      );
      autoTable(doc, {
        startY: cursorY,
        margin: { left: margin, right: margin },
        head: [
          ["Source Table", "Target Object", "Fields Mapped", "Confidence"],
        ],
        body: mappings.map((m) => [
          m.source_table,
          m.target_object,
          `${m.field_mappings.filter((fm) => fm.status === "Mapped").length}/${m.field_mappings.length}`,
          `${Math.round(m.confidence * 100)}%`,
        ]),
        headStyles: { fillColor: [40, 40, 60] },
        styles: { fontSize: 8, cellPadding: 4 },
      });
      cursorY = (doc as any).lastAutoTable.finalY + 20;
    }

    // DDL creation status
    if (ddlExecuteResults && ddlExecuteResults.length > 0) {
      const succeeded = ddlExecuteResults.filter((r) => r.status === "success");
      const failed = ddlExecuteResults.filter((r) => r.status === "error");
      sectionHeading(
        `DDL Creation — ${succeeded.length} succeeded, ${failed.length} failed`,
      );
      if (failed.length > 0) {
        autoTable(doc, {
          startY: cursorY,
          margin: { left: margin, right: margin },
          head: [["DDL", "Error"]],
          body: failed.map((r) => [r.ddl.slice(0, 80), r.error ?? ""]),
          headStyles: { fillColor: [90, 30, 30] },
          styles: { fontSize: 7, cellPadding: 4 },
        });
        cursorY = (doc as any).lastAutoTable.finalY + 20;
      } else {
        cursorY += 20;
      }
    }

    // Load results
    if (loadProgress && loadProgress.table_results.length > 0) {
      const label = loadProgress.dry_run
        ? "Validation Results"
        : "Load Results";
      sectionHeading(
        `${label} — ${loadProgress.total_rows_loaded.toLocaleString()} rows ${loadProgress.dry_run ? "would load" : "loaded"}` +
          (loadProgress.total_rows_failed > 0
            ? `, ${loadProgress.total_rows_failed.toLocaleString()} ${loadProgress.dry_run ? "would fail" : "failed"}`
            : ""),
      );
      if (dbLoadElapsedMs > 0) {
        ensureRoom(24);
        cursorY += 8;
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text(
          `Total Load Time: ${formatElapsed(dbLoadElapsedMs)}`,
          margin,
          cursorY,
        );
        doc.setTextColor(0);
        cursorY += 20;
      }
      autoTable(doc, {
        startY: cursorY,
        margin: { left: margin, right: margin },
        head: [
          [
            "Source Table",
            "Target Table",
            "Rows Read",
            "Loaded",
            "Failed",
            "Error",
          ],
        ],
        body: loadProgress.table_results.map((r) => [
          r.source_table,
          r.target_table,
          String(r.rows_read),
          String(r.rows_loaded),
          String(r.rows_failed),
          r.error ?? "",
        ]),
        headStyles: { fillColor: [40, 40, 60] },
        styles: { fontSize: 7, cellPadding: 4 },
        didParseCell: (data) => {
          if (data.section === "body" && data.column.index === 4) {
            const failed = Number(data.cell.raw ?? 0);
            if (failed > 0) data.cell.styles.textColor = [180, 60, 60];
          }
        },
      });
      cursorY = (doc as any).lastAutoTable.finalY + 20;
    } else if (fileLoadResults && fileLoadResults.length > 0) {
      const totalLoaded = fileLoadResults.reduce(
        (n, r) => n + r.rows_loaded,
        0,
      );
      const totalRejected = fileLoadResults.reduce(
        (n, r) => n + r.rows_rejected,
        0,
      );
      sectionHeading(
        `Load Results — ${totalLoaded.toLocaleString()} rows loaded` +
          (totalRejected > 0
            ? `, ${totalRejected.toLocaleString()} rejected`
            : ""),
      );
      if (fileLoadElapsedMs > 0) {
        ensureRoom(24);
        cursorY += 8;
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text(
          `Total Load Time: ${formatElapsed(fileLoadElapsedMs)}`,
          margin,
          cursorY,
        );
        doc.setTextColor(0);
        cursorY += 20;
      }
      autoTable(doc, {
        startY: cursorY,
        margin: { left: margin, right: margin },
        head: [
          [
            "Source Table",
            "Target Table",
            "Source Count",
            "Loaded",
            "Rejected",
            "Status",
          ],
        ],
        body: fileLoadResults.map((r) => [
          r.tableName,
          `${r.schema_name}.${r.table_name}`,
          String(r.rows_read ?? ""),
          String(r.rows_loaded),
          String(r.rows_rejected),
          r.error ?? (r.rows_rejected > 0 ? "Rejects" : "Success"),
        ]),
        headStyles: { fillColor: [40, 40, 60] },
        styles: { fontSize: 7, cellPadding: 4 },
        didParseCell: (data) => {
          if (data.section === "body" && data.column.index === 4) {
            const rejected = Number(data.cell.raw ?? 0);
            if (rejected > 0) data.cell.styles.textColor = [180, 60, 60];
          }
        },
      });
      cursorY = (doc as any).lastAutoTable.finalY + 20;
    }

    if (loadProgress?.dq_report) {
      ensureRoom(24);
      doc.setFontSize(9);
      doc.setTextColor(120);
      doc.text(
        `Data quality report: ${loadProgress.dq_report.filename} (${loadProgress.dq_report.modified_count} values flagged/redacted)`,
        margin,
        cursorY,
      );
      doc.setTextColor(0);
      cursorY += 20;
    }

    // Reconciliation
    const reconcileRows = reconcileMappings
      .map((m) => {
        const tab = reconcileTabState[reconcileTabKey(m)];
        if (!tab || (!tab.countResult && !tab.columnResult)) return null;
        const cr = tab.countResult;
        const colr = tab.columnResult;
        const reviewCount = colr
          ? colr.columns.filter((c) => c.status === "review").length
          : null;
        return [
          m.source_table,
          m.target_object,
          cr ? String(cr.source_count ?? "—") : "—",
          cr ? String(cr.target_count ?? "—") : "—",
          cr ? (cr.match ? "Match" : "Mismatch") : "—",
          reviewCount != null ? `${reviewCount} to review` : "—",
        ];
      })
      .filter((row): row is string[] => row !== null);
    if (reconcileRows.length > 0) {
      sectionHeading("Reconciliation");
      autoTable(doc, {
        startY: cursorY,
        margin: { left: margin, right: margin },
        head: [
          [
            "Source Table",
            "Target Table",
            "Source Count",
            "Target Count",
            "Counts",
            "Columns",
          ],
        ],
        body: reconcileRows,
        headStyles: { fillColor: [40, 40, 60] },
        styles: { fontSize: 8, cellPadding: 4 },
        didParseCell: (data) => {
          if (data.section === "body" && data.column.index === 4) {
            if (String(data.cell.raw) === "Mismatch")
              data.cell.styles.textColor = [180, 60, 60];
          }
        },
      });
      cursorY = (doc as any).lastAutoTable.finalY + 20;
    }

    doc.save(
      `${schemaResult.schema_name}_migration_summary_${filenameTimestamp()}.pdf`,
    );
  };

  const handleCopyDdl = (tableName: string, ddl: string) => {
    navigator.clipboard.writeText(ddl);
    setDdlCopied(tableName);
    setTimeout(() => setDdlCopied(null), 2000);
  };

  // Poll a running Load Data job until it reaches a terminal state. The
  // load itself runs server-side in the background, so this is safe to
  // abandon (e.g. navigating away) without affecting the job. `carryForward`
  // is only passed by a "Retry Failed Tables" run -- that job is scoped to
  // just the retried tables, so its own status never mentions the tables
  // that already succeeded in the original run; folding those back into
  // every poll keeps the results panel showing the full table set instead
  // of just the retried subset (see mergeCarryForwardIntoLoadStatus).
  const pollDataLoadJob = useCallback(
    (jobId: string, carryForward: DataLoadTableResult[] = []) => {
      if (loadPollRef.current) clearInterval(loadPollRef.current);
      loadPollRef.current = setInterval(async () => {
        try {
          const status = await apiv2.converterLoadDataStatus(jobId);
          setLoadProgress(
            mergeCarryForwardIntoLoadStatus(carryForward, status),
          );
          if (status.status !== "running") {
            if (loadPollRef.current) clearInterval(loadPollRef.current);
            loadPollRef.current = null;
            setLoading(false);
            setDbLoadElapsedMs(
              dbLoadStartedAtRef.current
                ? Date.now() - dbLoadStartedAtRef.current
                : 0,
            );
            if (status.status === "failed" && status.errors.length > 0) {
              setErr(status.errors[0]);
            }
            // 2026-09-03, requested by user: no longer auto-advances to
            // Load to Target — the completed summary below now offers
            // explicit "Go to Reconciliation" / "Continue to Load to
            // Target" buttons instead, so the user decides which comes
            // next rather than being carried straight to sf_load.
            if (projectId) {
              apiv2
                .converterSaveLoadHistory(projectId, {
                  job_type: "db_to_db",
                  status: status.status as "completed" | "failed" | "cancelled",
                  summary: status as unknown as Record<string, unknown>,
                })
                .catch(() => {
                  // Best-effort — losing a history entry never blocks the load result itself.
                });
            }
          }
        } catch (e) {
          if (loadPollRef.current) clearInterval(loadPollRef.current);
          loadPollRef.current = null;
          setLoading(false);
          setErr(e instanceof Error ? e.message : "Lost track of the load job");
        }
      }, 1500);
    },
    [projectId],
  );

  // Resolves the effective row limit for one table: its own override if
  // set, otherwise the global Load Options default. null = unlimited.
  const effectiveRowLimit = useCallback(
    (idx: number): number | null => {
      const mode = loadMappingConfig[idx]?.rowLimit ?? "default";
      if (mode === "all") return null;
      if (mode === "limited") {
        return Math.max(1, loadMappingConfig[idx]?.rowCount ?? 100);
      }
      return loadRowLimit === "limited" ? loadRowCount : null;
    },
    [loadMappingConfig, loadRowLimit, loadRowCount],
  );

  // Resolves the effective truncate-before-load flag for one table: its own
  // override if set, otherwise the global Load Options default.
  const effectiveTruncate = useCallback(
    (idx: number): boolean => {
      const mode = loadMappingConfig[idx]?.truncate ?? "default";
      if (mode === "yes") return true;
      if (mode === "no") return false;
      return loadTruncateBeforeLoad;
    },
    [loadMappingConfig, loadTruncateBeforeLoad],
  );

  // Whether table `idx` is included in the next load run. Defaults to true
  // (every table loads) until the user unchecks it in Mapping Summary.
  const effectiveIncluded = useCallback(
    (idx: number): boolean => loadMappingConfig[idx]?.included ?? true,
    [loadMappingConfig],
  );

  // Uploads a file as the data source for one mapped table (loadSourceMode
  // === "files") — auto-detects its format the same way the File Source
  // flow's bulk upload does, then inspects it for column names/types (see
  // converterInspectFile) so the resulting ConversionTask has everything
  // _ensure_fields requires without a manual layout step.
  const handleChooseSourceFileForRow = async (idx: number, file: File) => {
    setError(null);
    try {
      const uploaded = await apiv2.converterUploadFile(file, projectId);
      if (file.name.toLowerCase().endsWith(".pdf")) {
        await _applyPdfSourceFileForRow(idx, uploaded.file_path, file.name);
        return;
      }
      // Detect format from uploaded.filename, NOT file.name -- an Excel
      // workbook picked here gets converted to CSV server-side (see
      // DataConverterService.upload_source_file), so uploaded.filename is
      // "....csv" even though the user selected a ".xlsx" file. Detecting
      // off the original name would misclassify it as "fixed_width" below
      // and reject a file the backend already successfully converted.
      const { category, formatType } = _detectFileSourceCategory(
        uploaded.filename,
      );
      if (uploaded.excel_sheet_used && (uploaded.excel_sheet_count ?? 0) > 1) {
        setErr(
          `Note: "${file.name}" has ${uploaded.excel_sheet_count} sheets — used "${uploaded.excel_sheet_used}" for this table. Export the specific sheet as its own CSV first if that's the wrong one.`,
        );
      }
      if (category === "fixed_width" || category === "copybook") {
        setErr(
          `${file.name}: fixed-width/copybook files aren't supported here — export as CSV, TSV, JSON, JSONL, or Parquet instead.`,
        );
        return;
      }
      const detectedFormat = formatType as Exclude<
        ConverterSourceFormat["format_type"],
        "fixed_width"
      >;
      const inspected = await apiv2.converterInspectFile({
        file_path: uploaded.file_path,
        format_type: detectedFormat,
        encoding: "utf-8",
        has_header: detectedFormat === "csv" || detectedFormat === "tsv",
      });
      setLoadMappingConfig((prev) => {
        const next = [...prev];
        const base = next[idx] ?? {
          srcSchema: "",
          srcTable: mappings[idx]?.source_table ?? "",
          tgtSchema: ddlTargetSchema || "",
          tgtTable: mappings[idx]
            ? stagedTargetTable(mappings[idx].target_object)
            : "",
        };
        next[idx] = {
          ...base,
          sourceFile: {
            file_path: uploaded.file_path,
            filename: uploaded.filename,
          },
          sourceFileFormat: formatType,
          sourceFileFields: inspected.fields,
        };
        return next;
      });
      setFileUploadNotice({
        idx,
        filename: uploaded.filename,
        fieldCount: inspected.fields.length,
      });
      setTimeout(
        () => setFileUploadNotice((cur) => (cur?.idx === idx ? null : cur)),
        4000,
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : `Could not read ${file.name}`);
    }
  };

  // A PDF chosen directly at the Load step isn't itself a loadable format
  // (unlike CSV/TSV/JSON/JSONL/Parquet, it has no self-describing rows to
  // inspect) -- it has to go through the same pdfplumber table extraction
  // the "Generate from Legacy Code" PDF entry point uses (see
  // converterParseLegacyPdf/pdf_files_as_legacy_source), which turns every
  // detected table into its own persisted CSV. A multi-table PDF yields
  // several file_refs entries (one per table); match this row's mapped
  // source_table against the extracted table names the same way
  // legacy_schema_parser derived them (non-alphanumeric runs -> "_",
  // upper-cased) so the right table is picked even when the PDF has more
  // than one.
  const _applyPdfSourceFileForRow = async (
    idx: number,
    filePath: string,
    originalFilename: string,
  ) => {
    let extracted: SchemaExtractResult;
    try {
      // No project_id here -- passing one makes the backend diff/overwrite
      // the project's persisted source-schema snapshot
      // (_diff_and_snapshot_schema), which is the wrong side effect for
      // "just re-read one file for one already-mapped row."
      extracted = await apiv2.converterParseLegacyPdf([filePath]);
    } catch (e) {
      setErr(
        e instanceof Error
          ? e.message
          : `Could not extract tables from ${originalFilename}`,
      );
      return;
    }
    const refs = extracted.file_refs ?? {};
    const wantedTable = (mappings[idx]?.source_table ?? "").toUpperCase();
    const matchName = Object.keys(refs).find(
      (name) =>
        name
          .replace(/\.csv$/i, "")
          .replace(/[^A-Za-z0-9_]+/g, "_")
          .toUpperCase() === wantedTable,
    );
    if (!matchName) {
      const found = Object.keys(refs).join(", ") || "none";
      setErr(
        `${originalFilename}: couldn't find a table matching "${mappings[idx]?.source_table}" (extracted: ${found}).`,
      );
      return;
    }
    const ref = refs[matchName];
    try {
      const inspected = await apiv2.converterInspectFile({
        file_path: ref.file_path,
        format_type: "csv",
        encoding: "utf-8",
        has_header: true,
      });
      setLoadMappingConfig((prev) => {
        const next = [...prev];
        const base = next[idx] ?? {
          srcSchema: "",
          srcTable: mappings[idx]?.source_table ?? "",
          tgtSchema: ddlTargetSchema || "",
          tgtTable: mappings[idx]
            ? stagedTargetTable(mappings[idx].target_object)
            : "",
        };
        next[idx] = {
          ...base,
          sourceFile: { file_path: ref.file_path, filename: ref.filename },
          sourceFileFormat: "csv",
          sourceFileFields: inspected.fields,
        };
        return next;
      });
    } catch (e) {
      setErr(
        e instanceof Error
          ? e.message
          : `Could not read the extracted table from ${originalFilename}`,
      );
    }
  };

  // Auto-fills every mapped table's source file from what was already
  // uploaded and persisted during "Generate from Legacy Code" (see
  // legacyFileRefs/TableObjectMapping.source_file) — the whole point being
  // that a 100+-table crawl never needs its files re-selected one at a
  // time just to reach the Load step. A row with no remembered file (or
  // whose original file wasn't a re-loadable format) is left for manual
  // "Choose File" selection; an already-configured row (sourceFile already
  // set, e.g. from a prior manual choice) is left untouched rather than
  // overwritten.
  const handleAutoFillSourceFilesFromMapping = async () => {
    if (mappings.length === 0) return;
    setAutoFillingSourceFiles(true);
    setError(null);
    try {
      let filled = 0;
      let missing = 0;
      for (let i = 0; i < mappings.length; i++) {
        if (loadMappingConfig[i]?.sourceFile) continue; // already configured
        const sourceFileNames = (mappings[i].source_file ?? "")
          .split(";")
          .map((s) => s.trim())
          .filter(Boolean);
        const ref = sourceFileNames
          .map((name) => legacyFileRefs[name])
          .find((r) => r !== undefined);
        if (!ref) {
          missing++;
          continue;
        }
        const { category, formatType } = _detectFileSourceCategory(
          ref.filename,
        );
        if (category === "fixed_width" || category === "copybook") {
          missing++;
          continue;
        }
        const detectedFormat = formatType as Exclude<
          ConverterSourceFormat["format_type"],
          "fixed_width"
        >;
        try {
          const inspected = await apiv2.converterInspectFile({
            file_path: ref.file_path,
            format_type: detectedFormat,
            encoding: "utf-8",
            has_header: detectedFormat === "csv" || detectedFormat === "tsv",
          });
          setLoadMappingConfig((prev) => {
            const next = [...prev];
            const base = next[i] ?? {
              srcSchema: "",
              srcTable: mappings[i].source_table,
              tgtSchema: ddlTargetSchema || "",
              tgtTable: stagedTargetTable(mappings[i].target_object),
            };
            next[i] = {
              ...base,
              sourceFile: { file_path: ref.file_path, filename: ref.filename },
              sourceFileFormat: detectedFormat,
              sourceFileFields: inspected.fields,
            };
            return next;
          });
          filled++;
        } catch {
          missing++;
        }
      }
      if (filled === 0 && missing === 0) {
        setErr("Every table already has a source file configured.");
      } else if (missing > 0) {
        setErr(
          `Auto-filled ${filled} table(s) from their original files — ${missing} still need a file chosen manually (no remembered file, or an unsupported format).`,
        );
      }
    } finally {
      setAutoFillingSourceFiles(false);
    }
  };

  // The file-mode counterpart to handleLoadData — every included table
  // reads from its own uploaded file (see handleChooseSourceFileForRow)
  // instead of a query against a source database connection, so this needs
  // no sourceConnectionId at all. Submits the whole batch as ONE async job
  // (see pollFileLoadJob) instead of one synchronous HTTP call per table —
  // that job now flushes table_results after every file finishes (see
  // _run_file_bulk_load_job's docstring), so this gets real incremental
  // "N of M done" progress the same way DB-to-DB Load Data does, instead of
  // an all-or-nothing response.
  const handleStartFileBasedLoad = async () => {
    if (!targetConnectionId) {
      setErr("Connect a target database first");
      return;
    }
    if (mappings.length === 0) {
      setErr("No STTM mappings found. Complete the mapping step first.");
      return;
    }
    const includedIdx = mappings
      .map((_, i) => i)
      .filter((i) => effectiveIncluded(i));
    if (includedIdx.length === 0) {
      setErr(
        "No tables selected to load. Check at least one table in Mapping Summary.",
      );
      return;
    }
    const missing = includedIdx.filter(
      (i) => !loadMappingConfig[i]?.sourceFile,
    );
    if (missing.length > 0) {
      setErr(
        `Choose a source file for every selected table (${missing.length} still missing).`,
      );
      return;
    }
    // Snapshot exactly what row limit this run is about to send, for
    // display alongside Load Results below — same mechanism handleLoadData
    // uses for the DB-mode flow, so a stale per-table "Rows:" override
    // (Mapping Summary) is visible here instead of silently truncating a
    // "Load all records" run.
    const resolvedLimits = includedIdx.map((i) => effectiveRowLimit(i));
    const distinctLimits = new Set(resolvedLimits);
    setLastLoadRowLimitSummary(
      distinctLimits.size === 1
        ? resolvedLimits[0] == null
          ? "All records"
          : `First ${resolvedLimits[0].toLocaleString()} records per table`
        : "Mixed per-table row limits (see Mapping Summary overrides)",
    );
    setStartingFileLoad(true);
    setError(null);
    setFileLoadResults([]);
    setFileLoadCancelRequested(false);
    setFileLoadCurrentTable(null);
    setFileLoadTableProgress({});
    const startedAt = Date.now();
    setFileLoadStartedAt(startedAt);
    setFileLoadElapsedMs(0);
    // A real load now exists for this mapping — see skipLoaderResumeCheck's
    // own comment above checkResumable.
    setSkipLoaderResumeCheck(false);
    try {
      // Same Load Order setting DB-mode honors for its own display/FK
      // ordering, now honored server-side too (see stages below) — tasks
      // within one stage run concurrently, stages run one after another.
      // The job forces strictly sequential regardless of this grouping if
      // any task has bulk_load=True (see _run_file_bulk_load_job's
      // docstring on the OOM risk that's specific to); a plain
      // chunked-insert STTM file load (this flow) carries no such risk.
      const entries = includedIdx.map((i) => {
        const m = mappings[i];
        const cfg = loadMappingConfig[i]!;
        const mappedFields = m.field_mappings.filter(
          (fm) =>
            fm.status !== "Unmapped" && fm.source_column && fm.target_field,
        );
        const task: ConverterTask = {
          source: {
            path: cfg.sourceFile!.file_path,
            format_type: cfg.sourceFileFormat ?? "csv",
            encoding: "utf-8",
            has_header: true,
            fields: cfg.sourceFileFields ?? [],
            row_limit: effectiveRowLimit(i) ?? undefined,
          },
          mappings: mappedFields.map((fm) => ({
            source: fm.source_column,
            target: fm.target_field,
            target_type: "TEXT",
          })),
          target: {
            table_name: cfg.tgtTable,
            schema_name: cfg.tgtSchema,
            if_exists: effectiveTruncate(i) ? "truncate" : "append",
            chunk_size: 5000,
          },
        };
        return { task, tableName: cfg.tgtTable };
      });
      // computeLoadOrderForIndices returns stages as groups of POSITIONS
      // within includedIdx -- entries is built from includedIdx in the
      // same order, so those positions already line up with entries too.
      const stages = computeLoadOrderForIndices(includedIdx);
      const targetRef = { connectionId: targetConnectionId, projectId };
      const { job_id } = await apiv2.converterLoadFileBatchAsync(
        entries.map((e) => e.task),
        targetRef,
        stages,
      );
      setFileLoadJobId(job_id);
      pollFileLoadJob(job_id, entries, startedAt);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "File-based load failed");
      setStartingFileLoad(false);
      setFileLoadElapsedMs(Date.now() - startedAt);
    }
  };

  // Poll a running file-mode load job until it reaches a terminal state,
  // translating its DataLoadStatusResponse.table_results (appended to as
  // each file finishes — see _run_file_bulk_load_job) into fileLoadResults'
  // shape so the existing progress bar / results table JSX (built for the
  // old synchronous per-table flow) renders unchanged, just now updating
  // incrementally as the job progresses instead of jumping straight from
  // "running" to "every table done" in one step. Matched by table name
  // (source_table/target_table are both the target table name for a
  // file-mode task -- see _run_file_bulk_load_job) rather than by array
  // position, since tasks within a load-order stage now run concurrently
  // server-side and can complete in any order within that stage.
  const pollFileLoadJob = useCallback(
    (
      jobId: string,
      entries: { task: ConverterTask; tableName: string }[],
      startedAt: number,
    ) => {
      const byName = new Map(entries.map((e) => [e.tableName, e]));
      if (fileLoadPollRef.current) clearInterval(fileLoadPollRef.current);
      fileLoadPollRef.current = setInterval(async () => {
        try {
          const status = await apiv2.converterLoadDataStatus(jobId);
          setFileLoadResults(
            status.table_results.map((r) => {
              const entry = byName.get(r.target_table);
              if (r.error) {
                return {
                  tableName: entry?.tableName ?? r.target_table,
                  error: r.error,
                  table_name: entry?.task.target.table_name ?? r.target_table,
                  schema_name: entry?.task.target.schema_name ?? "",
                  rows_loaded: 0,
                  chunks_loaded: 0,
                  columns_loaded: [],
                  rows_rejected: r.rows_failed,
                  reject_file: null,
                  durationMs: r.duration_ms ?? undefined,
                };
              }
              return {
                tableName: entry?.tableName ?? r.target_table,
                table_name: entry?.task.target.table_name ?? r.target_table,
                schema_name: entry?.task.target.schema_name ?? "",
                rows_loaded: r.rows_loaded,
                rows_read: r.rows_read,
                chunks_loaded: 0,
                columns_loaded: [],
                rows_rejected: r.rows_failed,
                reject_file: r.reject_file ?? null,
                reject_reasons: r.reject_reasons ?? [],
                durationMs: r.duration_ms ?? undefined,
              };
            }),
          );
          setFileLoadCurrentTable(status.current_table ?? null);
          setFileLoadTableProgress(status.table_progress ?? {});
          if (status.status !== "running") {
            if (fileLoadPollRef.current) clearInterval(fileLoadPollRef.current);
            fileLoadPollRef.current = null;
            setStartingFileLoad(false);
            setFileLoadCancelRequested(false);
            setFileLoadCurrentTable(null);
            setFileLoadTableProgress({});
            setFileLoadElapsedMs(Date.now() - startedAt);
            // Only surface this job-level error when no per-table row
            // already shows it -- otherwise the same failure (e.g. a
            // single-table load) renders twice: once here, once in the
            // per-table results breakdown below.
            if (
              status.status === "failed" &&
              status.errors.length > 0 &&
              status.table_results.length === 0
            ) {
              setErr(status.errors[0]);
            }
          }
        } catch (e) {
          if (fileLoadPollRef.current) clearInterval(fileLoadPollRef.current);
          fileLoadPollRef.current = null;
          setStartingFileLoad(false);
          setFileLoadCancelRequested(false);
          setErr(e instanceof Error ? e.message : "Lost track of the load job");
        }
      }, 1500);
    },
    [],
  );

  // Ticks the file-load elapsed timer once a second while a load is in
  // progress, so "is this actually still running or just stuck?" always
  // has a visible, self-updating answer instead of a static spinner.
  useEffect(() => {
    if (!startingFileLoad || fileLoadStartedAt === null) return;
    const id = setInterval(
      () => setFileLoadElapsedMs(Date.now() - fileLoadStartedAt),
      1000,
    );
    return () => clearInterval(id);
  }, [startingFileLoad, fileLoadStartedAt]);

  // "1m 42s" / "8s" — never raw milliseconds in the UI.
  const formatElapsed = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  };

  // Ticks the DB-to-DB load elapsed timer once a second while the job is
  // running; frozen (see pollDataLoadJob) once it reaches a terminal state.
  useEffect(() => {
    if (
      loadProgress?.status !== "running" ||
      dbLoadStartedAtRef.current === null
    ) {
      return;
    }
    const startedAt = dbLoadStartedAtRef.current;
    const id = setInterval(
      () => setDbLoadElapsedMs(Date.now() - startedAt),
      1000,
    );
    return () => clearInterval(id);
  }, [loadProgress?.status]);

  // Builds the load_order groups to send to the backend from loadOrderMode,
  // for a given list of *original* mapping indices (e.g. only the tables the
  // user left checked in Mapping Summary). Group positions are relative to
  // that filtered list, since that's the array actually sent to the backend.
  // sequential -> FK/lookup-dependency-ordered one-table-per-stage (falls
  // back to declaration order when there's no dependency info to sort by
  // -- see computeFkAwareSequentialOrder); parallel -> a single stage with
  // every table; staged -> tables grouped by their per-table Load Stage
  // number, stages ascending.
  const computeLoadOrderForIndices = useCallback(
    (idxs: number[]): number[][] | undefined => {
      if (loadOrderMode === "sequential")
        return computeFkAwareSequentialOrder(idxs, mappings, sfObjects);
      if (loadOrderMode === "parallel") {
        return [idxs.map((_, pos) => pos)];
      }
      const stages = new Map<number, number[]>();
      idxs.forEach((origIdx, pos) => {
        const stage = loadMappingConfig[origIdx]?.loadStage ?? origIdx + 1;
        if (!stages.has(stage)) stages.set(stage, []);
        stages.get(stage)!.push(pos);
      });
      return Array.from(stages.keys())
        .sort((a, b) => a - b)
        .map((stage) => stages.get(stage)!);
    },
    [loadOrderMode, loadMappingConfig, mappings, sfObjects],
  );

  const handleLoadData = async () => {
    if (!sourceConnectionId || !targetConnectionId) return;
    if (mappings.length === 0) {
      setErr("No STTM mappings found. Complete the mapping step first.");
      return;
    }
    // A complex (multi-source-table) mapping with no join conditions at all
    // has no way to build a FROM clause that includes every table — that's
    // the one shape _run_data_load_job genuinely can't execute (it would
    // silently fall back to the "hub" table alone). A mapping with joins
    // defined loads fine now — the backend builds and executes the real
    // join query — so only flag the unexecutable no-joins case here.
    const unjoinableTables = mappings
      .filter(
        (m) =>
          (m.source_tables?.length ?? 0) > 1 && (m.joins?.length ?? 0) === 0,
      )
      .map((m) => m.source_tables!.join(" ⋈ "));
    if (unjoinableTables.length > 0) {
      setErr(
        `These mappings combine multiple source tables but have no join defined — regenerate them: ${unjoinableTables.join(", ")}`,
      );
      return;
    }
    // Only send tables the user left checked in Mapping Summary — unchecked
    // tables keep their mapping/config but are skipped for this run.
    const includedIdx = mappings
      .map((_, i) => i)
      .filter((i) => effectiveIncluded(i));
    if (includedIdx.length === 0) {
      setErr(
        "No tables selected to load. Check at least one table in Mapping Summary.",
      );
      return;
    }
    // Snapshot exactly what row limit this run is about to send, for
    // display in Load Results below — computed fresh from current state
    // right before the request, so it always reflects what was actually
    // submitted (not what was set on some earlier run).
    const resolvedLimits = includedIdx.map((i) => effectiveRowLimit(i));
    const distinctLimits = new Set(resolvedLimits);
    setLastLoadRowLimitSummary(
      distinctLimits.size === 1
        ? resolvedLimits[0] == null
          ? "All records"
          : `First ${resolvedLimits[0].toLocaleString()} records per table`
        : "Mixed per-table row limits (see Mapping Summary overrides)",
    );
    setLoading(true);
    setError(null);
    setLoadProgress(null);
    setLoadJobId(null);
    setDqReportDismissed(false);
    setLoadErrorBannerDismissed(false);
    setLoadResultsCleared(false);
    dbLoadStartedAtRef.current = Date.now();
    setDbLoadElapsedMs(0);
    try {
      const { job_id } = await apiv2.converterLoadData(
        { connectionId: sourceConnectionId, projectId },
        { connectionId: targetConnectionId, projectId },
        includedIdx.map((i) => mappings[i]),
        ddlTargetSchema || "",
        loadMappingConfig.length === mappings.length
          ? includedIdx.map((i) => loadMappingConfig[i])
          : undefined,
        loadRowLimit === "limited" ? loadRowCount : undefined,
        includedIdx.map((i) => effectiveRowLimit(i)),
        computeLoadOrderForIndices(includedIdx),
        loadTruncateBeforeLoad,
        includedIdx.map((i) => effectiveTruncate(i)),
        loadDryRun,
        projectId,
        activeTargetPlatform,
        loadTruncateCascade,
      );
      setLoadJobId(job_id);
      // A real load now exists for this mapping — future "loader" resumable
      // checks this session should be able to find it again (see
      // skipLoaderResumeCheck's own comment).
      setSkipLoaderResumeCheck(false);
      pollDataLoadJob(job_id);
    } catch (e) {
      setLoading(false);
      setErr(e instanceof Error ? e.message : "Data load failed to start");
    }
  };

  const handleCancelDataLoad = async () => {
    if (!loadJobId) return;
    try {
      const status = await apiv2.converterLoadDataCancel(loadJobId);
      setLoadProgress(status);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to cancel the load");
    }
  };

  const handleCancelFileLoad = async () => {
    if (!fileLoadJobId) return;
    setFileLoadCancelRequested(true);
    try {
      await apiv2.converterLoadDataCancel(fileLoadJobId);
    } catch (e) {
      setFileLoadCancelRequested(false);
      setErr(e instanceof Error ? e.message : "Failed to cancel the load");
    }
  };

  // Maps a load result back to its original `mappings` index by matching on
  // the effective source/target table names — table_results can complete
  // out of index order when tables load in parallel stages, so this can't
  // just use the result's position in the array.
  const findMappingIndexForResult = (r: DataLoadTableResult): number =>
    mappings.findIndex((m, i) => {
      const srcTable = loadMappingConfig[i]?.srcTable || m.source_table;
      const tgtTable =
        loadMappingConfig[i]?.tgtTable || stagedTargetTable(m.target_object);
      return srcTable === r.source_table && tgtTable === r.target_table;
    });

  // Re-runs only the tables that failed in the last load, sequentially in
  // their original order. Each failed table is truncated first — a table
  // can fail partway through a batch, leaving some rows already committed,
  // so retrying without truncating first could double-load those rows.
  const handleRetryFailedTables = async () => {
    if (!sourceConnectionId || !targetConnectionId || !loadProgress) return;
    const { failedIdx, unmatchedCount, unmatchedResults } =
      resolveFailedTableRetryPlan(
        loadProgress.table_results,
        findMappingIndexForResult,
      );
    if (failedIdx.length === 0) {
      if (unmatchedCount > 0) {
        setErr(
          `Could not match ${unmatchedCount} failed table(s) back to a mapping -- nothing to retry. Re-run the full load instead.`,
        );
      }
      return;
    }

    setLoading(true);
    setError(
      unmatchedCount > 0
        ? `${unmatchedCount} failed table(s) could not be matched back to a mapping and were skipped; retrying the remaining ${failedIdx.length}.`
        : null,
    );
    // Tables that already succeeded, and failed tables that couldn't even
    // be matched back to a mapping (so this retry job never covers them
    // either), aren't part of this retry job at all — carry both forward
    // manually, otherwise polling the new (retry-scoped) job would make
    // them vanish from the results panel entirely.
    const carryForward = [
      ...loadProgress.table_results.filter((r) => !r.error),
      ...unmatchedResults,
    ];
    setLoadProgress(null);
    setLoadJobId(null);
    setDqReportDismissed(false);
    setLoadErrorBannerDismissed(false);
    setLoadResultsCleared(false);
    dbLoadStartedAtRef.current = Date.now();
    setDbLoadElapsedMs(0);
    try {
      const { job_id } = await apiv2.converterLoadData(
        { connectionId: sourceConnectionId, projectId },
        { connectionId: targetConnectionId, projectId },
        failedIdx.map((i) => mappings[i]),
        ddlTargetSchema || "",
        loadMappingConfig.length === mappings.length
          ? failedIdx.map((i) => loadMappingConfig[i])
          : undefined,
        loadRowLimit === "limited" ? loadRowCount : undefined,
        failedIdx.map((i) => effectiveRowLimit(i)),
        undefined, // sequential, in original relative order
        loadTruncateBeforeLoad,
        failedIdx.map(() => true),
        loadProgress.dry_run ?? false,
        projectId,
        activeTargetPlatform,
        loadTruncateCascade,
      );
      setLoadJobId(job_id);
      // Seed the panel with the carried-forward rows immediately, rather
      // than waiting for the first poll tick — otherwise the previously
      // successful table briefly shows as "Queued" alongside the table
      // actually being retried, which reads as if everything is reloading.
      setLoadProgress(
        mergeCarryForwardIntoLoadStatus(carryForward, {
          job_id,
          status: "running",
          total_tables: failedIdx.length,
          tables_done: 0,
          current_table: null,
          total_rows_loaded: 0,
          total_rows_failed: 0,
          table_results: [],
          table_progress: {},
          dq_report: null,
          errors: [],
          cancel_requested: false,
          dry_run: loadProgress.dry_run,
        }),
      );
      pollDataLoadJob(job_id, carryForward);
    } catch (e) {
      setLoading(false);
      setErr(e instanceof Error ? e.message : "Retry failed to start");
    }
  };

  // ── SF Load handlers ─────────────────────────────────────────────────────
  // All operate on the mapping tab passed in (defaulting to whichever tab is
  // currently active), so a fetch/load/cancel always targets the right tab
  // even if the user has since switched away from it.

  // Re-sync this step's connections: this tab reads ddlTargetConn (the staged
  // DB, set in Create Tables) and sfConn (GovConnect, set in the GovConnect
  // step) directly rather than a private snapshot, so they're always live —
  // but the previously-fetched object metadata/field mappings for this tab
  // were computed against whatever those connections held at fetch time.
  // Drop the remembered localStorage edits (so a fix made elsewhere isn't
  // shadowed by a stale saved value) and clear this tab's fetched state so a
  // fresh "Load Fields & Auto-Map" is required before loading.
  const handleRefreshSfLoadConnection = (
    mapping: TableObjectMapping | undefined = activeSfLoadMapping,
  ) => {
    if (!mapping) return;
    clearConn("ddlTargetConn");
    clearConn("sfConn");
    updateSfLoadTab(sfLoadTabKey(mapping), {
      objectMeta: null,
      fieldMappings: [],
      error: null,
      result: null,
      jobId: null,
      progress: null,
    });
    setSfLoadConnRefreshed(true);
    setTimeout(() => setSfLoadConnRefreshed(false), 2000);
  };

  const handleSfLoadFetchObjectFields = async (
    mapping: TableObjectMapping | undefined = activeSfLoadMapping,
  ) => {
    if (!mapping) return;
    const key = sfLoadTabKey(mapping);
    const obj = mapping.target_object;
    updateSfLoadTab(key, {
      loading: true,
      error: null,
      objectMeta: null,
      fieldMappings: [],
    });
    try {
      const res = await apiv2.converterSalesforceObjects(
        sfConn,
        [obj],
        true,
        projectId,
      );
      const meta: SalesforceObjectMeta | undefined = res.objects.find(
        (o) => o.api_name === obj,
      );
      if (!meta) throw new Error("Could not load object metadata");

      // Load from the staging table created in the Create Tables step (in
      // the DDL target connection/schema), NOT the original source table
      // — that table holds the raw source data, while the staging table
      // holds the already-transformed rows ready for Salesforce. Re-scan
      // rather than trusting the cached schemaResult: the table may have
      // been dropped and recreated (e.g. re-running Create Tables against
      // an updated STTM) since the last scan, and DDL target-field names
      // can legitimately differ between STTM generations. Using a stale
      // column list here would submit a SELECT referencing a column that
      // no longer exists, failing with ORA-00904.
      //
      // 2026-09-01, requested by user: in "direct" mode there IS no staging
      // table at all — scan the real source connection/schema/table
      // instead (mapping.source_table, never staged suffixed) so the same
      // "Load Fields & Auto-Map" flow works for both strategies.
      const isDirect = crmLoadStrategy === "direct";
      const scanConnectionId = isDirect
        ? sourceConnectionId
        : targetConnectionId;
      if (!scanConnectionId)
        throw new Error(
          isDirect
            ? "Connect to a source database first"
            : "Connect to a target database first",
        );
      const scanSchema = isDirect
        ? sourceSchemaName || mapping.source_schema || ""
        : ddlTargetSchema || targetSchemaName || "";
      // 2026-09-02, requested by user: the tab's own confirmed/overridden
      // table name always wins over the auto-computed default — critical
      // when source and staging are the same physical connection/schema,
      // where trusting the computed name silently risks reading the real
      // source table instead of the staging one.
      const sfTableName =
        getSfLoadTab(key).sourceTable ||
        computeSfLoadSourceTableDefault(mapping);
      // Only scan this one table instead of the whole schema — scanning
      // every table (PK/FK/columns/row-count, 4 round trips each) was what
      // made this button slow/stuck on schemas with many tables.
      const freshSchema = await apiv2.converterSourceScan(
        { connectionId: scanConnectionId, projectId },
        scanSchema,
        [sfTableName],
      );
      const tbl = freshSchema.tables.find((t) => t.table_name === sfTableName);
      if (tbl) {
        const srcCols = tbl.columns
          .map((c: any) => (c.name ?? c.column_name) as string)
          .filter(Boolean);
        const sfFields = meta.fields.map((f) => f.api_name);
        const defaultValueMaps: Record<string, Record<string, string>> = {
          ITK_Gender__c: { M: "Male", F: "Female", U: "Unknown" },
          ITK_Sex__c: { M: "Male", F: "Female", U: "Unknown" },
        };

        // Prefer the target fields already curated in this mapping (as last
        // edited in the STTM Review and Export step). Only fields still
        // present on the object are used — anything else falls back to the
        // name-similarity guess below.
        const sttmBySourceColumn = new Map(
          mapping.field_mappings
            .filter((fm) => fm.status !== "Unmapped" && fm.target_field)
            .map((fm) => [fm.source_column, fm.target_field]),
        );

        const mapped = srcCols.map((col) => {
          const sttmField = sttmBySourceColumn.get(col);
          const colNorm = col.toUpperCase().replace(/_/g, "");
          const nameMatch = sfFields.find(
            (f) =>
              f.toUpperCase().replace(/_/g, "").replace(/C$/, "") === colNorm ||
              f.toUpperCase() === col.toUpperCase(),
          );
          const match =
            sttmField && sfFields.includes(sttmField) ? sttmField : nameMatch;
          return {
            source_column: col,
            sf_field: match ?? "",
            value_map: match
              ? (defaultValueMaps[match] ?? undefined)
              : undefined,
          };
        });
        updateSfLoadTab(key, { objectMeta: meta, fieldMappings: mapped });
      } else {
        // Silently leaving fieldMappings empty here made this look like the
        // button "did nothing" — most commonly hit right after importing an
        // existing STTM workbook, which only loads mapping data and does NOT
        // create the staging tables in the target DB, so there's nothing
        // yet for this scan to find.
        throw new Error(
          isDirect
            ? `Source table "${sfTableName}" not found in schema "${scanSchema}" on the connected source database.`
            : `Staging table "${sfTableName}" not found in schema "${scanSchema}" — run "Create Staging Tables" for this mapping before loading.`,
        );
      }
    } catch (e) {
      updateSfLoadTab(key, {
        error:
          e instanceof Error ? e.message : "Failed to fetch SF object fields",
      });
    } finally {
      updateSfLoadTab(key, { loading: false });
    }
  };

  // Poll a running load job until it reaches a terminal state, updating that
  // tab's progress as it goes. The load itself runs server-side in the
  // background (see /sf/load-from-db), so this is safe to abandon (e.g.
  // switching tabs or navigating away) without affecting the job.
  const pollSfLoadJob = useCallback(
    (key: string, jobId: string) => {
      if (sfLoadPollRefs.current[key]) {
        clearInterval(sfLoadPollRefs.current[key]);
      }
      sfLoadPollRefs.current[key] = setInterval(async () => {
        try {
          const status = await apiv2.converterLoadDbToSfStatus(jobId);
          updateSfLoadTab(key, { progress: status });
          if (status.status !== "running") {
            clearInterval(sfLoadPollRefs.current[key]);
            delete sfLoadPollRefs.current[key];
            updateSfLoadTab(key, {
              result: {
                rows_read: status.rows_read,
                rows_loaded: status.rows_loaded,
                rows_failed: status.rows_failed,
                errors: status.errors,
              },
              loading: false,
              error:
                status.status === "failed" && status.errors.length > 0
                  ? status.errors[0]
                  : null,
            });
            if (projectId) {
              apiv2
                .converterSaveFinalLoadHistory(projectId, {
                  target_type: sfConn.target_type,
                  status: status.status as "completed" | "failed" | "cancelled",
                  summary: status as unknown as Record<string, unknown>,
                })
                .catch(() => {
                  // Best-effort — losing a history entry never blocks the load result itself.
                });
            }
          }
        } catch (e) {
          clearInterval(sfLoadPollRefs.current[key]);
          delete sfLoadPollRefs.current[key];
          updateSfLoadTab(key, {
            loading: false,
            error:
              e instanceof Error ? e.message : "Lost track of the load job",
          });
        }
      }, 1500);
    },
    [updateSfLoadTab, projectId, sfConn.target_type],
  );

  const handleSfLoad = async (
    mapping: TableObjectMapping | undefined = activeSfLoadMapping,
  ) => {
    if (!mapping) return;
    const key = sfLoadTabKey(mapping);
    const tab = getSfLoadTab(key);
    const activeMappings = tab.fieldMappings.filter((m) => m.sf_field);
    if (activeMappings.length === 0) {
      updateSfLoadTab(key, { error: "No field mappings defined." });
      return;
    }
    updateSfLoadTab(key, {
      loading: true,
      error: null,
      result: null,
      progress: null,
      jobId: null,
    });
    // 2026-09-01, requested by user: "direct" mode reads straight from the
    // real source connection/table instead of the staging DB — no ddl/load
    // steps ever ran, so there's no staging table to read from at all.
    const isDirect = crmLoadStrategy === "direct";
    const loadConnectionId = isDirect ? sourceConnectionId : targetConnectionId;
    if (!loadConnectionId) {
      updateSfLoadTab(key, {
        loading: false,
        error: isDirect
          ? "Connect to a source database first"
          : "Connect to a target database first",
      });
      return;
    }
    try {
      const { job_id } = await apiv2.converterLoadDbToSf({
        sourceConnectionRef: { connectionId: loadConnectionId, projectId },
        sfConnection: sfConn,
        sourceSchema: isDirect
          ? tab.sourceSchema || sourceSchemaName || mapping.source_schema || ""
          : tab.sourceSchema || ddlTargetSchema || targetSchemaName || "",
        // 2026-09-02, requested by user: same override-wins-over-computed
        // rule as handleSfLoadFetchObjectFields — confirms/preserves
        // exactly which table the final load actually reads from.
        sourceTable:
          tab.sourceTable || computeSfLoadSourceTableDefault(mapping),
        sfObject: mapping.target_object,
        // 2026-09-03, reported by user: a source Y/N flag column loaded
        // verbatim into a Salesforce checkbox field failed with "Cannot
        // deserialize instance of boolean from VALUE_STRING value Y" —
        // sf_type (the target field's own describe() type, already fetched
        // into tab.objectMeta by "Load Fields & Auto-Map") lets the backend
        // convert Y/N-style values to a real boolean only for fields that
        // actually need it.
        fieldMappings: activeMappings.map((m) => ({
          ...m,
          value_map: m.value_map ?? undefined,
          sf_type: tab.objectMeta?.fields.find((f) => f.api_name === m.sf_field)
            ?.data_type,
        })),
        rowLimit: tab.rowLimit === "limited" ? tab.rowCount : undefined,
        operation: tab.operation,
        upsertKey: tab.operation === "upsert" ? tab.upsertKey : undefined,
        projectId,
      });
      updateSfLoadTab(key, { jobId: job_id });
      pollSfLoadJob(key, job_id);
    } catch (e) {
      updateSfLoadTab(key, {
        loading: false,
        error: e instanceof Error ? e.message : "SF load failed to start",
      });
    }
  };

  const handleCancelSfLoad = async (
    mapping: TableObjectMapping | undefined = activeSfLoadMapping,
  ) => {
    if (!mapping) return;
    const key = sfLoadTabKey(mapping);
    const jobId = getSfLoadTab(key).jobId;
    if (!jobId) return;
    try {
      const status = await apiv2.converterLoadDbToSfCancel(jobId);
      updateSfLoadTab(key, { progress: status });
    } catch (e) {
      updateSfLoadTab(key, {
        error: e instanceof Error ? e.message : "Failed to cancel the load",
      });
    }
  };

  // Dismiss the active tab's Load Results panel (rows/errors from its last
  // run) without touching its field mapping — lets the user clear a failed
  // attempt's output before retrying.
  const clearSfLoadResults = (
    mapping: TableObjectMapping | undefined = activeSfLoadMapping,
  ) => {
    if (!mapping) return;
    const key = sfLoadTabKey(mapping);
    updateSfLoadTab(key, { result: null, progress: null, jobId: null });
  };

  // ── Reconciliation handlers ───────────────────────────────────────────────
  // Source side reads the raw source table (sourceConnectionId); target side
  // reads the staging table created in Create Tables
  // (targetConnectionId/ddlTargetSchema) — same connection pairing as the
  // Load Data step.
  const buildReconcilePair = (
    mapping: TableObjectMapping,
    tab: ReconcileTabState,
  ) => {
    // Carry over the MDM Lookup config (if any) from the matching Load Data
    // row — matched by table name rather than array index, since
    // reconcileMappings may be editedMappings while loadMappingConfig is
    // indexed against the base mappings array.
    const loadIdx = mappings.findIndex(
      (m) =>
        m.source_table === mapping.source_table &&
        m.target_object === mapping.target_object,
    );
    const mdmLookup =
      loadIdx >= 0 ? loadMappingConfig[loadIdx]?.mdmLookup : undefined;
    return {
      srcSchema:
        tab.sourceSchema || mapping.source_schema || sourceSchemaName || "",
      srcTable: mapping.source_table,
      tgtSchema: tab.targetSchema || ddlTargetSchema || targetSchemaName || "",
      tgtTable: stagedTargetTable(mapping.target_object),
      ...(mdmLookup?.lookupTable
        ? {
            mdmLookupSchema: mdmLookup.lookupSchema,
            mdmLookupTable: mdmLookup.lookupTable,
            ...(mdmLookup.connection
              ? {
                  mdmLookupConnectionJson: JSON.stringify(mdmLookup.connection),
                }
              : {}),
          }
        : {}),
    };
  };

  // File-mode reconciliation has no source DB connection to query against
  // — the source side instead reads the exact same uploaded file the
  // Loader step already remembers for this table. loadMappingConfig only
  // ever gets filled in by visiting the Loader step (manually or via its
  // "Auto-Fill" button) in THIS session, which — same bug class as the
  // project-wide file-sourced flag fixed above — left every row here with
  // no file at all when Reconciliation is resumed directly. Falls back to resolving
  // straight from legacyFileRefs (the project's persisted schema snapshot)
  // via mapping.source_file, the same lookup handleAutoFillSourceFilesFromMapping
  // already does, so a visit to the Loader step is never required just to
  // reconcile. null only when truly no file is known for this table.
  const buildFileReconcileSourceFile = (
    mapping: TableObjectMapping,
  ): FileReconcileSourceFile | null => {
    const loadIdx = mappings.findIndex(
      (m) =>
        m.source_table === mapping.source_table &&
        m.target_object === mapping.target_object,
    );
    const cfg = loadIdx >= 0 ? loadMappingConfig[loadIdx] : undefined;
    if (cfg?.sourceFile) {
      return {
        path: cfg.sourceFile.file_path,
        format_type: cfg.sourceFileFormat ?? "csv",
        encoding: "utf-8",
        has_header: true,
        fields: cfg.sourceFileFields ?? [],
      };
    }
    const candidateNames = (mapping.source_file ?? "")
      .split(";")
      .map((s) => s.trim())
      .filter(Boolean);
    const ref = candidateNames
      .map((name) => legacyFileRefs[name])
      .find((r) => r !== undefined);
    if (!ref) return null;
    const { formatType } = _detectFileSourceCategory(ref.filename);
    if (formatType === "fixed_width") return null; // never reconcilable this way
    return {
      path: ref.file_path,
      format_type: formatType,
      encoding: "utf-8",
      has_header: true,
      fields: [],
    };
  };

  // Best-effort auto-save of reconciliation results, so re-entering the
  // Reconciliation action later can offer to resume. The persisted shape
  // is deliberately the same per-tab-keyed record reconcileTabState
  // already uses (not a single flat result) — reconciliation runs
  // independently per mapped table, so "resume" needs every tab's last
  // result, not just one.
  const persistReconciliationState = (
    patches: { key: string; patch: Partial<ReconcileTabState> }[],
  ) => {
    if (!projectId) return;
    const merged: Record<string, ReconcileTabState> = { ...reconcileTabState };
    for (const { key, patch } of patches) {
      merged[key] = { ...(merged[key] ?? DEFAULT_RECONCILE_TAB), ...patch };
    }
    const counts_result: Record<string, unknown> = {};
    const columns_result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(merged)) {
      if (v.countResult) counts_result[k] = v.countResult;
      if (v.columnResult) columns_result[k] = v.columnResult;
    }
    apiv2
      .converterSetReconciliation(projectId, { counts_result, columns_result })
      .catch(() => {});
  };

  // Mapping's own resume path — the user picks one specific saved entry
  // from the list (not a single yes/no choice like the other 3 actions),
  // so only that entry's full mappings are fetched, on demand, once chosen.
  const [resumingMappingEntry, setResumingMappingEntry] = useState(false);
  const handleResumeMappingEntry = async (historyId: string) => {
    if (!projectId) return;
    setResumingMappingEntry(true);
    try {
      const entry = await apiv2.converterGetMappingHistoryEntry(
        projectId,
        historyId,
      );
      setPendingResumeChoice(null);
      setAction("mapping");
      setMappings(entry.mappings);
      setEditedMappings(
        withDefaultStringTypes(JSON.parse(JSON.stringify(entry.mappings))),
      );
      setMappingEntryChoice("create");
      setStep("mapping");
    } catch (e) {
      setErr(
        e instanceof Error
          ? e.message
          : "Failed to load that saved mapping — try again.",
      );
    } finally {
      setResumingMappingEntry(false);
    }
  };

  // "Resume" — hydrate state from whatever checkResumable already fetched
  // and jump straight to the relevant step, skipping reconnect/rescan.
  // Mapping isn't handled here — it offers a list of saves to pick from
  // instead of one yes/no choice, see handleResumeMappingEntry above.
  const handleResumeChoice = () => {
    const info = pendingResumeChoice;
    if (!info) return;
    const a = info.action;
    setPendingResumeChoice(null);
    setAction(a);

    if (a === "loader") {
      // 2026-09-01, requested by user: any resumable loader state predates
      // "direct" load and was always staged — never re-ask the staged-vs-
      // direct question for a project that's already mid-flow.
      //
      // 2026-09-07, requested by user: except now RDBMS targets can ALSO
      // go direct, and a direct RDBMS session never creates a ddlState row
      // at all (no staging tables are ever generated for it) — the same
      // signal resolveLoaderResumeStep already uses below. Forcing
      // "staged" for that case would make stagedTargetTable resolve back
      // to the (never-created) suffixed table name on resume instead of
      // the real one the direct load actually wrote to.
      const resumedStrategy: "staged" | "direct" =
        targetPlatformMode === "rdbms" && !info.ddlState && !!info.loadJob
          ? "direct"
          : "staged";
      setCrmLoadStrategy(resumedStrategy);
      // Resumable loader state only ever exists for the mapped flow (file
      // loads have no ddlState/loadJob to resume) — skip the entry choice.
      setLoaderEntryChoice("mapped");
      if (info.ddlState) {
        setDdlTargetSchema(info.ddlState.target_schema || "");
        setDdlStatements(info.ddlState.ddl_statements);
        setDdlDuplicates(info.ddlState.duplicate_report);
        setDdlDuplicateDecisions({});
        // Restore the real mode this project was set up under (e.g.
        // "mirror") -- without this, a resumed mirror project still shows
        // Reconciliation's generic "Continue to Load to Target" instead of
        // "Map & Load to a Target", since targetPlatformMode otherwise only
        // ever gets inferred from the saved target-DB connection (see
        // refreshTargetConnectionFromSaved), which mirror projects have too.
        if (info.ddlState.target_platform_mode) {
          setTargetPlatformMode(info.ddlState.target_platform_mode);
        }
      }
      // A project can have a resumable sfLoadJob with no ddlState row at
      // all (see the per-action hydration effect's own comment above) —
      // the job's own request_meta.source_schema is the staging schema it
      // actually read from, so fall back to it before leaving this blank.
      if (!info.ddlState?.target_schema && info.sfLoadJob?.source_schema) {
        setDdlTargetSchema(info.sfLoadJob.source_schema);
      }
      if (info.loadJob) {
        setLoadJobId(info.loadJob.job_id);
        if (info.loadStatus) setLoadProgress(info.loadStatus);
        if (info.loadStatus?.status === "running") {
          setLoading(true);
          // True start time isn't tracked server-side — resuming mid-job
          // can only start the stopwatch from now, not the job's actual start.
          dbLoadStartedAtRef.current = Date.now();
          setDbLoadElapsedMs(0);
          pollDataLoadJob(info.loadJob.job_id);
        }
      }
      if (info.sfLoadJob) {
        // Same hydration the old standalone "load_target" resume branch
        // did — the job's own status/result is the valuable part to
        // surface immediately; matching it back to the exact tab it came
        // from would need request_meta round-tripped through the API, so
        // this shows it against the first mapped table's tab and lets the
        // user re-pick the right one if there's more than one.
        const key = sfLoadMappings[0] ? sfLoadTabKey(sfLoadMappings[0]) : null;
        if (key && info.sfLoadStatus) {
          const status = info.sfLoadStatus;
          const stillRunning = status.status === "running";
          updateSfLoadTab(key, {
            jobId: info.sfLoadJob.job_id,
            progress: status,
            loading: stillRunning,
            result: stillRunning
              ? null
              : {
                  rows_read: status.rows_read,
                  rows_loaded: status.rows_loaded,
                  rows_failed: status.rows_failed,
                  errors: status.errors,
                },
          });
          if (stillRunning) {
            pollSfLoadJob(key, info.sfLoadJob.job_id);
          }
        }
      }
      setMappingEntryChoice(null);
      // 2026-09-04, reported by user: resuming via jumpToLoadTarget/
      // jumpToLoadDataDirect (e.g. Reconciliation's "Continue to Load to
      // Target" button) must land exactly where THOSE jumps intended, not
      // the generic cold-start default below — otherwise resuming after a
      // page refresh silently lands back on the intermediate staging step
      // instead of the final target-load step the user actually clicked
      // into.
      //
      // Precedence (no intendedStep — a normal cold selectAction("loader")
      // entry): a project with ONLY a saved Load-to-Target job (the old
      // standalone "load_target" action, never touched DDL/staging load
      // under THIS project) resumes straight into "sf_load" — exactly
      // where the old 4-action UI would have landed it. Any project with
      // ddlState/loadJob present (the common case) still lands on "ddl"
      // first, unchanged from before this fold. An inferred-direct RDBMS
      // resume (see resumedStrategy above) always lands on "load" instead
      // — that's its only reachable step (effectiveActionSteps), and it's
      // also where the real target table name it wrote to lives.
      setStep(
        info.intendedStep ??
          (resumedStrategy === "direct"
            ? "load"
            : resolveLoaderResumeStep(!!info.ddlState, !!info.loadJob)),
      );
      return;
    }

    if (a === "reconciliation" && info.reconciliation) {
      const counts = (info.reconciliation.counts_result ?? {}) as Record<
        string,
        TableCountResult
      >;
      const columns = (info.reconciliation.columns_result ?? {}) as Record<
        string,
        ReconcileColumnResponse
      >;
      const keys = new Set([...Object.keys(counts), ...Object.keys(columns)]);
      const nextTabState: Record<string, ReconcileTabState> = {};
      keys.forEach((k) => {
        nextTabState[k] = {
          ...DEFAULT_RECONCILE_TAB,
          countResult: counts[k] ?? null,
          columnResult: columns[k] ?? null,
        };
      });
      setReconcileTabState(nextTabState);
      setReconciliationMappingConfirmed(true);
      setMappingEntryChoice(null);
      setStep("reconcile");
      return;
    }

    setMappingEntryChoice(null);
    setStep(ACTION_STEPS[a][0]);
  };

  // 2026-09-27, reported by user: Reconciliation's "Start Fresh" deleted
  // the saved backend draft but never cleared the reconciliation step's
  // own local state — sourceConnectionId/targetConnectionId stayed
  // connected and reconcileTabState/detailedReconcileResult still held the
  // previous run's results, so the screen after "Start Fresh" looked
  // identical to the last reconciliation instead of a genuinely fresh
  // connect-then-run screen. Same class of bug resetForFreshMappingFlow
  // above was written to fix for the mapping flow.
  const resetForFreshReconciliationFlow = () => {
    setSourceConnectionId(null);
    setSourceConnLabel("");
    setTargetConnectionId(null);
    setTargetConnLabel("");
    setReconcileActiveTab(0);
    setReconcileTabState({});
    setDetailedReconcileResult(null);
    setReconciliationMappingConfirmed(false);
  };

  // 2026-09-27, requested by user: the same "Start Fresh looked identical
  // to the last run" bug applied to "loader"/"createTables" too — their
  // target connection and previously-generated DDL statements stayed on
  // screen even after the backend's DDL draft was deleted. Shared by both
  // actions below since they render the same "ddl" step off the same
  // state.
  const resetForFreshDdlFlow = () => {
    setTargetConnectionId(null);
    setTargetConnLabel("");
    setTargetDbType(null);
    setTargetSchemaName("");
    setTargetSchemas([]);
    setTargetSchemaManualEntry(false);
    setDdlTargetSchema("");
    setDdlTargetConnected(null);
    setDdlStatements([]);
    setDdlDuplicates([]);
    setDdlDuplicateDecisions({});
  };

  // "Start Fresh" — proceed exactly like today (no saved state existed),
  // and best-effort clear the stale saved artifact so it doesn't keep
  // re-prompting next time. The Load-to-Target job has no delete endpoint
  // here — its artifact is the load job's own audit row, not a draft; not
  // hydrating it (already the effect of choosing this path, and of
  // landing back on "ddl" first) is enough.
  const handleStartFresh = () => {
    const info = pendingResumeChoice;
    setPendingResumeChoice(null);
    if (!info) return;
    const a = info.action;
    if (projectId) {
      if (a === "mapping") {
        apiv2.converterDeleteProjectMapping(projectId).catch(() => {});
      } else if (a === "loader" || a === "createTables") {
        apiv2.converterDeleteDdlState(projectId).catch(() => {});
      } else if (a === "reconciliation") {
        apiv2.converterDeleteReconciliation(projectId).catch(() => {});
      }
    }
    if (a === "mapping") {
      resetForFreshMappingFlow();
    } else if (a === "loader") {
      resetForFreshDdlFlow();
      setLoaderEntryChoice("mapped");
      // The previous load job's Load Results must not linger and look
      // like results for whatever this "start fresh" loader run produces.
      resetLoadResultsState();
      // 2026-09-16, reported by user: Start Fresh dropped straight into the
      // staged-vs-direct gate even when Direct is disabled (joined CRM
      // mapping) — same one-option-question fix as selectAction above.
      setCrmLoadStrategy(hasJoinedMappingsForDirectLoad ? "staged" : null);
    } else if (a === "createTables") {
      resetForFreshDdlFlow();
    } else if (a === "reconciliation") {
      resetForFreshReconciliationFlow();
    }
    setAction(a);
    setStep(ACTION_STEPS[a][0]);
    setMappingEntryChoice(null);
  };

  const handleReconcileCounts = async (
    mapping: TableObjectMapping | undefined = activeReconcileMapping,
  ) => {
    if (!mapping) return;
    const key = reconcileTabKey(mapping);
    const tab = getReconcileTab(key);
    updateReconcileTab(key, { countLoading: true, error: null });
    if (!targetConnectionId) {
      updateReconcileTab(key, {
        countLoading: false,
        error: "Connect a target database first",
      });
      return;
    }
    if (isMappingFileSourced(mapping)) {
      const sourceFile = buildFileReconcileSourceFile(mapping);
      if (!sourceFile) {
        updateReconcileTab(key, {
          countLoading: false,
          error:
            "No source file recorded for this table — choose one for it in the Loader step first.",
        });
        return;
      }
      try {
        const res = await apiv2.converterReconcileFileCounts({
          sourceFiles: { [mapping.source_table]: sourceFile },
          targetConnectionRef: { connectionId: targetConnectionId, projectId },
          mappings: [mapping],
          targetSchema: ddlTargetSchema || targetSchemaName || "",
          pairs: [buildReconcilePair(mapping, tab)],
          projectId,
        });
        updateReconcileTab(key, {
          countResult: res.results[0] ?? null,
          activeResultTab: "counts",
        });
        persistReconciliationState([
          { key, patch: { countResult: res.results[0] ?? null } },
        ]);
      } catch (e) {
        updateReconcileTab(key, {
          error: e instanceof Error ? e.message : "Count reconciliation failed",
        });
      } finally {
        updateReconcileTab(key, { countLoading: false });
      }
      return;
    }
    if (!sourceConnectionId) {
      updateReconcileTab(key, {
        countLoading: false,
        error: "Connect source and target databases first",
      });
      return;
    }
    try {
      const res = await apiv2.converterReconcileCounts({
        sourceConnectionRef: { connectionId: sourceConnectionId, projectId },
        targetConnectionRef: { connectionId: targetConnectionId, projectId },
        mappings: [mapping],
        targetSchema: ddlTargetSchema || targetSchemaName || "",
        pairs: [buildReconcilePair(mapping, tab)],
        projectId,
        targetPlatform: activeTargetPlatform,
      });
      updateReconcileTab(key, {
        countResult: res.results[0] ?? null,
        activeResultTab: "counts",
      });
      persistReconciliationState([
        { key, patch: { countResult: res.results[0] ?? null } },
      ]);
    } catch (e) {
      updateReconcileTab(key, {
        error: e instanceof Error ? e.message : "Count reconciliation failed",
      });
    } finally {
      updateReconcileTab(key, { countLoading: false });
    }
  };

  const handleReconcileAllCounts = async () => {
    if (reconcileMappings.length === 0) return;
    if (!targetConnectionId) {
      setErr("Connect a target database first");
      return;
    }
    // Split per-table (not project-wide) so a mixed project's live-DB
    // tables aren't silently dropped just because SOME other table in the
    // same project came from an uploaded file — see isMappingFileSourced.
    const fileSourcedMappings = reconcileMappings.filter((m) =>
      isMappingFileSourced(m),
    );
    const dbSourcedMappings = reconcileMappings.filter(
      (m) => !isMappingFileSourced(m),
    );
    if (dbSourcedMappings.length > 0 && !sourceConnectionId) {
      setErr("Connect source and target databases first");
      return;
    }
    reconcileMappings.forEach((m) =>
      updateReconcileTab(reconcileTabKey(m), {
        countLoading: true,
        error: null,
      }),
    );
    try {
      const resultByKey = new Map<string, TableCountResult>();

      if (fileSourcedMappings.length > 0) {
        const pairs = fileSourcedMappings.map((m) =>
          buildReconcilePair(m, getReconcileTab(reconcileTabKey(m))),
        );
        const fileRes = await apiv2.converterReconcileFileCounts({
          sourceFiles: Object.fromEntries(
            fileSourcedMappings
              .map(
                (m) =>
                  [m.source_table, buildFileReconcileSourceFile(m)] as const,
              )
              .filter(
                (entry): entry is [string, FileReconcileSourceFile] =>
                  entry[1] !== null,
              ),
          ),
          targetConnectionRef: { connectionId: targetConnectionId, projectId },
          mappings: fileSourcedMappings,
          targetSchema: ddlTargetSchema || targetSchemaName || "",
          pairs,
          projectId,
        });
        fileSourcedMappings.forEach((m, i) =>
          resultByKey.set(reconcileTabKey(m), fileRes.results[i]),
        );
      }

      if (dbSourcedMappings.length > 0) {
        const pairs = dbSourcedMappings.map((m) =>
          buildReconcilePair(m, getReconcileTab(reconcileTabKey(m))),
        );
        const dbRes = await apiv2.converterReconcileCounts({
          sourceConnectionRef: { connectionId: sourceConnectionId!, projectId },
          targetConnectionRef: { connectionId: targetConnectionId, projectId },
          mappings: dbSourcedMappings,
          targetSchema: ddlTargetSchema || targetSchemaName || "",
          pairs,
          projectId,
          targetPlatform: activeTargetPlatform,
        });
        dbSourcedMappings.forEach((m, i) =>
          resultByKey.set(reconcileTabKey(m), dbRes.results[i]),
        );
      }

      reconcileMappings.forEach((m) =>
        updateReconcileTab(reconcileTabKey(m), {
          countResult: resultByKey.get(reconcileTabKey(m)) ?? null,
          activeResultTab: "counts",
        }),
      );
      persistReconciliationState(
        reconcileMappings.map((m) => ({
          key: reconcileTabKey(m),
          patch: { countResult: resultByKey.get(reconcileTabKey(m)) ?? null },
        })),
      );
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : "Count reconciliation failed";
      reconcileMappings.forEach((m) =>
        updateReconcileTab(reconcileTabKey(m), { error: msg }),
      );
    } finally {
      reconcileMappings.forEach((m) =>
        updateReconcileTab(reconcileTabKey(m), { countLoading: false }),
      );
    }
  };

  const handleReconcileColumns = async (
    mapping: TableObjectMapping | undefined = activeReconcileMapping,
  ) => {
    if (!mapping) return;
    const key = reconcileTabKey(mapping);
    const tab = getReconcileTab(key);
    updateReconcileTab(key, { columnLoading: true, error: null });
    if (!targetConnectionId) {
      updateReconcileTab(key, {
        columnLoading: false,
        error: "Connect a target database first",
      });
      return;
    }
    if (isMappingFileSourced(mapping)) {
      const sourceFile = buildFileReconcileSourceFile(mapping);
      if (!sourceFile) {
        updateReconcileTab(key, {
          columnLoading: false,
          error:
            "No source file recorded for this table — choose one for it in the Loader step first.",
        });
        return;
      }
      try {
        const res = await apiv2.converterReconcileFileColumns({
          sourceFile,
          targetConnectionRef: { connectionId: targetConnectionId, projectId },
          mapping,
          targetSchema: ddlTargetSchema || targetSchemaName || "",
          pair: buildReconcilePair(mapping, tab),
          projectId,
          targetPlatform: activeTargetPlatform,
        });
        updateReconcileTab(key, {
          columnResult: res,
          activeResultTab: "columns",
        });
        persistReconciliationState([{ key, patch: { columnResult: res } }]);
      } catch (e) {
        updateReconcileTab(key, {
          error:
            e instanceof Error ? e.message : "Column reconciliation failed",
        });
      } finally {
        updateReconcileTab(key, { columnLoading: false });
      }
      return;
    }
    if (!sourceConnectionId) {
      updateReconcileTab(key, {
        columnLoading: false,
        error: "Connect source and target databases first",
      });
      return;
    }
    try {
      const res = await apiv2.converterReconcileColumns({
        sourceConnectionRef: { connectionId: sourceConnectionId, projectId },
        targetConnectionRef: { connectionId: targetConnectionId, projectId },
        mapping,
        targetSchema: ddlTargetSchema || targetSchemaName || "",
        pair: buildReconcilePair(mapping, tab),
        projectId,
        targetPlatform: activeTargetPlatform,
      });
      updateReconcileTab(key, {
        columnResult: res,
        activeResultTab: "columns",
      });
      persistReconciliationState([{ key, patch: { columnResult: res } }]);
    } catch (e) {
      updateReconcileTab(key, {
        error: e instanceof Error ? e.message : "Column reconciliation failed",
      });
    } finally {
      updateReconcileTab(key, { columnLoading: false });
    }
  };

  // Bulk "Run Column Reconciliation — All Tables" for the Files-mode
  // dashboard — reuses handleReconcileColumns per table (which already
  // handles its own tab-scoped loading/error state and the file-vs-DB
  // branching), running every mapped table concurrently.
  const handleReconcileAllColumns = async () => {
    if (reconcileMappings.length === 0) return;
    setReconcileAllColumnsLoading(true);
    try {
      await Promise.all(
        reconcileMappings.map((m) => handleReconcileColumns(m)),
      );
    } finally {
      setReconcileAllColumnsLoading(false);
    }
  };

  // Row-by-row reconciliation — matches source and target rows by
  // keyColumns (auto-detected PK, or a manual override the user picked)
  // and flags rows present on only one side plus rows present on both
  // whose non-key mapped values differ. Deliberately NOT wired into
  // persistReconciliationState/resume — unlike counts_result/columns_result,
  // row-level results aren't persisted across sessions (would need a new
  // DB column); each run is on-demand for the current session only.
  const handleReconcileRows = async (
    mapping: TableObjectMapping | undefined = activeReconcileMapping,
  ) => {
    if (!mapping) return;
    const key = reconcileTabKey(mapping);
    const tab = getReconcileTab(key);
    const keyColumns =
      tab.keyColumns.length > 0
        ? tab.keyColumns
        : getSuggestedReconcileKeyColumns(mapping);
    if (keyColumns.length === 0) {
      updateReconcileTab(key, {
        error:
          "Select at least one key column to match rows between source and target.",
      });
      return;
    }
    updateReconcileTab(key, { rowLoading: true, error: null, keyColumns });
    if (!targetConnectionId) {
      updateReconcileTab(key, {
        rowLoading: false,
        error: "Connect a target database first",
      });
      return;
    }
    if (isMappingFileSourced(mapping)) {
      const sourceFile = buildFileReconcileSourceFile(mapping);
      if (!sourceFile) {
        updateReconcileTab(key, {
          rowLoading: false,
          error:
            "No source file recorded for this table — choose one for it in the Loader step first.",
        });
        return;
      }
      try {
        const res = await apiv2.converterReconcileFileRows({
          sourceFile,
          targetConnectionRef: { connectionId: targetConnectionId, projectId },
          mapping,
          targetSchema: ddlTargetSchema || targetSchemaName || "",
          pair: buildReconcilePair(mapping, tab),
          keyColumns,
          projectId,
          targetPlatform: activeTargetPlatform,
        });
        updateReconcileTab(key, { rowResult: res, activeResultTab: "rows" });
      } catch (e) {
        updateReconcileTab(key, {
          error: e instanceof Error ? e.message : "Row reconciliation failed",
        });
      } finally {
        updateReconcileTab(key, { rowLoading: false });
      }
      return;
    }
    if (!sourceConnectionId) {
      updateReconcileTab(key, {
        rowLoading: false,
        error: "Connect source and target databases first",
      });
      return;
    }
    try {
      const res = await apiv2.converterReconcileRows({
        sourceConnectionRef: { connectionId: sourceConnectionId, projectId },
        targetConnectionRef: { connectionId: targetConnectionId, projectId },
        mapping,
        targetSchema: ddlTargetSchema || targetSchemaName || "",
        pair: buildReconcilePair(mapping, tab),
        keyColumns,
        projectId,
        targetPlatform: activeTargetPlatform,
      });
      updateReconcileTab(key, { rowResult: res, activeResultTab: "rows" });
    } catch (e) {
      updateReconcileTab(key, {
        error: e instanceof Error ? e.message : "Row reconciliation failed",
      });
    } finally {
      updateReconcileTab(key, { rowLoading: false });
    }
  };

  // Bulk "Run Row-by-Row Reconciliation — All Tables", mirroring
  // handleReconcileAllColumns. Tables with no detectable/selected key
  // column are skipped rather than failing the whole batch.
  //
  // Issue #754 (MI CW sizing): row-by-row reconciliation streams every
  // mapped table's content twice per side (once to hash-compare, once to
  // fetch mismatch detail — see _row_reconcile_fetch_by_keys's docstring
  // in service.py), unlike the cheap COUNT(*)-based count check above.
  // Firing that for every mapped table at once is exactly the "routine
  // use at MI CW scale" this issue says to avoid, so this bulk action
  // warns (and requires confirmation) whenever a known table count is
  // large or table sizes haven't been checked yet — it never blocks the
  // per-table action above, only this "all tables at once" shortcut.
  // Issue #753/#754 PR-review finding: this used to be hardcoded
  // independent of the backend's configurable ZTD_ROW_RECONCILE_MAX_ROWS
  // (service.py) — an engagement that tuned that env var for its own
  // hardware budget got a warning dialog describing a limit the backend
  // no longer enforced. Fetched once on mount; falls back to the
  // backend's own default (1,000,000) if the call fails or hasn't
  // resolved yet, so the warning can only ever be too conservative, never
  // silently absent.
  const [rowReconcileMaxRows, setRowReconcileMaxRows] = useState(1_000_000);
  useEffect(() => {
    let cancelled = false;
    apiv2
      .converterReconcileRowLimits()
      .then((res) => {
        if (!cancelled) setRowReconcileMaxRows(res.row_reconcile_max_rows);
      })
      .catch(() => {
        // Keep the default above — see comment there.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleReconcileAllRows = async () => {
    if (reconcileMappings.length === 0) return;
    const knownCounts = reconcileMappings
      .map(
        (m) => reconcileTabState[reconcileTabKey(m)]?.countResult?.source_count,
      )
      .filter((n): n is number => typeof n === "number");
    const anyLargeTable = knownCounts.some((n) => n > rowReconcileMaxRows);
    const hasUnknownCounts = knownCounts.length < reconcileMappings.length;
    if (anyLargeTable || hasUnknownCounts) {
      const reason = anyLargeTable
        ? `at least one mapped table has over ${rowReconcileMaxRows.toLocaleString()} rows`
        : "table sizes haven't been checked yet (run Count Check — All Tables first to see them)";
      const proceed = window.confirm(
        "Row-by-row reconciliation streams every mapped table's full content " +
          "twice per side (once to compare, once to fetch mismatch detail) — " +
          `expensive at large scale, and ${reason}.\n\n` +
          "For large-scale conversions, prefer running this only against " +
          "specific tables flagged by the Count Check / Column Reconciliation " +
          "results above, rather than routinely across all tables.\n\n" +
          "Continue running row-by-row reconciliation for all mapped tables anyway?",
      );
      if (!proceed) return;
    }
    setReconcileAllRowsLoading(true);
    try {
      await Promise.all(reconcileMappings.map((m) => handleReconcileRows(m)));
    } finally {
      setReconcileAllRowsLoading(false);
    }
  };

  // Auto-populate the active tab's row-reconciliation key column(s) from
  // the detected primary key the first time this table's tab is visited —
  // the user can still change the selection via the key-column chips
  // below; this only fills in a still-empty selection, never overwrites
  // one the user already made.
  useEffect(() => {
    if (!activeReconcileKey || !activeReconcileMapping) return;
    if (activeReconcileTab.keyColumns.length > 0) return;
    if (suggestedReconcileKeyColumns.length === 0) return;
    updateReconcileTab(activeReconcileKey, {
      keyColumns: suggestedReconcileKeyColumns,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReconcileKey, schemaResult]);

  // ── Auto-fetch tables + SF objects when entering sf_load step ───────────
  useEffect(() => {
    if (step !== "sf_load") return;

    const fetchTables = async () => {
      if (schemaResult) return; // already have tables
      if (!sourceConnectionId) return;
      try {
        const res = await apiv2.converterSourceScan({
          connectionId: sourceConnectionId,
          projectId,
        });
        setSchemaResult(res);
      } catch {
        // silent — user can still retry via "Load Fields & Auto-Map"
      }
    };

    const fetchSfObjects = async () => {
      if (sfObjects.length > 0) return; // already have objects
      try {
        const res = await apiv2.converterSalesforceObjects(
          sfConn,
          undefined,
          true,
          projectId,
        );
        setSfObjects(res.objects);
      } catch {
        // silent — user can still retry via "Load Fields & Auto-Map"
      }
    };

    fetchTables();
    fetchSfObjects();
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Clear stale messages/stats when switching steps ──────────────────────
  // Going back and forth between steps previously left the error banner and
  // the last run's results/logs (Create Tables, Load, Reconcile, Push to
  // GovConnect) visible on unrelated steps. Reset them on every step change,
  // but never touch a job that's still actively polling in the background.
  const prevStepRef = useRef<Step>(step);
  useEffect(() => {
    if (prevStepRef.current === step) return;
    prevStepRef.current = step;
    setError(null);
    if (step === "ddl") setDdlExecuteResults([]);
    // Deliberately NOT clearing loadProgress/loadResultsCleared here —
    // a completed load's results (or its failure reason) must survive
    // navigating away and back, or the confirmation this section exists
    // to provide would get silently wiped just by clicking another step
    // tab. Starting a brand new load (handleLoadData) already sets its
    // own fresh loadProgress; the explicit "Clear" button covers dismissal.
    if (step === "reconcile") {
      // Clear only transient loading/error flags from whatever was in
      // flight the last time this step was visited -- NOT the actual
      // result fields (countResult/columnResult/rowResult/activeResultTab/
      // keyColumns). A full DEFAULT_RECONCILE_TAB spread here used to wipe
      // out real results every time this step was re-entered, including
      // right after a resume rehydrated them from the server (resume sets
      // state, then navigates to this step, which ran this same effect and
      // immediately overwrote what resume had just restored) and even just
      // from navigating to another step and back within the same session.
      setReconcileTabState((prev) => {
        const next: typeof prev = {};
        for (const [key, tab] of Object.entries(prev)) {
          next[key] = {
            ...tab,
            countLoading: false,
            columnLoading: false,
            rowLoading: false,
            error: null,
          };
        }
        return next;
      });
    }
    if (step === "sf_load") {
      setSfLoadTabState((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          // Same reasoning as the reconcile branch above -- a completed
          // job's result/progress must survive revisiting this step; only
          // clear it for a tab that's genuinely back to its untouched
          // default (no result/progress ever recorded).
          if (sfLoadPollRefs.current[key]) continue;
          const tab = next[key];
          if (!tab.result && !tab.progress) {
            next[key] = DEFAULT_SF_LOAD_TAB;
          } else {
            next[key] = { ...tab, loading: false, error: null };
          }
        }
        return next;
      });
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Floating "scroll to top" affordance for long steps (e.g. many mapping
  // rows) so users don't have to scroll back up to reach action buttons.
  const [showScrollTop, setShowScrollTop] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ── Render ──────────────────────────────────────────────────────────────

  // Computed once and rendered inside all 3 top-level `return`s below (the
  // landing page, the mapping-entry-choice screen, and the main wizard) —
  // `selectAction` sets pendingResumeChoice/checkingResume while `action`
  // is still null (the resume check runs BEFORE action is set, so the user
  // can be asked before committing to either path), so this can't live only
  // in the main wizard's JSX or it's unreachable from the other two returns.
  const resumeModals = (
    <>
      {pendingResumeChoice &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60" />
            <div
              className={`relative w-full bg-surface-500 border border-white/10 rounded-xl shadow-2xl p-5 space-y-4 ${
                pendingResumeChoice.mappingHistory ? "max-w-lg" : "max-w-md"
              }`}
            >
              <div className="flex items-center gap-2.5">
                <History size={18} className="text-brand-400 shrink-0" />
                <h3 className="text-sm font-semibold text-white">
                  {pendingResumeChoice.mappingHistory
                    ? "Resume from a saved mapping"
                    : "Resume where you left off?"}
                </h3>
              </div>
              <p className="text-sm text-white/60">
                {pendingResumeChoice.summary}
              </p>
              {pendingResumeChoice.mappingHistory ? (
                <>
                  <ul className="space-y-1.5 max-h-72 overflow-y-auto">
                    {pendingResumeChoice.mappingHistory.map((entry) => (
                      <li key={entry.id}>
                        <button
                          onClick={() => handleResumeMappingEntry(entry.id)}
                          disabled={resumingMappingEntry}
                          className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/5 hover:border-brand-500/40 transition-all text-left disabled:opacity-50"
                        >
                          <span className="text-sm text-white/90">
                            {entry.table_count} table
                            {entry.table_count !== 1 ? "s" : ""} mapped
                          </span>
                          <span className="text-xs text-white/40 shrink-0">
                            Saved {new Date(entry.saved_at).toLocaleString()}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-between gap-2 pt-1">
                    {resumingMappingEntry && (
                      <span className="flex items-center gap-1.5 text-xs text-white/40">
                        <Loader2 size={12} className="animate-spin" />
                        Loading…
                      </span>
                    )}
                    <button
                      onClick={handleStartFresh}
                      disabled={resumingMappingEntry}
                      className="ztd-btn ztd-btn-secondary text-xs ml-auto"
                    >
                      Start Fresh Instead
                    </button>
                  </div>
                </>
              ) : (
                <div className="flex justify-end gap-2 pt-1">
                  <button
                    onClick={handleStartFresh}
                    className="ztd-btn ztd-btn-secondary text-xs"
                  >
                    Start Fresh
                  </button>
                  <button
                    onClick={handleResumeChoice}
                    className="ztd-btn ztd-btn-primary text-xs"
                  >
                    Resume
                  </button>
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
      {checkingResume &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <div className="flex items-center gap-2 text-sm text-white/70 bg-surface-500 border border-white/10 rounded-lg px-4 py-3 shadow-xl">
              <Loader2 size={14} className="animate-spin" />
              Checking for saved work…
            </div>
          </div>,
          document.body,
        )}

      {createPortal(
        <>
          <ConverterHistoryPanel<ConverterLoadHistoryEntryOut>
            open={loadHistoryPanelOpen}
            title="Data Loader History"
            subtitle="Last 10 saved runs for this project"
            entries={loadHistoryEntries}
            loading={loadHistoryLoading}
            emptyLabel="No saved Data Loader runs yet for this project."
            getKey={(e) => e.id}
            onClose={() => setLoadHistoryPanelOpen(false)}
            onSelect={async (entry) => {
              setLoadHistoryPanelOpen(false);
              if (!projectId) return;
              try {
                const detail = await apiv2.converterGetLoadHistoryEntry(
                  projectId,
                  entry.id,
                );
                setLoadProgress(
                  detail.summary as unknown as DataLoadStatusResponse,
                );
              } catch (e) {
                setErr(
                  e instanceof Error
                    ? e.message
                    : "Failed to load Data Loader history entry",
                );
              }
            }}
            renderEntry={(e) => (
              <>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-800 dark:text-slate-100">
                    {new Date(e.saved_at).toLocaleString()}
                  </span>
                  <span
                    className={`text-[10px] uppercase tracking-wide ${
                      e.status === "completed"
                        ? "text-green-600"
                        : e.status === "failed"
                          ? "text-red-600"
                          : "text-amber-600"
                    }`}
                  >
                    {e.status}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {e.job_type} · {e.total_tables} table(s) ·{" "}
                  {e.total_rows_loaded} loaded
                  {e.total_rows_failed
                    ? ` · ${e.total_rows_failed} failed`
                    : ""}
                </p>
              </>
            )}
          />

          <ConverterHistoryPanel<ConverterReconciliationHistoryEntryOut>
            open={reconciliationHistoryPanelOpen}
            title="Reconciliation History"
            subtitle="Last 10 saved runs for this project"
            entries={reconciliationHistoryEntries}
            loading={reconciliationHistoryLoading}
            emptyLabel="No saved reconciliation runs yet for this project."
            getKey={(e) => e.id}
            onClose={() => setReconciliationHistoryPanelOpen(false)}
            onSelect={handleSelectReconciliationHistoryEntry}
            renderEntry={(e) => (
              <>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-800 dark:text-slate-100">
                    {new Date(e.saved_at).toLocaleString()}
                  </span>
                  {e.overall_score !== null && (
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                      {Math.round(e.overall_score)}/100
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {e.table_count} table(s) reconciled
                </p>
              </>
            )}
          />

          <ConverterHistoryPanel<ConverterFinalLoadHistoryEntryOut>
            open={finalLoadHistoryPanelOpen}
            title="Load to Target History"
            subtitle="Last 10 saved runs for this project"
            entries={finalLoadHistoryEntries}
            loading={finalLoadHistoryLoading}
            emptyLabel="No saved Load to Target runs yet for this project."
            getKey={(e) => e.id}
            onClose={() => setFinalLoadHistoryPanelOpen(false)}
            onSelect={async (entry) => {
              setFinalLoadHistoryPanelOpen(false);
              if (!projectId) return;
              try {
                const detail = await apiv2.converterGetFinalLoadHistoryEntry(
                  projectId,
                  entry.id,
                );
                // No table/tab identifier is stored on the history entry
                // itself, so (matching handleResumeChoice's load_target
                // branch) this surfaces the result against the first mapped
                // table's tab and lets the user re-pick the right one if
                // there's more than one.
                const key = sfLoadMappings[0]
                  ? sfLoadTabKey(sfLoadMappings[0])
                  : null;
                if (key) {
                  const status =
                    detail.summary as unknown as DbToSfLoadStatusResponse;
                  updateSfLoadTab(key, {
                    progress: status,
                    result: {
                      rows_read: status.rows_read,
                      rows_loaded: status.rows_loaded,
                      rows_failed: status.rows_failed,
                      errors: status.errors,
                    },
                  });
                }
              } catch (e) {
                setErr(
                  e instanceof Error
                    ? e.message
                    : "Failed to load Load-to-Target history entry",
                );
              }
            }}
            renderEntry={(e) => (
              <>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-800 dark:text-slate-100">
                    {new Date(e.saved_at).toLocaleString()}
                  </span>
                  <span
                    className={`text-[10px] uppercase tracking-wide ${
                      e.status === "completed"
                        ? "text-green-600"
                        : e.status === "failed"
                          ? "text-red-600"
                          : "text-amber-600"
                    }`}
                  >
                    {e.status}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  {e.target_type} · {e.rows_loaded} loaded
                  {e.rows_failed ? ` · ${e.rows_failed} failed` : ""}
                </p>
              </>
            )}
          />
        </>,
        document.body,
      )}
    </>
  );

  // Landing page — pick one of the 4 standalone actions. Replaces the old
  // single continuous linear wizard entirely; each action below scopes the
  // step-render blocks further down to just its own ACTION_STEPS subset.
  if (action === null) {
    return (
      <div className="data-converter-agent max-w-5xl mx-auto px-4 py-4 space-y-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-white/5 text-white/50 hover:text-white transition"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white">Data Converter</h1>
            <p className="text-xs text-white/40 mt-0.5">
              Choose an action to get started
            </p>
          </div>
          {projectId && (
            <button
              onClick={() => setShowSavedConnections((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                showSavedConnections
                  ? "bg-brand-500/20 border-brand-500/50 text-brand-300"
                  : "bg-white/5 hover:bg-white/10 border-white/10 text-white/60 hover:text-white/80"
              }`}
            >
              <Database size={13} />
              Saved Connections
            </button>
          )}
        </div>

        {projectId && showSavedConnections && (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-white/80">
                Saved Connections
              </h3>
              <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
                <button
                  onClick={() => setSavedConnectionsKind("source")}
                  className={`px-3 py-1.5 text-xs font-medium transition ${
                    savedConnectionsKind === "source"
                      ? "bg-brand-500/20 text-brand-300"
                      : "text-white/50 hover:text-white"
                  }`}
                >
                  Source
                </button>
                <button
                  onClick={() => setSavedConnectionsKind("target")}
                  className={`px-3 py-1.5 text-xs font-medium transition ${
                    savedConnectionsKind === "target"
                      ? "bg-brand-500/20 text-brand-300"
                      : "text-white/50 hover:text-white"
                  }`}
                >
                  Target
                </button>
              </div>
            </div>
            <SavedConnectionsPanel
              key={savedConnectionsKind}
              projectId={projectId}
              kind={savedConnectionsKind}
              onActivated={() =>
                savedConnectionsKind === "source"
                  ? refreshSourceConnectionFromSaved()
                  : refreshTargetConnectionFromSaved(undefined, {
                      forceSchema: true,
                    })
              }
            />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {ACTION_META.map((a) => (
            <button
              key={a.id}
              onClick={() => selectAction(a.id)}
              className="text-left p-5 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/5 hover:border-brand-500/40 transition-all"
            >
              <div className="flex items-center gap-2 text-brand-400 mb-2">
                {a.icon}
                <span className="text-sm font-semibold text-white">
                  {a.label}
                </span>
              </div>
              <p className="text-xs text-white/50">{a.description}</p>
            </button>
          ))}
        </div>

        {projectId && (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5">
            <h3 className="text-sm font-semibold text-white/80 mb-1">
              Related Agents
            </h3>
            <p className="text-xs text-white/40 mb-3">
              Mapping suggestions already pick up the right domain vocabulary
              automatically (Child Support, Child Welfare, ...) — no setup
              needed. For profiling, semantic modeling, or metadata curation,
              jump to that agent directly for the full workflow and outputs.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() =>
                  openAgentInNewTab(
                    `/projects/${projectId}/agents/data-profiler`,
                  )
                }
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition"
              >
                <Microscope size={13} /> Data Profiler
                <ArrowUpRight size={12} className="text-white/40" />
              </button>
              <button
                onClick={() =>
                  openAgentInNewTab(
                    `/projects/${projectId}/agents/semantic-intelligence`,
                  )
                }
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition"
              >
                <GitMerge size={13} /> Semantic Intelligence
                <ArrowUpRight size={12} className="text-white/40" />
              </button>
              <button
                onClick={() =>
                  openAgentInNewTab(
                    `/projects/${projectId}/agents/metadata-curator`,
                  )
                }
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-white/10 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition"
              >
                <ListChecks size={13} /> Metadata Curator
                <ArrowUpRight size={12} className="text-white/40" />
              </button>
            </div>
          </div>
        )}

        {projectId && <ConverterActivityPanel projectId={projectId} />}
        {resumeModals}
      </div>
    );
  }

  // "Source → Target Mapping" is gated behind this choice: import an
  // existing mapping document (jumps straight to the mapping step, where
  // the upload UI already renders unconditionally) or build a fresh one
  // (today's connect-source/schema/connect-target/generate flow).
  if (action === "mapping" && mappingEntryChoice === null) {
    return (
      <div className="data-converter-agent max-w-5xl mx-auto px-4 py-4 space-y-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setAction(null)}
            className="p-2 rounded-lg hover:bg-white/5 text-white/50 hover:text-white transition"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white">
              Source → Target Mapping
            </h1>
            <p className="text-xs text-white/40 mt-0.5">
              Import an existing mapping, or build a new one from scratch
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={() => {
              resetForFreshMappingFlow();
              setMappingEntryChoice("import");
              setStep("mapping");
            }}
            className="text-left p-5 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/5 hover:border-brand-500/40 transition-all"
          >
            <div className="flex items-center gap-2 text-brand-400 mb-2">
              <Upload size={20} />
              <span className="text-sm font-semibold text-white">
                Import Your Own Mapping
              </span>
            </div>
            <p className="text-xs text-white/50">
              Upload an existing mapping document (spreadsheet or STTM export)
              and skip straight to reviewing it — no source/target connection
              required first.
            </p>
          </button>

          <button
            onClick={() => {
              resetForFreshMappingFlow();
              setMappingEntryChoice("create");
              setStep(ACTION_STEPS.mapping[0]);
            }}
            className="text-left p-5 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/5 hover:border-brand-500/40 transition-all"
          >
            <div className="flex items-center gap-2 text-brand-400 mb-2">
              <Link size={20} />
              <span className="text-sm font-semibold text-white">
                Create New Mapping
              </span>
            </div>
            <p className="text-xs text-white/50">
              Connect a source database, scan its schema, connect a target
              platform, and generate a fresh STTM field mapping — including from
              Files/Servers/Legacy Code (COBOL copybooks, SQL DDL, JSON/XML,
              SSIS, CSV/TSV, Parquet, PDF) as a source type, no live connection
              required for that path. When several source tables belong
              together, join keys are detected automatically.
            </p>
          </button>
        </div>
        {resumeModals}
      </div>
    );
  }

  // "Data Loader" is gated behind this choice too: load into a target that
  // already has a finalized Source → Target Mapping (the existing
  // ddl/load/sf_load flow below), or load a file directly into a database
  // table — no STTM mapping involved, since the file already dictates its
  // own target table/columns.
  if (action === "loader" && loaderEntryChoice === null) {
    return (
      <div className="data-converter-agent max-w-5xl mx-auto px-4 py-4 space-y-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setAction(null)}
            className="p-2 rounded-lg hover:bg-white/5 text-white/50 hover:text-white transition"
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white">Data Loader</h1>
            <p className="text-xs text-white/40 mt-0.5">
              Load into a mapped target, or load a file directly
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={() => setLoaderEntryChoice("mapped")}
            className="text-left p-5 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/5 hover:border-brand-500/40 transition-all"
          >
            <div className="flex items-center gap-2 text-brand-400 mb-2">
              <Database size={20} />
              <span className="text-sm font-semibold text-white">
                Load Into a Mapped Target
              </span>
            </div>
            <p className="text-xs text-white/50">
              Create staging tables from a persisted Source → Target Mapping,
              load source data into the stage layer, then push it into the
              connected target platform. Requires a finalized mapping.
            </p>
          </button>

          <button
            onClick={() => {
              setLoaderEntryChoice("file");
              setSourceMode("file");
              setStep("source");
            }}
            className="text-left p-5 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/5 hover:border-brand-500/40 transition-all"
          >
            <div className="flex items-center gap-2 text-brand-400 mb-2">
              <Upload size={20} />
              <span className="text-sm font-semibold text-white">
                Load a File Directly
              </span>
            </div>
            <p className="text-xs text-white/50">
              Upload a fixed-width, delimited, structured, or copybook file and
              load it straight into a database table — no STTM mapping needed
              since the file itself defines the target.
            </p>
          </button>
          {/* "Extract Tables from PDF" entry point removed from the Data
              Loader UI (2026-09-23, requested by user) -- code preserved on
              branch preserved/pdf-table-extraction-loader-entry and still
              intact below (isDirectFileLoad, loaderEntryChoice === "pdf",
              etc.) in case it's needed again later. */}
        </div>
        {resumeModals}
      </div>
    );
  }

  // The "file" and "pdf" entry points are the direct-load entry points left
  // in the Data Loader itself -- SharePoint moved to Source -> Target
  // Mapping's Legacy Code source type, and OneDrive was removed outright
  // (no longer needed).
  const isDirectFileLoad =
    loaderEntryChoice === "file" || loaderEntryChoice === "pdf";

  // 2026-09-01, requested by user: gates the "loader" action's normal steps
  // behind an explicit staged-vs-direct choice whenever the target is an
  // object-oriented platform — see the SectionCard below and
  // effectiveActionSteps above.
  //
  // 2026-09-07, requested by user: generalized to RDBMS targets too — any
  // source/target RDBMS pair (e.g. Azure Synapse -> Azure Synapse) can now
  // also choose to load directly, based on the already-finalized STTM
  // mapping, with no intermediate staging tables created or kept around.
  const loaderCrmStrategyPending =
    action === "loader" &&
    loaderEntryChoice === "mapped" &&
    (targetPlatformMode === "crm" || targetPlatformMode === "rdbms") &&
    crmLoadStrategy === null;
  // (hasJoinedMappingsForDirectLoad is declared earlier, right after the
  // `mappings` state hook — see the bugfix comment there.)

  // 2026-09-02, requested by user: the .xlsx mapping workbook saved at
  // Finalize, referable/downloadable/replaceable from every subsequent
  // step in the project (Create Tables, Load Data, Load to Target) — not
  // just the STTM Review step it was generated from. "Replace" reuses the
  // same import parser "Import Existing STTM" uses, then re-persists both
  // the JSON mapping and a fresh workbook reference.
  const renderProjectMappingFileCard = () =>
    projectId && (
      <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white/3 border border-white/8 text-xs text-white/50 flex-wrap">
        <span className="flex items-center gap-1.5">
          <FileSpreadsheet size={12} className="text-brand-400 flex-shrink-0" />
          {projectMappingFile?.filename
            ? `Mapping workbook: ${projectMappingFile.filename}`
            : "No mapping workbook saved yet — finalize the mapping to generate one"}
        </span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {projectMappingFile?.download_url && (
            <a
              href={projectMappingFile.download_url}
              download={projectMappingFile.filename ?? undefined}
              className="ztd-btn ztd-btn-secondary ztd-btn-sm"
            >
              <Download size={11} /> Download
            </a>
          )}
          <label
            className={`ztd-btn ztd-btn-secondary ztd-btn-sm cursor-pointer ${uploadingMappingFile ? "opacity-40 pointer-events-none" : ""}`}
          >
            {uploadingMappingFile ? (
              <Loader2 size={11} className="animate-spin" />
            ) : (
              <Upload size={11} />
            )}
            Replace
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) void handleReplaceProjectMappingFile(f);
              }}
            />
          </label>
        </div>
      </div>
    );

  // 2026-09-02, requested by user: the CRM credential form (target_type
  // tabs + fields + Test Connection) used to only be reachable via the
  // Mapping action's "salesforce" step — a project entered straight into
  // Data Loader (no "salesforce" step in ACTION_STEPS.loader) had no way to
  // add/select a Salesforce/Dynamics/Power Platform connection before its
  // final Load to Target step. Extracted here so both the "salesforce"
  // step AND the new inline card on the "sf_load" step (see below) render
  // the identical form instead of duplicating it.
  const renderCrmConnectFields = () => (
    <>
      {/* Target platform selector — genericizes this page beyond
      Salesforce. Dynamics 365 and Power Platform share one connector (same
      Dataverse Web API + Azure AD login), so switching between those two
      only changes the label. Workday (Prism Analytics) reuses the same
      tenant_id/client_id/client_secret/resource_url fields as Dynamics/
      Power Platform, with different labels — see renderCrmConnectFields'
      field-rendering block below. */}
      <div className="flex gap-2 mb-4">
        {[
          { value: "salesforce" as const, label: "Salesforce" },
          {
            value: "dynamics365" as const,
            label: "Dynamics 365",
          },
          {
            value: "power_platform" as const,
            label: "Power Platform",
          },
          {
            value: "workday" as const,
            label: "Workday",
          },
        ].map((opt) => {
          const active = sfConn.target_type === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => {
                setSfConn((p) => ({
                  ...p,
                  target_type: opt.value,
                }));
                setSfOrgInfo(null);
                setAvailableTargetObjects([]);
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                active
                  ? "bg-brand-500/20 border-brand-500/50 text-brand-300"
                  : "bg-white/5 border-white/10 text-white/50 hover:text-white"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>

      {sfConn.target_type === "salesforce" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField
            label="Instance URL"
            value={sfConn.instance_url}
            onChange={(v) => setSfConn((p) => ({ ...p, instance_url: v }))}
            placeholder="https://yourorg.salesforce.com"
            required
          />
          <InputField
            label="Username"
            value={sfConn.username}
            onChange={(v) => setSfConn((p) => ({ ...p, username: v }))}
            placeholder="admin@yourorg.com"
            required
          />
          <InputField
            label="Password"
            value={sfConn.password}
            onChange={(v) => setSfConn((p) => ({ ...p, password: v }))}
            type="password"
            required={!savedCrmConn?.has_password}
            placeholder={
              savedCrmConn?.has_password
                ? "•••• saved — leave blank to keep"
                : undefined
            }
          />
          <InputField
            label="Security Token"
            value={sfConn.security_token}
            onChange={(v) => setSfConn((p) => ({ ...p, security_token: v }))}
            type="password"
            placeholder="From Setup → My Personal Info → Reset Security Token"
            required
          />
          <InputField
            label="Connected App Consumer Key (optional)"
            value={sfConn.consumer_key ?? ""}
            onChange={(v) =>
              setSfConn((p) => ({
                ...p,
                consumer_key: v || null,
              }))
            }
          />
          <InputField
            label="Connected App Consumer Secret (optional)"
            value={sfConn.consumer_secret ?? ""}
            onChange={(v) =>
              setSfConn((p) => ({
                ...p,
                consumer_secret: v || null,
              }))
            }
            type="password"
            placeholder={
              savedCrmConn?.has_client_secret
                ? "•••• saved — leave blank to keep"
                : undefined
            }
          />
        </div>
      ) : sfConn.target_type === "workday" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField
            label="Workday Tenant"
            value={sfConn.tenant_id ?? ""}
            onChange={(v) => setSfConn((p) => ({ ...p, tenant_id: v || null }))}
            placeholder="Workday tenant name, e.g. acme_dept1"
            required
          />
          <InputField
            label="Integration System User Client ID"
            value={sfConn.client_id ?? ""}
            onChange={(v) => setSfConn((p) => ({ ...p, client_id: v || null }))}
            placeholder="Workday ISU API client ID"
            required
          />
          <InputField
            label="Client Secret"
            value={sfConn.client_secret ?? ""}
            onChange={(v) =>
              setSfConn((p) => ({
                ...p,
                client_secret: v || null,
              }))
            }
            type="password"
            required={!savedCrmConn?.has_client_secret}
            placeholder={
              savedCrmConn?.has_client_secret
                ? "•••• saved — leave blank to keep"
                : undefined
            }
          />
          <InputField
            label="Refresh Token"
            value={sfConn.refresh_token ?? ""}
            onChange={(v) =>
              setSfConn((p) => ({
                ...p,
                refresh_token: v || null,
              }))
            }
            type="password"
            required={!savedCrmConn?.has_refresh_token}
            placeholder={
              savedCrmConn?.has_refresh_token
                ? "•••• saved — leave blank to keep"
                : "ISU OAuth2 refresh token"
            }
          />
          <InputField
            label="Workday API Host"
            value={sfConn.resource_url ?? ""}
            onChange={(v) =>
              setSfConn((p) => ({
                ...p,
                resource_url: v || null,
              }))
            }
            placeholder="https://wd2-impl-services1.workday.com"
            required
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField
            label="Tenant ID"
            value={sfConn.tenant_id ?? ""}
            onChange={(v) => setSfConn((p) => ({ ...p, tenant_id: v || null }))}
            placeholder="Azure AD directory (tenant) ID"
            required
          />
          <InputField
            label="Client ID"
            value={sfConn.client_id ?? ""}
            onChange={(v) => setSfConn((p) => ({ ...p, client_id: v || null }))}
            placeholder="App registration application (client) ID"
            required
          />
          <InputField
            label="Client Secret"
            value={sfConn.client_secret ?? ""}
            onChange={(v) =>
              setSfConn((p) => ({
                ...p,
                client_secret: v || null,
              }))
            }
            type="password"
            required={!savedCrmConn?.has_client_secret}
            placeholder={
              savedCrmConn?.has_client_secret
                ? "•••• saved — leave blank to keep"
                : undefined
            }
          />
          <InputField
            label="Resource / Environment URL"
            value={sfConn.resource_url ?? ""}
            onChange={(v) =>
              setSfConn((p) => ({
                ...p,
                resource_url: v || null,
              }))
            }
            placeholder="https://yourorg.crm.dynamics.com"
            required
          />
        </div>
      )}

      {loadingTargetObjects && (
        <div className="flex items-center gap-2 text-xs text-white/40 py-2">
          <Loader2 size={12} className="animate-spin" /> Loading available
          objects…
        </div>
      )}

      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={handleSfConnect}
          disabled={
            connectingTargetPlatform ||
            (sfConn.target_type === "salesforce"
              ? !sfConn.instance_url || !sfConn.username
              : // Dynamics 365 / Power Platform / Workday all require the
                // same 3 fields (tenant_id/client_id/resource_url) — see
                // renderCrmConnectFields' field-rendering block above for
                // how each platform maps its own terminology onto them.
                !sfConn.tenant_id || !sfConn.client_id || !sfConn.resource_url)
          }
          className="ztd-btn ztd-btn-secondary"
        >
          {connectingTargetPlatform ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <RefreshCw size={14} />
          )}
          Test Connection
        </button>

        {sfOrgInfo && (
          <div className="flex items-center gap-2 text-sm text-green-400">
            <CheckCircle2 size={14} />
            Target Platform connection successful
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="data-converter-agent max-w-5xl mx-auto px-4 py-4 space-y-4">
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Scroll to top"
          className="fixed bottom-6 right-6 z-50 p-3 rounded-full bg-brand-500 text-black shadow-lg hover:bg-brand-400 transition"
        >
          <ArrowUp size={18} />
        </button>
      )}
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-2 rounded-lg hover:bg-white/5 text-white/50 hover:text-white transition"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-white">Data Converter</h1>
          <p className="text-xs text-white/40 mt-0.5">
            {ACTION_META.find((a) => a.id === action)?.label}
          </p>
        </div>
        <button
          onClick={() => setAction(null)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white/50 hover:text-white hover:bg-white/5 transition"
        >
          <ArrowLeft size={14} /> Back to Actions
        </button>
      </div>

      <ProgressBar
        steps={effectiveActionSteps(
          action,
          crmLoadStrategy,
          targetPlatformMode,
        )}
        current={step}
        onStepClick={setStep}
        labelOverrides={
          crmLoadStrategy === "direct" && targetPlatformMode === "rdbms"
            ? { load: "Load to Target" }
            : undefined
        }
      />

      {/* Error banner */}
      {error && (
        <InlineErrorBanner message={error} onDismiss={() => setError(null)} />
      )}

      {action !== "mapping" &&
        !isDirectFileLoad &&
        !(action === "reconciliation" && standaloneReconcileMode) &&
        scopedMappingStatus === "loading" && (
          <div className="flex items-center justify-center py-12 text-white/40 text-sm gap-2">
            <Loader2 size={16} className="animate-spin" /> Loading saved
            mapping…
          </div>
        )}

      {action !== "mapping" &&
        !isDirectFileLoad &&
        !(action === "reconciliation" && standaloneReconcileMode) &&
        scopedMappingStatus === "missing" && (
          <SectionCard title="No mapping found yet">
            <p className="text-sm text-white/50 mb-4">
              This action needs a mapping produced by Source → Target Mapping
              before it can run — generate a new one there, or use an existing
              mapping below.
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => selectAction("mapping")}
                className="ztd-btn ztd-btn-primary"
              >
                <Link size={14} /> Go to Source → Target Mapping
              </button>
              <label
                className={`ztd-btn ztd-btn-secondary cursor-pointer ${uploadingMappingFile ? "opacity-40 pointer-events-none" : ""}`}
              >
                {uploadingMappingFile ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Upload size={14} />
                )}
                Upload Mapping (.xlsx)
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (f) void handleReplaceProjectMappingFile(f);
                  }}
                />
              </label>
              <button
                onClick={() => void handleOpenMappingHistoryPicker()}
                className="ztd-btn ztd-btn-secondary"
              >
                <History size={14} /> Select from History
              </button>
              {action === "reconciliation" && (
                // 2026-10-15, requested by user: reconciliation can run with
                // no STTM mapping at all — this gate otherwise blocks it on
                // "go create a mapping" before the per-step content (which
                // offers the same escape hatch) ever gets a chance to render.
                <button
                  onClick={() => setStandaloneReconcileMode(true)}
                  className="ztd-btn ztd-btn-secondary"
                >
                  <GitCompareArrows size={14} /> Skip Mapping — Define Fields
                  Manually
                </button>
              )}
            </div>
          </SectionCard>
        )}

      {showMappingHistoryPicker &&
        createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/60"
              onClick={() => setShowMappingHistoryPicker(false)}
            />
            <div className="relative w-full max-w-lg bg-surface-500 border border-white/10 rounded-xl shadow-2xl p-5 space-y-4">
              <div className="flex items-center gap-2.5">
                <History size={18} className="text-brand-400 shrink-0" />
                <h3 className="text-sm font-semibold text-white">
                  Select a saved mapping
                </h3>
              </div>
              {loadingMappingHistory ? (
                <div className="flex items-center gap-2 text-sm text-white/40 py-4 justify-center">
                  <Loader2 size={14} className="animate-spin" /> Loading saved
                  mappings…
                </div>
              ) : !missingMappingHistory ||
                missingMappingHistory.length === 0 ? (
                <p className="text-sm text-white/50 py-2">
                  No saved mappings found for this project yet — generate and
                  finalize one from Source → Target Mapping first.
                </p>
              ) : (
                <ul className="space-y-1.5 max-h-72 overflow-y-auto">
                  {missingMappingHistory.map((entry) => (
                    <li key={entry.id}>
                      <button
                        onClick={() =>
                          void handleSelectMissingMappingHistoryEntry(entry.id)
                        }
                        disabled={selectingMappingHistoryId !== null}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-white/10 bg-white/[0.02] hover:bg-white/5 hover:border-brand-500/40 transition-all text-left disabled:opacity-50"
                      >
                        <span className="text-sm text-white/90">
                          {entry.table_count} table
                          {entry.table_count !== 1 ? "s" : ""} mapped
                        </span>
                        <span className="flex items-center gap-2 text-xs text-white/40 shrink-0">
                          {selectingMappingHistoryId === entry.id && (
                            <Loader2 size={12} className="animate-spin" />
                          )}
                          Saved {new Date(entry.saved_at).toLocaleString()}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex justify-end pt-1">
                <button
                  onClick={() => setShowMappingHistoryPicker(false)}
                  disabled={selectingMappingHistoryId !== null}
                  className="ztd-btn ztd-btn-secondary text-xs"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* 2026-09-01, requested by user: object-oriented targets get an
      explicit staged-vs-direct choice before Data Loader proceeds.
      2026-09-07, requested by user: generalized to RDBMS targets too. */}
      {loaderCrmStrategyPending && scopedMappingStatus === "ready" && (
        <>
          <button
            type="button"
            onClick={() => setLoaderEntryChoice(null)}
            className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition"
          >
            <ArrowLeft size={12} /> Back to Data Loader options
          </button>
          <SectionCard
            title={`How should Data Loader reach ${computeTargetPlatformLabel()}?`}
          >
            <p className="text-xs text-white/40 mb-4">
              Every mapped table needs a source connection, a target connection,
              and this finalized mapping — all three are already in place for
              this project. The only thing left to decide is how data gets from
              the source database to {computeTargetPlatformLabel()}.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => {
                  setCrmLoadStrategy("staged");
                  setStep("ddl");
                }}
                className="text-left p-4 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/5 hover:border-brand-500/40 transition-all"
              >
                <div className="flex items-center gap-2 text-brand-400 mb-2">
                  <Database size={18} />
                  <span className="text-sm font-semibold text-white">
                    Through an Intermediate Staging Database
                  </span>
                </div>
                <p className="text-xs text-white/50">
                  2 loads: source → staging tables (created here), then staging
                  → {computeTargetPlatformLabel()}. Required for any mapping
                  that joins several source tables together, and lets you
                  inspect/reconcile the staged data before it reaches the
                  target.
                </p>
              </button>
              <button
                onClick={() => {
                  setCrmLoadStrategy("direct");
                  if (targetPlatformMode === "rdbms") {
                    // The RDBMS direct flow reuses the plain "load" step's own
                    // job (same SELECT->INSERT job the staged flow runs, just
                    // pointed at the real target table — see stagedTargetTable)
                    // rather than the CRM-only "sf_load" push step. goNext's
                    // own pre-populate never runs for this jump (it's not a
                    // step-to-step advance), so seed loadMappingConfig here the
                    // same way — passing "direct" explicitly since the
                    // setCrmLoadStrategy call above hasn't taken effect yet in
                    // this same handler (state updates are async), and without
                    // it stagedTargetTable would still see the old (null)
                    // strategy and wrongly append the staging suffix.
                    setLoadMappingConfig(
                      buildDefaultLoadMappingConfig("direct"),
                    );
                    setStep("load");
                  } else {
                    setStep("sf_load");
                  }
                }}
                disabled={hasJoinedMappingsForDirectLoad}
                className="text-left p-4 rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/5 hover:border-brand-500/40 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white/[0.02] disabled:hover:border-white/10"
              >
                <div className="flex items-center gap-2 text-brand-400 mb-2">
                  <Zap size={18} />
                  <span className="text-sm font-semibold text-white">
                    Directly
                  </span>
                </div>
                <p className="text-xs text-white/50">
                  {hasJoinedMappingsForDirectLoad ? (
                    <>
                      Not available — this mapping joins several source tables
                      together, which requires materializing them into a staging
                      table first. Use staging instead.
                    </>
                  ) : (
                    <>
                      1 load: source → {computeTargetPlatformLabel()} directly,
                      no staging tables created or kept around.
                    </>
                  )}
                </p>
              </button>
            </div>
          </SectionCard>
        </>
      )}

      {(action === "mapping" ||
        (action === "loader" && isDirectFileLoad) ||
        (action === "reconciliation" && standaloneReconcileMode) ||
        (scopedMappingStatus === "ready" && !loaderCrmStrategyPending)) && (
        <>
          {/* ── Step: Source DB / File ──────────────────────────────────────── */}
          {step === "source" && (
            <div className="space-y-4">
              {action === "loader" ? (
                <button
                  type="button"
                  onClick={() => setLoaderEntryChoice(null)}
                  className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition"
                >
                  <ArrowLeft size={12} /> Back to Data Loader options
                </button>
              ) : (
                <>
                  {action === "mapping" && (
                    <button
                      type="button"
                      onClick={() => setMappingEntryChoice(null)}
                      className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition mb-1"
                    >
                      <ArrowLeft size={12} /> Back to Mapping options
                    </button>
                  )}
                  <div className="flex items-center gap-2 pb-1">
                    <span className="text-xs font-medium text-white/50">
                      Source Type:
                    </span>
                    <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
                      <button
                        onClick={() => setSourceMode("database")}
                        className={`px-3 py-1.5 text-xs font-medium transition ${
                          sourceMode === "database"
                            ? "bg-brand-500/20 text-brand-300"
                            : "text-white/50 hover:text-white"
                        }`}
                      >
                        Database
                      </button>
                      <button
                        onClick={() => setSourceMode("mainframe")}
                        className={`px-3 py-1.5 text-xs font-medium transition ${
                          sourceMode === "mainframe"
                            ? "bg-brand-500/20 text-brand-300"
                            : "text-white/50 hover:text-white"
                        }`}
                      >
                        Mainframe / JDBC Bridge
                      </button>
                      <button
                        onClick={() => setSourceMode("legacy")}
                        className={`px-3 py-1.5 text-xs font-medium transition ${
                          sourceMode === "legacy"
                            ? "bg-brand-500/20 text-brand-300"
                            : "text-white/50 hover:text-white"
                        }`}
                      >
                        Files/Servers/Legacy Code
                      </button>
                      <button
                        onClick={() => setSourceMode("crm")}
                        className={`px-3 py-1.5 text-xs font-medium transition ${
                          sourceMode === "crm"
                            ? "bg-brand-500/20 text-brand-300"
                            : "text-white/50 hover:text-white"
                        }`}
                      >
                        CRM / SaaS Systems
                      </button>
                    </div>
                  </div>
                </>
              )}

              {sourceMode === "legacy" && (
                <>
                  <div className="flex items-center gap-2 pb-1 flex-wrap">
                    <span className="text-xs font-medium text-white/50">
                      Acquire Via:
                    </span>
                    <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
                      <button
                        onClick={() => setLegacySourceMethod("upload")}
                        className={`px-3 py-1.5 text-xs font-medium transition ${
                          legacySourceMethod === "upload"
                            ? "bg-brand-500/20 text-brand-300"
                            : "text-white/50 hover:text-white"
                        }`}
                      >
                        Upload File(s)
                      </button>
                      <button
                        onClick={() => setLegacySourceMethod("sharepoint")}
                        className={`px-3 py-1.5 text-xs font-medium transition ${
                          legacySourceMethod === "sharepoint"
                            ? "bg-brand-500/20 text-brand-300"
                            : "text-white/50 hover:text-white"
                        }`}
                      >
                        SharePoint
                      </button>
                      <button
                        onClick={() => setLegacySourceMethod("pdf")}
                        className={`px-3 py-1.5 text-xs font-medium transition ${
                          legacySourceMethod === "pdf"
                            ? "bg-brand-500/20 text-brand-300"
                            : "text-white/50 hover:text-white"
                        }`}
                      >
                        PDF
                      </button>
                      <button
                        onClick={() => setLegacySourceMethod("sftp")}
                        className={`px-3 py-1.5 text-xs font-medium transition ${
                          legacySourceMethod === "sftp"
                            ? "bg-brand-500/20 text-brand-300"
                            : "text-white/50 hover:text-white"
                        }`}
                      >
                        SFTP
                      </button>
                    </div>
                  </div>

                  {legacySourceMethod === "upload" && (
                    <SectionCard title="Generate Schema from Files/Servers/Legacy Code">
                      <p className="text-xs text-white/40 mb-3">
                        Upload one or more pieces of legacy code or schema
                        artifacts — SQL DDL scripts, JSON/XML exports, SSIS
                        packages (.dtsx), CSV/TSV extracts, Parquet files, COBOL
                        copybooks, or PDF tables — and they're crawled straight
                        into a single source schema, no live database connection
                        required. Recognizes each file's format automatically
                        from its extension/content, and merges every table found
                        across all of them into one mappable source (pick a
                        whole folder to upload everything in it at once). For an
                        SSIS package, each Data Flow source/destination
                        component becomes one table — the "Review Package
                        Contents" panel below the parse result shows everything
                        else the package contained (connection managers, tasks,
                        every data-flow component, and variables).
                      </p>
                      <div className="flex flex-col gap-1.5 text-xs mb-3">
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="legacyUploadConsolidateMode"
                            className="mt-0.5"
                            checked={legacyConsolidateMode === "consolidate"}
                            onChange={() =>
                              setLegacyConsolidateMode("consolidate")
                            }
                          />
                          <span className="text-white/70">
                            Consolidate same-structure files into one table —
                            when several CSV/TSV/JSON/Parquet files share the
                            same columns (e.g. a batch of monthly exports of the
                            same table), merge their rows into a single source
                            table with a "Source_File" column tracing each row
                            back to its file.
                          </span>
                        </label>
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="legacyUploadConsolidateMode"
                            className="mt-0.5"
                            checked={legacyConsolidateMode === "separate"}
                            onChange={() =>
                              setLegacyConsolidateMode("separate")
                            }
                          />
                          <span className="text-white/70">
                            Import each file as its own table — every file
                            becomes its own separate source table with
                            independent mapping, regardless of structure.
                          </span>
                        </label>
                      </div>
                      {legacyFiles.length > 0 && (
                        <>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[11px] text-white/40">
                              {legacyFiles.length} file
                              {legacyFiles.length === 1 ? "" : "s"} selected
                            </span>
                            <button
                              onClick={() => setLegacyFiles([])}
                              className="text-[11px] text-white/50 hover:text-red-400"
                            >
                              Clear All
                            </button>
                          </div>
                          <ul className="mb-3 space-y-1">
                            {legacyFiles.map((f, i) => (
                              <li
                                key={`${f.name}-${i}`}
                                className="flex items-center justify-between gap-2 text-xs text-white/60 bg-white/5 rounded-lg px-2.5 py-1"
                              >
                                <span className="truncate">{f.name}</span>
                                <button
                                  onClick={() =>
                                    setLegacyFiles((prev) =>
                                      prev.filter((_, idx) => idx !== i),
                                    )
                                  }
                                  className="text-white/40 hover:text-red-400 flex-shrink-0"
                                  aria-label={`Remove ${f.name}`}
                                >
                                  <X size={12} />
                                </button>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                      <div className="flex items-center gap-3 flex-wrap">
                        <label className="ztd-btn ztd-btn-secondary cursor-pointer">
                          <Upload size={12} />
                          Choose File(s)
                          <input
                            type="file"
                            multiple
                            accept={_LEGACY_CODE_EXTENSIONS.join(",")}
                            className="hidden"
                            onChange={(e) => {
                              // Read files before resetting value — setting
                              // .value clears .files, and the state updater
                              // below runs after this handler returns, so a
                              // lazy re-read of e.target.files here would
                              // resolve to an already-empty FileList.
                              const picked = Array.from(e.target.files ?? []);
                              setLegacyFiles((prev) => [...prev, ...picked]);
                              e.target.value = "";
                            }}
                          />
                        </label>
                        <label className="ztd-btn ztd-btn-secondary cursor-pointer">
                          <Upload size={12} />
                          Choose Folder
                          <input
                            type="file"
                            multiple
                            // Folder selection is a non-standard (but broadly
                            // supported) HTML attribute with no React typing —
                            // cast to unknown props rather than fighting the
                            // JSX.IntrinsicElements input type.
                            {...({
                              webkitdirectory: "",
                              directory: "",
                            } as Record<string, string>)}
                            className="hidden"
                            onChange={(e) => {
                              const picked = Array.from(
                                e.target.files ?? [],
                              ).filter((f) =>
                                _LEGACY_CODE_EXTENSIONS.some((ext) =>
                                  f.name.toLowerCase().endsWith(ext),
                                ),
                              );
                              setLegacyFiles((prev) => [...prev, ...picked]);
                              e.target.value = "";
                            }}
                          />
                        </label>
                        <button
                          onClick={handleParseLegacyCode}
                          disabled={
                            parsingLegacyCode || legacyFiles.length === 0
                          }
                          className="ztd-btn ztd-btn-primary flex-shrink-0"
                        >
                          {parsingLegacyCode ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <ArrowRight size={14} />
                          )}
                          Parse & Continue
                          {legacyFiles.length > 1
                            ? ` (${legacyFiles.length} files)`
                            : ""}
                        </button>
                      </div>
                    </SectionCard>
                  )}

                  {legacySourceMethod === "upload" && (
                    <LegacyXmlImportCard
                      title="SAP BODS ETL Dataflow Import"
                      description={
                        <>
                          Upload one or more SAP BODS "Export as XML" dataflow
                          files (real{" "}
                          <code className="text-white/60">
                            &lt;DataIntegratorExport&gt;
                          </code>{" "}
                          job/workflow/dataflow definitions — not just table
                          schema). The join, lookup, and transformation logic
                          already in the BODS XML is parsed directly into an
                          STTM mapping, so there's no separate schema scan or
                          LLM-assisted mapping step for these files. Produces
                          four downloadable documents in one pass: the STTM
                          mapping workbook, source DDL, target DDL, and a
                          Job/Workflow/Dataflow audit workbook (everything the
                          parser found — and didn't find — for review).
                        </>
                      }
                      files={bodsDataflowFiles}
                      setFiles={setBodsDataflowFiles}
                      parsing={parsingBodsDataflow}
                      onParse={handleParseBodsDataflow}
                      parseButtonLabel="Parse BODS Dataflows & Generate Documents"
                      result={bodsDataflowResult}
                    />
                  )}

                  {legacySourceMethod === "upload" && (
                    <LegacyXmlImportCard
                      title="Informatica PowerCenter Mapping Import"
                      description={
                        <>
                          Upload one or more Informatica PowerCenter "Export to
                          XML" mapping files (real{" "}
                          <code className="text-white/60">
                            &lt;POWERMART&gt;
                          </code>{" "}
                          Source Qualifier/Expression/Joiner/Lookup/Filter
                          transformation logic — not just table schema). The
                          join, lookup, and transformation logic already in the
                          mapping XML is parsed directly into an STTM mapping,
                          so there's no separate schema scan or LLM-assisted
                          mapping step for these files. Produces four
                          downloadable documents in one pass: the STTM mapping
                          workbook, source DDL, target DDL, and a
                          Folder/Mapping/Target audit workbook (everything the
                          parser found — and didn't find — for review).
                        </>
                      }
                      files={informaticaMappingFiles}
                      setFiles={setInformaticaMappingFiles}
                      parsing={parsingInformaticaMapping}
                      onParse={handleParseInformaticaMapping}
                      parseButtonLabel="Parse Informatica Mappings & Generate Documents"
                      result={informaticaMappingResult}
                    />
                  )}

                  {legacySourceMethod === "sharepoint" && (
                    <SectionCard title="Generate Schema from SharePoint">
                      <p className="text-xs text-white/40 mb-3">
                        Connect to a SharePoint site, pick one or more Lists,
                        and they're crawled into a single source schema the same
                        way an uploaded file would be — no live database
                        connection required. Each selected list becomes one
                        table.
                      </p>
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <InputField
                            label="Tenant ID"
                            value={sharePointConn.tenant_id}
                            onChange={(v) =>
                              setSharePointConn((p) => ({
                                ...p,
                                tenant_id: v,
                              }))
                            }
                            placeholder="00000000-0000-0000-0000-000000000000"
                            required
                          />
                          <InputField
                            label="Client ID"
                            value={sharePointConn.client_id}
                            onChange={(v) =>
                              setSharePointConn((p) => ({
                                ...p,
                                client_id: v,
                              }))
                            }
                            required
                          />
                          <InputField
                            label="Client Secret"
                            value={sharePointConn.client_secret}
                            onChange={(v) =>
                              setSharePointConn((p) => ({
                                ...p,
                                client_secret: v,
                              }))
                            }
                            type="password"
                            required
                          />
                          <InputField
                            label="Site URL"
                            value={sharePointConn.site_url}
                            onChange={(v) =>
                              setSharePointConn((p) => ({
                                ...p,
                                site_url: v,
                              }))
                            }
                            placeholder="https://contoso.sharepoint.com/sites/TeamSite"
                            required
                          />
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => void handleSharePointConnect()}
                            disabled={
                              spConnecting ||
                              !sharePointConn.tenant_id ||
                              !sharePointConn.client_id ||
                              !sharePointConn.client_secret ||
                              !sharePointConn.site_url
                            }
                            className="ztd-btn ztd-btn-secondary"
                          >
                            {spConnecting ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <RefreshCw size={14} />
                            )}
                            {spConnected ? "Reconnect" : "Connect"}
                          </button>
                          {spConnected && (
                            <span className="flex items-center gap-1 text-xs text-green-400">
                              <CheckCircle2 size={14} /> Connected
                            </span>
                          )}
                        </div>
                        {spError && (
                          <InlineErrorBanner
                            message={spError}
                            onDismiss={() => setSpError(null)}
                          />
                        )}
                        {spConnected && (
                          <div className="rounded-lg border border-white/10">
                            <div className="flex items-center justify-between gap-2 border-b border-white/10 px-3 py-2">
                              <span className="text-xs text-white/60">
                                Lists
                              </span>
                              {spListing && (
                                <Loader2
                                  size={12}
                                  className="animate-spin text-white/40 flex-shrink-0"
                                />
                              )}
                            </div>
                            <ul className="max-h-56 overflow-y-auto divide-y divide-white/5">
                              {spLists.length === 0 && !spListing && (
                                <li className="px-3 py-2 text-xs text-white/40">
                                  No lists found on this site.
                                </li>
                              )}
                              {spLists.map((l) => (
                                <li
                                  key={l.id}
                                  className="flex items-center gap-1.5 pl-3"
                                >
                                  <input
                                    type="checkbox"
                                    className="flex-shrink-0 accent-brand-500"
                                    checked={spSelectedIds.has(l.id)}
                                    onChange={() =>
                                      handleSharePointToggleSelected(l.id)
                                    }
                                    aria-label={`Select ${l.name}`}
                                  />
                                  <label className="flex-1 flex items-center gap-2 px-2 py-1.5 text-xs text-white/70 cursor-pointer">
                                    <ListChecks
                                      size={12}
                                      className="flex-shrink-0 text-white/40"
                                    />
                                    <span className="truncate flex-1">
                                      {l.name}
                                    </span>
                                    <span className="text-white/35 flex-shrink-0">
                                      {l.item_count} item
                                      {l.item_count === 1 ? "" : "s"}
                                    </span>
                                  </label>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <button
                          onClick={handleParseLegacySharePoint}
                          disabled={
                            parsingLegacyCode || spSelectedIds.size === 0
                          }
                          className="ztd-btn ztd-btn-primary flex-shrink-0"
                        >
                          {parsingLegacyCode ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <ArrowRight size={14} />
                          )}
                          Parse & Continue
                          {spSelectedIds.size > 1
                            ? ` (${spSelectedIds.size} lists)`
                            : ""}
                        </button>
                      </div>
                    </SectionCard>
                  )}

                  {legacySourceMethod === "sftp" && (
                    <SectionCard title="Generate Schema from SFTP">
                      <p className="text-xs text-white/40 mb-3">
                        Connect to a remote SFTP server, browse into its
                        directories, and check one or more files — they're
                        downloaded and crawled into a single source schema the
                        same way an uploaded file would be. Recognizes each
                        file's format automatically (COBOL copybook, SQL DDL,
                        JSON/XML, SSIS, CSV/TSV, Parquet), same as the "Upload
                        File(s)" method. Credentials are request-scoped only and
                        never saved.
                      </p>
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <InputField
                            label="Host"
                            value={legacySftpConn.host}
                            onChange={(v) =>
                              setLegacySftpConn((p) => ({ ...p, host: v }))
                            }
                            placeholder="sftp.example.com"
                            required
                          />
                          <InputField
                            label="Port"
                            value={String(legacySftpConn.port ?? 22)}
                            onChange={(v) =>
                              setLegacySftpConn((p) => ({
                                ...p,
                                port: Number(v) || 22,
                              }))
                            }
                            placeholder="22"
                          />
                          <InputField
                            label="Username"
                            value={legacySftpConn.username ?? ""}
                            onChange={(v) =>
                              setLegacySftpConn((p) => ({
                                ...p,
                                username: v,
                              }))
                            }
                          />
                          <InputField
                            label="Password"
                            value={legacySftpConn.password ?? ""}
                            onChange={(v) =>
                              setLegacySftpConn((p) => ({
                                ...p,
                                password: v,
                              }))
                            }
                            type="password"
                            placeholder="Leave blank if using a private key"
                          />
                          <InputField
                            label="Private Key (optional)"
                            value={legacySftpConn.private_key ?? ""}
                            onChange={(v) =>
                              setLegacySftpConn((p) => ({
                                ...p,
                                private_key: v || null,
                              }))
                            }
                            type="password"
                            placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                          />
                          <InputField
                            label="Remote Directory"
                            value={legacySftpConn.remote_path ?? "."}
                            onChange={(v) =>
                              setLegacySftpConn((p) => ({
                                ...p,
                                remote_path: v || ".",
                              }))
                            }
                            placeholder="."
                          />
                          <div className="sm:col-span-2">
                            <InputField
                              label="Host Key Fingerprint (optional)"
                              value={legacySftpConn.host_key_fingerprint ?? ""}
                              onChange={(v) =>
                                setLegacySftpConn((p) => ({
                                  ...p,
                                  host_key_fingerprint: v || null,
                                }))
                              }
                              placeholder="SHA256:AbCdEf..."
                              hint="Get this with: ssh-keygen -lf <hostkey> -E sha256. Left blank, the connection trusts whatever host answers (no MITM protection)."
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={() => void handleLegacySftpConnect()}
                            disabled={
                              legacySftpConnecting || !legacySftpConn.host
                            }
                            className="ztd-btn ztd-btn-secondary"
                          >
                            {legacySftpConnecting ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <RefreshCw size={14} />
                            )}
                            {legacySftpConnected ? "Reconnect" : "Connect"}
                          </button>
                          {legacySftpConnected && (
                            <span className="flex items-center gap-1 text-xs text-green-400">
                              <CheckCircle2 size={14} /> Connected
                            </span>
                          )}
                        </div>
                        {legacySftpError && (
                          <InlineErrorBanner
                            message={legacySftpError}
                            onDismiss={() => setLegacySftpError(null)}
                          />
                        )}
                        {legacySftpConnected && (
                          <div className="rounded-lg border border-white/10">
                            <div className="flex items-center justify-between gap-2 border-b border-white/10 px-3 py-2">
                              <span className="text-xs text-white/60 truncate">
                                {legacySftpConn.remote_path}
                              </span>
                              {legacySftpListing && (
                                <Loader2
                                  size={12}
                                  className="animate-spin text-white/40 flex-shrink-0"
                                />
                              )}
                            </div>
                            <ul className="max-h-56 overflow-y-auto divide-y divide-white/5">
                              {legacySftpFiles.length === 0 &&
                                !legacySftpListing && (
                                  <li className="px-3 py-2 text-xs text-white/40">
                                    No files in this directory.
                                  </li>
                                )}
                              {legacySftpFiles.map((f) =>
                                f.is_dir ? (
                                  <li key={f.path}>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        void handleLegacySftpListFiles(f.path)
                                      }
                                      className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-white/70 hover:bg-white/5 text-left"
                                    >
                                      <Folder
                                        size={12}
                                        className="flex-shrink-0 text-brand-300"
                                      />
                                      <span className="truncate flex-1">
                                        {f.name}
                                      </span>
                                    </button>
                                  </li>
                                ) : (
                                  <li
                                    key={f.path}
                                    className="flex items-center gap-1.5 pl-3"
                                  >
                                    <input
                                      type="checkbox"
                                      className="flex-shrink-0 accent-brand-500"
                                      checked={legacySftpSelectedPaths.has(
                                        f.path,
                                      )}
                                      onChange={() =>
                                        handleLegacySftpToggleSelected(f.path)
                                      }
                                      aria-label={`Select ${f.name}`}
                                    />
                                    <label className="flex-1 flex items-center gap-2 px-2 py-1.5 text-xs text-white/70 cursor-pointer">
                                      <FileIcon
                                        size={12}
                                        className="flex-shrink-0 text-white/40"
                                      />
                                      <span className="truncate flex-1">
                                        {f.name}
                                      </span>
                                      <span className="text-white/35 flex-shrink-0">
                                        {(f.size / 1024).toFixed(1)} KB
                                      </span>
                                    </label>
                                  </li>
                                ),
                              )}
                            </ul>
                          </div>
                        )}
                        <div className="flex flex-col gap-1.5 text-xs">
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="legacySftpConsolidateMode"
                              className="mt-0.5"
                              checked={legacyConsolidateMode === "consolidate"}
                              onChange={() =>
                                setLegacyConsolidateMode("consolidate")
                              }
                            />
                            <span className="text-white/70">
                              Consolidate same-structure files into one table —
                              when several selected CSV/TSV/JSON/Parquet files
                              share the same columns, merge their rows into a
                              single source table with a "Source_File" column
                              tracing each row back to its file.
                            </span>
                          </label>
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="legacySftpConsolidateMode"
                              className="mt-0.5"
                              checked={legacyConsolidateMode === "separate"}
                              onChange={() =>
                                setLegacyConsolidateMode("separate")
                              }
                            />
                            <span className="text-white/70">
                              Import each file as its own table — every selected
                              file becomes its own separate source table with
                              independent mapping, regardless of structure.
                            </span>
                          </label>
                        </div>
                        <button
                          onClick={handleParseLegacySftp}
                          disabled={
                            parsingLegacyCode ||
                            legacySftpSelectedPaths.size === 0
                          }
                          className="ztd-btn ztd-btn-primary flex-shrink-0"
                        >
                          {parsingLegacyCode ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <ArrowRight size={14} />
                          )}
                          Parse & Continue
                          {legacySftpSelectedPaths.size > 1
                            ? ` (${legacySftpSelectedPaths.size} files)`
                            : ""}
                        </button>
                      </div>
                    </SectionCard>
                  )}

                  {legacySourceMethod === "pdf" && (
                    <SectionCard title="Generate Schema from PDF">
                      <p className="text-xs text-white/40 mb-3">
                        Upload one or more PDFs and automatically detect their
                        tables (no LLM involved) — they're crawled into a single
                        source schema the same way an uploaded file would be.
                        Image-only/scanned pages are read via OCR automatically.
                      </p>
                      <div className="space-y-3">
                        <div className="flex flex-col gap-1.5 text-xs">
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="pdfConsolidateMode"
                              className="mt-0.5"
                              checked={pdfConsolidateMode === "consolidate"}
                              onChange={() =>
                                setPdfConsolidateMode("consolidate")
                              }
                            />
                            <span className="text-white/70">
                              Consolidate similar PDFs into one table — when
                              several PDFs share the same layout (e.g. a batch
                              of the same memo/form template), merge their rows
                              into a single source table with a "Source_File"
                              column tracing each row back to its file.
                            </span>
                          </label>
                          <label className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="pdfConsolidateMode"
                              className="mt-0.5"
                              checked={pdfConsolidateMode === "separate"}
                              onChange={() => setPdfConsolidateMode("separate")}
                            />
                            <span className="text-white/70">
                              Import each PDF as its own table — every detected
                              table becomes its own separate source table, same
                              as the regular multi-file Legacy Code flow.
                            </span>
                          </label>
                        </div>
                        {legacyPdfUploads.length > 0 && (
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-[11px] text-white/40">
                              {legacyPdfUploads.length} file
                              {legacyPdfUploads.length > 1 ? "s" : ""} selected
                            </span>
                            <button
                              onClick={() => {
                                setLegacyPdfUploads([]);
                                setPdfError(null);
                                setPdfPreviewResult(null);
                              }}
                              className="text-[11px] text-white/40 hover:text-red-400 flex-shrink-0"
                            >
                              Clear All
                            </button>
                          </div>
                        )}
                        {legacyPdfUploads.length > 0 && (
                          <ul className="space-y-1 max-h-56 overflow-y-auto pr-1">
                            {legacyPdfUploads.map((u, i) => (
                              <li
                                key={`${u.filename}-${i}`}
                                className="flex items-center justify-between gap-2 text-xs text-white/60 bg-white/5 rounded-lg px-2.5 py-1"
                              >
                                <span className="truncate">
                                  {u.filename} (
                                  {(u.size_bytes / 1024).toFixed(1)} KB)
                                </span>
                                <button
                                  onClick={() =>
                                    setLegacyPdfUploads((prev) =>
                                      prev.filter((_, idx) => idx !== i),
                                    )
                                  }
                                  className="text-white/40 hover:text-red-400 flex-shrink-0"
                                  aria-label={`Remove ${u.filename}`}
                                >
                                  <X size={12} />
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        {pdfError && (
                          <InlineErrorBanner
                            message={pdfError}
                            onDismiss={() => setPdfError(null)}
                          />
                        )}
                        <div className="flex items-center gap-3 flex-wrap">
                          <label
                            className={`ztd-btn ztd-btn-secondary cursor-pointer ${pdfExtracting ? "opacity-40 pointer-events-none" : ""}`}
                          >
                            {pdfExtracting ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <FileText size={14} />
                            )}
                            Choose PDF File(s)
                            <input
                              type="file"
                              accept=".pdf"
                              multiple
                              className="hidden"
                              onChange={(e) => {
                                const files = Array.from(e.target.files ?? []);
                                e.target.value = "";
                                if (files.length)
                                  void handlePdfFilesSelected(files);
                              }}
                            />
                          </label>
                          <button
                            onClick={() => void handlePreviewLegacyPdf()}
                            disabled={
                              pdfPreviewLoading || legacyPdfUploads.length === 0
                            }
                            className="ztd-btn ztd-btn-secondary flex-shrink-0"
                          >
                            {pdfPreviewLoading ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Eye size={14} />
                            )}
                            Preview Parsed Data
                          </button>
                          <button
                            onClick={handleParseLegacyPdf}
                            disabled={
                              parsingLegacyCode || legacyPdfUploads.length === 0
                            }
                            className="ztd-btn ztd-btn-primary flex-shrink-0"
                          >
                            {parsingLegacyCode ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <ArrowRight size={14} />
                            )}
                            Parse & Continue
                            {legacyPdfUploads.length > 1
                              ? ` (${legacyPdfUploads.length} files)`
                              : ""}
                          </button>
                        </div>
                        <p className="text-[11px] text-white/40">
                          Either way, every resulting source table flows through
                          mapping, load, and reconciliation like any other —
                          consolidation only applies when the PDFs actually
                          share the same layout; mismatched files always get
                          their own table regardless of which option is selected
                          above.
                        </p>
                      </div>
                    </SectionCard>
                  )}

                  {(pdfPreviewResult || pdfPreviewError || pdfPreviewLoading) &&
                    createPortal(
                      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div
                          className="absolute inset-0 bg-black/60"
                          onClick={() => {
                            setPdfPreviewResult(null);
                            setPdfPreviewError(null);
                            setPdfPreviewLoading(false);
                          }}
                        />
                        <div className="relative w-full max-w-3xl max-h-[85vh] bg-surface-500 border border-white/10 rounded-xl shadow-2xl p-5 space-y-4 overflow-y-auto">
                          <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                              <Eye size={16} className="text-brand-400" />
                              Preview Parsed PDF Data
                            </h3>
                            <button
                              onClick={() => {
                                setPdfPreviewResult(null);
                                setPdfPreviewError(null);
                                setPdfPreviewLoading(false);
                              }}
                              className="p-1 rounded-lg hover:bg-white/5 text-white/50 hover:text-white transition"
                            >
                              <X size={16} />
                            </button>
                          </div>
                          {pdfPreviewLoading && (
                            <div className="flex items-center gap-2 text-sm text-white/40 py-4 justify-center">
                              <Loader2 size={14} className="animate-spin" />{" "}
                              Extracting tables…
                            </div>
                          )}
                          {pdfPreviewError && (
                            <InlineErrorBanner
                              message={pdfPreviewError}
                              onDismiss={() => setPdfPreviewError(null)}
                            />
                          )}
                          {!pdfPreviewLoading &&
                            pdfPreviewResult?.files.map((f, fi) => (
                              <div
                                key={`${f.filename}-${fi}`}
                                className="space-y-2"
                              >
                                <p className="text-xs font-medium text-white/70 truncate">
                                  {f.filename}
                                </p>
                                {f.error && (
                                  <p className="text-xs text-amber-400/80">
                                    {f.error}
                                  </p>
                                )}
                                {f.tables.map((t) => (
                                  <div
                                    key={t.table_index}
                                    className="border border-white/10 rounded-lg overflow-hidden"
                                  >
                                    <div className="px-3 py-1.5 text-[11px] text-white/40 bg-white/[0.03] flex items-center justify-between gap-2">
                                      <span>
                                        Table {t.table_index}
                                        {t.page_number > 0
                                          ? ` (page ${t.page_number})`
                                          : ""}
                                      </span>
                                      <span className="flex items-center gap-2 flex-shrink-0">
                                        {t.row_count} row
                                        {t.row_count !== 1 ? "s" : ""}
                                        {t.truncated
                                          ? ` — showing first ${t.rows.length}`
                                          : ""}
                                        <button
                                          onClick={() =>
                                            downloadTableAsPdf(
                                              `${f.filename} — Table ${t.table_index}`,
                                              t.columns,
                                              t.rows,
                                              `${f.filename.replace(/\.pdf$/i, "")}_table_${t.table_index}_${filenameTimestamp()}.pdf`,
                                            )
                                          }
                                          className="ztd-btn ztd-btn-secondary ztd-btn-sm !px-1.5"
                                          title="Download this table as a PDF"
                                        >
                                          <FileText size={11} />
                                        </button>
                                        <button
                                          onClick={() =>
                                            downloadTableAsExcel(
                                              `Table ${t.table_index}`,
                                              t.columns,
                                              t.rows,
                                              `${f.filename.replace(/\.pdf$/i, "")}_table_${t.table_index}_${filenameTimestamp()}.xls`,
                                            )
                                          }
                                          className="ztd-btn ztd-btn-secondary ztd-btn-sm !px-1.5"
                                          title="Download this table as an Excel workbook"
                                        >
                                          <FileSpreadsheet size={11} />
                                        </button>
                                      </span>
                                    </div>
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-xs">
                                        <thead>
                                          <tr className="text-left text-white/40">
                                            {t.columns.map((c, ci) => (
                                              <th
                                                key={`${c}-${ci}`}
                                                className="py-1 px-3 font-mono whitespace-nowrap"
                                              >
                                                {c}
                                              </th>
                                            ))}
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {t.rows.map((row, ri) => (
                                            <tr
                                              key={ri}
                                              className="border-t border-white/5 text-white/70"
                                            >
                                              {row.map((cell, ci) => (
                                                <td
                                                  key={ci}
                                                  className="py-1 px-3 whitespace-nowrap"
                                                >
                                                  {cell}
                                                </td>
                                              ))}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ))}
                          <div className="flex justify-end pt-1">
                            <button
                              onClick={() => {
                                setPdfPreviewResult(null);
                                setPdfPreviewError(null);
                                setPdfPreviewLoading(false);
                              }}
                              className="ztd-btn ztd-btn-secondary text-xs"
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </div>,
                      document.body,
                    )}
                </>
              )}

              {sourceMode === "crm" && (
                <div className="space-y-4">
                  <SectionCard title="Generate Schema from CRM / SaaS Systems">
                    <p className="text-xs text-white/40 mb-3">
                      Connect to a Salesforce, Dynamics 365, Power Platform, or
                      Workday org, pick one or more objects/datasets, and their
                      records are extracted and crawled into a source schema the
                      same way an uploaded file would be — no live database
                      connection required. Each selected object becomes one
                      table. Extract from one org and load into any other target
                      (a different CRM/SaaS org, a plain database, or a file) —
                      this connection is separate from the Target Platform
                      step's connection below.
                    </p>
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        {[
                          { value: "salesforce" as const, label: "Salesforce" },
                          {
                            value: "dynamics365" as const,
                            label: "Dynamics 365",
                          },
                          {
                            value: "power_platform" as const,
                            label: "Power Platform",
                          },
                          { value: "workday" as const, label: "Workday" },
                        ].map((opt) => {
                          const active =
                            crmSourceConn.target_type === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => {
                                setCrmSourceConn((p) => ({
                                  ...p,
                                  target_type: opt.value,
                                }));
                                setCrmSourceConnected(false);
                                setCrmSourceObjects([]);
                                setCrmSourceSelectedObjects(new Set());
                              }}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                                active
                                  ? "bg-brand-500/20 border-brand-500/50 text-brand-300"
                                  : "bg-white/5 border-white/10 text-white/50 hover:text-white"
                              }`}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>

                      {crmSourceConn.target_type === "salesforce" ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <InputField
                            label="Instance URL"
                            value={crmSourceConn.instance_url ?? ""}
                            onChange={(v) =>
                              setCrmSourceConn((p) => ({
                                ...p,
                                instance_url: v,
                              }))
                            }
                            placeholder="https://yourorg.salesforce.com"
                            required
                          />
                          <InputField
                            label="Username"
                            value={crmSourceConn.username ?? ""}
                            onChange={(v) =>
                              setCrmSourceConn((p) => ({
                                ...p,
                                username: v,
                              }))
                            }
                            placeholder="admin@yourorg.com"
                            required
                          />
                          <InputField
                            label="Password"
                            value={crmSourceConn.password ?? ""}
                            onChange={(v) =>
                              setCrmSourceConn((p) => ({
                                ...p,
                                password: v,
                              }))
                            }
                            type="password"
                            required
                          />
                          <InputField
                            label="Security Token"
                            value={crmSourceConn.security_token ?? ""}
                            onChange={(v) =>
                              setCrmSourceConn((p) => ({
                                ...p,
                                security_token: v,
                              }))
                            }
                            type="password"
                            placeholder="From Setup → My Personal Info → Reset Security Token"
                            required
                          />
                          <InputField
                            label="Connected App Consumer Key (optional)"
                            value={crmSourceConn.consumer_key ?? ""}
                            onChange={(v) =>
                              setCrmSourceConn((p) => ({
                                ...p,
                                consumer_key: v || null,
                              }))
                            }
                          />
                          <InputField
                            label="Connected App Consumer Secret (optional)"
                            value={crmSourceConn.consumer_secret ?? ""}
                            onChange={(v) =>
                              setCrmSourceConn((p) => ({
                                ...p,
                                consumer_secret: v || null,
                              }))
                            }
                            type="password"
                          />
                        </div>
                      ) : crmSourceConn.target_type === "workday" ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <InputField
                            label="Workday Tenant"
                            value={crmSourceConn.tenant_id ?? ""}
                            onChange={(v) =>
                              setCrmSourceConn((p) => ({
                                ...p,
                                tenant_id: v || null,
                              }))
                            }
                            placeholder="Workday tenant name, e.g. acme_dept1"
                            required
                          />
                          <InputField
                            label="Integration System User Client ID"
                            value={crmSourceConn.client_id ?? ""}
                            onChange={(v) =>
                              setCrmSourceConn((p) => ({
                                ...p,
                                client_id: v || null,
                              }))
                            }
                            placeholder="Workday ISU API client ID"
                            required
                          />
                          <InputField
                            label="Client Secret"
                            value={crmSourceConn.client_secret ?? ""}
                            onChange={(v) =>
                              setCrmSourceConn((p) => ({
                                ...p,
                                client_secret: v || null,
                              }))
                            }
                            type="password"
                            required
                          />
                          <InputField
                            label="Refresh Token"
                            value={crmSourceConn.refresh_token ?? ""}
                            onChange={(v) =>
                              setCrmSourceConn((p) => ({
                                ...p,
                                refresh_token: v || null,
                              }))
                            }
                            type="password"
                            placeholder="ISU OAuth2 refresh token"
                            required
                          />
                          <InputField
                            label="Workday API Host"
                            value={crmSourceConn.resource_url ?? ""}
                            onChange={(v) =>
                              setCrmSourceConn((p) => ({
                                ...p,
                                resource_url: v || null,
                              }))
                            }
                            placeholder="https://wd2-impl-services1.workday.com"
                            required
                          />
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <InputField
                            label="Tenant ID"
                            value={crmSourceConn.tenant_id ?? ""}
                            onChange={(v) =>
                              setCrmSourceConn((p) => ({
                                ...p,
                                tenant_id: v || null,
                              }))
                            }
                            placeholder="Azure AD directory (tenant) ID"
                            required
                          />
                          <InputField
                            label="Client ID"
                            value={crmSourceConn.client_id ?? ""}
                            onChange={(v) =>
                              setCrmSourceConn((p) => ({
                                ...p,
                                client_id: v || null,
                              }))
                            }
                            placeholder="App registration application (client) ID"
                            required
                          />
                          <InputField
                            label="Client Secret"
                            value={crmSourceConn.client_secret ?? ""}
                            onChange={(v) =>
                              setCrmSourceConn((p) => ({
                                ...p,
                                client_secret: v || null,
                              }))
                            }
                            type="password"
                            required
                          />
                          <InputField
                            label="Resource / Environment URL"
                            value={crmSourceConn.resource_url ?? ""}
                            onChange={(v) =>
                              setCrmSourceConn((p) => ({
                                ...p,
                                resource_url: v || null,
                              }))
                            }
                            placeholder="https://yourorg.crm.dynamics.com"
                            required
                          />
                        </div>
                      )}

                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => void handleCrmSourceConnect()}
                          disabled={
                            crmSourceConnecting ||
                            (crmSourceConn.target_type === "salesforce"
                              ? !crmSourceConn.instance_url ||
                                !crmSourceConn.username ||
                                !crmSourceConn.password ||
                                !crmSourceConn.security_token
                              : crmSourceConn.target_type === "workday"
                                ? !crmSourceConn.tenant_id ||
                                  !crmSourceConn.client_id ||
                                  !crmSourceConn.client_secret ||
                                  !crmSourceConn.refresh_token ||
                                  !crmSourceConn.resource_url
                                : !crmSourceConn.tenant_id ||
                                  !crmSourceConn.client_id ||
                                  !crmSourceConn.client_secret ||
                                  !crmSourceConn.resource_url)
                          }
                          className="ztd-btn ztd-btn-secondary"
                        >
                          {crmSourceConnecting ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <RefreshCw size={14} />
                          )}
                          {crmSourceConnected ? "Reconnect" : "Connect"}
                        </button>
                        {crmSourceConnected && (
                          <span className="flex items-center gap-1 text-xs text-green-400">
                            <CheckCircle2 size={14} /> Connected
                          </span>
                        )}
                      </div>
                      {crmSourceError && (
                        <InlineErrorBanner
                          message={crmSourceError}
                          onDismiss={() => setCrmSourceError(null)}
                        />
                      )}
                      {crmSourceLoadingObjects && (
                        <div className="flex items-center gap-2 text-xs text-white/40 py-2">
                          <Loader2 size={12} className="animate-spin" /> Loading
                          available objects…
                        </div>
                      )}
                      {crmSourceConnected && !crmSourceLoadingObjects && (
                        <div className="rounded-lg border border-white/10">
                          <div className="flex items-center justify-between gap-2 border-b border-white/10 px-3 py-2">
                            <span className="text-xs text-white/60">
                              Objects
                            </span>
                          </div>
                          <ul className="max-h-56 overflow-y-auto divide-y divide-white/5">
                            {crmSourceObjects.length === 0 && (
                              <li className="px-3 py-2 text-xs text-white/40">
                                No objects found.
                              </li>
                            )}
                            {crmSourceObjects.map((o) => (
                              <li
                                key={o.api_name}
                                className="flex items-center gap-1.5 pl-3"
                              >
                                <input
                                  type="checkbox"
                                  className="flex-shrink-0 accent-brand-500"
                                  checked={crmSourceSelectedObjects.has(
                                    o.api_name,
                                  )}
                                  onChange={() =>
                                    handleCrmSourceToggleSelected(o.api_name)
                                  }
                                  aria-label={`Select ${o.label}`}
                                />
                                <label className="flex-1 flex items-center gap-2 px-2 py-1.5 text-xs text-white/70 cursor-pointer">
                                  <span className="truncate flex-1">
                                    {o.label}{" "}
                                    <span className="text-white/35">
                                      ({o.api_name})
                                    </span>
                                  </span>
                                </label>
                                <button
                                  type="button"
                                  onClick={() => openCrmStreamLoad(o)}
                                  className="flex-shrink-0 mr-2 flex items-center gap-1 text-[11px] text-brand-400 hover:text-brand-300 transition"
                                  title="Stream this object's records directly into a target DB table, without extracting a full CSV first"
                                >
                                  <ArrowRight size={12} />
                                  Stream to DB
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <label className="flex items-center gap-2 text-xs text-white/50">
                        <input
                          type="checkbox"
                          className="accent-brand-500"
                          checked={crmParseSampleOnly}
                          onChange={(e) =>
                            setCrmParseSampleOnly(e.target.checked)
                          }
                        />
                        Preview sample only (schema/mapping preview, not the
                        full object)
                      </label>
                      <button
                        onClick={handleParseLegacyCrm}
                        disabled={
                          parsingLegacyCode ||
                          crmSourceSelectedObjects.size === 0
                        }
                        className="ztd-btn ztd-btn-primary flex-shrink-0"
                      >
                        {parsingLegacyCode ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <ArrowRight size={14} />
                        )}
                        Parse & Continue
                        {crmSourceSelectedObjects.size > 1
                          ? ` (${crmSourceSelectedObjects.size} objects)`
                          : ""}
                      </button>
                    </div>
                  </SectionCard>
                  {crmStreamModalObject &&
                    createPortal(
                      <CrmStreamLoadModal
                        object={crmStreamModalObject}
                        config={crmStreamConfig}
                        onConfigChange={setCrmStreamConfig}
                        hasTargetConnection={!!targetConnectionId}
                        starting={crmStreamStarting}
                        jobId={crmStreamJobId}
                        status={crmStreamStatus}
                        error={crmStreamError}
                        onErrorDismiss={() => setCrmStreamError(null)}
                        onStart={() => void handleStartCrmStreamLoad()}
                        onCancel={() => void handleCancelCrmStreamLoad()}
                        onClose={closeCrmStreamLoad}
                      />,
                      document.body,
                    )}
                </div>
              )}

              {sourceMode === "database" && (
                <>
                  {/* 2026-09-08, requested by user: the connection card and
                  saved-connections library used to stack full-width,
                  forcing a lot of scrolling to reach Scan Schema DDL below
                  — side by side on wider screens instead. */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                    <SectionCard title="Source Database Connection">
                      {sourceConnectionId ? (
                        // 2026-09-08: flex-wrap so the button group drops to
                        // its own line instead of crushing this status text
                        // down to a near-zero width in the half-width column.
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-2 text-sm text-green-400">
                              <CheckCircle2 size={14} />
                              Connected —{" "}
                              {sourceConnLabel || "saved connection"}
                              {connStatus &&
                                ` · ${connStatus.schemas.length} schema(s) found`}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                onClick={handleRefreshSourceConnection}
                                disabled={refreshingSourceConnection}
                                title="Re-test this connection — a saved/activated connection isn't verified until you test it"
                                className="ztd-btn ztd-btn-secondary"
                              >
                                {refreshingSourceConnection ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <RefreshCw size={14} />
                                )}
                                Refresh Connection
                              </button>
                              <button
                                onClick={() => setShowSourceConnectDialog(true)}
                                className="ztd-btn ztd-btn-secondary"
                              >
                                <RefreshCw size={14} />
                                Change connection
                              </button>
                            </div>
                          </div>

                          {/* 2026-09-15, requested by user: this connection's
                          own schema picker was rendered AFTER the "Additional
                          source connections" list, making it look orphaned /
                          attached to the extra connections instead of to this
                          (primary) connection. Keep it directly under this
                          connection's own status so each connection's schema
                          detail stays visually grouped with that connection. */}
                          {connStatus && connStatus.schemas.length > 0 && (
                            <div className="flex flex-wrap items-center gap-2 px-2.5 py-1.5 rounded-md border bg-white/5 border-white/10">
                              <span className="text-xs text-white/40">
                                This connection's schema:
                              </span>
                              {showSchemaPicker ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-64">
                                    <FieldSearchSelect
                                      value={sourceSchemaName}
                                      options={connStatus.schemas.map((s) => ({
                                        value: s,
                                        label: s,
                                      }))}
                                      onChange={(v) => {
                                        setSourceSchemaName(v);
                                        setShowSchemaPicker(false);
                                      }}
                                      placeholder="Search schemas…"
                                      className="w-full px-2.5 py-1 bg-surface-400/50 border border-white/10 rounded-md text-xs text-white focus:outline-none focus:border-brand-500/50 transition"
                                    />
                                  </div>
                                  <button
                                    onClick={() => setShowSchemaPicker(false)}
                                    className="text-xs text-white/40 hover:text-white transition"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1.5">
                                  <span className="px-2 py-0.5 rounded-md text-xs border bg-brand-500/20 border-brand-500/50 text-brand-300">
                                    {sourceSchemaName || connStatus.schemas[0]}
                                  </span>
                                  <button
                                    onClick={() => setShowSchemaPicker(true)}
                                    className="text-xs text-white/40 hover:text-white transition underline decoration-dotted underline-offset-2"
                                  >
                                    Change Schema
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setShowSourceConnectDialog(true)}
                            className="ztd-btn ztd-btn-primary"
                          >
                            <RefreshCw size={14} />
                            Connect Database
                          </button>
                          <span className="text-xs text-white/40">
                            Reuse a connection any agent already saved for this
                            project, or connect a new one.
                          </span>
                        </div>
                      )}

                      {extraSourceConnections.length > 0 && (
                        <div className="pt-3 flex flex-col gap-2">
                          <span className="text-xs text-white/40">
                            Additional source connections:
                          </span>
                          {extraSourceConnections.map((c) => (
                            <div
                              key={c.id}
                              className="flex flex-wrap items-center gap-2 px-2.5 py-1.5 rounded-md border bg-brand-500/10 border-brand-500/30"
                            >
                              <div className="flex items-center gap-1.5 text-xs text-green-400">
                                <CheckCircle2 size={12} />
                                Connected — {c.label}
                                {c.schemas.length > 0 &&
                                  ` · ${c.schemas.length} schema(s) found`}
                              </div>
                              {c.schemas.length > 0 &&
                                (extraSchemaPickerId === c.id ? (
                                  <div className="flex items-center gap-2">
                                    <div className="w-56">
                                      <FieldSearchSelect
                                        value={c.schemaName}
                                        options={c.schemas.map((s) => ({
                                          value: s,
                                          label: s,
                                        }))}
                                        onChange={(v) =>
                                          handleChangeExtraSourceSchema(c.id, v)
                                        }
                                        placeholder="Search schemas…"
                                        className="w-full px-2 py-0.5 bg-surface-400/50 border border-white/10 rounded-md text-xs text-white focus:outline-none focus:border-brand-500/50 transition"
                                      />
                                    </div>
                                    <button
                                      onClick={() =>
                                        setExtraSchemaPickerId(null)
                                      }
                                      className="text-xs text-white/40 hover:text-white transition"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1.5">
                                    <span className="px-2 py-0.5 rounded-md text-xs border bg-brand-500/20 border-brand-500/50 text-brand-300">
                                      {c.schemaName || c.schemas[0]}
                                    </span>
                                    <button
                                      onClick={() =>
                                        setExtraSchemaPickerId(c.id)
                                      }
                                      className="text-xs text-white/40 hover:text-white transition underline decoration-dotted underline-offset-2"
                                    >
                                      Change Schema
                                    </button>
                                  </div>
                                ))}
                              <button
                                onClick={() =>
                                  handleRemoveExtraSourceConnection(c.id)
                                }
                                title="Remove this additional connection"
                                className="ml-auto text-brand-200/60 hover:text-red-400 transition"
                              >
                                <X size={11} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </SectionCard>

                    {/* 2026-09-02, requested by user: a project can have
                    several saved source connections (different servers/DBs) —
                    always show the library, not just before the first one is
                    connected, so switching to a different one doesn't require
                    reconnecting from scratch. */}
                    {projectId && (
                      <SavedConnectionsPanel
                        projectId={projectId}
                        kind="source"
                        onActivated={handleSourceConnectionActivatedFromLibrary}
                        onConnectExisting={handleSourceConnectExisting}
                      />
                    )}
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    {/* 2026-09-16, requested by user: moved next to
                    "Scan Schema DDL" instead of inside the connection card,
                    so it reads as the next step after this connection is
                    set up rather than another action on the connection
                    itself. */}
                    {sourceConnectionId ? (
                      <button
                        onClick={() => setShowAddSourceConnectionDialog(true)}
                        className="ztd-btn ztd-btn-secondary"
                        title="Connect an additional source database — lets STTM-Mapping rows pull tables from more than one source connection"
                      >
                        <Plus size={14} />
                        Add Another Source Connection
                      </button>
                    ) : (
                      <div />
                    )}
                    <button
                      onClick={handleScanSchema}
                      disabled={
                        scanningSchema || !connStatus || !sourceSchemaName
                      }
                      className="ztd-btn ztd-btn-primary"
                    >
                      {scanningSchema ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <ArrowRight size={14} />
                      )}
                      Scan Schema DDL
                    </button>
                  </div>

                  <ConnectDatabaseDialog
                    open={showSourceConnectDialog}
                    onClose={() => setShowSourceConnectDialog(false)}
                    title="Connect Source Database"
                    projectId={projectId}
                    kind="source"
                    agentKey="data_converter"
                    defaultDbType="oracle"
                    testIdPrefix="converter-source"
                    onConnectExisting={handleSourceConnectExisting}
                    onConnectNew={handleSourceConnectNew}
                  />

                  <ConnectDatabaseDialog
                    open={showAddSourceConnectionDialog}
                    onClose={() => setShowAddSourceConnectionDialog(false)}
                    title="Add Another Source Connection"
                    projectId={projectId}
                    kind="source"
                    agentKey="data_converter"
                    defaultDbType="oracle"
                    testIdPrefix="converter-source-extra"
                    onConnectExisting={handleAddSourceConnectionExisting}
                    onConnectNew={handleAddSourceConnectionNew}
                  />
                </>
              )}

              {/* 2026-09-08, issue #621: promoted out of the "database" tab
              above into its own top-level Source Type — see sourceMode's
              declaration for context. */}
              {sourceMode === "mainframe" && (
                <>
                  <SectionCard title="Mainframe / JDBC Bridge / Copybook">
                    <p className="text-[11px] text-white/30 mb-3">
                      For sources ConnectDatabaseDialog doesn't cover — a live
                      vendor-agnostic JDBC connection (DB2 for z/OS, an ADABAS
                      SQL Gateway, ...) or a mainframe flat-file extract
                      described by a COBOL copybook (no live connection).
                    </p>
                    <div className="flex gap-2 mb-4">
                      {[
                        {
                          kind: "jdbc_bridge" as const,
                          label: "Mainframe / JDBC Bridge",
                          defaultType: "jdbc_bridge" as const,
                        },
                        {
                          kind: "mainframe_copybook" as const,
                          label: "Mainframe (Copybook / Flat File)",
                          defaultType: "mainframe_copybook" as const,
                        },
                      ].map((opt) => {
                        const active =
                          connectionKindOf(mainframeConn.db_type) === opt.kind;
                        return (
                          <button
                            key={opt.kind}
                            onClick={() =>
                              setMainframeConn((p) => ({
                                ...p,
                                db_type: opt.defaultType,
                              }))
                            }
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                              active
                                ? "bg-brand-500/20 border-brand-500/50 text-brand-300"
                                : "bg-white/5 border-white/10 text-white/50 hover:text-white"
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>

                    {connectionKindOf(mainframeConn.db_type) ===
                      "jdbc_bridge" && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <InputField
                          label="Bridge Label"
                          value={mainframeConn.bridge_label ?? ""}
                          onChange={(v) =>
                            setMainframeConn((p) => ({
                              ...p,
                              bridge_label: v || null,
                            }))
                          }
                          placeholder="e.g. DB2 for z/OS, ADABAS SQL Gateway"
                        />
                        <InputField
                          label="Host"
                          value={mainframeConn.host}
                          onChange={(v) =>
                            setMainframeConn((p) => ({ ...p, host: v }))
                          }
                          placeholder="mainframe.example.com"
                        />
                        <InputField
                          label="Port"
                          value={String(mainframeConn.port)}
                          onChange={(v) =>
                            setMainframeConn((p) => ({
                              ...p,
                              port: Number(v) || p.port,
                            }))
                          }
                          type="number"
                        />
                        <InputField
                          label="Schema to Scan"
                          value={mainframeConn.schema_name}
                          onChange={(v) =>
                            setMainframeConn((p) => ({ ...p, schema_name: v }))
                          }
                          placeholder="DB2P"
                        />
                        <InputField
                          label="JDBC Driver Class"
                          value={mainframeConn.jdbc_driver_class ?? ""}
                          onChange={(v) =>
                            setMainframeConn((p) => ({
                              ...p,
                              jdbc_driver_class: v || null,
                            }))
                          }
                          placeholder="com.ibm.db2.jcc.DB2Driver"
                          required
                        />
                        <InputField
                          label="JDBC URL"
                          value={mainframeConn.jdbc_url ?? ""}
                          onChange={(v) =>
                            setMainframeConn((p) => ({
                              ...p,
                              jdbc_url: v || null,
                            }))
                          }
                          placeholder="jdbc:db2://mainframe.example.com:446/DB2P"
                          required
                        />
                        <InputField
                          label="Driver Jar Path (optional)"
                          value={mainframeConn.jdbc_driver_jar_path ?? ""}
                          onChange={(v) =>
                            setMainframeConn((p) => ({
                              ...p,
                              jdbc_driver_jar_path: v || null,
                            }))
                          }
                          placeholder="/opt/drivers/db2jcc4.jar"
                        />
                        <InputField
                          label="Username"
                          value={mainframeConn.username}
                          onChange={(v) =>
                            setMainframeConn((p) => ({ ...p, username: v }))
                          }
                        />
                        <InputField
                          label="Password"
                          value={mainframeConn.password}
                          onChange={(v) =>
                            setMainframeConn((p) => ({ ...p, password: v }))
                          }
                          type="password"
                        />
                        <p className="sm:col-span-2 text-[11px] text-white/30">
                          Live, vendor-agnostic JDBC connection — works with DB2
                          for z/OS, an ADABAS SQL Gateway, or any other
                          JDBC-reachable mainframe bridge. Requires the API
                          image built with{" "}
                          <span className="font-mono">
                            ZTD_ENABLE_JDBC_BRIDGE=true
                          </span>{" "}
                          (adds a JVM) and your own licensed driver .jar.
                        </p>
                      </div>
                    )}

                    {connectionKindOf(mainframeConn.db_type) ===
                      "mainframe_copybook" && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <InputField
                            label="Table Name"
                            value={mainframeConn.database}
                            onChange={(v) =>
                              setMainframeConn((p) => ({ ...p, database: v }))
                            }
                            placeholder="CUSTOMER_EXTRACT"
                          />
                          <InputField
                            label="Schema (optional)"
                            value={mainframeConn.schema_name}
                            onChange={(v) =>
                              setMainframeConn((p) => ({
                                ...p,
                                schema_name: v,
                              }))
                            }
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-white/50 mb-1.5">
                            COBOL Copybook{" "}
                            <span className="text-red-400">*</span>
                          </label>
                          <textarea
                            value={mainframeConn.copybook_text ?? ""}
                            onChange={(e) =>
                              setMainframeConn((p) => ({
                                ...p,
                                copybook_text: e.target.value || null,
                              }))
                            }
                            rows={8}
                            placeholder={
                              "05 ACCT-ID        PIC 9(5).\n05 CUSTOMER-NAME  PIC X(10).\n05 BALANCE-CENTS  PIC 9(6)."
                            }
                            className="w-full px-3 py-2 bg-surface-400/50 border border-white/10 rounded-lg text-xs text-white font-mono focus:outline-none focus:border-brand-500/50 transition"
                          />
                        </div>
                        <p className="text-[11px] text-white/30">
                          No live connection — this parses the copybook into a
                          fixed-width field layout (the same mainframe/ADABAS
                          extract handling the Data Converter agent already
                          has), so it flows through the same Test/Scan/Mapping
                          pipeline as a live database.
                        </p>
                      </div>
                    )}

                    <div className="flex items-center gap-3 pt-2">
                      <button
                        onClick={handleMainframeSourceConnect}
                        disabled={
                          connectingMainframe ||
                          (connectionKindOf(mainframeConn.db_type) ===
                          "jdbc_bridge"
                            ? !mainframeConn.jdbc_driver_class ||
                              !mainframeConn.jdbc_url
                            : !(mainframeConn.copybook_text ?? "").trim())
                        }
                        className="ztd-btn ztd-btn-secondary"
                      >
                        {connectingMainframe ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Link size={14} />
                        )}
                        Connect
                      </button>
                    </div>
                  </SectionCard>

                  <div className="flex justify-end">
                    <button
                      onClick={handleScanSchema}
                      disabled={
                        scanningSchema || !connStatus || !sourceSchemaName
                      }
                      className="ztd-btn ztd-btn-primary"
                    >
                      {scanningSchema ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <ArrowRight size={14} />
                      )}
                      Scan Schema DDL
                    </button>
                  </div>
                </>
              )}

              {sourceMode === "file" && (
                <div className="space-y-4">
                  {loaderEntryChoice === "pdf" && fileWizardStep === 1 ? (
                    <SectionCard title="Extract Tables from PDF">
                      <p className="text-xs text-white/40 mb-3">
                        Upload a PDF, automatically detect its tables (no LLM
                        involved), and load one straight into a database table —
                        no STTM mapping needed since the detected table defines
                        the target. Image-only/scanned pages are read via OCR
                        automatically.
                      </p>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 flex-wrap">
                          <label
                            className={`ztd-btn ztd-btn-secondary cursor-pointer ${pdfExtracting ? "opacity-40 pointer-events-none" : ""}`}
                          >
                            {pdfExtracting ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <FileText size={14} />
                            )}
                            Choose PDF File
                            <input
                              type="file"
                              accept=".pdf"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                e.target.value = "";
                                if (file) void handlePdfFileSelected(file);
                              }}
                            />
                          </label>
                          {fileUpload && (
                            <span className="flex items-center gap-1 text-xs text-green-400">
                              <CheckCircle2 size={14} /> {fileUpload.filename} (
                              {(fileUpload.size_bytes / 1024).toFixed(1)} KB)
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="text-xs text-white/40 flex-shrink-0">
                            or import every PDF in a folder:
                          </span>
                          <label
                            className={`ztd-btn ztd-btn-secondary cursor-pointer ${pdfFolderLoading ? "opacity-40 pointer-events-none" : ""}`}
                          >
                            {pdfFolderLoading ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Folder size={14} />
                            )}
                            Import Folder
                            <input
                              type="file"
                              multiple
                              accept=".pdf"
                              className="hidden"
                              {...({
                                webkitdirectory: "",
                                directory: "",
                              } as Record<string, string>)}
                              onChange={(e) => {
                                const files = Array.from(e.target.files ?? []);
                                e.target.value = "";
                                if (files.length > 0) {
                                  void handlePdfFolderFilesSelected(files);
                                }
                              }}
                            />
                          </label>
                          {pdfFolderName && (
                            <span className="flex items-center gap-1 text-xs text-green-400">
                              <CheckCircle2 size={14} /> {pdfFolderName}
                            </span>
                          )}
                        </div>
                        {pdfError && (
                          <InlineErrorBanner
                            message={pdfError}
                            onDismiss={() => setPdfError(null)}
                          />
                        )}
                        {pdfTables.length > 0 && (
                          <div className="rounded-lg border border-white/10">
                            <div className="flex items-center justify-between gap-2 border-b border-white/10 px-3 py-2">
                              <span className="text-xs text-white/60">
                                Detected Tables
                              </span>
                            </div>
                            <ul className="max-h-56 overflow-y-auto divide-y divide-white/5">
                              {pdfTables.map((t) => (
                                <li
                                  key={t.table_index}
                                  className="flex items-center"
                                >
                                  <button
                                    type="button"
                                    disabled={
                                      pdfUsingIndex !== null &&
                                      pdfUsingIndex !== t.table_index
                                    }
                                    onClick={() => void handlePdfUseTable(t)}
                                    className="flex-1 flex items-center gap-2 px-3 py-1.5 text-xs text-white/70 hover:bg-white/5 text-left disabled:opacity-40"
                                  >
                                    {pdfUsingIndex === t.table_index ? (
                                      <Loader2
                                        size={12}
                                        className="animate-spin flex-shrink-0"
                                      />
                                    ) : (
                                      <Table2
                                        size={12}
                                        className="flex-shrink-0 text-white/40"
                                      />
                                    )}
                                    <span className="truncate flex-1">
                                      {t.source_file_count ? (
                                        <>
                                          Consolidated from{" "}
                                          {t.source_file_count} files
                                        </>
                                      ) : (
                                        <>
                                          Table {t.table_index} (page{" "}
                                          {t.page_number})
                                        </>
                                      )}
                                    </span>
                                    <span className="text-white/35 flex-shrink-0">
                                      {t.row_count} rows × {t.column_count} cols
                                    </span>
                                  </button>
                                  {t.download_url && (
                                    <a
                                      href={t.download_url}
                                      download={t.filename}
                                      title="Download CSV"
                                      className="px-2 py-1.5 text-white/40 hover:text-brand-300 flex-shrink-0"
                                    >
                                      <Download size={12} />
                                    </a>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </SectionCard>
                  ) : (
                    <>
                      {loaderEntryChoice === "file" && (
                        <div className="flex items-center gap-2 pb-1 flex-wrap">
                          <span className="text-xs font-medium text-white/50">
                            Load:
                          </span>
                          <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
                            <button
                              type="button"
                              onClick={() => setFileIngestMode("single")}
                              className={`px-3 py-1.5 text-xs font-medium transition ${
                                fileIngestMode === "single"
                                  ? "bg-brand-500/20 text-brand-300"
                                  : "text-white/50 hover:text-white"
                              }`}
                            >
                              Single File Upload
                            </button>
                            <button
                              type="button"
                              onClick={() => setFileIngestMode("bulk")}
                              className={`px-3 py-1.5 text-xs font-medium transition ${
                                fileIngestMode === "bulk"
                                  ? "bg-brand-500/20 text-brand-300"
                                  : "text-white/50 hover:text-white"
                              }`}
                            >
                              Bulk Folder Ingest
                            </button>
                            <button
                              type="button"
                              onClick={() => setFileIngestMode("multi_format")}
                              className={`px-3 py-1.5 text-xs font-medium transition ${
                                fileIngestMode === "multi_format"
                                  ? "bg-brand-500/20 text-brand-300"
                                  : "text-white/50 hover:text-white"
                              }`}
                            >
                              Multiple Files (Different Formats)
                            </button>
                          </div>
                        </div>
                      )}

                      {fileIngestMode === "multi_format" && (
                        <SectionCard title="Multiple Files — Independent Load Flows">
                          <p className="mb-3 text-[11px] text-white/45">
                            Each file gets its own tab with a fully independent
                            Detected Columns / Build & Preview / Connect Target
                            / Load Data flow and its own target table — files
                            can be any mix of formats or structures, including
                            two of the same extension with different columns.
                          </p>
                          <div className="flex flex-wrap items-center gap-2">
                            {multiFormatTabs.map((tab, i) => {
                              const active = i === multiFormatActiveIndex;
                              // The active tab's snapshot is only captured on
                              // tab-add/switch, so it goes stale the moment a
                              // load completes in place -- read live state
                              // for the active tab instead of its snapshot.
                              const done = active
                                ? !!fileLoadResult
                                : !!tab.snapshot.fileLoadResult;
                              return (
                                <div
                                  key={tab.file_path}
                                  className={`flex items-center rounded-lg border transition ${
                                    active
                                      ? "bg-brand-500/20 text-brand-300 border-brand-500/40"
                                      : "bg-surface-400/40 text-white/60 border-white/10 hover:text-white/85"
                                  } ${multiFormatBusy ? "opacity-40 pointer-events-none" : ""}`}
                                >
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleMultiFormatSwitchTab(i)
                                    }
                                    disabled={multiFormatBusy}
                                    className="flex items-center gap-1.5 pl-3 pr-1.5 py-1.5 text-xs font-medium"
                                    title={tab.filename}
                                  >
                                    {done ? (
                                      <CheckCircle2
                                        size={12}
                                        className="text-green-400 flex-shrink-0"
                                      />
                                    ) : (
                                      <FileText
                                        size={12}
                                        className="flex-shrink-0"
                                      />
                                    )}
                                    <span className="max-w-[10rem] truncate">
                                      {tab.filename}
                                    </span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMultiFormatRemoveTab(i);
                                    }}
                                    disabled={multiFormatBusy}
                                    className="pr-2 pl-1 py-1.5 text-white/30 hover:text-red-400"
                                    title={`Remove ${tab.filename}`}
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              );
                            })}
                            <label
                              className={`ztd-btn ztd-btn-secondary cursor-pointer !py-1.5 !px-2.5 text-xs ${
                                multiFormatBusy
                                  ? "opacity-40 pointer-events-none"
                                  : ""
                              }`}
                            >
                              {multiFormatBusy ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <Plus size={12} />
                              )}
                              Add File(s)
                              <input
                                type="file"
                                multiple
                                accept=".txt,.dat,.fil,.csv,.tsv,.json,.jsonl,.parquet,.cpy,.cbl,.cob"
                                className="hidden"
                                onChange={(e) => {
                                  const files = Array.from(
                                    e.target.files ?? [],
                                  );
                                  e.target.value = "";
                                  if (files.length)
                                    void handleMultiFormatFilesSelected(files);
                                }}
                              />
                            </label>
                          </div>
                          {multiFormatError && (
                            <div className="mt-3">
                              <InlineErrorBanner
                                message={multiFormatError}
                                onDismiss={() => setMultiFormatError(null)}
                              />
                            </div>
                          )}
                          {multiFormatTabs.length === 0 && !multiFormatBusy && (
                            <p className="mt-3 text-xs text-white/40 italic">
                              No files added yet — click "Add File(s)" to get
                              started.
                            </p>
                          )}
                        </SectionCard>
                      )}

                      {(loaderEntryChoice !== "file" ||
                        fileIngestMode === "single" ||
                        (fileIngestMode === "multi_format" &&
                          multiFormatActiveIndex !== null)) && (
                        <div className="space-y-4">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-white/45 px-1">
                            {(
                              [
                                {
                                  step: 1 as const,
                                  label: "Upload file",
                                  unlocked: fileIngestMode !== "multi_format",
                                },
                                {
                                  step: 2 as const,
                                  label:
                                    fileFormatType === "fixed_width"
                                      ? "Define record layout"
                                      : "Columns detected",
                                  unlocked: !!fileUpload,
                                },
                                {
                                  step: 3 as const,
                                  label: "Build & Preview",
                                  unlocked: layoutFields.length > 0,
                                },
                                {
                                  step: 4 as const,
                                  label: "Connect target & table",
                                  unlocked: !!filePreviewResult,
                                },
                                {
                                  step: 5 as const,
                                  label: "Load Data",
                                  unlocked:
                                    !!filePreviewResult && !!fileTargetConn,
                                },
                              ] as const
                            ).map((s, i, arr) => (
                              <span
                                key={s.step}
                                className="flex items-center gap-2"
                              >
                                <button
                                  type="button"
                                  disabled={!s.unlocked}
                                  onClick={() => setFileWizardStep(s.step)}
                                  className={
                                    fileWizardStep === s.step
                                      ? "text-brand-300 font-semibold"
                                      : s.unlocked
                                        ? "text-white/70 hover:text-white transition"
                                        : "cursor-not-allowed text-white/25"
                                  }
                                >
                                  {s.step}. {s.label}
                                </button>
                                {i < arr.length - 1 && (
                                  <ArrowRight
                                    size={10}
                                    className="text-white/25"
                                  />
                                )}
                              </span>
                            ))}
                          </div>

                          {fileWizardStep === 1 &&
                            loaderEntryChoice === "file" && (
                              <div className="flex items-center gap-2 pb-1 flex-wrap">
                                <span className="text-xs font-medium text-white/50">
                                  File Format:
                                </span>
                                <div className="inline-flex rounded-lg border border-white/10 overflow-hidden flex-wrap">
                                  {(
                                    [
                                      {
                                        value: "fixed_width",
                                        label: "Fixed-Width",
                                      },
                                      {
                                        value: "copybook",
                                        label: "Mainframe Copybook",
                                      },
                                      {
                                        value: "delimited",
                                        label: "Delimited (CSV / TSV)",
                                      },
                                      {
                                        value: "structured",
                                        label: "Structured (JSON / Parquet)",
                                      },
                                    ] as const
                                  ).map((opt) => (
                                    <button
                                      key={opt.value}
                                      onClick={() =>
                                        void handleFileCategoryChange(opt.value)
                                      }
                                      className={`px-3 py-1.5 text-xs font-medium transition ${
                                        fileCategory === opt.value
                                          ? "bg-brand-500/20 text-brand-300"
                                          : "text-white/50 hover:text-white"
                                      }`}
                                    >
                                      {opt.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                          {fileWizardStep === 1 && (
                            <SectionCard title="Upload Source File">
                              <>
                                <div className="flex items-center gap-3 flex-wrap">
                                  <label
                                    className={`ztd-btn ztd-btn-secondary cursor-pointer ${fileBusy ? "opacity-40 pointer-events-none" : ""}`}
                                  >
                                    {fileBusy ? (
                                      <Loader2
                                        size={14}
                                        className="animate-spin"
                                      />
                                    ) : (
                                      <Upload size={14} />
                                    )}
                                    Choose File(s) to Import
                                    <input
                                      type="file"
                                      multiple
                                      accept=".txt,.dat,.fil,.csv,.tsv,.json,.jsonl,.parquet"
                                      className="hidden"
                                      onChange={(e) => {
                                        const files = Array.from(
                                          e.target.files ?? [],
                                        );
                                        e.target.value = "";
                                        if (files.length)
                                          void handleFileSourceFilesSelected(
                                            files,
                                          );
                                      }}
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    disabled={fileBusy}
                                    onClick={() => setShowSftpPanel((v) => !v)}
                                    className={`ztd-btn ztd-btn-secondary ${fileBusy ? "opacity-40 pointer-events-none" : ""}`}
                                  >
                                    <Server size={14} />
                                    Import from SFTP
                                  </button>
                                  {fileUpload && (
                                    <span className="flex items-center gap-1 text-xs text-green-400">
                                      <CheckCircle2 size={14} />{" "}
                                      {fileUpload.filename} (
                                      {(fileUpload.size_bytes / 1024).toFixed(
                                        1,
                                      )}{" "}
                                      KB)
                                    </span>
                                  )}
                                </div>
                                {showSftpPanel && (
                                  <div className="mt-3 rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                      <InputField
                                        label="Host"
                                        value={sftpConn.host}
                                        onChange={(v) =>
                                          setSftpConn((p) => ({
                                            ...p,
                                            host: v,
                                          }))
                                        }
                                        placeholder="sftp.example.com"
                                        required
                                      />
                                      <InputField
                                        label="Port"
                                        value={String(sftpConn.port ?? 22)}
                                        onChange={(v) =>
                                          setSftpConn((p) => ({
                                            ...p,
                                            port: Number(v) || 22,
                                          }))
                                        }
                                        placeholder="22"
                                      />
                                      <InputField
                                        label="Username"
                                        value={sftpConn.username ?? ""}
                                        onChange={(v) =>
                                          setSftpConn((p) => ({
                                            ...p,
                                            username: v,
                                          }))
                                        }
                                      />
                                      <InputField
                                        label="Password"
                                        value={sftpConn.password ?? ""}
                                        onChange={(v) =>
                                          setSftpConn((p) => ({
                                            ...p,
                                            password: v,
                                          }))
                                        }
                                        type="password"
                                        placeholder="Leave blank if using a private key"
                                      />
                                      <InputField
                                        label="Private Key (optional)"
                                        value={sftpConn.private_key ?? ""}
                                        onChange={(v) =>
                                          setSftpConn((p) => ({
                                            ...p,
                                            private_key: v || null,
                                          }))
                                        }
                                        type="password"
                                        placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                                      />
                                      <InputField
                                        label="Remote Directory"
                                        value={sftpConn.remote_path ?? "."}
                                        onChange={(v) =>
                                          setSftpConn((p) => ({
                                            ...p,
                                            remote_path: v || ".",
                                          }))
                                        }
                                        placeholder="."
                                      />
                                      <div className="sm:col-span-2">
                                        <InputField
                                          label="Host Key Fingerprint (optional)"
                                          value={
                                            sftpConn.host_key_fingerprint ?? ""
                                          }
                                          onChange={(v) =>
                                            setSftpConn((p) => ({
                                              ...p,
                                              host_key_fingerprint: v || null,
                                            }))
                                          }
                                          placeholder="SHA256:AbCdEf..."
                                          hint="Get this with: ssh-keygen -lf <hostkey> -E sha256. Left blank, the connection trusts whatever host answers (no MITM protection)."
                                        />
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <button
                                        type="button"
                                        onClick={() => void handleSftpConnect()}
                                        disabled={
                                          sftpConnecting || !sftpConn.host
                                        }
                                        className="ztd-btn ztd-btn-secondary"
                                      >
                                        {sftpConnecting ? (
                                          <Loader2
                                            size={14}
                                            className="animate-spin"
                                          />
                                        ) : (
                                          <RefreshCw size={14} />
                                        )}
                                        {sftpConnected
                                          ? "Reconnect"
                                          : "Connect"}
                                      </button>
                                      {sftpConnected && (
                                        <span className="flex items-center gap-1 text-xs text-green-400">
                                          <CheckCircle2 size={14} /> Connected
                                        </span>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => setShowSftpPanel(false)}
                                        className="text-xs text-white/45 hover:text-white ml-auto"
                                      >
                                        Close
                                      </button>
                                    </div>
                                    {sftpError && (
                                      <InlineErrorBanner
                                        message={sftpError}
                                        onDismiss={() => setSftpError(null)}
                                      />
                                    )}
                                    {sftpConnected && (
                                      <div className="rounded-lg border border-white/10">
                                        <div className="flex items-center justify-between gap-2 border-b border-white/10 px-3 py-2">
                                          <span className="text-xs text-white/60 truncate">
                                            {sftpConn.remote_path}
                                          </span>
                                          {sftpListing && (
                                            <Loader2
                                              size={12}
                                              className="animate-spin text-white/40 flex-shrink-0"
                                            />
                                          )}
                                        </div>
                                        <ul className="max-h-56 overflow-y-auto divide-y divide-white/5">
                                          {sftpFiles.length === 0 &&
                                            !sftpListing && (
                                              <li className="px-3 py-2 text-xs text-white/40">
                                                No files in this directory.
                                              </li>
                                            )}
                                          {sftpFiles.map((f) => (
                                            <li key={f.path}>
                                              <button
                                                type="button"
                                                disabled={
                                                  !!sftpImportingPath &&
                                                  sftpImportingPath !== f.path
                                                }
                                                onClick={() =>
                                                  void handleSftpImportFile(f)
                                                }
                                                className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-white/70 hover:bg-white/5 text-left disabled:opacity-40"
                                              >
                                                {sftpImportingPath ===
                                                f.path ? (
                                                  <Loader2
                                                    size={12}
                                                    className="animate-spin flex-shrink-0"
                                                  />
                                                ) : f.is_dir ? (
                                                  <Folder
                                                    size={12}
                                                    className="flex-shrink-0 text-brand-300"
                                                  />
                                                ) : (
                                                  <FileIcon
                                                    size={12}
                                                    className="flex-shrink-0 text-white/40"
                                                  />
                                                )}
                                                <span className="truncate flex-1">
                                                  {f.name}
                                                </span>
                                                {!f.is_dir && (
                                                  <span className="text-white/35 flex-shrink-0">
                                                    {(f.size / 1024).toFixed(1)}{" "}
                                                    KB
                                                  </span>
                                                )}
                                              </button>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                              {fileUploadError && (
                                <div className="mt-3">
                                  <InlineErrorBanner
                                    message={fileUploadError}
                                    onDismiss={() => setFileUploadError(null)}
                                  />
                                </div>
                              )}
                              {additionalFileUploads.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  <p className="text-[11px] text-white/45">
                                    + {additionalFileUploads.length} more
                                    file(s), sharing the same layout/format —
                                    all will be loaded into the same target
                                    table:
                                  </p>
                                  <p className="text-[11px] text-amber-400">
                                    If these are actually DIFFERENT tables (not
                                    the same table split across files), use the
                                    "Files/Servers/Legacy Code" source type
                                    instead — it crawls every file's own columns
                                    and builds one mappable schema with a table
                                    per file, rather than loading them all into
                                    one.
                                  </p>
                                  <ul className="space-y-1">
                                    {additionalFileUploads.map((f, i) => (
                                      <li
                                        key={`${f.filename}-${i}`}
                                        className="flex items-center justify-between gap-2 text-xs text-white/60 bg-white/5 rounded-lg px-2.5 py-1"
                                      >
                                        <span className="truncate">
                                          {f.filename} (
                                          {(f.size_bytes / 1024).toFixed(1)} KB)
                                        </span>
                                        <button
                                          onClick={() =>
                                            setAdditionalFileUploads((prev) =>
                                              prev.filter(
                                                (_, idx) => idx !== i,
                                              ),
                                            )
                                          }
                                          className="text-white/40 hover:text-red-400 flex-shrink-0"
                                          aria-label={`Remove ${f.filename}`}
                                        >
                                          <X size={12} />
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              <p className="mt-2 text-[11px] text-white/45">
                                Accepted: .txt, .dat, .fil, .csv, .tsv, .json,
                                .jsonl, .parquet — select multiple files at once
                                (or a multi-file drag) to bulk-load them all
                                into the same target table; only the first is
                                used to detect the layout/columns.
                              </p>
                              {fileUpload && (
                                <div className="mt-3 max-w-xs">
                                  <InputField
                                    label="Encoding"
                                    value={fileEncoding}
                                    onChange={setFileEncoding}
                                    placeholder="utf-8"
                                  />
                                </div>
                              )}
                              {fileUpload &&
                                !layoutFields.length &&
                                fileFormatType === "fixed_width" && (
                                  <p className="mt-2 text-[11px] text-brand-300">
                                    Next: open "Record Layout / Copybook" below
                                    to define (or import) the field layout, then
                                    click "Parse Layout".
                                  </p>
                                )}
                              {detectingColumns && (
                                <p className="mt-2 flex items-center gap-1.5 text-[11px] text-brand-300">
                                  <Loader2 size={11} className="animate-spin" />
                                  Detecting columns from the file's header row…
                                </p>
                              )}
                            </SectionCard>
                          )}

                          {fileWizardStep === 1 && (
                            <div className="flex justify-end">
                              <button
                                type="button"
                                onClick={() => setFileWizardStep(2)}
                                disabled={!fileUpload}
                                className="ztd-btn ztd-btn-primary"
                              >
                                Next: Record Layout
                                <ArrowRight size={14} />
                              </button>
                            </div>
                          )}

                          {fileWizardStep === 2 && (
                            <SectionCard
                              key={fileUpload ? "layout-open" : "layout-closed"}
                              defaultCollapsed={!fileUpload}
                              title={
                                fileFormatType !== "fixed_width"
                                  ? "Detected Columns"
                                  : fileCategory === "copybook"
                                    ? "Mainframe Copybook Layout"
                                    : "Record Layout / Copybook"
                              }
                            >
                              {fileFormatType !== "fixed_width" ? (
                                <>
                                  <p className="mb-2 text-[11px] text-white/45">
                                    {fileFormatType.toUpperCase()} is
                                    self-describing — columns were detected
                                    automatically from the file's{" "}
                                    {fileFormatType === "csv" ||
                                    fileFormatType === "tsv"
                                      ? "header row"
                                      : "structure"}
                                    , no manual layout needed.
                                  </p>
                                  {fileCategory === "delimited" && (
                                    <div className="mb-3 flex items-center gap-2">
                                      <span className="text-[11px] text-white/45">
                                        Delimiter:
                                      </span>
                                      {(["csv", "tsv"] as const).map((f) => (
                                        <button
                                          key={f}
                                          onClick={async () => {
                                            setDelimitedSubFormat(f);
                                            setFileFormatType(f);
                                            if (fileUpload) {
                                              await _runColumnInspection(
                                                fileUpload.file_path,
                                                f,
                                                fileHasHeader,
                                              );
                                            }
                                          }}
                                          className={`px-2.5 py-1 rounded-md text-xs border transition ${
                                            delimitedSubFormat === f
                                              ? "bg-brand-500/20 border-brand-500/50 text-brand-300"
                                              : "bg-white/5 border-white/10 text-white/50 hover:text-white"
                                          }`}
                                        >
                                          {f === "csv"
                                            ? "Comma (CSV)"
                                            : "Tab (TSV)"}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                  {fileCategory === "structured" && (
                                    <div className="mb-3 flex items-center gap-2 flex-wrap">
                                      <span className="text-[11px] text-white/45">
                                        Format:
                                      </span>
                                      {(
                                        ["json", "jsonl", "parquet"] as const
                                      ).map((f) => (
                                        <button
                                          key={f}
                                          onClick={async () => {
                                            setStructuredSubFormat(f);
                                            setFileFormatType(f);
                                            if (fileUpload) {
                                              await _runColumnInspection(
                                                fileUpload.file_path,
                                                f,
                                                false,
                                              );
                                            }
                                          }}
                                          className={`px-2.5 py-1 rounded-md text-xs border transition ${
                                            structuredSubFormat === f
                                              ? "bg-brand-500/20 border-brand-500/50 text-brand-300"
                                              : "bg-white/5 border-white/10 text-white/50 hover:text-white"
                                          }`}
                                        >
                                          {f === "json"
                                            ? "JSON"
                                            : f === "jsonl"
                                              ? "JSON Lines"
                                              : "Parquet"}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                  {layoutFields.length > 0 && (
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-xs">
                                        <thead>
                                          <tr className="text-left text-white/40">
                                            <th className="py-1 pr-3">Name</th>
                                            <th className="py-1 pr-3">Type</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {layoutFields.map((f) => (
                                            <tr
                                              key={f.name}
                                              className="border-t border-white/5 text-white/70"
                                            >
                                              {" "}
                                              <td className="py-1 pr-3 font-mono">
                                                {f.name}
                                              </td>
                                              <td className="py-1 pr-3">
                                                {f.data_type}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                  {fileCategory === "delimited" && (
                                    <label className="mt-3 flex items-center gap-2 text-xs text-white/60">
                                      <input
                                        type="checkbox"
                                        checked={fileHasHeader}
                                        onChange={async (e) => {
                                          const hasHeader = e.target.checked;
                                          setFileHasHeader(hasHeader);
                                          if (!fileUpload) return;
                                          await _runColumnInspection(
                                            fileUpload.file_path,
                                            delimitedSubFormat,
                                            hasHeader,
                                          );
                                        }}
                                      />
                                      First row is a header (column names)
                                    </label>
                                  )}
                                </>
                              ) : (
                                <>
                                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                                    <button
                                      onClick={() => setLayoutFormat("simple")}
                                      className={`px-2.5 py-1 rounded-md text-xs border transition ${
                                        layoutFormat === "simple"
                                          ? "bg-brand-500/20 border-brand-500/50 text-brand-300"
                                          : "bg-white/5 border-white/10 text-white/50 hover:text-white"
                                      }`}
                                    >
                                      Simple (name,start,length,type)
                                    </button>
                                    <button
                                      onClick={() => setLayoutFormat("cobol")}
                                      className={`px-2.5 py-1 rounded-md text-xs border transition ${
                                        layoutFormat === "cobol"
                                          ? "bg-brand-500/20 border-brand-500/50 text-brand-300"
                                          : "bg-white/5 border-white/10 text-white/50 hover:text-white"
                                      }`}
                                    >
                                      COBOL Copybook (PIC)
                                    </button>
                                    <button
                                      onClick={() => setLayoutFormat("pyspark")}
                                      className={`px-2.5 py-1 rounded-md text-xs border transition ${
                                        layoutFormat === "pyspark"
                                          ? "bg-brand-500/20 border-brand-500/50 text-brand-300"
                                          : "bg-white/5 border-white/10 text-white/50 hover:text-white"
                                      }`}
                                    >
                                      PySpark (substring/alias)
                                    </button>
                                    <label className="ztd-btn ztd-btn-secondary ztd-btn-sm cursor-pointer ml-auto">
                                      <Upload size={12} />
                                      Import Layout File
                                      <input
                                        type="file"
                                        accept=".txt,.cpy,.cbl,.cob,.csv"
                                        className="hidden"
                                        onChange={(e) => {
                                          const f = e.target.files?.[0];
                                          e.target.value = "";
                                          if (f) void handleImportLayoutFile(f);
                                        }}
                                      />
                                    </label>
                                  </div>
                                  <p className="mb-2 text-[11px] text-white/45">
                                    Paste your layout below, or click "Import
                                    Layout File" to load it from a .txt/.cpy
                                    copybook file instead.
                                  </p>
                                  <textarea
                                    value={layoutText}
                                    onChange={(e) =>
                                      setLayoutText(e.target.value)
                                    }
                                    rows={8}
                                    className="w-full rounded-lg bg-black/30 border border-white/10 p-3 text-xs text-white/90 font-mono"
                                    placeholder={
                                      layoutFormat === "simple"
                                        ? "name,start,length,data_type,scale\nRET_ID,1,6,string\nACCEPTANCE_DATE,68,14,date"
                                        : layoutFormat === "pyspark"
                                          ? 'trim(substring("value", 1, 6)).alias("ret_id"),\ntrim(substring("value", 27, 7)).alias("vlt_number"),'
                                          : "05 ACCT-ID PIC 9(5).\n05 CUSTOMER-NAME PIC X(10)."
                                    }
                                  />
                                  <div className="flex items-center gap-3 pt-2">
                                    <button
                                      onClick={handleParseLayout}
                                      disabled={fileBusy}
                                      className="ztd-btn ztd-btn-secondary"
                                    >
                                      {fileBusy ? (
                                        <Loader2
                                          size={14}
                                          className="animate-spin"
                                        />
                                      ) : (
                                        <FileCode size={14} />
                                      )}
                                      Parse Layout
                                    </button>
                                    {layoutFields.length > 0 && (
                                      <span className="text-xs text-green-400">
                                        {layoutFields.length} field(s) parsed
                                      </span>
                                    )}
                                  </div>
                                  {layoutError && (
                                    <div className="mt-3">
                                      <InlineErrorBanner
                                        message={layoutError}
                                        onDismiss={() => setLayoutError(null)}
                                      />
                                    </div>
                                  )}
                                  {layoutFields.length > 0 && (
                                    <div className="mt-3 overflow-x-auto">
                                      <table className="w-full text-xs">
                                        <thead>
                                          <tr className="text-left text-white/40">
                                            <th className="py-1 pr-3">Name</th>
                                            <th className="py-1 pr-3">Start</th>
                                            <th className="py-1 pr-3">
                                              Length
                                            </th>
                                            <th className="py-1 pr-3">Type</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {layoutFields.map((f) => (
                                            <tr
                                              key={f.name}
                                              className="border-t border-white/5 text-white/70"
                                            >
                                              <td className="py-1 pr-3 font-mono">
                                                {f.name}
                                              </td>
                                              <td className="py-1 pr-3">
                                                {f.start}
                                              </td>
                                              <td className="py-1 pr-3">
                                                {f.length}
                                              </td>
                                              <td className="py-1 pr-3">
                                                {f.data_type}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </>
                              )}
                            </SectionCard>
                          )}

                          {fileWizardStep === 2 && (
                            <div className="flex justify-between">
                              <button
                                type="button"
                                onClick={() => setFileWizardStep(1)}
                                className="ztd-btn ztd-btn-secondary"
                              >
                                <ArrowLeft size={14} /> Back
                              </button>
                              <button
                                type="button"
                                onClick={() => setFileWizardStep(3)}
                                disabled={!layoutFields.length}
                                className="ztd-btn ztd-btn-primary"
                              >
                                Next: Build &amp; Preview
                                <ArrowRight size={14} />
                              </button>
                            </div>
                          )}

                          {fileWizardStep === 3 && (
                            <SectionCard
                              key={
                                layoutFields.length
                                  ? "target-open"
                                  : "target-closed"
                              }
                              defaultCollapsed={!layoutFields.length}
                              title="Build & Preview"
                            >
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={handleBuildAndPreviewFileTask}
                                  disabled={
                                    fileBusy ||
                                    !fileUpload ||
                                    !layoutFields.length
                                  }
                                  className="ztd-btn ztd-btn-primary"
                                >
                                  {fileBusy ? (
                                    <Loader2
                                      size={14}
                                      className="animate-spin"
                                    />
                                  ) : (
                                    <Play size={14} />
                                  )}
                                  Build &amp; Preview
                                </button>
                              </div>
                              {fileBuildError && (
                                <div className="mt-3">
                                  <InlineErrorBanner
                                    message={fileBuildError}
                                    onDismiss={() => setFileBuildError(null)}
                                  />
                                </div>
                              )}

                              {filePreviewResult && (
                                <div className="mt-4 border-t border-white/10 pt-4">
                                  <p className="mb-2 text-xs font-medium text-white/50">
                                    {filePreviewResult.rows_transformed} rows
                                    parsed, {filePreviewResult.rows_rejected}{" "}
                                    rejected
                                    {fileExcludeBadRecords &&
                                      filePreviewResult.bad_records_count > 0 &&
                                      `, ${filePreviewResult.bad_records_count} excluded (flagged)`}
                                  </p>
                                  {filePreviewResult.bad_records_count > 0 && (
                                    <div className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-3">
                                      <p className="text-xs text-amber-300">
                                        <AlertTriangle
                                          size={12}
                                          className="mr-1 inline-block"
                                        />
                                        {filePreviewResult.bad_records_count}{" "}
                                        record
                                        {filePreviewResult.bad_records_count ===
                                        1
                                          ? " looks"
                                          : "s look"}{" "}
                                        incomplete/malformed (mostly empty
                                        compared to other rows) and may not be
                                        real records.
                                      </p>
                                      <div className="mt-2 flex flex-wrap items-center gap-3">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            downloadBadRecordsCsv(
                                              filePreviewResult.columns,
                                              filePreviewResult.bad_records,
                                            )
                                          }
                                          className="ztd-btn ztd-btn-secondary text-xs"
                                        >
                                          <Download size={12} />
                                          Download flagged records for review
                                        </button>
                                        <label className="flex items-center gap-2 text-xs text-white/70">
                                          <input
                                            type="checkbox"
                                            checked={fileExcludeBadRecords}
                                            onChange={(e) =>
                                              setFileExcludeBadRecords(
                                                e.target.checked,
                                              )
                                            }
                                          />
                                          Exclude these from load
                                        </label>
                                      </div>
                                    </div>
                                  )}
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="text-left text-white/40">
                                          {filePreviewResult.columns.map(
                                            (c) => (
                                              <th key={c} className="py-1 pr-3">
                                                {c}
                                              </th>
                                            ),
                                          )}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {fileVisiblePreviewRows
                                          .slice(0, 10)
                                          .map((row, i) => (
                                            <tr
                                              key={i}
                                              className="border-t border-white/5 text-white/70"
                                            >
                                              {filePreviewResult.columns.map(
                                                (c) => (
                                                  <td
                                                    key={c}
                                                    className="py-1 pr-3"
                                                  >
                                                    {String(row[c] ?? "")}
                                                  </td>
                                                ),
                                              )}
                                            </tr>
                                          ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                            </SectionCard>
                          )}

                          {fileWizardStep === 3 && (
                            <div className="flex justify-between">
                              <button
                                type="button"
                                onClick={() => setFileWizardStep(2)}
                                className="ztd-btn ztd-btn-secondary"
                              >
                                <ArrowLeft size={14} /> Back
                              </button>
                              <button
                                type="button"
                                onClick={() => setFileWizardStep(4)}
                                disabled={!filePreviewResult}
                                className="ztd-btn ztd-btn-primary"
                              >
                                Next: Connect Target Database
                                <ArrowRight size={14} />
                              </button>
                            </div>
                          )}

                          {fileWizardStep === 4 && (
                            <SectionCard title="Connect Target Database">
                              {fileTargetConn ? (
                                <div className="flex items-center justify-between gap-3">
                                  <div className="flex items-center gap-2 text-sm text-green-400">
                                    <CheckCircle2 size={14} />
                                    Connected — {fileTargetConnLabel}
                                    {fileTargetDbType &&
                                      ` · ${fileTargetDbType}`}
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <button
                                      onClick={handleTestFileConnection}
                                      disabled={fileConnTesting}
                                      className="ztd-btn ztd-btn-secondary"
                                    >
                                      {fileConnTesting ? (
                                        <Loader2
                                          size={14}
                                          className="animate-spin"
                                        />
                                      ) : (
                                        <RefreshCw size={14} />
                                      )}
                                      Refresh Connection
                                    </button>
                                    <button
                                      onClick={() =>
                                        setShowFileTargetConnectDialog(true)
                                      }
                                      className="ztd-btn ztd-btn-secondary"
                                    >
                                      <RefreshCw size={14} />
                                      Change connection
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center gap-3">
                                  <button
                                    onClick={() =>
                                      setShowFileTargetConnectDialog(true)
                                    }
                                    className="ztd-btn ztd-btn-primary"
                                  >
                                    <RefreshCw size={14} />
                                    Connect Database
                                  </button>
                                  <span className="text-xs text-white/40">
                                    Reuse a connection any agent already saved
                                    for this project, or connect a new one.
                                  </span>
                                </div>
                              )}
                            </SectionCard>
                          )}

                          {fileWizardStep === 4 && (
                            <SectionCard title="Target Table">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <div className="flex items-center justify-between mb-1.5">
                                    <label className="block text-xs font-medium text-white/50">
                                      Target Table Name{" "}
                                      <span className="text-red-400">*</span>
                                    </label>
                                    {fileTargetConn && (
                                      <button
                                        type="button"
                                        onClick={handleScanFileTargetTables}
                                        disabled={fileTargetTablesScanning}
                                        className="text-[11px] text-brand-300 hover:text-brand-200 transition disabled:opacity-50"
                                      >
                                        {fileTargetTablesScanning ? (
                                          <Loader2
                                            size={11}
                                            className="inline animate-spin"
                                          />
                                        ) : (
                                          "Refresh table list"
                                        )}
                                      </button>
                                    )}
                                  </div>
                                  <input
                                    type="text"
                                    list="file-target-tables-datalist"
                                    value={fileTargetTable}
                                    onChange={(e) => {
                                      setFileTargetTable(e.target.value);
                                      const picked = fileTargetTables?.find(
                                        (t) => t.table_name === e.target.value,
                                      );
                                      if (picked)
                                        setFileTargetSchema(picked.schema_name);
                                    }}
                                    placeholder="Search existing tables or type a new table name…"
                                    className="w-full px-3 py-2 bg-surface-400/50 border border-white/10 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 transition"
                                  />
                                  {fileTargetTables && (
                                    <datalist id="file-target-tables-datalist">
                                      {fileTargetTables.map((t) => (
                                        <option
                                          key={`${t.schema_name}.${t.table_name}`}
                                          value={t.table_name}
                                        >
                                          {t.schema_name}.{t.table_name}
                                        </option>
                                      ))}
                                    </datalist>
                                  )}
                                  <p className="mt-1.5 text-[11px] text-white/40">
                                    Start typing to search tables that already
                                    exist in the connected target, or enter a
                                    name that doesn't exist yet to create a new
                                    one.
                                  </p>
                                  {fileTargetTablesError && (
                                    <p className="mt-1.5 text-[11px] text-red-400">
                                      {fileTargetTablesError}
                                    </p>
                                  )}
                                </div>
                                <InputField
                                  label="Schema (optional — defaults to connection owner)"
                                  value={fileTargetSchema}
                                  onChange={setFileTargetSchema}
                                  placeholder="public"
                                />
                              </div>

                              <div className="mt-4 border-t border-white/10 pt-4">
                                <label className="block text-xs font-medium text-white/50 mb-1.5">
                                  If Table Exists
                                </label>
                                <select
                                  value={fileIfExists}
                                  onChange={(e) =>
                                    setFileIfExists(
                                      e.target.value as
                                        | "fail"
                                        | "replace"
                                        | "append"
                                        | "truncate",
                                    )
                                  }
                                  className="w-full sm:w-1/2 px-3 py-2 bg-surface-400/50 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-brand-500/50 transition"
                                >
                                  <option value="append">Append</option>
                                  <option value="replace">
                                    Replace (drop &amp; recreate)
                                  </option>
                                  <option value="truncate">
                                    Truncate (clear rows, keep schema)
                                  </option>
                                  <option value="fail">Fail if exists</option>
                                </select>
                              </div>

                              {(fileTargetDbType === "snowflake" ||
                                fileTargetDbType === "synapse") && (
                                <label className="mt-3 flex items-center gap-2 text-xs text-white/60">
                                  <input
                                    type="checkbox"
                                    checked={fileBulkLoad}
                                    onChange={(e) =>
                                      setFileBulkLoad(e.target.checked)
                                    }
                                  />
                                  Bulk load (skip row-by-row insert — faster for
                                  large files)
                                </label>
                              )}
                              {fileBulkLoad &&
                                fileTargetDbType === "synapse" && (
                                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-lg border border-white/10 p-3">
                                    <InputField
                                      label="Storage Account Name"
                                      value={fileBlobAccountName}
                                      onChange={setFileBlobAccountName}
                                      placeholder="ztddl01"
                                      required
                                    />
                                    <InputField
                                      label="Container"
                                      value={fileBlobContainer}
                                      onChange={setFileBlobContainer}
                                      placeholder="ztdfs01"
                                      required
                                    />
                                    <div>
                                      <label className="block text-xs font-medium text-white/50 mb-1.5">
                                        Auth Mode
                                      </label>
                                      <select
                                        value={fileBlobAuthMode}
                                        onChange={(e) =>
                                          setFileBlobAuthMode(
                                            e.target.value as
                                              | "sas_token"
                                              | "account_key"
                                              | "aad_service_principal",
                                          )
                                        }
                                        className="w-full px-3 py-2 bg-surface-400/50 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-brand-500/50 transition"
                                      >
                                        <option value="account_key">
                                          Storage Account Key
                                        </option>
                                        <option value="sas_token">
                                          SAS Token
                                        </option>
                                        <option value="aad_service_principal">
                                          AAD Service Principal
                                        </option>
                                      </select>
                                    </div>
                                    <InputField
                                      label="Storage Account Key / SAS Token"
                                      value={fileBlobCredential}
                                      onChange={setFileBlobCredential}
                                      type="password"
                                      required
                                    />
                                  </div>
                                )}
                            </SectionCard>
                          )}

                          {fileWizardStep === 4 && (
                            <div className="flex justify-between">
                              <button
                                type="button"
                                onClick={() => setFileWizardStep(3)}
                                className="ztd-btn ztd-btn-secondary"
                              >
                                <ArrowLeft size={14} /> Back
                              </button>
                              <button
                                type="button"
                                onClick={() => setFileWizardStep(5)}
                                disabled={
                                  !fileTargetConn || !fileTargetTable.trim()
                                }
                                className="ztd-btn ztd-btn-primary"
                              >
                                Next: Load Data
                                <ArrowRight size={14} />
                              </button>
                            </div>
                          )}

                          <ConnectDatabaseDialog
                            open={showFileTargetConnectDialog}
                            onClose={() =>
                              setShowFileTargetConnectDialog(false)
                            }
                            title="Connect Target Database"
                            projectId={projectId}
                            kind="target"
                            agentKey="data_converter"
                            defaultDbType="postgresql"
                            testIdPrefix="converter-file-target"
                            onConnectExisting={handleFileTargetConnectExisting}
                            onConnectNew={handleFileTargetConnectNew}
                          />

                          {fileWizardStep === 5 && filePreviewResult && (
                            <SectionCard
                              title={`Preview — ${filePreviewResult.rows_transformed} rows parsed, ${filePreviewResult.rows_rejected} rejected${
                                fileExcludeBadRecords &&
                                filePreviewResult.bad_records_count > 0
                                  ? `, ${filePreviewResult.bad_records_count} excluded (flagged)`
                                  : ""
                              }`}
                            >
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-left text-white/40">
                                      {filePreviewResult.columns.map((c) => (
                                        <th key={c} className="py-1 pr-3">
                                          {c}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {fileVisiblePreviewRows
                                      .slice(0, 10)
                                      .map((row, i) => (
                                        <tr
                                          key={i}
                                          className="border-t border-white/5 text-white/70"
                                        >
                                          {filePreviewResult.columns.map(
                                            (c) => (
                                              <td key={c} className="py-1 pr-3">
                                                {String(row[c] ?? "")}
                                              </td>
                                            ),
                                          )}
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>

                              <div className="mt-4 space-y-3 border-t border-white/10 pt-4">
                                {fileTargetConn ? (
                                  <div className="flex items-center gap-2 text-sm text-green-400">
                                    <CheckCircle2 size={14} />
                                    Target: {fileTargetConnLabel}
                                    {fileTargetDbType &&
                                      ` (${fileTargetDbType})`}
                                  </div>
                                ) : (
                                  <p className="text-xs text-amber-400">
                                    Connect a target database above before
                                    loading.
                                  </p>
                                )}
                                {fileLoadError && (
                                  <InlineErrorBanner
                                    message={`Load failed: ${fileLoadError}`}
                                    onDismiss={() => setFileLoadError(null)}
                                  />
                                )}
                                {fileBusy && (
                                  <div className="space-y-1.5">
                                    <div className="flex items-center justify-between text-xs text-white/60">
                                      <span>Loading records…</span>
                                      {filePreviewResult && (
                                        <span>
                                          up to{" "}
                                          {(
                                            filePreviewResult.rows_transformed -
                                            filePreviewResult.rows_rejected -
                                            (fileExcludeBadRecords
                                              ? filePreviewResult.bad_records_count
                                              : 0)
                                          ).toLocaleString()}{" "}
                                          row(s)
                                        </span>
                                      )}
                                    </div>
                                    <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                                      <div className="h-full w-full bg-brand-500 animate-pulse" />
                                    </div>
                                  </div>
                                )}
                                <div className="flex items-center justify-between gap-3">
                                  <button
                                    type="button"
                                    onClick={() => setFileWizardStep(4)}
                                    className="ztd-btn ztd-btn-secondary"
                                  >
                                    <ArrowLeft size={14} /> Back
                                  </button>
                                  <button
                                    onClick={handleLoadFileTask}
                                    disabled={fileBusy || !fileTargetConn}
                                    className="ztd-btn ztd-btn-primary"
                                  >
                                    {fileBusy ? (
                                      <Loader2
                                        size={14}
                                        className="animate-spin"
                                      />
                                    ) : (
                                      <Upload size={14} />
                                    )}
                                    Load Data
                                    {additionalFileUploads.length > 0
                                      ? ` (${additionalFileUploads.length + 1} files)`
                                      : ""}
                                  </button>
                                </div>
                              </div>
                            </SectionCard>
                          )}

                          {fileWizardStep === 5 && fileLoadResult && (
                            <div className="space-y-2 rounded-xl border border-green-500/30 bg-green-500/10 p-3 text-sm text-green-300">
                              <div className="flex items-start gap-2">
                                <CheckCircle2
                                  size={16}
                                  className="mt-0.5 flex-shrink-0"
                                />
                                <span>
                                  {fileLoadResult.table_created && (
                                    <>
                                      Table {fileLoadResult.schema_name}.
                                      {fileLoadResult.table_name} created
                                      successfully.{" "}
                                    </>
                                  )}
                                  Loaded {fileLoadResult.rows_loaded} row(s)
                                  into {fileLoadResult.schema_name}.
                                  {fileLoadResult.table_name}
                                  {fileLoadResult.rows_rejected > 0 &&
                                    ` (${fileLoadResult.rows_rejected} rejected)`}
                                </span>
                              </div>
                              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                                <div className="h-full w-full bg-green-500" />
                              </div>
                            </div>
                          )}

                          {fileWizardStep === 5 &&
                            fileLoadResult?.warnings?.map((w, i) => (
                              <div
                                key={i}
                                className="flex items-start gap-2 rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-3 text-sm text-yellow-300"
                              >
                                <AlertTriangle
                                  size={16}
                                  className="mt-0.5 flex-shrink-0"
                                />
                                <span>{w}</span>
                              </div>
                            ))}

                          {fileWizardStep === 5 && fileBatchLoadResults && (
                            <div className="space-y-1.5">
                              {fileBatchLoadResults.map((r, i) => (
                                <div
                                  key={i}
                                  className={`flex items-start gap-2 rounded-xl border p-3 text-sm ${
                                    r.error
                                      ? "border-red-500/30 bg-red-500/10 text-red-300"
                                      : "border-green-500/30 bg-green-500/10 text-green-300"
                                  }`}
                                >
                                  {r.error ? (
                                    <AlertTriangle
                                      size={16}
                                      className="mt-0.5 flex-shrink-0"
                                    />
                                  ) : (
                                    <CheckCircle2
                                      size={16}
                                      className="mt-0.5 flex-shrink-0"
                                    />
                                  )}
                                  <span>
                                    {additionalFileUploads[i]?.filename ??
                                      `File ${i + 2}`}
                                    :{" "}
                                    {r.error
                                      ? r.error
                                      : (r.table_created
                                          ? `Table ${r.schema_name}.${r.table_name} created successfully. `
                                          : "") +
                                        `Loaded ${r.rows_loaded} row(s) into ${r.schema_name}.${r.table_name}` +
                                        (r.rows_rejected > 0
                                          ? ` (${r.rows_rejected} rejected)`
                                          : "")}
                                  </span>
                                </div>
                              ))}
                              {fileBatchLoadResults.flatMap((r, i) =>
                                (r.warnings ?? []).map((w, j) => (
                                  <div
                                    key={`${i}-${j}`}
                                    className="flex items-start gap-2 rounded-xl border border-yellow-500/30 bg-yellow-500/10 p-3 text-sm text-yellow-300"
                                  >
                                    <AlertTriangle
                                      size={16}
                                      className="mt-0.5 flex-shrink-0"
                                    />
                                    <span>
                                      {additionalFileUploads[i]?.filename ??
                                        `File ${i + 2}`}
                                      : {w}
                                    </span>
                                  </div>
                                )),
                              )}
                            </div>
                          )}

                          {fileWizardStep === 5 &&
                            (fileLoadResult || fileBatchLoadResults) &&
                            fileIngestMode !== "multi_format" && (
                              <div className="flex justify-end">
                                <button
                                  type="button"
                                  onClick={handleStartNewFileUpload}
                                  className="ztd-btn ztd-btn-secondary"
                                >
                                  <Upload size={14} /> Upload Another File
                                </button>
                              </div>
                            )}

                          {fileWizardStep === 5 &&
                            fileLoadResult &&
                            fileIngestMode === "multi_format" && (
                              <p className="text-[11px] text-white/45">
                                Loaded — switch tabs above to configure another
                                file, or "Add File(s)" to queue a new one.
                              </p>
                            )}
                        </div>
                      )}

                      {fileIngestMode === "bulk" && (
                        <SectionCard title="Bulk Folder Ingest (Multiple Files)">
                          <p className="mb-3 text-[11px] text-white/45">
                            One independent task per file, processed in parallel
                            across worker containers instead of one sequential
                            job.
                          </p>

                          <div className="mb-3">
                            <label className="block text-xs font-medium text-white/50 mb-1.5">
                              Format
                            </label>
                            <div className="flex gap-2">
                              {(
                                [
                                  {
                                    value: "fixed_width",
                                    label: "Fixed-Width",
                                  },
                                  { value: "csv", label: "CSV" },
                                  { value: "tsv", label: "TSV" },
                                ] as const
                              ).map((opt) => (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => {
                                    setFolderFormatType(opt.value);
                                    setFolderOneTablePerFile(
                                      opt.value !== "fixed_width",
                                    );
                                  }}
                                  className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                                    folderFormatType === opt.value
                                      ? "bg-brand-500/20 text-brand-300 border-brand-500/40"
                                      : "bg-surface-400/40 text-white/60 border-white/10 hover:text-white/85"
                                  }`}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                            <p className="mt-1.5 text-[11px] text-white/45">
                              {folderFormatType === "fixed_width"
                                ? "Needs a parsed copybook/layout above — best for a folder of same-structure extracts (e.g. daily files) sharing one target table."
                                : "Reads each file's own header row — no layout needed. Best for a folder of distinct source tables (e.g. a Bronze-layer load), one file per table."}
                            </p>
                          </div>

                          {folderFormatType !== "fixed_width" && (
                            <label className="mb-3 flex items-center gap-2 text-xs text-white/60">
                              <input
                                type="checkbox"
                                checked={folderOneTablePerFile}
                                onChange={(e) =>
                                  setFolderOneTablePerFile(e.target.checked)
                                }
                              />
                              One target table per file (named from each
                              filename) — turn off to load every file into the
                              single "Target Table Name" above instead
                            </label>
                          )}

                          <label className="ztd-btn ztd-btn-secondary cursor-pointer">
                            <Upload size={14} />
                            Choose Files
                            <input
                              type="file"
                              multiple
                              accept=".txt,.dat,.fil,.csv,.tsv,.json,.jsonl,.parquet"
                              className="hidden"
                              onChange={(e) => {
                                handleFolderFilesSelected(e.target.files);
                                e.target.value = "";
                              }}
                            />
                          </label>
                          {folderFiles.length > 0 && (
                            <ul className="mt-2 space-y-1 text-xs text-white/60">
                              {folderFiles.map((f, i) => (
                                <li
                                  key={`${f.name}-${i}`}
                                  className="flex items-center gap-1.5"
                                >
                                  <CheckCircle2
                                    size={12}
                                    className="text-green-400"
                                  />
                                  {f.name} ({(f.size / 1024).toFixed(1)} KB)
                                  {folderOneTablePerFile && (
                                    <span className="text-white/35">
                                      {" "}
                                      → {sanitizeTableNameFromFilename(f.name)}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}

                          {(fileTargetDbType === "snowflake" ||
                            fileTargetDbType === "synapse") && (
                            <label className="mt-3 flex items-center gap-2 text-xs text-white/60">
                              <input
                                type="checkbox"
                                checked={folderBulkLoad}
                                onChange={(e) =>
                                  setFolderBulkLoad(e.target.checked)
                                }
                              />
                              Bulk load (skip row-by-row insert — faster for
                              large files)
                            </label>
                          )}
                          {folderBulkLoad && fileTargetDbType === "synapse" && (
                            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-lg border border-white/10 p-3">
                              <InputField
                                label="Storage Account Name"
                                value={folderBlobAccountName}
                                onChange={setFolderBlobAccountName}
                                placeholder="ztddl01"
                                required
                              />
                              <InputField
                                label="Container"
                                value={folderBlobContainer}
                                onChange={setFolderBlobContainer}
                                placeholder="ztdfs01"
                                required
                              />
                              <div>
                                <label className="block text-xs font-medium text-white/50 mb-1.5">
                                  Auth Mode
                                </label>
                                <select
                                  value={folderBlobAuthMode}
                                  onChange={(e) =>
                                    setFolderBlobAuthMode(
                                      e.target.value as
                                        | "sas_token"
                                        | "account_key"
                                        | "aad_service_principal",
                                    )
                                  }
                                  className="w-full px-3 py-2 bg-surface-400/50 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-brand-500/50 transition"
                                >
                                  <option value="account_key">
                                    Storage Account Key
                                  </option>
                                  <option value="sas_token">SAS Token</option>
                                  <option value="aad_service_principal">
                                    AAD Service Principal
                                  </option>
                                </select>
                              </div>
                              <InputField
                                label="Storage Account Key / SAS Token"
                                value={folderBlobCredential}
                                onChange={setFolderBlobCredential}
                                type="password"
                                required
                              />
                            </div>
                          )}

                          <div className="flex items-center gap-3 pt-3">
                            <button
                              onClick={handleStartFolderIngest}
                              disabled={folderBusy || !folderFiles.length}
                              className="ztd-btn ztd-btn-primary"
                            >
                              {folderBusy ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <Play size={14} />
                              )}
                              Start Bulk Ingest
                            </button>
                          </div>

                          {folderPipelineRunId && (
                            <div className="mt-4 space-y-2 border-t border-white/10 pt-4">
                              {!folderProgress ? (
                                <p className="text-xs text-white/40 italic">
                                  Starting…
                                </p>
                              ) : (
                                <>
                                  <div className="flex items-center justify-between text-xs text-white/60">
                                    <span>
                                      {folderProgress.succeeded_count}{" "}
                                      succeeded, {folderProgress.failed_count}{" "}
                                      failed, of {folderProgress.total_count}{" "}
                                      file(s)
                                    </span>
                                    <span>
                                      {Math.round(
                                        folderProgress.pipeline_percent,
                                      )}
                                      %
                                    </span>
                                  </div>
                                  <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden">
                                    <div
                                      className="h-full bg-brand-500 transition-all"
                                      style={{
                                        width: `${folderProgress.pipeline_percent}%`,
                                      }}
                                    />
                                  </div>
                                  <ul className="space-y-1 text-xs text-white/50">
                                    {folderProgress.stage_statuses.map((s) => (
                                      <li key={s.stage_id}>
                                        {s.stage_name}: {s.status} (
                                        {s.completed_tasks}/{s.total_tasks})
                                      </li>
                                    ))}
                                  </ul>
                                </>
                              )}
                            </div>
                          )}
                        </SectionCard>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Schema DDL step, before a scan has produced a result yet — still
          needs a Back button so the user is never stuck without one. */}
          {step === "schema" && !schemaResult && (
            <div className="space-y-4">
              <p className="text-xs text-white/40 italic">
                {scanningSchema ? "Scanning schema…" : "No schema scanned yet."}
              </p>
              <button onClick={goPrev} className="ztd-btn ztd-btn-secondary">
                <ArrowLeft size={14} /> Back
              </button>
            </div>
          )}

          {/* ── Step: Schema DDL ───────────────────────────────────────────── */}
          {step === "schema" && schemaResult && (
            <div className="space-y-4">
              {extraSourceConnections.length > 0 && (
                <p className="text-xs text-brand-300/80">
                  {extraSourceConnections.length} additional source connection
                  {extraSourceConnections.length === 1 ? "" : "s"} — each
                  connection is shown as its own separate section below.
                </p>
              )}
              {tableNameConflicts.length > 0 && (
                <div className="space-y-2 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
                  <p className="text-xs font-medium text-amber-300 flex items-center gap-1.5">
                    <AlertTriangle size={12} />
                    {tableNameConflicts.length} table name
                    {tableNameConflicts.length === 1 ? "" : "s"} exist
                    {tableNameConflicts.length === 1 ? "s" : ""} in more than
                    one connection — pick which one to use
                  </p>
                  {tableNameConflicts.map((c) => {
                    const active = schemaResult.tables.find(
                      (t) => t.table_name === c.tableName,
                    );
                    const usingExtra = active?.source_connection_id != null;
                    return (
                      <div
                        key={c.tableName}
                        className="flex flex-wrap items-center justify-between gap-2 text-xs bg-white/3 rounded-md px-2.5 py-1.5"
                      >
                        <span className="text-white/70">
                          <span className="font-mono text-white/90">
                            {c.tableName}
                          </span>{" "}
                          — currently using{" "}
                          <span className="text-amber-300">
                            {usingExtra ? c.extraLabel : c.primaryLabel}
                          </span>{" "}
                          (
                          {usingExtra ? c.extraSchemaName : c.primarySchemaName}
                          )
                        </span>
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            type="button"
                            disabled={!usingExtra}
                            onClick={() =>
                              swapConflictTable(c.tableName, false)
                            }
                            className="px-2 py-0.5 rounded border border-white/10 text-white/60 hover:text-white disabled:opacity-40 disabled:cursor-default transition"
                          >
                            Use {c.primaryLabel}
                          </button>
                          <button
                            type="button"
                            disabled={usingExtra}
                            onClick={() => swapConflictTable(c.tableName, true)}
                            className="px-2 py-0.5 rounded border border-white/10 text-white/60 hover:text-white disabled:opacity-40 disabled:cursor-default transition"
                          >
                            Use {c.extraLabel}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {sourceMode !== "legacy" &&
                sourceMode !== "crm" &&
                (schemaResult.warnings?.length ?? 0) > 0 && (
                  <div className="space-y-1">
                    {schemaResult.warnings!.map((w, i) => (
                      <div
                        key={i}
                        className="flex items-start gap-2 text-xs text-amber-400"
                      >
                        <AlertTriangle
                          size={12}
                          className="mt-0.5 flex-shrink-0"
                        />
                        {w}
                      </div>
                    ))}
                  </div>
                )}

              {(sourceMode === "legacy" || sourceMode === "crm") &&
              legacySourceInfo ? (
                <SectionCard
                  title={`Schema: ${schemaResult.schema_name} · ${schemaResult.db_type.toUpperCase()} · ${schemaResult.tables.length} tables`}
                >
                  <div className="mb-3 space-y-1.5">
                    <div className="flex items-center gap-2 text-sm text-green-400">
                      <CheckCircle2 size={14} />
                      Parsed {legacySourceInfo.filename} (
                      {legacySourceInfo.format}) — {legacySourceInfo.tableCount}{" "}
                      table(s)
                    </div>
                    {legacySourceInfo.consolidationNote && (
                      <div className="flex items-start gap-2 text-xs text-sky-400">
                        <Info size={12} className="mt-0.5 flex-shrink-0" />
                        <span>{legacySourceInfo.consolidationNote}</span>
                        {Object.values(legacyFileRefs)[0]?.download_url && (
                          <a
                            href={Object.values(legacyFileRefs)[0].download_url}
                            download={Object.values(legacyFileRefs)[0].filename}
                            className="text-brand-300 hover:text-brand-200 underline underline-offset-2 flex-shrink-0"
                          >
                            Download CSV
                          </a>
                        )}
                      </div>
                    )}
                    {legacySourceInfo.warnings.length > 0 && (
                      <div className="flex items-start gap-2 text-xs text-amber-400">
                        <AlertTriangle
                          size={12}
                          className="mt-0.5 flex-shrink-0"
                        />
                        <span>
                          {legacySourceInfo.warnings.length} warning(s):{" "}
                          {legacySourceInfo.warnings.join("; ")}
                        </span>
                      </div>
                    )}
                    {Object.keys(legacySourceInfo.packageDetails).length >
                      0 && (
                      <div>
                        <button
                          type="button"
                          onClick={() => setLegacyPackageDetailsOpen((v) => !v)}
                          className="flex items-center gap-1.5 text-xs text-brand-300 hover:text-brand-200"
                        >
                          {legacyPackageDetailsOpen ? (
                            <ChevronDown size={12} />
                          ) : (
                            <ChevronRight size={12} />
                          )}
                          Review Package Contents
                        </button>
                        {legacyPackageDetailsOpen && (
                          <div className="mt-2 space-y-3">
                            {Object.entries(
                              legacySourceInfo.packageDetails,
                            ).map(([fname, details]) => (
                              <div
                                key={fname}
                                className="rounded-lg border border-white/10 bg-white/5 p-3 space-y-2.5 text-xs"
                              >
                                <div className="text-white/70 font-medium truncate">
                                  {fname}
                                </div>
                                <div>
                                  <div className="text-white/40 mb-1">
                                    Connection Managers (
                                    {details.connection_managers.length})
                                  </div>
                                  {details.connection_managers.length === 0 ? (
                                    <div className="text-white/30">
                                      None found
                                    </div>
                                  ) : (
                                    <ul className="space-y-0.5">
                                      {details.connection_managers.map(
                                        (cm, i) => (
                                          <li
                                            key={`${cm.name}-${i}`}
                                            className="text-white/60"
                                          >
                                            {cm.name}{" "}
                                            <span className="text-white/30">
                                              (
                                              {cm.creation_name ||
                                                "unknown type"}
                                              )
                                            </span>
                                          </li>
                                        ),
                                      )}
                                    </ul>
                                  )}
                                </div>
                                <div>
                                  <div className="text-white/40 mb-1">
                                    Tasks / Executables (
                                    {details.executables.length})
                                  </div>
                                  {details.executables.length === 0 ? (
                                    <div className="text-white/30">
                                      None found
                                    </div>
                                  ) : (
                                    <ul className="space-y-0.5">
                                      {details.executables.map((ex, i) => (
                                        <li
                                          key={`${ex.name}-${i}`}
                                          className="text-white/60"
                                        >
                                          {ex.name}{" "}
                                          <span className="text-white/30">
                                            ({ex.type || "unknown type"})
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                                <div>
                                  <div className="text-white/40 mb-1">
                                    Data Flow Components (
                                    {details.data_flow_components.length})
                                  </div>
                                  {details.data_flow_components.length === 0 ? (
                                    <div className="text-white/30">
                                      None found
                                    </div>
                                  ) : (
                                    <ul className="space-y-0.5">
                                      {details.data_flow_components.map(
                                        (c, i) => (
                                          <li
                                            key={`${c.name}-${i}`}
                                            className="text-white/60"
                                          >
                                            {c.name}{" "}
                                            <span className="text-white/30">
                                              ({c.role}
                                              {c.role !== "transformation"
                                                ? ` — ${c.column_count} column(s) captured in schema`
                                                : ""}
                                              )
                                            </span>
                                          </li>
                                        ),
                                      )}
                                    </ul>
                                  )}
                                </div>
                                <div>
                                  <div className="text-white/40 mb-1">
                                    Variables ({details.variables.length})
                                  </div>
                                  {details.variables.length === 0 ? (
                                    <div className="text-white/30">
                                      None found
                                    </div>
                                  ) : (
                                    <ul className="space-y-0.5">
                                      {details.variables.map((v, i) => (
                                        <li
                                          key={`${v.name}-${i}`}
                                          className="text-white/60"
                                        >
                                          {v.name}{" "}
                                          <span className="text-white/30">
                                            ({v.data_type || "unknown type"})
                                          </span>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {Object.keys(legacySourceInfo.additionalItems).length >
                      0 && (
                      <div>
                        <button
                          type="button"
                          onClick={() =>
                            setLegacyAdditionalItemsOpen((v) => !v)
                          }
                          className="flex items-center gap-1.5 text-xs text-brand-300 hover:text-brand-200"
                        >
                          {legacyAdditionalItemsOpen ? (
                            <ChevronDown size={12} />
                          ) : (
                            <ChevronRight size={12} />
                          )}
                          Review Additional Items Found
                        </button>
                        {legacyAdditionalItemsOpen && (
                          <div className="mt-2 space-y-3">
                            {Object.entries(
                              legacySourceInfo.additionalItems,
                            ).map(([fname, items]) => {
                              const grouped = items.reduce<
                                Record<string, AdditionalItem[]>
                              >((acc, item) => {
                                (acc[item.category] ??= []).push(item);
                                return acc;
                              }, {});
                              return (
                                <div
                                  key={fname}
                                  className="rounded-lg border border-white/10 bg-white/5 p-3 space-y-2.5 text-xs"
                                >
                                  <div className="text-white/70 font-medium truncate">
                                    {fname}
                                  </div>
                                  {Object.entries(grouped).map(
                                    ([category, categoryItems]) => (
                                      <div key={category}>
                                        <div className="text-white/40 mb-1 capitalize">
                                          {category.replace(/_/g, " ")} (
                                          {categoryItems.length})
                                        </div>
                                        <ul className="space-y-0.5">
                                          {categoryItems.map((item, i) => (
                                            <li
                                              key={`${item.name}-${i}`}
                                              className="text-white/60"
                                            >
                                              {item.name}
                                              {item.detail ? (
                                                <span className="text-white/30">
                                                  {" "}
                                                  ({item.detail})
                                                </span>
                                              ) : null}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    ),
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </SectionCard>
              ) : null}
              {groupTablesByConnection(schemaResult.tables).map((group) => {
                const isPrimary = group.key === "__primary__";
                const filterValue = isPrimary
                  ? schemaTableFilter
                  : (extraSchemaTableFilters[group.key] ?? "");
                const setFilterValue = (v: string) => {
                  if (isPrimary) setSchemaTableFilter(v);
                  else
                    setExtraSchemaTableFilters((prev) => ({
                      ...prev,
                      [group.key]: v,
                    }));
                };
                const filteredGroupTables = filterValue.trim()
                  ? group.tables.filter((t) =>
                      t.table_name
                        .toLowerCase()
                        .includes(filterValue.trim().toLowerCase()),
                    )
                  : group.tables;
                const dbTypeLabel = (
                  isPrimary
                    ? schemaResult.db_type
                    : (extraSchemaDbType[group.key] ?? "")
                ).toUpperCase();
                const extraConn = isPrimary
                  ? undefined
                  : extraSourceConnections.find(
                      (c) => c.connectionId === group.key,
                    );
                const rawTableCount = isPrimary
                  ? group.tables.length
                  : (extraSchemaRawTableCount[group.key] ??
                    group.tables.length);
                const allTablesConflicted =
                  !isPrimary && group.tables.length === 0 && rawTableCount > 0;
                return (
                  <SectionCard
                    key={group.key}
                    title={
                      allTablesConflicted
                        ? `Schema: ${group.schemaName}${
                            dbTypeLabel ? ` · ${dbTypeLabel}` : ""
                          } · 0 of ${rawTableCount} tables (all name conflicts — resolve above)`
                        : `Schema: ${group.schemaName}${
                            dbTypeLabel ? ` · ${dbTypeLabel}` : ""
                          } · ${group.tables.length} tables`
                    }
                    actions={
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() =>
                            downloadTableAsPdf(
                              `Schema — ${group.schemaName}`,
                              SCHEMA_EXPORT_HEADERS,
                              schemaExportRows(group.tables),
                              `schema_${group.schemaName}_${filenameTimestamp()}.pdf`,
                            )
                          }
                          className="ztd-btn ztd-btn-secondary ztd-btn-sm !px-2"
                          title="Download schema/table details as a PDF"
                        >
                          <FileText size={12} />
                        </button>
                        <button
                          onClick={() =>
                            downloadTableAsExcel(
                              group.schemaName,
                              SCHEMA_EXPORT_HEADERS,
                              schemaExportRows(group.tables),
                              `schema_${group.schemaName}_${filenameTimestamp()}.xls`,
                            )
                          }
                          className="ztd-btn ztd-btn-secondary ztd-btn-sm !px-2"
                          title="Download schema/table details as an Excel workbook"
                        >
                          <FileSpreadsheet size={12} />
                        </button>
                        {sourceMode !== "legacy" && sourceMode !== "crm" && (
                          <>
                            {isPrimary &&
                              connStatus &&
                              connStatus.schemas.length > 1 && (
                                <select
                                  value={sourceSchemaName}
                                  onChange={(e) => {
                                    if (!sourceConnectionId) return;
                                    setSourceSchemaName(e.target.value);
                                    void runSourceScan(
                                      sourceConnectionId,
                                      e.target.value,
                                    );
                                  }}
                                  disabled={scanningSchema}
                                  title="Switch to a different schema on this same connection without going back"
                                  className="px-2.5 py-1.5 rounded-lg bg-surface-400/50 border border-white/10 text-xs text-white focus:outline-none focus:border-brand-500/50"
                                >
                                  {connStatus.schemas.map((s) => (
                                    <option key={s} value={s}>
                                      {s}
                                    </option>
                                  ))}
                                </select>
                              )}
                            {!isPrimary &&
                              extraConn &&
                              extraConn.schemas.length > 1 && (
                                <select
                                  value={extraConn.schemaName}
                                  onChange={(e) => {
                                    void rescanExtraConnectionSchema(
                                      extraConn.id,
                                      extraConn.connectionId,
                                      e.target.value,
                                    );
                                  }}
                                  disabled={scanningExtraId === extraConn.id}
                                  title="Switch to a different schema on this connection without going back"
                                  className="px-2.5 py-1.5 rounded-lg bg-surface-400/50 border border-white/10 text-xs text-white focus:outline-none focus:border-brand-500/50"
                                >
                                  {extraConn.schemas.map((s) => (
                                    <option key={s} value={s}>
                                      {s}
                                    </option>
                                  ))}
                                </select>
                              )}
                            <button
                              onClick={() =>
                                isPrimary
                                  ? handleScanSchema()
                                  : extraConn &&
                                    void rescanExtraConnectionSchema(
                                      extraConn.id,
                                      extraConn.connectionId,
                                      extraConn.schemaName,
                                    )
                              }
                              disabled={
                                isPrimary
                                  ? scanningSchema
                                  : scanningExtraId === extraConn?.id
                              }
                              className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                              title="Re-run the schema scan against this connection's source database without going back to the Connect Source step"
                            >
                              {(
                                isPrimary
                                  ? scanningSchema
                                  : scanningExtraId === extraConn?.id
                              ) ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <RefreshCw size={12} />
                              )}
                              Re-scan Schema
                            </button>
                          </>
                        )}
                      </div>
                    }
                  >
                    <div className="relative mb-3">
                      <Search
                        size={14}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30"
                      />
                      <input
                        type="text"
                        value={filterValue}
                        onChange={(e) => setFilterValue(e.target.value)}
                        placeholder="Filter tables by name…"
                        className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-brand-400/50"
                      />
                    </div>
                    {isPrimary && group.tables.length === 0 && (
                      <p className="text-xs text-amber-300/80 mb-3">
                        No tables owned by schema "{schemaResult.schema_name}"
                        {schemaResult.db_type === "oracle"
                          ? " — in Oracle, a login can have query access to another schema's tables without owning any itself."
                          : schemaResult.db_type === "mssql" ||
                              schemaResult.db_type === "synapse"
                            ? ' — for SQL Server/Synapse, "Schema" means the T-SQL schema tables live under (usually "dbo"), not the database or SQL pool name.'
                            : " — double-check this is the schema your tables actually live in, not the database/catalog name."}{" "}
                        {connStatus && connStatus.schemas.length > 1
                          ? "Try a different schema from the dropdown above."
                          : ""}
                      </p>
                    )}
                    {allTablesConflicted && (
                      <p className="text-xs text-amber-300/80 mb-3">
                        All {rawTableCount} table
                        {rawTableCount === 1 ? "" : "s"} in this schema share a
                        name with another connection's table, so none are shown
                        here — use the conflict picker above to switch
                        individual tables to this connection's version, or pick
                        a different schema from the dropdown above.
                      </p>
                    )}
                    {filterValue.trim() && (
                      <p className="text-xs text-white/30 mb-2">
                        {filteredGroupTables.length} of {group.tables.length}{" "}
                        tables
                      </p>
                    )}
                    {/* Capped well below 100vh (accounting for the stepper,
                    this card's own header/search box, and the Back/Connect
                    footer buttons that sit outside this scroll region) so
                    the whole page fits in the viewport and only this
                    section's list scrolls — avoids the page-level
                    scrollbar without touching the shared app-shell layout.
                    Each connection's section is capped independently, same
                    as a single-connection scan always was. */}
                    <div className="space-y-2 max-h-[42vh] overflow-y-auto pr-1">
                      {filteredGroupTables.map((table) => (
                        <div
                          key={table.table_name}
                          className="border border-white/8 rounded-lg overflow-hidden"
                        >
                          <button
                            onClick={() => toggleTable(table.table_name)}
                            className="w-full flex items-center justify-between px-4 py-3 bg-white/3 hover:bg-white/6 transition text-left"
                          >
                            <div className="flex items-center gap-3">
                              <Table2 size={14} className="text-brand-400" />
                              <span className="text-sm font-medium text-white">
                                {table.table_name}
                              </span>
                              <span className="text-xs text-white/30">
                                {table.columns.length} cols
                              </span>
                              {table.row_count_estimate != null && (
                                <span className="text-xs text-white/20">
                                  ~{table.row_count_estimate.toLocaleString()}{" "}
                                  rows
                                </span>
                              )}
                            </div>
                            {expandedTables.has(table.table_name) ? (
                              <ChevronDown
                                size={14}
                                className="text-white/30"
                              />
                            ) : (
                              <ChevronRight
                                size={14}
                                className="text-white/30"
                              />
                            )}
                          </button>
                          {expandedTables.has(table.table_name) && (
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-white/3 text-white/40">
                                    <th className="px-4 py-2 text-left font-medium">
                                      Column
                                    </th>
                                    <th className="px-4 py-2 text-left font-medium">
                                      Type
                                    </th>
                                    <th className="px-4 py-2 text-center font-medium">
                                      Nullable
                                    </th>
                                    <th className="px-4 py-2 text-center font-medium">
                                      PK
                                    </th>
                                    <th className="px-4 py-2 text-center font-medium">
                                      FK
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {table.columns.map((col) => (
                                    <tr
                                      key={col.name}
                                      className="border-t border-white/5 hover:bg-white/2"
                                    >
                                      <td className="px-4 py-1.5 font-mono text-white/80">
                                        {col.name}
                                      </td>
                                      <td className="px-4 py-1.5 text-cyan-300/70">
                                        {col.data_type}
                                        {col.length ? `(${col.length})` : ""}
                                        {col.precision && col.scale
                                          ? `(${col.precision},${col.scale})`
                                          : ""}
                                      </td>
                                      <td className="px-4 py-1.5 text-center text-white/30">
                                        {col.nullable ? "✓" : "✗"}
                                      </td>
                                      <td className="px-4 py-1.5 text-center">
                                        {col.is_primary_key && (
                                          <span className="text-yellow-400">
                                            PK
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-4 py-1.5 text-center">
                                        {col.is_foreign_key && (
                                          <span
                                            className="text-purple-400"
                                            title={col.fk_references ?? ""}
                                          >
                                            FK
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {filteredGroupTables.length === 0 &&
                      group.tables.length > 0 && (
                        <p className="text-sm text-white/30 text-center py-6">
                          No tables match "{filterValue}"
                        </p>
                      )}
                  </SectionCard>
                );
              })}

              <div className="flex justify-between">
                <button onClick={goPrev} className="ztd-btn ztd-btn-secondary">
                  <ArrowLeft size={14} /> Back
                </button>
                <button onClick={goNext} className="ztd-btn ztd-btn-primary">
                  Connect Target Platform <ArrowRight size={14} />
                </button>
              </div>
            </div>
          )}

          {/* ── Step: Salesforce ───────────────────────────────────────────── */}
          {step === "salesforce" && (
            <div className="space-y-4">
              <div
                className={
                  targetPlatformMode === "mirror"
                    ? "space-y-4"
                    : "grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-4 items-stretch"
                }
              >
                <SectionCard title="Target Platform Connection">
                  {/* Top-level mode: a CRM/Dataverse platform, or a plain RDBMS
                the migration is landing in directly. Switching modes clears
                whatever object catalogue the other mode had loaded so
                Continue to Mapping can't fire against stale data. */}
                  <div className="flex gap-2 mb-4">
                    {[
                      { value: "crm" as const, label: "CRM / Platform" },
                      { value: "rdbms" as const, label: "RDBMS / Database" },
                      { value: "no_target" as const, label: "No Target Model" },
                    ].map((opt) => {
                      const active = targetModelChoice === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => {
                            setTargetModelChoice(opt.value);
                            setSfOrgInfo(null);
                            setAvailableTargetObjects([]);
                            setImportedTargetModelFile(null);
                            setImportedTargetSchema(null);
                            setImportedTargetModelCreateResult(null);
                            if (opt.value === "no_target") {
                              // Bucket defaults to its "Mirror Source" sub-option.
                              setNoTargetSubOption("mirror");
                              setTargetPlatformMode("mirror");
                            } else {
                              setTargetPlatformMode(opt.value);
                            }
                          }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                            active
                              ? "bg-brand-500/30 border-brand-500/60 text-brand-200"
                              : "bg-white/5 border-white/10 text-white/50 hover:text-white"
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>

                  {targetModelChoice === "no_target" && (
                    <div className="flex gap-2 mb-4">
                      {(
                        [
                          { value: "mirror" as const, label: "Mirror Source" },
                          {
                            value: "import" as const,
                            label: "Import Target Model",
                          },
                        ] as const
                      ).map((opt) => {
                        const active = noTargetSubOption === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                              setNoTargetSubOption(opt.value);
                              setTargetPlatformMode(
                                opt.value === "mirror" ? "mirror" : "rdbms",
                              );
                              setSfOrgInfo(null);
                              setAvailableTargetObjects([]);
                              setImportedTargetModelFile(null);
                              setImportedTargetSchema(null);
                              setImportedTargetModelCreateResult(null);
                            }}
                            className={`px-2.5 py-1 rounded-md text-[11px] font-medium border transition ${
                              active
                                ? "bg-brand-500/20 border-brand-500/50 text-brand-200"
                                : "bg-white/5 border-white/10 text-white/50 hover:text-white"
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {targetPlatformMode === "mirror" ? (
                    <div className="space-y-3">
                      <p className="text-xs text-white/50">
                        No real target model yet? Skip connecting/scanning
                        anything — this auto-generates a target model that's a
                        structural 1:1 mirror of the source table(s) you pick
                        below: same tables, same columns, carried through
                        unchanged as staging text columns. Since there's no real
                        target object to match against, this goes straight to
                        Review & Export — you can still remove tables/columns or
                        edit target names there before generating DDL.
                      </p>

                      {groupTablesByConnection(schemaResult?.tables ?? []).map(
                        (group) => {
                          const isPrimary = group.key === "__primary__";
                          const filterValue = isPrimary
                            ? sourceTableMappingFilter
                            : (extraMappingTableFilters[group.key] ?? "");
                          const setFilterValue = (v: string) => {
                            if (isPrimary) setSourceTableMappingFilter(v);
                            else
                              setExtraMappingTableFilters((prev) => ({
                                ...prev,
                                [group.key]: v,
                              }));
                          };
                          const filteredGroupTables = filterValue.trim()
                            ? group.tables.filter((t) =>
                                t.table_name
                                  .toLowerCase()
                                  .includes(filterValue.trim().toLowerCase()),
                              )
                            : group.tables;
                          const selectedInGroup = group.tables.filter((t) =>
                            selectedSourceTablesForMapping.has(t.table_name),
                          ).length;
                          return (
                            <div key={group.key} className="space-y-3">
                              <div className="flex items-center justify-between">
                                <p className="text-xs text-white/50">
                                  {!isPrimary && (
                                    <span className="font-semibold text-brand-300">
                                      {group.label}
                                      {group.schemaName
                                        ? ` · ${group.schemaName}`
                                        : ""}{" "}
                                      —{" "}
                                    </span>
                                  )}
                                  {selectedInGroup} of {group.tables.length}{" "}
                                  source table
                                  {group.tables.length === 1 ? "" : "s"}{" "}
                                  selected to mirror
                                </p>
                                <div className="flex gap-3 flex-shrink-0">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setSelectedSourceTablesForMapping(
                                        (prev) => {
                                          const next = new Set(prev);
                                          group.tables.forEach((t) =>
                                            next.add(t.table_name),
                                          );
                                          return next;
                                        },
                                      )
                                    }
                                    className="text-xs text-brand-300 hover:text-brand-200 transition"
                                  >
                                    Select All
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setSelectedSourceTablesForMapping(
                                        (prev) => {
                                          const next = new Set(prev);
                                          group.tables.forEach((t) =>
                                            next.delete(t.table_name),
                                          );
                                          return next;
                                        },
                                      )
                                    }
                                    className="text-xs text-white/40 hover:text-white/70 transition"
                                  >
                                    Select None
                                  </button>
                                </div>
                              </div>
                              <div className="relative">
                                <Search
                                  size={14}
                                  className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30"
                                />
                                <input
                                  type="text"
                                  value={filterValue}
                                  onChange={(e) =>
                                    setFilterValue(e.target.value)
                                  }
                                  placeholder="Search table names…"
                                  className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-brand-400/50"
                                />
                              </div>
                              {filterValue.trim() && (
                                <p className="text-xs text-white/30">
                                  {filteredGroupTables.length} of{" "}
                                  {group.tables.length} tables match
                                </p>
                              )}
                              <div className="max-h-40 overflow-y-auto space-y-2 p-3 bg-surface-400/30 rounded-lg border border-white/8">
                                <div className="flex flex-wrap gap-x-6 gap-y-2">
                                  {filteredGroupTables.map((t) => (
                                    <label
                                      key={t.table_name}
                                      className="flex items-center gap-2 cursor-pointer group"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selectedSourceTablesForMapping.has(
                                          t.table_name,
                                        )}
                                        onChange={(e) => {
                                          setSelectedSourceTablesForMapping(
                                            (prev) => {
                                              const next = new Set(prev);
                                              e.target.checked
                                                ? next.add(t.table_name)
                                                : next.delete(t.table_name);
                                              return next;
                                            },
                                          );
                                        }}
                                        className="w-4 h-4 rounded border border-white/20 bg-surface-400/50 accent-brand-500 cursor-pointer"
                                      />
                                      <span className="text-sm text-white/80 group-hover:text-white transition">
                                        {t.table_name}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                                {filteredGroupTables.length === 0 && (
                                  <p className="text-xs text-white/30 text-center py-4 w-full">
                                    No tables match "{filterValue}"
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        },
                      )}
                      {selectedSourceTablesForMapping.size === 0 && (
                        <p className="text-xs text-red-400">
                          Select at least one source table to mirror
                        </p>
                      )}

                      <button
                        onClick={handleGenerateMirrorTargetModel}
                        disabled={
                          generatingMirrorTargetModel ||
                          !schemaResult ||
                          schemaResult.tables.length === 0 ||
                          selectedSourceTablesForMapping.size === 0
                        }
                        className="ztd-btn ztd-btn-primary"
                      >
                        {generatingMirrorTargetModel ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <ArrowRight size={14} />
                        )}
                        Create Target Model &amp; Continue to Review
                      </button>
                    </div>
                  ) : targetPlatformMode === "rdbms" ? (
                    <div className="space-y-3">
                      {targetConnectionId ? (
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div className="flex items-center gap-2 text-sm text-green-400">
                            <CheckCircle2 size={14} />
                            Connected — {targetConnLabel || "saved connection"}
                            {ddlTargetConnected?.db_version
                              ? ` · ${ddlTargetConnected.db_version}`
                              : ""}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button
                              onClick={handleRefreshTargetConnection}
                              disabled={refreshingConnection}
                              title="Re-test this connection — a saved/activated connection isn't verified until you test it"
                              className="ztd-btn ztd-btn-secondary"
                            >
                              {refreshingConnection ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <RefreshCw size={14} />
                              )}
                              Refresh Connection
                            </button>
                            <button
                              onClick={() => setShowTargetConnectDialog(true)}
                              className="ztd-btn ztd-btn-secondary"
                            >
                              <RefreshCw size={14} />
                              Change connection
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setShowTargetConnectDialog(true)}
                            className="ztd-btn ztd-btn-primary"
                          >
                            <RefreshCw size={14} />
                            Connect Database
                          </button>
                          <span className="text-xs text-white/40">
                            Reuse a connection any agent already saved for this
                            project, or connect a new one.
                          </span>
                        </div>
                      )}

                      <ConnectDatabaseDialog
                        open={showTargetConnectDialog}
                        onClose={() => setShowTargetConnectDialog(false)}
                        title="Connect Target Database"
                        projectId={projectId}
                        kind="target"
                        agentKey="data_converter"
                        defaultDbType="oracle"
                        testIdPrefix="converter-target"
                        onConnectExisting={handleTargetConnectExisting}
                        onConnectNew={handleTargetConnectNew}
                      />

                      {targetConnectionId && (
                        <div className="max-w-sm">
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="block text-xs font-medium text-white/50">
                              Target Schema
                            </label>
                            <div className="flex items-center gap-3">
                              {targetSchemas.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setTargetSchemaManualEntry((v) => !v)
                                  }
                                  className="text-[11px] text-brand-400 hover:text-brand-300"
                                >
                                  {targetSchemaManualEntry
                                    ? "Choose from list"
                                    : "Type manually"}
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => void handleLoadTargetSchemas()}
                                disabled={loadingTargetSchemas}
                                title="List schemas that exist on the connected target"
                                className="flex items-center gap-1 text-[11px] text-white/40 hover:text-white/70 disabled:opacity-40"
                              >
                                {loadingTargetSchemas ? (
                                  <Loader2 size={10} className="animate-spin" />
                                ) : (
                                  <RefreshCw size={10} />
                                )}
                                {targetSchemas.length > 0
                                  ? "Refresh"
                                  : "List schemas"}
                              </button>
                            </div>
                          </div>
                          {targetSchemaManualEntry ||
                          targetSchemas.length === 0 ? (
                            <input
                              type="text"
                              value={ddlTargetSchema}
                              onChange={(e) =>
                                setDdlTargetSchema(e.target.value)
                              }
                              placeholder="Schema to scan"
                              className="w-full px-3 py-2 bg-surface-400/50 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-brand-500/50 transition"
                            />
                          ) : (
                            <select
                              value={ddlTargetSchema}
                              onChange={(e) =>
                                setDdlTargetSchema(e.target.value)
                              }
                              className="w-full px-3 py-2 bg-surface-400/50 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-brand-500/50 transition"
                            >
                              <option value="">Select schema…</option>
                              {targetSchemas.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      )}

                      {!(
                        targetModelChoice === "no_target" &&
                        noTargetSubOption === "import"
                      ) ? (
                        <>
                          <div className="flex items-center gap-3 pt-1">
                            <button
                              onClick={handleScanTargetRdbms}
                              disabled={
                                scanningTargetRdbms || !targetConnectionId
                              }
                              className="ztd-btn ztd-btn-secondary"
                            >
                              {scanningTargetRdbms ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <RefreshCw size={14} />
                              )}
                              Scan Target Schema
                            </button>

                            {sfOrgInfo && (
                              <div className="flex items-center gap-2 text-sm text-green-400">
                                <CheckCircle2 size={14} />
                                Scanned {sfOrgInfo.object_count} table(s)
                              </div>
                            )}
                          </div>

                          {targetSchemaChanges &&
                            (targetSchemaChanges.has_changes ? (
                              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl space-y-2">
                                <div className="flex items-center gap-2 text-sm text-amber-300 font-medium">
                                  <AlertTriangle size={14} />
                                  Schema changed since last scan
                                </div>
                                <div className="text-xs text-white/60 space-y-1.5">
                                  {targetSchemaChanges.tables_added.length >
                                    0 && (
                                    <div>
                                      <span className="text-green-400 font-medium">
                                        +{" "}
                                        {
                                          targetSchemaChanges.tables_added
                                            .length
                                        }{" "}
                                        table(s) added:
                                      </span>{" "}
                                      {targetSchemaChanges.tables_added.join(
                                        ", ",
                                      )}
                                    </div>
                                  )}
                                  {targetSchemaChanges.tables_removed.length >
                                    0 && (
                                    <div>
                                      <span className="text-red-400 font-medium">
                                        −{" "}
                                        {
                                          targetSchemaChanges.tables_removed
                                            .length
                                        }{" "}
                                        table(s) removed:
                                      </span>{" "}
                                      {targetSchemaChanges.tables_removed.join(
                                        ", ",
                                      )}
                                    </div>
                                  )}
                                  {targetSchemaChanges.tables_changed.length >
                                    0 && (
                                    <div className="space-y-1">
                                      <span className="text-amber-300 font-medium">
                                        {
                                          targetSchemaChanges.tables_changed
                                            .length
                                        }{" "}
                                        table(s) changed:
                                      </span>
                                      {targetSchemaChanges.tables_changed.map(
                                        (t) => (
                                          <div
                                            key={t.table_name}
                                            className="pl-3 border-l border-white/10"
                                          >
                                            <span className="text-white/80 font-medium">
                                              {t.table_name}
                                            </span>
                                            {t.columns_added.length > 0 && (
                                              <span className="text-green-400">
                                                {" "}
                                                +{t.columns_added.join(", ")}
                                              </span>
                                            )}
                                            {t.columns_removed.length > 0 && (
                                              <span className="text-red-400">
                                                {" "}
                                                −{t.columns_removed.join(", ")}
                                              </span>
                                            )}
                                            {t.columns_modified.length > 0 && (
                                              <span
                                                className="text-amber-300"
                                                title={t.columns_modified
                                                  .map(
                                                    (c) =>
                                                      `${c.column}: ${c.changes.join("; ")}`,
                                                  )
                                                  .join("\n")}
                                              >
                                                {" "}
                                                ~
                                                {t.columns_modified
                                                  .map((c) => c.column)
                                                  .join(", ")}
                                              </span>
                                            )}
                                          </div>
                                        ),
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-xs text-white/40">
                                <CheckCircle2 size={12} /> No schema changes
                                since the last scan
                              </div>
                            ))}
                        </>
                      ) : (
                        <div className="space-y-3 pt-1">
                          <p className="text-xs text-white/50">
                            For a target that doesn't have these tables yet —
                            upload a SQL DDL script, COBOL copybook, a data
                            dictionary export (CSV/Excel) from erwin, ER/Studio,
                            or another data modeling tool, an entity/attribute
                            XML export, or a JSON/XML/Parquet schema. Each
                            table's own declared column types are used as-is
                            (not collapsed to a generic staging type) to create
                            it in the connected target database above.
                          </p>
                          <div className="flex items-center gap-3">
                            <input
                              ref={importedTargetModelFileInputRef}
                              type="file"
                              accept={_LEGACY_CODE_EXTENSIONS.join(",")}
                              className="hidden"
                              onChange={(e) =>
                                setImportedTargetModelFile(
                                  e.target.files?.[0] ?? null,
                                )
                              }
                            />
                            <button
                              onClick={() =>
                                importedTargetModelFileInputRef.current?.click()
                              }
                              className="ztd-btn ztd-btn-secondary flex-shrink-0"
                            >
                              <Upload size={14} />
                              Import Data Model
                            </button>
                            <span className="text-xs text-white/40 truncate">
                              {importedTargetModelFile?.name ??
                                "No file selected"}
                            </span>
                            <button
                              onClick={handleParseImportedTargetModel}
                              disabled={
                                parsingImportedTargetModel ||
                                !importedTargetModelFile
                              }
                              className="ztd-btn ztd-btn-secondary flex-shrink-0"
                            >
                              {parsingImportedTargetModel ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <Upload size={14} />
                              )}
                              Parse Target Model
                            </button>
                          </div>

                          {importedTargetSchema && (
                            <div className="flex items-center gap-2 text-sm text-green-400">
                              <CheckCircle2 size={14} />
                              Parsed {importedTargetSchema.tables.length}{" "}
                              table(s) from{" "}
                              {importedTargetModelFile?.name ??
                                "the uploaded file"}
                            </div>
                          )}

                          <div className="flex items-center gap-3">
                            <button
                              onClick={handleCreateImportedTargetModel}
                              disabled={
                                creatingImportedTargetModel ||
                                !targetConnectionId ||
                                !importedTargetSchema ||
                                importedTargetSchema.tables.length === 0
                              }
                              className="ztd-btn ztd-btn-primary"
                            >
                              {creatingImportedTargetModel ? (
                                <Loader2 size={14} className="animate-spin" />
                              ) : (
                                <ArrowRight size={14} />
                              )}
                              Create Target Model in Database
                            </button>
                            {!targetConnectionId && (
                              <span className="text-xs text-white/40">
                                Connect a target database above first
                              </span>
                            )}
                          </div>

                          {importedTargetModelCreateResult && (
                            <div className="flex items-center gap-2 text-sm text-green-400">
                              <CheckCircle2 size={14} />
                              {importedTargetModelCreateResult.created} table(s)
                              created/updated
                              {importedTargetModelCreateResult.upToDate > 0
                                ? `, ${importedTargetModelCreateResult.upToDate} already up to date`
                                : ""}{" "}
                              — now select source tables to map below and
                              continue to mapping.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    renderCrmConnectFields()
                  )}
                </SectionCard>

                {/* 2026-09-02, requested by user: a project can have several
              saved target connections of different platforms/db_types
              (Oracle, Snowflake, Databricks, Salesforce, Dynamics 365, Power
              Platform, ...) — always show the library, not just before the
              first one is connected/tested, so switching to a different one
              doesn't require reconnecting from scratch. */}
                {projectId &&
                  targetPlatformMode !== "mirror" &&
                  (targetPlatformMode === "rdbms" ? (
                    <SavedConnectionsPanel
                      projectId={projectId}
                      kind="target"
                      onActivated={handleTargetConnectionActivatedFromLibrary}
                      onConnectExisting={handleTargetConnectExisting}
                    />
                  ) : (
                    <SavedConnectionsPanel
                      projectId={projectId}
                      kind="crm"
                      currentConn={sfConn}
                      onLoad={handleLoadSavedCrmConn}
                      onActivated={handleCrmConnectionActivatedFromLibrary}
                    />
                  ))}
              </div>

              {targetPlatformMode !== "mirror" && (
                <SectionCard title="Source Tables to Auto-Map">
                  {!sfOrgInfo ? (
                    // A saved connection's fields being pre-filled above does
                    // NOT mean it's actually live this session — sfOrgInfo is
                    // only set by a real "Test Connection" (or RDBMS scan)
                    // call. Showing the table list + best-match suggestions
                    // before that would let a reviewer approve mappings
                    // computed against a stale or no-longer-valid catalog.
                    <p className="text-xs text-white/40 py-2">
                      Test (or re-test) the Target Platform connection above to
                      load its current object catalog before selecting source
                      tables to auto-map.
                    </p>
                  ) : (
                    <>
                      {groupTablesByConnection(schemaResult?.tables ?? []).map(
                        (group) => {
                          const isPrimary = group.key === "__primary__";
                          const filterValue = isPrimary
                            ? sourceTableMappingFilter
                            : (extraMappingTableFilters[group.key] ?? "");
                          const setFilterValue = (v: string) => {
                            if (isPrimary) setSourceTableMappingFilter(v);
                            else
                              setExtraMappingTableFilters((prev) => ({
                                ...prev,
                                [group.key]: v,
                              }));
                          };
                          const filteredGroupTables = filterValue.trim()
                            ? group.tables.filter((t) =>
                                t.table_name
                                  .toLowerCase()
                                  .includes(filterValue.trim().toLowerCase()),
                              )
                            : group.tables;
                          const selectedInGroup = group.tables.filter((t) =>
                            selectedSourceTablesForMapping.has(t.table_name),
                          ).length;
                          return (
                            <div
                              key={group.key}
                              className={isPrimary ? "" : "mt-4"}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <p className="text-xs text-white/50">
                                  {isPrimary ? (
                                    <>
                                      {selectedInGroup} of {group.tables.length}{" "}
                                      tables selected — GovConnect target
                                      objects are loaded from your org's full
                                      standard + custom catalog (no longer
                                      limited to a fixed shortlist), and each
                                      selected table gets an auto-suggested
                                      best-match object you can still override.
                                    </>
                                  ) : (
                                    <>
                                      <span className="font-semibold text-brand-300">
                                        {group.label}
                                        {group.schemaName
                                          ? ` · ${group.schemaName}`
                                          : ""}
                                      </span>{" "}
                                      — {selectedInGroup} of{" "}
                                      {group.tables.length} table
                                      {group.tables.length === 1
                                        ? ""
                                        : "s"}{" "}
                                      selected
                                    </>
                                  )}
                                </p>
                                <div className="flex gap-3 flex-shrink-0">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setSelectedSourceTablesForMapping(
                                        (prev) => {
                                          const next = new Set(prev);
                                          group.tables.forEach((t) =>
                                            next.add(t.table_name),
                                          );
                                          return next;
                                        },
                                      )
                                    }
                                    className="text-xs text-brand-300 hover:text-brand-200 transition"
                                  >
                                    Select All
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setSelectedSourceTablesForMapping(
                                        (prev) => {
                                          const next = new Set(prev);
                                          group.tables.forEach((t) =>
                                            next.delete(t.table_name),
                                          );
                                          return next;
                                        },
                                      )
                                    }
                                    className="text-xs text-white/40 hover:text-white/70 transition"
                                  >
                                    Select None
                                  </button>
                                </div>
                              </div>
                              <div className="relative mt-2 mb-2">
                                <Search
                                  size={14}
                                  className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30"
                                />
                                <input
                                  type="text"
                                  value={filterValue}
                                  onChange={(e) =>
                                    setFilterValue(e.target.value)
                                  }
                                  placeholder="Search table names…"
                                  className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-brand-400/50"
                                />
                              </div>
                              {filterValue.trim() && (
                                <p className="text-xs text-white/30 mb-2">
                                  {filteredGroupTables.length} of{" "}
                                  {group.tables.length} tables match
                                </p>
                              )}
                              <div className="max-h-40 overflow-y-auto space-y-2 p-3 bg-surface-400/30 rounded-lg border border-white/8">
                                <div className="flex flex-wrap gap-x-6 gap-y-2">
                                  {filteredGroupTables.map((t) => (
                                    <label
                                      key={t.table_name}
                                      className="flex items-center gap-2 cursor-pointer group"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selectedSourceTablesForMapping.has(
                                          t.table_name,
                                        )}
                                        onChange={(e) => {
                                          setSelectedSourceTablesForMapping(
                                            (prev) => {
                                              const next = new Set(prev);
                                              e.target.checked
                                                ? next.add(t.table_name)
                                                : next.delete(t.table_name);
                                              return next;
                                            },
                                          );
                                        }}
                                        className="w-4 h-4 rounded border border-white/20 bg-surface-400/50 accent-brand-500 cursor-pointer"
                                      />
                                      <span className="text-sm text-white/80 group-hover:text-white transition">
                                        {t.table_name}
                                      </span>
                                    </label>
                                  ))}
                                </div>
                                {filteredGroupTables.length === 0 && (
                                  <p className="text-xs text-white/30 text-center py-4 w-full">
                                    No tables match "{filterValue}"
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        },
                      )}
                      {selectedSourceTablesForMapping.size === 0 && (
                        <p className="mt-1.5 text-xs text-red-400">
                          Select at least one source table to auto-map
                        </p>
                      )}
                    </>
                  )}
                </SectionCard>
              )}

              {/* Surfaced here (rather than only after "Continue to Mapping")
              so a domain skill / uploaded reference doc is already in place
              before the very first auto-suggested mapping is generated below
              — otherwise that first, trust-building suggestion is generated
              with no domain bias at all, since it fires before the operator
              would otherwise ever see these controls. Still shown again in
              the Mapping step itself, since adjusting either one later
              should not require clicking Back. */}
              {targetPlatformMode !== "mirror" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-stretch">
                  <FunctionalSkillsPicker projectId={projectId} />
                  <MappingKnowledgeBaseCard projectId={projectId} />
                </div>
              )}

              {targetPlatformMode === "mirror" ? (
                <div className="flex justify-start">
                  <button
                    onClick={goPrev}
                    className="ztd-btn ztd-btn-secondary"
                  >
                    <ArrowLeft size={14} /> Back
                  </button>
                </div>
              ) : (
                <div className="flex justify-between">
                  <button
                    onClick={goPrev}
                    className="ztd-btn ztd-btn-secondary"
                  >
                    <ArrowLeft size={14} /> Back
                  </button>
                  <button
                    onClick={handleLoadSfObjects}
                    disabled={
                      continuingToMapping ||
                      !sfOrgInfo ||
                      selectedSourceTablesForMapping.size === 0 ||
                      availableTargetObjects.length === 0
                    }
                    className="ztd-btn ztd-btn-primary"
                  >
                    {continuingToMapping ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <ArrowRight size={14} />
                    )}
                    Continue to Mapping
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Step: Mapping ──────────────────────────────────────────────── */}
          {step === "mapping" && (
            <div className="space-y-4">
              {/* Sticky action bar — stays visible while scrolling through mapping rows below */}
              <div className="sticky top-0 z-20 -mx-4 px-4 py-2.5 bg-surface-500/95 backdrop-blur border-b border-white/8 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm text-white/50">
                  {sfObjects.length} GovConnect objects loaded
                  {mappings.length > 0 &&
                    ` · ${mappings.length} table mappings generated`}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleGenerateMappings}
                    disabled={generatingMappings || mappingPairs.length === 0}
                    className="ztd-btn ztd-btn-primary"
                  >
                    {generatingMappings ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Zap size={14} />
                    )}
                    {mappings.length > 0
                      ? "Re-generate Mappings"
                      : "Generate Mappings"}
                  </button>
                  <button
                    onClick={() => handleGenerateSttm()}
                    disabled={generatingSttm || mappings.length === 0}
                    className="ztd-btn ztd-btn-primary"
                  >
                    {generatingSttm ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <FileSpreadsheet size={14} />
                    )}
                    Generate STTM
                  </button>
                </div>
              </div>

              {/* 2026-09-08, requested by user: these three used to stack as
              full-width horizontal bars; side by side as columns instead on
              wider screens (each already wraps its own internal row via
              flex-wrap, so narrowing them into a column is safe).
              2026-09-13, requested by user: items-stretch (rather than
              items-start) so all three end up the same height regardless of
              how much text each one happens to have. */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
                <FunctionalSkillsPicker projectId={projectId} />

                <ProfileSourceDataCard
                  schemaResult={schemaResult}
                  projectId={projectId}
                />

                {/* One-click premap — pre-populates every source table with a
                best-guess target object, biased by the domain skill
                auto-detected from the scanned schema. When several source
                tables land on the same target, join keys between them are
                auto-detected too, so the operator reviews/adjusts a full
                per-target list instead of building it pair by pair. */}
                {(() => {
                  const mappedTableCount = new Set(
                    mappings.flatMap((m) =>
                      m.source_tables && m.source_tables.length > 0
                        ? m.source_tables
                        : [m.source_table],
                    ),
                  ).size;
                  const totalTableCount = schemaResult?.tables.length ?? 0;
                  const allMapped =
                    totalTableCount > 0 && mappedTableCount >= totalTableCount;
                  return (
                    <div className="p-4 bg-brand-500/5 border border-brand-500/20 rounded-xl">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div>
                          <p className="text-sm font-semibold text-white">
                            Map All Remaining Tables
                          </p>
                          <p className="text-xs text-white/50 mt-0.5">
                            {schemaResult ? (
                              <>
                                {mappedTableCount} of {totalTableCount} source
                                tables mapped so far. Pre-populates every table
                                that doesn't have a mapping yet with its
                                best-guess target object, using domain
                                vocabulary auto-detected from the scanned schema
                                — tables that land on the same target are
                                automatically joined together. Existing mappings
                                are left as-is.
                              </>
                            ) : (
                              <>
                                {mappedTableCount} table(s) mapped (resumed from
                                a previous session). Reconnect a source database
                                and target platform to scan for and map
                                additional tables.
                              </>
                            )}
                          </p>
                        </div>
                        <button
                          onClick={handleAutoMapAll}
                          disabled={
                            autoMappingAll ||
                            !schemaResult ||
                            sfObjects.length === 0 ||
                            allMapped
                          }
                          className="ztd-btn ztd-btn-primary flex-shrink-0"
                        >
                          {autoMappingAll ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Sparkles size={14} />
                          )}
                          {allMapped
                            ? "All Tables Mapped"
                            : "Map All Remaining Tables"}
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>

              <MappingKnowledgeBaseCard projectId={projectId} />

              {/* Multi-pair mapping configuration — one independent Add-pair
              row per source connection when more than one is in play, each
              scoped to only that connection's own tables, so picking a
              table for one connection never shows or touches another
              connection's tables/selection. */}
              <div className="p-4 bg-white/3 border border-white/8 rounded-xl space-y-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-xs font-medium text-white/50 uppercase tracking-wide">
                      Source → Target Mapping Pairs
                    </p>
                    <p className="text-[11px] text-white/30 mt-0.5">
                      No suitable target object for a table? Add it below, then
                      click the <Copy size={10} className="inline -mt-0.5" />{" "}
                      icon next to its target field to load it as-is instead —
                      every source column carries through unchanged.
                    </p>
                  </div>
                  {/* 2026-09-01, requested by user: default to a single
                  src → target pair; mapping several at once is now an
                  explicit opt-in via this toggle. */}
                  <label className="flex items-center gap-1.5 text-[11px] text-white/40 hover:text-white/70 cursor-pointer flex-shrink-0 select-none">
                    <input
                      type="checkbox"
                      checked={bulkMappingMode}
                      onChange={(e) => {
                        setBulkMappingMode(e.target.checked);
                        if (!e.target.checked) {
                          setBulkSourceTablesByGroup((prev) =>
                            Object.fromEntries(
                              Object.entries(prev).map(([k, v]) => [
                                k,
                                capMappingSelectionForMode(v, false),
                              ]),
                            ),
                          );
                          setBulkTargetObjectsByGroup((prev) =>
                            Object.fromEntries(
                              Object.entries(prev).map(([k, v]) => [
                                k,
                                capMappingSelectionForMode(v, false),
                              ]),
                            ),
                          );
                        }
                      }}
                      className="w-3.5 h-3.5 rounded border border-white/20 bg-surface-400/50 accent-brand-500 cursor-pointer"
                    />
                    Map multiple tables/objects at once
                  </label>
                </div>
                {groupTablesByConnection(schemaResult?.tables ?? []).map(
                  (group) => {
                    const groupSourceTables =
                      bulkSourceTablesByGroup[group.key] ?? [];
                    const groupTargetObjects =
                      bulkTargetObjectsByGroup[group.key] ?? [];
                    const showConnectionLabel =
                      extraSourceConnections.length > 0;
                    return (
                      <div key={group.key} className="space-y-1.5">
                        {showConnectionLabel && (
                          <p className="text-[11px] font-medium text-white/40 uppercase tracking-wide">
                            {group.label}
                            {group.schemaName ? ` · ${group.schemaName}` : ""}
                          </p>
                        )}
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <FieldMultiSearchSelect
                              values={groupSourceTables}
                              options={group.tables.map((t) => ({
                                value: t.table_name,
                                label: t.table_name,
                              }))}
                              onChange={(v) =>
                                setBulkSourceTablesByGroup((prev) => ({
                                  ...prev,
                                  [group.key]: capMappingSelectionForMode(
                                    v,
                                    bulkMappingMode,
                                  ),
                                }))
                              }
                              placeholder={
                                bulkMappingMode
                                  ? "Select source table(s)…"
                                  : "Select source table…"
                              }
                              className="w-full px-3 py-2 bg-surface-400/50 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-brand-500/50 transition"
                            />
                          </div>
                          <ArrowRight
                            size={14}
                            className="text-white/30 flex-shrink-0"
                          />
                          <div className="flex-1">
                            <FieldMultiSearchSelect
                              values={groupTargetObjects}
                              options={sfObjectPickerOptions}
                              onChange={(v) =>
                                setBulkTargetObjectsByGroup((prev) => ({
                                  ...prev,
                                  [group.key]: capMappingSelectionForMode(
                                    v,
                                    bulkMappingMode,
                                  ),
                                }))
                              }
                              placeholder={
                                bulkMappingMode
                                  ? "Select target object(s)…"
                                  : "Select target object…"
                              }
                              className="w-full px-3 py-2 bg-surface-400/50 border border-white/10 rounded-lg text-sm text-brand-300 focus:outline-none focus:border-brand-500/50 transition"
                            />
                          </div>
                          <button
                            onClick={() => addBulkMappingPairs(group.key)}
                            disabled={
                              groupSourceTables.length === 0 &&
                              groupTargetObjects.length === 0
                            }
                            className="ztd-btn ztd-btn-secondary ztd-btn-sm flex-shrink-0"
                          >
                            <Plus size={12} /> Add
                          </button>
                        </div>
                      </div>
                    );
                  },
                )}
                {mappingPairs.length === 0 && (
                  <p className="text-xs text-white/30 italic text-center py-3">
                    No pairs yet — click "Auto-Map All Source Tables" above to
                    pre-populate every table, or select source table(s) and
                    target object(s) above and click "Add" to build pairs
                    manually.
                  </p>
                )}
                {(() => {
                  const duplicateTargetObjects =
                    getDuplicateTargetObjects(mappingPairs);
                  const showConnectionBadges =
                    extraSourceConnections.length > 0;
                  return mappingPairs.map((pair, idx) => {
                    const isDuplicateTarget =
                      pair.targetObject &&
                      duplicateTargetObjects.has(pair.targetObject);
                    const connInfo = showConnectionBadges
                      ? getSourceTableConnectionInfo(pair.sourceTable)
                      : null;
                    return (
                      <div
                        key={idx}
                        className={`flex items-center gap-2 rounded-lg ${
                          isDuplicateTarget
                            ? "bg-amber-500/10 ring-1 ring-amber-500/30 px-2 py-1.5"
                            : ""
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <FieldSearchSelect
                            value={pair.sourceTable}
                            options={sourceTablePickerOptions}
                            onChange={(v) =>
                              updateMappingPair(idx, "sourceTable", v)
                            }
                            placeholder="Search source tables…"
                            emptyLabel="No matching tables"
                            className="w-full px-3 py-2 bg-surface-400/50 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-brand-500/50 transition"
                          />
                        </div>
                        {connInfo && (
                          <span
                            title={`Source connection: ${connInfo.label}${
                              connInfo.schemaName
                                ? ` · ${connInfo.schemaName}`
                                : ""
                            }`}
                            className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-white/5 text-white/40 border border-white/10 flex-shrink-0 max-w-[110px] truncate"
                          >
                            {connInfo.label}
                          </span>
                        )}
                        <ArrowRight
                          size={14}
                          className="text-white/30 flex-shrink-0"
                        />
                        <div className="flex-1">
                          {pair.useSourceAsIsTarget ? (
                            <input
                              type="text"
                              value={pair.targetObject}
                              onChange={(e) =>
                                updateMappingPair(
                                  idx,
                                  "targetObject",
                                  e.target.value,
                                )
                              }
                              placeholder={
                                pair.sourceTable || "Target table name"
                              }
                              title="Loaded as-is — every source column carries through unchanged, no target object matching. Rename the target table here if you don't want to keep the source's own name."
                              className="w-full px-3 py-2 bg-surface-400/30 border border-dashed border-white/15 rounded-lg text-sm text-white/70 focus:outline-none focus:border-brand-500/50 focus:text-white transition"
                            />
                          ) : (
                            <FieldSearchSelect
                              value={pair.targetObject}
                              options={sfObjectPickerOptions}
                              onChange={(v) =>
                                updateMappingPair(idx, "targetObject", v)
                              }
                              placeholder="Search target objects…"
                              className="w-full px-3 py-2 bg-surface-400/50 border border-white/10 rounded-lg text-sm text-brand-300 focus:outline-none focus:border-brand-500/50 transition"
                            />
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleUseSourceAsIsTarget(idx)}
                          title={
                            pair.useSourceAsIsTarget
                              ? "Switch back to picking a real target object"
                              : "No suitable target object? Load this source table as-is (same columns/types), skipping target matching."
                          }
                          className={`p-1.5 rounded-lg transition flex-shrink-0 ${
                            pair.useSourceAsIsTarget
                              ? "text-brand-400 bg-brand-500/15"
                              : "text-white/30 hover:text-brand-400 hover:bg-brand-400/10"
                          }`}
                        >
                          <Copy size={14} />
                        </button>
                        {isDuplicateTarget && (
                          <span
                            title="Multiple source tables map to this same target object — confirm this is intentional"
                            className="flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 flex-shrink-0"
                          >
                            <AlertTriangle size={10} /> Duplicate target
                          </span>
                        )}
                        <button
                          onClick={() => removeMappingPair(idx)}
                          className="p-1.5 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-400/10 transition flex-shrink-0"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Upload a data-mapping document, at the user's discretion,
              instead of (or as a seed for) the AI auto-mapping above. */}
              <div className="p-3 bg-white/3 border border-white/8 rounded-xl flex items-center gap-3 flex-wrap">
                <span className="text-xs font-medium text-white/50 uppercase tracking-wide">
                  Or upload your own mapping document
                </span>
                <div className="flex gap-1 p-0.5 bg-white/5 rounded-lg">
                  {[
                    { value: "replace" as const, label: "Replace" },
                    { value: "seed" as const, label: "Seed & Fill Gaps" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setMappingImportMode(opt.value)}
                      className={`px-2.5 py-1 rounded-md text-xs font-medium transition ${
                        mappingImportMode === opt.value
                          ? "bg-brand-500/20 text-brand-300"
                          : "text-white/50 hover:text-white"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <label
                  className={`ztd-btn ztd-btn-secondary ztd-btn-sm cursor-pointer ${importingMappingDoc ? "opacity-40 pointer-events-none" : ""}`}
                >
                  {importingMappingDoc ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Upload size={12} />
                  )}
                  Upload Mapping Document
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (f) handleImportMappingDocument(f);
                    }}
                  />
                </label>
              </div>

              {mappingImportWarnings.length > 0 && (
                <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-xl text-xs text-yellow-300 space-y-1">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium">
                      {mappingImportWarnings.length} row(s) skipped during
                      import:
                    </p>
                    {mappingImportWarnings.length > 10 && (
                      <button
                        onClick={() =>
                          downloadFailuresTxt(
                            "Mapping import — rows skipped",
                            `mapping_import_skipped_rows_${filenameTimestamp()}.txt`,
                            mappingImportWarnings,
                          )
                        }
                        className="text-yellow-300 hover:text-yellow-200 underline underline-offset-2 flex-shrink-0"
                      >
                        Download all (.txt)
                      </button>
                    )}
                  </div>
                  {mappingImportWarnings.slice(0, 10).map((w, i) => (
                    <p key={i} className="text-yellow-300/70">
                      {w}
                    </p>
                  ))}
                  {mappingImportWarnings.length > 10 && (
                    <p className="text-yellow-300/50 italic">
                      + {mappingImportWarnings.length - 10} more — see download
                      above
                    </p>
                  )}
                </div>
              )}

              {mappings.length > 0 && (
                <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                  {mappings.map((m, idx) => {
                    const mapped = m.field_mappings.filter(
                      (f) => f.status === "Mapped",
                    ).length;
                    const review = m.field_mappings.filter(
                      (f) => f.status === "Review",
                    ).length;
                    const isComplex = (m.source_tables?.length ?? 0) > 1;
                    return (
                      <div
                        key={m.source_table}
                        className="border border-white/8 rounded-lg overflow-hidden"
                      >
                        <div className="w-full flex items-center justify-between px-4 py-3 bg-white/3 hover:bg-white/6 transition">
                          <button
                            onClick={() => toggleMapping(m.source_table)}
                            className="flex-1 flex items-center justify-between text-left min-w-0"
                          >
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className="text-sm font-semibold text-brand-300">
                                {m.target_label || m.target_object}
                              </span>
                              <ArrowLeft size={12} className="text-white/20" />
                              <span className="text-sm text-white/50 font-mono">
                                {isComplex
                                  ? m.source_tables!.join(" ⋈ ")
                                  : m.source_table}
                              </span>
                              {isComplex && (
                                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 flex items-center gap-1">
                                  <GitMerge size={10} /> Joined
                                </span>
                              )}
                              <StatusBadge
                                status={
                                  m.confidence >= 0.7 ? "Mapped" : "Review"
                                }
                                confidence={m.confidence}
                              />
                              <span className="text-xs text-white/30">
                                {mapped} mapped · {review} review
                              </span>
                            </div>
                            {expandedMappings.has(m.source_table) ? (
                              <ChevronDown
                                size={14}
                                className="text-white/30 flex-shrink-0"
                              />
                            ) : (
                              <ChevronRight
                                size={14}
                                className="text-white/30 flex-shrink-0"
                              />
                            )}
                          </button>
                          <button
                            onClick={() => removeMapping(idx)}
                            className="ml-2 p-1.5 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-400/10 transition flex-shrink-0"
                            title="Remove this table's mapping"
                          >
                            <X size={14} />
                          </button>
                        </div>
                        {isComplex && expandedMappings.has(m.source_table) && (
                          <div className="px-4 py-3 bg-blue-500/5 border-t border-blue-500/10 space-y-2">
                            {(m.joins ?? []).map((j, ji) => (
                              <p
                                key={ji}
                                className="text-[11px] font-mono text-blue-300/80"
                              >
                                {j.join_type === "left"
                                  ? "LEFT JOIN"
                                  : "INNER JOIN"}
                                : {j.left_table}.{j.left_column} ={" "}
                                {j.right_table}.{j.right_column}
                              </p>
                            ))}
                            {m.join_sql_preview && (
                              <pre className="text-[11px] font-mono text-white/50 bg-black/20 rounded-lg p-2 overflow-x-auto whitespace-pre">
                                {m.join_sql_preview}
                              </pre>
                            )}
                            <p className="text-[10px] text-amber-400/70">
                              Preview only — not yet executable from Load to
                              Target. See Data Load step for details.
                            </p>
                          </div>
                        )}
                        {expandedMappings.has(m.source_table) &&
                          (() => {
                            const fieldTable = (
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-surface-500/95 backdrop-blur text-white/40 sticky top-0 z-10">
                                    <th className="px-4 py-2 text-left font-medium">
                                      Source Table
                                    </th>
                                    <th className="px-4 py-2 text-left font-medium">
                                      Source Column
                                    </th>
                                    <th className="px-4 py-2 text-left font-medium">
                                      Source Type
                                    </th>
                                    <th className="px-4 py-2 text-left font-medium">
                                      Target Field
                                    </th>
                                    <th className="px-4 py-2 text-left font-medium">
                                      Target Type
                                    </th>
                                    <th className="px-4 py-2 text-left font-medium">
                                      Transform
                                    </th>
                                    <th className="px-4 py-2 text-center font-medium">
                                      Status
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(() => {
                                    const duplicateTargetFields =
                                      getDuplicateTargetFields(
                                        m.field_mappings,
                                      );
                                    return m.field_mappings.map((fm, fmIdx) => {
                                      const isDuplicateTarget =
                                        duplicateTargetFields.has(
                                          fm.target_field,
                                        );
                                      return (
                                        <tr
                                          key={`${fm.source_table_ref ?? ""}.${fm.source_column}.${fmIdx}`}
                                          className={`border-t border-white/5 hover:bg-white/2 ${isDuplicateTarget ? "bg-red-500/5" : ""}`}
                                        >
                                          <td className="px-4 py-1.5 font-mono text-white/50">
                                            {fm.source_table_ref ||
                                              m.source_table}
                                          </td>
                                          <td className="px-4 py-1.5 font-mono text-white/80">
                                            {fm.source_column}
                                          </td>
                                          <td className="px-4 py-1.5 text-cyan-300/70">
                                            {fm.source_type}
                                          </td>
                                          <td className="px-4 py-1.5 font-mono text-brand-300/80">
                                            <div className="flex items-center gap-1.5">
                                              {fm.target_field}
                                              {isDuplicateTarget && (
                                                <span
                                                  title="This target field is mapped from more than one source column — the last one loaded wins."
                                                  className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 flex-shrink-0"
                                                >
                                                  Duplicate
                                                </span>
                                              )}
                                              {(fm.target_field_type ===
                                                "picklist" ||
                                                fm.target_field_type ===
                                                  "multipicklist") &&
                                                (fm.picklist_values?.length ??
                                                  0) > 0 && (
                                                  <span
                                                    title={`Picklist values: ${fm.picklist_values!.join(", ")}`}
                                                    className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 flex-shrink-0"
                                                  >
                                                    Picklist
                                                  </span>
                                                )}
                                              {fm.target_field_type ===
                                                "reference" &&
                                                (fm.reference_to?.length ?? 0) >
                                                  0 && (
                                                  <span
                                                    title={`Lookup → ${fm.reference_to!.join(", ")}`}
                                                    className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 flex-shrink-0"
                                                  >
                                                    Lookup
                                                  </span>
                                                )}
                                            </div>
                                          </td>
                                          <td className="px-4 py-1.5 text-purple-300/70">
                                            {fm.target_type}
                                          </td>
                                          <td className="px-4 py-1.5 text-white/40">
                                            {fm.transformation}
                                          </td>
                                          <td className="px-4 py-1.5 text-center">
                                            <StatusBadge status={fm.status} />
                                          </td>
                                        </tr>
                                      );
                                    });
                                  })()}
                                </tbody>
                              </table>
                            );
                            const isPoppedOut =
                              popoutTableKey === m.source_table;
                            return (
                              <>
                                <div className="overflow-auto max-h-[400px]">
                                  {m.field_mappings.length >
                                    LARGE_TABLE_POPOUT_THRESHOLD && (
                                    <div className="flex justify-end px-2 py-1.5 sticky top-0 z-20 bg-surface-500/90 backdrop-blur">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setPopoutTableKey(m.source_table)
                                        }
                                        className="flex items-center gap-1 text-[11px] text-white/40 hover:text-white transition"
                                      >
                                        <Maximize2 size={11} /> Expand to window
                                        ({m.field_mappings.length} fields)
                                      </button>
                                    </div>
                                  )}
                                  {fieldTable}
                                </div>
                                {isPoppedOut &&
                                  createPortal(
                                    <div
                                      className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-6"
                                      onClick={() => setPopoutTableKey(null)}
                                    >
                                      <div
                                        className="bg-surface-500 border border-white/10 rounded-xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
                                          <div className="text-sm font-semibold text-white truncate">
                                            {m.target_label || m.target_object}
                                            <span className="text-white/30 mx-1.5">
                                              ←
                                            </span>
                                            <span className="font-mono text-white/60">
                                              {m.source_table}
                                            </span>
                                            <span className="text-white/30 text-xs ml-2">
                                              ({m.field_mappings.length} fields)
                                            </span>
                                          </div>
                                          <button
                                            onClick={() =>
                                              setPopoutTableKey(null)
                                            }
                                            className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition flex-shrink-0"
                                          >
                                            <X size={16} />
                                          </button>
                                        </div>
                                        <div className="overflow-auto flex-1">
                                          {fieldTable}
                                        </div>
                                      </div>
                                    </div>,
                                    document.body,
                                  )}
                              </>
                            );
                          })()}
                      </div>
                    );
                  })}
                </div>
              )}

              {(() => {
                const unmapped = computeUnmappedTables(
                  schemaResult?.tables ?? [],
                  mappings,
                );
                return (
                  unmapped.length > 0 && (
                    <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl">
                      <button
                        type="button"
                        onClick={() => setUnmappedTablesOpen((o) => !o)}
                        className="w-full flex items-center gap-2 text-amber-400"
                      >
                        {unmappedTablesOpen ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )}
                        <AlertTriangle size={14} />
                        <p className="text-sm font-semibold">
                          {unmapped.length} unmapped table
                          {unmapped.length !== 1 ? "s" : ""}
                        </p>
                      </button>
                      {unmappedTablesOpen && (
                        <>
                          <p className="text-xs text-white/40 mt-2 mb-3">
                            These source tables have no mapping yet — removing a
                            mapping above (or deleting all of its fields) puts a
                            table back here. Click one to pre-select it for a
                            new pair below.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {unmapped.map((t) => (
                              <button
                                key={t.table_name}
                                onClick={() => {
                                  const groupKey =
                                    t.source_connection_id ?? "__primary__";
                                  setBulkSourceTablesByGroup((prev) => ({
                                    ...prev,
                                    [groupKey]: (prev[groupKey] ?? []).includes(
                                      t.table_name,
                                    )
                                      ? (prev[groupKey] ?? [])
                                      : [
                                          ...(prev[groupKey] ?? []),
                                          t.table_name,
                                        ],
                                  }));
                                }}
                                title="Add to the Source → Target Mapping Pairs picker below"
                                className="px-2.5 py-1 rounded-md text-xs border bg-amber-500/10 border-amber-500/30 text-amber-300 hover:bg-amber-500/20 transition font-mono"
                              >
                                {t.table_name}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )
                );
              })()}

              <button
                type="button"
                onClick={() => setMappingEntryChoice(null)}
                className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition"
              >
                <ArrowLeft size={12} /> Back to Mapping options
              </button>
            </div>
          )}

          {/* ── Step: STTM Export ──────────────────────────────────────────── */}
          {step === "sttm" && (
            <div className="space-y-4">
              <SectionCard title="STTM Workbook Generated">
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-white/50 mb-1.5">
                      Project Name
                    </label>
                    <input
                      type="text"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value)}
                      className="w-full px-3 py-2 bg-surface-400/50 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-brand-500/50 transition"
                    />
                  </div>
                </div>

                {sttmResult && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-green-400">
                    <CheckCircle2 size={14} className="flex-shrink-0" />
                    <span className="truncate">
                      Workbook ready — {sttmResult.filename}
                    </span>
                    <a
                      href={sttmResult.download_url}
                      download={sttmResult.filename}
                      className="text-brand-300 hover:text-brand-200 underline underline-offset-2 flex-shrink-0"
                    >
                      Download
                    </a>
                    <span className="text-white/25 flex-shrink-0">
                      (see Version History for prior downloads)
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-2 flex-wrap mt-3">
                  {!sttmResult && (
                    <button
                      onClick={() => handleGenerateSttm()}
                      disabled={generatingSttm || mappings.length === 0}
                      className="ztd-btn ztd-btn-primary"
                    >
                      {generatingSttm ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <FileSpreadsheet size={14} />
                      )}
                      Generate STTM
                    </button>
                  )}
                  <label
                    className={`ztd-btn ztd-btn-secondary cursor-pointer ${importingSttm ? "opacity-40 pointer-events-none" : ""}`}
                  >
                    {importingSttm ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Upload size={14} />
                    )}
                    Import Existing STTM
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";
                        if (f) handleImportSttm(f);
                      }}
                    />
                  </label>
                </div>

                {sttmImportInfo && (
                  <div className="mt-3 p-3 bg-brand-500/10 border border-brand-500/20 rounded-lg text-xs text-brand-200 flex items-start gap-2">
                    <CheckCircle2 size={13} className="mt-0.5 flex-shrink-0" />
                    <span>
                      Imported {sttmImportInfo.table_count} table
                      {sttmImportInfo.table_count !== 1 ? "s" : ""} /{" "}
                      {sttmImportInfo.total_mapping_rows} mapping row
                      {sttmImportInfo.total_mapping_rows !== 1
                        ? "s"
                        : ""} from {sttmImportInfo.filename}. Review below, then
                      click "Generate STTM" for a downloadable copy.
                    </span>
                  </div>
                )}
              </SectionCard>

              {editedMappings.length > 0 && (
                <SectionCard title="STTM Results — Edit & Review">
                  <div className="mb-3">
                    <p className="text-xs text-white/40 mb-3">
                      Edit target field names or remove rows not needed. Add a
                      row, then either search/select an existing source column,
                      or leave Source Column blank and put a hardcoded value or
                      logic based on other source fields in Transform. Click
                      "Apply & Regenerate" to update the STTM.
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => addManualFieldMapping(sttmActiveTab)}
                        className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                      >
                        <Plus size={12} />
                        Add Field
                      </button>
                      <label
                        className={`ztd-btn ztd-btn-secondary ztd-btn-sm cursor-pointer ${importingRules ? "opacity-40 pointer-events-none" : ""}`}
                      >
                        {importingRules ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <Upload size={12} />
                        )}
                        Import Transformation Rules
                        <input
                          type="file"
                          accept=".xlsx,.xls"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = "";
                            if (f) handleImportTransformRules(f);
                          }}
                        />
                      </label>
                      <label
                        className={`ztd-btn ztd-btn-secondary ztd-btn-sm cursor-pointer ${importingLegacyRules ? "opacity-40 pointer-events-none" : ""}`}
                      >
                        {importingLegacyRules ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <Upload size={12} />
                        )}
                        Import Legacy Code Rules
                        <input
                          type="file"
                          accept=".sql,.pls,.plb,.pks,.pkb,.prc,.trg,.dtsx,.xml,.atl,.json,.txt,.xlsx,.xls"
                          className="hidden"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = "";
                            if (f) handleImportLegacyRules(f);
                          }}
                        />
                      </label>
                      <button
                        onClick={loadLegacyRulesHistory}
                        className="ztd-btn ztd-btn-secondary ztd-btn-sm flex-shrink-0"
                      >
                        <History size={12} />
                        Rule Import Audit Trail
                      </button>
                      <button
                        onClick={() =>
                          setKeepFieldsOpen((v) => {
                            const next = !v;
                            const mapping = editedMappings[sttmActiveTab];
                            if (next && mapping) {
                              setKeepFieldsSelected((prev) => ({
                                ...prev,
                                [sttmActiveTab]: new Set(
                                  mapping.field_mappings.map(
                                    (fm) => fm.source_column,
                                  ),
                                ),
                              }));
                            }
                            return next;
                          })
                        }
                        className="ztd-btn ztd-btn-secondary ztd-btn-sm flex-shrink-0"
                      >
                        <Search size={12} />
                        Limit Fields
                      </button>
                      {(removedFieldMappings[sttmActiveTab]?.length ?? 0) >
                        0 && (
                        <button
                          onClick={() => setRemovedFieldsOpen((v) => !v)}
                          className="ztd-btn ztd-btn-secondary ztd-btn-sm flex-shrink-0"
                        >
                          <RefreshCw size={12} />
                          {removedFieldMappings[sttmActiveTab]?.length ??
                            0}{" "}
                          removed
                        </button>
                      )}
                      {sttmHistory.length > 0 && (
                        <button
                          onClick={() => setSttmHistoryOpen((v) => !v)}
                          className="ztd-btn ztd-btn-secondary ztd-btn-sm flex-shrink-0"
                        >
                          <History size={12} />
                          {sttmHistory.length} version
                          {sttmHistory.length !== 1 ? "s" : ""}
                        </button>
                      )}
                    </div>
                  </div>

                  {legacyRulesError && (
                    <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-300 flex items-start gap-2">
                      <AlertTriangle
                        size={13}
                        className="mt-0.5 flex-shrink-0"
                      />
                      <span>{legacyRulesError}</span>
                    </div>
                  )}

                  {legacyRulesHistoryOpen && (
                    <div className="mb-4 border border-white/10 rounded-lg overflow-hidden">
                      <div className="px-3 py-2 bg-white/3 text-xs font-medium text-white/60">
                        Legacy Rule Import Audit Trail
                      </div>
                      {legacyRulesHistory.length === 0 ? (
                        <div className="p-3 text-xs text-white/40">
                          No legacy rule imports yet.
                        </div>
                      ) : (
                        <div className="divide-y divide-white/5">
                          {legacyRulesHistory.map((b) => (
                            <button
                              key={b.id}
                              onClick={async () => {
                                const full =
                                  await apiv2.converterLegacyRuleBatch(b.id);
                                setLegacyRulesBatch(full);
                                setLegacyRulesPanelOpen(true);
                              }}
                              className="w-full text-left px-3 py-2 text-xs hover:bg-white/5 transition flex items-center justify-between gap-3"
                            >
                              <span className="text-white/70">
                                {b.source_filename}{" "}
                                <span className="text-white/30">
                                  ({b.artifact_type})
                                </span>
                              </span>
                              <span className="text-white/40 flex-shrink-0">
                                {b.matched_count}/{b.item_count} matched ·{" "}
                                {new Date(b.created_at).toLocaleString()}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {legacyRulesPanelOpen && legacyRulesBatch && (
                    <div className="mb-4 border border-white/10 rounded-lg overflow-hidden">
                      <div className="px-3 py-2 bg-white/3 flex items-center justify-between gap-3">
                        <div className="text-xs">
                          <span className="font-medium text-white/70">
                            Legacy Rule Review
                          </span>{" "}
                          <span className="text-white/40">
                            — {legacyRulesBatch.source_filename} (
                            {legacyRulesBatch.artifact_type}) ·{" "}
                            {legacyRulesBatch.matched_count}/
                            {legacyRulesBatch.item_count} auto-matched ·{" "}
                            {legacyRulesApprovedCount} approved
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {legacyRulesPendingMatchedCount > 0 && (
                            <button
                              onClick={handleApproveAllMatchedLegacyItems}
                              disabled={applyingLegacyBatch}
                              className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                              title="Approve every auto-matched item that's still pending (unmatched items still need a target picked first)"
                            >
                              <CheckCheck size={12} />
                              Approve All Matched (
                              {legacyRulesPendingMatchedCount})
                            </button>
                          )}
                          <button
                            onClick={handleApplyLegacyBatch}
                            disabled={
                              applyingLegacyBatch ||
                              legacyRulesApprovedCount === 0
                            }
                            className="ztd-btn ztd-btn-primary ztd-btn-sm"
                            title={
                              legacyRulesApprovedCount === 0
                                ? "Approve (or edit) at least one rule below before applying"
                                : undefined
                            }
                          >
                            {applyingLegacyBatch ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <CheckCircle2 size={12} />
                            )}
                            Apply Approved Rules
                          </button>
                          <button
                            onClick={() => setLegacyRulesPanelOpen(false)}
                            className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      </div>
                      <div className="max-h-96 overflow-y-auto divide-y divide-white/5">
                        {legacyRulesBatch.items?.map((item) => {
                          // Default the pickers to the current auto-match (if any)
                          // so a reviewer can see and override it — this is what
                          // makes re-selecting table/field work after a Revert,
                          // since match_status stays "matched" across a revert.
                          const pickedValue =
                            legacyItemTargetPick[item.id] ??
                            (item.matched_target_table &&
                            item.matched_target_field
                              ? `${item.matched_target_table}::${item.matched_target_field}`
                              : "");
                          const [pickedTable, pickedField] = pickedValue
                            ? pickedValue.split("::")
                            : ["", ""];
                          const canApprove =
                            item.match_status === "matched" || !!pickedValue;
                          const displayTable =
                            pickedTable ||
                            item.matched_target_table ||
                            item.target_table ||
                            "—";
                          const displayField =
                            pickedField ||
                            item.matched_target_field ||
                            item.target_column ||
                            "—";
                          const isExpanded = expandedLegacyItemId === item.id;
                          const effectiveRule =
                            (item.review_status === "edited" &&
                              item.final_transformation) ||
                            item.expression;
                          return (
                            <div
                              key={item.id}
                              className="p-3 text-xs space-y-1.5"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="text-white/80 font-medium truncate">
                                    <span className="text-white/40">
                                      Table:
                                    </span>{" "}
                                    {displayTable}
                                    <span className="text-white/20 mx-1.5">
                                      |
                                    </span>
                                    <span className="text-white/40">
                                      Field:
                                    </span>{" "}
                                    {displayField}
                                  </div>
                                  <div className="text-white/30 truncate">
                                    {item.source_location}
                                  </div>
                                </div>
                                <span
                                  className={`flex-shrink-0 px-2 py-0.5 rounded-full ${
                                    item.match_status === "matched"
                                      ? "bg-green-500/10 text-green-400"
                                      : "bg-amber-500/10 text-amber-400"
                                  }`}
                                >
                                  {item.match_status === "matched"
                                    ? "auto-matched"
                                    : "unmatched"}
                                </span>
                              </div>
                              <div className="text-white/70 font-mono break-all bg-black/20 rounded p-2">
                                <span className="text-white/40 font-sans not-italic mr-1">
                                  Rule to apply:
                                </span>
                                {effectiveRule || (
                                  <span className="text-white/30 font-sans italic">
                                    direct copy — no transformation
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() =>
                                  setExpandedLegacyItemId(
                                    isExpanded ? null : item.id,
                                  )
                                }
                                className="text-white/40 hover:text-white/70 underline underline-offset-2"
                              >
                                {isExpanded ? "Hide Source" : "View Source"}
                              </button>
                              {isExpanded && (
                                <div className="text-white/70 font-mono break-all bg-black/20 rounded p-2">
                                  {item.raw_snippet || item.expression}
                                </div>
                              )}
                              {editingLegacyItemId === item.id ? (
                                <div className="flex items-center gap-2">
                                  <input
                                    value={editingLegacyItemValue}
                                    onChange={(e) =>
                                      setEditingLegacyItemValue(e.target.value)
                                    }
                                    className="flex-1 px-2 py-1 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono focus:outline-none focus:border-brand-500/50"
                                  />
                                  <button
                                    onClick={() =>
                                      handleReviewLegacyItem(
                                        item,
                                        "edited",
                                        editingLegacyItemValue,
                                      )
                                    }
                                    className="ztd-btn ztd-btn-primary ztd-btn-sm"
                                  >
                                    Save
                                  </button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span
                                    className={`px-2 py-0.5 rounded-full ${
                                      item.review_status === "approved" ||
                                      item.review_status === "edited"
                                        ? "bg-green-500/10 text-green-400"
                                        : item.review_status === "rejected"
                                          ? "bg-red-500/10 text-red-400"
                                          : "bg-white/5 text-white/40"
                                    }`}
                                  >
                                    {item.review_status}
                                  </span>
                                  {item.review_status === "pending" ? (
                                    <>
                                      <select
                                        value={pickedTable}
                                        onChange={(e) =>
                                          setLegacyItemTargetPick((prev) => ({
                                            ...prev,
                                            [item.id]: e.target.value
                                              ? `${e.target.value}::`
                                              : "",
                                          }))
                                        }
                                        className="px-2 py-1 bg-surface-400/50 border border-white/10 rounded text-xs text-white focus:outline-none focus:border-brand-500/50"
                                      >
                                        <option value="">
                                          Assign target table…
                                        </option>
                                        {legacyTargetTableOptions.map((opt) => (
                                          <option
                                            key={opt.value}
                                            value={opt.value}
                                          >
                                            {opt.label}
                                          </option>
                                        ))}
                                      </select>
                                      <select
                                        value={pickedField}
                                        disabled={!pickedTable}
                                        onChange={(e) =>
                                          setLegacyItemTargetPick((prev) => ({
                                            ...prev,
                                            [item.id]: `${pickedTable}::${e.target.value}`,
                                          }))
                                        }
                                        className="px-2 py-1 bg-surface-400/50 border border-white/10 rounded text-xs text-white focus:outline-none focus:border-brand-500/50 disabled:opacity-40"
                                      >
                                        <option value="">
                                          Assign target field…
                                        </option>
                                        {legacyTargetFieldOptionsForTable(
                                          pickedTable,
                                        ).map((opt) => (
                                          <option
                                            key={opt.value}
                                            value={opt.value}
                                          >
                                            {opt.label}
                                          </option>
                                        ))}
                                      </select>
                                      <button
                                        onClick={() =>
                                          handleReviewLegacyItem(
                                            item,
                                            "approved",
                                          )
                                        }
                                        disabled={!canApprove}
                                        className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                                        title={
                                          !canApprove
                                            ? "Assign a target field before approving"
                                            : undefined
                                        }
                                      >
                                        Approve
                                      </button>
                                      <button
                                        onClick={() => {
                                          setEditingLegacyItemId(item.id);
                                          setEditingLegacyItemValue(
                                            item.final_transformation ||
                                              item.expression,
                                          );
                                        }}
                                        className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleReviewLegacyItem(
                                            item,
                                            "rejected",
                                          )
                                        }
                                        className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                                      >
                                        Reject
                                      </button>
                                    </>
                                  ) : (
                                    <button
                                      onClick={() =>
                                        handleReviewLegacyItem(
                                          item,
                                          "pending",
                                          item.final_transformation ??
                                            undefined,
                                        )
                                      }
                                      className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                                      title="Send this rule back to pending so it can be re-reviewed"
                                    >
                                      Revert
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {keepFieldsOpen && editedMappings[sttmActiveTab] && (
                    <div className="mb-4 p-3 border border-white/10 rounded-lg bg-white/3 space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs text-white/50">
                          Pick the source columns to keep for{" "}
                          <span className="text-brand-300">
                            {editedMappings[sttmActiveTab].source_table}
                          </span>{" "}
                          — unchecked columns are removed from this tab.
                        </p>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => setKeepFieldsPasteOpen((v) => !v)}
                            className="text-[11px] text-white/50 hover:text-white/80 underline underline-offset-2"
                          >
                            {keepFieldsPasteOpen
                              ? "Use checkboxes"
                              : "Paste field names"}
                          </button>
                          <button
                            onClick={() =>
                              setAllKeepFields(sttmActiveTab, true)
                            }
                            className="text-[11px] text-white/50 hover:text-white/80 underline underline-offset-2"
                          >
                            Select all
                          </button>
                          <button
                            onClick={() =>
                              setAllKeepFields(sttmActiveTab, false)
                            }
                            className="text-[11px] text-white/50 hover:text-white/80 underline underline-offset-2"
                          >
                            Deselect all
                          </button>
                        </div>
                      </div>
                      {keepFieldsPasteOpen && (
                        <div className="space-y-1.5">
                          <textarea
                            value={keepFieldsPasteText}
                            onChange={(e) =>
                              setKeepFieldsPasteText(e.target.value)
                            }
                            placeholder="Paste column names to keep — one per line, or comma/tab-separated"
                            rows={4}
                            className="w-full px-3 py-2 rounded-lg bg-surface-400/60 border border-white/10 text-xs font-mono text-white focus:outline-none focus:border-brand-500/60"
                          />
                          <button
                            onClick={() => {
                              applyPastedKeepFields(
                                sttmActiveTab,
                                keepFieldsPasteText,
                              );
                              setKeepFieldsPasteText("");
                            }}
                            className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                          >
                            Check Pasted Names
                          </button>
                        </div>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 max-h-56 overflow-y-auto pr-1">
                        {editedMappings[sttmActiveTab].field_mappings.map(
                          (fm) => {
                            const key = fm.source_column.toUpperCase();
                            const checked =
                              getKeepFieldsSelection(sttmActiveTab).has(key);
                            return (
                              <label
                                key={key}
                                className="flex items-center gap-2 text-xs text-white/70 cursor-pointer select-none"
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() =>
                                    toggleKeepField(
                                      sttmActiveTab,
                                      fm.source_column,
                                    )
                                  }
                                  className="accent-brand-400"
                                />
                                <span className="font-mono truncate">
                                  {fm.source_column}
                                </span>
                              </label>
                            );
                          },
                        )}
                      </div>
                      <button
                        onClick={() =>
                          handleKeepOnlyFields(
                            sttmActiveTab,
                            getKeepFieldsSelection(sttmActiveTab),
                          )
                        }
                        className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                      >
                        <X size={12} /> Keep Selected Fields
                      </button>
                    </div>
                  )}
                  {removedFieldsOpen &&
                    (removedFieldMappings[sttmActiveTab]?.length ?? 0) > 0 && (
                      <div className="mb-4 border border-white/10 rounded-lg overflow-hidden">
                        <div className="px-3 py-2 bg-white/3 text-xs font-medium text-white/50">
                          Removed fields — click Add Back to restore a row
                        </div>
                        <div className="divide-y divide-white/5 max-h-48 overflow-y-auto">
                          {removedFieldMappings[sttmActiveTab]?.map(
                            (fm, idx) => (
                              <div
                                key={`${fm.source_column}-${idx}`}
                                className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-white/3"
                              >
                                <div className="flex items-center gap-2 min-w-0 text-xs">
                                  <span className="font-mono text-white/70">
                                    {fm.source_column}
                                  </span>
                                  <ArrowRight
                                    size={11}
                                    className="text-white/20"
                                  />
                                  <span className="font-mono text-brand-300/80 truncate">
                                    {fm.target_field || "(unmapped)"}
                                  </span>
                                </div>
                                <button
                                  onClick={() =>
                                    handleRestoreFieldMapping(
                                      sttmActiveTab,
                                      idx,
                                    )
                                  }
                                  className="ztd-btn ztd-btn-primary ztd-btn-sm flex-shrink-0"
                                >
                                  <Plus size={11} /> Add Back
                                </button>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    )}
                  <p className="text-[11px] text-white/30 -mt-2 mb-3">
                    Expects columns Table, Column (comma-separated for multiple
                    fields), Dimension, and Description. Only Accuracy/Validity
                    rows are applied — the Description becomes the Transform
                    rule for each matching mapped field.
                  </p>
                  {importRulesResult && (
                    <div
                      className={`mb-3 p-3 rounded-lg border text-xs space-y-1 ${
                        importRulesResult.unmatched.length > 0
                          ? "bg-amber-500/10 border-amber-500/20 text-amber-200"
                          : "bg-green-500/10 border-green-500/20 text-green-300"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 font-medium">
                          {importRulesResult.unmatched.length > 0 ? (
                            <AlertTriangle size={13} />
                          ) : (
                            <CheckCircle2 size={13} />
                          )}
                          Applied {importRulesResult.rules_applied}{" "}
                          transformation rule
                          {importRulesResult.rules_applied !== 1 ? "s" : ""}
                          {importRulesResult.unmatched.length > 0 &&
                            ` — ${importRulesResult.unmatched.length} row${importRulesResult.unmatched.length !== 1 ? "s" : ""} unmatched`}
                        </div>
                        <button
                          onClick={() => setImportRulesResult(null)}
                          className="flex-shrink-0 text-white/40 hover:text-white/70"
                          title="Clear this result"
                        >
                          <X size={13} />
                        </button>
                      </div>
                      {importRulesResult.applied.length > 0 && (
                        <div className="space-y-2 pt-1">
                          {importRulesResult.applied.map((a, i) => {
                            const key = `${a.table}::${a.column}`;
                            if (dqRuleRemoved.has(key)) return null;
                            const decision = dqRuleDecisions[key] ?? {
                              action: "include" as const,
                              logic: a.transformation,
                            };
                            return (
                              <div
                                key={i}
                                className="rounded-md border border-white/10 bg-black/20 p-2 space-y-1.5"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="text-white/70">
                                    <span className="font-mono text-white/90">
                                      {a.table}.{a.column}
                                    </span>{" "}
                                    <span className="text-white/40">
                                      ({a.dimension})
                                    </span>{" "}
                                    — {a.transformation}
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setDqRuleRemoved((prev) =>
                                        new Set(prev).add(key),
                                      )
                                    }
                                    className="flex-shrink-0 text-white/30 hover:text-red-300"
                                    title="Delete this rule from the batch"
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="flex rounded-md overflow-hidden border border-white/15">
                                    {(["exclude", "include"] as const).map(
                                      (opt) => (
                                        <button
                                          key={opt}
                                          type="button"
                                          onClick={() =>
                                            setDqRuleDecisions((prev) => ({
                                              ...prev,
                                              [key]: {
                                                action: opt,
                                                logic:
                                                  prev[key]?.logic ??
                                                  decision.logic,
                                              },
                                            }))
                                          }
                                          className={`px-2 py-1 text-[11px] font-medium ${
                                            decision.action === opt
                                              ? opt === "exclude"
                                                ? "bg-red-500/30 text-red-200"
                                                : "bg-emerald-500/30 text-emerald-200"
                                              : "bg-transparent text-white/50 hover:text-white/80"
                                          }`}
                                        >
                                          {opt === "exclude"
                                            ? "Exclude + DQ report"
                                            : "Include in load"}
                                        </button>
                                      ),
                                    )}
                                  </div>
                                  <input
                                    type="text"
                                    value={decision.logic}
                                    onChange={(e) =>
                                      setDqRuleDecisions((prev) => ({
                                        ...prev,
                                        [key]: {
                                          action: decision.action,
                                          logic: e.target.value,
                                        },
                                      }))
                                    }
                                    placeholder={
                                      decision.action === "exclude"
                                        ? `Rule condition, e.g. ${a.column} > today()`
                                        : "Transform logic, e.g. UPPER(FIRST_NAME)"
                                    }
                                    className="flex-1 bg-black/30 border border-white/15 rounded px-2 py-1 text-[11px] text-white/90 placeholder:text-white/25 focus:outline-none focus:border-white/30"
                                  />
                                </div>
                              </div>
                            );
                          })}
                          {dqRuleRemoved.size > 0 && (
                            <div className="border border-white/10 rounded-lg overflow-hidden">
                              <div className="px-3 py-2 bg-white/3 text-[11px] font-medium text-white/50">
                                Deleted rules — click Add Back to restore a rule
                              </div>
                              <div className="divide-y divide-white/5 max-h-40 overflow-y-auto">
                                {importRulesResult.applied
                                  .filter((a) =>
                                    dqRuleRemoved.has(
                                      `${a.table}::${a.column}`,
                                    ),
                                  )
                                  .map((a, i) => {
                                    const key = `${a.table}::${a.column}`;
                                    return (
                                      <div
                                        key={key + i}
                                        className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-white/3"
                                      >
                                        <div className="text-[11px] text-white/40 font-mono truncate">
                                          {a.table}.{a.column}
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setDqRuleRemoved((prev) => {
                                              const next = new Set(prev);
                                              next.delete(key);
                                              return next;
                                            })
                                          }
                                          className="ztd-btn ztd-btn-primary ztd-btn-sm flex-shrink-0"
                                        >
                                          <Plus size={11} /> Add Back
                                        </button>
                                      </div>
                                    );
                                  })}
                              </div>
                            </div>
                          )}
                          <div className="flex items-center gap-2 pt-1">
                            <button
                              type="button"
                              onClick={handleApplyDqDecisions}
                              disabled={
                                applyingDqDecisions ||
                                dqRuleRemoved.size ===
                                  importRulesResult.applied.length
                              }
                              className="px-3 py-1.5 text-[11px] font-medium rounded-md bg-purple-500/30 text-purple-200 hover:bg-purple-500/40 disabled:opacity-50"
                            >
                              {applyingDqDecisions
                                ? "Applying…"
                                : dqRuleRemoved.size > 0
                                  ? `Apply DQ Decisions (${importRulesResult.applied.length - dqRuleRemoved.size})`
                                  : "Apply DQ Decisions"}
                            </button>
                            {dqDecisionsApplied !== null && (
                              <span className="text-white/50">
                                Applied to {dqDecisionsApplied} field
                                {dqDecisionsApplied !== 1 ? "s" : ""} — will
                                take effect on the next Load Data run.
                              </span>
                            )}
                          </div>
                          {dqDecisionsNotFound.length > 0 && (
                            <div className="text-amber-300/80 pt-1">
                              {dqDecisionsNotFound.length} decision
                              {dqDecisionsNotFound.length !== 1 ? "s" : ""} did
                              not match a current mapping (edited or removed
                              since import) and were not applied:{" "}
                              {dqDecisionsNotFound
                                .map((n) => `${n.table}.${n.column}`)
                                .join(", ")}
                            </div>
                          )}
                        </div>
                      )}
                      {importRulesResult.unmatched.length > 0 && (
                        <ul className="pl-5 list-disc text-white/50">
                          {importRulesResult.unmatched.map((u, i) => (
                            <li key={i}>
                              {u.table}.{u.column} ({u.dimension}) — no mapped
                              field matched
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  {/* Version history panel */}
                  {sttmHistoryOpen && sttmHistory.length > 0 && (
                    <div className="mb-4 border border-white/10 rounded-lg overflow-hidden">
                      <div className="px-3 py-2 bg-white/3 text-xs font-medium text-white/50 flex items-center gap-2">
                        <History size={12} /> Version History — click Restore to
                        revert
                      </div>
                      <div className="divide-y divide-white/5 max-h-48 overflow-y-auto">
                        {sttmHistory.map((snap, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-white/3"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="text-xs font-mono text-white/25 flex-shrink-0">
                                v{snap.version}
                              </span>
                              <span className="text-xs text-white/60 truncate">
                                {snap.label}
                              </span>
                              <span className="text-xs text-white/25 flex-shrink-0">
                                {snap.timestamp}
                              </span>
                              <span className="text-xs text-white/25 flex-shrink-0">
                                {snap.mappings.reduce(
                                  (s, m) => s + m.field_mappings.length,
                                  0,
                                )}{" "}
                                fields
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {snap.sttmResult && (
                                <a
                                  href={snap.sttmResult.download_url}
                                  download={snap.sttmResult.filename}
                                  className="px-2.5 py-1 rounded bg-white/5 hover:bg-white/10 text-xs text-white/60 hover:text-white transition flex items-center gap-1"
                                >
                                  <Download size={11} /> Download
                                </a>
                              )}
                              <button
                                onClick={() => restoreSttmVersion(idx)}
                                className="ztd-btn ztd-btn-primary ztd-btn-sm"
                              >
                                Restore
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Tab bar */}
                  {(() => {
                    const tabs = sttmTabsToShow(
                      editedMappings,
                      removedTableMappings,
                      sttmActiveTab,
                      sttmTabFilter,
                    );
                    const filtering = tabs.matching !== tabs.total;
                    return (
                      <>
                        {tabs.filterable && (
                          <div className="flex items-center gap-2 mb-2">
                            <div className="relative flex-1 max-w-xs">
                              <Search
                                size={12}
                                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none"
                              />
                              <input
                                value={sttmTabFilter}
                                onChange={(e) =>
                                  setSttmTabFilter(e.target.value)
                                }
                                placeholder="Filter tables by name…"
                                aria-label="Filter STTM tables"
                                className="w-full pl-7 pr-2 py-1 bg-surface-400/50 border border-white/10 rounded-lg text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-brand-500/50 transition"
                              />
                            </div>
                            <span className="text-[11px] text-white/35">
                              {filtering
                                ? `${tabs.matching} of ${tabs.total} tables`
                                : `${tabs.total} tables`}
                            </span>
                          </div>
                        )}
                        <div
                          ref={sttmTabBarRef}
                          className={`relative flex gap-1 flex-wrap mb-3 border-b border-white/8 pb-0 ${
                            tabs.filterable ? "max-h-32 overflow-y-auto" : ""
                          }`}
                        >
                          {tabs.visible.map((mi) => {
                            const m = editedMappings[mi];
                            return (
                              <button
                                key={mi}
                                data-sttm-active-tab={
                                  sttmActiveTab === mi ? "true" : undefined
                                }
                                onClick={() => setSttmActiveTab(mi)}
                                className={`group flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-t-lg border-b-2 transition-all whitespace-nowrap ${
                                  sttmActiveTab === mi
                                    ? "border-brand-400 text-white bg-white/5 font-medium"
                                    : "border-transparent text-white/40 hover:text-white/70 hover:bg-white/3"
                                }`}
                              >
                                {m.target_label || m.target_object}
                                <span className="text-white/25">
                                  {m.field_mappings.length}
                                </span>
                                <span
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeTableMapping(mi);
                                  }}
                                  title="Remove this table's mapping"
                                  className="text-white/20 group-hover:text-red-400 hover:!text-red-300 transition"
                                >
                                  <X size={11} />
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    );
                  })()}
                  {/* Active tab content */}
                  {editedMappings[sttmActiveTab] &&
                    !removedTableMappings.has(sttmActiveTab) &&
                    (() => {
                      const m = editedMappings[sttmActiveTab];
                      const mi = sttmActiveTab;
                      const isComplex = (m.source_tables?.length ?? 0) > 1;
                      // Identity/as-is mappings (see buildIdentityMapping)
                      // have no real target object to "Change Target"
                      // against — every field carries its distinguishing
                      // notes string through unchanged, so this is a cheap,
                      // reliable way to detect them without depending on
                      // mappingPairs still holding a matching entry (joins/
                      // removals can make the two arrays diverge).
                      const isAsIsMapping =
                        !isComplex &&
                        m.field_mappings.length > 0 &&
                        m.field_mappings.every((fm) =>
                          fm.notes.startsWith("Loaded as-is"),
                        );
                      return (
                        <div className="border border-white/8 rounded-lg overflow-hidden">
                          <div className="flex items-center gap-3 flex-wrap px-4 py-2.5 bg-white/3">
                            <div className="flex flex-col leading-tight">
                              <span className="text-[9px] uppercase tracking-wide text-white/30">
                                Target Object
                              </span>
                              <span className="text-sm font-semibold text-brand-300">
                                {m.target_label || m.target_object}
                                {m.target_label &&
                                  m.target_label !== m.target_object && (
                                    <span className="ml-1.5 text-[10px] font-mono font-normal text-white/30">
                                      ({m.target_object})
                                    </span>
                                  )}
                              </span>
                            </div>
                            <ArrowLeft size={12} className="text-white/20" />
                            <div className="flex items-center gap-1 flex-wrap">
                              {(m.source_tables?.length
                                ? m.source_tables
                                : [m.source_table]
                              ).map((t, ti) => (
                                <span
                                  key={t}
                                  className="inline-flex items-center gap-1 text-sm text-white/50 font-mono"
                                >
                                  {ti > 0 && (
                                    <span className="text-blue-400/60">⋈</span>
                                  )}
                                  {t}
                                  {isComplex && (
                                    <button
                                      onClick={() =>
                                        ungroupTableFromMapping(mi, t)
                                      }
                                      className="text-white/20 hover:text-red-300"
                                      title={`Split ${t} out of this join into its own 1:1 mapping`}
                                    >
                                      <X size={11} />
                                    </button>
                                  )}
                                </span>
                              ))}
                            </div>
                            {isComplex && (
                              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-300 flex items-center gap-1">
                                <GitMerge size={10} /> Joined
                              </span>
                            )}
                            <span className="text-xs text-white/30">
                              {m.field_mappings.length} fields
                            </span>
                            {/* Grouped together (with ml-auto on the group,
                            not the last button) so they wrap onto a new
                            line as one unit instead of stranding just
                            "Download Table STTM" by itself. */}
                            <div className="flex items-center gap-2 flex-wrap ml-auto">
                              <button
                                onClick={() =>
                                  setJoinPickerMappingIdx(
                                    joinPickerMappingIdx === mi ? null : mi,
                                  )
                                }
                                className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                                title="Join another source table into this mapping"
                              >
                                <Plus size={11} /> Join Table
                              </button>
                              {isAsIsMapping && (
                                <button
                                  onClick={() => {
                                    const opening = renamingMappingIdx !== mi;
                                    setRenamingMappingIdx(opening ? mi : null);
                                    setRenameTargetInput(
                                      opening
                                        ? m.target_label || m.target_object
                                        : "",
                                    );
                                  }}
                                  className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                                  title="Rename this target table"
                                >
                                  <Pencil size={11} /> Rename
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  const opening = retargetMappingIdx !== mi;
                                  setRetargetMappingIdx(opening ? mi : null);
                                  setRetargetObjectApiName(
                                    opening ? m.target_object : "",
                                  );
                                }}
                                className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                                title="Remap this tab to a different target object"
                              >
                                <RefreshCw size={11} /> Change Target
                              </button>
                              <button
                                onClick={() => handleDownloadTableSttm(mi)}
                                disabled={downloadingTableIdx === mi}
                                className="ztd-btn ztd-btn-secondary ztd-btn-sm !px-2"
                                title={`Download STTM for ${m.source_table} only`}
                              >
                                {downloadingTableIdx === mi ? (
                                  <Loader2 size={12} className="animate-spin" />
                                ) : (
                                  <Download size={12} />
                                )}
                              </button>
                            </div>
                          </div>
                          {renamingMappingIdx === mi && (
                            <div className="px-4 py-2.5 bg-brand-500/5 border-t border-brand-500/10 flex items-center gap-2 flex-wrap text-xs">
                              <span className="text-white/40 whitespace-nowrap">
                                Rename target table to
                              </span>
                              <input
                                type="text"
                                value={renameTargetInput}
                                onChange={(e) =>
                                  setRenameTargetInput(e.target.value)
                                }
                                placeholder="Target table name"
                                className="w-64 px-2 py-1 bg-surface-400/60 border border-white/10 rounded text-white/70 focus:outline-none focus:border-brand-500/60"
                              />
                              <button
                                onClick={() => handleRenameIdentityTarget(mi)}
                                disabled={!renameTargetInput.trim()}
                                className="ztd-btn ztd-btn-primary ztd-btn-sm"
                              >
                                Rename
                              </button>
                              <button
                                onClick={() => {
                                  setRenamingMappingIdx(null);
                                  setRenameTargetInput("");
                                }}
                                className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                          {retargetMappingIdx === mi && (
                            <div className="px-4 py-2.5 bg-brand-500/5 border-t border-brand-500/10 flex items-center gap-2 flex-wrap text-xs">
                              <span className="text-white/40 whitespace-nowrap">
                                Remap to
                              </span>
                              <div className="w-64">
                                <FieldSearchSelect
                                  value={retargetObjectApiName}
                                  options={sfObjectPickerOptions}
                                  onChange={setRetargetObjectApiName}
                                  placeholder="Search target objects…"
                                  className="w-full px-2 py-1 bg-surface-400/60 border border-white/10 rounded text-white/70 focus:outline-none focus:border-brand-500/60"
                                />
                              </div>
                              <button
                                onClick={() => handleRetargetMapping(mi)}
                                disabled={
                                  retargeting ||
                                  !retargetObjectApiName ||
                                  retargetObjectApiName === m.target_object
                                }
                                className="ztd-btn ztd-btn-primary ztd-btn-sm"
                              >
                                {retargeting ? (
                                  <Loader2 size={11} className="animate-spin" />
                                ) : (
                                  "Remap"
                                )}
                              </button>
                              <button
                                onClick={() => setRetargetMappingIdx(null)}
                                className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                          {joinPickerMappingIdx === mi && (
                            <div className="px-4 py-2.5 bg-brand-500/5 border-t border-brand-500/10 flex items-center gap-2 flex-wrap text-xs">
                              <span className="text-white/40 whitespace-nowrap">
                                Join
                              </span>
                              <select
                                value={joinPickerTable}
                                onChange={(e) => {
                                  const table = e.target.value;
                                  setJoinPickerTable(table);
                                  setJoinPickerColumnPairs([
                                    { left: "", right: "" },
                                  ]);
                                  // Default to "include nothing" so the user
                                  // explicitly opts in to each column instead
                                  // of having to uncheck ones they don't want.
                                  setJoinPickerSelectedColumns([]);
                                }}
                                className="bg-surface-400/60 border border-white/10 rounded px-1.5 py-1 text-white/70 focus:outline-none focus:border-brand-500/60"
                              >
                                <option value="">Select table…</option>
                                {(schemaResult?.tables ?? [])
                                  .filter(
                                    (t) =>
                                      !(
                                        m.source_tables?.length
                                          ? m.source_tables
                                          : [m.source_table]
                                      ).includes(t.table_name),
                                  )
                                  .map((t) => (
                                    <option
                                      key={t.table_name}
                                      value={t.table_name}
                                    >
                                      {t.table_name}
                                      {(t.source_connection_id ?? null) !==
                                      (m.source_connection_id ?? null)
                                        ? " (different connection)"
                                        : ""}
                                    </option>
                                  ))}
                              </select>
                              {extraSourceConnections.length > 0 && (
                                <span
                                  className="text-white/30 italic"
                                  title="Picking a table marked '(different connection)' makes this a cross-connection join — each side is fetched separately and merged in process (DuckDB) instead of running as one SQL query, since no database engine can join across two different connections directly."
                                >
                                  (cross-connection joins supported)
                                </span>
                              )}
                              {joinPickerJoinType !== "cross" && (
                                <div className="w-full flex flex-col gap-1">
                                  {joinPickerColumnPairs.map((pair, pi) => (
                                    <div
                                      key={pi}
                                      className="flex items-center gap-1.5 flex-wrap"
                                    >
                                      {pi === 0 ? (
                                        <span className="text-white/40">
                                          on
                                        </span>
                                      ) : (
                                        <span className="text-white/40">
                                          and
                                        </span>
                                      )}
                                      <span className="font-mono text-white/60">
                                        {m.source_table}.
                                      </span>
                                      <select
                                        value={pair.left}
                                        onChange={(e) =>
                                          setJoinPickerColumnPairs((prev) =>
                                            prev.map((p, i) =>
                                              i === pi
                                                ? { ...p, left: e.target.value }
                                                : p,
                                            ),
                                          )
                                        }
                                        className="bg-surface-400/60 border border-white/10 rounded px-1.5 py-1 text-white/70 focus:outline-none focus:border-brand-500/60"
                                      >
                                        <option value="">column…</option>
                                        {(
                                          schemaResult?.tables.find(
                                            (t) =>
                                              t.table_name === m.source_table,
                                          )?.columns ?? []
                                        ).map((c) => (
                                          <option key={c.name} value={c.name}>
                                            {c.name}
                                          </option>
                                        ))}
                                      </select>
                                      <span className="text-white/40">=</span>
                                      <span className="font-mono text-white/60">
                                        {joinPickerTable || "…"}.
                                      </span>
                                      <select
                                        value={pair.right}
                                        onChange={(e) =>
                                          setJoinPickerColumnPairs((prev) =>
                                            prev.map((p, i) =>
                                              i === pi
                                                ? {
                                                    ...p,
                                                    right: e.target.value,
                                                  }
                                                : p,
                                            ),
                                          )
                                        }
                                        disabled={!joinPickerTable}
                                        className="bg-surface-400/60 border border-white/10 rounded px-1.5 py-1 text-white/70 focus:outline-none focus:border-brand-500/60 disabled:opacity-40"
                                      >
                                        <option value="">column…</option>
                                        {(
                                          schemaResult?.tables.find(
                                            (t) =>
                                              t.table_name === joinPickerTable,
                                          )?.columns ?? []
                                        ).map((c) => (
                                          <option key={c.name} value={c.name}>
                                            {c.name}
                                          </option>
                                        ))}
                                      </select>
                                      {joinPickerColumnPairs.length > 1 && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setJoinPickerColumnPairs((prev) =>
                                              prev.filter((_, i) => i !== pi),
                                            )
                                          }
                                          className="text-white/30 hover:text-white/60"
                                          title="Remove this column pair"
                                          aria-label="Remove this column pair"
                                        >
                                          <X size={12} />
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setJoinPickerColumnPairs((prev) => [
                                        ...prev,
                                        { left: "", right: "" },
                                      ])
                                    }
                                    disabled={!joinPickerTable}
                                    className="self-start text-[11px] text-brand-400 hover:text-brand-300 disabled:opacity-40"
                                    title="Add another column pair for a composite (multi-column) join key"
                                  >
                                    + AND column
                                  </button>
                                </div>
                              )}
                              {joinPickerJoinType === "cross" && (
                                <span className="text-white/30 italic">
                                  Cross join — no join column needed
                                </span>
                              )}
                              <select
                                value={joinPickerJoinType}
                                onChange={(e) => {
                                  const value = e.target.value as
                                    | "inner"
                                    | "left"
                                    | "right"
                                    | "full"
                                    | "cross";
                                  setJoinPickerJoinType(value);
                                  if (value === "cross") {
                                    setJoinPickerColumnPairs([
                                      { left: "", right: "" },
                                    ]);
                                  }
                                }}
                                className="bg-surface-400/60 border border-white/10 rounded px-1.5 py-1 uppercase tracking-wide text-[10px] text-white/70 focus:outline-none focus:border-brand-500/60"
                              >
                                <option value="inner">INNER</option>
                                <option value="left">LEFT</option>
                                <option value="right">RIGHT</option>
                                <option value="full">FULL OUTER</option>
                                <option value="cross">CROSS</option>
                              </select>
                              {joinPickerTable && (
                                <div className="w-full flex items-start gap-2 flex-wrap bg-surface-400/40 border border-white/10 rounded px-2 py-1.5">
                                  <span className="text-white/40 whitespace-nowrap py-0.5">
                                    Fields to include from {joinPickerTable}:
                                  </span>
                                  <div className="flex items-center gap-1.5 flex-wrap max-h-24 overflow-y-auto">
                                    {(
                                      schemaResult?.tables.find(
                                        (t) => t.table_name === joinPickerTable,
                                      )?.columns ?? []
                                    ).map((c) => {
                                      const checked =
                                        joinPickerSelectedColumns.includes(
                                          c.name,
                                        );
                                      return (
                                        <label
                                          key={c.name}
                                          className="flex items-center gap-1 text-white/60 cursor-pointer"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() =>
                                              setJoinPickerSelectedColumns(
                                                (prev) =>
                                                  checked
                                                    ? prev.filter(
                                                        (n) => n !== c.name,
                                                      )
                                                    : [...prev, c.name],
                                              )
                                            }
                                            className="accent-brand-500"
                                          />
                                          {c.name}
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              <button
                                onClick={() => handleAddTableToJoin(mi)}
                                disabled={
                                  joiningTable ||
                                  !joinPickerTable ||
                                  joinPickerSelectedColumns.length === 0 ||
                                  (joinPickerJoinType !== "cross" &&
                                    joinPickerColumnPairs.filter(
                                      (p) => p.left && p.right,
                                    ).length !== joinPickerColumnPairs.length)
                                }
                                className="ztd-btn ztd-btn-primary ztd-btn-sm"
                              >
                                {joiningTable ? (
                                  <Loader2 size={11} className="animate-spin" />
                                ) : (
                                  "Add Join"
                                )}
                              </button>
                              <button
                                onClick={() => setJoinPickerMappingIdx(null)}
                                className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                          {isComplex && (
                            <div className="px-4 py-2.5 bg-blue-500/5 border-t border-blue-500/10 space-y-1.5">
                              {(m.joins ?? []).map((j, ji) => (
                                <div
                                  key={ji}
                                  className="flex items-center gap-1.5 flex-wrap text-[11px] font-mono text-blue-300/80"
                                >
                                  <select
                                    value={j.join_type}
                                    onChange={(e) =>
                                      updateEditedJoinType(
                                        mi,
                                        ji,
                                        e.target.value as
                                          | "inner"
                                          | "left"
                                          | "right"
                                          | "full"
                                          | "cross",
                                      )
                                    }
                                    className="bg-surface-400/60 border border-white/10 rounded px-1 py-0.5 text-[10px] uppercase tracking-wide text-blue-300 focus:outline-none focus:border-brand-500/60"
                                  >
                                    <option value="inner">INNER JOIN</option>
                                    <option value="left">LEFT JOIN</option>
                                    <option value="right">RIGHT JOIN</option>
                                    <option value="full">
                                      FULL OUTER JOIN
                                    </option>
                                    <option value="cross">CROSS JOIN</option>
                                  </select>
                                  <select
                                    value={j.left_table}
                                    onChange={(e) =>
                                      updateEditedJoinTable(
                                        mi,
                                        ji,
                                        "left",
                                        e.target.value,
                                      )
                                    }
                                    className="bg-surface-400/60 border border-white/10 rounded px-1 py-0.5 text-blue-300 focus:outline-none focus:border-brand-500/60"
                                  >
                                    {(m.source_tables ?? []).map((t) => (
                                      <option key={t} value={t}>
                                        {t}
                                      </option>
                                    ))}
                                  </select>
                                  <span>.</span>
                                  <input
                                    value={j.left_column}
                                    onChange={(e) =>
                                      updateEditedJoinColumn(
                                        mi,
                                        ji,
                                        "left",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="col or col_a,col_b"
                                    title="Comma-separate multiple columns for a composite (multi-column) join key"
                                    className="w-32 bg-surface-400/60 border border-white/10 rounded px-1 py-0.5 text-blue-200 focus:outline-none focus:border-brand-500/60"
                                  />
                                  <span>=</span>
                                  <select
                                    value={j.right_table}
                                    onChange={(e) =>
                                      updateEditedJoinTable(
                                        mi,
                                        ji,
                                        "right",
                                        e.target.value,
                                      )
                                    }
                                    className="bg-surface-400/60 border border-white/10 rounded px-1 py-0.5 text-blue-300 focus:outline-none focus:border-brand-500/60"
                                  >
                                    {(m.source_tables ?? []).map((t) => (
                                      <option key={t} value={t}>
                                        {t}
                                      </option>
                                    ))}
                                  </select>
                                  <span>.</span>
                                  <input
                                    value={j.right_column}
                                    onChange={(e) =>
                                      updateEditedJoinColumn(
                                        mi,
                                        ji,
                                        "right",
                                        e.target.value,
                                      )
                                    }
                                    placeholder="col or col_a,col_b"
                                    title="Comma-separate multiple columns for a composite (multi-column) join key"
                                    className="w-32 bg-surface-400/60 border border-white/10 rounded px-1 py-0.5 text-blue-200 focus:outline-none focus:border-brand-500/60"
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                          {(removedJoinTables[mi]?.length ?? 0) > 0 && (
                            <div className="px-4 py-2 bg-amber-500/5 border-t border-amber-500/10 flex items-center gap-2 flex-wrap text-xs">
                              <span className="text-white/40 whitespace-nowrap">
                                Removed from this join:
                              </span>
                              {removedJoinTables[mi]!.map((r, ri) => (
                                <span
                                  key={`${r.table}-${ri}`}
                                  className="inline-flex items-center gap-1.5 pl-2 pr-1 py-0.5 rounded-full bg-white/5 border border-white/10 font-mono text-white/60"
                                >
                                  {r.table}
                                  <button
                                    onClick={() =>
                                      restoreUngroupedTable(mi, ri)
                                    }
                                    className="ztd-btn ztd-btn-primary ztd-btn-sm"
                                    title={`Add ${r.table} back into this join`}
                                  >
                                    <Plus size={10} /> Add Back
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="px-4 py-2.5 border-t border-white/8 flex items-center gap-2">
                            <span className="text-[11px] text-white/40 whitespace-nowrap">
                              Filter (WHERE)
                            </span>
                            <div className="w-72">
                              <ExpandableTextField
                                value={m.where_clause ?? ""}
                                onChange={(v) => updateEditedWhereClause(mi, v)}
                                title={`Filter — ${m.target_label || m.target_object}`}
                                placeholder="No filter — loads all rows"
                                hint={
                                  isComplex
                                    ? 'Optional SQL WHERE-clause fragment (no leading "WHERE"), ' +
                                      "applied across this join's full FROM clause at Load to " +
                                      "Target — only rows matching this condition are loaded. " +
                                      "Conditions may reference any of the joined tables " +
                                      `(${(m.source_tables ?? []).join(", ")}), qualified as ` +
                                      "table.column when a column name isn't unique across them, " +
                                      "e.g. PROJECT.STATUS = 'ACTIVE' AND EXPENSE.AMOUNT > 1000."
                                    : 'Optional SQL WHERE-clause fragment (no leading "WHERE"), ' +
                                      "applied to this table's extract at Load to Target — only " +
                                      "rows matching this condition are loaded, e.g. STATUS = 'ACTIVE' " +
                                      "AND REGION IN ('US','CA')."
                                }
                              />
                            </div>
                          </div>

                          {/* Aggregate (GROUP BY) rollup — for a Gold-layer
                              fact table built from a Silver/Bronze source
                              instead of a flat per-row copy. Every field is
                              either a group-by key or carries its own
                              aggregate function; a field left as neither is
                              dropped from the load entirely (mirrors
                              agents/data_converter/patterns.py's SELECT-
                              building rule). */}
                          <div className="px-4 py-2.5 border-t border-white/8">
                            <label className="flex items-center gap-2 text-[11px] text-white/60">
                              <input
                                type="checkbox"
                                checked={isAggregateModeEnabled(m, mi)}
                                onChange={(e) =>
                                  toggleMappingAggregateMode(
                                    mi,
                                    e.target.checked,
                                  )
                                }
                              />
                              Aggregate rollup (GROUP BY) — build a fact table
                              instead of a row-for-row copy
                            </label>
                            {isAggregateModeEnabled(m, mi) && (
                              <div className="mt-2 space-y-1.5 rounded-lg border border-white/10 bg-surface-400/30 p-3">
                                {m.field_mappings.map((fm, fi) => {
                                  const isGrouped = (
                                    m.group_by_columns ?? []
                                  ).includes(fm.target_field);
                                  return (
                                    <div
                                      key={`${fm.target_field}-${fi}`}
                                      className="flex items-center gap-3 text-xs"
                                    >
                                      <span className="w-40 truncate text-white/70">
                                        {fm.target_field}
                                      </span>
                                      <label className="flex items-center gap-1.5 text-white/50">
                                        <input
                                          type="checkbox"
                                          checked={isGrouped}
                                          onChange={() =>
                                            toggleGroupByColumn(
                                              mi,
                                              fm.target_field,
                                            )
                                          }
                                        />
                                        Group by
                                      </label>
                                      <select
                                        disabled={isGrouped}
                                        value={fm.aggregate_function ?? ""}
                                        onChange={(e) =>
                                          updateFieldAggregateFunction(
                                            mi,
                                            fi,
                                            (e.target.value || null) as
                                              | "sum"
                                              | "count"
                                              | "avg"
                                              | "min"
                                              | "max"
                                              | null,
                                          )
                                        }
                                        className="px-2 py-1 bg-surface-400/50 border border-white/10 rounded text-white/80 disabled:opacity-30 disabled:cursor-not-allowed"
                                      >
                                        <option value="">
                                          (exclude from rollup)
                                        </option>
                                        <option value="sum">SUM</option>
                                        <option value="count">COUNT</option>
                                        <option value="avg">AVG</option>
                                        <option value="min">MIN</option>
                                        <option value="max">MAX</option>
                                      </select>
                                    </div>
                                  );
                                })}
                                <p className="pt-1 text-[10px] text-white/35">
                                  Every field above is either a GROUP BY key or
                                  carries an aggregate function — a field left
                                  as neither is dropped from the load.
                                </p>
                              </div>
                            )}
                          </div>
                          <div className="overflow-auto max-h-[400px]">
                            <table className="min-w-[880px] text-xs table-fixed">
                              <colgroup>
                                <col className="w-[100px]" />
                                <col className="w-[150px]" />
                                <col className="w-[90px]" />
                                <col className="w-[230px]" />
                                <col className="w-[120px]" />
                                <col className="w-[70px]" />
                                <col className="w-[64px]" />
                                <col className="w-[56px]" />
                              </colgroup>
                              <thead>
                                <tr className="bg-surface-500/95 backdrop-blur text-white/40 sticky top-0 z-10">
                                  <th className="px-3 py-2 text-left font-medium">
                                    Source Table
                                  </th>
                                  <th className="px-3 py-2 text-left font-medium">
                                    Source Column
                                  </th>
                                  <th className="px-3 py-2 text-left font-medium">
                                    Source Type
                                  </th>
                                  <th className="px-3 py-2 text-left font-medium">
                                    Target Field
                                  </th>
                                  <th className="px-3 py-2 text-left font-medium">
                                    Target Type
                                  </th>
                                  <th className="px-3 py-2 text-left font-medium">
                                    Length
                                  </th>
                                  <th
                                    className="px-3 py-2 text-left font-medium cursor-help"
                                    title={
                                      'Applied during Load Data. Leave blank/"Direct" to pass the ' +
                                      "source value through unchanged. A quoted value ('Unknown') is " +
                                      "a hardcoded literal. An expression referencing other source " +
                                      "columns is evaluated per row, e.g. CONCAT(FIRST_NAME, ' ', " +
                                      "LAST_NAME), UPPER(GENDER), 'M' if GENDER=='Male' else 'F', " +
                                      "format_digits(PHONE, '###-###-####'), strip_special_chars(COL), " +
                                      "format_date(DOB, '%Y-%m-%d'). " +
                                      'Plain English value-mapping also works, e.g. "map it with ' +
                                      'source Gender field, M as Male, F as Female, else null", "first ' +
                                      '10 characters of COLUMN", "remove special characters", or ' +
                                      '"convert date to YYYY-MM-DD format". ' +
                                      "Anything else (a plain-English note) is documentation only."
                                    }
                                  >
                                    Transform ⓘ
                                  </th>
                                  <th className="px-3 py-2 text-center font-medium">
                                    Remove
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  const duplicateTargetFields =
                                    getDuplicateTargetFields(m.field_mappings);
                                  return m.field_mappings.map((fm, fi) => {
                                    const isDuplicateTarget =
                                      duplicateTargetFields.has(
                                        fm.target_field,
                                      );
                                    const cellKey = `${mi}-${fi}`;
                                    const isJoined =
                                      (m.source_tables?.length ?? 0) > 1;
                                    return (
                                      <tr
                                        key={fi}
                                        className={`border-t border-white/5 hover:bg-white/2 ${isDuplicateTarget ? "bg-red-500/5" : ""}`}
                                      >
                                        <td className="px-3 py-1.5 font-mono text-white/50">
                                          {isJoined &&
                                          editingSourceTableCell === cellKey ? (
                                            <select
                                              autoFocus
                                              value={
                                                fm.source_table_ref ||
                                                m.source_table
                                              }
                                              onChange={(e) => {
                                                updateEditedSourceTableRef(
                                                  mi,
                                                  fi,
                                                  e.target.value,
                                                );
                                                setEditingSourceTableCell(null);
                                              }}
                                              onBlur={() =>
                                                setEditingSourceTableCell(null)
                                              }
                                              className="w-full px-1 py-0.5 bg-surface-400/60 border border-white/10 rounded font-mono text-white/70 text-xs focus:outline-none focus:border-brand-500/60"
                                            >
                                              {m.source_tables!.map((t) => (
                                                <option key={t} value={t}>
                                                  {t}
                                                </option>
                                              ))}
                                            </select>
                                          ) : (
                                            <span className="inline-flex items-center gap-1">
                                              {fm.source_table_ref ||
                                                m.source_table}
                                              {isJoined && (
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    setEditingSourceTableCell(
                                                      cellKey,
                                                    )
                                                  }
                                                  className="text-white/20 hover:text-brand-300"
                                                  title="Reassign which joined source table this field comes from"
                                                >
                                                  <Pencil size={10} />
                                                </button>
                                              )}
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-3 py-1.5">
                                          <FieldSearchSelect
                                            value={fm.source_column}
                                            onChange={(v) =>
                                              selectEditedSourceColumn(
                                                mi,
                                                fi,
                                                v,
                                                fm.source_table_ref ||
                                                  m.source_table,
                                              )
                                            }
                                            onCreateNew={(v) =>
                                              updateEditedSourceColumn(
                                                mi,
                                                fi,
                                                v,
                                              )
                                            }
                                            createLabel={(typed) =>
                                              `Use "${typed}" (not in scanned schema)`
                                            }
                                            options={sourceColumnOptions(
                                              fm.source_table_ref ||
                                                m.source_table,
                                            )}
                                            placeholder="(none — hardcoded/derived)"
                                            className="w-full px-2 py-0.5 bg-surface-400/60 border border-white/10 rounded font-mono text-white/80 text-xs focus:outline-none focus:border-brand-500/60"
                                          />
                                        </td>
                                        <td className="px-3 py-1.5">
                                          <input
                                            value={fm.source_type}
                                            onChange={(e) =>
                                              updateEditedSourceType(
                                                mi,
                                                fi,
                                                e.target.value,
                                              )
                                            }
                                            placeholder="—"
                                            className="w-full px-2 py-0.5 bg-surface-400/60 border border-white/10 rounded text-cyan-300/70 text-xs focus:outline-none focus:border-brand-500/60"
                                          />
                                        </td>
                                        <td className="px-3 py-1.5">
                                          <div className="flex items-center gap-1.5">
                                            <FieldSearchSelect
                                              value={fm.target_field}
                                              onChange={(v) =>
                                                selectEditedTargetField(
                                                  mi,
                                                  fi,
                                                  v,
                                                )
                                              }
                                              onCreateNew={(v) =>
                                                createAndSelectTargetField(
                                                  mi,
                                                  fi,
                                                  v,
                                                )
                                              }
                                              createNameSuggestion={
                                                fm.source_column
                                              }
                                              placeholder="Search fields…"
                                              className="w-full px-2 py-0.5 bg-surface-400/60 border border-white/10 rounded font-mono text-brand-300 text-xs focus:outline-none focus:border-brand-500/60"
                                              options={
                                                targetFieldsFor(m.target_object)
                                                  .options
                                              }
                                            />
                                            {fm.status === "Unmapped" && (
                                              <span
                                                title="No confident match was found for this source column — pick a target field above to map it, or leave it unmapped to exclude it from the load."
                                                className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 flex-shrink-0"
                                              >
                                                Unmapped
                                              </span>
                                            )}
                                            {fm.status === "Review" && (
                                              <span
                                                title={`Low-confidence match${fm.confidence !== undefined ? ` (${Math.round(fm.confidence * 100)}%)` : ""} — double-check the target field above before loading.`}
                                                className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300 flex-shrink-0"
                                              >
                                                Review
                                              </span>
                                            )}
                                            {isDuplicateTarget && (
                                              <span
                                                title="This target field is mapped from more than one source column — the last one loaded wins."
                                                className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-500/20 text-red-300 flex-shrink-0"
                                              >
                                                Duplicate
                                              </span>
                                            )}
                                            {(fm.target_field_type ===
                                              "picklist" ||
                                              fm.target_field_type ===
                                                "multipicklist") &&
                                              (fm.picklist_values?.length ??
                                                0) > 0 && (
                                                <span
                                                  title={`Picklist values: ${fm.picklist_values!.join(", ")}`}
                                                  className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 flex-shrink-0"
                                                >
                                                  Picklist
                                                </span>
                                              )}
                                            {fm.target_field_type ===
                                              "reference" &&
                                              (fm.reference_to?.length ?? 0) >
                                                0 && (
                                                <span
                                                  title={`Lookup → ${fm.reference_to!.join(", ")}`}
                                                  className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 flex-shrink-0"
                                                >
                                                  Lookup
                                                </span>
                                              )}
                                          </div>
                                        </td>
                                        <td className="px-3 py-1.5">
                                          <select
                                            value={fm.target_type}
                                            onChange={(e) =>
                                              updateEditedTargetType(
                                                mi,
                                                fi,
                                                e.target.value,
                                              )
                                            }
                                            className="w-full px-2 py-0.5 bg-surface-400/60 border border-white/10 rounded font-mono text-purple-300/90 text-xs focus:outline-none focus:border-purple-500/60"
                                          >
                                            {fm.target_type &&
                                              !SF_FIELD_TYPES.includes(
                                                fm.target_type,
                                              ) && (
                                                <option value={fm.target_type}>
                                                  {fm.target_type}
                                                </option>
                                              )}
                                            {SF_FIELD_TYPES.map((t) => (
                                              <option key={t} value={t}>
                                                {t}
                                              </option>
                                            ))}
                                          </select>
                                        </td>
                                        <td className="px-3 py-1.5">
                                          <input
                                            type="number"
                                            min={1}
                                            value={fm.target_length ?? ""}
                                            onChange={(e) =>
                                              updateEditedTargetLength(
                                                mi,
                                                fi,
                                                e.target.value === ""
                                                  ? null
                                                  : Number(e.target.value),
                                              )
                                            }
                                            placeholder="—"
                                            className="w-16 px-2 py-0.5 bg-surface-400/60 border border-white/10 rounded text-white/70 text-xs focus:outline-none focus:border-brand-500/60"
                                          />
                                        </td>
                                        <td className="px-3 py-1.5">
                                          <ExpandableTextField
                                            value={fm.transformation}
                                            onChange={(v) =>
                                              updateEditedTransformation(
                                                mi,
                                                fi,
                                                v,
                                              )
                                            }
                                            title={`Transform — ${fm.target_field || fm.source_column}`}
                                            placeholder="Custom transform, hardcoded value, or logic…"
                                            hint={
                                              'Applied during Load Data. Leave blank/"Direct" to pass the ' +
                                              "source value through unchanged. A quoted value ('Unknown') is " +
                                              "a hardcoded literal. An expression referencing other source " +
                                              "columns is evaluated per row, e.g. CONCAT(FIRST_NAME, ' ', " +
                                              "LAST_NAME), UPPER(GENDER), 'M' if GENDER=='Male' else 'F', " +
                                              "format_digits(PHONE, '###-###-####'), strip_special_chars(COL), " +
                                              "format_date(DOB, '%Y-%m-%d'). " +
                                              'Plain English value-mapping also works, e.g. "map it with ' +
                                              'source Gender field, M as Male, F as Female, else null", "first ' +
                                              '10 characters of COLUMN", "remove special characters", or ' +
                                              '"convert date to YYYY-MM-DD format".'
                                            }
                                            compact
                                          />
                                        </td>
                                        <td className="px-3 py-1.5 text-center">
                                          <button
                                            onClick={() =>
                                              removeEditedFieldMapping(mi, fi)
                                            }
                                            className="text-red-400/60 hover:text-red-400 transition"
                                            title="Remove this row"
                                          >
                                            <X size={13} />
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  });
                                })()}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })()}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={handleApplyAndRegenerate}
                      disabled={generatingSttm}
                      className="ztd-btn ztd-btn-primary"
                    >
                      {generatingSttm ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <RefreshCw size={14} />
                      )}
                      Apply & Regenerate STTM
                    </button>
                    {sttmResult && (
                      <a
                        href={sttmResult.download_url}
                        download={sttmResult.filename}
                        className="ztd-btn ztd-btn-secondary"
                      >
                        <Download size={14} /> Download STTM
                      </a>
                    )}
                  </div>
                </SectionCard>
              )}

              {(() => {
                const unmapped = computeUnmappedTables(
                  schemaResult?.tables ?? [],
                  editedMappings.filter(
                    (_, mi) => !removedTableMappings.has(mi),
                  ),
                );
                return (
                  unmapped.length > 0 && (
                    <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-xl">
                      <button
                        type="button"
                        onClick={() => setUnmappedReviewTablesOpen((o) => !o)}
                        className="w-full flex items-center gap-2 text-amber-400"
                      >
                        {unmappedReviewTablesOpen ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )}
                        <AlertTriangle size={14} />
                        <p className="text-sm font-semibold">
                          {unmapped.length} unmapped table
                          {unmapped.length !== 1 ? "s" : ""}
                        </p>
                      </button>
                      {unmappedReviewTablesOpen && (
                        <>
                          <p className="text-xs text-white/40 mt-2 mb-3">
                            These source tables have no mapping left in this
                            STTM — removing a table's tab above (or deleting all
                            of its field rows) put it here. Go back to the
                            mapping step to re-add one.
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {unmapped.map((t) => (
                              <span
                                key={t.table_name}
                                className="px-2.5 py-1 rounded-md text-xs border bg-amber-500/10 border-amber-500/30 text-amber-300 font-mono"
                              >
                                {t.table_name}
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )
                );
              })()}

              <SectionCard title="Finalize">
                <p className="text-xs text-white/40 mb-3">
                  Saves this mapping as the project's finalized version (also
                  picked up by Data Loader / Reconciliation / Load to Target),
                  and generates and saves a Data Dictionary workbook alongside
                  it.
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={handleFinalizeMapping}
                    disabled={finalizing || mappings.length === 0}
                    className="ztd-btn ztd-btn-primary"
                  >
                    {finalizing ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Check size={14} />
                    )}
                    Finalize & Save Mapping + Data Dictionary
                  </button>
                  {finalizedAt && (
                    <div className="flex items-center gap-2 text-sm text-green-400">
                      <CheckCircle2 size={14} />
                      Saved{" "}
                      {finalizedAt.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  )}
                  {dataDictionaryResult && (
                    <a
                      href={dataDictionaryResult.download_url}
                      download={dataDictionaryResult.filename}
                      className="ztd-btn ztd-btn-secondary"
                    >
                      <Download size={14} /> Download Data Dictionary
                    </a>
                  )}
                  {projectMappingFile?.download_url && (
                    <a
                      href={projectMappingFile.download_url}
                      download={projectMappingFile.filename ?? undefined}
                      className="ztd-btn ztd-btn-secondary"
                    >
                      <Download size={14} /> Download Mapping (.xlsx)
                    </a>
                  )}
                </div>
              </SectionCard>

              <div className="flex justify-between">
                <button onClick={goPrev} className="ztd-btn ztd-btn-secondary">
                  <ArrowLeft size={14} /> Back to Mapping
                </button>
                <div className="flex gap-2">
                  <button
                    // #622: this used to call handleGenerateSttm() directly,
                    // regenerating from the stale `mappings` state instead of
                    // `editedMappings` — silently discarded any unsaved
                    // per-field edits. handleApplyAndRegenerate commits
                    // editedMappings first, same as the in-editor "Apply &
                    // Regenerate STTM" button, so both regenerate actions now
                    // share identical, safe semantics.
                    onClick={handleApplyAndRegenerate}
                    disabled={generatingSttm}
                    className="ztd-btn ztd-btn-secondary"
                  >
                    {generatingSttm ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <RefreshCw size={14} />
                    )}
                    Regenerate
                  </button>
                  {/* 2026-09-01, requested by user: don't force everyone
                  through Create Tables before Load Data — offer both as
                  explicit next steps from Finalize. issue #622: this is a
                  shortcut to the same "Load Data" destination as the Create
                  Tables step's own forward button below — kept as two
                  buttons (not consolidated) since the destination step's
                  behavior genuinely differs (skips vs. requires table
                  creation), but disabled + labeled here so it can never
                  produce the unsafe "load into tables that don't exist"
                  outcome, and the enabled tooltip makes the shortcut
                  explicit instead of looking like a plain duplicate. */}
                  <button
                    onClick={() => jumpToLoadDataDirect()}
                    disabled={!sttmResult || loaderStateExists === false}
                    title={
                      loaderStateExists === false
                        ? "No staging tables exist yet for this project — use Create Tables first"
                        : "Shortcut: skip Create Tables and load into the staging tables that already exist"
                    }
                    className="ztd-btn ztd-btn-secondary"
                  >
                    Load Data <ArrowRight size={14} />
                  </button>
                  <button
                    onClick={() => selectAction("loader")}
                    disabled={!sttmResult}
                    className="ztd-btn ztd-btn-primary"
                  >
                    Create Tables <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Step: Create Tables (DDL) ──────────────────────────────────── */}
          {step === "ddl" && (
            <div className="space-y-4">
              {loaderEntryChoice === "mapped" && (
                <button
                  type="button"
                  onClick={() => setLoaderEntryChoice(null)}
                  className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition"
                >
                  <ArrowLeft size={12} /> Back to Data Loader options
                </button>
              )}
              {renderProjectMappingFileCard()}
              {/* 2026-09-08, requested by user: side by side on wider
              screens instead of a full-width stack — see the identical
              layout change on the Load Data step. */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                {/* Panel 1 — Target DB connection */}
                <SectionCard title="Target Database Connection">
                  {targetConnectionId ? (
                    // 2026-09-08: flex-wrap so the buttons drop to their own
                    // line instead of squeezing this text in the half column.
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-sm text-green-400">
                        <CheckCircle2 size={14} />
                        Connected — {targetConnLabel || "saved connection"}
                        {ddlTargetConnected?.db_version
                          ? ` · ${ddlTargetConnected.db_version}`
                          : ""}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={handleRefreshTargetConnection}
                          disabled={refreshingConnection}
                          title="Re-test this connection — a saved/activated connection isn't verified until you test it"
                          className="ztd-btn ztd-btn-secondary"
                        >
                          {refreshingConnection ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <RefreshCw size={14} />
                          )}
                          Refresh Connection
                        </button>
                        <button
                          onClick={() => setShowTargetConnectDialog(true)}
                          className="ztd-btn ztd-btn-secondary"
                        >
                          <RefreshCw size={14} />
                          Change connection
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setShowTargetConnectDialog(true)}
                        className="ztd-btn ztd-btn-primary"
                      >
                        <RefreshCw size={14} />
                        Connect Database
                      </button>
                      <span className="text-xs text-white/40">
                        Reuse a connection any agent already saved for this
                        project, or connect a new one.
                      </span>
                    </div>
                  )}

                  <ConnectDatabaseDialog
                    open={showTargetConnectDialog}
                    onClose={() => setShowTargetConnectDialog(false)}
                    title="Connect Target (Staging) Database"
                    projectId={projectId}
                    kind="target"
                    agentKey="data_converter"
                    defaultDbType="oracle"
                    testIdPrefix="converter-target"
                    onConnectExisting={handleTargetConnectExisting}
                    onConnectNew={handleTargetConnectNew}
                  />
                </SectionCard>

                {/* 2026-09-02, requested by user: always show the saved
                target-connection library (Oracle/Snowflake/Databricks/etc.),
                not just before the first one is connected, so this project's
                other saved staging DBs stay one click away. */}
                {projectId && (
                  <SavedConnectionsPanel
                    projectId={projectId}
                    kind="target"
                    onActivated={handleTargetConnectionActivatedFromLibrary}
                    onConnectExisting={handleTargetConnectExisting}
                  />
                )}
              </div>

              {/* Panel 2 — Generate DDL + preview */}
              <SectionCard title="Generate & Preview DDL">
                <div className="mb-4 rounded-lg border border-white/10 bg-white/3 p-3 space-y-2.5">
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="text-xs font-medium text-white/50 shrink-0">
                      Table Naming
                    </label>
                    <select
                      value={tableNamingMode}
                      onChange={(e) =>
                        setTableNamingMode(
                          e.target.value as
                            | "auto"
                            | "prefix"
                            | "suffix"
                            | "none",
                        )
                      }
                      className="px-2.5 py-1.5 bg-surface-400/50 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-brand-500/50 transition"
                    >
                      <option value="auto">
                        Automatic (platform default suffix)
                      </option>
                      <option value="prefix">Custom Prefix</option>
                      <option value="suffix">Custom Suffix</option>
                      <option value="none">No Prefix/Suffix</option>
                    </select>
                    {(tableNamingMode === "prefix" ||
                      tableNamingMode === "suffix") && (
                      <input
                        type="text"
                        value={tableNamingValue}
                        onChange={(e) => setTableNamingValue(e.target.value)}
                        placeholder={
                          tableNamingMode === "prefix"
                            ? "e.g. stg_"
                            : "e.g. _dbx"
                        }
                        className="w-32 px-2.5 py-1.5 bg-surface-400/50 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-brand-500/50 transition"
                      />
                    )}
                  </div>

                  {tableNamingMode !== "auto" && (
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-4 text-xs text-white/60">
                        <label className="flex items-center gap-1.5">
                          <input
                            type="radio"
                            checked={tableNamingScope === "all"}
                            onChange={() => setTableNamingScope("all")}
                          />
                          Apply to all tables
                        </label>
                        <label className="flex items-center gap-1.5">
                          <input
                            type="radio"
                            checked={tableNamingScope !== "all"}
                            onChange={() =>
                              setTableNamingScope(new Set(ddlTargetTableNames))
                            }
                          />
                          Apply to selected tables only
                        </label>
                      </div>
                      {tableNamingScope !== "all" && (
                        <div className="max-h-40 overflow-y-auto rounded-lg border border-white/10 bg-surface-500/40 p-2 space-y-1">
                          {ddlTargetTableNames.length === 0 ? (
                            <p className="text-[11px] text-white/35 italic">
                              No mapped tables yet.
                            </p>
                          ) : (
                            ddlTargetTableNames.map((t) => (
                              <label
                                key={t}
                                className="flex items-center gap-2 text-xs text-white/60"
                              >
                                <input
                                  type="checkbox"
                                  checked={tableNamingScope.has(t)}
                                  onChange={(e) =>
                                    setTableNamingScope((prev) => {
                                      if (prev === "all") return prev;
                                      const next = new Set(prev);
                                      if (e.target.checked) next.add(t);
                                      else next.delete(t);
                                      return next;
                                    })
                                  }
                                />
                                {t}
                              </label>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <label className="flex items-center gap-2 text-xs text-white/60">
                    <input
                      type="checkbox"
                      checked={includeAuditColumns}
                      onChange={(e) => setIncludeAuditColumns(e.target.checked)}
                    />
                    Add audit columns (created/modified date &amp; by) to every
                    generated table
                  </label>
                </div>

                <div className="flex items-end gap-3 mb-4">
                  <div className="w-full max-w-xs">
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-xs font-medium text-white/50">
                        Target Schema Name
                      </label>
                      <div className="flex items-center gap-3">
                        {targetSchemas.length > 0 && (
                          <button
                            type="button"
                            onClick={() =>
                              setTargetSchemaManualEntry((v) => !v)
                            }
                            className="text-[11px] text-brand-400 hover:text-brand-300"
                          >
                            {targetSchemaManualEntry
                              ? "Choose from list"
                              : "Type manually"}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => void handleLoadTargetSchemas()}
                          disabled={!targetConnectionId || loadingTargetSchemas}
                          title="List schemas that already exist on the connected target"
                          className="flex items-center gap-1 text-[11px] text-white/40 hover:text-white/70 disabled:opacity-40"
                        >
                          {loadingTargetSchemas ? (
                            <Loader2 size={10} className="animate-spin" />
                          ) : (
                            <RefreshCw size={10} />
                          )}
                          {targetSchemas.length > 0
                            ? "Refresh"
                            : "List schemas"}
                        </button>
                      </div>
                    </div>
                    {targetSchemaManualEntry || targetSchemas.length === 0 ? (
                      <input
                        type="text"
                        value={ddlTargetSchema}
                        onChange={(e) => setDdlTargetSchema(e.target.value)}
                        placeholder="MIGRATION"
                        className="w-full px-3 py-2 bg-surface-400/50 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-brand-500/50 transition"
                      />
                    ) : (
                      <select
                        value={ddlTargetSchema}
                        onChange={(e) => setDdlTargetSchema(e.target.value)}
                        className="w-full px-3 py-2 bg-surface-400/50 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-brand-500/50 transition"
                      >
                        <option value="">Select schema…</option>
                        {targetSchemas.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <button
                    onClick={handleGenerateDdl}
                    disabled={ddlBusy || mappings.length === 0}
                    className="ztd-btn ztd-btn-primary"
                  >
                    {generatingDdl ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : (
                      <Zap size={14} />
                    )}
                    {ddlStatements.length > 0
                      ? "Regenerate DDL"
                      : "Generate DDL"}
                  </button>
                </div>

                {ddlStatements.length === 0 && (
                  <p className="text-xs text-white/30 italic text-center py-4">
                    Click "Generate DDL" to preview CREATE TABLE statements from
                    your STTM schema
                  </p>
                )}

                {sttmMappingLog.length > 0 && (
                  <div className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-white/3 px-4 py-2.5">
                    <span className="text-xs text-white/50">
                      {sttmMappingLog.length} duplicate decision
                      {sttmMappingLog.length !== 1 ? "s" : ""} logged this
                      session
                    </span>
                    <button
                      onClick={downloadSttmMappingLog}
                      className="ztd-btn ztd-btn-secondary text-xs shrink-0"
                      title="Download every duplicate/mismatch and the default action applied to it, as one text file"
                    >
                      <Download size={12} />
                      Download STTM Mapping Log
                    </button>
                  </div>
                )}

                {ddlDuplicates.length > 0 && (
                  <div className="mb-3 rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-4 py-3 space-y-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => setDdlDuplicatesExpanded((v) => !v)}
                        className="flex items-center gap-2 text-sm font-medium text-yellow-400 hover:text-yellow-300 transition"
                      >
                        {ddlDuplicatesExpanded ? (
                          <ChevronDown size={14} />
                        ) : (
                          <ChevronRight size={14} />
                        )}
                        <AlertTriangle size={14} />
                        {ddlDuplicates.length} duplicate
                        {ddlDuplicates.length !== 1 ? "s" : ""} across{" "}
                        {ddlDuplicateTableNames.length} target table
                        {ddlDuplicateTableNames.length !== 1 ? "s" : ""} —
                        review and decide below
                      </button>
                      <button
                        onClick={handleApproveAllDdlDuplicatesDefault}
                        disabled={ddlBusy}
                        className="ztd-btn ztd-btn-secondary text-xs shrink-0"
                        title="Set every duplicate, in every table, to the default action (Remove), log the decisions, and regenerate DDL"
                      >
                        {resolvingDdlDuplicates ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <CheckCheck size={12} />
                        )}
                        Approve All (Default)
                      </button>
                    </div>
                    {ddlDuplicatesExpanded && (
                      <>
                        {/* One tab per target table, instead of a single
                            flat list mixing every table's duplicates
                            together — the count badge lets a reviewer see
                            which tables still need attention at a glance. */}
                        <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
                          {ddlDuplicateTableNames.map((table) => {
                            const count =
                              ddlDuplicatesByTable.get(table)?.length ?? 0;
                            const isActive =
                              table === ddlDuplicateActiveTableResolved;
                            return (
                              <button
                                key={table}
                                type="button"
                                onClick={() =>
                                  setDdlDuplicateActiveTable(table)
                                }
                                className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium border transition ${
                                  isActive
                                    ? "bg-yellow-500/25 text-yellow-200 border-yellow-500/40"
                                    : "bg-black/10 text-yellow-300/60 border-yellow-500/15 hover:text-yellow-200"
                                }`}
                              >
                                {table} ({count})
                              </button>
                            );
                          })}
                        </div>

                        {ddlDuplicateActiveTableResolved && (
                          <>
                            <div className="flex items-center justify-between gap-2 pt-1">
                              <span className="text-[11px] text-yellow-300/50">
                                {ddlDuplicatesByTable.get(
                                  ddlDuplicateActiveTableResolved,
                                )?.length ?? 0}{" "}
                                item(s) for '{ddlDuplicateActiveTableResolved}'
                              </span>
                              <button
                                onClick={() =>
                                  handleApproveTableDdlDuplicatesDefault(
                                    ddlDuplicateActiveTableResolved,
                                  )
                                }
                                disabled={ddlBusy}
                                className="ztd-btn ztd-btn-secondary text-[11px] shrink-0"
                                title={`Set every duplicate for '${ddlDuplicateActiveTableResolved}' to the default action (Remove), log the decisions, and regenerate DDL`}
                              >
                                {resolvingDdlDuplicates ? (
                                  <Loader2 size={11} className="animate-spin" />
                                ) : (
                                  <CheckCheck size={11} />
                                )}
                                Approve Default for This Table
                              </button>
                            </div>
                            <ul className="space-y-2">
                              {(
                                ddlDuplicatesByTable.get(
                                  ddlDuplicateActiveTableResolved,
                                ) ?? []
                              ).map((d, i) => {
                                const key = ddlDuplicateKey(d);
                                const decision = ddlDuplicateDecisions[key];
                                // `action` still defaults to "remove" for
                                // resolution purposes (unchanged), but the
                                // button styling must distinguish "this is
                                // what happens by default" (amber — not yet
                                // confirmed) from "the reviewer explicitly
                                // picked this" (green) — showing the default
                                // as already-confirmed green before anyone
                                // has looked at the row is misleading in a
                                // review workflow.
                                const hasDecision = decision !== undefined;
                                const action = decision?.action ?? "remove";
                                return (
                                  <li
                                    key={i}
                                    className="text-xs text-yellow-300/80 rounded-md border border-yellow-500/15 bg-black/10 px-3 py-2 space-y-1.5"
                                  >
                                    <div className="flex items-start gap-1.5">
                                      <span className="mt-0.5 shrink-0">
                                        {d.type === "duplicate_table"
                                          ? "📋"
                                          : "🔑"}
                                      </span>
                                      <span>{d.detail}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 pl-5">
                                      {(
                                        ["remove", "keep", "rename"] as const
                                      ).map((opt) => (
                                        <button
                                          key={opt}
                                          onClick={() =>
                                            setDdlDuplicateDecision(d, opt)
                                          }
                                          className={`px-2 py-1 rounded text-[11px] font-medium transition ${
                                            hasDecision
                                              ? action === opt
                                                ? "bg-brand-500/30 text-brand-300 border border-brand-500/40"
                                                : "bg-white/5 text-white/50 border border-white/10 hover:text-white/80"
                                              : opt === "remove"
                                                ? "bg-yellow-500/20 text-yellow-300 border border-yellow-500/40"
                                                : "bg-white/5 text-white/50 border border-white/10 hover:text-white/80"
                                          }`}
                                        >
                                          {opt === "remove"
                                            ? "Remove (default)"
                                            : opt === "keep"
                                              ? "Keep both"
                                              : "Edit name"}
                                        </button>
                                      ))}
                                      {action === "rename" && (
                                        <input
                                          type="text"
                                          value={
                                            decision?.newName ??
                                            (d.column
                                              ? `${d.column}_2`
                                              : `${d.table}_2`)
                                          }
                                          onChange={(e) =>
                                            setDdlDuplicateNewName(
                                              d,
                                              e.target.value,
                                            )
                                          }
                                          className="ml-1 px-2 py-1 bg-surface-400/50 border border-white/10 rounded text-[11px] text-white focus:outline-none focus:border-brand-500/50"
                                          placeholder="New name"
                                        />
                                      )}
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </>
                        )}
                        <div className="flex justify-end pt-1">
                          <button
                            onClick={handleApplyDdlDuplicateDecisions}
                            disabled={ddlBusy}
                            className="ztd-btn ztd-btn-secondary text-xs"
                          >
                            {resolvingDdlDuplicates ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <RefreshCw size={12} />
                            )}
                            Apply My Choices & Regenerate DDL
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {ddlStatements.length > 0 && (
                  <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
                    {ddlStatements.map((s) => {
                      const execResult = ddlExecuteResults?.find((r) =>
                        r.ddl.includes(s.table_name),
                      );
                      return (
                        <div
                          key={s.table_name}
                          className="border border-white/8 rounded-xl overflow-hidden"
                        >
                          <div className="flex items-center justify-between px-4 py-2.5 bg-white/3">
                            <div className="flex items-center gap-2">
                              <Database size={13} className="text-brand-400" />
                              <span className="text-sm font-medium text-white">
                                {ddlTargetSchema || ""}.{s.table_name}
                              </span>
                              {execResult && (
                                <span
                                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${execResult.status === "success" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}
                                >
                                  {execResult.status === "success"
                                    ? "✓ Created"
                                    : "✗ Failed"}
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => handleCopyDdl(s.table_name, s.ddl)}
                              className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition"
                            >
                              {ddlCopied === s.table_name ? (
                                <CheckCircle2
                                  size={12}
                                  className="text-green-400"
                                />
                              ) : (
                                <Download size={12} />
                              )}
                              {ddlCopied === s.table_name ? "Copied!" : "Copy"}
                            </button>
                          </div>
                          <pre className="px-4 py-3 text-xs text-white/70 bg-black/20 overflow-x-auto font-mono leading-relaxed whitespace-pre">
                            {s.ddl}
                          </pre>
                          {execResult?.error && (
                            <div className="px-4 py-2 bg-red-500/10 border-t border-red-500/20 text-xs text-red-400">
                              {execResult.error}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </SectionCard>

              {/* 2026-09-08, requested by user: Create/Drop Tables used to
              stack as full-width horizontal bars; side by side as columns
              instead on wider screens. */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                {/* Panel 3 — Create tables in target DB */}
                {ddlStatements.length > 0 && (
                  <div className="p-4 rounded-xl border border-white/8 bg-white/2 flex items-center justify-between gap-4 flex-wrap">
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium text-white">
                        Create Tables in Target Database
                      </p>
                      <p className="text-xs text-white/40">
                        {ddlTargetConnected
                          ? `Connected to ${targetConnLabel || "target database"} · checks each of ${ddlStatements.length} table${ddlStatements.length !== 1 ? "s" : ""} in schema ${ddlTargetSchema || targetSchemaName} against what's already there — only creates or recreates what's missing or has drifted`
                          : "Connect a target database above first, then execute DDL directly in the target schema"}
                      </p>
                      {ddlReconcileResults && (
                        <div className="pt-1.5 space-y-1.5">
                          <button
                            onClick={() =>
                              downloadFailuresTxt(
                                "Create Tables check results",
                                `create_tables_results_${filenameTimestamp()}.txt`,
                                ddlReconcileResults.map(
                                  (r, i) =>
                                    `[${i + 1}] ${r.table_name} — ${
                                      r.action === "up_to_date"
                                        ? "Up to date"
                                        : r.action === "recreate"
                                          ? "Recreate"
                                          : "Create"
                                    }${r.diff.length > 0 ? `\n${r.diff.join("\n")}` : ""}`,
                                ),
                              )
                            }
                            className="text-xs text-white/40 hover:text-white/70 underline underline-offset-2 inline-flex items-center gap-1"
                          >
                            <Download size={11} /> Download list (.txt)
                          </button>
                          <div className="max-h-[220px] overflow-y-auto pr-1 space-y-1">
                            {ddlReconcileResults.map((r) => (
                              <div
                                key={r.table_name}
                                className="flex items-start gap-2 text-xs"
                                title={
                                  r.diff.length > 0
                                    ? r.diff.join("\n")
                                    : undefined
                                }
                              >
                                <span
                                  className={`mt-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${
                                    r.action === "up_to_date"
                                      ? "bg-green-500/15 text-green-400"
                                      : r.action === "recreate"
                                        ? "bg-amber-500/15 text-amber-400"
                                        : "bg-brand-500/15 text-brand-300"
                                  }`}
                                >
                                  {r.action === "up_to_date"
                                    ? "Up to date"
                                    : r.action === "recreate"
                                      ? "Recreate"
                                      : "Create"}
                                </span>
                                <span className="text-white/60 font-mono">
                                  {r.table_name}
                                </span>
                                {r.diff.length > 0 && (
                                  <span className="text-white/35">
                                    — {r.diff[0]}
                                    {r.diff.length > 1
                                      ? ` (+${r.diff.length - 1} more)`
                                      : ""}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {ddlExecuteResults && (
                        <div className="flex items-center gap-3 pt-1 text-sm">
                          <span className="text-green-400">
                            ✓{" "}
                            {
                              ddlExecuteResults.filter(
                                (r) => r.status === "success",
                              ).length
                            }{" "}
                            created
                          </span>
                          {ddlExecuteResults.some(
                            (r) => r.status === "error",
                          ) && (
                            <>
                              <span className="text-red-400">
                                ✗{" "}
                                {
                                  ddlExecuteResults.filter(
                                    (r) => r.status === "error",
                                  ).length
                                }{" "}
                                failed
                              </span>
                              <button
                                onClick={() =>
                                  downloadFailuresTxt(
                                    "DDL execution failures",
                                    `ddl_execution_failures_${filenameTimestamp()}.txt`,
                                    ddlExecuteResults
                                      .filter((r) => r.status === "error")
                                      .map((r, i) => {
                                        const tableMatch = r.ddl.match(
                                          /CREATE TABLE\s+("[^"]+"\.)?"([^"]+)"/i,
                                        );
                                        const table =
                                          tableMatch?.[2] ?? "(unknown table)";
                                        return `[${i + 1}] ${table}\n${r.error}\n---\n${r.ddl}`;
                                      }),
                                  )
                                }
                                className="text-xs text-red-300 hover:text-red-200 underline underline-offset-2"
                              >
                                Download failures (.txt)
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-end gap-2">
                      <button
                        onClick={handleExecuteDdl}
                        disabled={executingDdl || !ddlTargetConnected}
                        className="ztd-btn ztd-btn-primary"
                      >
                        {executingDdl ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Zap size={14} />
                        )}
                        {reconcilingDdl ? "Checking…" : "Create Tables"}
                      </button>
                    </div>
                  </div>
                )}

                {/* Panel 4 — Drop tables in target DB (standalone, destructive) */}
                {ddlStatements.length > 0 && (
                  <div className="p-4 rounded-xl border border-red-500/20 bg-red-500/5 flex items-center justify-between gap-4 flex-wrap">
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium text-white">
                        Drop Tables in Target Database
                      </p>
                      <p className="text-xs text-white/40">
                        {ddlTargetConnected
                          ? `Connected to ${targetConnLabel || "target database"} · will drop ${ddlStatements.length} table${ddlStatements.length !== 1 ? "s" : ""} in schema ${ddlTargetSchema || targetSchemaName} if they exist`
                          : "Connect a target database above first, then drop tables directly in the target schema"}
                      </p>
                      {dropTablesResult && (
                        <div className="pt-1 space-y-1.5">
                          <div className="flex items-center gap-3 text-sm flex-wrap">
                            {dropTablesResult.dropped > 0 && (
                              <span className="text-green-400">
                                ✓ {dropTablesResult.dropped} dropped
                              </span>
                            )}
                            {dropTablesResult.skipped > 0 && (
                              <span className="text-yellow-400">
                                – {dropTablesResult.skipped} didn't exist
                              </span>
                            )}
                            {dropTablesResult.failed > 0 && (
                              <>
                                <span className="text-red-400">
                                  ✗ {dropTablesResult.failed} failed
                                </span>
                                <button
                                  onClick={() =>
                                    downloadFailuresTxt(
                                      "Drop table failures",
                                      `drop_tables_failures_${filenameTimestamp()}.txt`,
                                      dropTablesResult.results
                                        .filter((r) => r.status === "error")
                                        .map(
                                          (r, i) =>
                                            `[${i + 1}] ${r.table_name}\n${r.error}`,
                                        ),
                                    )
                                  }
                                  className="text-xs text-red-300 hover:text-red-200 underline underline-offset-2"
                                >
                                  Download failures (.txt)
                                </button>
                              </>
                            )}
                            <button
                              onClick={() =>
                                downloadFailuresTxt(
                                  "Drop table results",
                                  `drop_tables_results_${filenameTimestamp()}.txt`,
                                  dropTablesResult.results.map(
                                    (r, i) =>
                                      `[${i + 1}] ${r.table_name} — ${
                                        r.status === "success"
                                          ? "Dropped"
                                          : r.status === "skipped"
                                            ? "Skipped (didn't exist)"
                                            : "Failed"
                                      }${r.error ? `: ${r.error}` : ""}`,
                                  ),
                                )
                              }
                              className="text-xs text-white/40 hover:text-white/70 underline underline-offset-2 inline-flex items-center gap-1"
                            >
                              <Download size={11} /> Download list (.txt)
                            </button>
                          </div>
                          <div className="max-h-[220px] overflow-y-auto pr-1 space-y-1">
                            {dropTablesResult.results.map((r) => (
                              <div
                                key={r.table_name}
                                className="flex items-center gap-2 text-xs"
                                title={r.error ?? undefined}
                              >
                                <span
                                  className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${
                                    r.status === "success"
                                      ? "bg-green-500/15 text-green-400"
                                      : r.status === "skipped"
                                        ? "bg-yellow-500/15 text-yellow-400"
                                        : "bg-red-500/15 text-red-400"
                                  }`}
                                >
                                  {r.status === "success"
                                    ? "Dropped"
                                    : r.status === "skipped"
                                      ? "Skipped"
                                      : "Failed"}
                                </span>
                                <span className="text-white/60 font-mono">
                                  {r.table_name}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-end gap-2">
                      <button
                        onClick={handleDropTablesClick}
                        onBlur={() => setDropTablesArmed(false)}
                        disabled={dropTablesLoading || !ddlTargetConnected}
                        className={`ztd-btn ${dropTablesArmed ? "ztd-btn-danger" : "ztd-btn-secondary"}`}
                      >
                        {dropTablesLoading ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Trash2 size={14} />
                        )}
                        {dropTablesArmed
                          ? "Confirm Drop Tables?"
                          : "Drop Tables"}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setAction(null)}
                  className="ztd-btn ztd-btn-secondary"
                >
                  <ArrowLeft size={14} /> Back to Actions
                </button>
                <button
                  onClick={goNext}
                  disabled={mappings.length === 0}
                  className="ztd-btn ztd-btn-primary"
                >
                  Load Data <ArrowRight size={14} />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 8: Load Data ───────────────────────────────────────────── */}
          {step === "load" && (
            <div className="space-y-4">
              {renderProjectMappingFileCard()}
              {projectId && (
                <div className="flex justify-end">
                  <button
                    onClick={openLoadHistoryPanel}
                    className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                    title="View last 10 saved Data Loader runs"
                  >
                    <History size={13} /> Load History
                  </button>
                </div>
              )}
              {/* 2026-09-08, requested by user: Source/Target Connection each
              used to be a full-width stacked block (card + saved-connections
              library), forcing a lot of vertical scrolling to reach the
              actual step content below. Side-by-side on wider screens,
              stacked again on narrow ones. */}
              {/* 2026-09-13, requested by user: this toggle used to live
              inside the "Source Connection" card, making it taller than
              "Target Connection" next to it and breaking the side-by-side
              symmetry. Moved above the grid — same placement already used
              for the (differently-scoped) Source DB step's own Source Type
              toggle — so both cards stay the same shape. */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-white/50">
                  Source Type:
                </span>
                <div className="inline-flex rounded-lg border border-white/10 overflow-hidden">
                  <button
                    onClick={() => setLoadSourceMode("database")}
                    className={`px-3 py-1.5 text-xs font-medium transition ${
                      loadSourceMode === "database"
                        ? "bg-brand-500/20 text-brand-300"
                        : "text-white/50 hover:text-white"
                    }`}
                  >
                    Database
                  </button>
                  <button
                    onClick={() => setLoadSourceMode("files")}
                    className={`px-3 py-1.5 text-xs font-medium transition ${
                      loadSourceMode === "files"
                        ? "bg-brand-500/20 text-brand-300"
                        : "text-white/50 hover:text-white"
                    }`}
                  >
                    Files (no source database)
                  </button>
                </div>
              </div>

              {/* issue #623: Source/Target Connection side-by-side instead
              of full-width stacked, same pattern applied on Reconciliation
              below. */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                <div className="space-y-4">
                  <SectionCard title="Source Connection">
                    {loadSourceMode === "files" ? (
                      <div className="space-y-2">
                        <p className="text-xs text-white/45">
                          No source database needed — each mapped table reads
                          from a file. Tables crawled via "Generate from Legacy
                          Code" already remember which file they came from, so
                          you don't need to pick one file at a time.
                        </p>
                        <button
                          onClick={handleAutoFillSourceFilesFromMapping}
                          disabled={
                            autoFillingSourceFiles || mappings.length === 0
                          }
                          className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                        >
                          {autoFillingSourceFiles ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <RefreshCw size={12} />
                          )}
                          Auto-Fill Source Files from Mapping
                        </button>
                      </div>
                    ) : (
                      <>
                        {sourceConnectionId ? (
                          // 2026-09-08: flex-wrap so the button drops below
                          // instead of squeezing this text in the half column.
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-2 text-sm text-green-400">
                              <CheckCircle2 size={14} />
                              {sourceConnLabel || "saved connection"}
                            </div>{" "}
                            <button
                              onClick={() => setShowSourceConnectDialog(true)}
                              className="ztd-btn ztd-btn-secondary"
                            >
                              <RefreshCw size={14} />
                              Change connection
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => setShowSourceConnectDialog(true)}
                              className="ztd-btn ztd-btn-primary"
                            >
                              <RefreshCw size={14} />
                              Connect Database
                            </button>
                            <span className="text-xs text-white/40">
                              Needed to read source data — reuse a connection
                              any agent already saved for this project, or
                              connect a new one.
                            </span>
                          </div>
                        )}

                        <ConnectDatabaseDialog
                          open={showSourceConnectDialog}
                          onClose={() => setShowSourceConnectDialog(false)}
                          title="Connect Source Database"
                          projectId={projectId}
                          kind="source"
                          agentKey="data_converter"
                          defaultDbType="oracle"
                          testIdPrefix="converter-source-load"
                          onConnectExisting={handleSourceConnectExistingForLoad}
                          onConnectNew={handleSourceConnectNew}
                        />
                      </>
                    )}
                  </SectionCard>

                  {loadSourceMode === "database" && projectId && (
                    <SavedConnectionsPanel
                      projectId={projectId}
                      kind="source"
                      onActivated={handleSourceConnectionActivatedFromLibrary}
                      onConnectExisting={handleSourceConnectExistingForLoad}
                    />
                  )}
                </div>

                <div className="space-y-4">
                  <SectionCard title="Target Connection">
                    {targetConnectionId ? (
                      // 2026-09-08: flex-wrap so the button drops below
                      // instead of squeezing this text in the half column.
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-sm text-green-400">
                          <CheckCircle2 size={14} />
                          {targetConnLabel || "saved connection"}
                        </div>
                        <button
                          onClick={() => setShowTargetConnectDialog(true)}
                          className="ztd-btn ztd-btn-secondary"
                        >
                          <RefreshCw size={14} />
                          Change connection
                        </button>
                      </div>
                    ) : (
                      <p className="text-sm text-white/40">
                        {crmLoadStrategy === "direct"
                          ? "No target connection yet — connect the target database above (or via Saved Connections below)."
                          : "No target connection yet — go back to Create Staging Tables."}
                      </p>
                    )}

                    <ConnectDatabaseDialog
                      open={showTargetConnectDialog}
                      onClose={() => setShowTargetConnectDialog(false)}
                      title="Connect Target Database"
                      projectId={projectId}
                      kind="target"
                      agentKey="data_converter"
                      defaultDbType="oracle"
                      testIdPrefix="converter-target-load"
                      onConnectExisting={handleTargetConnectExisting}
                      onConnectNew={handleTargetConnectNew}
                    />
                  </SectionCard>

                  {/* 2026-09-02, requested by user: same as the source
                  library above -- always visible so a different saved
                  staging DB is one click away. */}
                  {projectId && (
                    <SavedConnectionsPanel
                      projectId={projectId}
                      kind="target"
                      onActivated={handleTargetConnectionActivatedFromLibrary}
                      onConnectExisting={handleTargetConnectExisting}
                    />
                  )}
                </div>
              </div>

              <SectionCard
                title="Mapping Summary"
                actions={
                  mappings.length > 0 ? (
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-white/40">
                        {mappings.filter((_, i) => effectiveIncluded(i)).length}{" "}
                        of {mappings.length} tables selected to load
                      </span>
                      <button
                        onClick={() =>
                          setLoadMappingConfig((prev) =>
                            mappings.map((m, i) => ({
                              ...(prev[i] ?? {
                                srcSchema: getMappingSourceSchemaDefault(m),
                                srcTable: m.source_table,
                                tgtSchema: ddlTargetSchema || "",
                                tgtTable: stagedTargetTable(m.target_object),
                              }),
                              included: true,
                            })),
                          )
                        }
                        className="ztd-btn ztd-btn-ghost ztd-btn-sm"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() =>
                          setLoadMappingConfig((prev) =>
                            mappings.map((m, i) => ({
                              ...(prev[i] ?? {
                                srcSchema: getMappingSourceSchemaDefault(m),
                                srcTable: m.source_table,
                                tgtSchema: ddlTargetSchema || "",
                                tgtTable: stagedTargetTable(m.target_object),
                              }),
                              included: false,
                            })),
                          )
                        }
                        className="ztd-btn ztd-btn-ghost ztd-btn-sm"
                      >
                        Select None
                      </button>
                      <span className="w-px h-4 bg-white/10" />
                      <span className="text-xs text-white/30">
                        Set load mode for all:
                      </span>
                      <button
                        onClick={() =>
                          setMappings((prev) =>
                            prev.map((mm) => ({
                              ...mm,
                              load_mode: "full",
                              incremental_column: undefined,
                            })),
                          )
                        }
                        title="Sets every table's load mode to Full (read every row every run)."
                        className="ztd-btn ztd-btn-ghost ztd-btn-sm"
                      >
                        All Full
                      </button>
                      <button
                        onClick={() =>
                          setMappings((prev) =>
                            prev.map((mm, mi) => {
                              // Same unsupported check as the per-row
                              // "Incremental" option below — joined,
                              // aggregate, custom-SQL, and MDM-lookup
                              // mappings can't drive an incremental filter,
                              // so bulk-setting leaves those untouched.
                              const unsupported =
                                (mm.source_tables?.length ?? 0) > 1 ||
                                !!mm.group_by_columns?.length ||
                                !!mm.custom_load_sql ||
                                !!loadMappingConfig[mi]?.mdmLookup?.lookupTable;
                              if (unsupported) return mm;
                              const firstMappedCol = mm.field_mappings.find(
                                (fm) =>
                                  fm.status !== "Unmapped" &&
                                  fm.source_column &&
                                  fm.target_field,
                              )?.source_column;
                              return {
                                ...mm,
                                load_mode: "incremental",
                                incremental_column:
                                  mm.incremental_column ?? firstMappedCol,
                              };
                            }),
                          )
                        }
                        title="Sets every table that supports it to Incremental load, keeping any incremental column already chosen (joined/aggregate/custom-SQL/MDM-lookup tables are left as Full since they can't support it)."
                        className="ztd-btn ztd-btn-ghost ztd-btn-sm"
                      >
                        All Incremental
                      </button>
                    </div>
                  ) : undefined
                }
              >
                {mappings.some(
                  (m) =>
                    (m.source_tables?.length ?? 0) > 1 &&
                    (m.joins?.length ?? 0) > 0,
                ) && (
                  <div className="mb-3 p-3 bg-brand-500/10 border border-brand-500/30 rounded-xl flex items-start gap-2">
                    <AlertTriangle
                      size={14}
                      className="text-brand-400 flex-shrink-0 mt-0.5"
                    />
                    <p className="text-xs text-brand-300">
                      One or more mappings below combine multiple source tables
                      via an auto-detected join. Data Load will execute the real
                      join query for these — review the join conditions in the
                      mapping step if a row count looks off.
                    </p>
                  </div>
                )}
                {mappings.some(
                  (m) =>
                    (m.source_tables?.length ?? 0) > 1 &&
                    (m.joins?.length ?? 0) === 0,
                ) && (
                  <div className="mb-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-start gap-2">
                    <AlertTriangle
                      size={14}
                      className="text-amber-400 flex-shrink-0 mt-0.5"
                    />
                    <p className="text-xs text-amber-300">
                      One or more mappings below combine multiple source tables
                      but have no join condition defined — Data Load can't build
                      a query for these. Regenerate them from the mapping step
                      before loading.
                    </p>
                  </div>
                )}
                {mappings.length === 0 ? (
                  <p className="text-sm text-white/40">
                    No STTM mappings available. Go back and generate STTM first.
                  </p>
                ) : (
                  <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
                    {/* Column headers */}
                    <div className="grid grid-cols-[auto_1fr_1fr_auto_1fr_1fr_auto] gap-2 items-center px-1">
                      <span className="text-xs text-white/30 font-medium">
                        Load
                      </span>
                      <span className="text-xs text-white/30 font-medium">
                        Source Schema
                      </span>
                      <span className="text-xs text-white/30 font-medium">
                        Source Table
                      </span>
                      <span />
                      <span className="text-xs text-white/30 font-medium">
                        Target Schema
                      </span>
                      <span className="text-xs text-white/30 font-medium">
                        Target Table
                      </span>
                      <span className="text-xs text-white/30 font-medium text-right">
                        Fields
                      </span>
                    </div>
                    {mappings.map((m, idx) => {
                      const cfg = loadMappingConfig[idx] ?? {
                        srcSchema: getMappingSourceSchemaDefault(m),
                        srcTable: m.source_table,
                        tgtSchema: ddlTargetSchema || "",
                        tgtTable: stagedTargetTable(m.target_object),
                      };
                      const mapped = m.field_mappings.filter(
                        (fm) =>
                          fm.status !== "Unmapped" &&
                          fm.source_column &&
                          fm.target_field,
                      );
                      const updateCfg = (patch: Partial<typeof cfg>) =>
                        setLoadMappingConfig((prev) => {
                          const next = [...prev];
                          next[idx] = { ...cfg, ...patch };
                          return next;
                        });
                      // Issue #599 — load_mode/incremental_column live on the
                      // mapping itself (agents/data_converter/patterns.py's
                      // TableObjectMapping), not the per-run cfg override,
                      // since a table's incremental tracking column doesn't
                      // change from run to run the way a row limit might.
                      const updateMapping = (patch: Partial<typeof m>) =>
                        setMappings((prev) =>
                          prev.map((mm, mi) =>
                            mi !== idx ? mm : { ...mm, ...patch },
                          ),
                        );
                      const incrementalUnsupported =
                        (m.source_tables?.length ?? 0) > 1 ||
                        !!m.group_by_columns?.length ||
                        !!m.custom_load_sql ||
                        !!cfg.mdmLookup?.lookupTable;
                      const lookup: MdmLookupConfig = cfg.mdmLookup ?? {
                        lookupSchema: cfg.srcSchema,
                        lookupTable: "",
                        sourceMatchField: "",
                        lookupMatchField: "",
                        lookupFields: "",
                        overrideFields: "",
                      };
                      const updateLookup = (patch: Partial<MdmLookupConfig>) =>
                        updateCfg({ mdmLookup: { ...lookup, ...patch } });
                      // Any edit to the custom connection's own fields
                      // (host/port/db/etc.) changes WHICH database is being
                      // scanned, so any schema/table/match-field/column
                      // selections made against the previous connection no
                      // longer mean anything and must be cleared — otherwise
                      // a "Table" selection could silently carry over to an
                      // identically-named-but-wrong table on the new
                      // connection.
                      const updateLookupConnection = (
                        patch: Partial<DbConnectionConfig>,
                      ) =>
                        updateLookup({
                          connection: {
                            ...(lookup.connection as DbConnectionConfig),
                            ...patch,
                          },
                          lookupSchema: "",
                          lookupTable: "",
                          lookupMatchField: "",
                          lookupFields: "",
                          overrideFields: "",
                        });
                      const lookupOpen = mdmLookupOpen.has(idx);
                      const toggleLookupOpen = () => {
                        setMdmLookupOpen((prev) => {
                          const next = new Set(prev);
                          next.has(idx) ? next.delete(idx) : next.add(idx);
                          return next;
                        });
                        if (lookup.lookupSchema)
                          ensureMdmSchemaScanned(
                            lookup.lookupSchema,
                            lookup.useCustomConnection
                              ? lookup.connection
                              : undefined,
                          );
                        // 2026-09-03, reported by user: "Match field on
                        // {source}..." showed no options at all whenever
                        // this project's source schema was never freshly
                        // scanned THIS session via the Source DB step (a
                        // resumed project, one loaded from mapping
                        // history, etc.) -- sourceTableColumns below relied
                        // solely on that scan's schemaResult. Piggybacks on
                        // the same lazy-scan cache the lookup side already
                        // uses so this dropdown is populated independently
                        // of whether Source DB's own scan ever ran. Skipped
                        // when schemaResult already has this table, so a
                        // project that DID scan this session doesn't pay
                        // for a redundant re-scan.
                        if (
                          cfg.srcSchema &&
                          !schemaResult?.tables.some(
                            (t) => t.table_name === cfg.srcTable,
                          )
                        ) {
                          ensureMdmSchemaScanned(cfg.srcSchema);
                        }
                      };
                      const loaderScriptIsOpen = loaderScriptOpen.has(idx);
                      const fetchLoaderScriptPreview = async () => {
                        const previewConnRef = getMappingSourceConnectionRef(m);
                        if (!previewConnRef) return;
                        setLoaderScriptPreview((prev) => ({
                          ...prev,
                          [idx]: "loading",
                        }));
                        try {
                          const result = await apiv2.converterPreviewLoaderSql({
                            sourceConnectionRef: previewConnRef,
                            mapping: m,
                            targetSchema: cfg.tgtSchema || "",
                            pair: {
                              srcSchema: cfg.srcSchema,
                              srcTable: cfg.srcTable,
                              tgtSchema: cfg.tgtSchema,
                              tgtTable: cfg.tgtTable,
                              // 2026-09-02, requested by user: the preview
                              // previously never sent the configured MDM
                              // lookup through at all, so it could never
                              // reflect the lookup join or its extra
                              // columns — same field names build_loader_sql_preview
                              // (service.py) and the real load job already
                              // both read from `pair`.
                              ...(cfg.mdmLookup?.lookupTable
                                ? {
                                    mdmLookupSchema: cfg.mdmLookup.lookupSchema,
                                    mdmLookupTable: cfg.mdmLookup.lookupTable,
                                    mdmSourceMatchField:
                                      cfg.mdmLookup.sourceMatchField,
                                    mdmLookupMatchField:
                                      cfg.mdmLookup.lookupMatchField,
                                    mdmLookupFields: cfg.mdmLookup.lookupFields,
                                    mdmOverrideFields:
                                      cfg.mdmLookup.overrideFields,
                                    ...(cfg.mdmLookup.sourceMode === "mdmRun"
                                      ? { mdmLookupIsGoldenJson: "true" }
                                      : {}),
                                    ...(cfg.mdmLookup.connection
                                      ? {
                                          mdmLookupConnectionJson:
                                            JSON.stringify(
                                              cfg.mdmLookup.connection,
                                            ),
                                        }
                                      : {}),
                                  }
                                : {}),
                            },
                            projectId,
                            targetPlatform: activeTargetPlatform,
                          });
                          setLoaderScriptPreview((prev) => ({
                            ...prev,
                            [idx]: result,
                          }));
                        } catch (e) {
                          // Surface the backend's actual detail message
                          // (e.g. a 400 explaining exactly which identifier/
                          // combination is invalid) instead of a generic
                          // "check the fields above" — that generic message
                          // gave no way to tell a real validation failure
                          // apart from a transient network error.
                          setLoaderScriptPreview((prev) => ({
                            ...prev,
                            [idx]: {
                              error:
                                e instanceof Error
                                  ? e.message
                                  : "Could not generate a preview for this table.",
                            },
                          }));
                        }
                      };
                      const toggleLoaderScriptOpen = () => {
                        const willOpen = !loaderScriptIsOpen;
                        setLoaderScriptOpen((prev) => {
                          const next = new Set(prev);
                          next.has(idx) ? next.delete(idx) : next.add(idx);
                          return next;
                        });
                        if (willOpen) void fetchLoaderScriptPreview();
                      };
                      // Schemas available to pick as the lookup schema — from the
                      // Source DB step's Test Connection, plus the current value
                      // in case it was set before that list was available. Only
                      // meaningful for the SAME-connection case — a custom
                      // connection has no pre-enumerated schema list, so its
                      // schema is typed directly (see the panel below).
                      const schemaOptions = Array.from(
                        new Set(
                          [
                            ...(connStatus?.schemas ?? []),
                            lookup.lookupSchema,
                          ].filter(Boolean),
                        ),
                      );
                      const lookupConnRef = lookup.useCustomConnection
                        ? lookup.connection
                        : undefined;
                      const lookupSchemaScan =
                        mdmSchemaCache[
                          mdmSchemaCacheKey(lookup.lookupSchema, lookupConnRef)
                        ];
                      const lookupTableOptions =
                        typeof lookupSchemaScan === "object"
                          ? lookupSchemaScan.tables.map((t) => t.table_name)
                          : [];
                      const lookupTableColumns =
                        typeof lookupSchemaScan === "object"
                          ? (lookupSchemaScan.tables.find(
                              (t) => t.table_name === lookup.lookupTable,
                            )?.columns ?? [])
                          : [];
                      const sourceTableColumns =
                        schemaResult?.tables.find(
                          (t) => t.table_name === cfg.srcTable,
                        )?.columns ??
                        (typeof mdmSchemaCache[cfg.srcSchema] === "object"
                          ? (
                              mdmSchemaCache[
                                cfg.srcSchema
                              ] as SchemaExtractResult
                            ).tables.find((t) => t.table_name === cfg.srcTable)
                              ?.columns
                          : undefined) ??
                        [];
                      // The match field is already used for the join and (almost
                      // always) has no equivalent column on the target table —
                      // exclude it here so it can't be accidentally selected as
                      // an "extra" column to load, which is what caused an
                      // invalid-identifier insert error previously.
                      //
                      // Split the remaining lookup columns into two buckets: a
                      // column whose name matches an already-mapped target
                      // field is an "override" candidate (its value can replace
                      // the source's for that field, golden-record-wins); any
                      // other column is a pure "extra" addition with no source
                      // equivalent. A column only ever appears in one bucket.
                      const nonMatchLookupColumns = lookupTableColumns.filter(
                        (c) => c.name !== lookup.lookupMatchField,
                      );
                      const mappedTargetFields = new Set(
                        mapped.map((fm) => fm.target_field.toUpperCase()),
                      );
                      const overridableLookupFieldOptions =
                        nonMatchLookupColumns.filter((c) =>
                          mappedTargetFields.has(c.name.toUpperCase()),
                        );
                      const extraLookupFieldOptions =
                        nonMatchLookupColumns.filter(
                          (c) => !mappedTargetFields.has(c.name.toUpperCase()),
                        );
                      const selectedLookupFields = new Set(
                        lookup.lookupFields
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
                      );
                      const selectedOverrideFields = new Set(
                        lookup.overrideFields
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
                      );
                      return (
                        <div
                          key={idx}
                          className={`rounded-lg border border-white/8 bg-white/2 overflow-hidden ${
                            cfg.included === false ? "opacity-40" : ""
                          }`}
                        >
                          <div className="grid grid-cols-[auto_1fr_1fr_auto_1fr_1fr_auto] gap-2 items-start p-3">
                            <input
                              type="checkbox"
                              checked={cfg.included ?? true}
                              onChange={(e) =>
                                updateCfg({ included: e.target.checked })
                              }
                              className="accent-brand-500 mt-1.5"
                              title="Include this table in the next Load Data run"
                            />
                            <div className="min-w-0">
                              <input
                                value={cfg.srcSchema}
                                onChange={(e) =>
                                  updateCfg({ srcSchema: e.target.value })
                                }
                                className="w-full px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                                placeholder="Schema"
                              />
                              {extraSourceConnections.length > 0 && (
                                <span
                                  title="Source connection this table loads from"
                                  className="mt-1 inline-flex max-w-full items-center truncate px-1.5 py-0.5 rounded text-[10px] bg-white/5 text-white/40 border border-white/10"
                                >
                                  {getMappingSourceConnectionInfo(m)?.label ||
                                    sourceConnLabel ||
                                    "Primary connection"}
                                </span>
                              )}
                            </div>
                            <input
                              value={cfg.srcTable}
                              onChange={(e) =>
                                updateCfg({ srcTable: e.target.value })
                              }
                              className="px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                              placeholder="Table"
                            />
                            <ArrowRight
                              size={12}
                              className="text-white/20 mx-1 mt-1.5"
                            />
                            <input
                              value={cfg.tgtSchema}
                              onChange={(e) =>
                                updateCfg({ tgtSchema: e.target.value })
                              }
                              className="px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-brand-300 font-mono placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                              placeholder="Schema"
                            />
                            <input
                              value={cfg.tgtTable}
                              onChange={(e) =>
                                updateCfg({ tgtTable: e.target.value })
                              }
                              className="px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-brand-300 font-mono placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                              placeholder="Table"
                            />
                            <span className="text-xs text-white/40 text-right pr-1 mt-1.5">
                              {mapped.length}f
                            </span>
                          </div>
                          <div className="px-3 pb-3">
                            {loadSourceMode === "files" && (
                              <div className="flex items-center gap-2 flex-wrap pb-3 mb-3 border-b border-white/8">
                                {cfg.sourceFile ? (
                                  <>
                                    <span className="flex items-center gap-1 text-xs text-green-400">
                                      <CheckCircle2 size={12} />{" "}
                                      {cfg.sourceFile.filename}
                                      {cfg.sourceFileFields
                                        ? ` — ${cfg.sourceFileFields.length} column(s) detected`
                                        : ""}
                                    </span>
                                    {fileUploadNotice?.idx === idx && (
                                      <span className="flex items-center gap-1 text-[11px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded px-1.5 py-0.5 animate-fade-in">
                                        <CheckCircle2 size={10} />
                                        Uploaded "{fileUploadNotice.filename}" —
                                        re-inspected,{" "}
                                        {fileUploadNotice.fieldCount} column(s)
                                        detected
                                      </span>
                                    )}
                                    {/* Resolved rows (via Auto-Fill or a
                                    prior manual choice) don't need a
                                    full-size button drawing the eye —
                                    override is still one click away. */}
                                    <label className="text-[11px] text-white/40 hover:text-white/70 underline cursor-pointer">
                                      Change
                                      <input
                                        type="file"
                                        // .xlsx/.xls are auto-converted to CSV
                                        // server-side -- see
                                        // handleChooseSourceFileForRow /
                                        // DataConverterService.upload_source_file.
                                        accept=".csv,.tsv,.json,.jsonl,.parquet,.xlsx,.xls"
                                        className="hidden"
                                        onChange={(e) => {
                                          const f = e.target.files?.[0];
                                          e.target.value = "";
                                          if (f)
                                            void handleChooseSourceFileForRow(
                                              idx,
                                              f,
                                            );
                                        }}
                                      />
                                    </label>
                                  </>
                                ) : (
                                  <>
                                    <label className="ztd-btn ztd-btn-secondary ztd-btn-sm cursor-pointer">
                                      <Upload size={12} />
                                      Choose File
                                      <input
                                        type="file"
                                        accept=".csv,.tsv,.json,.jsonl,.parquet,.xlsx,.xls"
                                        className="hidden"
                                        onChange={(e) => {
                                          const f = e.target.files?.[0];
                                          e.target.value = "";
                                          if (f)
                                            void handleChooseSourceFileForRow(
                                              idx,
                                              f,
                                            );
                                        }}
                                      />
                                    </label>
                                    <span className="text-xs text-red-400">
                                      No source file chosen yet
                                      {m.source_file
                                        ? ` — this table's data was already captured when its source was crawled (originally: ${m.source_file}); use "Auto-Fill Source Files from Mapping" above instead of choosing files one at a time.`
                                        : ""}
                                    </span>
                                  </>
                                )}
                              </div>
                            )}
                            <div className="flex items-center gap-4 flex-wrap">
                              <button
                                onClick={toggleLookupOpen}
                                className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                              >
                                <Link size={12} />
                                MDM Lookup
                                {cfg.mdmLookup
                                  ? cfg.mdmLookup.sourceMode === "mdmRun"
                                    ? " — configured (MDM Run)"
                                    : " — configured"
                                  : ""}
                                {lookupOpen ? (
                                  <ChevronDown size={12} />
                                ) : (
                                  <ChevronRight size={12} />
                                )}
                              </button>
                              {loadSourceMode !== "files" && (
                                <button
                                  onClick={toggleLoaderScriptOpen}
                                  disabled={!sourceConnectionId}
                                  title={
                                    sourceConnectionId
                                      ? "View (and, via the filter/column controls, tweak) the exact SQL this table's load will run"
                                      : "Connect a source database first"
                                  }
                                  className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                                >
                                  <FileCode size={12} />
                                  Loader Script
                                  {loaderScriptIsOpen ? (
                                    <ChevronDown size={12} />
                                  ) : (
                                    <ChevronRight size={12} />
                                  )}
                                </button>
                              )}
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs text-white/30">
                                  Rows:
                                </span>
                                <select
                                  value={cfg.rowLimit ?? "default"}
                                  onChange={(e) =>
                                    updateCfg({
                                      rowLimit: e.target
                                        .value as (typeof cfg)["rowLimit"],
                                    })
                                  }
                                  className="px-2 py-1 bg-surface-400/50 border border-white/10 rounded text-xs text-white focus:outline-none focus:border-brand-500/50"
                                >
                                  <option value="default">
                                    Use default (
                                    {loadRowLimit === "all"
                                      ? "all"
                                      : `first ${loadRowCount}`}
                                    )
                                  </option>
                                  <option value="all">All records</option>
                                  <option value="limited">
                                    First N records
                                  </option>
                                </select>
                                {cfg.rowLimit === "limited" && (
                                  <input
                                    type="number"
                                    min={1}
                                    value={cfg.rowCount ?? 100}
                                    onChange={(e) =>
                                      updateCfg({
                                        rowCount: Math.max(
                                          1,
                                          Number(e.target.value),
                                        ),
                                      })
                                    }
                                    className="w-20 px-2 py-1 bg-surface-400/50 border border-white/10 rounded text-xs text-white text-center focus:outline-none focus:border-brand-500/50"
                                  />
                                )}
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs text-white/30">
                                  Truncate:
                                </span>
                                <select
                                  value={cfg.truncate ?? "default"}
                                  onChange={(e) =>
                                    updateCfg({
                                      truncate: e.target
                                        .value as (typeof cfg)["truncate"],
                                    })
                                  }
                                  title="Clear this table's existing rows before loading, so a re-run doesn't append duplicates."
                                  className="px-2 py-1 bg-surface-400/50 border border-white/10 rounded text-xs text-white focus:outline-none focus:border-brand-500/50"
                                >
                                  <option value="default">
                                    Use default (
                                    {loadTruncateBeforeLoad
                                      ? "truncate"
                                      : "append"}
                                    )
                                  </option>
                                  <option value="yes">
                                    Truncate before load
                                  </option>
                                  <option value="no">
                                    Append (keep existing)
                                  </option>
                                </select>
                              </div>
                              {loadOrderMode === "staged" && (
                                <div className="flex items-center gap-1.5">
                                  <span className="text-xs text-white/30">
                                    Load stage:
                                  </span>
                                  <input
                                    type="number"
                                    min={1}
                                    value={cfg.loadStage ?? idx + 1}
                                    onChange={(e) =>
                                      updateCfg({
                                        loadStage: Math.max(
                                          1,
                                          Number(e.target.value),
                                        ),
                                      })
                                    }
                                    title="Tables sharing the same stage number load concurrently; stages run in ascending order."
                                    className="w-16 px-2 py-1 bg-surface-400/50 border border-white/10 rounded text-xs text-white text-center focus:outline-none focus:border-brand-500/50"
                                  />
                                </div>
                              )}
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs text-white/30">
                                  Load mode:
                                </span>
                                <select
                                  value={m.load_mode ?? "full"}
                                  disabled={incrementalUnsupported}
                                  onChange={(e) => {
                                    const mode = e.target.value as
                                      | "full"
                                      | "incremental";
                                    updateMapping({
                                      load_mode: mode,
                                      incremental_column:
                                        mode === "incremental"
                                          ? (m.incremental_column ??
                                            mapped[0]?.source_column)
                                          : undefined,
                                    });
                                  }}
                                  title={
                                    incrementalUnsupported
                                      ? "Incremental load isn't supported for a joined, aggregate (GROUP BY), custom-SQL, or MDM-lookup mapping — switch this table to Full load."
                                      : "Full: read every row every run. Incremental: only read rows newer than the last run (append-only — never detects updates or deletes to already-loaded rows)."
                                  }
                                  className="px-2 py-1 bg-surface-400/50 border border-white/10 rounded text-xs text-white focus:outline-none focus:border-brand-500/50 disabled:opacity-40"
                                >
                                  <option value="full">Full</option>
                                  <option value="incremental">
                                    Incremental
                                  </option>
                                </select>
                              </div>
                            </div>
                            {m.load_mode === "incremental" &&
                              !incrementalUnsupported && (
                                <div className="mt-3 p-3 rounded-lg border border-amber-500/20 bg-amber-500/5 space-y-2">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs text-white/50">
                                      Incremental column (must increase every
                                      run — a timestamp or auto-incrementing
                                      id):
                                    </span>
                                    <select
                                      value={m.incremental_column ?? ""}
                                      onChange={(e) =>
                                        updateMapping({
                                          incremental_column: e.target.value,
                                        })
                                      }
                                      className="px-2 py-1 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono focus:outline-none focus:border-brand-500/50"
                                    >
                                      <option value="" disabled>
                                        Select a mapped source column…
                                      </option>
                                      {mapped.map((fm) => (
                                        <option
                                          key={fm.source_column}
                                          value={fm.source_column}
                                        >
                                          {fm.source_column}
                                        </option>
                                      ))}
                                    </select>
                                    <button
                                      onClick={() =>
                                        void handleResetLoadWatermark(
                                          idx,
                                          cfg.tgtTable,
                                        )
                                      }
                                      disabled={resettingWatermark.has(idx)}
                                      title="Deletes this table's saved watermark so the next run re-reads every row from scratch."
                                      className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                                    >
                                      {resettingWatermark.has(idx) ? (
                                        <Loader2
                                          size={11}
                                          className="animate-spin"
                                        />
                                      ) : (
                                        <RefreshCw size={11} />
                                      )}
                                      Force Full Reload (next run)
                                    </button>
                                  </div>
                                  {(() => {
                                    const wm = loadWatermarks[idx];
                                    if (wm === undefined) return null;
                                    if (wm === null) {
                                      return (
                                        <p className="text-[11px] text-white/40">
                                          No saved watermark yet — the next run
                                          reads every row.
                                        </p>
                                      );
                                    }
                                    const columnChanged =
                                      wm.incremental_column.toUpperCase() !==
                                      (
                                        m.incremental_column ?? ""
                                      ).toUpperCase();
                                    return (
                                      <p className="text-[11px] text-white/50">
                                        Currently loaded up to{" "}
                                        <span className="font-mono text-white/70">
                                          {formatWatermarkValue(wm)}
                                        </span>{" "}
                                        on <code>{wm.incremental_column}</code>{" "}
                                        (
                                        {new Date(
                                          wm.last_run_at,
                                        ).toLocaleString()}
                                        ).
                                        {columnChanged && (
                                          <span className="text-amber-300/80">
                                            {" "}
                                            Incremental column changed since
                                            then — the next run reads every row
                                            from scratch.
                                          </span>
                                        )}
                                      </p>
                                    );
                                  })()}
                                  <p className="text-[11px] text-amber-300/70">
                                    Append-only: only reads rows where this
                                    column is greater than the last run's
                                    highest value. Never detects updates to
                                    already-loaded rows or deletes — use "Force
                                    Full Reload" if the source data changed in a
                                    way incremental loading can't see.
                                  </p>
                                </div>
                              )}
                            {lookupOpen && (
                              <div className="mt-3 p-3 rounded-lg border border-white/10 bg-surface-400/30 space-y-2">
                                <p className="text-xs text-white/40">
                                  Loads every {cfg.srcTable || "source"} row
                                  regardless of whether it has a match in the
                                  lookup table. For any field you mark as
                                  "override" below, the lookup table's (golden
                                  record) value is used when a match exists —
                                  the source's own value is kept otherwise. You
                                  can also pull extra columns (with no source
                                  equivalent) from the lookup table; the target
                                  table must already have matching columns for
                                  anything you list here.
                                </p>
                                <div className="flex items-center gap-2 text-xs text-white/50">
                                  <span className="text-white/30">
                                    Lookup source:
                                  </span>
                                  <div className="inline-flex rounded border border-white/10 overflow-hidden">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        // Only clear the connection/table
                                        // fields when actually leaving
                                        // "MDM Agent Run" mode -- otherwise
                                        // re-clicking "Manual" while already
                                        // in manual mode would wipe out
                                        // whatever the user typed in by hand.
                                        const wasMdmRun =
                                          lookup.sourceMode === "mdmRun";
                                        updateLookup({
                                          sourceMode: "manual",
                                          mdmRunId: undefined,
                                          ...(wasMdmRun
                                            ? {
                                                useCustomConnection: false,
                                                connection: undefined,
                                                lookupSchema: cfg.srcSchema,
                                                lookupTable: "",
                                                lookupMatchField: "",
                                                lookupFields: "",
                                                overrideFields: "",
                                              }
                                            : {}),
                                        });
                                      }}
                                      className={`px-2 py-1 ${
                                        !lookup.sourceMode ||
                                        lookup.sourceMode === "manual"
                                          ? "bg-brand-500/20 text-brand-300"
                                          : "bg-surface-400/30 text-white/40"
                                      }`}
                                    >
                                      Manual
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        updateLookup({ sourceMode: "mdmRun" });
                                        ensureMdmOracleOutputs();
                                      }}
                                      className={`px-2 py-1 ${
                                        lookup.sourceMode === "mdmRun"
                                          ? "bg-brand-500/20 text-brand-300"
                                          : "bg-surface-400/30 text-white/40"
                                      }`}
                                    >
                                      MDM Agent Run
                                    </button>
                                  </div>
                                </div>
                                {lookup.sourceMode === "mdmRun" ? (
                                  <div className="space-y-2">
                                    <p className="text-xs text-white/40">
                                      Auto-discovers a completed MDM agent run's
                                      Oracle golden-record push — connection,
                                      schema, and table are filled in for you.
                                      That table only has cluster_id/golden_json
                                      columns, so the match/override/extra
                                      fields below are JSON key names inside the
                                      golden record (e.g. "email"), typed by
                                      hand.
                                    </p>
                                    <select
                                      value={lookup.mdmRunId ?? ""}
                                      onChange={(e) => {
                                        const run = (
                                          Array.isArray(mdmOracleOutputs)
                                            ? mdmOracleOutputs
                                            : []
                                        ).find(
                                          (o) => o.run_id === e.target.value,
                                        );
                                        if (!run) {
                                          updateLookup({ mdmRunId: undefined });
                                          return;
                                        }
                                        updateLookup({
                                          mdmRunId: run.run_id,
                                          useCustomConnection: true,
                                          connection: {
                                            db_type: "oracle",
                                            host: run.host ?? "",
                                            port: run.port ?? 1521,
                                            database: "",
                                            service_name:
                                              run.service_name ?? "",
                                            username:
                                              lookup.connection?.username ?? "",
                                            password:
                                              lookup.connection?.password ?? "",
                                            schema_name: run.schema_name ?? "",
                                          },
                                          lookupSchema: run.schema_name ?? "",
                                          lookupTable: run.table_name ?? "",
                                          lookupMatchField: "",
                                          lookupFields: "",
                                          overrideFields: "",
                                        });
                                      }}
                                      className="w-full px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono focus:outline-none focus:border-brand-500/50"
                                    >
                                      <option value="">
                                        {mdmOracleOutputs === "loading"
                                          ? "Loading MDM runs…"
                                          : mdmOracleOutputs === "error"
                                            ? "Failed to load MDM runs"
                                            : "Select a completed MDM run…"}
                                      </option>
                                      {(Array.isArray(mdmOracleOutputs)
                                        ? mdmOracleOutputs
                                        : []
                                      ).map((o) => (
                                        <option key={o.run_id} value={o.run_id}>
                                          {o.run_name} ({o.pushed_count}{" "}
                                          records)
                                        </option>
                                      ))}
                                    </select>
                                    {lookup.mdmRunId && (
                                      <p className="text-[10px] text-white/30 font-mono">
                                        {lookup.connection?.host}:
                                        {lookup.connection?.port}/
                                        {lookup.connection?.service_name} —{" "}
                                        {lookup.lookupSchema}.
                                        {lookup.lookupTable}
                                      </p>
                                    )}
                                    <div className="grid grid-cols-2 gap-2">
                                      <input
                                        value={
                                          lookup.connection?.username ?? ""
                                        }
                                        onChange={(e) =>
                                          updateLookupConnection({
                                            username: e.target.value,
                                          })
                                        }
                                        placeholder="Username"
                                        className="px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                                      />
                                      <input
                                        type="password"
                                        value={
                                          lookup.connection?.password ?? ""
                                        }
                                        onChange={(e) =>
                                          updateLookupConnection({
                                            password: e.target.value,
                                          })
                                        }
                                        placeholder="Password"
                                        autoComplete="new-password"
                                        className="px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <label className="flex items-center gap-2 text-xs text-white/50">
                                      <input
                                        type="checkbox"
                                        checked={!!lookup.useCustomConnection}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          updateLookup({
                                            useCustomConnection: checked,
                                            lookupSchema: "",
                                            lookupTable: "",
                                            lookupMatchField: "",
                                            lookupFields: "",
                                            overrideFields: "",
                                            connection: checked
                                              ? (lookup.connection ?? {
                                                  db_type: "postgres",
                                                  host: "",
                                                  port: 0,
                                                  database: "",
                                                  username: "",
                                                  password: "",
                                                  schema_name: "",
                                                })
                                              : undefined,
                                          });
                                        }}
                                        className="accent-brand-500"
                                      />
                                      Lookup table is in a different database
                                      connection
                                    </label>
                                    {lookup.useCustomConnection && (
                                      <div className="grid grid-cols-3 gap-2">
                                        <select
                                          value={
                                            lookup.connection?.db_type ??
                                            "postgres"
                                          }
                                          onChange={(e) =>
                                            updateLookupConnection({
                                              db_type: e.target
                                                .value as DbConnectionConfig["db_type"],
                                            })
                                          }
                                          className="px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono focus:outline-none focus:border-brand-500/50"
                                        >
                                          <option value="postgres">
                                            PostgreSQL
                                          </option>
                                          <option value="mysql">MySQL</option>
                                          <option value="mssql">
                                            SQL Server
                                          </option>
                                          <option value="oracle">Oracle</option>
                                          <option value="synapse">
                                            Synapse
                                          </option>
                                          <option value="snowflake">
                                            Snowflake
                                          </option>
                                          <option value="databricks">
                                            Databricks
                                          </option>
                                        </select>
                                        <input
                                          value={lookup.connection?.host ?? ""}
                                          onChange={(e) =>
                                            updateLookupConnection({
                                              host: e.target.value,
                                            })
                                          }
                                          placeholder="Host"
                                          className="px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                                        />
                                        <input
                                          type="number"
                                          value={lookup.connection?.port ?? 0}
                                          onChange={(e) =>
                                            updateLookupConnection({
                                              port: Number(e.target.value) || 0,
                                            })
                                          }
                                          placeholder="Port"
                                          className="px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                                        />
                                        <input
                                          value={
                                            lookup.connection?.database ?? ""
                                          }
                                          onChange={(e) =>
                                            updateLookupConnection({
                                              database: e.target.value,
                                            })
                                          }
                                          placeholder="Database"
                                          className="px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                                        />
                                        <input
                                          value={
                                            lookup.connection?.username ?? ""
                                          }
                                          onChange={(e) =>
                                            updateLookupConnection({
                                              username: e.target.value,
                                            })
                                          }
                                          placeholder="Username"
                                          className="px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                                        />
                                        <input
                                          type="password"
                                          value={
                                            lookup.connection?.password ?? ""
                                          }
                                          onChange={(e) =>
                                            updateLookupConnection({
                                              password: e.target.value,
                                            })
                                          }
                                          placeholder="Password"
                                          autoComplete="new-password"
                                          className="px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                                        />
                                      </div>
                                    )}
                                    <div className="grid grid-cols-2 gap-2">
                                      {lookup.useCustomConnection ? (
                                        <input
                                          value={lookup.lookupSchema}
                                          onChange={(e) =>
                                            updateLookup({
                                              lookupSchema: e.target.value,
                                              lookupTable: "",
                                              lookupMatchField: "",
                                              lookupFields: "",
                                              overrideFields: "",
                                            })
                                          }
                                          onBlur={(e) =>
                                            ensureMdmSchemaScanned(
                                              e.target.value,
                                              lookup.connection,
                                            )
                                          }
                                          placeholder="Schema (on the connection above)"
                                          className="px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                                        />
                                      ) : (
                                        <select
                                          value={lookup.lookupSchema}
                                          onChange={(e) => {
                                            updateLookup({
                                              lookupSchema: e.target.value,
                                              lookupTable: "",
                                              lookupMatchField: "",
                                              lookupFields: "",
                                              overrideFields: "",
                                            });
                                            ensureMdmSchemaScanned(
                                              e.target.value,
                                            );
                                          }}
                                          className="px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono focus:outline-none focus:border-brand-500/50"
                                        >
                                          <option value="">
                                            Select schema…
                                          </option>
                                          {schemaOptions.map((s) => (
                                            <option key={s} value={s}>
                                              {s}
                                            </option>
                                          ))}
                                        </select>
                                      )}
                                      <select
                                        value={lookup.lookupTable}
                                        onChange={(e) =>
                                          updateLookup({
                                            lookupTable: e.target.value,
                                            lookupMatchField: "",
                                            lookupFields: "",
                                            overrideFields: "",
                                          })
                                        }
                                        disabled={!lookup.lookupSchema}
                                        className="px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono focus:outline-none focus:border-brand-500/50 disabled:opacity-40"
                                      >
                                        <option value="">
                                          {lookupSchemaScan === "loading"
                                            ? "Loading tables…"
                                            : lookupSchemaScan === "error"
                                              ? "Failed to load tables"
                                              : "Select table…"}
                                        </option>
                                        {lookupTableOptions.map((t) => (
                                          <option key={t} value={t}>
                                            {t}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  </>
                                )}
                                <div className="grid grid-cols-2 gap-2">
                                  <select
                                    value={lookup.sourceMatchField}
                                    onChange={(e) =>
                                      updateLookup({
                                        sourceMatchField: e.target.value,
                                      })
                                    }
                                    className="px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono focus:outline-none focus:border-brand-500/50"
                                  >
                                    <option value="">
                                      Match field on {cfg.srcTable || "source"}…
                                    </option>
                                    {sourceTableColumns.map((c) => (
                                      <option key={c.name} value={c.name}>
                                        {c.name}
                                      </option>
                                    ))}
                                  </select>
                                  {lookup.sourceMode === "mdmRun" ? (
                                    <input
                                      value={lookup.lookupMatchField}
                                      onChange={(e) =>
                                        updateLookup({
                                          lookupMatchField: e.target.value,
                                        })
                                      }
                                      disabled={!lookup.lookupTable}
                                      placeholder='Golden record JSON key, e.g. "email"'
                                      className="px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono placeholder-white/20 focus:outline-none focus:border-brand-500/50 disabled:opacity-40"
                                    />
                                  ) : (
                                    <select
                                      value={lookup.lookupMatchField}
                                      onChange={(e) =>
                                        updateLookup({
                                          lookupMatchField: e.target.value,
                                        })
                                      }
                                      disabled={!lookup.lookupTable}
                                      className="px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono focus:outline-none focus:border-brand-500/50 disabled:opacity-40"
                                    >
                                      <option value="">
                                        Match field on lookup table…
                                      </option>
                                      {lookupTableColumns.map((c) => (
                                        <option key={c.name} value={c.name}>
                                          {c.name}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                </div>
                                <div>
                                  <label className="block text-xs text-white/30 mb-1">
                                    Override fields — use the lookup table's
                                    value instead of the source's when matched
                                  </label>
                                  {lookup.sourceMode === "mdmRun" ? (
                                    <input
                                      value={lookup.overrideFields}
                                      onChange={(e) =>
                                        updateLookup({
                                          overrideFields: e.target.value,
                                        })
                                      }
                                      disabled={!lookup.lookupTable}
                                      placeholder="Comma-separated golden record JSON keys, e.g. email, phone"
                                      className="w-full px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono placeholder-white/20 focus:outline-none focus:border-brand-500/50 disabled:opacity-40"
                                    />
                                  ) : (
                                    <select
                                      multiple
                                      value={Array.from(selectedOverrideFields)}
                                      onChange={(e) =>
                                        updateLookup({
                                          overrideFields: Array.from(
                                            e.target.selectedOptions,
                                          )
                                            .map((o) => o.value)
                                            .join(", "),
                                        })
                                      }
                                      disabled={!lookup.lookupTable}
                                      size={Math.min(
                                        5,
                                        Math.max(
                                          2,
                                          overridableLookupFieldOptions.length,
                                        ),
                                      )}
                                      className="w-full px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono focus:outline-none focus:border-brand-500/50 disabled:opacity-40"
                                    >
                                      {overridableLookupFieldOptions.length ===
                                        0 && (
                                        <option value="" disabled>
                                          No lookup columns match an already
                                          mapped target field
                                        </option>
                                      )}
                                      {overridableLookupFieldOptions.map(
                                        (c) => (
                                          <option key={c.name} value={c.name}>
                                            {c.name}
                                          </option>
                                        ),
                                      )}
                                    </select>
                                  )}
                                </div>
                                <div>
                                  <label className="block text-xs text-white/30 mb-1">
                                    Optional: extra lookup columns to load into
                                    the target row
                                  </label>
                                  {lookup.sourceMode === "mdmRun" ? (
                                    <input
                                      value={lookup.lookupFields}
                                      onChange={(e) =>
                                        updateLookup({
                                          lookupFields: e.target.value,
                                        })
                                      }
                                      disabled={!lookup.lookupTable}
                                      placeholder="Comma-separated golden record JSON keys, e.g. ssn, dob"
                                      className="w-full px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono placeholder-white/20 focus:outline-none focus:border-brand-500/50 disabled:opacity-40"
                                    />
                                  ) : (
                                    <select
                                      multiple
                                      value={Array.from(selectedLookupFields)}
                                      onChange={(e) =>
                                        updateLookup({
                                          lookupFields: Array.from(
                                            e.target.selectedOptions,
                                          )
                                            .map((o) => o.value)
                                            .join(", "),
                                        })
                                      }
                                      disabled={!lookup.lookupTable}
                                      size={Math.min(
                                        5,
                                        Math.max(
                                          2,
                                          extraLookupFieldOptions.length,
                                        ),
                                      )}
                                      className="w-full px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-xs text-white font-mono focus:outline-none focus:border-brand-500/50 disabled:opacity-40"
                                    >
                                      {extraLookupFieldOptions.map((c) => (
                                        <option key={c.name} value={c.name}>
                                          {c.name}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                </div>
                                {cfg.mdmLookup && (
                                  <button
                                    onClick={() =>
                                      updateCfg({ mdmLookup: undefined })
                                    }
                                    className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                                  >
                                    <X size={12} /> Remove lookup
                                  </button>
                                )}
                              </div>
                            )}
                            {loaderScriptIsOpen &&
                              loadSourceMode !== "files" && (
                                <div className="mt-3 p-3 rounded-lg border border-white/10 bg-surface-400/30 space-y-3">
                                  <p className="text-xs text-white/40">
                                    Preview of the exact SQL this table's load
                                    will run — computed server-side from this
                                    row's schema/table/filter fields above, so
                                    it can never silently drift from what
                                    actually executes. The SELECT below is
                                    editable: save a custom version to fully
                                    replace the auto-generated one for this
                                    table (it must still return exactly the same{" "}
                                    {mapped.length} column
                                    {mapped.length !== 1 ? "s" : ""}, in the
                                    same order, as the field mapping below).
                                    Re-validated server-side before every load —
                                    a single SELECT/WITH statement only, no
                                    DML/DDL keywords, no statement separators or
                                    comments.
                                  </p>
                                  <div className="w-72">
                                    <ExpandableTextField
                                      value={m.where_clause ?? ""}
                                      onChange={(v) =>
                                        setMappings((prev) =>
                                          prev.map((mm, mi) =>
                                            mi !== idx
                                              ? mm
                                              : { ...mm, where_clause: v },
                                          ),
                                        )
                                      }
                                      title={`Filter — ${m.target_label || m.target_object}`}
                                      placeholder="No filter — loads all rows"
                                      hint={
                                        'Optional SQL WHERE-clause fragment (no leading "WHERE"), ' +
                                        "applied to this table's extract at Load to Target."
                                      }
                                    />
                                  </div>
                                  {loaderScriptPreview[idx] === "loading" ? (
                                    <div className="flex items-center gap-2 text-xs text-white/40">
                                      <Loader2
                                        size={12}
                                        className="animate-spin"
                                      />
                                      Generating preview…
                                    </div>
                                  ) : loaderScriptPreview[idx] &&
                                    typeof loaderScriptPreview[idx] ===
                                      "object" &&
                                    "error" in loaderScriptPreview[idx] ? (
                                    <p className="text-xs text-red-400">
                                      {
                                        (
                                          loaderScriptPreview[idx] as {
                                            error: string;
                                          }
                                        ).error
                                      }
                                    </p>
                                  ) : loaderScriptPreview[idx] ? (
                                    <div className="space-y-2">
                                      <div>
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="text-[10px] uppercase tracking-wide text-white/30">
                                            SELECT (reads source rows) —
                                            editable
                                          </span>
                                          <div className="flex items-center gap-2">
                                            {m.custom_load_sql && (
                                              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-brand-500/15 text-brand-300">
                                                Custom
                                              </span>
                                            )}
                                            <button
                                              onClick={() =>
                                                navigator.clipboard.writeText(
                                                  m.custom_load_sql ||
                                                    formatSqlForDisplay(
                                                      (
                                                        loaderScriptPreview[
                                                          idx
                                                        ] as LoaderSqlPreviewOut
                                                      ).select_sql,
                                                    ),
                                                )
                                              }
                                              className="text-white/30 hover:text-white/60"
                                              title="Copy to clipboard"
                                            >
                                              <Copy size={11} />
                                            </button>
                                          </div>
                                        </div>
                                        <textarea
                                          value={
                                            m.custom_load_sql ||
                                            formatSqlForDisplay(
                                              (
                                                loaderScriptPreview[
                                                  idx
                                                ] as LoaderSqlPreviewOut
                                              ).select_sql,
                                            )
                                          }
                                          onChange={(e) =>
                                            setMappings((prev) =>
                                              prev.map((mm, mi) =>
                                                mi !== idx
                                                  ? mm
                                                  : {
                                                      ...mm,
                                                      custom_load_sql:
                                                        e.target.value,
                                                    },
                                              ),
                                            )
                                          }
                                          rows={8}
                                          spellCheck={false}
                                          className="w-full text-[11px] font-mono text-white/80 bg-black/20 border border-white/10 rounded-lg p-2 focus:outline-none focus:border-brand-500/50 resize-y"
                                        />
                                        <div className="flex items-center gap-2 mt-1.5">
                                          <button
                                            onClick={() =>
                                              void handleSaveLoaderScript(idx)
                                            }
                                            disabled={
                                              savingLoaderScript.has(idx) ||
                                              !m.custom_load_sql
                                            }
                                            className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                                          >
                                            {savingLoaderScript.has(idx) ? (
                                              <Loader2
                                                size={11}
                                                className="animate-spin"
                                              />
                                            ) : (
                                              <Check size={11} />
                                            )}
                                            Save Script
                                          </button>
                                          {m.custom_load_sql && (
                                            <button
                                              onClick={() => {
                                                setMappings((prev) =>
                                                  prev.map((mm, mi) =>
                                                    mi !== idx
                                                      ? mm
                                                      : {
                                                          ...mm,
                                                          custom_load_sql: "",
                                                        },
                                                  ),
                                                );
                                                void handleSaveLoaderScript(
                                                  idx,
                                                );
                                              }}
                                              className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                                            >
                                              <RefreshCw size={11} /> Reset to
                                              Auto-Generated
                                            </button>
                                          )}
                                          {loaderScriptSavedAt[idx] && (
                                            <span className="flex items-center gap-1 text-xs text-green-400">
                                              <CheckCircle2 size={12} /> Saved
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div>
                                        <div className="flex items-center justify-between mb-1">
                                          <span className="text-[10px] uppercase tracking-wide text-white/30">
                                            INSERT (writes each row to target)
                                          </span>
                                          <button
                                            onClick={() =>
                                              navigator.clipboard.writeText(
                                                formatSqlForDisplay(
                                                  (
                                                    loaderScriptPreview[
                                                      idx
                                                    ] as LoaderSqlPreviewOut
                                                  ).insert_sql,
                                                ),
                                              )
                                            }
                                            className="text-white/30 hover:text-white/60"
                                            title="Copy to clipboard"
                                          >
                                            <Copy size={11} />
                                          </button>
                                        </div>
                                        <pre className="text-[11px] font-mono text-white/70 bg-black/20 rounded-lg p-2 overflow-x-auto whitespace-pre-wrap break-all">
                                          {formatSqlForDisplay(
                                            (
                                              loaderScriptPreview[
                                                idx
                                              ] as LoaderSqlPreviewOut
                                            ).insert_sql,
                                          )}
                                        </pre>
                                      </div>
                                    </div>
                                  ) : null}
                                  <button
                                    onClick={() =>
                                      void fetchLoaderScriptPreview()
                                    }
                                    className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                                  >
                                    <RefreshCw size={12} /> Refresh Preview
                                  </button>
                                </div>
                              )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </SectionCard>

              {/* Row Limit & Load Order */}
              <SectionCard title="Load Options">
                <div className="space-y-5">
                  <div>
                    <p className="text-xs text-white/40 mb-2">
                      Default row limit — applies to every table below that
                      hasn't been given its own row-limit override (see "Rows"
                      in Mapping Summary above).
                    </p>
                    <div className="flex items-center gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="rowLimit"
                          checked={loadRowLimit === "all"}
                          onChange={() => setLoadRowLimit("all")}
                          className="accent-brand-500"
                        />
                        <span className="text-sm text-white/70">
                          Load all records
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="rowLimit"
                          checked={loadRowLimit === "limited"}
                          onChange={() => setLoadRowLimit("limited")}
                          className="accent-brand-500"
                        />
                        <span className="text-sm text-white/70">
                          Load first
                        </span>
                      </label>
                      {loadRowLimit === "limited" && (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={1}
                            value={loadRowCount}
                            onChange={(e) =>
                              setLoadRowCount(
                                Math.max(1, Number(e.target.value)),
                              )
                            }
                            className="w-24 px-2 py-1.5 bg-surface-400/50 border border-white/10 rounded text-sm text-white text-center focus:outline-none focus:border-brand-500/50"
                          />
                          <span className="text-sm text-white/50">
                            records per table
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-white/40 mb-2">
                      Truncate target tables — clears out any rows already in a
                      target table before loading, so re-running a load doesn't
                      append duplicates on top of the last run (overridable per
                      table via "Truncate" in Mapping Summary above).
                    </p>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={loadTruncateBeforeLoad}
                        onChange={(e) =>
                          setLoadTruncateBeforeLoad(e.target.checked)
                        }
                        className="accent-brand-500"
                      />
                      <span className="text-sm text-white/70">
                        Truncate every target table before loading
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer mt-2">
                      <input
                        type="checkbox"
                        checked={loadTruncateCascade}
                        disabled={!loadTruncateBeforeLoad}
                        onChange={(e) =>
                          setLoadTruncateCascade(e.target.checked)
                        }
                        className="accent-brand-500"
                      />
                      <span
                        className={`text-sm ${loadTruncateBeforeLoad ? "text-white/70" : "text-white/30"}`}
                      >
                        Cascade truncate (also clears dependent rows in
                        FK-referencing tables — Oracle &amp; PostgreSQL targets
                        only, ignored otherwise)
                      </span>
                    </label>
                  </div>

                  <div>
                    <p className="text-xs text-white/40 mb-2">
                      Validate only — runs the real insert against every target
                      table, so genuine type/length/null/FK constraint errors
                      surface exactly as they would in a real load, but every
                      change is rolled back afterward. Nothing is written to the
                      target — run this first if you want to check for problems
                      before committing to a real load.
                    </p>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={loadDryRun}
                        onChange={(e) => setLoadDryRun(e.target.checked)}
                        className="accent-brand-500"
                      />
                      <span className="text-sm text-white/70">
                        Validate only (dry run — no data will be written)
                      </span>
                    </label>
                  </div>

                  <div>
                    <p className="text-xs text-white/40 mb-2">
                      Load order — controls which tables load at the same time
                      vs. one after another.
                    </p>
                    <div className="flex items-center gap-6 flex-wrap">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="loadOrderMode"
                          checked={loadOrderMode === "sequential"}
                          onChange={() => setLoadOrderMode("sequential")}
                          className="accent-brand-500"
                        />
                        <span className="text-sm text-white/70">
                          Sequential
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="loadOrderMode"
                          checked={loadOrderMode === "parallel"}
                          onChange={() => setLoadOrderMode("parallel")}
                          className="accent-brand-500"
                        />
                        <span className="text-sm text-white/70">
                          All in parallel
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="loadOrderMode"
                          checked={loadOrderMode === "staged"}
                          onChange={() => setLoadOrderMode("staged")}
                          className="accent-brand-500"
                        />
                        <span className="text-sm text-white/70">
                          Custom stages
                        </span>
                      </label>
                    </div>
                    <p className="text-xs text-white/30 mt-2">
                      {loadOrderMode === "sequential" &&
                        "Tables load one at a time. When the target schema has FK/lookup relationships between the selected tables, they're ordered automatically so referenced (parent) tables load before the tables that reference them — otherwise they load in the order shown in Mapping Summary above."}
                      {loadOrderMode === "parallel" &&
                        "Every table loads at the same time — only use this when the tables have no dependency on each other (e.g. no MDM lookup against a table also being loaded)."}
                      {loadOrderMode === "staged" &&
                        'Set a "Load stage" number for each table in Mapping Summary above. Stages run in ascending order; every table sharing a stage number loads concurrently within that stage — use this to keep dependent tables (e.g. a parent loaded before a child that looks it up) in order while letting independent tables run in parallel.'}
                    </p>
                  </div>
                </div>
              </SectionCard>

              {/* Progress bar while the background load job is running */}
              {loading &&
                loadJobId &&
                (() => {
                  // 2026-09-08, requested by user: previously this only
                  // showed one combined line/bar for however many tables
                  // were running at once (current_table is a comma-joined
                  // list in parallel/staged mode) — no way to tell which
                  // specific tables were done, in flight, or not started
                  // yet. Reconstructed per-table from data already on the
                  // wire: table_results (populated as each table finishes)
                  // for done/failed rows, current_table for what's actively
                  // running right now — no live row count exists per
                  // in-flight table, only the aggregate below updates
                  // continuously — and everything else as queued.
                  const currentTableNames =
                    loadProgress?.current_tables ??
                    (loadProgress?.current_table
                      ? loadProgress.current_table.split(", ")
                      : []);
                  const plannedTables = mappings
                    .map((m, i) => ({ m, i }))
                    .filter(({ i }) => effectiveIncluded(i))
                    .map(({ m, i }) => ({
                      label:
                        m.source_tables && m.source_tables.length > 0
                          ? m.source_tables.join(" ⋈ ")
                          : m.source_table,
                      // current_table only ever reports the join's hub
                      // table (mapping.source_table), never the "A ⋈ B"
                      // display label above — matched separately below so
                      // a joined mapping doesn't wrongly show as "Queued"
                      // while it's actually loading.
                      hubLabel: m.source_table,
                      target:
                        loadMappingConfig[i]?.tgtTable ||
                        stagedTargetTable(m.target_object),
                    }));
                  return (
                    <SectionCard
                      title={
                        loadProgress?.dry_run
                          ? "Validation Progress"
                          : "Load Progress"
                      }
                    >
                      <div className="space-y-3">
                        <div className="text-xs text-white/60 break-words">
                          {loadProgress?.cancel_requested
                            ? "Cancelling — finishing current table…"
                            : loadProgress?.current_table
                              ? `${loadProgress.dry_run ? "Validating" : "Loading"} ${loadProgress.current_table}`
                              : "Starting…"}
                        </div>
                        <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                          <div
                            className="h-full bg-brand-500 transition-all duration-300"
                            style={{
                              width:
                                loadProgress && loadProgress.total_tables > 0
                                  ? `${Math.round((loadProgress.tables_done / loadProgress.total_tables) * 100)}%`
                                  : "4%",
                            }}
                          />
                        </div>

                        {plannedTables.length > 0 && (
                          <div className="max-h-64 overflow-y-auto rounded-lg border border-white/8 divide-y divide-white/5">
                            {plannedTables.map((t) => {
                              const result = loadProgress?.table_results.find(
                                (r) => r.source_table === t.label,
                              );
                              const isCurrent =
                                !result &&
                                (currentTableNames.includes(t.label) ||
                                  currentTableNames.includes(t.hubLabel));
                              // Live in-flight row counts (table_progress) key
                              // off the same hub-vs-join-label split as
                              // current_table above.
                              const live =
                                loadProgress?.table_progress?.[t.label] ??
                                loadProgress?.table_progress?.[t.hubLabel];
                              return (
                                <div
                                  key={t.label}
                                  className="flex items-center justify-between gap-3 px-3 py-1.5 text-xs"
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    {result ? (
                                      result.error ? (
                                        <AlertTriangle
                                          size={12}
                                          className="text-red-400 flex-shrink-0"
                                        />
                                      ) : (
                                        <CheckCircle2
                                          size={12}
                                          className="text-green-400 flex-shrink-0"
                                        />
                                      )
                                    ) : isCurrent ? (
                                      <Loader2
                                        size={12}
                                        className="text-brand-400 animate-spin flex-shrink-0"
                                      />
                                    ) : (
                                      <Clock
                                        size={12}
                                        className="text-white/25 flex-shrink-0"
                                      />
                                    )}
                                    <span className="truncate text-white/70">
                                      {t.label}{" "}
                                      <span className="text-white/30">→</span>{" "}
                                      {t.target}
                                    </span>
                                  </div>
                                  <span className="flex-shrink-0 text-white/50">
                                    {result
                                      ? result.error
                                        ? `Failed${result.carried_forward ? " (not retried, kept from earlier run)" : ""}`
                                        : `${result.rows_loaded.toLocaleString()} row(s)${
                                            result.rows_failed > 0
                                              ? `, ${result.rows_failed.toLocaleString()} failed`
                                              : ""
                                          }${
                                            result.rows_dq_excluded
                                              ? `, ${result.rows_dq_excluded.toLocaleString()} DQ excluded`
                                              : ""
                                          }${
                                            result.carried_forward
                                              ? " (kept from earlier run)"
                                              : ""
                                          }`
                                      : isCurrent
                                        ? loadProgress?.dry_run
                                          ? "Validating…"
                                          : live
                                            ? `${live.rows_loaded.toLocaleString()} row(s)${
                                                live.rows_failed > 0
                                                  ? `, ${live.rows_failed.toLocaleString()} failed`
                                                  : ""
                                              }…`
                                            : "Loading…"
                                        : "Queued"}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 text-xs">
                            {loadProgress && (
                              <span className="text-white/60">
                                {loadProgress.tables_done}/
                                {loadProgress.total_tables} tables
                              </span>
                            )}
                            <span className="text-green-400">
                              {(
                                loadProgress?.total_rows_loaded ?? 0
                              ).toLocaleString()}{" "}
                              rows{" "}
                              {loadProgress?.dry_run ? "would load" : "loaded"}
                            </span>
                            {(loadProgress?.total_rows_failed ?? 0) > 0 && (
                              <span className="text-red-400">
                                {loadProgress?.total_rows_failed.toLocaleString()}{" "}
                                {loadProgress?.dry_run
                                  ? "would fail"
                                  : "failed"}
                              </span>
                            )}
                          </div>
                          <button
                            onClick={handleCancelDataLoad}
                            disabled={!!loadProgress?.cancel_requested}
                            className="px-3 py-1 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300 text-xs font-medium transition disabled:opacity-40"
                          >
                            {loadProgress?.cancel_requested
                              ? "Cancelling…"
                              : "Cancel"}
                          </button>
                        </div>
                      </div>
                    </SectionCard>
                  );
                })()}

              {/* Results — shown for every terminal status (completed,
              failed, or cancelled), not just when at least one table
              actually started loading. A load that fails before any table
              starts (bad connection, invalid request, ...) previously left
              no confirmation at all once the progress bar above disappeared
              — this card is now the one place that always answers "did it
              work," with the top-level failure reason called out plainly
              when there's nothing to show in the per-table breakdown. */}
              {loadProgress &&
                loadProgress.status !== "running" &&
                !loadResultsCleared && (
                  <SectionCard
                    title={
                      loadProgress.dry_run
                        ? loadProgress.status === "cancelled"
                          ? "Validation Results — Cancelled"
                          : loadProgress.status === "failed"
                            ? "Validation Failed"
                            : "Validation Results"
                        : loadProgress.status === "cancelled"
                          ? "Load Results — Cancelled"
                          : loadProgress.status === "failed"
                            ? "Load Failed"
                            : "Load Results"
                    }
                    actions={
                      <div className="flex items-center gap-2">
                        {!loading &&
                          loadProgress.table_results.some((r) => r.error) && (
                            <button
                              onClick={handleRetryFailedTables}
                              className="ztd-btn ztd-btn-primary ztd-btn-sm"
                              title="Re-run only the tables that failed, truncating each first so retried rows don't duplicate any partial load"
                            >
                              <RefreshCw size={12} /> Retry Failed Tables
                            </button>
                          )}
                        <button
                          onClick={() =>
                            downloadLoadResultsCsv(loadProgress.table_results)
                          }
                          className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                        >
                          <Download size={12} /> Download
                        </button>
                        <button
                          onClick={handleDownloadMigrationSummary}
                          className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                          title="Consolidated PDF: schema stats, mapping coverage, DDL status, load results, DQ and reconciliation findings"
                        >
                          <Download size={12} /> Migration Summary
                        </button>
                        <button
                          onClick={() => setLoadResultsCleared(true)}
                          className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                        >
                          <X size={12} /> Clear
                        </button>
                      </div>
                    }
                  >
                    <div className="flex items-center gap-2 mb-3 text-sm font-medium">
                      {loadProgress.status === "failed" ? (
                        <span className="flex items-center gap-1.5 text-red-400">
                          <AlertTriangle size={15} />
                          Load did not complete — see the reason below.
                        </span>
                      ) : loadProgress.status === "cancelled" ? (
                        <span className="flex items-center gap-1.5 text-amber-400">
                          <AlertTriangle size={15} />
                          Load was cancelled part-way through.
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-green-400">
                          <CheckCircle2 size={15} />
                          {loadProgress.dry_run
                            ? "Validation finished."
                            : "Load finished."}
                        </span>
                      )}
                    </div>
                    {lastLoadRowLimitSummary && (
                      <p className="text-xs text-white/40 mb-3">
                        Row limit applied to this run: {lastLoadRowLimitSummary}
                      </p>
                    )}
                    {(() => {
                      // Suppress any job-level error already shown on a
                      // per-table row below -- otherwise a single-table
                      // failure shows the identical message twice.
                      const tableErrors = new Set(
                        loadProgress.table_results
                          .map((r) => r.error)
                          .filter((e): e is string => Boolean(e)),
                      );
                      const uniqueErrors = loadProgress.errors.filter(
                        (e) => !tableErrors.has(e),
                      );
                      if (
                        uniqueErrors.length === 0 ||
                        loadErrorBannerDismissed
                      ) {
                        return null;
                      }
                      return (
                        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 space-y-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-xs font-medium text-red-300">
                              {loadProgress.table_results.length === 0
                                ? "The load failed before any table started — nothing was written:"
                                : "Error(s) reported during this run:"}
                            </p>
                            <button
                              onClick={() => setLoadErrorBannerDismissed(true)}
                              className="p-1 -m-1 rounded hover:bg-white/10 text-red-400/60 hover:text-red-400 transition shrink-0"
                              title="Clear"
                            >
                              <X size={14} />
                            </button>
                          </div>
                          {uniqueErrors.map((e, i) => (
                            <p key={i} className="text-xs text-red-400/90">
                              {e}
                            </p>
                          ))}
                        </div>
                      );
                    })()}
                    <div className="flex items-center gap-6 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Table2 size={16} className="text-green-400" />
                        <span className="text-green-400 font-semibold">
                          {loadProgress.table_results
                            .filter((r) => !r.error)
                            .length.toLocaleString()}
                          /{loadProgress.total_tables.toLocaleString()}
                        </span>
                        <span className="text-white/40">tables loaded</span>
                      </div>
                      {loadProgress.table_results.some((r) => r.error) && (
                        <div className="flex items-center gap-2 text-sm">
                          <AlertTriangle size={16} className="text-red-400" />
                          <span className="text-red-400 font-semibold">
                            {loadProgress.table_results
                              .filter((r) => r.error)
                              .length.toLocaleString()}
                          </span>
                          <span className="text-white/40">tables failed</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-sm">
                        <CheckCircle2 size={16} className="text-green-400" />
                        <span className="text-green-400 font-semibold">
                          {loadProgress.total_rows_loaded.toLocaleString()}
                        </span>
                        <span className="text-white/40">rows loaded</span>
                      </div>
                      {loadProgress.total_rows_failed > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                          <AlertTriangle size={16} className="text-red-400" />
                          <span className="text-red-400 font-semibold">
                            {loadProgress.total_rows_failed.toLocaleString()}
                          </span>
                          <span className="text-white/40">failed</span>
                          <button
                            onClick={() =>
                              downloadFailuresTxt(
                                "Data Load failures",
                                `data_load_failures_${filenameTimestamp()}.txt`,
                                loadProgress.table_results
                                  .filter((r) => r.error || r.rows_failed > 0)
                                  .map(
                                    (r) =>
                                      `${r.source_table} -> ${r.target_table}: ${r.rows_failed.toLocaleString()} row(s) failed` +
                                      (r.error ? `\n${r.error}` : ""),
                                  ),
                              )
                            }
                            className="text-xs text-red-300 hover:text-red-200 underline underline-offset-2"
                          >
                            Download failures (.txt)
                          </button>
                        </div>
                      )}
                      {dbLoadElapsedMs > 0 && (
                        <div className="flex items-center gap-2 text-sm">
                          <Clock size={16} className="text-white/50" />
                          <span className="text-white/70 font-semibold">
                            {formatElapsed(dbLoadElapsedMs)}
                          </span>
                          <span className="text-white/40">total load time</span>
                        </div>
                      )}
                    </div>
                    {loadProgress.table_results.length > 0 && (
                      <div className="max-h-[55vh] overflow-y-auto pr-1 space-y-4">
                        <div className="overflow-auto rounded-lg border border-white/8">
                          <table className="w-full text-xs">
                            <thead className="sticky top-0 z-10">
                              <tr className="bg-surface-300 text-white/40">
                                <th className="px-4 py-2 text-left font-medium">
                                  Source Table
                                </th>
                                <th className="px-4 py-2 text-left font-medium">
                                  Target Table
                                </th>
                                <th className="px-4 py-2 text-right font-medium">
                                  Source Count
                                </th>
                                <th className="px-4 py-2 text-right font-medium">
                                  Rows Read
                                </th>
                                <th
                                  className="px-4 py-2 text-right font-medium"
                                  title="Source Count minus Rows Read — rows never fetched, either because of a row limit or (with an MDM lookup) because they had no match in the lookup table. Not a count of anything actually merged."
                                >
                                  Rows Not Read
                                </th>
                                <th className="px-4 py-2 text-right font-medium">
                                  Loaded
                                </th>
                                <th className="px-4 py-2 text-right font-medium">
                                  Failed
                                </th>
                                <th
                                  className="px-4 py-2 text-right font-medium"
                                  title="Rows skipped by an Exclude-type DQ rule at Load Data — never inserted, not counted as Failed."
                                >
                                  DQ Excluded
                                </th>
                                <th className="px-4 py-2 text-left font-medium">
                                  Status
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {loadProgress.table_results.map((r) => (
                                <tr
                                  key={r.source_table}
                                  className="border-t border-white/5 hover:bg-white/2"
                                >
                                  <td className="px-4 py-2 font-mono text-white/70">
                                    {r.source_table}
                                  </td>
                                  <td className="px-4 py-2 font-mono text-brand-300/70">
                                    {r.target_table}
                                  </td>
                                  <td className="px-4 py-2 text-right text-white/50">
                                    {r.source_total_count.toLocaleString()}
                                  </td>
                                  <td className="px-4 py-2 text-right text-white/50">
                                    {r.rows_read.toLocaleString()}
                                  </td>
                                  <td className="px-4 py-2 text-right text-white/40">
                                    {Math.max(
                                      0,
                                      r.source_total_count - r.rows_read,
                                    ).toLocaleString()}
                                  </td>
                                  <td className="px-4 py-2 text-right text-green-400">
                                    {r.rows_loaded.toLocaleString()}
                                  </td>
                                  <td className="px-4 py-2 text-right text-red-400">
                                    {r.rows_failed > 0
                                      ? r.rows_failed.toLocaleString()
                                      : "—"}
                                  </td>
                                  <td className="px-4 py-2 text-right text-amber-300/80">
                                    {r.rows_dq_excluded
                                      ? r.rows_dq_excluded.toLocaleString()
                                      : "—"}
                                  </td>
                                  <td className="px-4 py-2 whitespace-nowrap">
                                    {r.error ? (
                                      <span
                                        className="text-red-400 truncate max-w-[240px] block"
                                        title={
                                          r.carried_forward
                                            ? `${r.error} (could not be matched back to a mapping — not retried by this job)`
                                            : r.error
                                        }
                                      >
                                        {r.error}
                                        {r.carried_forward && (
                                          <span className="text-white/30">
                                            {" "}
                                            (not retried)
                                          </span>
                                        )}
                                      </span>
                                    ) : r.carried_forward ? (
                                      <span
                                        className="inline-flex items-center gap-1 text-green-400"
                                        title="Already succeeded in the original run — not reprocessed by this retry"
                                      >
                                        <CheckCircle2 size={13} /> Success
                                        <span className="text-white/30">
                                          (kept from earlier run)
                                        </span>
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 text-green-400">
                                        <CheckCircle2 size={13} /> Success
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    {loadProgress.dq_report && !dqReportDismissed && (
                      <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2 text-sm text-amber-300">
                          <AlertTriangle size={14} />
                          {loadProgress.dq_report.modified_count} record
                          {loadProgress.dq_report.modified_count !== 1
                            ? "s"
                            : ""}{" "}
                          had Date of Birth and/or Phone Number blanked for
                          failing the DQ rule during load.
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() =>
                              downloadBase64File(
                                loadProgress.dq_report!.filename,
                                loadProgress.dq_report!.content_base64,
                                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                              )
                            }
                            className="ztd-btn ztd-btn-secondary"
                          >
                            <Download size={14} /> Download DQ Report
                          </button>
                          <button
                            onClick={() => setDqReportDismissed(true)}
                            className="p-2 rounded-lg hover:bg-white/10 text-amber-300/60 hover:text-amber-300 transition"
                            title="Dismiss"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    )}
                    {/* 2026-09-08, issue #622: removed the
                    "Go to Reconciliation" / "Continue to Load to Target"
                    buttons here — duplicated the "Reconciliation" / "Load
                    Data" buttons in this step's own footer below. */}
                  </SectionCard>
                )}

              {error && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <span className="flex-1">{error}</span>
                  <button
                    onClick={() => setError(null)}
                    className="p-1 -m-1 rounded hover:bg-white/10 text-red-400/60 hover:text-red-400 transition shrink-0"
                    title="Clear"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              {(startingFileLoad || fileLoadResults) &&
                (() => {
                  const totalFileLoadTables = mappings.filter((_, i) =>
                    effectiveIncluded(i),
                  ).length;
                  const doneCount = fileLoadResults?.length ?? 0;
                  const pct =
                    totalFileLoadTables > 0
                      ? Math.round((doneCount / totalFileLoadTables) * 100)
                      : 0;
                  // Mirrors the DB-mode Load Progress list above: which
                  // specific file/table is queued, in flight, or done — not
                  // just the aggregate done-count the bar alone can show.
                  // Matched on target table name, same key
                  // _run_file_bulk_load_job's current_table/table_results
                  // both use for file-mode jobs.
                  const currentTableNames = fileLoadCurrentTable
                    ? fileLoadCurrentTable.split(", ")
                    : [];
                  const plannedFileTables = mappings
                    .map((m, i) => ({ m, i }))
                    .filter(({ i }) => effectiveIncluded(i))
                    .map(({ m, i }) => {
                      const cfg = loadMappingConfig[i];
                      return {
                        label: cfg?.sourceFile?.filename ?? m.source_table,
                        target:
                          cfg?.tgtTable || stagedTargetTable(m.target_object),
                      };
                    });
                  return (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2 text-xs text-white/60">
                        <span className="flex items-center gap-2">
                          <Clock size={12} />
                          {startingFileLoad ? (
                            <span>
                              {fileLoadCancelRequested
                                ? "Cancelling — finishing current table… "
                                : "Load running — "}
                              {formatElapsed(fileLoadElapsedMs)} elapsed
                              {totalFileLoadTables > 0
                                ? ` · ${doneCount} of ${totalFileLoadTables} table(s) done`
                                : ""}
                            </span>
                          ) : (
                            <span>
                              Load finished in{" "}
                              {formatElapsed(fileLoadElapsedMs)}
                            </span>
                          )}
                        </span>
                        {startingFileLoad && (
                          <button
                            onClick={handleCancelFileLoad}
                            disabled={fileLoadCancelRequested}
                            className="px-3 py-1 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300 text-xs font-medium transition disabled:opacity-40"
                          >
                            {fileLoadCancelRequested ? "Cancelling…" : "Cancel"}
                          </button>
                        )}
                      </div>
                      <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                        <div
                          className="h-full bg-brand-500 transition-all duration-300"
                          style={{
                            width: startingFileLoad
                              ? totalFileLoadTables > 0
                                ? `${pct}%`
                                : "4%"
                              : "100%",
                          }}
                        />
                      </div>
                      {startingFileLoad && plannedFileTables.length > 0 && (
                        <div className="max-h-64 overflow-y-auto rounded-lg border border-white/8 divide-y divide-white/5">
                          {plannedFileTables.map((t, ti) => {
                            const result = fileLoadResults?.find(
                              (r) => r.tableName === t.target,
                            );
                            const isCurrent =
                              !result && currentTableNames.includes(t.target);
                            return (
                              <div
                                key={`${t.target}-${ti}`}
                                className="flex items-center justify-between gap-3 px-3 py-1.5 text-xs"
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  {result ? (
                                    result.error ? (
                                      <AlertTriangle
                                        size={12}
                                        className="text-red-400 flex-shrink-0"
                                      />
                                    ) : (
                                      <CheckCircle2
                                        size={12}
                                        className="text-green-400 flex-shrink-0"
                                      />
                                    )
                                  ) : isCurrent ? (
                                    <Loader2
                                      size={12}
                                      className="text-brand-400 animate-spin flex-shrink-0"
                                    />
                                  ) : (
                                    <Clock
                                      size={12}
                                      className="text-white/25 flex-shrink-0"
                                    />
                                  )}
                                  <span className="truncate text-white/70">
                                    {t.label}{" "}
                                    <span className="text-white/30">→</span>{" "}
                                    {t.target}
                                  </span>
                                </div>
                                <span className="flex-shrink-0 text-white/50">
                                  {result
                                    ? result.error
                                      ? "Failed"
                                      : `${result.rows_loaded.toLocaleString()} row(s)${
                                          result.rows_rejected > 0
                                            ? `, ${result.rows_rejected.toLocaleString()} rejected`
                                            : ""
                                        }`
                                    : isCurrent
                                      ? fileLoadTableProgress[t.target]
                                        ? `Loading… ${fileLoadTableProgress[
                                            t.target
                                          ].rows_loaded.toLocaleString()} row(s)`
                                        : "Loading…"
                                      : "Queued"}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

              {fileLoadResults && (
                <div className="space-y-2">
                  {lastLoadRowLimitSummary && (
                    <div className="text-xs text-white/40">
                      Row limit applied to this run: {lastLoadRowLimitSummary}
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-white/40">
                      {fileLoadResults.length} table(s)
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          downloadFileLoadResultsCsv(fileLoadResults)
                        }
                        className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                      >
                        <Download size={12} /> Download
                      </button>
                      <button
                        onClick={handleDownloadMigrationSummary}
                        className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                        title="Consolidated PDF: schema stats, mapping coverage, DDL status, load results, DQ and reconciliation findings"
                      >
                        <Download size={12} /> Migration Summary
                      </button>
                    </div>
                  </div>
                  {/* Mirrors the DB-mode Load Results stat row above
                  (tables/rows loaded+failed with icons) for visual
                  consistency between the two load paths. */}
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2 text-sm">
                      <Table2 size={16} className="text-green-400" />
                      <span className="text-green-400 font-semibold">
                        {fileLoadResults
                          .filter((r) => !r.error)
                          .length.toLocaleString()}
                        /{fileLoadResults.length.toLocaleString()}
                      </span>
                      <span className="text-white/40">tables loaded</span>
                    </div>
                    {fileLoadResults.some((r) => r.error) && (
                      <div className="flex items-center gap-2 text-sm">
                        <AlertTriangle size={16} className="text-red-400" />
                        <span className="text-red-400 font-semibold">
                          {fileLoadResults
                            .filter((r) => r.error)
                            .length.toLocaleString()}
                        </span>
                        <span className="text-white/40">tables failed</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle2 size={16} className="text-green-400" />
                      <span className="text-green-400 font-semibold">
                        {fileLoadResults
                          .reduce((sum, r) => sum + r.rows_loaded, 0)
                          .toLocaleString()}
                      </span>
                      <span className="text-white/40">rows loaded</span>
                    </div>
                    {fileLoadResults.some((r) => r.rows_rejected > 0) && (
                      <div className="flex items-center gap-2 text-sm">
                        <AlertTriangle size={16} className="text-red-400" />
                        <span className="text-red-400 font-semibold">
                          {fileLoadResults
                            .reduce((sum, r) => sum + r.rows_rejected, 0)
                            .toLocaleString()}
                        </span>
                        <span className="text-white/40">rejected</span>
                        <button
                          onClick={() =>
                            downloadFailuresTxt(
                              "File Load failures",
                              `file_load_failures_${filenameTimestamp()}.txt`,
                              fileLoadResults
                                .filter((r) => r.error || r.rows_rejected > 0)
                                .map(
                                  (r) =>
                                    `${r.tableName} -> ${r.schema_name}.${r.table_name}: ${r.rows_rejected.toLocaleString()} row(s) rejected` +
                                    (r.error ? `\n${r.error}` : ""),
                                ),
                            )
                          }
                          className="text-xs text-red-300 hover:text-red-200 underline underline-offset-2"
                        >
                          Download failures (.txt)
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="max-h-[55vh] overflow-y-auto pr-1 space-y-2">
                    <div className="overflow-auto rounded-lg border border-white/8">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 z-10">
                          <tr className="bg-surface-300 text-left text-white/40">
                            <th className="px-3 py-2 font-medium">
                              Source Table
                            </th>
                            <th className="px-3 py-2 font-medium">
                              Target Table
                            </th>
                            <th className="px-3 py-2 font-medium text-right">
                              Source Count
                            </th>
                            <th className="px-3 py-2 font-medium text-right">
                              Loaded
                            </th>
                            <th className="px-3 py-2 font-medium text-right">
                              Rejected
                            </th>
                            <th className="px-3 py-2 font-medium">Duration</th>
                            <th className="px-3 py-2 font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                          {fileLoadResults.map((r, i) => {
                            // Any rejected row is a real failure worth
                            // flagging red, not a "successful" load that
                            // happens to mention a rejection count in passing
                            // -- even a single bad row out of thousands
                            // should not read as all-green.
                            const hasFailure =
                              Boolean(r.error) || r.rows_rejected > 0;
                            return (
                              <tr
                                key={i}
                                className={
                                  r.error
                                    ? "bg-red-500/10"
                                    : hasFailure
                                      ? "bg-amber-500/5"
                                      : undefined
                                }
                              >
                                <td className="px-3 py-2 text-white/80 break-words">
                                  {r.tableName}
                                </td>
                                <td className="px-3 py-2 text-white/60 break-words">
                                  {r.schema_name}.{r.table_name}
                                  {r.table_created ? " (created)" : ""}
                                </td>
                                <td className="px-3 py-2 text-right text-white/60">
                                  {r.rows_read ?? "—"}
                                </td>
                                <td className="px-3 py-2 text-right text-green-300">
                                  {r.rows_loaded}
                                </td>
                                <td
                                  className={`px-3 py-2 text-right ${
                                    r.rows_rejected > 0
                                      ? "text-red-300"
                                      : "text-white/40"
                                  }`}
                                >
                                  {r.rows_rejected}
                                </td>
                                <td className="px-3 py-2 text-white/50">
                                  {r.durationMs !== undefined
                                    ? formatElapsed(r.durationMs)
                                    : "—"}
                                </td>
                                <td className="px-3 py-2">
                                  {r.error ? (
                                    <span className="flex items-center gap-1 text-red-300">
                                      <AlertTriangle size={12} /> Failed
                                    </span>
                                  ) : hasFailure ? (
                                    <span className="flex items-center gap-1 text-amber-300">
                                      <AlertTriangle size={12} /> Rejects
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1 text-green-300">
                                      <CheckCircle2 size={12} /> OK
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {fileLoadResults.some(
                      (r) => Boolean(r.error) || r.rows_rejected > 0,
                    ) && (
                      <div className="space-y-1.5">
                        {fileLoadResults
                          .filter(
                            (r) => Boolean(r.error) || r.rows_rejected > 0,
                          )
                          .map((r, i) => (
                            <div
                              key={i}
                              className="rounded-lg border border-red-500/30 bg-red-500/10 p-2 text-xs text-red-200"
                            >
                              <div className="mb-0.5 font-medium text-red-300">
                                {r.tableName}
                              </div>
                              {r.error && <div>{r.error}</div>}
                              {(r.reject_reasons ?? []).map((rr, j) => (
                                <div key={j}>
                                  {rr.count} row(s): {rr.reason}
                                </div>
                              ))}
                              {r.reject_file && (
                                <a
                                  href={r.reject_file}
                                  download
                                  className="text-brand-300 hover:text-brand-200 underline underline-offset-2"
                                >
                                  Download all {r.rows_rejected} rejected row(s)
                                </a>
                              )}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between">
                <button onClick={goPrev} className="ztd-btn ztd-btn-secondary">
                  <ArrowLeft size={14} /> Back
                </button>
                <div className="flex gap-2">
                  {loadSourceMode === "files" ? (
                    <button
                      onClick={handleStartFileBasedLoad}
                      disabled={
                        startingFileLoad ||
                        mappings.length === 0 ||
                        !targetConnectionId ||
                        mappings.every((_, i) => !effectiveIncluded(i))
                      }
                      className="ztd-btn ztd-btn-primary"
                      title={
                        mappings.every((_, i) => !effectiveIncluded(i))
                          ? "No tables selected — check at least one table in Mapping Summary"
                          : undefined
                      }
                    >
                      {startingFileLoad ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Upload size={14} />
                      )}
                      {startingFileLoad
                        ? "Loading…"
                        : (fileLoadResults?.length ?? 0) > 0
                          ? "Re-load from Files"
                          : "Load Data from Files"}
                    </button>
                  ) : (
                    <button
                      onClick={handleLoadData}
                      disabled={
                        loading ||
                        mappings.length === 0 ||
                        !sourceConnectionId ||
                        !targetConnectionId ||
                        mappings.every((_, i) => !effectiveIncluded(i))
                      }
                      className="ztd-btn ztd-btn-primary"
                      title={
                        mappings.every((_, i) => !effectiveIncluded(i))
                          ? "No tables selected — check at least one table in Mapping Summary"
                          : undefined
                      }
                    >
                      {loading ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Upload size={14} />
                      )}
                      {loading
                        ? loadDryRun
                          ? "Validating…"
                          : "Loading…"
                        : loadDryRun
                          ? "Validate (Dry Run)"
                          : loadProgress && loadProgress.status !== "running"
                            ? "Re-load"
                            : "Load Data"}
                    </button>
                  )}
                  <button
                    onClick={() => selectAction("reconciliation")}
                    className="ztd-btn ztd-btn-primary"
                  >
                    Reconciliation <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Step: Load to Salesforce ─────────────────────────────────── */}
          {step === "sf_load" && (
            <div className="space-y-4">
              {projectId && (
                <div className="flex justify-end">
                  <button
                    onClick={openFinalLoadHistoryPanel}
                    className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                    title="View last 10 saved Load to Target runs"
                  >
                    <History size={13} /> Load to Target History
                  </button>
                </div>
              )}
              {/* 2026-09-08, requested by user: side by side instead of
              stacked when both boxes are showing. */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                {renderProjectMappingFileCard()}
                {/* 2026-09-01, requested by user: makes the active staged-vs-
                direct choice visible on the step itself, with a way back to
                re-decide (only reachable when the loader is the active
                action — a resumed direct session skips the choice screen
                entirely, see handleResumeChoice). */}
                {action === "loader" && crmLoadStrategy && (
                  <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white/3 border border-white/8 text-xs text-white/50 flex-wrap">
                    <span className="flex items-center gap-1.5">
                      {crmLoadStrategy === "direct" ? (
                        <Zap size={12} className="text-brand-400" />
                      ) : (
                        <Database size={12} className="text-brand-400" />
                      )}
                      {crmLoadStrategy === "direct"
                        ? "Loading directly from source — no staging tables"
                        : "Loading through the staging database"}
                    </span>
                    <button
                      onClick={() => setCrmLoadStrategy(null)}
                      className="text-brand-300 hover:text-brand-200 underline underline-offset-2"
                    >
                      Change
                    </button>
                  </div>
                )}
              </div>
              {/* 2026-09-02, requested by user: entering Data Loader
              directly (never having visited the Mapping action's
              "salesforce" step this session, and no saved CRM connection
              tested yet) previously left no way to add/select a
              Salesforce/Dynamics/Power Platform connection before this
              final step. Same connect form as the "salesforce" step
              (renderCrmConnectFields), shown inline here until a
              connection is actually tested — untouched once sfOrgInfo is
              set (whether from this card or the Mapping flow earlier). */}
              {/* 2026-09-02, requested by user: a project can have several
              saved CRM connections (Salesforce, Dynamics 365, Power
              Platform, ...) — always show the library, not just before the
              first one is tested, so switching to a different one doesn't
              require reconnecting from scratch. */}
              {/* 2026-09-08, requested by user: side by side on wider
              screens instead of a full-width stack, when both the connect
              card and the saved-connections library are showing. */}
              {targetPlatformMode === "crm" && !sfOrgInfo ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                  <SectionCard
                    title={`Connect ${computeTargetPlatformLabel()}`}
                  >
                    {renderCrmConnectFields()}
                  </SectionCard>
                  {projectId && (
                    <SavedConnectionsPanel
                      projectId={projectId}
                      kind="crm"
                      currentConn={sfConn}
                      onLoad={handleLoadSavedCrmConn}
                      onActivated={handleCrmConnectionActivatedFromLibrary}
                    />
                  )}
                </div>
              ) : (
                targetPlatformMode === "crm" &&
                projectId && (
                  <SavedConnectionsPanel
                    projectId={projectId}
                    kind="crm"
                    currentConn={sfConn}
                    onLoad={handleLoadSavedCrmConn}
                    onActivated={handleCrmConnectionActivatedFromLibrary}
                  />
                )
              )}
              {sfLoadMappings.length === 0 ? (
                <SectionCard title="Load to Target">
                  <p className="text-sm text-white/50">
                    No STTM mappings found yet. Go back to the STTM-Mapping step
                    and generate mappings first — each mapped table → target
                    object pair will get its own tab here.
                  </p>
                </SectionCard>
              ) : (
                <>
                  {/* Tab bar — one per mapped source table → GovConnect object */}
                  <div className="flex gap-1 flex-wrap border-b border-white/8 pb-0">
                    {sfLoadMappings.map((m, idx) => {
                      const key = sfLoadTabKey(m);
                      const tab = getSfLoadTab(key);
                      return (
                        <button
                          key={key}
                          onClick={() => setSfLoadActiveTab(idx)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-t-lg border-b-2 transition-all whitespace-nowrap ${
                            sfLoadActiveTab === idx
                              ? "border-brand-400 text-white bg-white/5 font-medium"
                              : "border-transparent text-white/40 hover:text-white/70 hover:bg-white/3"
                          }`}
                        >
                          {m.source_table}
                          <ArrowRight size={10} className="text-white/20" />
                          {m.target_object}
                          {tab.loading && (
                            <Loader2
                              size={11}
                              className="animate-spin text-brand-400"
                            />
                          )}
                          {/* 2026-09-04, reported by user: "when the load
                          fails the tick on the top should not be green" --
                          the job-level status "completed" only means the
                          batch job finished running without crashing, not
                          that every row actually loaded; a run with 0
                          loaded/1 failed still reported "completed" and
                          showed the same green check as a fully successful
                          one. */}
                          {tab.progress?.status === "completed" &&
                            tab.progress.rows_failed === 0 && (
                              <CheckCircle2
                                size={12}
                                className="text-green-400"
                              />
                            )}
                          {((tab.progress?.status === "completed" &&
                            tab.progress.rows_failed > 0) ||
                            tab.progress?.status === "failed" ||
                            tab.progress?.status === "cancelled") && (
                            <AlertTriangle
                              size={12}
                              className={
                                tab.progress.status === "completed"
                                  ? "text-red-400"
                                  : "text-yellow-400"
                              }
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {activeSfLoadMapping && activeSfLoadKey && (
                    <>
                      {/* Source schema override + fetch/auto-map */}
                      <SectionCard
                        title={`${activeSfLoadMapping.source_table} → ${activeSfLoadMapping.target_object}`}
                        actions={
                          <button
                            onClick={() =>
                              handleRefreshSfLoadConnection(activeSfLoadMapping)
                            }
                            title="Re-sync the staged DB / Target Platform connections (clears any stale saved values) and clear fetched field mappings"
                            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs transition ${
                              sfLoadConnRefreshed
                                ? "bg-green-500/10 border-green-500/30 text-green-300"
                                : "bg-white/5 hover:bg-white/10 border-white/10 text-white/60 hover:text-white/80"
                            }`}
                          >
                            {sfLoadConnRefreshed ? (
                              <>
                                <CheckCircle2 size={12} /> Refreshed!
                              </>
                            ) : (
                              <>
                                <RefreshCw size={12} /> Refresh Connection
                              </>
                            )}
                          </button>
                        }
                      >
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs text-white/50 mb-1">
                                {crmLoadStrategy === "direct"
                                  ? "Source Schema (real source DB)"
                                  : "Source Schema"}
                              </label>
                              <input
                                value={
                                  activeSfLoadTab.sourceSchema ||
                                  (crmLoadStrategy === "direct"
                                    ? sourceSchemaName ||
                                      activeSfLoadMapping.source_schema ||
                                      ""
                                    : ddlTargetSchema || targetSchemaName || "")
                                }
                                onChange={(e) =>
                                  updateSfLoadTab(activeSfLoadKey, {
                                    sourceSchema: e.target.value,
                                  })
                                }
                                placeholder="e.g. STAGING"
                                className="w-full px-3 py-2 rounded-lg bg-surface-400/60 border border-white/10 text-sm text-white focus:outline-none focus:border-brand-500/60"
                              />
                            </div>
                            <div>
                              {/* 2026-09-02, requested by user: this table
                              name is now always visible and editable —
                              never silently assumed — since a project
                              whose source and staging connections happen
                              to be the same physical database/schema
                              (source and staging table NAMES still differ,
                              but only this label makes that visible) needs
                              an explicit way to confirm it's really
                              reading from the staging table, not the
                              source one, especially when entering this
                              step standalone (resumed session, or via the
                              standalone Create Staging Tables action)
                              rather than fresh off "Load Data to Staging"
                              in the same browser tab. */}
                              <label className="block text-xs text-white/50 mb-1">
                                {crmLoadStrategy === "direct"
                                  ? "Source Table (real source table)"
                                  : "Staging Table"}
                              </label>
                              <input
                                value={
                                  activeSfLoadTab.sourceTable ||
                                  computeSfLoadSourceTableDefault(
                                    activeSfLoadMapping,
                                  )
                                }
                                onChange={(e) =>
                                  updateSfLoadTab(activeSfLoadKey, {
                                    sourceTable: e.target.value,
                                  })
                                }
                                placeholder={computeSfLoadSourceTableDefault(
                                  activeSfLoadMapping,
                                )}
                                className="w-full px-3 py-2 rounded-lg bg-surface-400/60 border border-white/10 text-sm text-white font-mono focus:outline-none focus:border-brand-500/60"
                              />
                            </div>
                          </div>
                          <button
                            onClick={() =>
                              handleSfLoadFetchObjectFields(activeSfLoadMapping)
                            }
                            disabled={activeSfLoadTab.loading}
                            className="ztd-btn ztd-btn-secondary"
                          >
                            {activeSfLoadTab.loading ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Zap size={14} />
                            )}
                            Load Fields & Auto-Map
                          </button>
                        </div>
                      </SectionCard>

                      {activeSfLoadTab.error && (
                        <div className="px-4 py-2.5 rounded-lg bg-red-900/20 border border-red-500/20 text-xs text-red-300">
                          {activeSfLoadTab.error}
                        </div>
                      )}

                      {/* 2026-09-08, requested by user: side-by-side
                      instead of stacked panes. */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                        {/* Operation */}
                        <SectionCard title="Load Operation">
                          <div className="flex gap-4 flex-wrap">
                            {(["insert", "upsert", "update"] as const).map(
                              (op) => (
                                <label
                                  key={op}
                                  className="flex items-center gap-2 cursor-pointer"
                                >
                                  <input
                                    type="radio"
                                    checked={activeSfLoadTab.operation === op}
                                    onChange={() =>
                                      updateSfLoadTab(activeSfLoadKey, {
                                        operation: op,
                                      })
                                    }
                                    className="accent-brand-500"
                                  />
                                  <span className="text-sm text-white/70 capitalize">
                                    {op}
                                  </span>
                                </label>
                              ),
                            )}
                          </div>
                          {activeSfLoadTab.operation === "upsert" && (
                            <div className="mt-3">
                              <label className="block text-xs text-white/50 mb-1">
                                External ID Field (upsert key)
                              </label>
                              <input
                                value={activeSfLoadTab.upsertKey}
                                onChange={(e) =>
                                  updateSfLoadTab(activeSfLoadKey, {
                                    upsertKey: e.target.value,
                                  })
                                }
                                placeholder="e.g. External_Id__c"
                                className="w-full px-3 py-2 rounded-lg bg-surface-400/60 border border-white/10 text-sm text-white focus:outline-none focus:border-brand-500/60"
                              />
                            </div>
                          )}
                        </SectionCard>

                        {/* Row limit */}
                        <SectionCard title="Row Limit">
                          <div className="flex gap-6 items-center flex-wrap">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                checked={activeSfLoadTab.rowLimit === "all"}
                                onChange={() =>
                                  updateSfLoadTab(activeSfLoadKey, {
                                    rowLimit: "all",
                                  })
                                }
                                className="accent-brand-500"
                              />
                              <span className="text-sm text-white/70">
                                Load all records
                              </span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="radio"
                                checked={activeSfLoadTab.rowLimit === "limited"}
                                onChange={() =>
                                  updateSfLoadTab(activeSfLoadKey, {
                                    rowLimit: "limited",
                                  })
                                }
                                className="accent-brand-500"
                              />
                              <span className="text-sm text-white/70">
                                Load first N records
                              </span>
                            </label>
                            {activeSfLoadTab.rowLimit === "limited" && (
                              <input
                                type="number"
                                min={1}
                                value={activeSfLoadTab.rowCount}
                                onChange={(e) =>
                                  updateSfLoadTab(activeSfLoadKey, {
                                    rowCount: Number(e.target.value),
                                  })
                                }
                                className="w-28 px-3 py-1.5 rounded-lg bg-surface-400/60 border border-white/10 text-sm text-white focus:outline-none focus:border-brand-500/60"
                              />
                            )}
                          </div>
                        </SectionCard>
                      </div>

                      {/* Field mapping table */}
                      {activeSfLoadTab.objectMeta &&
                        activeSfLoadTab.fieldMappings.length > 0 && (
                          <SectionCard
                            title={`Field Mapping — ${activeSfLoadMapping.source_table} → ${activeSfLoadMapping.target_object}`}
                          >
                            <p className="text-xs text-white/40 mb-3">
                              Edit SF field targets or clear rows you don't want
                              to load. Rows with empty SF Field will be skipped.
                            </p>
                            <div className="overflow-auto max-h-[440px]">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-white/3 text-white/40">
                                    <th className="px-3 py-2 text-left font-medium">
                                      Source Column
                                    </th>
                                    <th className="px-3 py-2 text-left font-medium">
                                      SF Target Field
                                    </th>
                                    <th className="px-3 py-2 text-left font-medium">
                                      SF Type
                                    </th>
                                    <th
                                      className="px-3 py-2 text-left font-medium"
                                      title='FROM=TO pairs, comma-separated (e.g. M=Male,F=Female) — or a transform expression / plain-English instruction (e.g. UPPER(FirstName), or "M as Male, F as Female")'
                                    >
                                      Value Map
                                    </th>
                                    <th className="px-3 py-2 text-center font-medium">
                                      Remove
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {activeSfLoadTab.fieldMappings.map(
                                    (fm, i) => {
                                      const objectMeta =
                                        activeSfLoadTab.objectMeta!;
                                      const fieldMeta = objectMeta.fields.find(
                                        (f) => f.api_name === fm.sf_field,
                                      );
                                      const valueMapStr = !fm.value_map
                                        ? ""
                                        : typeof fm.value_map === "string"
                                          ? fm.value_map
                                          : Object.entries(fm.value_map)
                                              .map(([k, v]) => `${k}=${v}`)
                                              .join(",");
                                      return (
                                        <tr
                                          key={i}
                                          className="border-t border-white/5 hover:bg-white/2"
                                        >
                                          <td className="px-3 py-1.5 font-mono text-white/80">
                                            {fm.source_column}
                                          </td>
                                          <td className="px-3 py-1.5">
                                            <FieldSearchSelect
                                              value={fm.sf_field}
                                              onChange={(v) =>
                                                updateSfLoadTab(
                                                  activeSfLoadKey,
                                                  {
                                                    fieldMappings:
                                                      activeSfLoadTab.fieldMappings.map(
                                                        (m, idx) =>
                                                          idx === i
                                                            ? {
                                                                ...m,
                                                                sf_field: v,
                                                              }
                                                            : m,
                                                      ),
                                                  },
                                                )
                                              }
                                              placeholder="Search fields…"
                                              className="w-full px-2 py-0.5 bg-surface-400/60 border border-white/10 rounded text-brand-300 text-xs focus:outline-none focus:border-brand-500/60"
                                              options={[
                                                {
                                                  value: "",
                                                  label: "— skip —",
                                                },
                                                ...objectMeta.fields.map(
                                                  (f) => ({
                                                    value: f.api_name,
                                                    label: f.api_name,
                                                    sublabel: f.data_type,
                                                  }),
                                                ),
                                              ]}
                                            />
                                          </td>
                                          <td className="px-3 py-1.5 text-purple-300/70">
                                            {fieldMeta?.data_type ?? "—"}
                                          </td>
                                          <td className="px-3 py-1.5">
                                            <ExpandableTextField
                                              value={valueMapStr}
                                              title={`Value Map — ${fm.sf_field || fm.source_column}`}
                                              placeholder={
                                                fieldMeta?.data_type ===
                                                  "picklist" ||
                                                fieldMeta?.data_type ===
                                                  "multipicklist"
                                                  ? "e.g. M=Male,F=Female"
                                                  : "e.g. UPPER(FirstName)"
                                              }
                                              hint="FROM=TO pairs, comma-separated (e.g. M=Male,F=Female) — or a transform expression / plain-English instruction (e.g. UPPER(FirstName), or 'M as Male, F as Female')"
                                              valueClassName="font-mono text-yellow-300/80"
                                              onChange={(raw) => {
                                                // FROM=TO pairs (the
                                                // original format) still win
                                                // when every non-blank,
                                                // comma-separated segment has
                                                // one -- anything else (no
                                                // "=" pairs found at all,
                                                // e.g. "UPPER(FirstName)" or
                                                // "M as Male, F as Female")
                                                // is sent through as-is for
                                                // the backend's transform
                                                // engine to compile.
                                                const parsed: Record<
                                                  string,
                                                  string
                                                > = {};
                                                raw
                                                  .split(",")
                                                  .forEach((pair) => {
                                                    const [k, ...rest] =
                                                      pair.split("=");
                                                    if (
                                                      k?.trim() &&
                                                      rest.length
                                                    )
                                                      parsed[k.trim()] = rest
                                                        .join("=")
                                                        .trim();
                                                  });
                                                const nextValueMap:
                                                  | Record<string, string>
                                                  | string
                                                  | undefined = Object.keys(
                                                  parsed,
                                                ).length
                                                  ? parsed
                                                  : raw.trim()
                                                    ? raw.trim()
                                                    : undefined;
                                                updateSfLoadTab(
                                                  activeSfLoadKey,
                                                  {
                                                    fieldMappings:
                                                      activeSfLoadTab.fieldMappings.map(
                                                        (m, idx) =>
                                                          idx === i
                                                            ? {
                                                                ...m,
                                                                value_map:
                                                                  nextValueMap,
                                                              }
                                                            : m,
                                                      ),
                                                  },
                                                );
                                              }}
                                            />
                                          </td>
                                          <td className="px-3 py-1.5 text-center">
                                            <button
                                              onClick={() => {
                                                updateSfLoadTab(
                                                  activeSfLoadKey,
                                                  {
                                                    fieldMappings:
                                                      activeSfLoadTab.fieldMappings.filter(
                                                        (_, idx) => idx !== i,
                                                      ),
                                                  },
                                                );
                                              }}
                                              className="text-red-400/60 hover:text-red-400 transition"
                                              title="Remove this row"
                                            >
                                              <X size={13} />
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    },
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </SectionCard>
                        )}

                      {/* Results */}
                      {activeSfLoadTab.result && (
                        <SectionCard
                          title={
                            activeSfLoadTab.progress?.status === "cancelled"
                              ? "Load Results — Cancelled"
                              : "Load Results"
                          }
                          actions={
                            <button
                              onClick={() =>
                                clearSfLoadResults(activeSfLoadMapping)
                              }
                              className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                            >
                              <X size={12} /> Clear
                            </button>
                          }
                        >
                          {activeSfLoadTab.progress?.status === "cancelled" && (
                            <p className="text-xs text-yellow-400/80 mb-3">
                              Load was cancelled partway through — the totals
                              below only cover batches that ran before
                              cancellation. Remaining rows were not loaded.
                            </p>
                          )}
                          <div className="flex gap-6 flex-wrap mb-4">
                            <div className="text-center">
                              <div className="text-2xl font-bold text-white">
                                {activeSfLoadTab.result.rows_read.toLocaleString()}
                              </div>
                              <div className="text-xs text-white/40 mt-0.5">
                                Rows read
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="text-2xl font-bold text-green-400">
                                {activeSfLoadTab.result.rows_loaded.toLocaleString()}
                              </div>
                              <div className="text-xs text-white/40 mt-0.5">
                                Loaded to SF
                              </div>
                            </div>
                            {activeSfLoadTab.result.rows_failed > 0 && (
                              <div className="text-center">
                                <div className="text-2xl font-bold text-red-400">
                                  {activeSfLoadTab.result.rows_failed.toLocaleString()}
                                </div>
                                <div className="text-xs text-white/40 mt-0.5">
                                  Failed
                                </div>
                              </div>
                            )}
                          </div>
                          {activeSfLoadTab.result.errors.length > 0 && (
                            <div className="space-y-1 mt-2">
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-xs text-red-400/80 font-medium">
                                  Errors (first{" "}
                                  {activeSfLoadTab.result.errors.length}):
                                </p>
                                <button
                                  onClick={async () => {
                                    const result = activeSfLoadTab.result!;
                                    const res = await fetch(
                                      "/api/v2/data-converter/sf/load-error-report",
                                      {
                                        method: "POST",
                                        headers: {
                                          "Content-Type": "application/json",
                                          "X-CSRF-Token":
                                            document.cookie.match(
                                              /ztd_csrf_token=([^;]+)/,
                                            )?.[1] ?? "",
                                        },
                                        credentials: "include",
                                        body: JSON.stringify({
                                          sf_object:
                                            activeSfLoadMapping.target_object,
                                          source_table:
                                            activeSfLoadMapping.source_table,
                                          rows_read: result.rows_read,
                                          rows_loaded: result.rows_loaded,
                                          rows_failed: result.rows_failed,
                                          errors: result.errors,
                                        }),
                                      },
                                    );
                                    const blob = await res.blob();
                                    const url =
                                      window.URL.createObjectURL(blob);
                                    const a = document.createElement("a");
                                    a.href = url;
                                    a.download = `sf_load_errors_${activeSfLoadMapping.target_object}_${filenameTimestamp()}.xlsx`;
                                    a.click();
                                    window.URL.revokeObjectURL(url);
                                  }}
                                  className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-red-900/20 hover:bg-red-900/40 text-xs text-red-400 border border-red-500/20 transition"
                                >
                                  <Download size={12} /> Download Error Report
                                </button>
                              </div>
                              {activeSfLoadTab.result.errors.map((e, i) => (
                                <div
                                  key={i}
                                  className="text-xs font-mono text-red-300/60 bg-red-900/10 rounded px-2 py-1"
                                >
                                  {e}
                                </div>
                              ))}
                            </div>
                          )}
                        </SectionCard>
                      )}

                      {activeSfLoadTab.loading && activeSfLoadTab.jobId && (
                        <div className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg bg-white/3 border border-white/8 text-xs text-white/60">
                          <div className="flex items-center gap-3">
                            <Loader2
                              size={13}
                              className="animate-spin text-brand-400"
                            />
                            <span>
                              {activeSfLoadTab.progress?.cancel_requested
                                ? "Cancelling — finishing current batch…"
                                : activeSfLoadTab.progress
                                  ? `Batch ${activeSfLoadTab.progress.batches_done}/${activeSfLoadTab.progress.batches_total || "?"} · ${activeSfLoadTab.progress.rows_loaded.toLocaleString()} loaded`
                                  : "Starting…"}
                            </span>
                          </div>
                          <button
                            onClick={() =>
                              handleCancelSfLoad(activeSfLoadMapping)
                            }
                            disabled={
                              !!activeSfLoadTab.progress?.cancel_requested
                            }
                            className="px-3 py-1 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-300 text-xs font-medium transition disabled:opacity-40"
                          >
                            {activeSfLoadTab.progress?.cancel_requested
                              ? "Cancelling…"
                              : "Cancel"}
                          </button>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <button
                          onClick={() => setAction(null)}
                          className="ztd-btn ztd-btn-secondary"
                        >
                          <ArrowLeft size={14} /> Back to Actions
                        </button>
                        <button
                          onClick={() => handleSfLoad(activeSfLoadMapping)}
                          disabled={
                            activeSfLoadTab.loading ||
                            activeSfLoadTab.fieldMappings.filter(
                              (m) => m.sf_field,
                            ).length === 0
                          }
                          className="ztd-btn ztd-btn-primary"
                        >
                          {activeSfLoadTab.loading ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <Cloud size={14} />
                          )}
                          {activeSfLoadTab.loading
                            ? "Loading to Target…"
                            : "Load to Target"}
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Step: Reconciliation Dashboard ──────────────────────────────── */}
          {step === "reconcile" && (
            <div className="space-y-4">
              {standaloneReconcileMode ? (
                <StandaloneReconcilePanel
                  projectId={projectId}
                  sourceConnectionId={sourceConnectionId}
                  sourceConnLabel={sourceConnLabel}
                  targetConnectionId={targetConnectionId}
                  targetConnLabel={targetConnLabel}
                  handleSourceConnectNew={handleSourceConnectNew}
                  handleSourceConnectExistingForLoad={
                    handleSourceConnectExistingForLoad
                  }
                  handleSourceConnectionActivatedFromLibrary={
                    handleSourceConnectionActivatedFromLibrary
                  }
                  handleTargetConnectNew={handleTargetConnectNew}
                  handleTargetConnectExisting={handleTargetConnectExisting}
                  handleTargetConnectionActivatedFromLibrary={
                    handleTargetConnectionActivatedFromLibrary
                  }
                  onBackToMappingChoice={() =>
                    setStandaloneReconcileMode(false)
                  }
                />
              ) : !reconciliationMappingConfirmed ? (
                // 2026-09-27, requested by user: entering Reconciliation
                // directly (the landing-page action, not the natural
                // forward chain after a load) must let the user pick WHICH
                // mapping it targets instead of silently running against
                // whatever the project's current mapping happens to be —
                // otherwise this action has no reason to exist separately
                // from Data Loader. Reuses the same Select-from-History/
                // Upload-mapping handlers the "No mapping found yet" empty
                // state already uses below.
                <SectionCard title="Which mapping should this reconciliation use?">
                  <p className="text-sm text-white/50 mb-4">
                    {mappings.length > 0
                      ? `The project's current mapping has ${mappings.length} mapped table${mappings.length !== 1 ? "s" : ""}. Continue with it, or pick a different one.`
                      : "No mapping is loaded yet for this project — select a saved mapping or import an STTM file."}
                  </p>
                  <div className="flex items-center gap-3 flex-wrap">
                    {mappings.length > 0 && (
                      <button
                        onClick={() => setReconciliationMappingConfirmed(true)}
                        className="ztd-btn ztd-btn-primary"
                      >
                        <CheckCircle2 size={14} /> Continue with Current Mapping
                      </button>
                    )}
                    <button
                      onClick={() => void handleOpenMappingHistoryPicker()}
                      className="ztd-btn ztd-btn-secondary"
                    >
                      <History size={14} /> Select from History
                    </button>
                    <label
                      className={`ztd-btn ztd-btn-secondary cursor-pointer ${uploadingMappingFile ? "opacity-40 pointer-events-none" : ""}`}
                    >
                      {uploadingMappingFile ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Upload size={14} />
                      )}
                      Import STTM Mapping (.xlsx)
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = "";
                          if (f) void handleReplaceProjectMappingFile(f);
                        }}
                      />
                    </label>
                    {/* 2026-10-15, requested by user: a way to run
                    reconciliation with no STTM mapping involved at all —
                    hand-define the source/target tables and the key/
                    compare fields instead of picking a project mapping. */}
                    <button
                      onClick={() => setStandaloneReconcileMode(true)}
                      className="ztd-btn ztd-btn-secondary"
                    >
                      <GitCompareArrows size={14} /> Skip Mapping — Define
                      Fields Manually
                    </button>
                  </div>
                </SectionCard>
              ) : reconcileMappings.length === 0 ? (
                <SectionCard title="Reconciliation">
                  <p className="text-sm text-white/50">
                    No mapped tables yet — generate the STTM mapping first.
                  </p>
                </SectionCard>
              ) : (
                <>
                  {/* 2026-09-03, requested by user: "enable the ability to
                  select the source and target connections" — Reconciliation
                  previously had no way to see or change which source/target
                  database it runs against; it just silently used whatever
                  sourceConnectionId/targetConnectionId happened to already
                  be set (empty for a resumed session that never revisited
                  those earlier steps this session), surfacing only as a
                  generic "Connect source and target databases first" error
                  once a check/reconcile button was clicked. Same
                  Connection card + always-visible Saved Connections library
                  pattern as the Load Data step, reusing its own dialog
                  state/handlers so a connection made here is the exact same
                  one every other step reads from. */}
                  {/* 2026-09-08, issue #623: side-by-side on wider
                  screens instead of a full-width stack — see the identical
                  layout change on the Load Data step above. */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                    <div className="space-y-4">
                      <SectionCard title="Source Connection">
                        {sourceConnectionId ? (
                          // 2026-09-08: flex-wrap so the button drops below
                          // instead of squeezing this text in the half column.
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-2 text-sm text-green-400">
                              <CheckCircle2 size={14} />
                              {sourceConnLabel || "saved connection"}
                            </div>
                            <button
                              onClick={() => setShowSourceConnectDialog(true)}
                              className="ztd-btn ztd-btn-secondary"
                            >
                              <RefreshCw size={14} />
                              Change connection
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => setShowSourceConnectDialog(true)}
                              className="ztd-btn ztd-btn-primary"
                            >
                              <RefreshCw size={14} />
                              Connect Database
                            </button>
                            <span className="text-xs text-white/40">
                              Needed to read source data for reconciliation.
                            </span>
                          </div>
                        )}
                        <ConnectDatabaseDialog
                          open={showSourceConnectDialog}
                          onClose={() => setShowSourceConnectDialog(false)}
                          title="Connect Source Database"
                          projectId={projectId}
                          kind="source"
                          agentKey="data_converter"
                          defaultDbType="oracle"
                          testIdPrefix="converter-source-reconcile"
                          onConnectExisting={handleSourceConnectExistingForLoad}
                          onConnectNew={handleSourceConnectNew}
                        />
                      </SectionCard>

                      {projectId && (
                        <SavedConnectionsPanel
                          projectId={projectId}
                          kind="source"
                          onActivated={
                            handleSourceConnectionActivatedFromLibrary
                          }
                          onConnectExisting={handleSourceConnectExistingForLoad}
                        />
                      )}
                    </div>

                    <div className="space-y-4">
                      <SectionCard title="Target Connection">
                        {targetConnectionId ? (
                          // 2026-09-08: flex-wrap so the button drops below
                          // instead of squeezing this text in the half column.
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="flex items-center gap-2 text-sm text-green-400">
                              <CheckCircle2 size={14} />
                              {targetConnLabel || "saved connection"}
                            </div>
                            <button
                              onClick={() => setShowTargetConnectDialog(true)}
                              className="ztd-btn ztd-btn-secondary"
                            >
                              <RefreshCw size={14} />
                              Change connection
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => setShowTargetConnectDialog(true)}
                              className="ztd-btn ztd-btn-primary"
                            >
                              <RefreshCw size={14} />
                              Connect Database
                            </button>
                            <span className="text-xs text-white/40">
                              Needed to read the staged target data for
                              reconciliation.
                            </span>
                          </div>
                        )}
                        <ConnectDatabaseDialog
                          open={showTargetConnectDialog}
                          onClose={() => setShowTargetConnectDialog(false)}
                          title="Connect Target Database"
                          projectId={projectId}
                          kind="target"
                          agentKey="data_converter"
                          defaultDbType="oracle"
                          testIdPrefix="converter-target-reconcile"
                          onConnectExisting={handleTargetConnectExisting}
                          onConnectNew={handleTargetConnectNew}
                        />
                      </SectionCard>

                      {projectId && (
                        <SavedConnectionsPanel
                          projectId={projectId}
                          kind="target"
                          onActivated={
                            handleTargetConnectionActivatedFromLibrary
                          }
                          onConnectExisting={handleTargetConnectExisting}
                        />
                      )}
                    </div>
                  </div>

                  <SectionCard
                    title="Reconciliation Dashboard"
                    actions={
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={handleReconcileAllCounts}
                          className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                        >
                          <GitCompareArrows size={13} /> Run Count Check — All
                          Tables
                        </button>
                        {/* 2026-09-08, requested by user: "Detailed
                        Reconciliation + DQ" removed — no longer wanted.
                        Run Column Reconciliation still shows for file-sourced
                        tables exactly as before. */}
                        {reconcileMappings.some(isMappingFileSourced) && (
                          <button
                            onClick={handleReconcileAllColumns}
                            disabled={reconcileAllColumnsLoading}
                            className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                          >
                            {reconcileAllColumnsLoading ? (
                              <Loader2 size={13} className="animate-spin" />
                            ) : (
                              <GitCompareArrows size={13} />
                            )}
                            Run Column Reconciliation — All Tables
                          </button>
                        )}
                        <button
                          onClick={handleReconcileAllRows}
                          disabled={reconcileAllRowsLoading}
                          className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                          title="Row-by-row content comparison for every mapped table — expensive at large scale (streams each table twice per side); prefer the per-table action below for specifically flagged tables"
                        >
                          {reconcileAllRowsLoading ? (
                            <Loader2 size={13} className="animate-spin" />
                          ) : (
                            <GitCompareArrows size={13} />
                          )}
                          Run Row-by-Row Reconciliation — All Tables
                        </button>
                        <button
                          onClick={downloadReconciliationCsv}
                          className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                          title="Download the count-check results below as CSV"
                        >
                          <Download size={13} /> Download Results
                        </button>
                        <button
                          onClick={openReconciliationHistoryPanel}
                          className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                          title="View last 10 saved reconciliation runs"
                        >
                          <History size={13} /> History
                        </button>
                      </div>
                    }
                  >
                    <p className="text-xs text-white/50 mb-4">
                      Row-count and column-by-column checks between each raw
                      source table and its created target table, based on the
                      latest STTM. One tab per mapped table.
                    </p>

                    <div className="overflow-auto max-h-[50vh] rounded-xl border border-white/10">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 z-10">
                          <tr className="bg-surface-300 text-left text-xs text-white/50 uppercase tracking-wide">
                            <th className="px-3 py-2">Source Table</th>
                            <th className="px-3 py-2">Target Table</th>
                            <th className="px-3 py-2 text-right">
                              Source Count
                            </th>
                            <th className="px-3 py-2 text-right">
                              Target Count
                            </th>
                            <th className="px-3 py-2 text-right">Difference</th>
                            <th className="px-3 py-2">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reconcileMappings.map((m, idx) => {
                            const key = reconcileTabKey(m);
                            const tab = getReconcileTab(key);
                            const result = tab.countResult;
                            return (
                              <tr
                                key={key}
                                onClick={() => setReconcileActiveTab(idx)}
                                className={`border-t border-white/10 cursor-pointer hover:bg-white/5 transition ${
                                  reconcileActiveTab === idx
                                    ? "bg-brand-500/10"
                                    : ""
                                }`}
                              >
                                <td className="px-3 py-2 text-white/80">
                                  {m.source_table}
                                  {result?.source_is_joined && (
                                    <span
                                      className="ml-1.5 inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-blue-500/15 text-blue-300 text-[9px] uppercase tracking-wide align-middle"
                                      title="Counted from this mapping's joined source tables"
                                    >
                                      <GitMerge size={9} /> Joined
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-white/80">
                                  {stagedTargetTable(m.target_object)}
                                  {ddlStatements.length > 0 &&
                                    !ddlStatements.some(
                                      (d) =>
                                        d.table_name ===
                                        stagedTargetTable(m.target_object),
                                    ) && (
                                      <span
                                        className="ml-1.5 inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-amber-500/15 text-amber-300 text-[9px] uppercase tracking-wide align-middle"
                                        title={`No table named "${stagedTargetTable(m.target_object)}" was among the tables Create Staging Tables last created — the target object name may have changed since; regenerate/recreate staging tables for this mapping.`}
                                      >
                                        <AlertTriangle size={9} /> Not created?
                                      </span>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-right text-white font-medium">
                                  {tab.countLoading ? (
                                    <Loader2
                                      size={13}
                                      className="animate-spin inline text-white/40"
                                    />
                                  ) : (
                                    (result?.source_count ?? "—")
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right text-white font-medium">
                                  {tab.countLoading ? (
                                    <Loader2
                                      size={13}
                                      className="animate-spin inline text-white/40"
                                    />
                                  ) : (
                                    (result?.target_count ?? "—")
                                  )}
                                </td>
                                <td className="px-3 py-2 text-right text-white/70">
                                  {tab.countLoading
                                    ? "—"
                                    : (result?.difference ?? "—")}
                                </td>
                                <td className="px-3 py-2">
                                  {tab.countLoading ? (
                                    <span className="text-white/40 text-xs">
                                      Checking…
                                    </span>
                                  ) : tab.error || result?.error ? (
                                    <span className="flex items-center gap-1 text-red-300 text-xs">
                                      <AlertTriangle size={12} /> Error
                                    </span>
                                  ) : !result ? (
                                    <span className="text-white/30 text-xs">
                                      Not checked
                                    </span>
                                  ) : result.match ? (
                                    <span className="flex items-center gap-1 text-green-400 text-xs font-semibold">
                                      <CheckCircle2 size={12} /> Match
                                    </span>
                                  ) : result.mdm_matched ? (
                                    <span className="flex items-center gap-1 text-brand-400 text-xs font-semibold">
                                      <CheckCircle2 size={12} /> Matched with
                                      MDM
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1 text-amber-400 text-xs font-semibold">
                                      <AlertTriangle size={12} /> Mismatch
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </SectionCard>

                  {detailedReconcileResult && (
                    <SectionCard
                      title="Detailed Reconciliation & Data Quality"
                      actions={
                        <button
                          onClick={handleGenerateFinalTableDq}
                          disabled={finalTableDqLoading}
                          className="ztd-btn ztd-btn-secondary ztd-btn-sm"
                        >
                          {finalTableDqLoading ? (
                            <Loader2 size={13} className="animate-spin" />
                          ) : (
                            <CheckCheck size={13} />
                          )}
                          Generate Final-Table DQ Report
                        </button>
                      }
                    >
                      <ConverterQualityReport
                        tables={detailedReconcileResult.tables}
                        overallScore={detailedReconcileResult.overall_score}
                        title="Reconciliation Quality Scorecard"
                      />
                    </SectionCard>
                  )}

                  {finalTableDqReport && (
                    <SectionCard title="Final Table Data Quality Report">
                      <ConverterQualityReport
                        tables={finalTableDqReport.tables}
                        overallScore={finalTableDqReport.overall_score}
                        title="Post-Load Target Table Data Quality"
                      />
                    </SectionCard>
                  )}

                  <div className="flex gap-1 overflow-x-auto border-b border-white/10 pb-px">
                    {reconcileMappings.map((m, idx) => {
                      const key = reconcileTabKey(m);
                      const tab = getReconcileTab(key);
                      return (
                        <button
                          key={key}
                          onClick={() => setReconcileActiveTab(idx)}
                          className={`flex items-center gap-2 px-4 py-2 text-sm whitespace-nowrap border-b-2 transition ${
                            reconcileActiveTab === idx
                              ? "border-brand-400 text-white"
                              : "border-transparent text-white/50 hover:text-white/80"
                          }`}
                        >
                          {m.source_table}
                          <ArrowRight size={12} className="text-white/30" />
                          {stagedTargetTable(m.target_object)}
                          {tab.countResult && (
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${
                                tab.countResult.match
                                  ? "bg-green-400"
                                  : tab.countResult.mdm_matched
                                    ? "bg-brand-400"
                                    : "bg-amber-400"
                              }`}
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>

                  {activeReconcileMapping && activeReconcileKey && (
                    <>
                      <SectionCard
                        title={`${activeReconcileMapping.source_table} → ${stagedTargetTable(activeReconcileMapping.target_object)}`}
                      >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-xs text-white/50 mb-1">
                              Source Schema
                            </label>
                            <input
                              value={
                                activeReconcileTab.sourceSchema ||
                                activeReconcileMapping.source_schema ||
                                sourceSchemaName ||
                                ""
                              }
                              onChange={(e) =>
                                updateReconcileTab(activeReconcileKey, {
                                  sourceSchema: e.target.value,
                                })
                              }
                              className="w-full px-3 py-2 rounded-lg bg-surface-400/60 border border-white/10 text-sm text-white focus:outline-none focus:border-brand-500/60"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-white/50 mb-1">
                              Target Schema
                            </label>
                            <input
                              value={
                                activeReconcileTab.targetSchema ||
                                ddlTargetSchema ||
                                targetSchemaName ||
                                ""
                              }
                              onChange={(e) =>
                                updateReconcileTab(activeReconcileKey, {
                                  targetSchema: e.target.value,
                                })
                              }
                              className="w-full px-3 py-2 rounded-lg bg-surface-400/60 border border-white/10 text-sm text-white focus:outline-none focus:border-brand-500/60"
                            />
                          </div>
                        </div>

                        {isMappingFileSourced(activeReconcileMapping) && (
                          <div className="flex items-center gap-2 flex-wrap mb-4 pb-4 border-b border-white/8">
                            <label className="ztd-btn ztd-btn-secondary ztd-btn-sm cursor-pointer">
                              <Upload size={12} />
                              Choose File
                              <input
                                type="file"
                                accept=".csv,.tsv,.json,.jsonl,.parquet,.xlsx,.xls"
                                className="hidden"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  e.target.value = "";
                                  if (!f) return;
                                  const loadIdx = mappings.findIndex(
                                    (m) =>
                                      m.source_table ===
                                        activeReconcileMapping.source_table &&
                                      m.target_object ===
                                        activeReconcileMapping.target_object,
                                  );
                                  if (loadIdx >= 0)
                                    void handleChooseSourceFileForRow(
                                      loadIdx,
                                      f,
                                    );
                                }}
                              />
                            </label>
                            {(() => {
                              const known = buildFileReconcileSourceFile(
                                activeReconcileMapping,
                              );
                              return known ? (
                                <span className="flex items-center gap-1 text-xs text-green-400">
                                  <CheckCircle2 size={12} />{" "}
                                  {known.path.split("__").pop()}
                                </span>
                              ) : (
                                <span className="text-xs text-white/40">
                                  No source file known for this table yet — pick
                                  one to reconcile against.
                                </span>
                              );
                            })()}
                          </div>
                        )}

                        {activeReconcileTab.error && (
                          <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-300 flex items-center gap-2">
                            <AlertTriangle size={14} />{" "}
                            {activeReconcileTab.error}
                          </div>
                        )}

                        <div className="flex items-center gap-3 flex-wrap mb-4">
                          <button
                            onClick={() =>
                              handleReconcileCounts(activeReconcileMapping)
                            }
                            disabled={activeReconcileTab.countLoading}
                            className="ztd-btn ztd-btn-secondary"
                          >
                            {activeReconcileTab.countLoading ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <GitCompareArrows size={14} />
                            )}
                            Run Count Check
                          </button>
                          <button
                            onClick={() =>
                              handleReconcileColumns(activeReconcileMapping)
                            }
                            disabled={activeReconcileTab.columnLoading}
                            className="ztd-btn ztd-btn-primary"
                          >
                            {activeReconcileTab.columnLoading ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Scale size={14} />
                            )}
                            Run Column Reconciliation
                          </button>
                          <button
                            onClick={() =>
                              handleReconcileRows(activeReconcileMapping)
                            }
                            disabled={
                              activeReconcileTab.rowLoading ||
                              (activeReconcileTab.keyColumns.length === 0 &&
                                suggestedReconcileKeyColumns.length === 0)
                            }
                            className="ztd-btn ztd-btn-primary"
                            title="Compares actual row content between source and target, matched by the key column(s) selected below"
                          >
                            {activeReconcileTab.rowLoading ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Scale size={14} />
                            )}
                            Run Row-by-Row Reconciliation
                          </button>
                        </div>

                        {(activeReconcileTab.countResult ||
                          activeReconcileTab.columnResult ||
                          activeReconcileTab.rowResult) && (
                          <div className="flex items-center gap-1 mb-4 border-b border-white/10">
                            {activeReconcileTab.countResult && (
                              <button
                                onClick={() =>
                                  updateReconcileTab(activeReconcileKey!, {
                                    activeResultTab: "counts",
                                  })
                                }
                                className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                                  activeReconcileTab.activeResultTab ===
                                  "counts"
                                    ? "border-brand-400 text-white"
                                    : "border-transparent text-white/50 hover:text-white/80"
                                }`}
                              >
                                Count Check
                              </button>
                            )}
                            {activeReconcileTab.columnResult && (
                              <button
                                onClick={() =>
                                  updateReconcileTab(activeReconcileKey!, {
                                    activeResultTab: "columns",
                                  })
                                }
                                className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                                  activeReconcileTab.activeResultTab ===
                                  "columns"
                                    ? "border-brand-400 text-white"
                                    : "border-transparent text-white/50 hover:text-white/80"
                                }`}
                              >
                                Column-by-Column Reconciliation
                              </button>
                            )}
                            {activeReconcileTab.rowResult && (
                              <button
                                onClick={() =>
                                  updateReconcileTab(activeReconcileKey!, {
                                    activeResultTab: "rows",
                                  })
                                }
                                className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                                  activeReconcileTab.activeResultTab === "rows"
                                    ? "border-brand-400 text-white"
                                    : "border-transparent text-white/50 hover:text-white/80"
                                }`}
                              >
                                Row-by-Row Reconciliation
                              </button>
                            )}
                          </div>
                        )}

                        {activeReconcileMapping && (
                          <div className="mb-4">
                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                              <span className="text-xs text-white/50">
                                Row reconciliation key column(s):
                              </span>
                              {activeReconcileTab.keyColumns.length === 0 &&
                                suggestedReconcileKeyColumns.length === 0 && (
                                  <span className="text-xs text-amber-300/80 flex items-center gap-1">
                                    <AlertTriangle size={11} /> No primary key
                                    detected for this table — select column(s)
                                    below to match rows.
                                  </span>
                                )}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {activeReconcileMapping.field_mappings
                                .filter(
                                  (fm) =>
                                    fm.status !== "Unmapped" && fm.target_field,
                                )
                                .map((fm) => {
                                  const selected =
                                    activeReconcileTab.keyColumns.includes(
                                      fm.target_field,
                                    );
                                  return (
                                    <button
                                      key={fm.target_field}
                                      type="button"
                                      onClick={() =>
                                        updateReconcileTab(
                                          activeReconcileKey!,
                                          {
                                            keyColumns: selected
                                              ? activeReconcileTab.keyColumns.filter(
                                                  (k) => k !== fm.target_field,
                                                )
                                              : [
                                                  ...activeReconcileTab.keyColumns,
                                                  fm.target_field,
                                                ],
                                          },
                                        )
                                      }
                                      className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${
                                        selected
                                          ? "bg-brand-500/20 border-brand-500/40 text-brand-200"
                                          : "bg-white/5 border-white/10 text-white/50 hover:bg-white/10"
                                      }`}
                                    >
                                      {fm.target_field}
                                    </button>
                                  );
                                })}
                            </div>
                          </div>
                        )}

                        {activeReconcileTab.activeResultTab === "counts" &&
                          activeReconcileTab.countResult && (
                            <div
                              className={`grid grid-cols-2 sm:grid-cols-5 gap-4 p-4 rounded-xl border ${
                                activeReconcileTab.countResult.match
                                  ? "bg-green-500/10 border-green-500/20"
                                  : activeReconcileTab.countResult.mdm_matched
                                    ? "bg-brand-500/10 border-brand-500/20"
                                    : "bg-amber-500/10 border-amber-500/20"
                              }`}
                            >
                              <div>
                                <div className="text-white/40 text-xs flex items-center gap-1">
                                  Source Count
                                  {activeReconcileTab.countResult
                                    .source_is_joined && (
                                    <span
                                      className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-blue-500/15 text-blue-300 text-[9px] uppercase tracking-wide"
                                      title="Counted from this mapping's joined source tables (+ its filter, if any) — not a raw COUNT(*) of one physical table"
                                    >
                                      <GitMerge size={9} /> Joined
                                    </span>
                                  )}
                                </div>
                                <div className="text-white font-semibold">
                                  {activeReconcileTab.countResult
                                    .source_count ?? "—"}
                                </div>
                              </div>
                              <div>
                                <div className="text-white/40 text-xs">
                                  Target Count
                                </div>
                                <div className="text-white font-semibold">
                                  {activeReconcileTab.countResult
                                    .target_count ?? "—"}
                                </div>
                              </div>
                              <div>
                                <div className="text-white/40 text-xs">
                                  Difference
                                </div>
                                <div className="text-white font-semibold">
                                  {activeReconcileTab.countResult.difference ??
                                    "—"}
                                </div>
                              </div>
                              {activeReconcileTab.countResult
                                .mdm_lookup_count != null && (
                                <div>
                                  <div className="text-white/40 text-xs">
                                    MDM Lookup Count
                                  </div>
                                  <div className="text-brand-300 font-semibold">
                                    {
                                      activeReconcileTab.countResult
                                        .mdm_lookup_count
                                    }
                                  </div>
                                </div>
                              )}
                              <div>
                                <div className="text-white/40 text-xs">
                                  Status
                                </div>
                                <div className="flex items-center gap-1.5 font-semibold">
                                  {activeReconcileTab.countResult.match ? (
                                    <>
                                      <CheckCircle2
                                        size={14}
                                        className="text-green-400"
                                      />
                                      <span className="text-green-400">
                                        Match
                                      </span>
                                    </>
                                  ) : activeReconcileTab.countResult
                                      .mdm_matched ? (
                                    <>
                                      <CheckCircle2
                                        size={14}
                                        className="text-brand-400"
                                      />
                                      <span className="text-brand-400">
                                        Matched with MDM data
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <AlertTriangle
                                        size={14}
                                        className="text-amber-400"
                                      />
                                      <span className="text-amber-400">
                                        Mismatch
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>
                              {activeReconcileTab.countResult.error && (
                                <div className="col-span-2 sm:col-span-5 text-xs text-red-300">
                                  {activeReconcileTab.countResult.error}
                                </div>
                              )}
                            </div>
                          )}
                      </SectionCard>

                      {activeReconcileTab.activeResultTab === "columns" &&
                        activeReconcileTab.columnResult && (
                          <SectionCard
                            title={`Column-by-Column Reconciliation — ${activeReconcileTab.columnResult.source_total_rows} source rows / ${activeReconcileTab.columnResult.target_total_rows} target rows`}
                            actions={
                              <div className="flex items-center gap-1.5">
                                {activeReconcileTab.columnResult.columns.some(
                                  (c: ColumnReconcileResult) =>
                                    c.status !== "match",
                                ) && (
                                  <button
                                    onClick={() =>
                                      downloadFailuresTxt(
                                        "Reconciliation mismatches",
                                        `reconciliation_mismatches_${filenameTimestamp()}.txt`,
                                        activeReconcileTab
                                          .columnResult!.columns.filter(
                                            (c: ColumnReconcileResult) =>
                                              c.status !== "match",
                                          )
                                          .map(
                                            (c: ColumnReconcileResult) =>
                                              `${c.source_column} -> ${c.target_field}: source nulls=${c.source_null_count}, target nulls=${c.target_null_count}, source distinct=${c.source_distinct_count}, target distinct=${c.target_distinct_count}`,
                                          ),
                                      )
                                    }
                                    className="ztd-btn ztd-btn-secondary ztd-btn-sm !px-2"
                                    title="Download mismatches as .txt"
                                  >
                                    <Download size={12} />
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    const cr = activeReconcileTab.columnResult!;
                                    downloadTableAsPdf(
                                      `Column Reconciliation — ${cr.source_table} → ${cr.target_table}`,
                                      RECONCILE_COLUMN_EXPORT_HEADERS,
                                      reconcileColumnExportRows(cr.columns),
                                      `reconciliation_${cr.target_table}.pdf`,
                                    );
                                  }}
                                  className="ztd-btn ztd-btn-secondary ztd-btn-sm !px-2"
                                  title="Download this table's full column reconciliation as a PDF"
                                >
                                  <FileText size={12} />
                                </button>
                                <button
                                  onClick={() => {
                                    const cr = activeReconcileTab.columnResult!;
                                    downloadTableAsExcel(
                                      `Recon ${cr.target_table}`,
                                      RECONCILE_COLUMN_EXPORT_HEADERS,
                                      reconcileColumnExportRows(cr.columns),
                                      `reconciliation_${cr.target_table}.xls`,
                                    );
                                  }}
                                  className="ztd-btn ztd-btn-secondary ztd-btn-sm !px-2"
                                  title="Download this table's full column reconciliation as an Excel workbook"
                                >
                                  <FileSpreadsheet size={12} />
                                </button>
                              </div>
                            }
                          >
                            <div className="overflow-auto max-h-[50vh]">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-surface-500/95 backdrop-blur text-white/40 sticky top-0 z-10">
                                    <th className="px-3 py-2 text-left font-medium">
                                      Source Column
                                    </th>
                                    <th className="px-3 py-2 text-left font-medium">
                                      Target Field
                                    </th>
                                    <th className="px-3 py-2 text-right font-medium">
                                      Source Nulls
                                    </th>
                                    <th className="px-3 py-2 text-right font-medium">
                                      Target Nulls
                                    </th>
                                    <th className="px-3 py-2 text-right font-medium">
                                      Source Distinct
                                    </th>
                                    <th className="px-3 py-2 text-right font-medium">
                                      Target Distinct
                                    </th>
                                    <th className="px-3 py-2 text-center font-medium">
                                      Status
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {activeReconcileTab.columnResult.columns.map(
                                    (c: ColumnReconcileResult) => (
                                      <tr
                                        key={c.source_column}
                                        className="border-t border-white/5 hover:bg-white/2"
                                      >
                                        <td className="px-3 py-1.5 font-mono text-white/80">
                                          {c.source_column}
                                        </td>
                                        <td className="px-3 py-1.5 font-mono text-brand-300/80">
                                          {c.target_field}
                                        </td>
                                        <td className="px-3 py-1.5 text-right text-white/70">
                                          {c.source_null_count}
                                        </td>
                                        <td className="px-3 py-1.5 text-right text-white/70">
                                          {c.target_null_count}
                                        </td>
                                        <td className="px-3 py-1.5 text-right text-white/50">
                                          {c.source_distinct_count ?? "N/A"}
                                        </td>
                                        <td className="px-3 py-1.5 text-right text-white/50">
                                          {c.target_distinct_count ?? "N/A"}
                                        </td>
                                        <td className="px-3 py-1.5 text-center">
                                          {c.status === "match" ? (
                                            <span className="inline-flex items-center gap-1 text-green-400">
                                              <CheckCircle2 size={12} /> Match
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center gap-1 text-amber-400">
                                              <AlertTriangle size={12} /> Review
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    ),
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </SectionCard>
                        )}

                      {activeReconcileTab.activeResultTab === "rows" &&
                        activeReconcileTab.rowResult && (
                          <SectionCard
                            title={`Row-by-Row Reconciliation — ${activeReconcileTab.rowResult.matched_rows} matched / ${activeReconcileTab.rowResult.mismatched_rows} mismatched / ${activeReconcileTab.rowResult.missing_in_target} missing in target / ${activeReconcileTab.rowResult.missing_in_source} missing in source`}
                            actions={
                              <div className="flex items-center gap-1.5">
                                {activeReconcileTab.rowResult.mismatches
                                  .length > 0 && (
                                  <button
                                    onClick={() =>
                                      downloadFailuresTxt(
                                        "Row reconciliation mismatches",
                                        `row_reconciliation_mismatches_${filenameTimestamp()}.txt`,
                                        activeReconcileTab.rowResult!.mismatches.map(
                                          (m) =>
                                            `${Object.entries(m.key)
                                              .map(
                                                ([k, v]) =>
                                                  `${k}=${v ?? "NULL"}`,
                                              )
                                              .join(
                                                ", ",
                                              )}: ${m.differing_columns
                                              .map(
                                                (d) =>
                                                  `${d.target_field} (source=${d.source_value ?? "NULL"}, target=${d.target_value ?? "NULL"})`,
                                              )
                                              .join("; ")}`,
                                        ),
                                      )
                                    }
                                    className="ztd-btn ztd-btn-secondary ztd-btn-sm !px-2"
                                    title="Download mismatches as .txt"
                                  >
                                    <Download size={12} />
                                  </button>
                                )}
                                <button
                                  onClick={() => {
                                    const rr = activeReconcileTab.rowResult!;
                                    downloadTableAsPdf(
                                      `Row Reconciliation — ${rr.source_table} → ${rr.target_table}`,
                                      RECONCILE_ROW_EXPORT_HEADERS,
                                      reconcileRowExportRows(rr.mismatches),
                                      `row_reconciliation_${rr.target_table}.pdf`,
                                    );
                                  }}
                                  className="ztd-btn ztd-btn-secondary ztd-btn-sm !px-2"
                                  title="Download this table's row mismatches as a PDF"
                                >
                                  <FileText size={12} />
                                </button>
                                <button
                                  onClick={() => {
                                    const rr = activeReconcileTab.rowResult!;
                                    downloadTableAsExcel(
                                      `Row Recon ${rr.target_table}`,
                                      RECONCILE_ROW_EXPORT_HEADERS,
                                      reconcileRowExportRows(rr.mismatches),
                                      `row_reconciliation_${rr.target_table}.xls`,
                                    );
                                  }}
                                  className="ztd-btn ztd-btn-secondary ztd-btn-sm !px-2"
                                  title="Download this table's row mismatches as an Excel workbook"
                                >
                                  <FileSpreadsheet size={12} />
                                </button>
                              </div>
                            }
                          >
                            <p className="text-xs text-white/50 mb-3">
                              Matched by key column(s):{" "}
                              <span className="font-mono text-brand-300/80">
                                {activeReconcileTab.rowResult.key_columns.join(
                                  ", ",
                                )}
                              </span>{" "}
                              — {activeReconcileTab.rowResult.source_total_rows}{" "}
                              source rows scanned /{" "}
                              {activeReconcileTab.rowResult.target_total_rows}{" "}
                              target rows scanned.
                            </p>
                            {activeReconcileTab.rowResult.truncated && (
                              <div className="mb-3 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300 flex items-center gap-2">
                                <AlertTriangle size={12} /> Scan capped at{" "}
                                {activeReconcileTab.rowResult.max_rows_scanned.toLocaleString()}{" "}
                                rows per side — results reflect only the scanned
                                portion, not necessarily the whole table.
                              </div>
                            )}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 rounded-xl border bg-white/5 border-white/10 mb-4">
                              <div>
                                <div className="text-white/40 text-xs">
                                  Matched
                                </div>
                                <div className="text-lg font-semibold text-green-400">
                                  {activeReconcileTab.rowResult.matched_rows}
                                </div>
                              </div>
                              <div>
                                <div className="text-white/40 text-xs">
                                  Mismatched
                                </div>
                                <div className="text-lg font-semibold text-amber-400">
                                  {activeReconcileTab.rowResult.mismatched_rows}
                                </div>
                              </div>
                              <div>
                                <div className="text-white/40 text-xs">
                                  Missing in Target
                                </div>
                                <div className="text-lg font-semibold text-red-400">
                                  {
                                    activeReconcileTab.rowResult
                                      .missing_in_target
                                  }
                                </div>
                              </div>
                              <div>
                                <div className="text-white/40 text-xs">
                                  Missing in Source
                                </div>
                                <div className="text-lg font-semibold text-red-400">
                                  {
                                    activeReconcileTab.rowResult
                                      .missing_in_source
                                  }
                                </div>
                              </div>
                            </div>

                            {activeReconcileTab.rowResult.mismatches.length >
                              0 && (
                              <>
                                <p className="text-xs text-white/50 mb-2">
                                  Mismatched rows (showing{" "}
                                  {
                                    activeReconcileTab.rowResult.mismatches
                                      .length
                                  }
                                  {activeReconcileTab.rowResult
                                    .mismatches_truncated
                                    ? ` of ${activeReconcileTab.rowResult.mismatched_rows}`
                                    : ""}
                                  ) — only differing columns shown:
                                </p>
                                <div className="overflow-auto max-h-[40vh] mb-4">
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="bg-surface-500/95 backdrop-blur text-white/40 sticky top-0 z-10">
                                        <th className="px-3 py-2 text-left font-medium">
                                          Key
                                        </th>
                                        <th className="px-3 py-2 text-left font-medium">
                                          Differing Columns
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {activeReconcileTab.rowResult.mismatches.map(
                                        (m: RowMismatchDetail, i: number) => (
                                          <tr
                                            key={i}
                                            className="border-t border-white/5 hover:bg-white/2"
                                          >
                                            <td className="px-3 py-1.5 font-mono text-white/80 align-top">
                                              {Object.entries(m.key)
                                                .map(
                                                  ([k, v]) =>
                                                    `${k}=${v ?? "NULL"}`,
                                                )
                                                .join(", ")}
                                            </td>
                                            <td className="px-3 py-1.5">
                                              {m.differing_columns.map((d) => (
                                                <div
                                                  key={d.target_field}
                                                  className="mb-0.5"
                                                >
                                                  <span className="font-mono text-brand-300/80">
                                                    {d.target_field}
                                                  </span>
                                                  :{" "}
                                                  <span className="text-red-300">
                                                    {d.source_value ?? "NULL"}
                                                  </span>{" "}
                                                  →{" "}
                                                  <span className="text-green-300">
                                                    {d.target_value ?? "NULL"}
                                                  </span>
                                                </div>
                                              ))}
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </>
                            )}

                            {(activeReconcileTab.rowResult
                              .missing_in_target_rows.length > 0 ||
                              activeReconcileTab.rowResult
                                .missing_in_source_rows.length > 0) && (
                              <div className="grid sm:grid-cols-2 gap-4">
                                {activeReconcileTab.rowResult
                                  .missing_in_target_rows.length > 0 && (
                                  <div>
                                    <p className="text-xs text-white/50 mb-1">
                                      Missing in target (showing{" "}
                                      {
                                        activeReconcileTab.rowResult
                                          .missing_in_target_rows.length
                                      }
                                      {activeReconcileTab.rowResult
                                        .missing_in_target_truncated
                                        ? ` of ${activeReconcileTab.rowResult.missing_in_target}`
                                        : ""}
                                      ):
                                    </p>
                                    <div className="overflow-auto max-h-[30vh]">
                                      <ul className="text-xs font-mono text-white/70 space-y-0.5">
                                        {activeReconcileTab.rowResult.missing_in_target_rows.map(
                                          (r: RowSummaryDetail, i: number) => (
                                            <li key={i}>
                                              {Object.entries(r.key)
                                                .map(
                                                  ([k, v]) =>
                                                    `${k}=${v ?? "NULL"}`,
                                                )
                                                .join(", ")}
                                            </li>
                                          ),
                                        )}
                                      </ul>
                                    </div>
                                  </div>
                                )}
                                {activeReconcileTab.rowResult
                                  .missing_in_source_rows.length > 0 && (
                                  <div>
                                    <p className="text-xs text-white/50 mb-1">
                                      Missing in source (showing{" "}
                                      {
                                        activeReconcileTab.rowResult
                                          .missing_in_source_rows.length
                                      }
                                      {activeReconcileTab.rowResult
                                        .missing_in_source_truncated
                                        ? ` of ${activeReconcileTab.rowResult.missing_in_source}`
                                        : ""}
                                      ):
                                    </p>
                                    <div className="overflow-auto max-h-[30vh]">
                                      <ul className="text-xs font-mono text-white/70 space-y-0.5">
                                        {activeReconcileTab.rowResult.missing_in_source_rows.map(
                                          (r: RowSummaryDetail, i: number) => (
                                            <li key={i}>
                                              {Object.entries(r.key)
                                                .map(
                                                  ([k, v]) =>
                                                    `${k}=${v ?? "NULL"}`,
                                                )
                                                .join(", ")}
                                            </li>
                                          ),
                                        )}
                                      </ul>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </SectionCard>
                        )}
                    </>
                  )}

                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => setAction(null)}
                      className="ztd-btn ztd-btn-secondary"
                    >
                      <ArrowLeft size={14} /> Back to Actions
                    </button>
                    {/* A direct (no-staging) source -> target load already
                    wrote straight into the real target table — there's no
                    separate staging table left to push from, so Reconciliation
                    (comparing source against that same real target) is the
                    last step of this flow, not a jumping-off point to one
                    more load. Only a CRM-staged flow has an actual
                    Load-to-Target step (sf_load) left to jump to.

                    2026-10-01, reported by user: "Continue to Load to
                    Target" still showed for an RDBMS-staged project and,
                    clicking it, just sent them back to the "load" step —
                    the very step whose results they were already looking
                    at Reconciliation for. jumpToLoadTarget's own comment
                    already establishes why: for an RDBMS (or mirror)
                    target, the staging load IS the final load, so its
                    destinationStep for a non-CRM target is "load" itself,
                    not some further, distinct push. Mirror mode got an
                    explicit carve-out for this same reason (the
                    map-to-a-real-target choice below); RDBMS staged never
                    did, which is exactly the bug reported here. Gate this
                    button on targetPlatformMode === "crm" too, matching
                    the comment's own stated precondition. */}
                    {targetPlatformMode === "mirror" ? (
                      // Mirror mode has no real target platform yet — its
                      // "staging" tables are the finished output, so give the
                      // user an explicit yes/no choice instead of the
                      // confusing "Continue to Load to Target" (which used
                      // to just re-land on the already-completed staging
                      // load). "Back to Actions" above is the "no" answer.
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-white/40">
                          Want to map &amp; load this data into a real target
                          system?
                        </span>
                        <button
                          onClick={() => void startMirrorTargetMapping()}
                          disabled={startingMirrorTargetMapping}
                          className="ztd-btn ztd-btn-primary"
                        >
                          {startingMirrorTargetMapping ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <ArrowRight size={14} />
                          )}
                          Map &amp; Load to a Target
                        </button>
                      </div>
                    ) : (
                      targetPlatformMode === "crm" &&
                      crmLoadStrategy !== "direct" && (
                        <button
                          onClick={() => void jumpToLoadTarget()}
                          className="ztd-btn ztd-btn-primary"
                        >
                          Continue to Load to Target <ArrowRight size={14} />
                        </button>
                      )
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
      {resumeModals}
    </div>
  );
}
